import AmazonV1UpgradeNotice from './AmazonV1UpgradeNotice';
import {amazonV2ErrorMessage} from './amazonV2ConnectionMessages';
import {customerOrderSelectableStores, customerOrderSyncTargetStores} from './lib/customerOrderStores';
import {isCustomerConnectionReturnPath} from './lib/customerConnectionReturnPath';
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  BarChart3,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  Copy,
  CreditCard,
  Download,
  Eye,
  EyeOff,
  ExternalLink,
  FileText,
  Globe2,
  HandCoins,
  History,
  Info,
  KeyRound,
  LayoutDashboard,
  Loader2,
  LogOut,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Package,
  Phone,
  Power,
  PlusSquare,
  RotateCcw,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  Square,
  Store,
  Tags,
  Trash2,
  Truck,
  Upload,
  User,
  Users,
  WandSparkles,
  Wallet,
  X,
} from "lucide-react";
import { ChangeEvent, ClipboardEvent as ReactClipboardEvent, FormEvent, MouseEvent as ReactMouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import {
  apiClient,
  ApiError,
  AddressSuggestion,
  AffiliateDashboardResponse,
  AdminAffiliateLabelCreditResponse,
  AdminCustomerActionResponse,
  AdminCustomerDetail,
  AmazonStatusResponse,
  CreateShipmentResponse,
  CreateStorePayload,
  DashboardSummary,
  EtsyBrowserConnectionPayload,
  LabelBalanceAdjustmentSummary,
  LabelBalancesResponse,
  LabelBalanceSummary,
  LabelProductSummary,
  LabelPurchaseSummary,
  OrderDetail,
  OrderSummary,
  OrderSyncPlatform,
  ProfileDetails,
  RecognizedAccount,
  SessionUser,
  ShipmentDetail,
  SignInEmailTwoFactorResponse,
  StoreDetail,
  StoreSenderAddressPayload,
  StoreSenderAddressSummary,
  StoreSyncLog,
  StoreSummary,
  StorePlatform,
  SupportRequestSummary,
  TeamMemberSummary,
  TrainingVideoSummary,
  UserRole,
} from "@/lib/api";
import { panelTranslations } from "@/panelTranslations";
import AmazonV2ConnectionPage from "./AmazonV2ConnectionPage";
import AmazonV2AdminPage from "./AmazonV2AdminPage";
import { amazonV2Translations } from "./amazonV2Translations";
import { customerGermanTranslationPatterns, customerGermanTranslations } from "@/customerGermanTranslations";
import { customerEnglishTranslationPatterns, customerEnglishTranslations } from "@/customerEnglishTranslations";
import { translateAuthApiErrorMessage } from "@/authErrorTranslations";
import { recentFeatureTranslationPatterns, recentFeatureTranslations } from "@/recentFeatureTranslations";
import { labelInventoryExpiryMeta, labelInventoryPdfCreatedAt } from "@/lib/labelInventoryDates";
import { summarizeCustomerStores } from "@/lib/customerStorePresentation";
import { buildAppVersionRefreshUrl, isSameAppVersion } from "@/lib/appVersion";
import { assertCompleteBulkLabelPdf } from "@/lib/bulkLabelPdf";
import {
  bulkOrderLabelPdfChunkSize,
  downloadBulkOrderLabelPdfBatches,
  maxBulkOrderLabelOrders,
  pendingBulkOrderLabelDrafts,
} from "@/lib/bulkOrderLabels";
import { customerOrdersViewDateFrom, CustomerOrdersViewRange, normalizeCustomerOrdersViewRange } from "@/lib/customerOrderView";
import { initialCustomerOrderSelection, persistCustomerOrderSelection, readCustomerOrderSelection, resolveCustomerOrderSelection } from "@/lib/customerOrderPreferences";
import { manualAddressBlockIsComplete, parseManualAddressBlock } from "@/lib/manualAddressParser";
import { getCurrentProductVersionLabel, latestRelease, releaseNotes } from "@/lib/releaseNotes";
import { normalizeSourceOrderDate } from "../shared/orderDate";
import { meetsLabelCheckoutMinimum, minimumLabelCheckoutMessage } from "../shared/labelCheckoutPolicy";
import { ApiAccessPage } from "@/ApiAccessPage";
import { JtlConnectPage } from "@/JtlConnectPage";
import { JtlDeviceHealthPage } from "@/JtlDeviceHealthPage";

type AuthState =
  | { status: "loading"; user: null }
  | { status: "signed-out"; user: null }
  | { status: "signed-in"; user: SessionUser };

declare global {
  interface Window {
    shopify?: {
      idToken?: () => Promise<string>;
      toast?: {
        show?: (message: string, options?: Record<string, unknown>) => void;
      };
    };
  }
}

const AUTH_SESSION_CACHE_KEY = "kenpost.sessionUser.v1";
const DASHBOARD_SUMMARY_CACHE_KEY = "kenpost.dashboardSummary.v1";
const DASHBOARD_SUMMARY_CACHE_TTL_MS = 5 * 60 * 1000;
const ADMIN_CUSTOMER_DETAIL_CACHE_KEY = "kenpost.adminCustomerDetails.v1";
const ADMIN_CUSTOMER_DETAIL_CACHE_TTL_MS = 5 * 60 * 1000;
const PLATFORM_INSIGHTS_CACHE_KEY = "kenpost.platformInsights.v1";
const PLATFORM_INSIGHTS_CACHE_TTL_MS = 5 * 60 * 1000;

function isSessionUser(value: unknown): value is SessionUser {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<SessionUser>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.email === "string" &&
    (candidate.role === "admin" || candidate.role === "customer" || candidate.role === "subcustomer") &&
    typeof candidate.active === "boolean"
  );
}

function canUseCachedAuthUser() {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  return path === "/" || path === "/versions" || path.startsWith("/admin") || path.startsWith("/customer");
}

function shouldVerifyAuthOnLoad() {
  if (typeof window === "undefined") return true;
  return canUseCachedAuthUser();
}

function readCachedAuthUser() {
  if (typeof window === "undefined" || !canUseCachedAuthUser()) return null;
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(AUTH_SESSION_CACHE_KEY) || "null");
    return isSessionUser(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function cacheAuthUser(user: SessionUser) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(AUTH_SESSION_CACHE_KEY, JSON.stringify(user));
  } catch {
    // Session cache only improves reload speed; auth still comes from the server cookie.
  }
}

function clearCachedAuthUser() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(AUTH_SESSION_CACHE_KEY);
    window.sessionStorage.removeItem(DASHBOARD_SUMMARY_CACHE_KEY);
    window.sessionStorage.removeItem(ADMIN_CUSTOMER_DETAIL_CACHE_KEY);
    window.sessionStorage.removeItem(PLATFORM_INSIGHTS_CACHE_KEY);
  } catch {
    // Ignore storage failures; logout/server auth remains authoritative.
  }
}

function dashboardSummaryCacheKey(user: SessionUser) {
  return `${user.role}:${user.parentCustomerId || user.id}`;
}

function isDashboardSummary(value: unknown): value is DashboardSummary {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<DashboardSummary>;
  return Boolean(candidate.role && candidate.user && candidate.totals && Array.isArray(candidate.recentActivity));
}

function readCachedDashboardSummary(user: SessionUser) {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(DASHBOARD_SUMMARY_CACHE_KEY) || "null") as {
      cachedAt?: number;
      key?: string;
      summary?: unknown;
    } | null;
    if (!parsed || parsed.key !== dashboardSummaryCacheKey(user) || !isDashboardSummary(parsed.summary)) return null;
    if (!parsed.cachedAt || Date.now() - parsed.cachedAt > DASHBOARD_SUMMARY_CACHE_TTL_MS) return null;
    return parsed.summary;
  } catch {
    return null;
  }
}

function cacheDashboardSummary(user: SessionUser, summary: DashboardSummary) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      DASHBOARD_SUMMARY_CACHE_KEY,
      JSON.stringify({ cachedAt: Date.now(), key: dashboardSummaryCacheKey(user), summary })
    );
  } catch {
    // Dashboard cache only removes reload flicker; live data still comes from the API.
  }
}

function isAdminCustomerDetail(value: unknown): value is AdminCustomerDetail {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<AdminCustomerDetail>;
  return Boolean(
    candidate.customer &&
      typeof candidate.customer.id === "string" &&
      candidate.totals &&
      Array.isArray(candidate.stores) &&
      Array.isArray(candidate.teamMembers) &&
      Array.isArray(candidate.balances) &&
      Array.isArray(candidate.purchases) &&
      Array.isArray(candidate.orders) &&
      Array.isArray(candidate.shipments) &&
      Array.isArray(candidate.invoices)
  );
}

function readCachedAdminCustomerDetail(customerId?: string) {
  if (typeof window === "undefined" || !customerId) return null;
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(ADMIN_CUSTOMER_DETAIL_CACHE_KEY) || "{}") as Record<
      string,
      { cachedAt?: number; detail?: unknown }
    >;
    const entry = parsed[customerId];
    if (!entry || !entry.cachedAt || Date.now() - entry.cachedAt > ADMIN_CUSTOMER_DETAIL_CACHE_TTL_MS) return null;
    return isAdminCustomerDetail(entry.detail) ? entry.detail : null;
  } catch {
    return null;
  }
}

function cacheAdminCustomerDetail(customerId: string | undefined, detail: AdminCustomerDetail) {
  if (typeof window === "undefined" || !customerId) return;
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(ADMIN_CUSTOMER_DETAIL_CACHE_KEY) || "{}") as Record<
      string,
      { cachedAt?: number; detail?: unknown }
    >;
    parsed[customerId] = { cachedAt: Date.now(), detail };
    window.sessionStorage.setItem(ADMIN_CUSTOMER_DETAIL_CACHE_KEY, JSON.stringify(parsed));
  } catch {
    // The detail cache is an interface speed-up only; server data remains authoritative.
  }
}

type PlatformInsightsPayload = {
  amazonConnections?: DataRow[];
  ebayConnections?: DataRow[];
  env?: Record<string, unknown>;
  platforms: DataRow[];
  stores: DataRow[];
};

function isPlatformInsightsPayload(value: unknown): value is PlatformInsightsPayload {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<PlatformInsightsPayload>;
  return Array.isArray(candidate.platforms) && Array.isArray(candidate.stores);
}

function readCachedPlatformInsights() {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(PLATFORM_INSIGHTS_CACHE_KEY) || "null") as {
      cachedAt?: number;
      data?: unknown;
    } | null;
    if (!parsed || !parsed.cachedAt || Date.now() - parsed.cachedAt > PLATFORM_INSIGHTS_CACHE_TTL_MS) return null;
    return isPlatformInsightsPayload(parsed.data) ? parsed.data : null;
  } catch {
    return null;
  }
}

function cachePlatformInsights(data: PlatformInsightsPayload) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(PLATFORM_INSIGHTS_CACHE_KEY, JSON.stringify({ cachedAt: Date.now(), data }));
  } catch {
    // Platform cache keeps the analysis screen from flashing blank; API data remains canonical.
  }
}

const roleLabels: Record<UserRole, string> = {
  admin: "Admin",
  customer: "Müşteri",
  subcustomer: "Alt müşteri",
};

type PanelNavItem = {
  children?: PanelNavItem[];
  exclude?: string[];
  icon: typeof LayoutDashboard;
  label: string;
  match?: string[];
  ownerOnly?: boolean;
  to: string;
};

const adminLabelNav: PanelNavItem[] = [
  { to: "/admin/label-products", label: "Etiket Ürünleri", icon: Package, match: ["/admin/labels"] },
  { to: "/admin/label-sources", label: "Etiket Envanteri", icon: Tags },
  { to: "/admin/expired-labels", label: "Süresi Bitmiş Etiketler", icon: Clock3 },
  { to: "/admin/faulty-labels", label: "Hatalı Etiketler", icon: AlertTriangle },
  { to: "/admin/canceled-labels", label: "Müşteri İptalleri", icon: History },
  { to: "/admin/label-checks", label: "Etiket Kontrolleri", icon: ShieldCheck },
];

const adminNav: PanelNavItem[] = [
  { to: "/admin", label: "Kontrol paneli", icon: LayoutDashboard },
  { to: "/admin/customers", label: "Müşteriler", icon: Users },
  {
    to: "/admin/label-products",
    label: "Etiketler",
    icon: Package,
    match: ["/admin/labels", "/admin/label-products", "/admin/label-sources", "/admin/faulty-labels", "/admin/canceled-labels", "/admin/label-checks", "/admin/missing-labels"],
    children: adminLabelNav,
  },
  { to: "/admin/platforms", label: "Platformlar", icon: Store },
  { to: "/admin/amazon-v2", label: "Amazon V2", icon: Store },
  { to: "/admin/jtl-connect-health", label: "JTL bağlantı sağlığı", icon: ShieldCheck },
  { to: "/admin/billing", label: "Faturalama", icon: CreditCard },
  {
    to: "/admin/affiliates",
    label: "Affiliate ve kazançlar",
    icon: HandCoins,
    match: ["/admin/commissions"],
    children: [
      { to: "/admin/affiliates", label: "Referans bağlantıları", icon: Users },
      { to: "/admin/commissions", label: "Komisyon ve bonus", icon: Wallet },
    ],
  },
  { to: "/admin/orders", label: "Siparişler", icon: ShoppingCart },
  { to: "/admin/shipments", label: "Gönderiler", icon: Truck },
  { to: "/admin/reports", label: "Raporlar", icon: BarChart3 },
  { to: "/admin/support", label: "Destek", icon: MessageCircle },
  { to: "/admin/activity-logs", label: "İşlem Logları", icon: History },
  { to: "/admin/profile", label: "Profil", icon: User },
];

const customerNav: PanelNavItem[] = [
  { to: "/customer", label: "Genel Bakış", icon: LayoutDashboard },
  { to: "/customer/reports/shipping-health", label: "Kargo Sağlığı", icon: ShieldCheck },
  { to: "/customer/stores", label: "Mağazalar", icon: Store, ownerOnly: true },
  { to: "/customer/orders", label: "Siparişler", icon: ShoppingCart },
  { to: "/customer/create-label", label: "Etiket oluştur", icon: PlusSquare },
  { to: "/customer/invoices", label: "Faturalar", icon: FileText, ownerOnly: true },
  { to: "/customer/affiliate", label: "Referans ve kazanç", icon: HandCoins, ownerOnly: true },
  { to: "/customer/buy-labels", label: "Etiket satın al", icon: CreditCard, ownerOnly: true },
  { to: "/customer/balances", label: "Etiket bakiyeleri", icon: Wallet, ownerOnly: true },
  { to: "/customer/shipments", label: "Gönderiler", icon: Send },
  { to: "/customer/label-inventory", label: "Etiket envanteri", icon: Tags },
  { to: "/customer/api", label: "API", icon: KeyRound, ownerOnly: true },
  { to: "/customer/reports", label: "Raporlar", icon: BarChart3, exclude: ["/customer/reports/shipping-health"] },
];

const customerAccountNav: PanelNavItem[] = [
  { to: "/customer/profile", label: "Profil", icon: User },
  { to: "/customer/team", label: "Ekip yönetimi", icon: Users, ownerOnly: true },
  { to: "/customer/support", label: "Bize ulaşın", icon: MessageCircle },
];

function navPathMatches(pathname: string, item: PanelNavItem, basePath?: string) {
  if ((item.exclude || []).some((target) => pathname === target || pathname.startsWith(`${target}/`))) return false;
  const targets = [item.to, ...(item.match || []), ...(item.children || []).flatMap((child) => [child.to, ...(child.match || [])])];
  return targets.some((target) => (target === basePath ? pathname === target : pathname === target || pathname.startsWith(`${target}/`)));
}

const internalOrderSyncPlatforms: OrderSyncPlatform[] = [
  "afterbuy",
  "amazon",
  "etsy",
  "shopify",
  "shopware",
  "billbee",
  "aboutyou",
  "otto",
  "woocommerce",
  "kaufland",
  "opencart",
  "plenty",
];

const orderSyncIntervalOptions = [
  { label: "5 dakikada bir", value: 5 },
  { label: "10 dakikada bir", value: 10 },
  { label: "15 dakikada bir", value: 15 },
  { label: "30 dakikada bir", value: 30 },
  { label: "Saatte bir", value: 60 },
  { label: "3 saatte bir", value: 180 },
  { label: "6 saatte bir", value: 360 },
  { label: "12 saatte bir", value: 720 },
  { label: "Günde bir", value: 1440 },
];

const plentySyncRangeOptions = [
  { label: "Son 1 gün", value: "1d" },
  { label: "Son 3 gün", value: "3d" },
  { label: "Son 7 gün", value: "7d" },
  { label: "Son 30 gün", value: "30d" },
  { label: "Son 3 ay", value: "3m" },
  { label: "Tüm geçmiş", value: "all" },
];

const woocommerceCountryOptions = [
  { code: "DE", label: "Almanya" },
  { code: "AT", label: "Avusturya" },
  { code: "BE", label: "Belçika" },
  { code: "NL", label: "Hollanda" },
  { code: "FR", label: "Fransa" },
  { code: "LU", label: "Lüksemburg" },
  { code: "CH", label: "İsviçre" },
  { code: "PL", label: "Polonya" },
  { code: "CZ", label: "Çekya" },
  { code: "DK", label: "Danimarka" },
  { code: "SE", label: "İsveç" },
  { code: "NO", label: "Norveç" },
  { code: "FI", label: "Finlandiya" },
  { code: "IT", label: "İtalya" },
  { code: "ES", label: "İspanya" },
  { code: "PT", label: "Portekiz" },
  { code: "IE", label: "İrlanda" },
  { code: "GB", label: "Birleşik Krallık" },
  { code: "GR", label: "Yunanistan" },
  { code: "HU", label: "Macaristan" },
  { code: "RO", label: "Romanya" },
  { code: "BG", label: "Bulgaristan" },
  { code: "HR", label: "Hırvatistan" },
  { code: "SI", label: "Slovenya" },
  { code: "SK", label: "Slovakya" },
  { code: "EE", label: "Estonya" },
  { code: "LV", label: "Letonya" },
  { code: "LT", label: "Litvanya" },
  { code: "TR", label: "Türkiye" },
];

function WooCommerceCountryFilterFields({
  countryCode,
  enabled,
  onCountryCodeChange,
  onEnabledChange,
}: {
  countryCode: string;
  enabled: boolean;
  onCountryCodeChange: (value: string) => void;
  onEnabledChange: (value: boolean) => void;
}) {
  const normalizedCountryCode = countryCode.trim().toUpperCase() || "DE";
  const knownCountry = woocommerceCountryOptions.some((option) => option.code === normalizedCountryCode);
  return (
    <div className="rounded-2xl border border-violet-100 bg-violet-50/60 p-4">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_240px] md:items-end">
        <label className="flex items-start justify-between gap-4 rounded-xl border border-violet-100 bg-white px-4 py-3">
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-900">Yalnızca seçili ülkenin siparişlerini getir</span>
            <span className="mt-1 block text-sm leading-6 text-slate-600">
              Kapalıyken WooCommerce mağazasındaki tüm ülkelerin siparişleri alınır.
            </span>
          </span>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => onEnabledChange(event.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300"
          />
        </label>
        <label className="block space-y-1.5">
          <span className="text-sm font-semibold text-slate-700">Sipariş ülkesi</span>
          <select
            value={normalizedCountryCode}
            onChange={(event) => onCountryCodeChange(event.target.value)}
            disabled={!enabled}
            className="h-11 w-full rounded-xl border border-violet-100 bg-white px-3 text-sm text-slate-950 outline-none transition focus:border-violet-300 focus:ring-4 focus:ring-violet-100 disabled:bg-slate-100 disabled:text-slate-400"
          >
            {!knownCountry ? <option value={normalizedCountryCode}>{normalizedCountryCode}</option> : null}
            {woocommerceCountryOptions.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label} ({option.code})
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

const storePlatformOptions: Array<{ label: string; value: StorePlatform }> = [
  { value: "manual", label: "Manuel" },
  { value: "afterbuy", label: "Afterbuy" },
  { value: "shopify", label: "Shopify" },
  { value: "shopware", label: "Shopware" },
  { value: "billbee", label: "Billbee" },
  { value: "aboutyou", label: "AboutYou" },
  { value: "amazon", label: "Amazon" },
  { value: "amazon_v2", label: "Amazon V2" },
  { value: "etsy", label: "Etsy" },
  { value: "ebay", label: "eBay" },
  { value: "otto", label: "OTTO" },
  { value: "jtl", label: "JTL" },
  { value: "woocommerce", label: "WooCommerce" },
  { value: "kaufland", label: "Kaufland" },
  { value: "opencart", label: "OpenCart" },
  { value: "plenty", label: "PlentyONE" },
];

const customerStoreIntegrations: Array<{ label: string; detail: string; platform: StorePlatform; href?: string; to?: string }> = [
  { label: "Afterbuy", detail: "Sipariş alma ve takip numarası geri yazma", platform: "afterbuy", to: "/customer/stores/afterbuy/new" },
  { label: "Shopify", detail: "Resmi Shopify uygulamasını kurun", platform: "shopify", href: "https://apps.shopify.com/connector-for-kenpost" },
  { label: "Shopware", detail: "Sipariş alma ve takip numarası geri yazma", platform: "shopware", to: "/customer/stores/shopware/new" },
  { label: "Billbee", detail: "Billbee siparişlerini içe aktarın", platform: "billbee", to: "/customer/stores/billbee/new" },
  { label: "OTTO", detail: "OTTO mağaza bağlantısı", platform: "otto", to: "/customer/stores/otto/new" },
  { label: "About You", detail: "About You sipariş bağlantısı", platform: "aboutyou", to: "/customer/stores/aboutyou/new" },
  { label: "Amazon V2", detail: "Amazon siparişleri ve kargo takibi", platform: "amazon_v2", to: "/customer/stores/amazon-v2/new" },
  { label: "Etsy", detail: "Browser JSON sipariş kaynağı", platform: "etsy", to: "/customer/stores/etsy/new" },
  { label: "eBay", detail: "Güvenli eBay bağlantısı", platform: "ebay", href: "/api/ebay/connect?returnUrl=/customer/stores" },
  { label: "JTL", detail: "JTL Connect · veritabanı şifresi gerektirmez", platform: "jtl", to: "/customer/jtl-connect" },
  { label: "WooCommerce", detail: "WooCommerce mağazanızı bağlayın", platform: "woocommerce", to: "/customer/stores/woocommerce/new" },
  { label: "Kaufland", detail: "Kaufland marketplace bağlantısı", platform: "kaufland", to: "/customer/stores/kaufland/new" },
  { label: "OpenCart", detail: "OpenCart mağaza bağlantısı", platform: "opencart", to: "/customer/stores/opencart/new" },
  { label: "PlentyONE", detail: "Sipariş alma ve kargo takibini geri yazma", platform: "plenty", to: "/customer/stores/plenty/new" },
];

type StoreConnectionProfile = {
  caption: string;
  fieldHelp?: Record<string, string>;
  fieldPlaceholder?: Record<string, string>;
  requirements: string[];
  steps: string[];
  submitLabel: string;
  summary: Array<{ label: string; value: string }>;
};

const defaultStoreConnectionProfile: StoreConnectionProfile = {
  caption: "Mağaza bilgilerini ekleyin; sipariş senkronu mağaza kaydından sonra yönetilir.",
  requirements: ["Mağaza adı", "Platform bağlantı bilgisi"],
  steps: ["Mağaza adını girin.", "Platform bilgilerini doldurun.", "Mağazayı oluşturup ilk senkronu başlatın."],
  submitLabel: "Mağaza oluştur",
  summary: [
    { label: "Bağlantı", value: "Manuel" },
    { label: "Sipariş", value: "İsteğe bağlı" },
    { label: "Takip", value: "Panelden" },
  ],
};

const storeConnectionProfiles: Partial<Record<StorePlatform, StoreConnectionProfile>> = {
  plenty: {
    caption: "PlentyONE REST API ile siparişleri alın; KenPost'ta oluşturulan kargo takip bilgisini PlentyONE'a geri yazın.",
    fieldHelp: {
      plentySystemId: "PlentyONE alan adındaki p ile başlayan sistem numarası (ör. p64513 için 64513).",
      plentyUsername: "REST API erişimi olan PlentyONE kullanıcı adı.",
      plentyPassword: "PlentyONE REST API kullanıcısının şifresi.",
    },
    fieldPlaceholder: {
      plentySystemId: "ör. 64513",
      plentyUsername: "API kullanıcı adı",
      plentyPassword: "API şifresi",
    },
    requirements: ["PlentyONE System-ID", "REST API kullanıcı adı", "REST API şifresi", "Sipariş okuma ve kargo yazma yetkileri"],
    steps: ["PlentyONE API kullanıcısının yetkilerini kontrol edin.", "System-ID ve API kullanıcı bilgilerini girin.", "Mağaza oluşunca ilk sipariş senkronunu başlatın."],
    submitLabel: "PlentyONE mağazasını bağla",
    summary: [
      { label: "Bağlantı", value: "PlentyONE REST API" },
      { label: "Sipariş", value: "Otomatik senkron" },
      { label: "Takip", value: "Paket ve gönderim kaydı" },
    ],
  },
  afterbuy: {
    caption: "Afterbuy XML arayüzüyle siparişlerinizi alın ve oluşturulan takip numaralarını Afterbuy'a geri yazın.",
    fieldHelp: {
      afterbuyAccountName: "Afterbuy hesabında görünen kullanıcı/hesap adı.",
      afterbuyPartnerToken: "KenPost için oluşturulan XML-Schnittstelle PartnerToken değeri.",
      afterbuyAccountToken: "Afterbuy ekranında UserToken veya AccountToken olarak gösterilen değer.",
    },
    fieldPlaceholder: {
      afterbuyAccountName: "ör. bucomberlin",
      afterbuyPartnerToken: "PartnerToken",
      afterbuyAccountToken: "AccountToken / UserToken",
    },
    requirements: ["Afterbuy hesap adı", "XML-Schnittstelle PartnerToken", "AccountToken / UserToken"],
    steps: ["Afterbuy'da XML-Schnittstelle erişimini açın.", "KenPost için verilen tokenları girin.", "Bağlantı doğrulandıktan sonra ilk sipariş senkronunu başlatın."],
    submitLabel: "Afterbuy mağazasını bağla",
    summary: [
      { label: "Bağlantı", value: "Afterbuy XML API" },
      { label: "Sipariş", value: "Otomatik senkron" },
      { label: "Takip", value: "Afterbuy'a geri yazma" },
    ],
  },
  shopify: {
    caption: "Bu form mevcut KenPost müşteri panelindeki eski harici Shopify connector akışı içindir. App Store'daki KenPost Billing Bridge Shopify Admin içinde kurulum izinleri, App Bridge ve Shopify API ile çalışır.",
    fieldHelp: {
      shopDomain: "Örnek: mystore.myshopify.com",
      shopifyAccessToken: "Mevcut müşteri connector erişim bilgisi. Orders ve fulfillment izinleri açık olmalı.",
      shopifyClientId: "Varsa uygulama/client ID bilgisini ekleyin.",
    },
    fieldPlaceholder: {
      shopDomain: "mystore.myshopify.com",
      shopifyAccessToken: "Connector erişim bilgisi",
      shopifyClientId: "Opsiyonel",
    },
    requirements: ["Bu form yalnızca mevcut KenPost müşteri connector'ı içindir", "myshopify.com domaini", "Connector erişim bilgisi", "Orders okuma izni", "Fulfillment geri yazma izni"],
    steps: ["KenPost müşteri panelindeki harici connector için mağaza erişim bilgisini hazırlayın.", "Domain ve erişim bilgisini bu forma girin.", "Mağaza oluşunca sipariş senkronunu başlatın."],
    submitLabel: "Shopify mağazasını bağla",
    summary: [
      { label: "Bağlantı", value: "Admin API" },
      { label: "Sipariş", value: "Otomatik senkron" },
      { label: "Takip", value: "Fulfillment" },
    ],
  },
  shopware: {
    caption: "Shopware 6 Admin API ile siparişlerinizi alın ve oluşturulan takip numaralarını doğru teslimata geri yazın.",
    fieldHelp: {
      shopwareStoreUrl: "Shopware mağazanızın ana adresi. /api eklemeyin.",
      shopwareClientId: "Shopware Yönetim > Entegrasyonlar bölümündeki Access Key ID.",
      shopwareClientSecret: "Aynı entegrasyona ait Secret Access Key. Bu değer ekranda tekrar gösterilmez.",
    },
    fieldPlaceholder: {
      shopwareStoreUrl: "https://shop.example.de",
      shopwareClientId: "Access Key ID",
      shopwareClientSecret: "Secret Access Key",
    },
    requirements: ["Shopware 6 mağaza URL'si", "Admin API Access Key ID", "Secret Access Key", "Sipariş okuma ve teslimat güncelleme yetkisi"],
    steps: ["Shopware yönetiminde KenPost için bir entegrasyon oluşturun.", "URL, Access Key ID ve Secret Access Key bilgilerini girin.", "Bağlantı doğrulandıktan sonra 30 dakikalık sipariş senkronunu başlatın."],
    submitLabel: "Shopware mağazasını bağla",
    summary: [
      { label: "Bağlantı", value: "Admin API / OAuth" },
      { label: "Sipariş", value: "30 dakikada bir" },
      { label: "Takip", value: "Teslimata geri yazma" },
    ],
  },
  billbee: {
    caption: "Billbee API bilgileriyle siparişlerinizi içe aktarın ve mağaza bazlı senkron yönetin.",
    fieldHelp: {
      billbeeName: "Panelde görünecek Billbee bağlantı adı.",
      billbeeApiKey: "Billbee API anahtarı.",
      billbeeApiUser: "Billbee API kullanıcı adı.",
      billbeeApiPassword: "Billbee API şifresi.",
    },
    requirements: ["Billbee bağlantı adı", "API key", "API kullanıcı", "API şifre"],
    steps: ["Billbee API erişimini açın.", "API key ve kullanıcı bilgilerini girin.", "Mağazayı oluşturup siparişleri senkronlayın."],
    submitLabel: "Billbee mağazasını bağla",
    summary: [
      { label: "Bağlantı", value: "Billbee API" },
      { label: "Sipariş", value: "İçe aktarım" },
      { label: "Takip", value: "Panelden" },
    ],
  },
  aboutyou: {
    caption: "About You bağlantısını API key ile ekleyin; sipariş çekme akışı mağaza ekranından çalışır.",
    requirements: ["About You adı", "API anahtarı"],
    steps: ["API erişim bilgisini hazırlayın.", "Bağlantı adı ve API key bilgisini girin.", "Mağazayı oluşturun."],
    submitLabel: "About You mağazasını bağla",
    summary: [
      { label: "Bağlantı", value: "API key" },
      { label: "Sipariş", value: "Senkron" },
      { label: "Takip", value: "Panelden" },
    ],
  },
  otto: {
    caption: "OTTO Partner Connect client bilgileriyle mağaza bağlantısını oluşturun.",
    fieldHelp: {
      ottoName: "Müşterinin göreceği OTTO mağaza adı.",
      ottoClientId: "OTTO Partner Connect Client ID.",
      ottoClientSecret: "OTTO Partner Connect Client Secret.",
    },
    requirements: ["OTTO mağaza adı", "Client ID", "Client Secret"],
    steps: ["OTTO Partner Connect hesabında client bilgilerini açın.", "Client ID ve Secret alanlarını doldurun.", "Mağazayı oluşturup senkronu çalıştırın."],
    submitLabel: "OTTO mağazasını bağla",
    summary: [
      { label: "Bağlantı", value: "Partner Connect" },
      { label: "Sipariş", value: "Otomatik senkron" },
      { label: "Takip", value: "Sipariş API" },
    ],
  },
  jtl: {
    caption: "JTL için platform bağlantısı önceliklidir; IP/DB modu yalnızca özel kurulumlarda kullanılır.",
    fieldHelp: {
      jtlStoreUrl: "JTL bağlantı URL'i. Örnek: https://shop.example.com",
      jtlClientId: "JTL platform bağlantısı Client ID.",
      jtlClientSecret: "JTL platform bağlantısı Client Secret.",
      jtlDbHost: "Yalnızca IP/DB kurulumunda gerekli.",
      jtlDbPort: "MSSQL portu. Genellikle 1433.",
      jtlDbName: "JTL veritabanı adı.",
      jtlDbUser: "Veritabanı kullanıcısı.",
      jtlDbPassword: "Veritabanı şifresi.",
    },
    fieldPlaceholder: {
      jtlStoreUrl: "https://...",
      jtlDbPort: "1433",
    },
    requirements: ["JTL URL", "Client ID", "Client Secret", "Opsiyonel IP/DB bilgileri"],
    steps: ["Önce Platform Bağlantısı modunu deneyin.", "Özel kurulum gerekiyorsa IP/DB modunu seçin.", "Mağaza oluşunca bağlantıyı test edin."],
    submitLabel: "JTL mağazasını bağla",
    summary: [
      { label: "Bağlantı", value: "Platform / IP" },
      { label: "Sipariş", value: "JTL senkron" },
      { label: "Kurulum", value: "Panel bağlantısı" },
    ],
  },
  woocommerce: {
    caption: "WooCommerce REST API anahtarlarıyla mağazanızı bağlayın.",
    requirements: ["Mağaza URL", "Consumer Key", "Consumer Secret"],
    steps: ["WooCommerce REST API anahtarlarını oluşturun.", "URL ve anahtarları girin.", "Mağazayı oluşturup siparişleri çekin."],
    submitLabel: "WooCommerce mağazasını bağla",
    summary: [
      { label: "Bağlantı", value: "REST API" },
      { label: "Sipariş", value: "Otomatik senkron" },
      { label: "Takip", value: "Panelden" },
    ],
  },
  kaufland: {
    caption: "Kaufland marketplace bağlantısı için client key ve secret bilgilerini girin.",
    requirements: ["Client Key", "Secret Key", "Storefront"],
    steps: ["Kaufland API credentials alanından key alın.", "Storefront değerini kontrol edin.", "Mağaza kaydını oluşturun."],
    submitLabel: "Kaufland mağazasını bağla",
    summary: [
      { label: "Bağlantı", value: "Marketplace API" },
      { label: "Sipariş", value: "Otomatik senkron" },
      { label: "Ülke", value: "Storefront" },
    ],
  },
  opencart: {
    caption: "OpenCart mağazanızı admin erişimi veya API anahtarı/beyaz IP yöntemiyle bağlayın.",
    fieldHelp: {
      opencartAdminUrl: "API modu için mağaza URL'i de yeterlidir; admin modu için /admin adresini girin. Örnek: https://site.com/admin",
      opencartUsername: "API modunda OpenCart API kullanıcı adı; admin modunda admin kullanıcısı.",
      opencartPassword: "API modunda OpenCart API anahtarı; admin modunda admin şifresi.",
    },
    fieldPlaceholder: {
      opencartAdminUrl: "https://site.com/admin veya https://site.com",
      opencartUsername: "API kullanıcı adı",
      opencartPassword: "API anahtarı veya admin şifresi",
    },
    requirements: ["Mağaza/Admin URL", "API kullanıcı adı veya admin kullanıcı", "API anahtarı veya admin şifre", "Gerekirse KenPost sabit IP whitelist"],
    steps: [
      "Önce API anahtarı / beyaz IP modunu deneyin.",
      "OpenCart API kullanıcısı ve anahtarı girin.",
      "Sipariş listesi için admin erişimi gerekiyorsa admin moduna geçin.",
    ],
    submitLabel: "OpenCart mağazasını bağla",
    summary: [
      { label: "Bağlantı", value: "Admin / API" },
      { label: "Sipariş", value: "OpenCart senkron" },
      { label: "IP", value: "Relay destekli" },
    ],
  },
};

type AuthLocale = "tr" | "en" | "de";

type AuthCopy = {
  languageLabel: string;
  brand: {
    tagline: string;
    heading: string;
    body: string;
    featuresLabel: string;
    marketplacesLabel: string;
    secureNote: string;
  };
  features: Array<{ label: string; detail: string }>;
  signIn: {
    title: string;
    caption: string;
    recognizedTitle: string;
    recognizedCaption: string;
    useAccount: string;
    useAnotherAccount: string;
    removeAccount: string;
    removeFailed: string;
    recognizedError: string;
    backToAccounts: string;
    checkingAccounts: string;
    forgot: string;
    submit: string;
    submitting: string;
    noAccount: string;
    register: string;
    rememberMe: string;
    genericError: string;
  };
  signUp: {
    title: string;
    caption: string;
    submit: string;
    haveAccount: string;
    signIn: string;
    genericError: string;
  };
  twoFactor: {
    title: string;
    caption: string;
    accountLabel: string;
    unknownAccount: string;
    codeLabel: string;
    missingChallenge: string;
    invalidCode: string;
    newCodeSent: string;
    resendFailed: string;
    submit: string;
    resend: string;
    resending: string;
    backToSignIn: string;
  };
  forgotPassword: {
    title: string;
    caption: string;
    success: string;
    submit: string;
    genericError: string;
    backToSignIn: string;
  };
  resetPassword: {
    title: string;
    caption: string;
    invalidToken: string;
    minLength: string;
    mismatch: string;
    success: string;
    submit: string;
    genericError: string;
  };
  verifyEmail: {
    title: string;
    caption: string;
    missingCode: string;
    genericError: string;
    checking: string;
    success: string;
    invalid: string;
    backToSignIn: string;
  };
  verifyEmailSent: {
    title: string;
    caption: string;
    successWithEmail: (email: string) => string;
    success: string;
    emailLabel: string;
    resend: string;
    resending: string;
    resendFailed: string;
    resent: string;
    signIn: string;
    backToSignUp: string;
  };
  fields: {
    email: string;
    password: string;
    phone: string;
    company: string;
    referralCode: string;
    newPassword: string;
    confirmPassword: string;
  };
  passwordToggle: {
    show: string;
    hide: string;
  };
};

const AUTH_LOCALE_STORAGE_KEY = "kenpost_locale";
const AUTH_LOCALE_COOKIE_NAME = "kenpost_locale";
const AUTH_REMEMBER_EMAIL_STORAGE_KEY = "kenpost_remembered_email";
const PANEL_TRANSLATABLE_ATTRIBUTES = ["aria-label", "alt", "placeholder", "title"] as const;

const authLocales: Array<{ code: AuthLocale; label: string; short: string }> = [
  { code: "tr", label: "Türkçe", short: "TR" },
  { code: "en", label: "English", short: "EN" },
  { code: "de", label: "Deutsch", short: "DE" },
];

const authMarketplaceTiles: Array<{ label: string; logoSrc: string; logoClassName?: string }> = [
  { label: "Afterbuy", logoSrc: "/logos/platforms/afterbuy.svg" },
  { label: "Shopify", logoSrc: "/logos/platforms/shopify.svg" },
  { label: "Shopware", logoSrc: "/logos/platforms/shopware.svg" },
  { label: "eBay", logoSrc: "/logos/platforms/ebay.svg" },
  { label: "Amazon", logoSrc: "/logos/platforms/amazon.png", logoClassName: "max-h-7" },
  { label: "Etsy", logoSrc: "/logos/platforms/etsy.svg" },
  { label: "WooCommerce", logoSrc: "/logos/platforms/woocommerce.svg" },
  { label: "Kaufland", logoSrc: "/logos/platforms/kaufland.svg" },
  { label: "Billbee", logoSrc: "/logos/platforms/billbee.svg" },
  { label: "OTTO", logoSrc: "/logos/platforms/otto.svg" },
  { label: "About You", logoSrc: "/logos/platforms/aboutyou.png" },
  { label: "OpenCart", logoSrc: "/logos/platforms/opencart.svg" },
  { label: "JTL", logoSrc: "/logos/platforms/jtl.svg", logoClassName: "max-h-8" },
  { label: "PlentyONE", logoSrc: "/logos/platforms/plentyone.svg" },
];

const KENPOST_LOGO_SRC = "/kenpost-logo-transparent.png";
const KENPOST_LOGO_DARK_SRC = "/kenpost-logo-dark.png";
const KENPOST_VERSION_LABEL = getCurrentProductVersionLabel();
const KENPOST_BUILD_VERSION = __KENPOST_APP_VERSION__;
const APP_VERSION_CHECK_INTERVAL_MS = 60 * 1000;

const localeTags: Record<AuthLocale, string> = {
  tr: "tr-TR",
  en: "en-GB",
  de: "de-DE",
};
const DEFAULT_SEO_TITLE = "KenPost | Kargo etiketi ve sipariş yönetimi";
const DEFAULT_SEO_DESCRIPTION =
  "KenPost ile e-ticaret kargo etiketlerini, siparişleri, müşteri hesaplarını ve pazar yeri bağlantılarını tek panelde yönetin.";
const APP_PUBLIC_ORIGIN = "https://app.kenpost.de";
const PUBLIC_SUPPORT_EMAIL = "hakan.olcer@web.de";

const authSeoTitles: Record<AuthLocale, Record<string, string>> = {
  tr: {
    "/sign-in": "Giriş yap | KenPost",
    "/sign-up": "Kayıt ol | KenPost",
    "/forgot-password": "Şifre sıfırla | KenPost",
    "/reset-password": "Yeni şifre | KenPost",
    "/email-two-factor": "E-posta doğrulama | KenPost",
    "/verify-email": "E-posta doğrulama | KenPost",
    "/verify-email-sent": "E-postanızı kontrol edin | KenPost",
  },
  en: {
    "/sign-in": "Sign in | KenPost",
    "/sign-up": "Create account | KenPost",
    "/forgot-password": "Reset password | KenPost",
    "/reset-password": "New password | KenPost",
    "/email-two-factor": "Email verification | KenPost",
    "/verify-email": "Email verification | KenPost",
    "/verify-email-sent": "Check your email | KenPost",
  },
  de: {
    "/sign-in": "Anmelden | KenPost",
    "/sign-up": "Registrieren | KenPost",
    "/forgot-password": "Passwort zurücksetzen | KenPost",
    "/reset-password": "Neues Passwort | KenPost",
    "/email-two-factor": "E-Mail-Verifizierung | KenPost",
    "/verify-email": "E-Mail-Verifizierung | KenPost",
    "/verify-email-sent": "E-Mail prüfen | KenPost",
  },
};

const publicInfoPageKeys = ["support", "privacy", "faq", "changelog", "docs", "pricing"] as const;
type PublicInfoPageKey = (typeof publicInfoPageKeys)[number];

const authPublicLinks = [
  { key: "support", path: "/support" },
  { key: "privacy", path: "/privacy" },
  { key: "faq", path: "/faq" },
  { key: "docs", path: "/docs" },
  { key: "pricing", path: "/pricing" },
  { key: "changelog", path: "/changelog" },
] as const;

type AuthPublicLinkKey = (typeof authPublicLinks)[number]["key"];

const authPublicNavCopy: Record<AuthLocale, { ariaLabel: string; links: Record<AuthPublicLinkKey, string> }> = {
  tr: {
    ariaLabel: "Resmi sayfalar",
    links: {
      support: "Destek",
      privacy: "Gizlilik",
      faq: "SSS",
      docs: "Dokümanlar",
      pricing: "Fiyatlandırma",
      changelog: "Değişiklikler",
    },
  },
  en: {
    ariaLabel: "Official pages",
    links: {
      support: "Support",
      privacy: "Privacy",
      faq: "FAQ",
      docs: "Docs",
      pricing: "Pricing",
      changelog: "Changelog",
    },
  },
  de: {
    ariaLabel: "Offizielle Seiten",
    links: {
      support: "Support",
      privacy: "Datenschutz",
      faq: "FAQ",
      docs: "Dokumentation",
      pricing: "Preise",
      changelog: "Änderungen",
    },
  },
};

type PublicInfoSection = {
  body?: string;
  items?: string[];
  title: string;
};

type PublicInfoPage = {
  cta?: { href: string; label: string; secondaryHref?: string; secondaryLabel?: string };
  description: string;
  eyebrow: string;
  highlights?: string[];
  icon: typeof MessageCircle;
  navLabel: string;
  path: string;
  sections: PublicInfoSection[];
  title: string;
  updated: string;
};

const publicInfoPages: Record<PublicInfoPageKey, PublicInfoPage> = {
  support: {
    cta: {
      href: `mailto:${PUBLIC_SUPPORT_EMAIL}?subject=KenPost%20Billing%20Bridge%20support`,
      label: "Email support",
      secondaryHref: "/docs",
      secondaryLabel: "Open docs",
    },
    description:
      "Contact KenPost support for the embedded Shopify API operations console, live store-data visibility, API health, payment-status review, and App Store review questions.",
    eyebrow: "Support",
    highlights: ["Support email", PUBLIC_SUPPORT_EMAIL, "Operations and API help"],
    icon: MessageCircle,
    navLabel: "Support",
    path: "/support",
    sections: [
      {
        title: "How to reach us",
        body: `For app support, pricing questions, or review access questions, email ${PUBLIC_SUPPORT_EMAIL}. Include your Shopify store domain, KenPost account email, and the order, request, or log reference if the request is about an operational record.`,
      },
      {
        title: "What we can help with",
        items: [
          "Embedded Shopify app home access, App Bridge session checks, and token exchange diagnostics.",
          "Shopify Admin API order and product visibility for the currently installed merchant store.",
          "Confirmation that KenPost Billing Bridge does not require a custom app Admin API token.",
          "Webhook delivery, app permission, payment-status review, and product/API visibility questions.",
        ],
      },
      {
        title: "Review access",
        body:
          "If Shopify needs a review account or a test scenario, contact support with the app name and review reference. We can provide the right KenPost test context without exposing private merchant data.",
      },
    ],
    title: "KenPost Billing Bridge Support",
    updated: "June 9, 2026",
  },
  privacy: {
    cta: {
      href: `mailto:${PUBLIC_SUPPORT_EMAIL}?subject=KenPost%20Billing%20Bridge%20privacy`,
      label: "Privacy contact",
      secondaryHref: "/support",
      secondaryLabel: "Support",
    },
    description:
      "Privacy policy for KenPost Billing Bridge, covering Shopify store data loaded through installed app permissions, support, diagnostics, and operational records.",
    eyebrow: "Privacy",
    highlights: ["No sale of personal data", "Shopify API data only for operations", "Contact: hakan.olcer@web.de"],
    icon: ShieldCheck,
    navLabel: "Privacy",
    path: "/privacy",
    sections: [
      {
        title: "Data we process",
        items: [
          "Merchant and user account details such as name, company, email address, and contact information.",
          "Shopify store, order, product, inventory, and payment-status data loaded through the installed Shopify app permissions.",
          "Payment-status review, request, and operational records needed to operate the embedded Shopify Admin console.",
          "Support requests, technical diagnostics, webhook delivery details, and security logs needed to operate and secure the app.",
        ],
      },
      {
        title: "Why we use the data",
        items: [
          "To show the current merchant store's Shopify orders, products, inventory, and payment-status signals inside Shopify Admin.",
          "To compare payment-status and operational signals against Shopify order status without asking merchants for custom app tokens.",
          "To keep payment-status review, support, and operational records consistent.",
          "To investigate failed webhooks, answer support requests, prevent duplicate processing, and secure the service.",
        ],
      },
      {
        title: "Service providers",
        body:
          "KenPost uses infrastructure and communication providers such as Cloudflare, database/storage services, Shopify APIs, and email delivery providers to run the app. These providers are used only for the operational purposes described here.",
      },
      {
        title: "Retention and deletion",
        body:
          "App-plan, support, and operational records are retained only as long as needed for operation, security, legal, and audit purposes. Deletion requests can be sent to the privacy contact.",
      },
      {
        title: "Contact",
        body: `For privacy questions, access requests, or deletion requests, contact ${PUBLIC_SUPPORT_EMAIL}.`,
      },
    ],
    title: "Privacy Policy",
    updated: "June 9, 2026",
  },
  faq: {
    cta: {
      href: "/docs",
      label: "Read docs",
      secondaryHref: "/support",
      secondaryLabel: "Contact support",
    },
    description:
      "Frequently asked questions about KenPost Billing Bridge, the embedded Shopify API operations console, live store data, requests, and payment-status review.",
    eyebrow: "FAQ",
    highlights: ["Shopify API app home", "Live store data", "No custom app token"],
    icon: FileText,
    navLabel: "FAQ",
    path: "/faq",
    sections: [
      {
        title: "What does KenPost Billing Bridge do?",
        body:
          "The app opens a compact Shopify Admin console for the installed store's operations team. It uses the installed Shopify app permissions to load the current merchant store's orders, products, payment-status signals, and API health. It does not show preloaded KenPost sample data and it does not send reviewers to the customer purchase screen.",
      },
      {
        title: "Does the app ask for a custom Shopify Admin API token?",
        body:
          "No. The embedded App Store app uses Shopify App Bridge session tokens, token exchange, and Shopify Admin GraphQL API. Reviewers and merchants do not need to provide a custom Shopify Admin API token.",
      },
      {
        title: "Does the app create shipping labels?",
        body:
          "No. The Shopify app home focuses on monitoring Shopify API data, payment status, requests, products, and operational health. It is not a carrier label generator or checkout storefront.",
      },
      {
        title: "What happens if the review store has no orders?",
        body:
          "The app still loads live Shopify store and product data through the Admin API and shows an empty order state for that store. It never falls back to preloaded KenPost data.",
      },
    ],
    title: "Frequently Asked Questions",
    updated: "June 9, 2026",
  },
  changelog: {
    cta: {
      href: "/versions",
      label: "Product versions",
      secondaryHref: "/docs",
      secondaryLabel: "Docs",
    },
    description:
      "Public changelog for the KenPost Billing Bridge app and related Shopify API operations-console improvements.",
    eyebrow: "Changelog",
    highlights: ["App Store resubmission", "Shopify API operations", "June 10, 2026"],
    icon: History,
    navLabel: "Changelog",
    path: "/changelog",
    sections: [
      {
        title: "June 10, 2026",
        items: [
          "The Shopify embedded app home was updated to use Shopify App Bridge session tokens, token exchange, and Shopify Admin GraphQL API.",
          "The app now requests `read_orders` and `read_products` so the console can load the current merchant store's Shopify data instead of preloaded KenPost data.",
          "The submitted Shopify app home was separated from older KenPost customer connector routes so reviewers are not asked for a custom app Admin API token.",
          "The public review screencast and screenshots were replaced to show the Shopify API operations dashboard, order table, product/API records, and health signals.",
        ],
      },
      {
        title: "June 9, 2026",
        items: [
          "Public support, privacy, FAQ, documentation, pricing, and changelog pages were added for App Store review links.",
          "The Shopify app home was separated from the KenPost customer panel and now opens an embedded operations console.",
          "The app listing support email was aligned to hakan.olcer@web.de.",
          "The App Store pricing direction was set to one fixed monthly plan at EUR 99.",
        ],
      },
    ],
    title: "Changelog",
    updated: "June 9, 2026",
  },
  docs: {
    cta: {
      href: "/support",
      label: "Get support",
      secondaryHref: "/faq",
      secondaryLabel: "FAQ",
    },
    description:
      "Documentation for KenPost Billing Bridge's embedded Shopify app home, Shopify Admin API data flow, request visibility, payment-status review, and operational health.",
    eyebrow: "Documentation",
    highlights: ["Embedded app home", "Shopify Admin API", "No custom token"],
    icon: FileText,
    navLabel: "Docs",
    path: "/docs",
    sections: [
      {
        title: "Overview",
        body:
          "KenPost Billing Bridge is the Shopify-side operations view for the installed merchant store. It is a standalone embedded operations console, not an embedded copy of the existing KenPost customer application. It loads that store's Shopify orders, products, payment-status signals, and API health through Shopify Admin GraphQL.",
      },
      {
        title: "Shopify app home flow",
        items: [
          "The store operations team opens the app from Shopify Admin.",
          "The embedded app home requests a Shopify App Bridge session token.",
          "The backend verifies the session token, exchanges it for a Shopify access token, and calls Shopify Admin GraphQL.",
          "The console shows the current store's recent orders, products, inventory signals, payment-status review rows, and API records.",
          "The app home does not redirect reviewers or merchants to the KenPost customer purchase panel.",
          "The app home does not ask reviewers for a custom app Admin API token.",
        ],
      },
      {
        title: "Payment-status review",
        items: [
          "The console highlights recent Shopify orders with pending, authorized, or partially paid financial statuses.",
          "The store operations team can compare those rows with product and API health signals in one embedded Shopify Admin view.",
          "If the review store has no matching orders, the app shows a true empty state instead of sample or preloaded rows.",
        ],
      },
      {
        title: "Operational notes",
        items: [
          "Use the Shopify app home to confirm Shopify API connectivity, recent orders, products, stock signals, and payment-status review records.",
          "Use support if an order does not appear, product data is missing, permissions need attention, or payment-status review needs investigation.",
        ],
      },
    ],
    title: "KenPost Billing Bridge Documentation",
    updated: "June 10, 2026",
  },
  pricing: {
    cta: {
      href: `mailto:${PUBLIC_SUPPORT_EMAIL}?subject=KenPost%20Billing%20Bridge%20pricing`,
      label: "Pricing questions",
      secondaryHref: "/privacy",
      secondaryLabel: "Privacy",
    },
    description:
      "Pricing for KenPost Billing Bridge: one fixed public plan at EUR 99 per month through Shopify App Store pricing for the embedded operations console.",
    eyebrow: "Pricing",
    highlights: ["EUR 99/month", "One public plan", "Operations console"],
    icon: CreditCard,
    navLabel: "Pricing",
    path: "/pricing",
    sections: [
      {
        title: "Plan",
        body:
          "KenPost Billing Bridge has one fixed monthly app plan: EUR 99 per month through Shopify App Store pricing. The plan provides access to the embedded Shopify API operations console for live store-data visibility, payment-status review, request visibility, product visibility, and operational health controls.",
      },
      {
        title: "What is included",
        items: [
          "Embedded Shopify app home for store operations.",
          "Live Shopify Admin API order, product, inventory, and payment-status visibility.",
          "Incoming request, API health, and operational log visibility.",
          "Embedded operational health checks for App Bridge, token exchange, and Shopify Admin GraphQL.",
          "Support through hakan.olcer@web.de.",
        ],
      },
    ],
    title: "Pricing",
    updated: "June 9, 2026",
  },
};

function publicInfoPageKey(pathname: string): PublicInfoPageKey | null {
  const normalized = pathname.replace(/^\/+/, "").replace(/\/+$/, "");
  return publicInfoPageKeys.includes(normalized as PublicInfoPageKey) ? (normalized as PublicInfoPageKey) : null;
}

function isPublicInfoPath(pathname: string) {
  return publicInfoPageKey(pathname) !== null;
}

type SeoState = {
  canonicalPath: string;
  description: string;
  robots: string;
  title: string;
};

function absoluteAppUrl(path: string) {
  const safePath = path.startsWith("/") ? path : `/${path}`;
  if (typeof window === "undefined") return `${APP_PUBLIC_ORIGIN}${safePath}`;
  return new URL(safePath, window.location.origin).toString();
}

function setMetaAttribute(attribute: "name" | "property", key: string, content: string) {
  if (typeof document === "undefined") return;
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonicalLink(href: string) {
  if (typeof document === "undefined") return;
  let element = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  element.href = href;
}

function resolveSeoState(pathname: string, auth: AuthState): SeoState {
  const locale = currentAuthLocale();
  const authPages: Record<string, { description: string; title: string; robots?: string }> = {
    "/sign-in": {
      title: "Giriş yap | KenPost",
      description: "KenPost hesabınıza giriş yapın; kargo etiketleri, siparişler ve mağaza bağlantılarını tek panelden yönetin.",
    },
    "/sign-up": {
      title: "Kayıt ol | KenPost",
      description: "KenPost hesabınızı oluşturun ve e-ticaret gönderilerinizi pazar yeri bağlantılarıyla tek panelde yönetin.",
    },
    "/forgot-password": {
      title: "Şifre sıfırla | KenPost",
      description: "KenPost hesabınız için güvenli şifre sıfırlama bağlantısı isteyin.",
      robots: "noindex,nofollow",
    },
    "/reset-password": {
      title: "Yeni şifre | KenPost",
      description: "KenPost hesabınız için yeni şifre belirleyin.",
      robots: "noindex,nofollow",
    },
    "/email-two-factor": {
      title: "E-posta doğrulama | KenPost",
      description: "KenPost girişinizi e-posta doğrulama koduyla tamamlayın.",
      robots: "noindex,nofollow",
    },
    "/verify-email": {
      title: "E-posta doğrulama | KenPost",
      description: "KenPost hesap e-postanızı doğrulayın.",
      robots: "noindex,nofollow",
    },
    "/verify-email-sent": {
      title: "E-postanızı kontrol edin | KenPost",
      description: "KenPost hesap doğrulama e-postası için gelen kutunuzu kontrol edin.",
      robots: "noindex,nofollow",
    },
  };

  const authPage = authPages[pathname];
  if (authPage) {
    return {
      canonicalPath: pathname,
      description: authPage.description,
      robots: authPage.robots || "index,follow",
      title: authSeoTitles[locale]?.[pathname] || authPage.title,
    };
  }

  const publicPageKey = publicInfoPageKey(pathname);
  if (publicPageKey) {
    const page = publicInfoPages[publicPageKey];
    return {
      canonicalPath: page.path,
      description: page.description,
      robots: "index,follow",
      title: `${page.title} | KenPost Billing Bridge`,
    };
  }

  if (pathname === "/versions") {
    return {
      canonicalPath: "/versions",
      description: "KenPost Version 2 sürüm notları, güvenlik iyileştirmeleri ve platform geçiş durumu.",
      robots: "noindex,follow",
      title: "Sürüm notları | KenPost",
    };
  }

  if (pathname.startsWith("/admin")) {
    return {
      canonicalPath: pathname,
      description: "KenPost yönetim paneli; müşteri, etiket, sipariş, fatura ve rapor operasyonları.",
      robots: "noindex,nofollow,noarchive",
      title: `${translatePanelText(currentPageTitle(pathname, "admin"), locale)} | KenPost Admin`,
    };
  }

  if (pathname.startsWith("/customer")) {
    const role = auth.status === "signed-in" && auth.user.role !== "admin" ? auth.user.role : "customer";
    return {
      canonicalPath: pathname,
      description: "KenPost müşteri paneli; mağaza bağlantıları, siparişler, etiket bakiyeleri ve gönderi yönetimi.",
      robots: "noindex,nofollow,noarchive",
      title: `${translatePanelText(currentPageTitle(pathname, role), locale)} | KenPost`,
    };
  }

  return {
    canonicalPath: "/sign-in",
    description: DEFAULT_SEO_DESCRIPTION,
    robots: "index,follow",
    title: DEFAULT_SEO_TITLE,
  };
}

function useDocumentMetadata(pathname: string, auth: AuthState) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const seo = resolveSeoState(pathname, auth);
    const canonicalUrl = absoluteAppUrl(seo.canonicalPath);
    const logoUrl = absoluteAppUrl(KENPOST_LOGO_SRC);

    document.title = seo.title;
    setMetaAttribute("name", "description", seo.description);
    setMetaAttribute("name", "robots", seo.robots);
    setMetaAttribute("name", "theme-color", "#26343d");
    setMetaAttribute("property", "og:site_name", "KenPost");
    setMetaAttribute("property", "og:type", "website");
    setMetaAttribute("property", "og:locale", isPublicInfoPath(seo.canonicalPath) ? "en_US" : "tr_TR");
    setMetaAttribute("property", "og:title", seo.title);
    setMetaAttribute("property", "og:description", seo.description);
    setMetaAttribute("property", "og:url", canonicalUrl);
    setMetaAttribute("property", "og:image", logoUrl);
    setMetaAttribute("name", "twitter:card", "summary");
    setMetaAttribute("name", "twitter:title", seo.title);
    setMetaAttribute("name", "twitter:description", seo.description);
    setMetaAttribute("name", "twitter:image", logoUrl);
    setCanonicalLink(canonicalUrl);
  }, [auth, pathname]);
}

const authCopy: Record<AuthLocale, AuthCopy> = {
  tr: {
    languageLabel: "Dil seçimi",
    brand: {
      tagline: "Kargo süreçlerinizi tek panelde yönetin",
      heading: "Etiket, sipariş ve mağaza yönetimi tek ekranda.",
      body: "Mağazalarınızı bağlayın, siparişlerinizi senkronize edin ve kargo etiketlerinizi hızlıca hazırlayın.",
      featuresLabel: "Müşteri paneli",
      marketplacesLabel: "Bağlanabildiğiniz pazar yerleri",
      secureNote: "Güvenli bağlantı ile korunur.",
    },
    features: [
      { label: "Kargo etiketleri", detail: "Hazır toplu etiket oluşturma" },
      { label: "Sipariş yönetimi", detail: "Tüm siparişler tek listede" },
      { label: "Mağaza bağlantıları", detail: "Pazar yeri entegrasyonları" },
      { label: "Otomatik senkron", detail: "Siparişler düzenli güncellenir" },
    ],
    signIn: {
      title: "Giriş yap",
      caption: "Hesabınıza giriş yaparak devam edin.",
      recognizedTitle: "Tekrar hoş geldiniz",
      recognizedCaption: "Giriş yapmak istediğiniz hesabı seçin.",
      useAccount: "Bu hesapla devam et",
      useAnotherAccount: "Başka bir hesapla giriş yap",
      removeAccount: "Bu cihazdan kaldır",
      removeFailed: "Hesap bu cihazdan kaldırılamadı.",
      recognizedError: "Kayıtlı hesapla giriş yapılamadı. Lütfen şifrenizle giriş yapın.",
      backToAccounts: "Kayıtlı hesaplara dön",
      checkingAccounts: "Kayıtlı hesaplar kontrol ediliyor...",
      forgot: "Şifremi unuttum",
      submit: "Giriş yap",
      submitting: "Giriş yapılıyor...",
      noAccount: "Hesabın yok mu?",
      register: "Kayıt ol",
      rememberMe: "Beni hatırla",
      genericError: "Giriş yapılamadı.",
    },
    signUp: {
      title: "Kayıt ol",
      caption: "KenPost hesabınızı oluşturun.",
      submit: "Kayıt ol",
      haveAccount: "Hesabın var mı?",
      signIn: "Giriş yap",
      genericError: "Kayıt tamamlanamadı.",
    },
    twoFactor: {
      title: "E-posta doğrulama",
      caption: "Girişinizi tamamlamak için gönderilen kodu yazın.",
      accountLabel: "Kod gönderilen hesap:",
      unknownAccount: "Bilinmiyor",
      codeLabel: "6 haneli kod",
      missingChallenge: "Doğrulama oturumu bulunamadı. Lütfen tekrar giriş yapın.",
      invalidCode: "Kod doğrulanamadı.",
      newCodeSent: "Yeni doğrulama kodu gönderildi.",
      resendFailed: "Yeni kod gönderilemedi.",
      submit: "Girişi tamamla",
      resend: "Kodu tekrar gönder",
      resending: "Gönderiliyor...",
      backToSignIn: "Girişe dön",
    },
    forgotPassword: {
      title: "Şifre sıfırla",
      caption: "Hesabınız için sıfırlama bağlantısı isteyin.",
      success: "E-posta sistemimizde varsa şifre sıfırlama bağlantısı gönderildi.",
      submit: "Bağlantı gönder",
      genericError: "Şifre sıfırlama isteği alınamadı.",
      backToSignIn: "Girişe dön",
    },
    resetPassword: {
      title: "Yeni şifre",
      caption: "Hesabınız için yeni şifre belirleyin.",
      invalidToken: "Sıfırlama bağlantısı geçersiz veya eksik.",
      minLength: "Şifre en az 8 karakter olmalı.",
      mismatch: "Şifreler aynı değil.",
      success: "Şifreniz güncellendi. Giriş sayfasına yönlendiriliyorsunuz.",
      submit: "Şifreyi güncelle",
      genericError: "Şifre sıfırlanamadı.",
    },
    verifyEmail: {
      title: "E-posta doğrulama",
      caption: "Hesap e-posta durumunuz kontrol ediliyor.",
      missingCode: "Doğrulama bağlantısı eksik.",
      genericError: "E-posta doğrulanamadı.",
      checking: "Doğrulanıyor",
      success: "E-postanız doğrulandı. Artık giriş yapabilirsiniz.",
      invalid: "Doğrulama bağlantısı geçersiz.",
      backToSignIn: "Girişe dön",
    },
    verifyEmailSent: {
      title: "E-postanızı kontrol edin",
      caption: "Doğrulama bağlantısı gönderildiyse gelen kutunuzda görünür.",
      successWithEmail: (email) => `${email} adresi için doğrulama bağlantısını kontrol edin.`,
      success: "Doğrulama bağlantınızı gelen kutunuzda kontrol edin.",
      emailLabel: "E-posta adresi",
      resend: "Doğrulama e-postasını tekrar gönder",
      resending: "Gönderiliyor...",
      resendFailed: "Doğrulama e-postası gönderilemedi.",
      resent: "Doğrulama e-postası tekrar gönderildi.",
      signIn: "Giriş yap",
      backToSignUp: "Kayıt ekranına dön",
    },
    fields: {
      email: "E-posta adresi",
      password: "Şifre",
      phone: "Telefon",
      company: "Firma",
      referralCode: "Referans kodu (isteğe bağlı)",
      newPassword: "Yeni şifre",
      confirmPassword: "Yeni şifre tekrar",
    },
    passwordToggle: {
      show: "Şifreyi göster",
      hide: "Şifreyi gizle",
    },
  },
  en: {
    languageLabel: "Language",
    brand: {
      tagline: "Manage shipping workflows from one panel",
      heading: "Labels, orders and store management on one screen.",
      body: "Connect your sales channels, sync orders and prepare shipping labels quickly.",
      featuresLabel: "Customer panel",
      marketplacesLabel: "Supported marketplaces",
      secureNote: "Protected with a secure connection.",
    },
    features: [
      { label: "Shipping labels", detail: "Fast batch label creation" },
      { label: "Order management", detail: "All orders in one list" },
      { label: "Store connections", detail: "Marketplace integrations" },
      { label: "Auto sync", detail: "Orders stay up to date" },
    ],
    signIn: {
      title: "Sign in",
      caption: "Continue to your KenPost account.",
      recognizedTitle: "Welcome back",
      recognizedCaption: "Choose the account you want to use.",
      useAccount: "Continue with this account",
      useAnotherAccount: "Sign in with another account",
      removeAccount: "Remove from this device",
      removeFailed: "The account could not be removed from this device.",
      recognizedError: "Could not sign in with the saved account. Please use your password.",
      backToAccounts: "Back to saved accounts",
      checkingAccounts: "Checking saved accounts...",
      forgot: "Forgot password",
      submit: "Sign in",
      submitting: "Signing in...",
      noAccount: "No account yet?",
      register: "Create account",
      rememberMe: "Remember me",
      genericError: "Could not sign in.",
    },
    signUp: {
      title: "Create account",
      caption: "Create your KenPost account.",
      submit: "Create account",
      haveAccount: "Already have an account?",
      signIn: "Sign in",
      genericError: "Registration could not be completed.",
    },
    twoFactor: {
      title: "Email verification",
      caption: "Enter the code we sent to complete sign-in.",
      accountLabel: "Code sent to:",
      unknownAccount: "Unknown",
      codeLabel: "6-digit code",
      missingChallenge: "Verification session was not found. Please sign in again.",
      invalidCode: "Code could not be verified.",
      newCodeSent: "A new verification code was sent.",
      resendFailed: "A new code could not be sent.",
      submit: "Complete sign-in",
      resend: "Resend code",
      resending: "Sending...",
      backToSignIn: "Back to sign in",
    },
    forgotPassword: {
      title: "Reset password",
      caption: "Request a reset link for your account.",
      success: "If this email exists in our system, a reset link has been sent.",
      submit: "Send link",
      genericError: "Password reset request could not be created.",
      backToSignIn: "Back to sign in",
    },
    resetPassword: {
      title: "New password",
      caption: "Choose a new password for your account.",
      invalidToken: "Reset link is invalid or missing.",
      minLength: "Password must be at least 8 characters.",
      mismatch: "Passwords do not match.",
      success: "Your password was updated. Redirecting to sign in.",
      submit: "Update password",
      genericError: "Password could not be reset.",
    },
    verifyEmail: {
      title: "Email verification",
      caption: "Checking your account email status.",
      missingCode: "Verification link is missing.",
      genericError: "Email could not be verified.",
      checking: "Verifying",
      success: "Your email was verified. You can now sign in.",
      invalid: "Verification link is invalid.",
      backToSignIn: "Back to sign in",
    },
    verifyEmailSent: {
      title: "Check your email",
      caption: "If a verification link was sent, it will appear in your inbox.",
      successWithEmail: (email) => `Check ${email} for the verification link.`,
      success: "Check your inbox for the verification link.",
      emailLabel: "Email address",
      resend: "Resend verification email",
      resending: "Sending...",
      resendFailed: "Verification email could not be sent.",
      resent: "Verification email was sent again.",
      signIn: "Sign in",
      backToSignUp: "Back to sign up",
    },
    fields: {
      email: "Email address",
      password: "Password",
      phone: "Phone",
      company: "Company",
      referralCode: "Referral code (optional)",
      newPassword: "New password",
      confirmPassword: "Confirm new password",
    },
    passwordToggle: {
      show: "Show password",
      hide: "Hide password",
    },
  },
  de: {
    languageLabel: "Sprache",
    brand: {
      tagline: "Versandprozesse in einem Panel verwalten",
      heading: "Labels, Bestellungen und Shops auf einem Bildschirm.",
      body: "Verbinden Sie Ihre Verkaufskanäle, synchronisieren Sie Bestellungen und erstellen Sie Versandlabels schneller.",
      featuresLabel: "Kundenpanel",
      marketplacesLabel: "Unterstützte Marktplätze",
      secureNote: "Durch eine sichere Verbindung geschützt.",
    },
    features: [
      { label: "Versandlabels", detail: "Schnelle Sammelerstellung" },
      { label: "Bestellungen", detail: "Alle Aufträge in einer Liste" },
      { label: "Shop-Verbindungen", detail: "Marktplatz-Integrationen" },
      { label: "Automatische Sync", detail: "Bestellungen bleiben aktuell" },
    ],
    signIn: {
      title: "Anmelden",
      caption: "Melden Sie sich bei Ihrem KenPost Konto an.",
      recognizedTitle: "Willkommen zurück",
      recognizedCaption: "Wählen Sie das Konto aus, mit dem Sie fortfahren möchten.",
      useAccount: "Mit diesem Konto fortfahren",
      useAnotherAccount: "Mit einem anderen Konto anmelden",
      removeAccount: "Von diesem Gerät entfernen",
      removeFailed: "Das Konto konnte nicht von diesem Gerät entfernt werden.",
      recognizedError: "Die Anmeldung mit dem gespeicherten Konto ist fehlgeschlagen. Bitte verwenden Sie Ihr Passwort.",
      backToAccounts: "Zurück zu gespeicherten Konten",
      checkingAccounts: "Gespeicherte Konten werden geprüft...",
      forgot: "Passwort vergessen",
      submit: "Anmelden",
      submitting: "Anmeldung...",
      noAccount: "Noch kein Konto?",
      register: "Registrieren",
      rememberMe: "Angemeldet bleiben",
      genericError: "Anmeldung fehlgeschlagen.",
    },
    signUp: {
      title: "Registrieren",
      caption: "Erstellen Sie Ihr KenPost Konto.",
      submit: "Registrieren",
      haveAccount: "Sie haben ein Konto?",
      signIn: "Anmelden",
      genericError: "Registrierung konnte nicht abgeschlossen werden.",
    },
    twoFactor: {
      title: "E-Mail-Verifizierung",
      caption: "Geben Sie den Code ein, um die Anmeldung abzuschließen.",
      accountLabel: "Code gesendet an:",
      unknownAccount: "Unbekannt",
      codeLabel: "6-stelliger Code",
      missingChallenge: "Verifizierungssitzung wurde nicht gefunden. Bitte melden Sie sich erneut an.",
      invalidCode: "Code konnte nicht bestätigt werden.",
      newCodeSent: "Ein neuer Verifizierungscode wurde gesendet.",
      resendFailed: "Neuer Code konnte nicht gesendet werden.",
      submit: "Anmeldung abschließen",
      resend: "Code erneut senden",
      resending: "Senden...",
      backToSignIn: "Zur Anmeldung",
    },
    forgotPassword: {
      title: "Passwort zurücksetzen",
      caption: "Fordern Sie einen Link zum Zurücksetzen an.",
      success: "Falls diese E-Mail existiert, wurde ein Link zum Zurücksetzen gesendet.",
      submit: "Link senden",
      genericError: "Anfrage zum Zurücksetzen konnte nicht erstellt werden.",
      backToSignIn: "Zur Anmeldung",
    },
    resetPassword: {
      title: "Neues Passwort",
      caption: "Legen Sie ein neues Passwort für Ihr Konto fest.",
      invalidToken: "Der Link ist ungültig oder fehlt.",
      minLength: "Das Passwort muss mindestens 8 Zeichen haben.",
      mismatch: "Die Passwörter stimmen nicht überein.",
      success: "Ihr Passwort wurde aktualisiert. Weiterleitung zur Anmeldung.",
      submit: "Passwort aktualisieren",
      genericError: "Passwort konnte nicht zurückgesetzt werden.",
    },
    verifyEmail: {
      title: "E-Mail-Verifizierung",
      caption: "Der E-Mail-Status Ihres Kontos wird geprüft.",
      missingCode: "Verifizierungslink fehlt.",
      genericError: "E-Mail konnte nicht verifiziert werden.",
      checking: "Wird verifiziert",
      success: "Ihre E-Mail wurde bestätigt. Sie können sich anmelden.",
      invalid: "Verifizierungslink ist ungültig.",
      backToSignIn: "Zur Anmeldung",
    },
    verifyEmailSent: {
      title: "E-Mail prüfen",
      caption: "Wenn ein Verifizierungslink gesendet wurde, finden Sie ihn im Posteingang.",
      successWithEmail: (email) => `Prüfen Sie ${email} für den Verifizierungslink.`,
      success: "Prüfen Sie Ihren Posteingang für den Verifizierungslink.",
      emailLabel: "E-Mail-Adresse",
      resend: "Verifizierungs-E-Mail erneut senden",
      resending: "Senden...",
      resendFailed: "Verifizierungs-E-Mail konnte nicht gesendet werden.",
      resent: "Verifizierungs-E-Mail wurde erneut gesendet.",
      signIn: "Anmelden",
      backToSignUp: "Zur Registrierung",
    },
    fields: {
      email: "E-Mail-Adresse",
      password: "Passwort",
      phone: "Telefon",
      company: "Firma",
      referralCode: "Empfehlungscode (optional)",
      newPassword: "Neues Passwort",
      confirmPassword: "Neues Passwort wiederholen",
    },
    passwordToggle: {
      show: "Passwort anzeigen",
      hide: "Passwort ausblenden",
    },
  },
};

function normalizeAuthLocale(value: string | null | undefined): AuthLocale | null {
  if (value === "tr" || value === "en" || value === "de") return value;
  return null;
}

function initialAuthLocale(): AuthLocale {
  if (typeof window === "undefined") return "tr";
  const stored = normalizeAuthLocale(window.localStorage.getItem(AUTH_LOCALE_STORAGE_KEY));
  if (stored) return stored;
  const browserLocale = normalizeAuthLocale(window.navigator.language?.slice(0, 2));
  return browserLocale || "tr";
}

function currentAuthLocale(): AuthLocale {
  if (typeof window === "undefined") return "tr";
  return normalizeAuthLocale(window.localStorage.getItem(AUTH_LOCALE_STORAGE_KEY)) || "tr";
}

function currentLocaleTag() {
  return localeTags[currentAuthLocale()];
}

function normalizePanelTranslationText(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

const panelCanonicalByText = (() => {
  const lookup = new Map<string, string>();
  (Object.keys(panelTranslations.tr) as string[]).forEach((source) => {
    lookup.set(normalizePanelTranslationText(source), source);
    (Object.keys(panelTranslations) as AuthLocale[]).forEach((locale) => {
      const translated = panelTranslations[locale][source];
      if (translated) lookup.set(normalizePanelTranslationText(translated), source);
    });
  });
  return lookup;
})();

const panelTranslationOverrides: Partial<Record<AuthLocale, Record<string, string>>> = {
  en: {
    "Açık siparişler": "Open orders",
    "Bağlı mağazalar": "Connected stores",
    "Bugünkü gönderi ritmini ve hesabın dikkat isteyebileceği alanlarını hızlıca okuyun.": "Read today's shipment pace and account areas that may need attention.",
    "Etiket engelli": "Label blocked",
    "Etiket Satın Al": "Buy labels",
    "Etikete dönüştürülmeye hazır": "Ready to turn into labels",
    "Gönderi başarı oranı": "Shipment success rate",
    "Hazır etiketler": "Ready labels",
    "Hesap hazır": "Account ready",
    "Mağaza hareketlerini, etiket hazır durumunu ve gönderi temposunu tek yerden takip edin.": "Track store activity, label readiness and shipment pace from one place.",
    "Mağazalarım": "My stores",
    "Müşteri Kontrol Merkezi": "Customer control center",
    "Operasyon Nabzı": "Operational pulse",
    "Özel adres": "Special address",
    "Panel bilgileri yüklenmeye hazır": "Panel information is ready to load",
    "Pazar yerine takip yazımı başarılı olan gönderiler baz alınır.": "Based on shipments whose tracking updates were written to the marketplace successfully.",
    "Siparişleri görüntüle": "View orders",
    "Sistemde kayıtlı çıktı": "Saved outputs in the system",
    "Son hareketler": "Recent activity",
    "Şu anda kullanıma hazır": "Ready to use now",
    "Tekrar hoş geldin": "Welcome back",
    "Yeni Sipariş Oluştur": "Create new order",
    "Tüm mağazalar": "All stores",
    "Ürün seçin": "Select product",
    "Adres": "Address",
    "Adres 1": "Address line 1",
    "Adres 2": "Address line 2",
    "Adresi ayrıştır": "Parse address",
    "Alıcı": "Recipient",
    "Alıcı adresini tek kutuya yapıştırabilir veya alanları doğrudan doldurabilirsiniz.": "Paste the recipient address into one box or fill in the fields directly.",
    "Alıcı ve adres bilgilerini aşağıdaki alanlara girin.": "Enter the recipient and address details in the fields below.",
    "Aranıyor": "Searching",
    "Aynı alıcı ve adrese 1 ile 50 arasında ayrı etiket oluşturabilirsiniz.": "You can create between 1 and 50 separate labels for the same recipient and address.",
    "Bu seçimler tekli ve toplu manuel etiket oluşturma için kullanılır.": "These settings are used for both single and bulk manual label creation.",
    "CSV, TSV veya XLSX dosyasındaki adresleri toplu manuel etikete çevirin.": "Turn addresses in a CSV, TSV or XLSX file into manual labels in bulk.",
    "Dosya seç": "Choose file",
    "Durum": "Status",
    "Eski alıcılar": "Recent recipients",
    "Etiket adedi": "Label quantity",
    "Etiket bilgileri yükleniyor": "Loading label information",
    "Etiket özeti": "Label summary",
    "Etiket ürünü": "Label product",
    "Etiketi oluştur": "Create label",
    "Firma": "Company",
    "Firma (opsiyonel)": "Company (optional)",
    "Firma / Alıcı": "Company / Recipient",
    "Formu temizle": "Clear form",
    "Geçerli": "Valid",
    "Giden": "Outbound",
    "Gönderi": "Outbound",
    "Gönderi tipi": "Shipment type",
    "Hatalı": "Invalid",
    "Henüz manuel alıcı yok.": "No manual recipient yet.",
    "Henüz manuel etiket yok.": "No manual label yet.",
    "İade / Retoure": "Return",
    "Importu oluştur": "Create import",
    "İşlemler": "Actions",
    "Kredi": "Credits",
    "Manuel etiket": "Manual label",
    "Manuel etiket oluştur": "Create manual labels",
    "Müşteri adı": "Customer name",
    "Oluşacak": "Labels to create",
    "Oluşturulan manuel etiketleri buradan tekrar indirebilir, yenileyebilir veya aynı alıcıyı yeniden kullanabilirsiniz.": "Download or refresh created manual labels here, or reuse the same recipient.",
    "Ortak seçenekler": "Shared options",
    "Paket": "Packages",
    "Packstation veya Postfiliale adresleri manuel etikette bloklanır.": "Packstation and Postfiliale addresses are blocked for manual labels.",
    "PDF, ZIP ve gönderi detayına hızlıca ulaşabilirsiniz.": "Quickly access the PDF, ZIP and shipment details.",
    "Posta kodu": "Postal code",
    "Seçili üründe kullanılabilir kredi yok.": "The selected product has no available credits.",
    "Sipariş no": "Order number",
    "Sokak ve kapı numarası": "Street and house number",
    "Son manuel alıcılar yükleniyor": "Loading recent manual recipients",
    "Son manuel etiketler": "Recent manual labels",
    "Son manuel etiketler yükleniyor": "Loading recent manual labels",
    "Sütunlar: companyName, recipientName, address1, address2, postalCode, city, email, phone, orderNumber, packageCount. Tek adres kutusu için addressText de kullanılabilir.": "Columns: companyName, recipientName, address1, address2, postalCode, city, email, phone, orderNumber, packageCount. You can also use addressText for the single address box.",
    "Şehir": "City",
    "Şehir kontrol ediliyor": "Checking city",
    "Takip / Sipariş": "Tracking / Order",
    "Tarih": "Date",
    "Tek adres kutusu": "Single address box",
    "Tekli manuel": "Single entry",
    "Tekli manuel etiket": "Single manual label",
    "Tekli ve toplu manuel etiketleri hızlı adres girişi, tekrar kullanım ve indirme aksiyonlarıyla yönetin.": "Manage single and bulk manual labels with quick address entry, reuse and download actions.",
    "Tekrar kullan": "Reuse",
    "Tip": "Type",
    "Toplu import": "Bulk import",
    "Toplu manuel import": "Bulk manual import",
    "Tümünü ZIP indir": "Download all as ZIP",
    "Etiket": "Label",
    "Ülke": "Country",
    "Ürün": "Product",
    "Ürün seçilmedi": "No product selected",
    "Varsayılan yap": "Save as default",
    "Yazdığım adresle devam et": "Use entered address",
    "Yenile": "Refresh",
    "created": "Created",
    "failed": "Failed",
    "printed": "Printed",
    "ready": "Ready",
    ...recentFeatureTranslations.en,
    ...customerEnglishTranslations,
    ...amazonV2Translations.en,
  },
  de: {
    "1 gün": "1 Tag",
    "3 gün": "3 Tage",
    "7 gün": "7 Tage",
    "30 gün": "30 Tage",
    "Açık": "Offen",
    "Açık siparişler": "Offene Bestellungen",
    "Aralık": "Zeitraum",
    "Bağlı mağazalar": "Verbundene Shops",
    "Bugünkü gönderi ritmini ve hesabın dikkat isteyebileceği alanlarını hızlıca okuyun.": "Prüfen Sie den heutigen Versandrhythmus und Bereiche im Konto, die Aufmerksamkeit brauchen könnten.",
    "Çıkış Yap": "Abmelden",
    "Detay": "Details",
    "Etiket": "Label",
    "Etiket bekleyen": "Wartet auf Label",
    "Etiket bekleyen açık siparişler": "Offene Bestellungen ohne Label",
    "Etiket bekliyor": "Wartet auf Label",
    "Etiket durumu": "Labelstatus",
    "Etiket engelli": "Label gesperrt",
    "Etiket var": "Label vorhanden",
    "Etiket Satın Al": "Labels kaufen",
    "Etiket ürünü": "Labelprodukt",
    "Etiketli": "Mit Label",
    "Etiketi olanlar": "Mit Label",
    "Etikete dönüştürülmeye hazır": "Bereit zur Umwandlung in Labels",
    "Filtreler": "Filter",
    "Gönderi başarı oranı": "Sendungserfolgsquote",
    "Gönderilenler": "Versendet",
    "Hazır etiketler": "Bereite Labels",
    "Hepsi": "Alle",
    "Hesap hazır": "Konto bereit",
    "İptal": "Storniert",
    "Kargo Sağlığı": "Versandstatus",
    "Kayıt": "Datensatz",
    "Kredi": "Guthaben",
    "kredi": "Guthaben",
    "Listeyi yenile": "Liste aktualisieren",
    "Mağaza": "Shop",
    "Mağaza hareketlerini, etiket hazır durumunu ve gönderi temposunu tek yerden takip edin.": "Verfolgen Sie Shop-Aktivität, Label-Bereitschaft und Versandtempo an einem Ort.",
    "Mağazalarım": "Meine Shops",
    "Manuel etiket": "Manuelles Label",
    "Mevcut kredi": "Verfügbares Guthaben",
    "Müşteri Kontrol Merkezi": "Kunden-Kontrollzentrum",
    "Ödeme": "Zahlung",
    "Operasyon Nabzı": "Operativer Puls",
    "Özel adres": "Spezialadresse",
    "Panel bilgileri yüklenmeye hazır": "Panelinformationen können geladen werden",
    "Pazar yerine takip yazımı başarılı olan gönderiler baz alınır.": "Basiert auf Sendungen, deren Tracking erfolgreich an den Marktplatz übertragen wurde.",
    "satır": "Zeilen",
    "Sadece gönderilmemiş": "Nur unversendete",
    "Seçim": "Auswahl",
    "Sipariş": "Bestellung",
    "sipariş": "Bestellung",
    "Sipariş kontrolü": "Bestellabgleich",
    "Sipariş no, müşteri, e-posta, şehir ara": "Bestell-Nr., Kunde, E-Mail oder Stadt suchen",
    "Siparişler": "Bestellungen",
    "Siparişleri görüntüle": "Bestellungen anzeigen",
    "Sistemde kayıtlı çıktı": "Im System gespeicherte Ausgaben",
    "Senkronizasyon": "Synchronisierung",
    "Senkronizasyonu başlat": "Synchronisierung starten",
    "Senkron aralığı sipariş çekme işlemini belirler; liste filtreleri ayrıca aşağıdan yönetilir.": "Der Synchronisationszeitraum bestimmt den Bestellabruf; die Listenfilter werden unten verwaltet.",
    "Son hareketler": "Letzte Aktivitäten",
    "Store": "Shop",
    "Şu anda kullanıma hazır": "Jetzt einsatzbereit",
    "Tekrar hoş geldin": "Willkommen zurück",
    "Tekrar hoş geldin,": "Willkommen zurück,",
    "Temizle": "Zurücksetzen",
    "Tarih": "Datum",
    "Toplam": "Gesamt",
    "Toplam sipariş": "Bestellungen",
    "Toplam Sipariş": "Bestellungen",
    "Toplu ZIP geçmişi": "ZIP-Verlauf",
    "Tüm durumlar": "Alle Status",
    "Tüm mağazalar": "Alle Shops",
    "Tüm mağazaları senkronla": "Alle Shops synchronisieren",
    "Tüm mağazalarınızdaki siparişleri filtreleyin, senkronlayın, CSV alın ve etiket akışını aynı ekrandan yönetin.": "Filtern und synchronisieren Sie Bestellungen aus allen Shops, exportieren Sie CSV-Dateien und steuern Sie den Label-Ablauf auf einer Seite.",
    "Ürün seçin": "Produkt auswählen",
    "Ülke": "Land",
    "Yeni Sipariş Oluştur": "Neue Bestellung erstellen",
    "AÇIK": "OFFEN",
    "ARALIK": "ZEITRAUM",
    "CUSTOMER": "KUNDE",
    "ETIKET": "LABEL",
    "ETIKET BEKLEYEN": "WARTET AUF LABEL",
    "ETIKETLI": "MIT LABEL",
    "FULFILLMENT": "VERSANDSTATUS",
    "Fulfillment": "Versandstatus",
    "ITEMS": "ARTIKEL",
    "Items": "Artikel",
    "KREDI": "GUTHABEN",
    "MEVCUT KREDI": "VERFÜGBARES GUTHABEN",
    "ORDER": "BESTELLUNG",
    "ÖDEME": "ZAHLUNG",
    "Platform": "Plattform",
    "PRODUCT": "PRODUKT",
    "SEÇIM": "AUSWAHL",
    "SIPARIŞ": "BESTELLUNGEN",
    "TARIH": "DATUM",
    "TARİH": "DATUM",
    "TOPLAM": "GESAMT",
    "TOPLAM SIPARIŞ": "BESTELLUNGEN",
    "TRACKING": "SENDUNGSVERFOLGUNG",
    "Tracking": "Sendungsverfolgung",
    "WEIGHT": "GEWICHT",
    "label product": "Labelprodukt",
    "need_to_be_sent": "Versand offen",
    "sent": "Versendet",
    "50 satır": "50 Zeilen",
    "100 satır": "100 Zeilen",
    "250 satır": "250 Zeilen",
    "Adres": "Adresse",
    "Adres 1": "Adresszeile 1",
    "Adres 2": "Adresszeile 2",
    "Adresi ayrıştır": "Adresse aufteilen",
    "Alıcı": "Empfänger",
    "Alıcı adresini tek kutuya yapıştırabilir veya alanları doğrudan doldurabilirsiniz.": "Fügen Sie die Empfängeradresse in ein Feld ein oder füllen Sie die Felder direkt aus.",
    "Alıcı ve adres bilgilerini aşağıdaki alanlara girin.": "Geben Sie die Empfänger- und Adressdaten in die folgenden Felder ein.",
    "Aranıyor": "Suche läuft",
    "Aynı alıcı ve adrese 1 ile 50 arasında ayrı etiket oluşturabilirsiniz.": "Sie können 1 bis 50 separate Labels für denselben Empfänger und dieselbe Adresse erstellen.",
    "Bu seçimler tekli ve toplu manuel etiket oluşturma için kullanılır.": "Diese Einstellungen gelten für die einzelne und die gebündelte manuelle Label-Erstellung.",
    "CSV, TSV veya XLSX dosyasındaki adresleri toplu manuel etikete çevirin.": "Erstellen Sie aus Adressen in einer CSV-, TSV- oder XLSX-Datei gebündelt manuelle Labels.",
    "Dosya seç": "Datei auswählen",
    "Durum": "Status",
    "Eski alıcılar": "Letzte Empfänger",
    "Etiket adedi": "Label-Anzahl",
    "Etiket bilgileri yükleniyor": "Label-Informationen werden geladen",
    "Etiket özeti": "Label-Zusammenfassung",
    "Etiketi oluştur": "Label erstellen",
    "Firma": "Firma",
    "Firma (opsiyonel)": "Firma (optional)",
    "Firma / Alıcı": "Firma / Empfänger",
    "Formu temizle": "Formular leeren",
    "Geçerli": "Gültig",
    "Giden": "Versand",
    "Gönderi": "Versand",
    "Gönderi tipi": "Sendungstyp",
    "Hatalı": "Fehlerhaft",
    "Henüz manuel alıcı yok.": "Noch kein manueller Empfänger vorhanden.",
    "Henüz manuel etiket yok.": "Noch kein manuelles Label vorhanden.",
    "İade / Retoure": "Retoure",
    "Importu oluştur": "Import erstellen",
    "İşlemler": "Aktionen",
    "Manuel etiket oluştur": "Manuelle Labels erstellen",
    "Müşteri adı": "Kundenname",
    "Oluşacak": "Zu erstellen",
    "Oluşturulan manuel etiketleri buradan tekrar indirebilir, yenileyebilir veya aynı alıcıyı yeniden kullanabilirsiniz.": "Hier können Sie erstellte manuelle Labels erneut herunterladen, aktualisieren oder denselben Empfänger wiederverwenden.",
    "Ortak seçenekler": "Gemeinsame Einstellungen",
    "Paket": "Pakete",
    "Packstation veya Postfiliale adresleri manuel etikette bloklanır.": "Packstation- und Postfiliale-Adressen sind für manuelle Labels gesperrt.",
    "PDF, ZIP ve gönderi detayına hızlıca ulaşabilirsiniz.": "PDF, ZIP und Sendungsdetails sind schnell erreichbar.",
    "Posta kodu": "Postleitzahl",
    "Seçili üründe kullanılabilir kredi yok.": "Für das ausgewählte Produkt ist kein Guthaben verfügbar.",
    "Sipariş no": "Bestellnummer",
    "Sokak ve kapı numarası": "Straße und Hausnummer",
    "Son manuel alıcılar yükleniyor": "Letzte manuelle Empfänger werden geladen",
    "Son manuel etiketler": "Letzte manuelle Labels",
    "Son manuel etiketler yükleniyor": "Letzte manuelle Labels werden geladen",
    "Sütunlar: companyName, recipientName, address1, address2, postalCode, city, email, phone, orderNumber, packageCount. Tek adres kutusu için addressText de kullanılabilir.": "Spalten: companyName, recipientName, address1, address2, postalCode, city, email, phone, orderNumber, packageCount. Für das einzelne Adressfeld kann auch addressText verwendet werden.",
    "Şehir": "Stadt",
    "Şehir kontrol ediliyor": "Ort wird geprüft",
    "Takip / Sipariş": "Sendungsverfolgung / Bestellung",
    "Tek adres kutusu": "Einzelnes Adressfeld",
    "Tekli manuel": "Einzeleingabe",
    "Tekli manuel etiket": "Einzelnes manuelles Label",
    "Tekli ve toplu manuel etiketleri hızlı adres girişi, tekrar kullanım ve indirme aksiyonlarıyla yönetin.": "Verwalten Sie einzelne und gebündelte manuelle Labels mit schneller Adresseingabe, Wiederverwendung und Download-Aktionen.",
    "Tekrar kullan": "Wiederverwenden",
    "Tip": "Typ",
    "Toplu import": "Sammelimport",
    "Toplu manuel import": "Manueller Sammelimport",
    "Tümünü ZIP indir": "Alle als ZIP herunterladen",
    "Ürün": "Produkt",
    "Ürün seçilmedi": "Kein Produkt ausgewählt",
    "Varsayılan yap": "Als Standard speichern",
    "Yazdığım adresle devam et": "Eingegebene Adresse verwenden",
    "Yenile": "Aktualisieren",
    "created": "Erstellt",
    "failed": "Fehlgeschlagen",
    "printed": "Gedruckt",
    "ready": "Bereit",
    ...recentFeatureTranslations.de,
    ...customerGermanTranslations,
    ...amazonV2Translations.de,
  },
};

const panelOverrideCanonicalByText = (() => {
  const lookup = new Map<string, string>();
  (Object.values(panelTranslationOverrides) as Array<Record<string, string> | undefined>).forEach((translations) => {
    Object.entries(translations || {}).forEach(([source, translated]) => {
      lookup.set(normalizePanelTranslationText(source), source);
      lookup.set(normalizePanelTranslationText(translated), source);
    });
  });
  return lookup;
})();

type PanelTranslationPattern = {
  pattern: RegExp;
  replace: string | ((match: RegExpMatchArray) => string);
};

const panelTranslationPatterns: Partial<Record<AuthLocale, PanelTranslationPattern[]>> = {
  en: [
    ...recentFeatureTranslationPatterns.en,
    ...customerEnglishTranslationPatterns,
    { pattern: /^(.+?) · ([\d.,]+) kredi$/, replace: (_match) => `${_match[1]} · ${_match[2]} credits` },
    { pattern: /^(.+?) · ([\d.,]+) adet$/, replace: (_match) => `${_match[1]} · ${_match[2]} labels` },
    { pattern: /^(.+?) · Giden$/, replace: (_match) => `${_match[1]} · Outbound` },
    { pattern: /^(\d+) etiket hazır$/, replace: (_match) => `${_match[1]} labels ready` },
    { pattern: /^(\d+) etiketi oluştur$/, replace: (_match) => `Create ${_match[1]} labels` },
    { pattern: /^(\d+) etiket için yeterli kredi yok\. Kullanılabilir kredi: ([\d.,]+)\.$/, replace: (_match) => `Not enough credits for ${_match[1]} labels. Available credits: ${_match[2]}.` },
    { pattern: /^(\d+) etiket oluşturuldu, (\d+) etiket başarısız\.$/, replace: (_match) => `${_match[1]} labels created, ${_match[2]} failed.` },
    {
      pattern: /^Etiket oluşturuldu ve sipariş otomatik arşivlendi(?:, takip no: (.+))?\.$/,
      replace: (_match) => `Label created and order archived automatically${_match[1] ? `, tracking number: ${_match[1]}` : ""}.`,
    },
    {
      pattern: /^([\d.,]+) etiket oluşturuldu, ([\d.,]+) mevcut gönderi atlandı, ([\d.,]+) hata\. ([\d.,]+) sipariş otomatik arşivlendi\.(?: ([\d.,]+) etiket ZIP olarak indiriliyor\.)?$/,
      replace: (_match) =>
        `${_match[1]} labels created, ${_match[2]} existing shipments skipped, ${_match[3]} errors. ${_match[4]} orders archived automatically.` +
        (_match[5] ? ` A ZIP containing ${_match[5]} labels is being downloaded.` : ""),
    },
    { pattern: /^(\d+) geçerli satır hazırlandı\.$/, replace: (_match) => `${_match[1]} valid rows prepared.` },
    { pattern: /^(\d+) kullanılabilir kredi$/, replace: (_match) => `${_match[1]} available credits` },
    { pattern: /^(\d+) eski manuel alıcı$/, replace: (_match) => `${_match[1]} recent manual recipients` },
    { pattern: /^Etiket (\d+)$/, replace: (_match) => `Label ${_match[1]}` },
    { pattern: /^Admin destek modu: (.+) olarak müşteri panelindesiniz\.$/, replace: (_match) => `Admin support mode: You are viewing the customer panel as ${_match[1]}.` },
    { pattern: /^(\d+) satır$/, replace: (_match) => `${_match[1]} rows` },
    { pattern: /^(\d+) gün$/, replace: (_match) => `${_match[1]} days` },
    { pattern: /^(\d+) aktif mağaza$/, replace: (_match) => `${_match[1]} active stores` },
    { pattern: /^(\d+) aktif mağazada sipariş senkronu çalışmış görünüyor\.$/, replace: (_match) => `Order sync has run for ${_match[1]} active stores.` },
    { pattern: /^Shopware bağlantısı doğrulandı\. API erişiminde ([\d.,]+) sipariş görünüyor\.$/, replace: (_match) => `Shopware connection verified. ${_match[1]} orders are visible through the API.` },
    { pattern: /^(.+?) DHL takip sayfasını yeni sekmede aç$/, replace: (_match) => `Open DHL tracking page for ${_match[1]} in a new tab` },
    { pattern: /^Tekrar hoş geldin,\s*(.+)$/, replace: (_match) => `Welcome back, ${_match[1]}` },
  ],
  de: [
    ...recentFeatureTranslationPatterns.de,
    ...customerGermanTranslationPatterns,
    { pattern: /^(.+?) · ([\d.,]+) kredi$/, replace: (_match) => `${_match[1]} · ${_match[2]} Guthaben` },
    { pattern: /^(.+?) · ([\d.,]+) adet$/, replace: (_match) => `${_match[1]} · ${_match[2]} Labels` },
    { pattern: /^(.+?) · Giden$/, replace: (_match) => `${_match[1]} · Versand` },
    { pattern: /^(\d+) etiket hazır$/, replace: (_match) => `${_match[1]} Labels bereit` },
    { pattern: /^(\d+) etiketi oluştur$/, replace: (_match) => `${_match[1]} Labels erstellen` },
    { pattern: /^(\d+) etiket için yeterli kredi yok\. Kullanılabilir kredi: ([\d.,]+)\.$/, replace: (_match) => `Nicht genügend Guthaben für ${_match[1]} Labels. Verfügbar: ${_match[2]}.` },
    { pattern: /^(\d+) etiket oluşturuldu, (\d+) etiket başarısız\.$/, replace: (_match) => `${_match[1]} Labels erstellt, ${_match[2]} fehlgeschlagen.` },
    {
      pattern: /^Etiket oluşturuldu ve sipariş otomatik arşivlendi(?:, takip no: (.+))?\.$/,
      replace: (_match) => `Label erstellt und Bestellung automatisch archiviert${_match[1] ? `, Sendungsnummer: ${_match[1]}` : ""}.`,
    },
    {
      pattern: /^([\d.,]+) etiket oluşturuldu, ([\d.,]+) mevcut gönderi atlandı, ([\d.,]+) hata\. ([\d.,]+) sipariş otomatik arşivlendi\.(?: ([\d.,]+) etiket ZIP olarak indiriliyor\.)?$/,
      replace: (_match) =>
        `${_match[1]} Labels erstellt, ${_match[2]} vorhandene Sendungen übersprungen, ${_match[3]} Fehler. ${_match[4]} Bestellungen automatisch archiviert.` +
        (_match[5] ? ` Eine ZIP-Datei mit ${_match[5]} Labels wird heruntergeladen.` : ""),
    },
    { pattern: /^(\d+) geçerli satır hazırlandı\.$/, replace: (_match) => `${_match[1]} gültige Zeilen vorbereitet.` },
    { pattern: /^(\d+) kullanılabilir kredi$/, replace: (_match) => `${_match[1]} verfügbares Guthaben` },
    { pattern: /^(\d+) eski manuel alıcı$/, replace: (_match) => `${_match[1]} letzte manuelle Empfänger` },
    { pattern: /^Etiket (\d+)$/, replace: (_match) => `Label ${_match[1]}` },
    { pattern: /^Admin destek modu: (.+) olarak müşteri panelindesiniz\.$/, replace: (_match) => `Admin-Supportmodus: Sie sehen den Kundenbereich als ${_match[1]}.` },
    { pattern: /^(\d+) satır$/, replace: (_match) => `${_match[1]} Zeilen` },
    { pattern: /^(\d+) gün$/, replace: (_match) => `${_match[1]} Tage` },
    { pattern: /^(\d+) aktif mağaza$/, replace: (_match) => `${_match[1]} aktive Shops` },
    { pattern: /^(\d+) aktif mağazada sipariş senkronu çalışmış görünüyor\.$/, replace: (_match) => `Die Bestellsynchronisierung wurde für ${_match[1]} aktive Shops ausgeführt.` },
    { pattern: /^Shopware bağlantısı doğrulandı\. API erişiminde ([\d.,]+) sipariş görünüyor\.$/, replace: (_match) => `Shopware-Verbindung geprüft. Über die API sind ${_match[1]} Bestellungen sichtbar.` },
    { pattern: /^(\d+) item$/, replace: (_match) => `${_match[1]} Artikel` },
    { pattern: /^(\d+) items$/, replace: (_match) => `${_match[1]} Artikel` },
    { pattern: /^(.+?) DHL takip sayfasını yeni sekmede aç$/, replace: (_match) => `DHL-Sendungsverfolgung für ${_match[1]} in neuem Tab öffnen` },
    { pattern: /^Tekrar hoş geldin,\s*(.+)$/, replace: (_match) => `Willkommen zurück, ${_match[1]}` },
  ],
};

function applyPanelTranslationPatterns(value: string, locale: AuthLocale) {
  const patterns = panelTranslationPatterns[locale] || [];
  for (const item of patterns) {
    const match = value.match(item.pattern);
    if (!match) continue;
    return typeof item.replace === "function" ? item.replace(match) : value.replace(item.pattern, item.replace);
  }
  return null;
}

function translatePanelText(value: string, locale: AuthLocale) {
  const normalized = normalizePanelTranslationText(value);
  if (!normalized) return value;
  const directOverride = panelTranslationOverrides[locale]?.[normalized];
  const patternOverride = directOverride ? null : applyPanelTranslationPatterns(normalized, locale);
  if (directOverride || patternOverride) {
    const leading = value.match(/^\s*/)?.[0] || "";
    const trailing = value.match(/\s*$/)?.[0] || "";
    return `${leading}${directOverride || patternOverride}${trailing}`;
  }
  const source = panelCanonicalByText.get(normalized) || panelOverrideCanonicalByText.get(normalized);
  if (!source) return value;
  const translated = panelTranslationOverrides[locale]?.[source] || panelTranslations[locale][source] || panelTranslations.tr[source] || source;
  if (!translated || translated === normalized) return value;
  const leading = value.match(/^\s*/)?.[0] || "";
  const trailing = value.match(/\s*$/)?.[0] || "";
  return `${leading}${translated}${trailing}`;
}

function translatedPanelText(value: string) {
  return translatePanelText(value, currentAuthLocale());
}

function shouldSkipPanelTextNode(parent: HTMLElement | null) {
  if (!parent) return true;
  const tagName = parent.tagName.toLowerCase();
  return ["code", "input", "pre", "script", "style", "textarea"].includes(tagName) || Boolean(parent.closest("[data-i18n-skip]"));
}

function translatePanelElementAttributes(element: Element, locale: AuthLocale) {
  PANEL_TRANSLATABLE_ATTRIBUTES.forEach((attribute) => {
    const value = element.getAttribute(attribute);
    if (!value) return;
    const translated = translatePanelText(value, locale);
    if (translated !== value) element.setAttribute(attribute, translated);
  });
}

function applyPanelTranslations(root: Element, locale: AuthLocale) {
  translatePanelElementAttributes(root, locale);
  root.querySelectorAll("*").forEach((element) => translatePanelElementAttributes(element, locale));

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    const textNode = node as Text;
    const parent = textNode.parentElement;
    if (!shouldSkipPanelTextNode(parent)) {
      const translated = translatePanelText(textNode.nodeValue || "", locale);
      if (translated !== textNode.nodeValue) textNode.nodeValue = translated;
    }
    node = walker.nextNode();
  }
}

function applyPanelTranslationToNode(node: Node, locale: AuthLocale) {
  if (node.nodeType === Node.TEXT_NODE) {
    const textNode = node as Text;
    if (shouldSkipPanelTextNode(textNode.parentElement)) return;
    const translated = translatePanelText(textNode.nodeValue || "", locale);
    if (translated !== textNode.nodeValue) textNode.nodeValue = translated;
    return;
  }
  if (node.nodeType === Node.ELEMENT_NODE) applyPanelTranslations(node as Element, locale);
}

type StoreCredentialFormField = { key: string; label: string; type?: string };

const storeCredentialFields: Record<StorePlatform, StoreCredentialFormField[]> = {
  afterbuy: [
    { key: "afterbuyAccountName", label: "Afterbuy hesap adı" },
    { key: "afterbuyPartnerToken", label: "PartnerToken", type: "password" },
    { key: "afterbuyAccountToken", label: "AccountToken / UserToken", type: "password" },
  ],
  amazon: [],
  amazon_v2: [],
  etsy: [
    { key: "etsyUsername", label: "Etsy kullanıcı adı / e-posta" },
    { key: "etsyPassword", label: "Etsy şifre", type: "password" },
    { key: "etsyBrowserProfile", label: "Browser profili" },
    { key: "etsyBrowserPort", label: "Browser portu" },
  ],
  ebay: [],
  manual: [{ key: "storeUrl", label: "Opsiyonel URL" }],
  shopify: [
    { key: "shopDomain", label: "myshopify.com domaini" },
    { key: "shopifyAccessToken", label: "Connector erişim bilgisi", type: "password" },
    { key: "shopifyClientId", label: "İstemci ID" },
  ],
  shopware: [
    { key: "shopwareStoreUrl", label: "Shopware mağaza URL" },
    { key: "shopwareClientId", label: "Access Key ID" },
    { key: "shopwareClientSecret", label: "Secret Access Key", type: "password" },
  ],
  billbee: [
    { key: "billbeeName", label: "Billbee adı" },
    { key: "billbeeApiKey", label: "API anahtarı", type: "password" },
    { key: "billbeeApiUser", label: "API kullanıcı" },
    { key: "billbeeApiPassword", label: "API şifre", type: "password" },
  ],
  aboutyou: [
    { key: "aboutyouName", label: "AboutYou adı" },
    { key: "aboutyouApiKey", label: "API anahtarı", type: "password" },
  ],
  otto: [
    { key: "ottoName", label: "OTTO adı" },
    { key: "ottoClientId", label: "İstemci ID" },
    { key: "ottoClientSecret", label: "İstemci gizli anahtarı", type: "password" },
  ],
  jtl: [
    { key: "jtlStoreUrl", label: "JTL URL" },
    { key: "jtlClientId", label: "İstemci ID" },
    { key: "jtlClientSecret", label: "İstemci gizli anahtarı", type: "password" },
    { key: "jtlConnectionMode", label: "Bağlantı modu (connector/ip)" },
    { key: "jtlDbHost", label: "JTL DB sunucu" },
    { key: "jtlDbPort", label: "JTL DB port" },
    { key: "jtlDbName", label: "JTL DB adı" },
    { key: "jtlDbUser", label: "JTL DB kullanıcı" },
    { key: "jtlDbPassword", label: "JTL DB şifre", type: "password" },
  ],
  woocommerce: [
    { key: "woocommerceStoreUrl", label: "Mağaza URL" },
    { key: "woocommerceConsumerKey", label: "Tüketici anahtarı", type: "password" },
    { key: "woocommerceConsumerSecret", label: "Tüketici gizli anahtarı", type: "password" },
  ],
  kaufland: [
    { key: "kauflandClientKey", label: "İstemci anahtarı", type: "password" },
    { key: "kauflandSecretKey", label: "Gizli anahtar", type: "password" },
    { key: "kauflandStorefront", label: "Vitrin" },
  ],
  opencart: [
    { key: "opencartAdminUrl", label: "Mağaza / Admin URL" },
    { key: "opencartUsername", label: "API kullanıcı adı" },
    { key: "opencartPassword", label: "API anahtarı", type: "password" },
  ],
  plenty: [
    { key: "plentySystemId", label: "PlentyONE System-ID" },
    { key: "plentyUsername", label: "API kullanıcı adı" },
    { key: "plentyPassword", label: "API şifresi", type: "password" },
  ],
};

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function KenpostLogo({
  className,
  onDark = false,
}: {
  className?: string;
  onDark?: boolean;
}) {
  return (
    <span
      className="inline-flex min-w-0 shrink-0 items-center overflow-hidden bg-transparent"
      aria-label="KenPost"
    >
      <img
        src={onDark ? KENPOST_LOGO_DARK_SRC : KENPOST_LOGO_SRC}
        alt="KenPost"
        className={cx("block h-auto w-[150px] max-w-full object-contain", className)}
        decoding="async"
      />
    </span>
  );
}

function VersionBadge({ dark = false }: { dark?: boolean }) {
  return (
    <span
      className={cx(
        "inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold leading-none shadow-sm",
        dark ? "border-lime-200/25 bg-lime-300/10 text-lime-100" : "border-lime-200 bg-lime-50 text-lime-800"
      )}
      title={KENPOST_VERSION_LABEL}
    >
      <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">{KENPOST_VERSION_LABEL}</span>
    </span>
  );
}

function PublicActionLink({ href, label, secondary = false }: { href: string; label: string; secondary?: boolean }) {
  const className = cx(
    "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition",
    secondary
      ? "border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
      : "bg-slate-950 text-white shadow-sm hover:bg-slate-800"
  );

  if (href.startsWith("/")) {
    return (
      <Link to={href} className={className}>
        {label}
        <ChevronRight className="h-4 w-4" />
      </Link>
    );
  }

  return (
    <a href={href} className={className}>
      {label}
      <ExternalLink className="h-4 w-4" />
    </a>
  );
}

function PublicInfoRoute({ pageKey }: { pageKey: PublicInfoPageKey }) {
  const page = publicInfoPages[pageKey];
  const Icon = page.icon;

  return (
    <main className="min-h-screen bg-[#f7f8f7] text-slate-950">
      <header className="border-b border-slate-200 bg-white/95">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center justify-between gap-4">
            <Link to="/support" aria-label="KenPost support home">
              <KenpostLogo className="w-[138px]" />
            </Link>
            <VersionBadge />
          </div>
          <nav className="flex max-w-full flex-wrap gap-1 text-sm" aria-label="Public pages">
            {publicInfoPageKeys.map((key) => {
              const item = publicInfoPages[key];
              const active = key === pageKey;
              return (
                <Link
                  key={key}
                  to={item.path}
                  className={cx(
                    "whitespace-nowrap rounded-lg px-3 py-2 font-semibold transition",
                    active ? "bg-slate-950 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                  )}
                >
                  {item.navLabel}
                </Link>
              );
            })}
          </nav>
        </div>
      </header>

      <section className="border-b border-slate-800 bg-slate-950 text-white">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[1fr_320px] lg:items-end lg:py-16">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-lime-200/25 bg-lime-300/10 px-3 py-1 text-xs font-semibold text-lime-100">
              <Icon className="h-4 w-4" />
              {page.eyebrow}
            </div>
            <h1 className="mt-5 max-w-4xl text-4xl font-semibold tracking-tight sm:text-5xl">{page.title}</h1>
            <p className="mt-5 max-w-3xl text-base leading-7 text-slate-300 sm:text-lg">{page.description}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <PublicActionLink href={page.cta?.href || "/support"} label={page.cta?.label || "Support"} />
              {page.cta?.secondaryHref && page.cta.secondaryLabel ? (
                <PublicActionLink href={page.cta.secondaryHref} label={page.cta.secondaryLabel} secondary />
              ) : null}
            </div>
          </div>

          <aside className="rounded-lg border border-white/10 bg-white/5 p-4">
            <p className="text-xs font-semibold uppercase text-slate-400">Updated</p>
            <p className="mt-1 text-sm font-semibold text-white">{page.updated}</p>
            {page.highlights?.length ? (
              <div className="mt-5 flex flex-wrap gap-2">
                {page.highlights.map((item) => (
                  <span key={item} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-semibold text-slate-200">
                    {item}
                  </span>
                ))}
              </div>
            ) : null}
          </aside>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-4 px-4 py-8 sm:px-6 lg:grid-cols-2 lg:py-10">
        {page.sections.map((section) => (
          <article key={section.title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold tracking-tight text-slate-950">{section.title}</h2>
            {section.body ? <p className="mt-3 text-sm leading-6 text-slate-600">{section.body}</p> : null}
            {section.items?.length ? (
              <ul className="mt-4 space-y-2 text-sm leading-6 text-slate-600">
                {section.items.map((item) => (
                  <li key={item} className="flex gap-2">
                    <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-600" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </article>
        ))}
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-6 text-sm text-slate-500 sm:px-6 md:flex-row md:items-center md:justify-between">
          <p>KenPost Billing Bridge. Support: {PUBLIC_SUPPORT_EMAIL}</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/privacy" className="font-semibold text-slate-700 hover:text-slate-950">
              Privacy
            </Link>
            <Link to="/support" className="font-semibold text-slate-700 hover:text-slate-950">
              Support
            </Link>
            <Link to="/sign-in" className="font-semibold text-slate-700 hover:text-slate-950">
              Sign in
            </Link>
          </div>
        </div>
      </footer>
    </main>
  );
}

function AppVersionRefreshBanner() {
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [locale, setLocale] = useState<AuthLocale>(() => initialAuthLocale());

  useEffect(() => {
    if (typeof document === "undefined") return;

    const syncLocale = () => setLocale(initialAuthLocale());
    syncLocale();
    const observer = new MutationObserver(syncLocale);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let disposed = false;

    const checkVersion = async () => {
      try {
        const response = await fetch(`/app-version.json?ts=${Date.now()}`, { cache: "no-store" });
        if (!response.ok) return;
        const manifest = (await response.json()) as { version?: unknown };
        const candidate = typeof manifest.version === "string" ? manifest.version.trim() : "";
        if (!candidate || isSameAppVersion(KENPOST_BUILD_VERSION, candidate) || disposed) return;
        setLatestVersion(candidate);
      } catch {
        // Version checks must never interrupt the customer workflow.
      }
    };

    void checkVersion();
    const interval = window.setInterval(checkVersion, APP_VERSION_CHECK_INTERVAL_MS);
    const onFocus = () => void checkVersion();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void checkVersion();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  if (!latestVersion) return null;

  const refreshIntoLatestVersion = () => {
    setIsRefreshing(true);
    window.location.replace(buildAppVersionRefreshUrl(window.location.href, latestVersion));
  };

  return (
    <div className="fixed inset-x-4 bottom-14 z-[70] mx-auto flex max-w-xl items-center justify-between gap-3 rounded-2xl border border-lime-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-2xl shadow-slate-950/15 ring-1 ring-lime-100 sm:bottom-4">
      <div className="min-w-0">
        <p className="font-semibold text-slate-950">{translatePanelText("Yeni sürüm hazır", locale)}</p>
        <p className="mt-0.5 text-xs leading-5 text-slate-500">
          {translatePanelText(
            "Sayfa eski önbellekten çalışıyor olabilir. Güncel etiket penceresi için sayfayı yenileyin.",
            locale,
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={refreshIntoLatestVersion}
        disabled={isRefreshing}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-slate-950 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-wait disabled:opacity-70"
      >
        {isRefreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
        {translatePanelText(isRefreshing ? "Güncelleniyor" : "Güncelle", locale)}
      </button>
    </div>
  );
}

function ProductVersionFloatingLink() {
  return (
    <Link
      to="/versions"
      className="fixed bottom-3 right-3 z-50 inline-flex items-center gap-2 rounded-full border border-slate-300/80 bg-white/95 px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm backdrop-blur transition hover:border-slate-400 hover:bg-white"
      title={`Product ${getCurrentProductVersionLabel()}`}
    >
      <History className="h-3.5 w-3.5" />
      {getCurrentProductVersionLabel()}
    </Link>
  );
}

function CustomerSidebarAccountSection({
  balances,
  creditTotal,
}: {
  balances?: LabelBalanceSummary[] | null;
  creditTotal: number | null;
}) {
  const creditLabel = creditTotal === null ? "..." : numberText(creditTotal);
  const creditMeterWidth =
    creditTotal === null ? "38%" : `${Math.min(100, Math.max(8, Math.round((Number(creditTotal) / 500) * 100)))}%`;
  const balanceRows = [...(balances || [])]
    .filter((balance) => Number(balance.quantityAvailable || 0) > 0)
    .sort((a, b) => balanceSortBucket(a) - balanceSortBucket(b) || balanceProductLabel(a).localeCompare(balanceProductLabel(b), currentLocaleTag()));
  const previewBalanceRows = balanceRows.slice(0, 3);
  const overflowBalanceRows = balanceRows.slice(3);
  const overflowBalanceTotal = overflowBalanceRows.reduce((sum, item) => sum + Number(item.quantityAvailable || 0), 0);
  const balanceDetailTitle = balanceRows.length
    ? balanceRows.map((balance) => `${balanceProductLabel(balance)}: ${numberText(balance.quantityAvailable)}`).join("\n")
    : "Etiket bakiyesi yok";

  return (
    <div className="customer-sidebar-account-section space-y-2">
      <p className="px-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Hesap</p>
      <div
        className="customer-sidebar-credit-card group relative rounded-xl border border-slate-200 bg-slate-50/80 p-3 shadow-sm outline-none transition focus-within:border-slate-300 focus-within:ring-2 focus-within:ring-lime-100 hover:border-slate-300"
        tabIndex={0}
        title={balanceDetailTitle}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex min-w-0 items-center gap-2 text-xs font-semibold text-slate-500">
            <Wallet className="h-4 w-4 shrink-0" />
            <span className="truncate">Kalan etiket</span>
          </span>
          <strong className="shrink-0 text-sm font-semibold text-slate-950">{creditLabel}</strong>
        </div>
        <div className="customer-sidebar-credit-meter mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
          <div className="h-full rounded-full bg-slate-950 transition-[width]" style={{ width: creditMeterWidth }} />
        </div>
        <div className="customer-sidebar-credit-footer mt-2 flex items-center justify-between gap-3 text-xs text-slate-500">
          <span>Kalan</span>
          <span className="font-semibold text-slate-700">{creditLabel}</span>
        </div>
        {balanceRows.length > 0 ? (
          <>
            <div className="mt-3 space-y-1.5 border-t border-slate-200/70 pt-2">
              {previewBalanceRows.map((balance) => (
                <div key={balance.labelProductId} className="flex items-center justify-between gap-2 text-[11px] leading-4">
                  <span className="min-w-0 truncate text-slate-500">{balanceProductLabel(balance)}</span>
                  <span className="shrink-0 font-semibold text-slate-800">{numberText(balance.quantityAvailable)}</span>
                </div>
              ))}
              {overflowBalanceRows.length > 0 ? (
                <div className="flex items-center justify-between gap-2 text-[11px] leading-4 text-slate-400">
                  <span>Diğer ürünler</span>
                  <span className="font-semibold">+{numberText(overflowBalanceTotal)}</span>
                </div>
              ) : null}
            </div>
            <div className="pointer-events-none absolute bottom-full left-0 z-40 mb-2 hidden w-full rounded-xl border border-slate-200 bg-white p-3 text-xs shadow-xl ring-1 ring-slate-900/5 group-hover:block group-focus-within:block">
              <p className="mb-2 font-semibold text-slate-950">Etiket kırılımı</p>
              <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
                {balanceRows.map((balance) => (
                  <div key={balance.labelProductId} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-2 py-1.5">
                    <span className="min-w-0 truncate text-slate-600">{balanceProductLabel(balance)}</span>
                    <span className="shrink-0 font-semibold text-slate-950">{numberText(balance.quantityAvailable)}</span>
                  </div>
                ))}
              </div>
              {overflowBalanceRows.length > 0 ? <p className="mt-2 text-[11px] text-slate-400">Kartta ilk 3 ürün gösterilir; tüm kırılım burada.</p> : null}
            </div>
          </>
        ) : creditTotal === null ? (
          <div className="mt-3 h-12 animate-pulse rounded-lg bg-slate-100" />
        ) : (
          <p className="mt-3 rounded-lg bg-white px-2 py-1.5 text-[11px] font-medium text-slate-500">Ürün bazlı bakiye yok.</p>
        )}
      </div>
    </div>
  );
}

function headerGreetingName(user: SessionUser) {
  return user.firstName || displayName(user);
}

function customerHeaderSubtitle(user: SessionUser) {
  return user.companyName || roleLabels[user.role];
}

function AppShell({
  user,
  onLogout,
  onStopImpersonation,
  children,
}: {
  user: SessionUser;
  onLogout: () => Promise<void>;
  onStopImpersonation: () => Promise<void>;
  children: ReactNode;
}) {
  const location = useLocation();
  const { locale, setLocale, copy } = useAuthLocale();
  const isAdmin = user.role === "admin";
  const isImpersonating = Boolean(user.impersonation);
  const sessionExitLabel = isImpersonating ? "Admin panele dön" : "Çıkış Yap";
  const sessionExitAction = isImpersonating ? onStopImpersonation : onLogout;
  const navItems = isAdmin ? adminNav : customerNav.filter((item) => user.role !== "subcustomer" || !item.ownerOnly);
  const accountNavItems = isAdmin ? [] : customerAccountNav.filter((item) => user.role !== "subcustomer" || !item.ownerOnly);
  const basePath = isAdmin ? "/admin" : "/customer";
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sidebarBalances, setSidebarBalances] = useState<LabelBalanceSummary[] | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const lastActivityPathRef = useRef("");
  usePanelTranslations(locale, location.pathname, user.role);

  useEffect(() => {
    const pagePath = `${location.pathname}${location.search || ""}`;
    if (lastActivityPathRef.current === pagePath) return;
    lastActivityPathRef.current = pagePath;
    const timeout = window.setTimeout(() => {
      apiClient
        .trackActivity({
          action: "page.view",
          pagePath,
          metadata: {
            role: user.role,
          },
        })
        .catch(() => undefined);
    }, 500);
    return () => window.clearTimeout(timeout);
  }, [location.pathname, location.search, user.role]);

  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) setUserMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setUserMenuOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [userMenuOpen]);

  const sidebarCreditTotal = sidebarBalances === null
    ? null
    : sidebarBalances.reduce((sum, balance) => sum + Number(balance.quantityAvailable || 0), 0);

  useEffect(() => {
    if (isAdmin) {
      setSidebarBalances(null);
      return;
    }

    let active = true;
    setSidebarBalances(null);
    apiClient
      .balances()
      .then((result) => {
        if (!active) return;
        setSidebarBalances(result.balances || []);
      })
      .catch(() => {
        if (active) setSidebarBalances(null);
      });

    return () => {
      active = false;
    };
  }, [isAdmin, user.id, user.parentCustomerId]);

  return (
    <div data-panel-i18n-root className={cx("flex min-h-screen", isAdmin ? "bg-[#f4f6fb]" : "bg-slate-50")}>
      {mobileNavOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Menüyü kapat"
            className="absolute inset-0 bg-slate-950/45"
            onClick={() => setMobileNavOpen(false)}
          />
          <aside
            className={cx(
              "relative flex h-full w-[min(21rem,88vw)] flex-col shadow-2xl",
              isAdmin
                ? "bg-[#26343d] text-white"
                : "bg-white text-slate-900"
            )}
          >
            <div className={cx("flex items-center justify-between border-b px-4 py-3", isAdmin ? "border-white/10" : "border-slate-200")}>
              <div className="flex min-w-0 flex-1 items-center justify-center gap-3">
                <div className="min-w-0 text-center">
                  <KenpostLogo className="mx-auto h-auto w-[188px]" onDark={isAdmin} />
                </div>
              </div>
              <button
                type="button"
                aria-label="Menüyü kapat"
                className={cx(
                  "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition",
                  isAdmin ? "bg-white/10 text-white hover:bg-white/15" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                )}
                onClick={() => setMobileNavOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex-1 space-y-1.5 overflow-y-auto p-3">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = navPathMatches(location.pathname, item, basePath);

                return (
                  <div key={item.to}>
                    <Link
                      to={item.to}
                      onClick={() => setMobileNavOpen(false)}
                      className={cx(
                        "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-[0.9375rem] transition",
                        isAdmin
                          ? active
                            ? "bg-white/15 text-white shadow-[0_8px_24px_rgba(0,0,0,0.2)] ring-1 ring-white/20"
                            : "text-slate-200 hover:bg-white/10 hover:text-white"
                          : active
                            ? "bg-slate-900 text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                      )}
                    >
                      <Icon
                        className={cx(
                          "h-5 w-5 shrink-0",
                          isAdmin
                            ? active
                              ? "text-lime-300"
                              : "text-slate-300 group-hover:text-white"
                            : active
                              ? "text-lime-300"
                              : "text-slate-400"
                        )}
                      />
                      <span className="truncate font-medium">{item.label}</span>
                      {item.children ? <ChevronRight className={cx("ml-auto h-4 w-4 transition", active && "rotate-90")} /> : null}
                    </Link>
                    {item.children && active ? (
                      <div className={cx("ml-7 mt-1 space-y-1 border-l pl-3", isAdmin ? "border-white/10" : "border-slate-200")}>
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = navPathMatches(location.pathname, child, basePath);
                          return (
                            <Link
                              key={child.to}
                              to={child.to}
                              onClick={() => setMobileNavOpen(false)}
                              className={cx(
                                "group flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition",
                                isAdmin
                                  ? childActive
                                    ? "bg-white/15 text-white"
                                    : "text-slate-300 hover:bg-white/10 hover:text-white"
                                  : childActive
                                    ? "bg-slate-100 text-slate-950"
                                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                              )}
                            >
                              <ChildIcon className={cx("h-4 w-4 shrink-0", isAdmin ? "text-lime-300" : "text-slate-400")} />
                              <span className="truncate">{child.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}

              {!isAdmin && accountNavItems.length > 0 ? (
                <div className="pt-3">
                  <CustomerSidebarAccountSection
                    balances={sidebarBalances}
                    creditTotal={sidebarCreditTotal}
                  />
                </div>
              ) : null}
            </nav>

            {isAdmin ? (
              <div className="border-t border-white/10 p-4">
                <div className="mb-3 flex min-w-0 items-center gap-3 rounded-xl px-3 py-2">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10">
                    <User className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{displayName(user)}</p>
                    <p className="truncate text-xs text-slate-300">{roleLabels[user.role]}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMobileNavOpen(false);
                    void onLogout();
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/15"
                >
                  <LogOut className="h-4 w-4" />
                  Çıkış
                </button>
              </div>
            ) : null}
          </aside>
        </div>
      ) : null}

      <aside
        className={cx(
          "customer-sidebar-desktop fixed inset-y-0 left-0 z-30 hidden h-screen w-72 shrink-0 flex-col border-r lg:flex",
          isAdmin
            ? "border-white/10 bg-[#26343d] text-white"
            : "border-slate-200 bg-white text-slate-900"
        )}
      >
        <div className={cx("customer-sidebar-brand border-b px-5 py-4", isAdmin ? "border-white/10" : "border-slate-200")}>
          <div className="flex items-center justify-center">
            <div className="min-w-0 text-center">
              <KenpostLogo className="customer-sidebar-logo mx-auto h-auto w-[208px]" onDark={isAdmin} />
            </div>
          </div>
        </div>

        <nav className="customer-sidebar-nav flex-1 space-y-1.5 overflow-y-auto p-3">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = navPathMatches(location.pathname, item, basePath);

            return (
              <div key={item.to}>
                <Link
                  to={item.to}
                  className={cx(
                    "customer-sidebar-nav-link group flex items-center gap-3 rounded-xl px-3 py-2.5 text-[0.9375rem] transition",
                    isAdmin
                      ? active
                        ? "bg-white/15 text-white shadow-[0_8px_24px_rgba(0,0,0,0.2)] ring-1 ring-white/20"
                        : "text-slate-200 hover:bg-white/10 hover:text-white"
                      : active
                        ? "bg-slate-900 text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                  )}
                >
                  <Icon
                    className={cx(
                      "customer-sidebar-nav-icon h-5 w-5 shrink-0",
                      isAdmin ? (active ? "text-lime-300" : "text-slate-300 group-hover:text-white") : active ? "text-lime-300" : "text-slate-400"
                    )}
                  />
                  <span className="truncate font-medium">{item.label}</span>
                  {item.children ? <ChevronRight className={cx("ml-auto h-4 w-4 transition", active && "rotate-90")} /> : null}
                </Link>
                {item.children && active ? (
                  <div className={cx("ml-7 mt-1 space-y-1 border-l pl-3", isAdmin ? "border-white/10" : "border-slate-200")}>
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = navPathMatches(location.pathname, child, basePath);
                      return (
                        <Link
                          key={child.to}
                          to={child.to}
                          className={cx(
                            "group flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition",
                            isAdmin
                              ? childActive
                                ? "bg-white/15 text-white"
                                : "text-slate-300 hover:bg-white/10 hover:text-white"
                              : childActive
                                ? "bg-slate-100 text-slate-950"
                                : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                          )}
                        >
                          <ChildIcon className={cx("h-4 w-4 shrink-0", isAdmin ? "text-lime-300" : "text-slate-400")} />
                          <span className="truncate">{child.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>

        <div className={cx("customer-sidebar-account shrink-0 border-t p-3", isAdmin ? "border-white/10" : "border-slate-200")}>
          {!isAdmin ? (
            <CustomerSidebarAccountSection balances={sidebarBalances} creditTotal={sidebarCreditTotal} />
          ) : (
            <>
              <div className="mb-3 flex items-center gap-3 rounded-xl px-3 py-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10">
                  <User className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{displayName(user)}</p>
                  <p className="truncate text-xs text-slate-300">{roleLabels[user.role]}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => void onLogout()}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/15"
              >
                <LogOut className="h-4 w-4" />
                Çıkış
              </button>
            </>
          )}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col lg:ml-72">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 px-3 py-3 backdrop-blur sm:px-4 lg:px-8">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                aria-label="Menüyü aç"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 lg:hidden"
                onClick={() => setMobileNavOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </button>
              <Link to={basePath} className="inline-flex shrink-0 lg:hidden" aria-label="KenPost ana sayfa">
                <KenpostLogo className="w-[118px] sm:w-[136px]" />
              </Link>
              <div className="hidden min-w-0 sm:block">
                {isAdmin ? (
                  <>
                    <div className="flex min-w-0 items-center gap-2">
                      <p className="shrink-0 text-xs font-semibold text-slate-500">{roleLabels[user.role]}</p>
                      <span className="min-w-0 truncate text-xs font-semibold text-lime-700">{KENPOST_VERSION_LABEL}</span>
                    </div>
                    <h2 className="truncate text-base font-semibold text-slate-950 sm:text-lg">{currentPageTitle(location.pathname, user.role)}</h2>
                  </>
                ) : (
                  <>
                    <h2 className="truncate text-base font-semibold text-slate-950 sm:text-lg">Tekrar hoş geldin, {headerGreetingName(user)}</h2>
                    <p className="mt-0.5 truncate text-sm text-slate-500">{customerHeaderSubtitle(user)}</p>
                  </>
                )}
              </div>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <LanguageSwitcher locale={locale} onLocaleChange={setLocale} label={copy.languageLabel} />
              {isAdmin ? (
                <div
                  className="hidden min-w-0 max-w-[230px] items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600 shadow-sm sm:flex"
                  title={`${displayName(user)} · ${user.email}`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-xs font-semibold leading-4 text-slate-950">{displayName(user)}</span>
                    <span className="block truncate text-[11px] leading-4 text-slate-500">{user.email}</span>
                  </span>
                  <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-500" />
                </div>
              ) : (
                <div ref={userMenuRef} className="relative min-w-0">
                  <button
                    type="button"
                    aria-expanded={userMenuOpen}
                    aria-haspopup="menu"
                    onClick={() => setUserMenuOpen((current) => !current)}
                    className={cx(
                      "flex h-10 min-w-10 items-center justify-center gap-2 rounded-xl border px-2 text-left transition sm:h-auto sm:max-w-[250px] sm:px-3 sm:py-1.5",
                      userMenuOpen
                        ? "border-slate-300 bg-slate-50 shadow-sm"
                        : "border-transparent hover:border-slate-200 hover:bg-slate-50",
                    )}
                    title={`${displayName(user)} · ${user.email}`}
                  >
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-slate-900 text-white sm:hidden"><User className="h-3.5 w-3.5" /></span>
                    <span className="hidden min-w-0 text-right sm:block">
                      <span className="block truncate text-sm font-semibold leading-5 text-slate-950">{displayName(user)}</span>
                      <span className="block truncate text-xs leading-4 text-slate-500">{user.email}</span>
                    </span>
                    <ChevronDown className={cx("hidden h-4 w-4 shrink-0 text-slate-400 transition sm:block", userMenuOpen && "rotate-180")} />
                  </button>
                  {userMenuOpen ? (
                    <div role="menu" className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl shadow-slate-950/15 ring-1 ring-slate-900/5">
                      <div className="border-b border-slate-100 px-3 py-2 sm:hidden">
                        <p className="truncate text-sm font-semibold text-slate-950">{displayName(user)}</p>
                        <p className="truncate text-xs text-slate-500">{user.email}</p>
                      </div>
                      <div className="space-y-1 pt-1 sm:pt-0">
                        {accountNavItems.map((item) => {
                          const Icon = item.icon;
                          const active = location.pathname.startsWith(item.to);
                          return (
                            <Link
                              key={item.to}
                              to={item.to}
                              role="menuitem"
                              onClick={() => setUserMenuOpen(false)}
                              className={cx(
                                "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition",
                                active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100 hover:text-slate-950",
                              )}
                            >
                              <Icon className={cx("h-4 w-4 shrink-0", active ? "text-lime-300" : "text-slate-400")} />
                              <span>{item.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
              <button
                type="button"
                aria-label={sessionExitLabel}
                title={sessionExitLabel}
                onClick={() => void sessionExitAction()}
                className={cx(
                  "inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold shadow-sm transition focus:outline-none focus:ring-2",
                  isImpersonating
                    ? "border border-amber-300 bg-amber-50 text-amber-950 hover:bg-amber-100 focus:ring-amber-200"
                    : "bg-slate-950 text-white hover:bg-slate-800 focus:ring-slate-300"
                )}
              >
                {isImpersonating ? <ArrowLeft className="h-4 w-4" /> : <LogOut className="h-4 w-4" />}
                <span className="hidden sm:inline">{sessionExitLabel}</span>
              </button>
            </div>
          </div>
          {isImpersonating ? (
            <div className="mt-3 flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
              <span>
                Admin destek modu: {user.impersonation?.adminDisplayName || user.impersonation?.adminEmail || "Admin"} olarak müşteri panelindesiniz.
              </span>
              <button
                type="button"
                onClick={() => void onStopImpersonation()}
                className="inline-flex w-fit items-center justify-center rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 shadow-sm transition hover:bg-amber-100"
              >
                Admin panele dön
              </button>
            </div>
          ) : null}
        </header>
        <MigrationNotice />
        <main key={`${locale}:${location.pathname}`} className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}

function MigrationNotice() {
  const visibleFrom = Date.parse("2026-05-29T22:00:00.000Z");
  const visibleUntil = Date.parse("2026-05-30T22:30:00.000Z");
  const now = Date.now();
  if (now < visibleFrom || now >= visibleUntil) return null;

  return (
    <div className="border-b border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 lg:px-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
          <div>
            <p className="text-sm font-semibold">30.05.2026 KenPost Version 2 geçişi</p>
            <p className="mt-0.5 text-sm text-amber-900">
              Bugün Berlin saatiyle 18:00-23:59 arasında sistem kapalı olacaktır. Geçişten sonra yeni sistemimiz ve son özelliklerimizle tekrar karşınızda olacağız.
            </p>
          </div>
        </div>
        <span className="inline-flex w-fit rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-900">
          18:00-23:59
        </span>
      </div>
    </div>
  );
}

function useAuthLocale() {
  const [locale, setLocaleState] = useState<AuthLocale>(() => initialAuthLocale());

  function setLocale(nextLocale: AuthLocale) {
    setLocaleState(nextLocale);
  }

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(AUTH_LOCALE_STORAGE_KEY, locale);
    window.document.cookie = `${AUTH_LOCALE_COOKIE_NAME}=${locale}; path=/; max-age=31536000; SameSite=Lax`;
    window.document.documentElement.lang = locale;
  }, [locale]);

  return { locale, setLocale, copy: authCopy[locale] };
}

function usePanelTranslations(locale: AuthLocale, watchKey: string, role: UserRole) {
  useEffect(() => {
    const root = document.querySelector<HTMLElement>("[data-panel-i18n-root]");
    if (!root) return;

    document.title = `${translatePanelText(currentPageTitle(watchKey, role), locale)} | ${role === "admin" ? "KenPost Admin" : "KenPost"}`;
    const previousLocale = root.dataset.panelI18nLocale as AuthLocale | undefined;
    root.dataset.panelI18nLocale = locale;

    // The application source is Turkish. Avoid walking the complete page and
    // observing every table mutation for the default locale. When returning
    // from another language, one pass restores the persistent shell first.
    if (locale === "tr") {
      if (previousLocale && previousLocale !== "tr") applyPanelTranslations(root, locale);
      return;
    }

    applyPanelTranslations(root, locale);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes") {
          translatePanelElementAttributes(mutation.target as Element, locale);
          continue;
        }
        if (mutation.type === "characterData") {
          applyPanelTranslationToNode(mutation.target, locale);
          continue;
        }
        mutation.addedNodes.forEach((node) => applyPanelTranslationToNode(node, locale));
      }
    });
    observer.observe(root, {
      attributes: true,
      attributeFilter: [...PANEL_TRANSLATABLE_ATTRIBUTES],
      characterData: true,
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [locale, role, watchKey]);
}

function authPageDocumentTitle(pathname: string, copy: AuthCopy) {
  if (pathname === "/sign-in") return copy.signIn.title;
  if (pathname === "/sign-up") return copy.signUp.title;
  if (pathname === "/forgot-password") return copy.forgotPassword.title;
  if (pathname === "/reset-password") return copy.resetPassword.title;
  if (pathname === "/email-two-factor") return copy.twoFactor.title;
  if (pathname === "/verify-email") return copy.verifyEmail.title;
  if (pathname === "/verify-email-sent") return copy.verifyEmailSent.title;
  return "";
}

function LanguageSwitcher({
  locale,
  onLocaleChange,
  label,
}: {
  locale: AuthLocale;
  onLocaleChange: (locale: AuthLocale) => void;
  label: string;
}) {
  return (
    <div className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-sm" aria-label={label}>
      {authLocales.map((item) => {
        const active = item.code === locale;
        return (
          <button
            key={item.code}
            type="button"
            onClick={() => onLocaleChange(item.code)}
            className={cx(
              "h-8 min-w-8 rounded-md px-2 text-xs font-semibold transition sm:px-3",
              active ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900",
            )}
            aria-pressed={active}
            title={item.label}
          >
            {item.short}
          </button>
        );
      })}
    </div>
  );
}

function AuthLayout({
  children,
  locale,
  onLocaleChange,
}: {
  children: ReactNode;
  locale: AuthLocale;
  onLocaleChange: (locale: AuthLocale) => void;
}) {
  const copy = authCopy[locale];
  const publicNavCopy = authPublicNavCopy[locale];
  const location = useLocation();

  useEffect(() => {
    if (typeof document === "undefined") return;
    const title = authPageDocumentTitle(location.pathname, copy);
    if (title) document.title = `${title} | KenPost`;
  }, [copy, location.pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-[#f4f6fb] text-slate-950">
      <MigrationNotice />
      <div className="mx-auto flex w-full max-w-7xl flex-1 items-center justify-center px-4 py-8">
        <div className="grid w-full max-w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl xl:grid-cols-[minmax(0,1fr)_460px] 2xl:grid-cols-[minmax(0,1fr)_500px]">
          <div className="hidden min-w-0 overflow-hidden bg-[#26343d] p-10 text-white xl:block">
            <div className="flex h-full min-h-[600px] flex-col justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <div>
                    <KenpostLogo className="h-auto w-[260px]" onDark />
                  </div>
                </div>
                <div className="mt-10 max-w-2xl">
                  <p className="text-sm font-semibold uppercase text-lime-200">{copy.brand.featuresLabel}</p>
                  <h2 className="mt-4 text-4xl font-semibold leading-tight">{copy.brand.heading}</h2>
                  <p className="mt-4 max-w-xl text-base leading-7 text-slate-300">{copy.brand.body}</p>
                </div>
                <div className="mt-8 grid min-w-0 grid-cols-2 gap-3 min-[1320px]:grid-cols-4">
                  {copy.features.map((feature, index) => {
                    const accents = ["text-lime-300", "text-emerald-300", "text-amber-300", "text-violet-300"];
                    const icons = [Tags, ShoppingCart, Store, ShieldCheck];
                    const FeatureIcon = icons[index] || Store;
                    return (
                      <div key={feature.label} className="rounded-lg border border-white/10 bg-white/[0.06] p-3">
                        <FeatureIcon className={cx("h-5 w-5", accents[index] || "text-lime-200")} />
                        <p className="mt-3 text-sm font-semibold leading-5">{feature.label}</p>
                        <p className="mt-1 text-xs leading-5 text-slate-300">{feature.detail}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-lime-200">{copy.brand.marketplacesLabel}</p>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  {authMarketplaceTiles.map((platform) => (
                    <div key={platform.label} className="flex h-16 items-center justify-center rounded-lg border border-white/10 bg-white px-4 py-3 shadow-sm">
                      <img
                        src={platform.logoSrc}
                        alt={platform.label}
                        className={cx("max-h-9 max-w-full object-contain", platform.logoClassName)}
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex min-h-[640px] min-w-0 flex-col p-6 sm:p-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 xl:hidden">
                <div>
                  <KenpostLogo className="h-auto w-[220px]" />
                </div>
              </div>
              <div className="sm:ml-auto">
                <LanguageSwitcher locale={locale} onLocaleChange={onLocaleChange} label={copy.languageLabel} />
              </div>
            </div>
            <div className="flex flex-1 flex-col justify-center py-8">{children}</div>
            <p className="flex items-center justify-center gap-2 text-center text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 text-emerald-500" />
              {copy.brand.secureNote}
            </p>
            <nav className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center text-xs font-semibold text-slate-500" aria-label={publicNavCopy.ariaLabel}>
              {authPublicLinks.map((item) => (
                <Link key={item.path} to={item.path} className="hover:text-slate-950">
                  {publicNavCopy.links[item.key]}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}

function safeAuthReturnPath(search: string, user: Pick<SessionUser, "role">) {
  const requested = new URLSearchParams(search).get("returnTo") || "";
  if (user.role !== "admin" && requested.startsWith("/oauth/authorize?") && !requested.startsWith("//")) return requested;
  if (user.role !== "admin" && isCustomerConnectionReturnPath(requested)) return requested;
  return user.role === "admin" ? "/admin" : "/customer";
}

function emailTwoFactorPath(response: SignInEmailTwoFactorResponse, rememberDevice = false, returnTo = "") {
  const params = new URLSearchParams({
    challenge: response.challenge,
    email: response.email,
  });
  if (rememberDevice) params.set("rememberDevice", "1");
  if (returnTo.startsWith("/oauth/authorize?") && !returnTo.startsWith("//")) params.set("returnTo", returnTo);
  if (isCustomerConnectionReturnPath(returnTo)) params.set("returnTo", returnTo);
  return `/email-two-factor?${params.toString()}`;
}

function SignIn({ onSignedIn }: { onSignedIn: (user: SessionUser) => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, setLocale, copy } = useAuthLocale();
  const [email, setEmail] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(AUTH_REMEMBER_EMAIL_STORAGE_KEY) || "";
  });
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [recognizedAccounts, setRecognizedAccounts] = useState<RecognizedAccount[]>([]);
  const [recognizedLoading, setRecognizedLoading] = useState(true);
  const [showCredentialForm, setShowCredentialForm] = useState(false);
  const [recognizedSubmittingId, setRecognizedSubmittingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    apiClient
      .recognizedAccounts()
      .then((result) => {
        if (!active) return;
        setRecognizedAccounts(result.accounts);
        setShowCredentialForm(result.accounts.length === 0);
      })
      .catch(() => {
        if (!active) return;
        setShowCredentialForm(true);
      })
      .finally(() => {
        if (active) setRecognizedLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  function persistRememberedEmail() {
    if (rememberMe) {
      window.localStorage.setItem(AUTH_REMEMBER_EMAIL_STORAGE_KEY, email.trim());
    } else {
      window.localStorage.removeItem(AUTH_REMEMBER_EMAIL_STORAGE_KEY);
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await apiClient.login(email, password, rememberMe);
      if ("requiresTwoFactor" in result && result.requiresTwoFactor) {
        persistRememberedEmail();
        navigate(emailTwoFactorPath(result, rememberMe, new URLSearchParams(location.search).get("returnTo") || ""), { replace: true });
        return;
      }
      if ("user" in result) {
        persistRememberedEmail();
        onSignedIn(result.user);
        navigate(safeAuthReturnPath(location.search, result.user), { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? translateAuthApiErrorMessage(err.message, locale, copy.signIn.genericError) : copy.signIn.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  async function signInWithRecognizedAccount(account: RecognizedAccount) {
    setError(null);
    setRecognizedSubmittingId(account.id);
    try {
      const result = await apiClient.loginRecognizedAccount(account.id);
      onSignedIn(result.user);
      navigate(safeAuthReturnPath(location.search, result.user), { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? translateAuthApiErrorMessage(err.message, locale, copy.signIn.recognizedError) : copy.signIn.recognizedError);
      setEmail(account.email);
      setShowCredentialForm(true);
    } finally {
      setRecognizedSubmittingId(null);
    }
  }

  async function removeRecognizedAccount(account: RecognizedAccount) {
    setError(null);
    setRemovingId(account.id);
    try {
      await apiClient.removeRecognizedAccount(account.id);
      setRecognizedAccounts((current) => {
        const next = current.filter((item) => item.id !== account.id);
        if (next.length === 0) setShowCredentialForm(true);
        return next;
      });
    } catch (err) {
      setError(err instanceof ApiError ? translateAuthApiErrorMessage(err.message, locale, copy.signIn.removeFailed) : copy.signIn.removeFailed);
    } finally {
      setRemovingId(null);
    }
  }

  function openCredentialForm() {
    setError(null);
    setEmail("");
    setPassword("");
    setShowCredentialForm(true);
  }

  const showRecognizedAccounts = !recognizedLoading && recognizedAccounts.length > 0 && !showCredentialForm;

  return (
    <AuthLayout locale={locale} onLocaleChange={setLocale}>
      <AuthHeader
        title={showRecognizedAccounts ? copy.signIn.recognizedTitle : copy.signIn.title}
        caption={showRecognizedAccounts ? copy.signIn.recognizedCaption : copy.signIn.caption}
      />
      {recognizedLoading ? (
        <div className="mt-8 flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          {copy.signIn.checkingAccounts}
        </div>
      ) : null}
      {showRecognizedAccounts ? (
        <div className="mt-8 space-y-3">
          {recognizedAccounts.map((account) => {
            const initials = (account.displayName || account.email)
              .split(/\s+/)
              .map((part) => part[0])
              .join("")
              .slice(0, 2)
              .toUpperCase();
            const signingIn = recognizedSubmittingId === account.id;
            const removing = removingId === account.id;
            return (
              <div key={account.id} className="flex items-stretch gap-2 rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
                <button
                  type="button"
                  disabled={Boolean(recognizedSubmittingId || removingId)}
                  onClick={() => void signInWithRecognizedAccount(account)}
                  aria-label={`${copy.signIn.useAccount}: ${account.displayName}`}
                  className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">
                    {initials || <User className="h-5 w-5" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-slate-950">{account.displayName}</span>
                    <span className="block truncate text-xs text-slate-500">{account.email}</span>
                  </span>
                  {signingIn ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-500" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
                </button>
                <button
                  type="button"
                  disabled={Boolean(recognizedSubmittingId || removingId)}
                  onClick={() => void removeRecognizedAccount(account)}
                  aria-label={`${copy.signIn.removeAccount}: ${account.displayName}`}
                  title={copy.signIn.removeAccount}
                  className="inline-flex w-10 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              </div>
            );
          })}
          {error ? <ErrorBanner message={error} /> : null}
          <button
            type="button"
            onClick={openCredentialForm}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50"
          >
            <User className="h-4 w-4" />
            {copy.signIn.useAnotherAccount}
          </button>
        </div>
      ) : null}
      {!recognizedLoading && showCredentialForm ? <form className="mt-8 space-y-4" onSubmit={onSubmit}>
        <Field label={copy.fields.email} type="email" value={email} onChange={setEmail} autoComplete="email" required />
        <Field
          label={copy.fields.password}
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required
          showPasswordLabel={copy.passwordToggle.show}
          hidePasswordLabel={copy.passwordToggle.hide}
        />
        <div className="flex items-center justify-between gap-3 text-sm">
          <label className="inline-flex min-w-0 items-center gap-2 font-medium text-slate-600">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(event) => setRememberMe(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-slate-900 accent-slate-900 focus:ring-slate-900"
            />
            <span>{copy.signIn.rememberMe}</span>
          </label>
          <Link className="font-semibold text-slate-950 hover:underline" to="/forgot-password">
            {copy.signIn.forgot}
          </Link>
        </div>
        {error ? <ErrorBanner message={error} /> : null}
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {submitting ? copy.signIn.submitting : copy.signIn.submit}
        </button>
        {recognizedAccounts.length > 0 ? (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setShowCredentialForm(false);
            }}
            className="w-full text-center text-sm font-semibold text-slate-600 hover:text-slate-950 hover:underline"
          >
            {copy.signIn.backToAccounts}
          </button>
        ) : null}
      </form> : null}
      {!recognizedLoading && (showCredentialForm || recognizedAccounts.length === 0) ? <p className="mt-6 text-center text-sm text-slate-500">
        {copy.signIn.noAccount}{" "}
        <Link className="font-semibold text-slate-950 hover:underline" to="/sign-up">
          {copy.signIn.register}
        </Link>
      </p> : null}
    </AuthLayout>
  );
}

function EmailTwoFactor({ onSignedIn }: { onSignedIn: (user: SessionUser) => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const { locale, setLocale, copy } = useAuthLocale();
  const rememberDevice = params.get("rememberDevice") === "1";
  const [challenge, setChallenge] = useState(params.get("challenge") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(challenge ? null : copy.twoFactor.missingChallenge);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!challenge) {
      setError(copy.twoFactor.missingChallenge);
      return;
    }
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const result = await apiClient.verifyEmailTwoFactor({ challenge, code, rememberDevice });
      onSignedIn(result.user);
      navigate(safeAuthReturnPath(location.search, result.user), { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? translateAuthApiErrorMessage(err.message, locale, copy.twoFactor.invalidCode) : copy.twoFactor.invalidCode);
    } finally {
      setSubmitting(false);
    }
  }

  async function resendCode() {
    if (!challenge) {
      setError(copy.twoFactor.missingChallenge);
      return;
    }
    setError(null);
    setSuccess(null);
    setResending(true);
    try {
      const result = await apiClient.resendEmailTwoFactor(challenge);
      setChallenge(result.challenge);
      setEmail(result.email);
      setCode("");
      setSuccess(copy.twoFactor.newCodeSent);
      navigate(emailTwoFactorPath(result, rememberDevice, params.get("returnTo") || ""), { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? translateAuthApiErrorMessage(err.message, locale, copy.twoFactor.resendFailed) : copy.twoFactor.resendFailed);
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthLayout locale={locale} onLocaleChange={setLocale}>
      <AuthHeader title={copy.twoFactor.title} caption={copy.twoFactor.caption} />
      <form className="mt-8 space-y-4" onSubmit={onSubmit}>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
          {copy.twoFactor.accountLabel} <span className="font-semibold text-slate-950">{email || copy.twoFactor.unknownAccount}</span>
        </div>
        <label className="block space-y-2">
          <span className="text-sm font-medium text-slate-700">{copy.twoFactor.codeLabel}</span>
          <input
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            className="h-12 w-full rounded-xl border border-slate-300 bg-white px-3 text-center font-mono text-xl text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
            placeholder="000000"
            required
          />
        </label>
        {error ? <ErrorBanner message={error} /> : null}
        {success ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-700">{success}</div> : null}
        <button
          type="submit"
          disabled={submitting || code.length !== 6}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {copy.twoFactor.submit}
        </button>
      </form>
      <div className="mt-5 flex items-center justify-between gap-3 text-sm">
        <button
          type="button"
          onClick={() => void resendCode()}
          disabled={resending || !challenge}
          className="font-semibold text-slate-950 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
        >
          {resending ? copy.twoFactor.resending : copy.twoFactor.resend}
        </button>
        <Link className="font-semibold text-slate-500 hover:text-slate-950" to="/sign-in">
          {copy.twoFactor.backToSignIn}
        </Link>
      </div>
    </AuthLayout>
  );
}

function ForgotPassword() {
  const { locale, setLocale, copy } = useAuthLocale();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.forgotPassword(email);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? translateAuthApiErrorMessage(err.message, locale, copy.forgotPassword.genericError) : copy.forgotPassword.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout locale={locale} onLocaleChange={setLocale}>
      <AuthHeader title={copy.forgotPassword.title} caption={copy.forgotPassword.caption} />
      {done ? (
        <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          {copy.forgotPassword.success}
        </div>
      ) : (
        <form className="mt-8 space-y-4" onSubmit={onSubmit}>
          <Field label={copy.fields.email} type="email" value={email} onChange={setEmail} autoComplete="email" required />
          {error ? <ErrorBanner message={error} /> : null}
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {copy.forgotPassword.submit}
          </button>
        </form>
      )}
      <p className="mt-6 text-center text-sm text-slate-500">
        <Link className="font-semibold text-slate-950 hover:underline" to="/sign-in">
          {copy.forgotPassword.backToSignIn}
        </Link>
      </p>
    </AuthLayout>
  );
}

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const { locale, setLocale, copy } = useAuthLocale();
  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token") || params.get("code") || "";
  }, [location.search]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!token) {
      setError(copy.resetPassword.invalidToken);
      return;
    }
    if (password.length < 8) {
      setError(copy.resetPassword.minLength);
      return;
    }
    if (password !== confirmPassword) {
      setError(copy.resetPassword.mismatch);
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.resetPassword({ password, token });
      setDone(true);
      window.setTimeout(() => navigate("/sign-in", { replace: true }), 1500);
    } catch (err) {
      setError(err instanceof ApiError ? translateAuthApiErrorMessage(err.message, locale, copy.resetPassword.genericError) : copy.resetPassword.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout locale={locale} onLocaleChange={setLocale}>
      <AuthHeader title={copy.resetPassword.title} caption={copy.resetPassword.caption} />
      {done ? (
        <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          {copy.resetPassword.success}
        </div>
      ) : (
        <form className="mt-8 space-y-4" onSubmit={onSubmit}>
          <Field
            label={copy.fields.newPassword}
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
            showPasswordLabel={copy.passwordToggle.show}
            hidePasswordLabel={copy.passwordToggle.hide}
          />
          <Field
            label={copy.fields.confirmPassword}
            type="password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            required
            showPasswordLabel={copy.passwordToggle.show}
            hidePasswordLabel={copy.passwordToggle.hide}
          />
          {error ? <ErrorBanner message={error} /> : null}
          <button
            type="submit"
            disabled={submitting || !token}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {copy.resetPassword.submit}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}

function VerifyEmailPage() {
  const location = useLocation();
  const { locale, setLocale, copy } = useAuthLocale();
  const code = useMemo(() => new URLSearchParams(location.search).get("code") || new URLSearchParams(location.search).get("token") || "", [location.search]);
  const [status, setStatus] = useState<"loading" | "success" | "failed">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    if (!code) {
      setStatus("failed");
      setMessage(copy.verifyEmail.missingCode);
      return;
    }
    apiClient
      .verifyEmail(code)
      .then(() => {
        if (active) setStatus("success");
      })
      .catch((err) => {
        if (!active) return;
        setStatus("failed");
        setMessage(err instanceof ApiError ? translateAuthApiErrorMessage(err.message, locale, copy.verifyEmail.genericError) : copy.verifyEmail.genericError);
      });
    return () => {
      active = false;
    };
  }, [code, copy.verifyEmail.genericError, copy.verifyEmail.missingCode, locale]);

  return (
    <AuthLayout locale={locale} onLocaleChange={setLocale}>
      <AuthHeader title={copy.verifyEmail.title} caption={copy.verifyEmail.caption} />
      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
        {status === "loading" ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {copy.verifyEmail.checking}
          </span>
        ) : status === "success" ? (
          <span className="text-emerald-700">{copy.verifyEmail.success}</span>
        ) : (
          <span className="text-red-700">{message || copy.verifyEmail.invalid}</span>
        )}
      </div>
      <Link className="mt-6 inline-flex w-full justify-center rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-slate-800" to="/sign-in">
        {copy.verifyEmail.backToSignIn}
      </Link>
    </AuthLayout>
  );
}

function VerifyEmailSentPage() {
  const location = useLocation();
  const { locale, setLocale, copy } = useAuthLocale();
  const emailFromUrl = useMemo(() => new URLSearchParams(location.search).get("email") || "", [location.search]);
  const [email, setEmail] = useState(emailFromUrl);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function resendVerificationEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setResending(true);
    try {
      await apiClient.sendVerificationEmail(email);
      setNotice(copy.verifyEmailSent.resent);
    } catch (err) {
      setError(err instanceof ApiError ? translateAuthApiErrorMessage(err.message, locale, copy.verifyEmailSent.resendFailed) : copy.verifyEmailSent.resendFailed);
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthLayout locale={locale} onLocaleChange={setLocale}>
      <AuthHeader title={copy.verifyEmailSent.title} caption={copy.verifyEmailSent.caption} />
      <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
        {email ? copy.verifyEmailSent.successWithEmail(email) : copy.verifyEmailSent.success}
      </div>
      <form className="mt-5 space-y-3 rounded-xl border border-slate-200 bg-white p-4" onSubmit={resendVerificationEmail}>
        {!emailFromUrl ? (
          <Field label={copy.verifyEmailSent.emailLabel} type="email" value={email} onChange={setEmail} autoComplete="email" required />
        ) : null}
        {error ? <ErrorBanner message={error} /> : null}
        {notice ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-700">{notice}</div> : null}
        <button
          type="submit"
          disabled={resending || !email}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {resending ? copy.verifyEmailSent.resending : copy.verifyEmailSent.resend}
        </button>
      </form>
      <div className="mt-6 grid gap-3">
        <Link className="inline-flex w-full justify-center rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-slate-800" to="/sign-in">
          {copy.verifyEmailSent.signIn}
        </Link>
        <Link className="inline-flex w-full justify-center rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50" to="/sign-up">
          {copy.verifyEmailSent.backToSignUp}
        </Link>
      </div>
    </AuthLayout>
  );
}

function referralCodeFromSearch(search: string) {
  const params = new URLSearchParams(search);
  return (params.get("ref") || params.get("referral") || params.get("affiliate") || "").trim().toUpperCase();
}

function SignUp({ onSignedIn }: { onSignedIn: (user: SessionUser) => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, setLocale, copy } = useAuthLocale();
  const initialReferralCode = useMemo(() => referralCodeFromSearch(location.search), [location.search]);
  const [form, setForm] = useState({
    companyName: "",
    email: "",
    phone: "",
    password: "",
    referralCode: initialReferralCode,
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (form.phone.replace(/\D/g, "").length < 6) {
      setError(translateAuthApiErrorMessage("Telefon numarası zorunludur.", locale, copy.signUp.genericError));
      return;
    }
    setSubmitting(true);
    try {
      const normalizedReferralCode = form.referralCode.trim().toUpperCase();
      const result = await apiClient.register({
        companyName: form.companyName,
        email: form.email,
        password: form.password,
        phone: form.phone,
        ...(normalizedReferralCode ? { referralCode: normalizedReferralCode } : {}),
      });
      if ("requiresEmailVerification" in result && result.requiresEmailVerification) {
        navigate(`/verify-email-sent?email=${encodeURIComponent(result.email)}`, { replace: true });
        return;
      }
      if ("user" in result) {
        onSignedIn(result.user);
        navigate(result.user.role === "admin" ? "/admin" : "/customer", { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? translateAuthApiErrorMessage(err.message, locale, copy.signUp.genericError) : copy.signUp.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout locale={locale} onLocaleChange={setLocale}>
      <AuthHeader title={copy.signUp.title} caption={copy.signUp.caption} />
      <form className="mt-8 space-y-4" onSubmit={onSubmit}>
        <Field label={copy.fields.email} type="email" value={form.email} onChange={(value) => setForm((current) => ({ ...current, email: value }))} autoComplete="email" required />
        <Field label={copy.fields.phone} type="tel" value={form.phone} onChange={(value) => setForm((current) => ({ ...current, phone: value }))} autoComplete="tel" required />
        <Field
          label={copy.fields.password}
          type="password"
          value={form.password}
          onChange={(value) => setForm((current) => ({ ...current, password: value }))}
          autoComplete="new-password"
          required
          showPasswordLabel={copy.passwordToggle.show}
          hidePasswordLabel={copy.passwordToggle.hide}
        />
        <Field label={copy.fields.company} value={form.companyName} onChange={(value) => setForm((current) => ({ ...current, companyName: value }))} autoComplete="organization" />
        <Field label={copy.fields.referralCode} value={form.referralCode} onChange={(value) => setForm((current) => ({ ...current, referralCode: value.toUpperCase() }))} autoComplete="off" />
        {error ? <ErrorBanner message={error} /> : null}
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {copy.signUp.submit}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        {copy.signUp.haveAccount}{" "}
        <Link className="font-semibold text-slate-950 hover:underline" to="/sign-in">
          {copy.signUp.signIn}
        </Link>
      </p>
    </AuthLayout>
  );
}

function Dashboard({ user }: { user: SessionUser }) {
  const [summary, setSummary] = useState<DashboardSummary | null>(() => readCachedDashboardSummary(user));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    apiClient
      .dashboard()
      .then((result) => {
        if (!active) return;
        setSummary(result);
        setError(null);
        cacheDashboardSummary(user, result);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Kontrol paneli verisi alınamadı.");
      });
    return () => {
      active = false;
    };
  }, [user]);

  const cards = useMemo(() => {
    const totals = summary?.totals;
    if (user.role === "admin") {
      return [
        { label: "Müşteriler", value: totals?.customers ?? 0, icon: Users },
        { label: "Alt müşteriler", value: totals?.subcustomers ?? 0, icon: ShieldCheck },
        { label: "Siparişler", value: totals?.orders ?? 0, icon: ShoppingCart },
        { label: "Gönderiler", value: totals?.shipments ?? 0, icon: Truck },
      ];
    }

    return [
      { label: "Mağazalar", value: totals?.stores ?? 0, icon: Store },
      { label: "Siparişler", value: totals?.orders ?? 0, icon: ShoppingCart },
      { label: "Gönderiler", value: totals?.shipments ?? 0, icon: Send },
      { label: "Kullanılabilir etiket", value: totals?.availableLabels ?? 0, icon: Tags },
    ];
  }, [summary, user.role]);
  const shipmentPerformance = summary?.performance?.shipments;
  const dhlPerformance = summary?.performance?.dhl;
  const operations = summary?.operations;
  const trackingDenominator = shipmentPerformance
    ? shipmentPerformance.trackingSuccess + shipmentPerformance.trackingFailed + shipmentPerformance.trackingPending + shipmentPerformance.trackingDisabled
    : 0;
  const trackingSuccessRate = trackingDenominator > 0 ? Math.round((Number(shipmentPerformance?.trackingSuccess || 0) / trackingDenominator) * 100) : 0;
  const dhlErrorRate = dhlPerformance?.checked ? Math.round((Number(dhlPerformance.errors || 0) / dhlPerformance.checked) * 100) : 0;
  const dashboardLoading = !summary;
  const recentItems = useMemo(() => {
    if (!summary) return placeholderActivity();
    return summary.recentActivity.length > 0 ? summary.recentActivity : emptyDashboardActivity(user.role);
  }, [summary, user.role]);

  if (error && !summary) {
    return <ErrorPanel title="Kontrol paneli açılamadı" message={error} />;
  }

  if (user.role !== "admin") {
    const totals = summary?.totals;
    const labelInventory = summary?.labelInventory;
    const availableLabelCount = Number(labelInventory?.availableBalance ?? totals?.availableLabels ?? 0);
    const totalLabelCount = Number(labelInventory?.total ?? totals?.totalLabels ?? availableLabelCount);
    const usedLabelCount = Number(labelInventory?.consumed ?? totals?.usedLabels ?? 0);
    const voidedLabelCount = Number(labelInventory?.voided ?? totals?.voidedLabels ?? 0);
    const expiredLabelCount = Number(labelInventory?.expired ?? totals?.expiredLabels ?? 0);
    const labelHistoryTotal = Math.max(totalLabelCount, availableLabelCount + usedLabelCount + voidedLabelCount + expiredLabelCount);
    const availableLabelWidth = labelHistoryTotal > 0 ? Math.max(6, Math.min(100, Math.round((availableLabelCount / labelHistoryTotal) * 100))) : 0;
    const quickActions = [
      { label: "Etiket oluştur", detail: "Sipariş için yeni etiket", to: "/customer/create-label", icon: PlusSquare, primary: true },
      { label: "Mağazalar", detail: "Bağlantıları ve senkronu yönet", to: "/customer/stores", icon: Store },
      { label: "Etiket satın al", detail: "Bakiye ve fiyatları kontrol et", to: "/customer/buy-labels", icon: CreditCard },
      { label: "Gönderiler", detail: "Tracking ve etiket çıktıları", to: "/customer/shipments", icon: Send },
    ];
    const customerCards: Array<{ icon: ReactNode; label: string; tone: keyof typeof adminStatToneClasses; value: unknown }> = [
      { icon: <Store className="h-5 w-5" />, label: "Mağaza", tone: "cyan", value: totals?.stores ?? 0 },
      { icon: <ShoppingCart className="h-5 w-5" />, label: "Sipariş", tone: "amber", value: totals?.orders ?? 0 },
      { icon: <Truck className="h-5 w-5" />, label: "Gönderi", tone: "violet", value: totals?.shipments ?? 0 },
      { icon: <Tags className="h-5 w-5" />, label: "Kalan etiket", tone: "emerald", value: availableLabelCount },
    ];
    const operationHighlights: Array<{ detail: string; icon: ReactNode; label: string; tone: keyof typeof adminStatToneClasses; value: unknown }> = [
      { detail: "Bugünkü çıkış", icon: <Clock3 className="h-5 w-5" />, label: "Bugün", tone: "cyan", value: operations?.shipmentsToday || 0 },
      { detail: "Son 7 gün", icon: <Truck className="h-5 w-5" />, label: "Haftalık", tone: "emerald", value: operations?.shipments7 || 0 },
      { detail: "Toplam etiket", icon: <Tags className="h-5 w-5" />, label: "Kullanılan", tone: "violet", value: usedLabelCount },
      { detail: "Açık sipariş", icon: <AlertTriangle className="h-5 w-5" />, label: "Etiket bekleyen", tone: "amber", value: operations?.waitingForLabelOrders || 0 },
    ];
    const shipmentSignals: Array<{ detail: string; icon: ReactNode; label: string; tone: keyof typeof adminStatToneClasses; value: unknown }> = [
      { detail: "Teslim sinyali", icon: <CheckCircle2 className="h-5 w-5" />, label: "Teslim edilen", tone: "emerald", value: operations?.delivered || 0 },
      { detail: "Aktif taşıma", icon: <Send className="h-5 w-5" />, label: "Yolda paket", tone: "cyan", value: operations?.inTransit || 0 },
      { detail: "Retoure / iade", icon: <Package className="h-5 w-5" />, label: "İade paket", tone: "violet", value: operations?.returnShipments || 0 },
      { detail: "Müdahale gerekebilir", icon: <AlertTriangle className="h-5 w-5" />, label: "Sorunlu paket", tone: "red", value: operations?.problemShipments || 0 },
    ];
    const attentionCount = Number(operations?.waitingForLabelOrders || 0) + Number(operations?.problemShipments || 0);
    return (
      <div className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950">Genel Bakış</h1>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
              Mağaza, sipariş, etiket ve gönderi durumunu tek ekranda takip edin.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Link className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800" to="/customer/create-label">
              <PlusSquare className="h-4 w-4" />
              Etiket oluştur
            </Link>
            <Link className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50" to="/customer/orders">
              <ShoppingCart className="h-4 w-4" />
              Siparişler
            </Link>
          </div>
        </div>

        {error ? <AdminNotice message={error} tone="warning" /> : null}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {customerCards.map((card) => (
            <AdminStatCard key={card.label} icon={card.icon} label={card.label} value={card.value} loading={dashboardLoading} tone={card.tone} />
          ))}
        </section>

        <section className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
          <AdminPanel
            title="Operasyon özeti"
            caption={attentionCount > 0 ? `${numberText(attentionCount)} konu takip bekliyor.` : "Bugün kritik uyarı görünmüyor."}
          >
            <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_260px]">
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Kalan kullanılabilir</p>
                    <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
                      {dashboardLoading ? "..." : numberText(availableLabelCount)}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      Toplam: {dashboardLoading ? "..." : numberText(totalLabelCount)} · Kullanıldı: {dashboardLoading ? "..." : numberText(usedLabelCount)} · Void: {dashboardLoading ? "..." : numberText(voidedLabelCount)}
                    </p>
                  </div>
                  <span
                    className={cx(
                      "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
                      attentionCount > 0 ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"
                    )}
                  >
                    {attentionCount > 0 ? "Kontrol et" : "Stabil"}
                  </span>
                </div>
                <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200">
                  <div className="h-full rounded-full bg-emerald-500 transition-[width]" style={{ width: `${availableLabelWidth}%` }} />
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <div className="rounded-lg border border-slate-100 bg-white px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Toplam etiket</p>
                    <p className="mt-1 text-sm font-semibold text-slate-950">{dashboardLoading ? "..." : numberText(totalLabelCount)}</p>
                  </div>
                  <div className="rounded-lg border border-slate-100 bg-white px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Kullanıldı</p>
                    <p className="mt-1 text-sm font-semibold text-slate-950">{dashboardLoading ? "..." : numberText(usedLabelCount)}</p>
                  </div>
                  <div className="rounded-lg border border-slate-100 bg-white px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Void</p>
                    <p className="mt-1 text-sm font-semibold text-slate-950">{dashboardLoading ? "..." : numberText(voidedLabelCount)}</p>
                  </div>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Açık sipariş</p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">{dashboardLoading ? "..." : numberText(operations?.openOrders || 0)}</p>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Etiket bekleyen</p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">{dashboardLoading ? "..." : numberText(operations?.waitingForLabelOrders || 0)}</p>
                </div>
              </div>
            </div>
            <div className="grid gap-3 border-t border-slate-100 p-4 md:grid-cols-2">
              {operationHighlights.map((item) => (
                <div key={item.label} className="flex min-w-0 items-center gap-3 rounded-lg border border-slate-100 bg-white p-3 shadow-sm">
                  <div className={cx("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ring-1", adminStatToneClasses[item.tone])}>{item.icon}</div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-950">{dashboardLoading ? "..." : numberText(item.value)}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">{item.label} · {item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </AdminPanel>

          <AdminPanel title="Hızlı işlemler" caption="En sık kullanılan müşteri akışları.">
            <div className="grid gap-2 p-3">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Link
                    key={action.to}
                    to={action.to}
                    className={cx(
                      "group flex min-w-0 items-center gap-3 rounded-lg border p-2.5 transition",
                      action.primary
                        ? "border-slate-900 bg-slate-900 text-white shadow-sm hover:bg-slate-800"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                    )}
                  >
                    <div className={cx("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", action.primary ? "bg-white/10 text-cyan-100" : "bg-slate-50 text-slate-500")}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={cx("truncate text-sm font-semibold", action.primary ? "text-white" : "text-slate-950")}>{action.label}</p>
                      <p className={cx("mt-0.5 truncate text-xs", action.primary ? "text-slate-300" : "text-slate-500")}>{action.detail}</p>
                    </div>
                    <ChevronRight className={cx("h-4 w-4 shrink-0 transition group-hover:translate-x-0.5", action.primary ? "text-slate-300" : "text-slate-400")} />
                  </Link>
                );
              })}
            </div>
          </AdminPanel>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {shipmentSignals.map((item) => (
            <div key={item.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{item.label}</p>
                  <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">{dashboardLoading ? "..." : numberText(item.value)}</p>
                  <p className="mt-1 truncate text-xs text-slate-500">{item.detail}</p>
                </div>
                <div className={cx("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1", adminStatToneClasses[item.tone])}>{item.icon}</div>
              </div>
            </div>
          ))}
        </section>

        <AdminPanel title="Son hareketler" count={recentItems.length}>
          <div className="divide-y divide-slate-100">
            {recentItems.map((item) => (
              <div key={item.id} className="flex items-center gap-4 px-4 py-4 transition hover:bg-slate-50/80">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-cyan-50 text-cyan-700 ring-1 ring-cyan-100">
                  <History className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-950">{item.label}</p>
                  <p className="mt-1 truncate text-sm text-slate-500">{item.detail}</p>
                </div>
                <span className="hidden text-xs font-medium text-slate-400 sm:block">{formatDate(item.createdAt)}</span>
              </div>
            ))}
          </div>
        </AdminPanel>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl bg-[#08152b] text-white shadow-sm">
        <div className="grid gap-6 p-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:p-8">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-cyan-100">
                <LayoutDashboard className="h-3.5 w-3.5" />
                Yönetim Özeti
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-300/25 bg-emerald-400/10 px-3 py-1.5 text-xs font-semibold text-emerald-100">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                {dashboardLoading ? "Veriler güncelleniyor" : "Canlı veriler hazır"}
              </span>
            </div>
            <p className="mt-8 text-xs font-semibold uppercase tracking-[0.24em] text-slate-300">Operasyon kontrol paneli</p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl">Merhaba {displayName(user)}</h1>
            <p className="mt-5 max-w-3xl text-base leading-7 text-slate-200">
              Müşteri, sipariş, gönderi, etiket stoğu ve sorun sinyallerini tek bakışta okuyun. Sayfa, son veriyi hızlı gösterir ve arka planda canlı sayılarla yenilenir.
            </p>
            <div className="mt-8 grid gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-white/15 bg-white/10 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Son 30 gün gönderi</p>
                <p className="mt-3 text-3xl font-semibold">{summary ? numberText(operations?.shipments30) : "..."}</p>
                <p className="mt-2 text-sm text-slate-300">Canlı operasyon temposu</p>
              </div>
              <div className="rounded-xl border border-white/15 bg-white/10 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Aktif müşteri</p>
                <p className="mt-3 text-3xl font-semibold">{summary ? numberText(operations?.activeCustomers30) : "..."}</p>
                <p className="mt-2 text-sm text-slate-300">Son 30 günde hareketli</p>
              </div>
              <div className="rounded-xl border border-white/15 bg-white/10 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Hazır etiket</p>
                <p className="mt-3 text-3xl font-semibold">{summary ? numberText(summary.totals.availableLabels) : "..."}</p>
                <p className="mt-2 text-sm text-slate-300">Temiz veya kontrol bekleyen stok</p>
              </div>
            </div>
          </div>
          <aside className="rounded-2xl border border-white/15 bg-white/10 p-5">
            <h2 className="text-lg font-semibold">Bugün dikkat edilecekler</h2>
            <p className="mt-2 text-sm leading-6 text-slate-300">Sorunlu paket, düşük bakiye ve bekleyen siparişleri hızlıca ayırır.</p>
            <div className="mt-5 grid gap-3">
              {[
                ["Bugünkü gönderi", summary ? numberText(operations?.shipmentsToday) : "..."],
                ["Etiket bekleyen sipariş", summary ? numberText(operations?.waitingForLabelOrders) : "..."],
                ["Sorunlu paket", summary ? numberText(operations?.problemShipments) : "..."],
                ["Düşük bakiyeli müşteri", summary ? numberText(operations?.lowBalanceCustomers) : "..."],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/10 px-3 py-2.5">
                  <span className="min-w-0 truncate text-sm text-slate-300">{label}</span>
                  <strong className="shrink-0 text-sm text-white">{value}</strong>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card, index) => {
          const Icon = card.icon;
          const tones: Array<keyof typeof adminStatToneClasses> = ["emerald", "cyan", "violet", "amber"];
          return (
            <AdminStatCard key={card.label} label={card.label} value={card.value} loading={dashboardLoading} tone={tones[index] || "slate"} icon={<Icon className="h-5 w-5" />} />
          );
        })}
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard icon={<Clock3 className="h-5 w-5" />} label="Bugünkü gönderi" value={operations?.shipmentsToday || 0} loading={dashboardLoading} tone="cyan" />
        <AdminStatCard icon={<Truck className="h-5 w-5" />} label="Son 7 gün" value={operations?.shipments7 || 0} loading={dashboardLoading} tone="emerald" />
        <AdminStatCard icon={<BarChart3 className="h-5 w-5" />} label="Son 30 gün" value={operations?.shipments30 || 0} loading={dashboardLoading} tone="violet" />
        <AdminStatCard icon={<AlertTriangle className="h-5 w-5" />} label="Sorunlu paket" value={operations?.problemShipments || 0} loading={dashboardLoading} tone="red" />
        <AdminStatCard icon={<CheckCircle2 className="h-5 w-5" />} label="Teslim sinyali" value={operations?.delivered || 0} loading={dashboardLoading} tone="emerald" />
        <AdminStatCard icon={<Send className="h-5 w-5" />} label="Yolda sinyali" value={operations?.inTransit || 0} loading={dashboardLoading} tone="amber" />
        <AdminStatCard icon={<Tags className="h-5 w-5" />} label="Düşük stok ürünü" value={operations?.lowStockProducts || 0} loading={dashboardLoading} tone="amber" />
        <AdminStatCard icon={<Wallet className="h-5 w-5" />} label="Düşük bakiyeli müşteri" value={operations?.lowBalanceCustomers || 0} loading={dashboardLoading} tone="red" />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <AdminPanel title="Takip performansı" caption="Pazar yeri takip yazımı, label üretimi ve iade/iptal sinyalleri.">
          <div className="grid gap-3 p-5 sm:grid-cols-2">
            <InfoLine label="Başarı oranı" value={percentText(trackingSuccessRate)} />
            <InfoLine label="Takip başarılı" value={numberText(shipmentPerformance?.trackingSuccess)} />
            <InfoLine label="Takip hatalı" value={numberText(shipmentPerformance?.trackingFailed)} />
            <InfoLine label="Takip bekleyen" value={numberText(shipmentPerformance?.trackingPending)} />
            <InfoLine label="Label hatası" value={numberText(shipmentPerformance?.labelFailed)} />
            <InfoLine label="Retoure / iade" value={numberText(shipmentPerformance?.returns)} />
          </div>
        </AdminPanel>
        <AdminPanel title="DHL kontrol özeti" caption="DHL taşıyıcı kontrol sonuçları ve hatalı/şüpheli etiket sinyalleri.">
          <div className="grid gap-3 p-5 sm:grid-cols-2">
            <InfoLine label="Kontrol edilen" value={numberText(dhlPerformance?.checked)} />
            <InfoLine label="Temiz / açık" value={numberText(dhlPerformance?.clean)} />
            <InfoLine label="Hareketli / kullanılmış" value={numberText(dhlPerformance?.used)} />
            <InfoLine label="DHL hata" value={numberText(dhlPerformance?.errors)} />
            <InfoLine label="Teslim sinyali" value={numberText(dhlPerformance?.deliveredHints)} />
            <InfoLine label="İade sinyali" value={numberText(dhlPerformance?.returnHints)} />
          </div>
        </AdminPanel>
      </section>

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <AdminPanel title="Son hareketler" count={recentItems.length}>
          <div className="divide-y divide-slate-100">
            {recentItems.map((item) => (
              <div key={item.id} className="flex items-center gap-4 p-5 transition hover:bg-slate-50/80">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700 ring-1 ring-cyan-100">
                  <ChevronRight className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-950">{item.label}</p>
                  <p className="truncate text-sm text-slate-500">{item.detail}</p>
                </div>
                <p className="hidden text-xs text-slate-400 sm:block">{formatDate(item.createdAt)}</p>
              </div>
            ))}
          </div>
        </AdminPanel>

        <div className="space-y-5">
          <AdminPanel title="Hızlı yönetim">
            <div className="grid gap-2 p-4">
              {[
                { label: "Yeni müşteri", to: "/admin/customers/new", icon: PlusSquare },
                { label: "Etiket kaynağı yükle", to: "/admin/label-sources/upload", icon: Package },
                { label: "Faturalama", to: "/admin/billing", icon: CreditCard },
                { label: "Raporlar", to: "/admin/reports", icon: BarChart3 },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.to} to={item.to} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50">
                    <span className="flex min-w-0 items-center gap-2">
                      <Icon className="h-4 w-4 shrink-0 text-slate-500" />
                      <span className="truncate">{item.label}</span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                  </Link>
                );
              })}
            </div>
          </AdminPanel>
          <AdminPanel title="Rol kapsamı">
            <div className="space-y-3 p-4">
            {roleCapability(user.role).map((item) => (
              <div key={item} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2">
                <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" />
                <span className="text-sm text-slate-700">{item}</span>
              </div>
            ))}
            </div>
          </AdminPanel>
        </div>
      </section>
    </div>
  );
}

type DataRow = Record<string, unknown>;

type ConfirmationTone = "default" | "danger" | "warning";

type ConfirmationDialogRequest = {
  cancelLabel?: string;
  confirmLabel?: string;
  description: string;
  details?: string[];
  tone?: ConfirmationTone;
  title: string;
};

type ActiveConfirmationDialog = ConfirmationDialogRequest & {
  resolve: (confirmed: boolean) => void;
};

function ConfirmationDialog({
  request,
  onCancel,
  onConfirm,
}: {
  request: ActiveConfirmationDialog | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    if (!request) return undefined;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [request, onCancel]);

  if (!request) return null;

  const tone = request.tone || "default";
  const destructive = tone === "danger";
  const warning = tone === "warning";

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/45 p-4">
      <button type="button" className="absolute inset-0 cursor-default" aria-label="Onay penceresini kapat" onClick={onCancel} />
      <section className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
        <div className="flex items-start gap-4 border-b border-slate-100 px-5 py-4">
          <div
            className={cx(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border",
              destructive && "border-red-200 bg-red-50 text-red-700",
              warning && "border-amber-200 bg-amber-50 text-amber-700",
              !destructive && !warning && "border-slate-200 bg-slate-50 text-slate-700"
            )}
          >
            {destructive || warning ? <AlertTriangle className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-slate-950">{request.title}</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">{request.description}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
            aria-label="Onay penceresini kapat"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {request.details?.length ? (
          <div className="space-y-2 px-5 py-4">
            {request.details.map((detail) => (
              <div key={detail} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-5 text-slate-600">
                {detail}
              </div>
            ))}
          </div>
        ) : null}
        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            {request.cancelLabel || "Vazgeç"}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={cx(
              "inline-flex min-h-11 items-center justify-center rounded-xl px-4 text-sm font-semibold text-white shadow-sm transition",
              destructive && "bg-red-600 hover:bg-red-700",
              warning && "bg-amber-600 hover:bg-amber-700",
              !destructive && !warning && "bg-slate-900 hover:bg-slate-800"
            )}
          >
            {request.confirmLabel || "Onayla"}
          </button>
        </div>
      </section>
    </div>
  );
}

function useConfirmationDialog() {
  const [request, setRequest] = useState<ActiveConfirmationDialog | null>(null);

  function askConfirmation(options: ConfirmationDialogRequest) {
    return new Promise<boolean>((resolve) => {
      setRequest((current) => {
        current?.resolve(false);
        return { ...options, resolve };
      });
    });
  }

  function close(confirmed: boolean) {
    setRequest((current) => {
      current?.resolve(confirmed);
      return null;
    });
  }

  return {
    askConfirmation,
    confirmationDialog: <ConfirmationDialog request={request} onCancel={() => close(false)} onConfirm={() => close(true)} />,
  };
}

function carrierDeliveryBadge(row: DataRow | null | undefined) {
  const severity = text((row?.normalizedCarrierStatus as DataRow | undefined)?.severity || row?.carrierDeliverySeverity);
  const label = text((row?.normalizedCarrierStatus as DataRow | undefined)?.label || row?.carrierDeliveryLabel || row?.carrierDeliveryStatus) || "Bilinmiyor";
  return (
    <span
      className={cx(
        "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
        severity === "success" && "border-emerald-200 bg-emerald-50 text-emerald-700",
        severity === "danger" && "border-red-200 bg-red-50 text-red-700",
        severity === "warning" && "border-amber-200 bg-amber-50 text-amber-700",
        (!severity || severity === "neutral") && "border-slate-200 bg-slate-50 text-slate-600"
      )}
    >
      {label}
    </span>
  );
}

function carrierEventRows(row: DataRow | null | undefined) {
  return Array.isArray(row?.carrierEvents) ? (row?.carrierEvents as DataRow[]) : [];
}

function CarrierEventsDrawer({ row, onClose }: { row: DataRow | null; onClose: () => void }) {
  useEffect(() => {
    if (!row) return undefined;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [row, onClose]);

  if (!row) return null;

  const events = carrierEventRows(row);
  const trackingNumber = text(row.trackingNumber) || text(row.id);
  const inactive = isCancelledShipmentRow(row) || isTechnicalVoidShipmentRow(row);
  const trackingLink = inactive ? "" : text(row.carrierTrackingLink) || shipmentTrackingHref(trackingNumber);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/35">
      <button type="button" className="hidden flex-1 cursor-default sm:block" aria-label="Takip olaylarını kapat" onClick={onClose} />
      <aside className="flex h-full w-full max-w-xl flex-col bg-white shadow-2xl ring-1 ring-slate-200">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-cyan-600">DHL takip olayları</p>
            <h2 className="mt-1 break-words text-xl font-semibold text-slate-950">{trackingNumber || "-"}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {carrierDeliveryBadge(row)}
              {text(row.carrierDeliveryTerminal) === "true" || row.carrierDeliveryTerminal === true ? statusBadge("completed") : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
            aria-label="Takip olaylarını kapat"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <dl className="grid gap-3 sm:grid-cols-2">
            <FaultyLabelField label="Son kontrol" value={dateText(row.carrierCheckCheckedAt || row.carrierDeliveryStatusAt)} />
            <FaultyLabelField label="Son event" value={dateText(row.carrierDeliveryLastEventAt)} />
            <FaultyLabelField label="Carrier check" value={text(row.carrierCheckStatus) || "-"} />
            <FaultyLabelField label="Mesaj" value={text(row.carrierCheckMessage) || "-"} />
          </dl>
          {trackingLink ? (
            <a
              href={trackingLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <ExternalLink className="h-4 w-4" />
              DHL takip sayfasını aç
            </a>
          ) : null}
          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-950">Event timeline</h3>
            {events.length === 0 ? (
              <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                Bu takip numarası için kaydedilmiş DHL event kaydı yok.
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                {events.map((event, index) => (
                  <div key={`${text(event.at)}-${index}`} className="flex gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700 ring-1 ring-cyan-100">
                      <Truck className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="break-words text-sm font-semibold text-slate-950">{text(event.text) || text(event.statusText) || "DHL event"}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {[dateText(event.at), text(event.location), text(event.statusCode)].filter((item) => item && item !== "-").join(" · ") || "-"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}

function adminCustomerFullName(customer: DataRow) {
  return [customer.firstName, customer.lastName].map(text).filter(Boolean).join(" ").trim();
}

function adminCustomerPrimary(customer: DataRow) {
  return text(customer.companyName) || adminCustomerFullName(customer) || text(customer.email) || text(customer.id) || "Müşteri";
}

function adminCustomerDetail(customer: DataRow) {
  return [adminCustomerFullName(customer), text(customer.email), text(customer.id)].filter((item, index, items) => item && items.indexOf(item) === index).join(" · ");
}

function adminCustomerSearchText(customer: DataRow) {
  return [adminCustomerPrimary(customer), adminCustomerDetail(customer), text(customer.role)].join(" ").toLocaleLowerCase("tr-TR");
}

function sortAdminCustomers(customers: DataRow[]) {
  return [...customers].sort((a, b) => adminCustomerPrimary(a).localeCompare(adminCustomerPrimary(b), "tr-TR", { numeric: true, sensitivity: "base" }));
}

function AdminCustomerPicker({
  customers,
  onChange,
  value,
}: {
  customers: DataRow[];
  onChange: (customerId: string) => void;
  value: string;
}) {
  const selectedCustomer = customers.find((customer) => text(customer.id) === value) || null;
  const [query, setQuery] = useState(() => (selectedCustomer ? adminCustomerPrimary(selectedCustomer) : ""));
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const sortedCustomers = useMemo(() => sortAdminCustomers(customers), [customers]);
  const normalizedQuery = query.trim().toLocaleLowerCase("tr-TR");
  const visibleCustomers = useMemo(
    () => sortedCustomers.filter((customer) => !normalizedQuery || adminCustomerSearchText(customer).includes(normalizedQuery)).slice(0, 80),
    [normalizedQuery, sortedCustomers]
  );

  useEffect(() => {
    if (!open) setQuery(selectedCustomer ? adminCustomerPrimary(selectedCustomer) : "");
  }, [open, selectedCustomer]);

  useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    },
    []
  );

  function scheduleClose() {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  }

  function cancelClose() {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
  }

  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold uppercase text-slate-500">Müşteri</span>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm transition focus-within:border-slate-400 focus-within:ring-4 focus-within:ring-slate-900/5">
        <div className="flex items-center gap-2 px-3">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            value={query}
            onBlur={scheduleClose}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => {
              cancelClose();
              setOpen(true);
            }}
            placeholder="Firma, ad-soyad veya e-posta ara"
            className="h-11 min-w-0 flex-1 border-0 bg-transparent text-sm text-slate-950 outline-none placeholder:text-slate-400"
          />
          {value ? (
            <button
              type="button"
              onClick={() => {
                onChange("");
                setQuery("");
                setOpen(true);
              }}
              className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label="Müşteri seçimini temizle"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        {selectedCustomer ? (
          <div className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
            <span className="font-semibold text-slate-700">{adminCustomerPrimary(selectedCustomer)}</span>
            {adminCustomerDetail(selectedCustomer) ? <span className="ml-2">{adminCustomerDetail(selectedCustomer)}</span> : null}
          </div>
        ) : null}
        {open ? (
          <div className="max-h-64 overflow-y-auto border-t border-slate-100 bg-white">
            {visibleCustomers.length > 0 ? (
              visibleCustomers.map((customer) => {
                const customerId = text(customer.id);
                const selected = customerId === value;
                return (
                  <button
                    key={customerId}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onChange(customerId);
                      setQuery(adminCustomerPrimary(customer));
                      setOpen(false);
                    }}
                    className={cx("flex w-full items-start gap-3 px-3 py-2.5 text-left transition hover:bg-slate-50", selected && "bg-slate-50")}
                  >
                    <span className={cx("mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-xs font-semibold", selected ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-slate-50 text-slate-500")}>
                      {adminCustomerPrimary(customer).slice(0, 2).toLocaleUpperCase("tr-TR")}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-950">{adminCustomerPrimary(customer)}</span>
                      <span className="mt-0.5 block truncate text-xs text-slate-500">{adminCustomerDetail(customer) || "Detay yok"}</span>
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-4 text-sm text-slate-500">Müşteri bulunamadı.</div>
            )}
          </div>
        ) : null}
      </div>
    </label>
  );
}

type AdminCustomerLabelPriceRow = {
  active: boolean;
  basePrice: number;
  labelProductId: string;
  labelProductName: string;
  price: number | null;
};

const customerDetailTabs = [
  { key: "overview", label: "Genel Bakış" },
  { key: "team", label: "Ekip Üyeleri" },
  { key: "stores", label: "Mağazalar" },
  { key: "credits", label: "Krediler ve Etiketler" },
  { key: "pricing", label: "Fiyatlandırma" },
  { key: "invoices", label: "Faturalar" },
  { key: "purchases", label: "Satın Alımlar" },
  { key: "shipments", label: "Gönderiler" },
] as const;

type CustomerDetailTab = (typeof customerDetailTabs)[number]["key"];

function normalizeCustomerDetailTab(value: string | null): CustomerDetailTab {
  return customerDetailTabs.some((item) => item.key === value) ? (value as CustomerDetailTab) : "overview";
}

type DataColumn = {
  label: string;
  value: (row: DataRow) => ReactNode;
  align?: "left" | "right";
};

type ResourceSection = {
  key: string;
  title: string;
  columns: DataColumn[];
  empty?: string;
};

function AdminPageHeader({
  action,
  caption,
  eyebrow = "Yönetim",
  icon,
  title,
}: {
  action?: ReactNode;
  caption: string;
  eyebrow?: string;
  icon?: ReactNode;
  title: string;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between lg:p-6">
        <div className="flex min-w-0 items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-lime-300 shadow-sm ring-1 ring-slate-900/10">
            {icon || <LayoutDashboard className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-400">{eyebrow}</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">{title}</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">{caption}</p>
          </div>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </section>
  );
}

function CustomerPageHeader({
  action,
  caption,
  icon,
  title,
}: {
  action?: ReactNode;
  caption: string;
  icon?: ReactNode;
  title: string;
}) {
  return <AdminPageHeader title={title} caption={caption} icon={icon} action={action} eyebrow="Müşteri paneli" />;
}

function AdminPanel({
  action,
  allowOverflow = false,
  caption,
  children,
  className,
  count,
  title,
}: {
  action?: ReactNode;
  allowOverflow?: boolean;
  caption?: string;
  children: ReactNode;
  className?: string;
  count?: number;
  title?: string;
}) {
  return (
    <section className={cx("min-w-0 rounded-2xl border border-slate-200 bg-white shadow-sm", allowOverflow ? "" : "overflow-hidden", className)}>
      {title ? (
        <div className="flex flex-col gap-3 border-b border-slate-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-slate-950">{title}</h2>
              {typeof count === "number" ? (
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-500">{numberText(count)}</span>
              ) : null}
            </div>
            {caption ? <p className="mt-1 text-sm text-slate-500">{caption}</p> : null}
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

function AdminNotice({ message, tone = "success" }: { message: string; tone?: "success" | "warning" }) {
  return (
    <div
      className={cx(
        "flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm",
        tone === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-900"
      )}
    >
      <CheckCircle2 className={cx("mt-0.5 h-4 w-4 shrink-0", tone === "success" ? "text-emerald-600" : "text-amber-600")} />
      <span>{message}</span>
    </div>
  );
}

function ResourcePage({
  title,
  caption,
  endpoint,
  sections,
  headerAction,
  beforeSections,
}: {
  title: string;
  caption: string;
  endpoint: string;
  sections: ResourceSection[];
  headerAction?: ReactNode;
  beforeSections?: ReactNode;
}) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    apiClient
      .resource(endpoint)
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Veri alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [endpoint]);

  const isAdminResource = endpoint.startsWith("/api/admin") || endpoint.startsWith("/api/order-sync");

  return (
    <div className="space-y-5">
      <AdminPageHeader title={title} caption={caption} action={headerAction} eyebrow={isAdminResource ? "Yönetim" : "Müşteri paneli"} />
      {error ? <ErrorBanner message={error} /> : null}
      {beforeSections}
      {loading ? (
        <LoadingPanel label="Yükleniyor" />
      ) : (
        sections.map((section) => {
          const rows = Array.isArray(data?.[section.key]) ? (data?.[section.key] as DataRow[]) : [];
          return (
          <AdminPanel key={section.key} title={section.title} count={rows.length}>
            <DataTable
              rows={rows}
              columns={section.columns}
              empty={section.empty || "Kayıt yok."}
            />
          </AdminPanel>
          );
        })
      )}
    </div>
  );
}

function DataTable({
  rows,
  columns,
  empty,
  rowClassName,
}: {
  rows: DataRow[];
  columns: DataColumn[];
  empty: string;
  rowClassName?: (row: DataRow) => string;
}) {
  if (rows.length === 0) {
    return (
      <div className="flex min-h-48 flex-col items-center justify-center gap-3 bg-slate-50/60 p-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-400 shadow-sm">
          <Package className="h-5 w-5" />
        </div>
        <p className="text-sm font-medium text-slate-600">{empty}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[820px] divide-y divide-slate-100 text-sm">
        <thead className="bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
          <tr>
            {columns.map((column) => (
              <th key={column.label} className={cx("whitespace-nowrap px-5 py-3.5", column.align === "right" && "text-right")}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row, index) => (
            <tr
              key={String(row.id || row.requestId || row.referralCode || index)}
              className={cx("align-top transition hover:bg-slate-50/70", rowClassName?.(row))}
            >
              {columns.map((column) => (
                <td key={column.label} className={cx("px-5 py-4 text-slate-700", column.align === "right" && "text-right")}>
                  {column.value(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const adminStatToneClasses = {
  amber: "bg-amber-50 text-amber-700 ring-amber-100",
  cyan: "bg-cyan-50 text-cyan-700 ring-cyan-100",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  red: "bg-red-50 text-red-700 ring-red-100",
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  violet: "bg-violet-50 text-violet-700 ring-violet-100",
};

function AdminStatCard({
  detail,
  icon,
  label,
  loading = false,
  tone,
  value,
}: {
  detail?: string;
  icon: ReactNode;
  label: string;
  loading?: boolean;
  tone: keyof typeof adminStatToneClasses;
  value: unknown;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
          {loading ? (
            <span className="mt-3 block h-7 w-20 animate-pulse rounded-md bg-slate-200" aria-label="Yükleniyor" />
          ) : (
            <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">{typeof value === "string" ? value : numberText(value)}</p>
          )}
          {detail ? <p className="mt-1 text-xs font-medium text-slate-500">{detail}</p> : null}
        </div>
        <div className={cx("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1", adminStatToneClasses[tone])}>{icon}</div>
      </div>
    </div>
  );
}

function CustomerAvatar({
  email,
  name,
  row,
  size = "md",
}: {
  email?: string | null;
  name?: string | null;
  row?: DataRow;
  size?: "md" | "lg";
}) {
  const resolvedName = name || (row ? displayRowName(row) : "");
  const resolvedEmail = email || (row ? text(row.email) : "");
  const source = resolvedName || resolvedEmail;
  const initials = source
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toLocaleUpperCase("tr-TR"))
    .join("");

  return (
    <div
      className={cx(
        "flex shrink-0 items-center justify-center rounded-2xl bg-slate-950 font-semibold text-white shadow-sm ring-1 ring-slate-900/10",
        size === "lg" ? "h-16 w-16 text-xl" : "h-11 w-11 text-sm"
      )}
    >
      {initials || <Building2 className={size === "lg" ? "h-7 w-7" : "h-5 w-5"} />}
    </div>
  );
}

function AdminCustomersPage() {
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();
  const [customers, setCustomers] = useState<DataRow[]>([]);
  const [meta, setMeta] = useState({ hasMore: false, limit: 50, offset: 0, returnedCount: 0, totalCount: 0 });
  const [serverSummary, setServerSummary] = useState({
    active: 0,
    active30: 0,
    inactive: 0,
    lowBalanceCustomers: 0,
    newCustomers: 0,
    problemCustomers: 0,
    total: 0,
    unverified: 0,
  });
  const [loading, setLoading] = useState(true);
  const [busyCustomerId, setBusyCustomerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive" | "unverified">("all");
  const [sortFilter, setSortFilter] = useState("created");
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [search, sortFilter, statusFilter]);

  useEffect(() => {
    let active = true;
    const handle = window.setTimeout(() => {
      const params = new URLSearchParams({ limit: "50", offset: String(page * 50) });
      const normalizedSearch = search.trim();
      if (normalizedSearch) params.set("search", normalizedSearch);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (sortFilter !== "created") params.set("sort", sortFilter);

      setLoading(true);
      setError(null);
      apiClient
        .resource<{ customers?: DataRow[]; meta?: DataRow; summary?: DataRow }>(`/api/admin/customers?${params.toString()}`)
        .then((result) => {
          if (!active) return;
          setCustomers(Array.isArray(result.customers) ? (result.customers as DataRow[]) : []);
          setMeta({
            hasMore: result.meta?.hasMore === true,
            limit: Number(result.meta?.limit || 50),
            offset: Number(result.meta?.offset || page * 50),
            returnedCount: Number(result.meta?.returnedCount || 0),
            totalCount: Number(result.meta?.totalCount || 0),
          });
          setServerSummary({
            active: Number(result.summary?.active || 0),
            active30: Number(result.summary?.active30 || 0),
            inactive: Number(result.summary?.inactive || 0),
            lowBalanceCustomers: Number(result.summary?.lowBalanceCustomers || 0),
            newCustomers: Number(result.summary?.newCustomers || 0),
            problemCustomers: Number(result.summary?.problemCustomers || 0),
            total: Number(result.summary?.total || 0),
            unverified: Number(result.summary?.unverified || 0),
          });
        })
        .catch((err) => {
          if (active) setError(err instanceof ApiError ? err.message : "Müşteriler alınamadı.");
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 220);

    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [page, search, sortFilter, statusFilter]);

  const summary = useMemo(() => {
    return customers.reduce<{
      labels: number;
      shipments: number;
      stores: number;
    }>(
      (acc, row) => {
        acc.stores += Number(row.storesCount || 0);
        acc.shipments += Number(row.shipmentsCount || 0);
        acc.labels += Number(row.availableLabels || 0);
        return acc;
      },
      { labels: 0, shipments: 0, stores: 0 }
    );
  }, [customers]);

  async function startSupportMode(customerId: string) {
    setBusyCustomerId(customerId);
    setError(null);
    try {
      const result = await apiClient.impersonateCustomer(customerId);
      cacheAuthUser(result.user);
      window.location.assign(result.redirect || "/customer");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Müşteri hesabına geçilemedi.");
    } finally {
      setBusyCustomerId(null);
    }
  }

  async function setCustomerArchived(customerId: string, archived: boolean) {
    const confirmed = await askConfirmation({
      title: archived ? "Müşteri arşivlensin mi?" : "Müşteri yeniden aktifleştirilsin mi?",
      description: archived
        ? "Hesap günlük müşteri listesinden kaldırılır ve oturumları kapatılır. Sipariş, etiket ve finans geçmişi silinmez; Arşiv filtresinden geri alınabilir."
        : "Hesap yeniden giriş yapabilir ve aktif müşteri listesinde görünür.",
      confirmLabel: archived ? "Arşivle" : "Aktifleştir",
    });
    if (!confirmed) return;
    setBusyCustomerId(customerId);
    setError(null);
    try {
      await apiClient.adminSetCustomerStatus({ active: !archived, customerId });
      const remainsInCurrentFilter = statusFilter === "all" || statusFilter === "unverified";
      setCustomers((current) => remainsInCurrentFilter
        ? current.map((row) => text(row.id) === customerId ? { ...row, active: !archived } : row)
        : current.filter((row) => text(row.id) !== customerId));
      if (!remainsInCurrentFilter) {
        setMeta((current) => ({ ...current, returnedCount: Math.max(0, current.returnedCount - 1), totalCount: Math.max(0, current.totalCount - 1) }));
      }
      setServerSummary((current) => ({
        ...current,
        active: Math.max(0, current.active + (archived ? -1 : 1)),
        inactive: Math.max(0, current.inactive + (archived ? 1 : -1)),
      }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Müşteri arşiv durumu değiştirilemedi.");
    } finally {
      setBusyCustomerId(null);
    }
  }

  const statusFilters: Array<{ value: typeof statusFilter; label: string; count: number }> = [
    { value: "all", label: "Tümü", count: serverSummary.total },
    { value: "active", label: "Aktif", count: serverSummary.active },
    { value: "inactive", label: "Arşiv", count: serverSummary.inactive },
    { value: "unverified", label: "Doğrulama bekleyen", count: serverSummary.unverified },
  ];

  const exportParams = new URLSearchParams({ limit: "50", offset: String(page * 50), export: "csv" });
  if (search.trim()) exportParams.set("search", search.trim());
  if (statusFilter !== "all") exportParams.set("status", statusFilter);
  if (sortFilter !== "created") exportParams.set("sort", sortFilter);

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950">Müşteriler</h1>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
              Aktif müşterileri yönetin; gereksiz hesapları geçmişini silmeden arşivleyip gerektiğinde geri alın.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/admin/customer-prices"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <Tags className="h-4 w-4" />
              Fiyatlar
            </Link>
            <a
              href={`/api/admin/customers?${exportParams.toString()}`}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <Download className="h-4 w-4" />
              CSV export
            </a>
            <Link
              to="/admin/customers/new"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              <PlusSquare className="h-4 w-4" />
              Yeni müşteri
            </Link>
          </div>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <AdminStatCard icon={<Users className="h-5 w-5" />} label="Toplam hesap" value={serverSummary.total} tone="slate" />
        <AdminStatCard icon={<CheckCircle2 className="h-5 w-5" />} label="Aktif hesap" value={serverSummary.active} tone="emerald" />
        <AdminStatCard icon={<Store className="h-5 w-5" />} label="Son 30 gün aktif" value={serverSummary.active30 || 0} tone="cyan" />
        <AdminStatCard icon={<AlertTriangle className="h-5 w-5" />} label="Düşük bakiyeli" value={serverSummary.lowBalanceCustomers || 0} tone="red" />
        <AdminStatCard icon={<Truck className="h-5 w-5" />} label="Sorunlu müşteri" value={serverSummary.problemCustomers || 0} tone="violet" />
      </section>

      {error ? <ErrorBanner message={error} /> : null}

      <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Müşteri, şirket veya e-posta ara"
                className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:bg-white focus:ring-4 focus:ring-slate-900/5"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={sortFilter}
                onChange={(event) => setSortFilter(event.target.value)}
                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none transition focus:border-slate-400"
              >
                <option value="created">Yeni müşteri</option>
                <option value="shipments30">En çok gönderi</option>
                <option value="revenue30">En çok gelir</option>
                <option value="lowBalance">Düşük bakiye</option>
                <option value="problems">En çok sorun</option>
              </select>
              <div className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
                {statusFilters.map((item) => {
                  const active = statusFilter === item.value;
                  return (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setStatusFilter(item.value)}
                      className={cx(
                        "inline-flex h-9 items-center gap-2 rounded-lg px-3 text-xs font-semibold transition",
                        active ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-950"
                      )}
                    >
                      {item.label}
                      <span className={cx("rounded-full px-1.5 py-0.5 text-[11px]", active ? "bg-slate-100 text-slate-600" : "bg-white text-slate-400")}>
                        {numberText(item.count)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-medium text-slate-500">
          <span>
            {loading
              ? "Müşteriler yükleniyor"
              : `${numberText(meta.offset + 1)}-${numberText(meta.offset + customers.length)} / ${numberText(meta.totalCount)} kayıt`}
          </span>
          <span className="inline-flex items-center gap-2">
            <SlidersHorizontal className="h-3.5 w-3.5" />
            Arama ve durum filtresi aktif
          </span>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Müşteri hesapları hazırlanıyor
          </div>
        ) : customers.length === 0 ? (
          <div className="p-8 text-sm text-slate-500">Bu filtrelerle eşleşen müşteri bulunamadı.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[980px] table-fixed divide-y divide-slate-100 text-sm">
              <colgroup>
                <col className="w-[38%]" />
                <col className="w-[12%]" />
                <col className="w-[8%]" />
                <col className="w-[8%]" />
                <col className="w-[8%]" />
                <col className="w-[8%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[18%]" />
              </colgroup>
              <thead className="bg-white text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-5 py-3">Müşteri</th>
                  <th className="px-4 py-3">Rol</th>
                  <th className="px-4 py-3 text-right">Mağaza</th>
                  <th className="px-4 py-3 text-right">Sipariş</th>
                  <th className="px-4 py-3 text-right">Gönderi</th>
                  <th className="px-4 py-3 text-right">Kalan etiket</th>
                  <th className="px-4 py-3 text-right">30g gelir</th>
                  <th className="px-4 py-3 text-right">Sağlık</th>
                  <th className="sticky right-0 z-10 bg-white px-5 py-3 text-right shadow-[-12px_0_20px_rgba(15,23,42,0.06)]">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {customers.map((row) => {
                  const id = text(row.id);
                  return (
                    <tr key={id} className="align-middle transition hover:bg-slate-50/80">
                      <td className="px-5 py-4">
                        <Link className="group flex min-w-0 items-center gap-3" to={`/admin/customers/${encodeURIComponent(id)}`}>
                          <CustomerAvatar row={row} />
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-slate-950 group-hover:underline">{displayRowName(row)}</p>
                            <p className="mt-1 truncate text-xs text-slate-500">{text(row.email)}</p>
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                              {row.emailVerified === true ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Doğrulanmış
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">
                                  <Clock3 className="h-3 w-3" />
                                  Bekliyor
                                </span>
                              )}
                              <span className="text-slate-400">Son giriş: {dateText(row.lastSignedIn)}</span>
                            </div>
                          </div>
                        </Link>
                      </td>
                      <td className="px-4 py-4">{badge(text(row.role))}</td>
                      <td className="px-4 py-4 text-right font-medium text-slate-800">{numberText(row.storesCount)}</td>
                      <td className="px-4 py-4 text-right text-slate-600">{numberText(row.ordersCount)}</td>
                      <td className="px-4 py-4 text-right text-slate-600">{numberText(row.shipmentsCount)}</td>
                      <td className="px-4 py-4 text-right font-semibold text-slate-950">{numberText(row.availableLabels)}</td>
                      <td className="px-4 py-4 text-right text-slate-600">{moneyText(row.last30Revenue, "EUR")}</td>
                      <td className="px-4 py-4 text-right">
                        <span className={cx("rounded-full px-2 py-1 text-xs font-semibold", Number(row.healthScore || 0) >= 70 ? "bg-emerald-50 text-emerald-700" : Number(row.healthScore || 0) >= 45 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700")}>
                          {numberText(row.healthScore)}
                        </span>
                      </td>
                      <td className="sticky right-0 z-10 bg-white px-5 py-4 shadow-[-12px_0_20px_rgba(15,23,42,0.06)]">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            disabled={busyCustomerId === id}
                            onClick={() => void startSupportMode(id)}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                          >
                            {busyCustomerId === id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <User className="h-3.5 w-3.5" />}
                            Hesaba geç
                          </button>
                          <button
                            type="button"
                            disabled={busyCustomerId === id}
                            onClick={() => void setCustomerArchived(id, row.active !== false)}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
                          >
                            {busyCustomerId === id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : row.active !== false ? <Archive className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                            {row.active !== false ? "Arşivle" : "Geri al"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50 px-5 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
          <span>Sayfa {numberText(page + 1)}</span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={loading || page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50"
            >
              Önceki
            </button>
            <button
              type="button"
              disabled={loading || !meta.hasMore}
              onClick={() => setPage((current) => current + 1)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50"
            >
              Sonraki
            </button>
          </div>
        </div>
      </section>
      {confirmationDialog}
    </div>
  );
}

function AdminCustomerDetailPage() {
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();
  const { customerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = normalizeCustomerDetailTab(searchParams.get("tab"));
  const [data, setData] = useState<AdminCustomerDetail | null>(() => readCachedAdminCustomerDetail(customerId));
  const [labelProducts, setLabelProducts] = useState<DataRow[]>([]);
  const [customerLabelPrices, setCustomerLabelPrices] = useState<AdminCustomerLabelPriceRow[]>([]);
  const [loading, setLoading] = useState(() => !readCachedAdminCustomerDetail(customerId));
  const [priceSaving, setPriceSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accountAction, setAccountAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const cached = readCachedAdminCustomerDetail(customerId);
    setData(cached);
    setLoading(true);
    setError(null);
    setLabelProducts([]);
    setCustomerLabelPrices([]);
    apiClient
      .adminCustomer(customerId || "")
      .then((result) => {
        if (!active) return;
        setData(result);
        cacheAdminCustomerDetail(customerId, result);
        setLoading(false);
        const ownerId = result.customer.ownerId || result.customer.id;
        void Promise.allSettled([
          apiClient.adminLabelProducts(),
          apiClient.adminCustomerLabelPrices(ownerId),
        ]).then(([productsResult, pricesResult]) => {
          if (!active) return;
          if (productsResult.status === "fulfilled") setLabelProducts(productsResult.value.products as DataRow[]);
          if (pricesResult.status === "fulfilled") {
            setCustomerLabelPrices(
              pricesResult.value.prices.map((item) => ({
                active: item.active,
                basePrice: item.basePrice,
                labelProductId: item.labelProductId,
                labelProductName: item.labelProductName,
                price: item.price,
              }))
            );
          }
        });
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Müşteri detayı alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [customerId]);

  async function refreshCustomerDetail(successMessage?: string) {
    if (!customerId) return;
    setError(null);
    const result = await apiClient.adminCustomer(customerId);
    setData(result);
    cacheAdminCustomerDetail(customerId, result);
    const ownerId = result.customer.ownerId || result.customer.id;
    const [productsResult, pricesResult] = await Promise.all([
      apiClient.adminLabelProducts(),
      apiClient.adminCustomerLabelPrices(ownerId),
    ]);
    setLabelProducts(productsResult.products as DataRow[]);
    setCustomerLabelPrices(
      pricesResult.prices.map((item) => ({
        active: item.active,
        basePrice: item.basePrice,
        labelProductId: item.labelProductId,
        labelProductName: item.labelProductName,
        price: item.price,
      }))
    );
    if (successMessage) setNotice(successMessage);
  }

  function selectCustomerTab(tab: CustomerDetailTab) {
    const next = new URLSearchParams(searchParams);
    if (tab === "overview") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { replace: true });
  }

  function updateCustomerPrice(labelProductId: string, value: string) {
    setCustomerLabelPrices((current) =>
      current.map((item) => (item.labelProductId === labelProductId ? { ...item, price: value === "" ? null : Number(value) } : item))
    );
  }

  async function saveCustomerPrices(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!data) return;
    setPriceSaving(true);
    setError(null);
    setNotice(null);
    try {
      const ownerId = data.customer.ownerId || data.customer.id;
      const result = await apiClient.saveAdminCustomerLabelPrices({
        customerId: ownerId,
        items: customerLabelPrices.map((item) => ({ active: item.active, labelProductId: item.labelProductId, price: item.price })),
      });
      setNotice(`Fiyatlar kaydedildi. Yeni: ${result.createdCount}, güncellenen: ${result.updatedCount}, silinen: ${result.deletedCount}.`);
      await refreshCustomerDetail().catch(() => undefined);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Fiyatlar kaydedilemedi.");
    } finally {
      setPriceSaving(false);
    }
  }

  async function startSupportMode() {
    if (!data?.customer.id) return;
    setBusy(true);
    setError(null);
    try {
      const result = await apiClient.impersonateCustomer(data.customer.id);
      cacheAuthUser(result.user);
      window.location.assign(result.redirect || "/customer");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Müşteri hesabına geçilemedi.");
    } finally {
      setBusy(false);
    }
  }

  async function runCustomerAccountAction(actionKey: string, action: () => Promise<AdminCustomerActionResponse>, fallbackMessage: string) {
    if (!data?.customer.id) return;
    setAccountAction(actionKey);
    setError(null);
    setNotice(null);
    try {
      const result = await action();
      await refreshCustomerDetail(result.message || fallbackMessage);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Hesap işlemi tamamlanamadı.");
    } finally {
      setAccountAction(null);
    }
  }

  if (loading && !data) return <CustomerDetailSkeleton />;
  if (error && !data) return <ErrorPanel title="Müşteri detayı açılamadı" message={error} />;
  if (!data) return <ErrorPanel title="Müşteri bulunamadı" message="Kayıt bulunamadı." />;

  const customerName =
    data.customer.companyName ||
    [data.customer.firstName, data.customer.lastName].filter(Boolean).join(" ").trim() ||
    data.customer.email ||
    "-";
  const customerEmail = data.customer.email || "-";
  const accountChecks = [
    { label: "Hesap aktif", ok: data.customer.active, value: data.customer.active ? "Aktif" : "Pasif" },
    { label: "E-posta", ok: data.customer.emailVerified, value: data.customer.emailVerified ? "Doğrulandı" : "Bekliyor" },
  ];
  const totals = data.totals || {
    availableLabels: 0,
    expiredLabels: 0,
    invoicesCount: 0,
    ordersCount: 0,
    shipmentsCount: 0,
    storesCount: 0,
    teamMembersCount: 0,
    totalLabels: 0,
    usedLabels: 0,
    voidedLabels: 0,
  };
  const labelInventory = data.labelInventory;
  const customerAvailableLabels = Number(labelInventory?.availableBalance ?? totals.availableLabels ?? 0);
  const customerTotalLabels = Number(labelInventory?.total ?? totals.totalLabels ?? customerAvailableLabels);
  const customerUsedLabels = Number(labelInventory?.consumed ?? totals.usedLabels ?? 0);
  const customerVoidedLabels = Number(labelInventory?.voided ?? totals.voidedLabels ?? 0);
  const customerExpiredLabels = Number(labelInventory?.expired ?? totals.expiredLabels ?? 0);
  accountChecks.push(
    { label: "Mağaza bağlantısı", ok: totals.storesCount > 0, value: `${numberText(totals.storesCount)} mağaza` },
    { label: "Kalan etiket", ok: customerAvailableLabels > 0, value: `${numberText(customerAvailableLabels)} adet` }
  );
  const healthScore = Math.round((accountChecks.filter((item) => item.ok).length / accountChecks.length) * 100);
  const stores = data.stores || [];
  const orders = data.orders || [];
  const shipments = data.shipments || [];
  const teamMembers = data.teamMembers || [];
  const balances = data.balances || [];
  const purchases = data.purchases || [];
  const invoices = data.invoices || [];
  const latestPurchase = purchases[0];
  const primaryStore = stores[0];
  const latestOrder = orders[0];
  const latestShipment = shipments[0];
  const accountActionBusy = Boolean(accountAction);
  const accountActionButtonClass =
    "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60";

  return (
    <div className="space-y-5">
      {notice ? <AdminNotice message={notice} /> : null}
      {error ? <ErrorBanner message={error} /> : null}
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="bg-[#07142c] p-5 text-white sm:p-6">
          <Link className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition hover:text-white" to="/admin/customers">
            <ArrowLeft className="h-4 w-4" />
            Müşterilere dön
          </Link>

          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center">
              <CustomerAvatar name={customerName} email={customerEmail} size="lg" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-cyan-100 ring-1 ring-white/10">
                    {roleLabels[data.customer.role]}
                  </span>
                  <span
                    className={cx(
                      "rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
                      data.customer.active ? "bg-emerald-400/10 text-emerald-100 ring-emerald-300/20" : "bg-red-400/10 text-red-100 ring-red-300/20"
                    )}
                  >
                    {data.customer.active ? "Aktif hesap" : "Pasif hesap"}
                  </span>
                  <span
                    className={cx(
                      "rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
                      data.customer.emailVerified ? "bg-emerald-400/10 text-emerald-100 ring-emerald-300/20" : "bg-amber-400/10 text-amber-100 ring-amber-300/20"
                    )}
                  >
                    {data.customer.emailVerified ? "E-posta doğrulandı" : "E-posta bekliyor"}
                  </span>
                </div>
                <h1 className="mt-3 break-words text-2xl font-semibold tracking-tight sm:text-3xl">{customerName}</h1>
                <p className="mt-1 break-all text-sm text-slate-300">{customerEmail}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Link
                to={`/admin/customer-prices?customerId=${encodeURIComponent(data.customer.id)}`}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                <Tags className="h-4 w-4" />
                Fiyatları aç
              </Link>
              <button
                type="button"
                disabled={busy}
                onClick={() => void startSupportMode()}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-sm transition hover:bg-slate-100 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <User className="h-4 w-4" />}
                Müşteri olarak gör
              </button>
            </div>
          </div>

          <div className="mt-6 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(260px,0.8fr)]">
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Ana mağaza</p>
              <p className="mt-2 truncate text-base font-semibold text-white">{text(primaryStore?.name) || "Bağlı mağaza yok"}</p>
              <p className="mt-1 truncate text-sm text-slate-300">
                {text(primaryStore?.platform) || "Müşteri mağaza bağlantısı kurmamış"}{text(primaryStore?.ordersSyncedAt) ? ` · son sync ${dateText(primaryStore?.ordersSyncedAt)}` : ""}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Son hareket</p>
              <p className="mt-2 truncate text-base font-semibold text-white">{text(latestOrder?.orderNumber) || text(latestShipment?.trackingNumber) || "Hareket yok"}</p>
              <p className="mt-1 truncate text-sm text-slate-300">
                {text(latestOrder?.sourcePlatform) || text(latestShipment?.status) || "Sipariş veya gönderi bulunamadı"}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Hesap hazırlığı</p>
                <p className="text-sm font-semibold text-white">{healthScore}%</p>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-cyan-300" style={{ width: `${healthScore}%` }} />
              </div>
              <p className="mt-2 text-xs text-slate-300">Aktiflik, e-posta, mağaza ve etiket bakiyesi kontrolü.</p>
            </div>
          </div>
        </div>

        <div className="grid gap-3 bg-slate-50 p-4 sm:grid-cols-2 xl:grid-cols-4">
          {accountChecks.map((item) => (
            <div key={item.label} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-950">{item.label}</p>
                  <p className="mt-1 truncate text-sm text-slate-500">{item.value}</p>
                </div>
                <span
                  className={cx(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                    item.ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                  )}
                >
                  {item.ok ? <CheckCircle2 className="h-4 w-4" /> : <Clock3 className="h-4 w-4" />}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <AdminPanel
        title="Hesap işlemleri"
        caption="E-posta doğrulama, hesap arşivleme/geri alma ve parola kurtarma işlemlerini müşteri adına buradan yönetin. Arşivleme geçmiş kayıtları silmez."
      >
        <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-5">
          <button
            type="button"
            disabled={accountActionBusy || data.customer.emailVerified}
            onClick={() =>
              void runCustomerAccountAction(
                "verify-email",
                () => apiClient.adminVerifyCustomerEmail(data.customer.id),
                "Müşteri e-postası doğrulandı."
              )
            }
            className={accountActionButtonClass}
          >
            {accountAction === "verify-email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {data.customer.emailVerified ? "E-posta doğrulandı" : "E-postayı doğrula"}
          </button>
          <button
            type="button"
            disabled={accountActionBusy || data.customer.emailVerified || !data.customer.email}
            onClick={() =>
              void runCustomerAccountAction(
                "send-verification",
                () => apiClient.adminSendCustomerVerificationEmail(data.customer.id),
                "Doğrulama e-postası gönderildi."
              )
            }
            className={accountActionButtonClass}
          >
            {accountAction === "send-verification" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Doğrulama maili gönder
          </button>
          <button
            type="button"
            disabled={accountActionBusy || !data.customer.active || !data.customer.email}
            onClick={() =>
              void runCustomerAccountAction(
                "send-reset",
                () => apiClient.adminSendCustomerPasswordReset(data.customer.id),
                "Şifre sıfırlama e-postası gönderildi."
              )
            }
            className={accountActionButtonClass}
            title={!data.customer.active ? "Pasif hesaba sıfırlama linki gönderilemez." : undefined}
          >
            {accountAction === "send-reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Şifre sıfırlama gönder
          </button>
          <button
            type="button"
            disabled={accountActionBusy}
            onClick={async () => {
              const confirmed = await askConfirmation({
                title: data.customer.active ? "Müşteri arşivlensin mi?" : "Müşteri yeniden aktifleştirilsin mi?",
                description: data.customer.active
                  ? "Hesap oturumları kapatılır ve aktif listeden kaldırılır. Sipariş, etiket ve finans geçmişi korunur."
                  : "Hesap yeniden giriş yapabilir ve aktif müşteri listesinde görünür.",
                confirmLabel: data.customer.active ? "Arşivle" : "Aktifleştir",
              });
              if (!confirmed) return;
              void runCustomerAccountAction(
                "toggle-status",
                () => apiClient.adminSetCustomerStatus({ active: !data.customer.active, customerId: data.customer.id }),
                !data.customer.active ? "Müşteri hesabı aktif edildi." : "Müşteri hesabı arşivlendi."
              );
            }}
            className={accountActionButtonClass}
          >
            {accountAction === "toggle-status" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
            {data.customer.active ? "Hesabı arşivle" : "Hesabı geri al"}
          </button>
          <button
            type="button"
            disabled={accountActionBusy || !data.customer.emailTwoFactorEnabled}
            onClick={() =>
              void runCustomerAccountAction(
                "disable-2fa",
                () => apiClient.adminDisableCustomerEmailTwoFactor(data.customer.id),
                "E-posta 2FA kapatıldı."
              )
            }
            className={accountActionButtonClass}
          >
            {accountAction === "disable-2fa" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {data.customer.emailTwoFactorEnabled ? "E-posta 2FA kapat" : "2FA kapalı"}
          </button>
        </div>
      </AdminPanel>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
        <MetricCard label="Mağaza" value={totals.storesCount} icon={<Store className="h-5 w-5" />} />
        <MetricCard label="Sipariş" value={totals.ordersCount} icon={<ShoppingCart className="h-5 w-5" />} />
        <MetricCard label="Gönderi" value={totals.shipmentsCount} icon={<Truck className="h-5 w-5" />} />
        <MetricCard label="Kalan etiket" value={customerAvailableLabels} icon={<Tags className="h-5 w-5" />} />
        <MetricCard label="Fatura" value={totals.invoicesCount} icon={<FileText className="h-5 w-5" />} />
        <MetricCard label="Ekip" value={totals.teamMembersCount} icon={<Users className="h-5 w-5" />} />
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Toplam etiket" value={customerTotalLabels} icon={<Tags className="h-5 w-5" />} />
        <MetricCard label="Kullanıldı" value={customerUsedLabels} icon={<Package className="h-5 w-5" />} />
        <MetricCard label="Void" value={customerVoidedLabels} icon={<AlertTriangle className="h-5 w-5" />} />
        <MetricCard label="Süresi doldu" value={customerExpiredLabels} icon={<Clock3 className="h-5 w-5" />} />
      </section>

      {loading ? (
        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-100 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-800">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Son bilgiler arka planda yenileniyor
        </div>
      ) : null}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-slate-50/80 p-2">
          <div className="flex gap-2 overflow-x-auto" role="tablist" aria-label="Müşteri detay bölümleri">
            {customerDetailTabs.map((tab) => {
              const selected = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => selectCustomerTab(tab.key)}
                  className={cx(
                    "shrink-0 rounded-xl px-3 py-2 text-sm font-semibold transition",
                    selected
                      ? "bg-slate-950 text-white shadow-sm"
                      : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-950"
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {activeTab === "overview" ? (
            <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
              <div className="min-w-0 space-y-5">
                <DataSection
                  title="Son siparişler"
                  rows={orders}
                  empty="Sipariş yok."
                  columns={[
                    {
                      label: "Sipariş",
                      value: (row) => (
                        <NameStack title={text(row.orderNumber)} detail={`${text(row.sourcePlatform)}${text(row.storeName) ? ` · ${text(row.storeName)}` : ""}`} />
                      ),
                    },
                    { label: "Alıcı", value: (row) => <NameStack title={text(row.recipientName)} detail={`${text(row.city)}, ${text(row.countryCode)}`} /> },
                    { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                    { label: "Tarih", value: (row) => dateText(row.createdAt) },
                  ]}
                />
                <DataSection
                  title="Son gönderiler"
                  rows={shipments}
                  empty="Gönderi yok."
                  columns={[
                    { label: "Takip", value: (row) => <NameStack title={text(row.trackingNumber)} detail={text(row.orderNumber)} /> },
                    { label: "Etiket", value: (row) => text(row.labelProductName) },
                    { label: "Kaynak senkron", value: (row) => statusBadge(text(row.sourcePlatformTrackingSyncStatus)) },
                    { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                    { label: "Tarih", value: (row) => dateText(row.createdAt) },
                  ]}
                />
              </div>
              <div className="min-w-0 space-y-5">
                <DetailCard
                  title="Hesap bilgileri"
                  items={[
                    ["Rol", roleLabels[data.customer.role]],
                    ["Durum", data.customer.active ? "Aktif" : "Pasif"],
                    ["E-posta doğrulama", data.customer.emailVerified ? "Doğrulanmış" : "Bekliyor"],
                    ["E-posta 2FA", data.customer.emailTwoFactorEnabled ? "Açık" : "Kapalı"],
                    ["Telefon", data.customer.phone || "-"],
                    ["Vergi no", data.customer.taxId || "-"],
                    ["Oluşturulma", dateText(data.customer.createdAt)],
                    ["Son giriş", dateText(data.customer.lastSignedIn)],
                  ]}
                />
                <DetailCard
                  title="Fatura ve gönderici adresi"
                  items={[
                    ["Fatura adresi", compactAddress(data.customer.billingStreet1, data.customer.billingPostalCode, data.customer.billingCity, data.customer.billingCountry)],
                    ["Gönderici adı", data.customer.senderNameOverride || "-"],
                    ["Gönderici adresi", compactAddress(data.customer.senderStreet1, data.customer.senderPostalCode, data.customer.senderCity, data.customer.senderCountry)],
                  ]}
                />
              </div>
            </div>
          ) : null}

          {activeTab === "team" ? (
            <DataSection
              title="Ekip üyeleri"
              rows={teamMembers}
              empty="Ekip üyesi yok."
              columns={[
                { label: "Kullanıcı", value: (row) => <NameStack title={displayRowName(row)} detail={text(row.email)} /> },
                { label: "Son giriş", value: (row) => dateText(row.lastSignedIn) },
                { label: "Durum", value: (row) => statusBadge(row.active ? "active" : "inactive") },
              ]}
            />
          ) : null}

          {activeTab === "stores" ? (
            <DataSection
              title="Bağlı mağazalar"
              rows={stores}
              empty="Bağlı mağaza yok."
              columns={[
                {
                  label: "Mağaza",
                  value: (row) => (
                    <NameStack title={text(row.name)} detail={`${text(row.platform)}${text(row.externalStoreId) ? ` · ${text(row.externalStoreId)}` : ""}`} />
                  ),
                },
                { label: "Senkron sipariş", value: (row) => numberText(row.ordersCount), align: "right" },
                { label: "Son senkron", value: (row) => dateText(row.ordersSyncedAt) },
                { label: "Durum", value: (row) => statusBadge(row.active ? "active" : "inactive") },
              ]}
            />
          ) : null}

          {activeTab === "credits" ? (
            <div className="min-w-0">
              <AdminLabelCreditCart
                balances={balances}
                customerId={data.customer.ownerId || data.customer.id}
                customerName={customerName}
                prices={customerLabelPrices}
                products={labelProducts}
                onDone={refreshCustomerDetail}
              />
            </div>
          ) : null}

          {activeTab === "pricing" ? (
            <AdminPanel title="Fiyatlandırma" caption={`${customerName} için ürün bazlı özel fiyatları bu sekmeden düzenleyin.`}>
              <form className="space-y-4 p-5" onSubmit={saveCustomerPrices}>
                <div className="grid gap-3">
                  {customerLabelPrices.map((item) => (
                    <label key={item.labelProductId} className="grid gap-2 rounded-xl border border-slate-100 bg-slate-50 p-3 sm:grid-cols-[1fr_160px] sm:items-center">
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{item.labelProductName}</span>
                        <span className="block text-xs text-slate-500">Baz fiyat: {moneyText(item.basePrice, "EUR")}</span>
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.price ?? ""}
                        onChange={(event) => updateCustomerPrice(item.labelProductId, event.target.value)}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                        placeholder="Baz fiyat"
                      />
                    </label>
                  ))}
                  {customerLabelPrices.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">Fiyat ürünü bulunamadı.</div>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="submit"
                    disabled={priceSaving || customerLabelPrices.length === 0}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                  >
                    {priceSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                    Kaydet
                  </button>
                  <Link
                    to={`/admin/customer-prices?customerId=${encodeURIComponent(data.customer.ownerId || data.customer.id)}`}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Fiyat matrisini aç
                  </Link>
                </div>
              </form>
            </AdminPanel>
          ) : null}

          {activeTab === "invoices" ? (
            <DataSection
              title="Faturalar"
              rows={invoices}
              empty="Fatura yok."
              columns={[
                { label: "No", value: (row) => <NameStack title={text(row.invoiceNumber)} detail={text(row.storeName)} /> },
                { label: "Tutar", value: (row) => moneyText(row.grossTotal, row.currency), align: "right" },
                { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                { label: "Tarih", value: (row) => dateText(row.createdAt) },
              ]}
            />
          ) : null}

          {activeTab === "purchases" ? (
            <div className="space-y-4">
              <AdminPanel
                title="Ürün bazlı kalan kredi"
                caption="Toplam bakiye yerine her etiket ürününün kullanılabilir adedi ayrı gösterilir."
                count={balances.length}
              >
                <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-4">
                  {balances.map((balance) => (
                    <div key={text(balance.id) || text(balance.labelProductId)} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-950">{text(balance.labelProductName) || "Etiket ürünü"}</p>
                      <p className="mt-2 text-2xl font-semibold tracking-tight text-emerald-700">{numberText(balance.quantityAvailable)}</p>
                      <p className="mt-1 text-xs font-medium text-slate-500">kullanılabilir kredi</p>
                    </div>
                  ))}
                  {balances.length === 0 ? <p className="text-sm text-slate-500">Ürün bakiyesi yok.</p> : null}
                </div>
              </AdminPanel>

              {latestPurchase ? (
                <section className="rounded-2xl border border-cyan-200 bg-cyan-50 p-4 shadow-sm sm:p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-cyan-700">Son satın alma</p>
                      <p className="mt-1 text-lg font-semibold text-slate-950">
                        {numberText(latestPurchase.quantity)} × {text(latestPurchase.labelProductName) || "etiket kredisi"}
                      </p>
                      <p className="mt-1 text-sm text-slate-600">
                        {[text(latestPurchase.shopifyOrderName) && `Shopify ${text(latestPurchase.shopifyOrderName)}`, dateText(latestPurchase.createdAt)]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {[
                        ["Kullanılabilir", latestPurchase.availableQuantity, "text-emerald-700"],
                        ["Kullanıldı", latestPurchase.usedQuantity, "text-violet-700"],
                        ["Void", latestPurchase.voidedQuantity, "text-red-700"],
                        ["Süresi doldu", latestPurchase.expiredQuantity, "text-amber-700"],
                      ].map(([label, value, tone]) => (
                        <div key={String(label)} className="min-w-28 rounded-xl border border-white/80 bg-white px-3 py-2 shadow-sm">
                          <p className="text-xs font-medium text-slate-500">{label}</p>
                          <p className={cx("mt-1 text-lg font-semibold", String(tone))}>{numberText(value)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              ) : null}

              <DataSection
                title="Etiket satın almaları"
                rows={purchases}
                empty="Satın alma yok."
                columns={[
                  {
                    label: "Ürün",
                    value: (row) => (
                      <NameStack
                        title={`${numberText(row.quantity)} × ${text(row.labelProductName) || "Etiket"}`}
                        detail={text(row.shopifyOrderName) ? `Shopify ${text(row.shopifyOrderName)}` : text(row.type)}
                      />
                    ),
                  },
                  {
                    label: "Kredi hareketi",
                    value: (row) => (
                      <NameStack
                        title={`${numberText(row.availableQuantity)} kullanılabilir`}
                        detail={`${numberText(row.usedQuantity)} kullanıldı · ${numberText(row.voidedQuantity)} void · ${numberText(row.expiredQuantity)} süresi doldu`}
                      />
                    ),
                  },
                  { label: "Tutar", value: (row) => moneyText(row.totalPrice, "EUR"), align: "right" },
                  { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                  { label: "Tarih", value: (row) => dateText(row.createdAt) },
                ]}
              />
            </div>
          ) : null}

          {activeTab === "shipments" ? (
            <DataSection
              title="Gönderiler"
              rows={shipments}
              empty="Gönderi yok."
              columns={[
                { label: "Takip", value: (row) => <NameStack title={text(row.trackingNumber)} detail={text(row.orderNumber)} /> },
                { label: "Etiket", value: (row) => text(row.labelProductName) },
                { label: "Kaynak senkron", value: (row) => statusBadge(text(row.sourcePlatformTrackingSyncStatus)) },
                { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                { label: "Tarih", value: (row) => dateText(row.createdAt) },
              ]}
            />
          ) : null}
        </div>
      </section>
      {confirmationDialog}
    </div>
  );
}

function CustomerDetailSkeleton() {
  return (
    <div className="space-y-5" aria-label="Müşteri detayı yükleniyor">
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="bg-[#07142c] p-5 text-white sm:p-6">
          <div className="h-5 w-32 animate-pulse rounded bg-white/15" />
          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="h-14 w-14 animate-pulse rounded-2xl bg-white/15" />
            <div className="min-w-0 flex-1 space-y-3">
              <div className="h-5 w-56 max-w-full animate-pulse rounded bg-white/15" />
              <div className="h-4 w-72 max-w-full animate-pulse rounded bg-white/10" />
            </div>
          </div>
          <div className="mt-6 grid gap-3 lg:grid-cols-3">
            {["Ana mağaza", "Son hareket", "Hesap hazırlığı"].map((item) => (
              <div key={item} className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                <div className="h-3 w-24 animate-pulse rounded bg-white/10" />
                <div className="mt-4 h-5 w-40 animate-pulse rounded bg-white/15" />
                <div className="mt-3 h-4 w-52 max-w-full animate-pulse rounded bg-white/10" />
              </div>
            ))}
          </div>
        </div>
        <div className="grid gap-3 bg-slate-50 p-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="h-4 w-28 animate-pulse rounded bg-slate-200" />
              <div className="mt-3 h-4 w-20 animate-pulse rounded bg-slate-100" />
            </div>
          ))}
        </div>
      </section>
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="h-3 w-20 animate-pulse rounded bg-slate-200" />
            <div className="mt-4 h-7 w-16 animate-pulse rounded bg-slate-100" />
          </div>
        ))}
      </section>
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex gap-2 overflow-x-auto border-b border-slate-200 bg-slate-50/80 p-2">
          {customerDetailTabs.map((tab) => (
            <div key={tab.key} className="h-9 w-28 shrink-0 animate-pulse rounded-xl bg-slate-200" />
          ))}
        </div>
        <div className="space-y-3 p-5">
          <div className="h-5 w-40 animate-pulse rounded bg-slate-200" />
          <div className="h-28 animate-pulse rounded-xl bg-slate-100" />
          <div className="h-28 animate-pulse rounded-xl bg-slate-100" />
        </div>
      </section>
    </div>
  );
}

type LabelCreditCartRow = {
  labelProductId: string;
  quantity: string;
  unitPrice: string;
};

type LabelCreditBalanceRow = {
  labelProductId: string;
  quantityAvailable: number;
};

type PendingLabelCreditOperation = {
  createdAt: string;
  idempotencyKey: string;
  quantity: number;
};

function labelCreditOperationStorageKey(customerId: string) {
  return `kenpost:admin-label-credit:${customerId}`;
}

function readPendingLabelCreditOperation(customerId: string): PendingLabelCreditOperation | null {
  try {
    const value = window.localStorage.getItem(labelCreditOperationStorageKey(customerId));
    if (!value) return null;
    const parsed = JSON.parse(value) as Partial<PendingLabelCreditOperation>;
    if (!text(parsed.idempotencyKey)) return null;
    return {
      createdAt: text(parsed.createdAt),
      idempotencyKey: text(parsed.idempotencyKey),
      quantity: Number(parsed.quantity || 0),
    };
  } catch {
    return null;
  }
}

function writePendingLabelCreditOperation(customerId: string, operation: PendingLabelCreditOperation | null) {
  try {
    if (operation) {
      window.localStorage.setItem(labelCreditOperationStorageKey(customerId), JSON.stringify(operation));
    } else {
      window.localStorage.removeItem(labelCreditOperationStorageKey(customerId));
    }
  } catch {
    // The server-side idempotency key still protects the operation when browser storage is unavailable.
  }
}

function wait(milliseconds: number) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

type LabelProductSortable = {
  name?: unknown;
  weightMaxGrams?: unknown;
  weightMinGrams?: unknown;
};

function labelProductSortWeight(product: LabelProductSortable) {
  const direct = Number(product.weightMaxGrams || product.weightMinGrams || 0);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const name = text(product.name);
  const kgMatch = name.match(/(\d+(?:[,.]\d+)?)\s*kg/i);
  if (kgMatch?.[1]) return Number(kgMatch[1].replace(",", ".")) * 1000;
  const gramMatch = name.match(/(\d+(?:[,.]\d+)?)\s*g\b/i);
  if (gramMatch?.[1]) return Number(gramMatch[1].replace(",", "."));
  return Number.MAX_SAFE_INTEGER;
}

function AdminLabelCreditCart({
  balances,
  customerId,
  customerName,
  onDone,
  prices,
  products,
}: {
  balances: LabelCreditBalanceRow[];
  customerId: string;
  customerName: string;
  onDone: (message?: string) => Promise<void>;
  prices: AdminCustomerLabelPriceRow[];
  products: DataRow[];
}) {
  const activeProducts = useMemo(
    () =>
      sortLabelProductsForOrders(products.filter((product) => text(product.id) && product.active !== false)),
    [products]
  );
  const priceByProduct = useMemo(() => new Map(prices.map((item) => [item.labelProductId, item])), [prices]);
  const balanceByProduct = useMemo(
    () => new Map(balances.map((item) => [item.labelProductId, Number(item.quantityAvailable || 0)])),
    [balances]
  );
  const [rows, setRows] = useState<LabelCreditCartRow[]>([]);
  const [bonus, setBonus] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingOperationKey, setPendingOperationKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  function numberInput(value: string) {
    const parsed = Number(String(value || "").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function productStock(productId: string) {
    const product = activeProducts.find((item) => text(item.id) === productId);
    return Number(product?.availableCount || 0);
  }

  function currentBalance(productId: string) {
    return balanceByProduct.get(productId) || 0;
  }

  function effectiveNetPrice(productId: string) {
    const price = priceByProduct.get(productId);
    const customerPrice = Number(price?.price);
    if (price && price.active && Number.isFinite(customerPrice) && customerPrice > 0) return customerPrice;
    const product = activeProducts.find((item) => text(item.id) === productId);
    return Number(product?.basePrice || price?.basePrice || 0);
  }

  function buildRows(current: LabelCreditCartRow[] = []) {
    const currentByProduct = new Map(current.map((row) => [row.labelProductId, row]));
    return activeProducts.map((product) => {
      const productId = text(product.id);
      const existing = currentByProduct.get(productId);
      return {
        labelProductId: productId,
        quantity: existing?.quantity || "",
        unitPrice: existing?.unitPrice || effectiveNetPrice(productId).toFixed(2),
      };
    });
  }

  useEffect(() => {
    setRows((current) => buildRows(current));
  }, [activeProducts, priceByProduct]);

  useEffect(() => {
    const pending = readPendingLabelCreditOperation(customerId);
    if (!pending) return;
    let active = true;
    setPendingOperationKey(pending.idempotencyKey);
    setSaving(true);
    setError(null);
    setSuccess(null);

    void (async () => {
      try {
        let result = await apiClient.adminLabelCreditCartOperation(pending.idempotencyKey);
        for (let attempt = 0; result.status === "processing" && attempt < 60; attempt += 1) {
          await wait(1_500);
          if (!active) return;
          result = await apiClient.adminLabelCreditCartOperation(pending.idempotencyKey);
        }
        if (!active) return;
        if (result.status === "completed") {
          const message = `${numberText(result.summary.quantity)} etiket kredisi eklendi. Yenilenen sayfadaki işlem güvenli şekilde doğrulandı.`;
          writePendingLabelCreditOperation(customerId, null);
          setPendingOperationKey("");
          setRows((current) => current.map((row) => ({ ...row, quantity: "" })));
          setBonus(false);
          setNotes("");
          setSuccess(message);
          await onDone(message).catch(() => undefined);
        } else if (result.status === "failed") {
          writePendingLabelCreditOperation(customerId, null);
          setPendingOperationKey("");
          setError(result.error);
        } else {
          setError("Kredi işlemi sunucuda hâlâ doğrulanıyor. Aynı krediyi tekrar eklemeyin; sayfayı yenilediğinizde kontrol otomatik devam eder.");
        }
      } catch (err) {
        if (!active) return;
        if (err instanceof ApiError && err.status === 404) {
          writePendingLabelCreditOperation(customerId, null);
          setPendingOperationKey("");
        }
        setError(err instanceof ApiError ? err.message : "Önceki kredi işleminin durumu alınamadı.");
      } finally {
        if (active) setSaving(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [customerId]);

  function updateRow(rowId: string, patch: Partial<LabelCreditCartRow>) {
    setRows((current) =>
      current.map((row) => (row.labelProductId === rowId ? { ...row, ...patch } : row))
    );
  }

  const totals = rows.reduce(
    (sum, row) => {
      const quantity = Math.max(0, Math.trunc(numberInput(row.quantity)));
      const netUnit = bonus ? 0 : Math.max(0, numberInput(row.unitPrice));
      const net = Math.round(quantity * netUnit * 100) / 100;
      return {
        currentBalance: sum.currentBalance + currentBalance(row.labelProductId),
        gross: Math.round((sum.gross + net * 1.19) * 100) / 100,
        net: Math.round((sum.net + net) * 100) / 100,
        quantity: sum.quantity + quantity,
        totalBalance: sum.totalBalance + currentBalance(row.labelProductId) + quantity,
        vat: Math.round((sum.vat + net * 0.19) * 100) / 100,
      };
    },
    { currentBalance: 0, gross: 0, net: 0, quantity: 0, totalBalance: 0, vat: 0 }
  );
  const cartLines = rows
    .map((row) => {
      const product = activeProducts.find((item) => text(item.id) === row.labelProductId);
      const quantity = Math.max(0, Math.trunc(numberInput(row.quantity)));
      const balance = currentBalance(row.labelProductId);
      const unitPrice = Math.max(0, numberInput(row.unitPrice));
      return {
        balance,
        carrier: text(product?.carrier) || "DHL",
        net: bonus ? 0 : Math.round(quantity * unitPrice * 100) / 100,
        productName: text(product?.name) || row.labelProductId,
        quantity,
        row,
        stock: productStock(row.labelProductId),
        totalBalance: balance + quantity,
        unitPrice,
      };
    })
    .filter((line) => line.quantity > 0);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pendingOperationKey) {
      setError("Önceki kredi işlemi henüz doğrulanıyor. Mükerrer kredi oluşmaması için yeni işlem başlatılmadı.");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    let idempotencyKey = "";
    try {
      const items = rows
        .map((row) => ({
          bonus,
          labelProductId: row.labelProductId,
          quantity: Math.trunc(numberInput(row.quantity)),
          unitPrice: Math.max(0, numberInput(row.unitPrice)),
        }))
        .filter((item) => item.quantity > 0);
      idempotencyKey = `label-credit:${crypto.randomUUID()}`;
      writePendingLabelCreditOperation(customerId, {
        createdAt: new Date().toISOString(),
        idempotencyKey,
        quantity: items.reduce((sum, item) => sum + item.quantity, 0),
      });
      setPendingOperationKey(idempotencyKey);

      let result = await apiClient.allocateAdminLabelCreditCart({
        createInvoice: false,
        customerId,
        idempotencyKey,
        items,
        notes,
      });
      for (let attempt = 0; result.status === "processing" && attempt < 60; attempt += 1) {
        await wait(1_500);
        result = await apiClient.adminLabelCreditCartOperation(idempotencyKey);
      }
      if (result.status === "processing") {
        throw new Error("Kredi işlemi sunucuda devam ediyor. Aynı krediyi tekrar eklemeyin; durum otomatik olarak korunuyor.");
      }
      if (result.status === "failed") throw new Error(result.error);

      const message = `${numberText(result.summary.quantity)} etiket kredisi eklendi.`;
      writePendingLabelCreditOperation(customerId, null);
      setPendingOperationKey("");
      setSuccess(message);
      setRows(buildRows().map((row) => ({ ...row, quantity: "" })));
      setBonus(false);
      setNotes("");
      await onDone(message).catch(() => undefined);
    } catch (err) {
      if (idempotencyKey) {
        try {
          const status = await apiClient.adminLabelCreditCartOperation(idempotencyKey);
          if (status.status === "completed") {
            const message = `${numberText(status.summary.quantity)} etiket kredisi eklendi ve sunucudan doğrulandı.`;
            writePendingLabelCreditOperation(customerId, null);
            setPendingOperationKey("");
            setSuccess(message);
            setRows(buildRows().map((row) => ({ ...row, quantity: "" })));
            setBonus(false);
            setNotes("");
            await onDone(message).catch(() => undefined);
            return;
          }
          if (status.status === "failed") {
            writePendingLabelCreditOperation(customerId, null);
            setPendingOperationKey("");
            setError(status.error);
            return;
          }
          setError("İşlem sunucuda sürüyor ve güvenli işlem anahtarıyla korunuyor. Aynı krediyi tekrar eklemeyin.");
          return;
        } catch (statusError) {
          if (statusError instanceof ApiError && statusError.status === 404) {
            writePendingLabelCreditOperation(customerId, null);
            setPendingOperationKey("");
          }
        }
      }
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Kredi sepeti eklenemedi.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPanel title="Etiket kredi sepeti" caption={`${customerName} için aktif etiket ürünleri ve mevcut kredi durumu.`}>
      <form className="space-y-4 p-5" onSubmit={submit}>
        {success ? <AdminNotice message={success} /> : null}
        {pendingOperationKey ? (
          <AdminNotice
            tone="warning"
            message="Kredi işlemi tekil işlem anahtarıyla sunucuda doğrulanıyor. Sayfa yenilense bile aynı kredi ikinci kez eklenmez."
          />
        ) : null}
        {error ? <ErrorBanner message={error} /> : null}
        {activeProducts.length === 0 ? (
          <AdminNotice tone="warning" message="Aktif etiket ürünü bulunamadı. Önce etiket ürünü tanımlayın veya ürünü aktif hale getirin." />
        ) : (
          <>
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
              <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="overflow-x-auto">
                  <div className="min-w-[980px] text-sm">
                    <div className="grid grid-cols-[minmax(210px,1.4fr)_110px_120px_120px_120px_130px_120px] items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                      <span>Ürün</span>
                      <span className="text-right">Hazır stok</span>
                      <span className="text-right">Mevcut kredi</span>
                      <span className="text-right">Yeni kredi</span>
                      <span className="text-right">Toplam kredi</span>
                      <span className="text-right">Kullanım birim net</span>
                      <span className="text-right">Tutar</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {rows.map((row) => {
                        const product = activeProducts.find((item) => text(item.id) === row.labelProductId);
                        const quantity = Math.max(0, Math.trunc(numberInput(row.quantity)));
                        const balance = currentBalance(row.labelProductId);
                        const unitPrice = Math.max(0, numberInput(row.unitPrice));
                        const rowNet = bonus ? 0 : Math.round(quantity * unitPrice * 100) / 100;
                        const productName = text(product?.name) || row.labelProductId;
                        return (
                          <div key={row.labelProductId} className="grid grid-cols-[minmax(210px,1.4fr)_110px_120px_120px_120px_130px_120px] items-center gap-3 px-4 py-3">
                            <div className="min-w-0">
                              <p className="truncate font-semibold text-slate-950">{productName}</p>
                              <p className="mt-1 truncate text-xs text-slate-500">{text(product?.carrier) || "DHL"}</p>
                            </div>
                            <p className="text-right font-semibold text-slate-700">{numberText(productStock(row.labelProductId))}</p>
                            <p className="text-right font-semibold text-slate-700">{numberText(balance)}</p>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={row.quantity}
                              onChange={(event) => updateRow(row.labelProductId, { quantity: event.target.value })}
                              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-right text-sm font-semibold text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                              aria-label={`${productName} yeni kredi`}
                            />
                            <p className="text-right font-semibold text-slate-950">{numberText(balance + quantity)}</p>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.unitPrice}
                              onChange={(event) => updateRow(row.labelProductId, { unitPrice: event.target.value })}
                              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-right text-sm font-semibold text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                              aria-label={`${productName} birim net`}
                            />
                            <p className="text-right font-semibold text-slate-950">{moneyText(rowNet, "EUR")}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <aside className="flex min-h-full flex-col rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-950">Sepet özeti</p>
                    <p className="mt-1 text-xs text-slate-500">{cartLines.length ? `${numberText(cartLines.length)} ürün satırı seçildi` : "Henüz ürün seçilmedi"}</p>
                  </div>
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500">
                    <ShoppingCart className="h-4 w-4" />
                  </span>
                </div>

                <div className="mt-4 flex-1 space-y-2">
                  {cartLines.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
                      Soldaki ürünlerden yeni kredi adedi girince sepet burada görünür.
                    </div>
                  ) : (
                    cartLines.map((line) => (
                      <div key={line.row.labelProductId} className="rounded-lg border border-slate-200 bg-white p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-950">{line.productName}</p>
                            <p className="mt-1 text-xs text-slate-500">{numberText(line.balance)} + {numberText(line.quantity)} = {numberText(line.totalBalance)} kredi</p>
                          </div>
                          <p className="shrink-0 text-sm font-semibold text-slate-950">{moneyText(line.net, "EUR")}</p>
                        </div>
                        <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                          <span>{numberText(line.quantity)} adet</span>
                          <span>{bonus ? `Bonus · ${moneyText(line.unitPrice, "EUR")} kullanım fiyatı` : `${moneyText(line.unitPrice, "EUR")} net`}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="mt-4 space-y-2 border-t border-slate-200 pt-4 text-sm">
                  <div className="flex items-center justify-between text-slate-600">
                    <span>Mevcut kredi</span>
                    <span className="font-semibold text-slate-950">{numberText(totals.currentBalance)}</span>
                  </div>
                  <div className="flex items-center justify-between text-slate-600">
                    <span>Yeni kredi</span>
                    <span className="font-semibold text-slate-950">{numberText(totals.quantity)}</span>
                  </div>
                  <div className="flex items-center justify-between text-slate-600">
                    <span>Toplam kredi</span>
                    <span className="font-semibold text-slate-950">{numberText(totals.totalBalance)}</span>
                  </div>
                  <div className="flex items-center justify-between text-slate-600">
                    <span>Net</span>
                    <span className="font-semibold text-slate-950">{moneyText(totals.net, "EUR")}</span>
                  </div>
                  <div className="flex items-center justify-between text-slate-600">
                    <span>KDV %19</span>
                    <span className="font-semibold text-slate-950">{moneyText(totals.vat, "EUR")}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-slate-950 px-3 py-2 text-white">
                    <span className="text-sm font-semibold">Brüt</span>
                    <span className="text-lg font-semibold">{moneyText(totals.gross, "EUR")}</span>
                  </div>
                </div>

                <label className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={bonus}
                    onChange={(event) => setBonus(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-slate-900"
                  />
                  Bonus/telafi etiketi
                </label>

                <button
                  type="submit"
                  disabled={saving || Boolean(pendingOperationKey) || activeProducts.length === 0 || totals.quantity <= 0}
                  className="mt-3 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tags className="h-4 w-4" />}
                  {saving || pendingOperationKey ? "Kredi işlemi doğrulanıyor" : "Kredi ekle"}
                </button>
              </aside>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-slate-700">Not</span>
              <textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={3}
                placeholder="Bu kredi ekleme işlemi için not ekleyin."
                className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
              />
              <span className="mt-1.5 block text-xs text-slate-500">Bu not kredi hareketi detayında işlem notu olarak saklanır.</span>
            </label>
          </>
        )}
      </form>
    </AdminPanel>
  );
}

type LabelProductMetricKey = "all" | "available" | "allocatable" | "assigned" | "consumed" | "void" | "checkError";

const labelProductMetricTabs: Array<{ key: LabelProductMetricKey; label: string }> = [
  { key: "all", label: "Tümü" },
  { key: "available", label: "Available" },
  { key: "allocatable", label: "Atanabilir" },
  { key: "assigned", label: "Atanmış" },
  { key: "consumed", label: "Tüketilmiş" },
  { key: "void", label: "Void" },
  { key: "checkError", label: "Hatalı" },
];

function labelProductMetricValue(row: DataRow, key: LabelProductMetricKey) {
  if (key === "all") return rowNumber(row, "sourceCount");
  if (key === "available") return rowNumber(row, "availableRawCount") || rowNumber(row, "availableCount");
  if (key === "allocatable") return rowNumber(row, "availableCount");
  if (key === "assigned") return rowNumber(row, "assignedCount");
  if (key === "consumed") return rowNumber(row, "consumedCount");
  if (key === "void") return rowNumber(row, "voidCount");
  return rowNumber(row, "checkErrorCount");
}

function AdminLabelsPage() {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [bulkChecking, setBulkChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [metric, setMetric] = useState<LabelProductMetricKey>("all");

  async function loadLabels() {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.resource("/api/admin/labels");
      setData(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etiket verileri alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadLabels();
  }, []);

  async function checkCarrier(labelSourceId: string) {
    setCheckingId(labelSourceId);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.checkLabelCarrier(labelSourceId);
      const labelSource = result.labelSource as { carrierCheckStatus?: unknown; carrierCheckMessage?: unknown };
      setNotice(`${text(labelSource.carrierCheckStatus)}: ${text(labelSource.carrierCheckMessage) || "Taşıyıcı kontrolü tamamlandı."}`);
      await loadLabels();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Taşıyıcı kontrolü tamamlanamadı.");
    } finally {
      setCheckingId(null);
    }
  }

  async function checkVisibleCarriers() {
    const ids = recentSources
      .filter((row) => {
        const carrierHint = `${text(row.labelProductCarrier)} ${text(row.labelProductName)}`.toLocaleLowerCase("de-DE");
        return carrierHint.includes("dhl");
      })
      .map((row) => text(row.id))
      .filter(Boolean);

    if (ids.length === 0) {
      setNotice("Görünen listede DHL etiketi bulunamadı.");
      return;
    }

    setBulkChecking(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.checkLabelCarriers(ids);
      setNotice(`${result.requestedCount} DHL etiketi Cloudflare içi kontrol kuyruğuna alındı.`);
      window.setTimeout(() => void loadLabels(), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Toplu taşıyıcı kontrolü başlatılamadı.");
    } finally {
      setBulkChecking(false);
    }
  }

  const products = Array.isArray(data?.products) ? (data?.products as DataRow[]) : [];
  const recentSources = Array.isArray(data?.recentSources) ? (data?.recentSources as DataRow[]) : [];
  const totalSources = products.reduce((sum, row) => sum + Number(row.sourceCount || 0), 0);
  const availableSources = products.reduce((sum, row) => sum + labelProductMetricValue(row, "available"), 0);
  const allocatableSources = products.reduce((sum, row) => sum + labelProductMetricValue(row, "allocatable"), 0);
  const assignedSources = products.reduce((sum, row) => sum + labelProductMetricValue(row, "assigned"), 0);
  const consumedSources = products.reduce((sum, row) => sum + Number(row.consumedCount || 0), 0);
  const voidSources = products.reduce((sum, row) => sum + labelProductMetricValue(row, "void"), 0);
  const faultySources = products.reduce((sum, row) => sum + labelProductMetricValue(row, "checkError"), 0);
  const expiredSources = products.reduce((sum, row) => sum + Number(row.expiredCount || 0), 0);
  const selectedMetricTotal = products.reduce((sum, row) => sum + labelProductMetricValue(row, metric), 0);
  const visibleProducts = metric === "all" ? products : products.filter((row) => labelProductMetricValue(row, metric) > 0);
  const carrierCount = new Set(products.map((row) => text(row.carrier)).filter(Boolean)).size;

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Etiket Ürünleri"
        caption="Ürün bazlı stoğu, atanabilir etiketleri, tüketilenleri, void ve hatalı etiketleri eski sistemdeki kırılımla yönetin."
        icon={<Package className="h-5 w-5" />}
        action={
          <div className="flex flex-wrap gap-2">
            <Link
              to="/admin/label-sources"
              className="inline-flex w-fit items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <Tags className="h-3.5 w-3.5" />
              Envanter
            </Link>
            <Link
              to="/admin/faulty-labels"
              className="inline-flex w-fit items-center justify-center gap-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 shadow-sm transition hover:bg-red-100"
            >
              <History className="h-3.5 w-3.5" />
              Hatalı Etiketler
            </Link>
            <Link
              to="/admin/label-products/new"
              className="inline-flex w-fit items-center justify-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              <PlusSquare className="h-3.5 w-3.5" />
              Yeni Etiket Ürünü Oluştur
            </Link>
          </div>
        }
      />
      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}
      {loading ? (
        <LoadingPanel label="Etiket verileri yükleniyor" />
      ) : (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <AdminStatCard label="Ürün" value={products.length} tone="cyan" icon={<Package className="h-5 w-5" />} />
            <AdminStatCard label="Toplam" value={totalSources} tone="slate" icon={<Tags className="h-5 w-5" />} />
            <AdminStatCard label="Atanabilir" value={allocatableSources} tone="emerald" icon={<CheckCircle2 className="h-5 w-5" />} />
            <AdminStatCard label="Atanmış" value={assignedSources} tone="violet" icon={<Users className="h-5 w-5" />} />
            <AdminStatCard label="Tüketilen" value={consumedSources} tone="amber" icon={<Clock3 className="h-5 w-5" />} />
          </section>

          <section className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Etiket Ürünü Durumu</h2>
                <p className="mt-1 text-sm text-slate-500">{numberText(carrierCount)} taşıyıcı grubu üzerinden ürün bazlı stok dağılımı.</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white px-5 py-3 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Seçili toplam</p>
                <p className="mt-1 text-2xl font-semibold text-slate-950">{numberText(selectedMetricTotal)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1 text-sm sm:grid-cols-4 xl:grid-cols-7">
              {labelProductMetricTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setMetric(tab.key)}
                  className={cx(
                    "rounded-xl px-3 py-2 font-semibold transition",
                    metric === tab.key ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:bg-white/70 hover:text-slate-800"
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
              {visibleProducts.map((product) => {
                const total = Math.max(1, rowNumber(product, "sourceCount"));
                const allocatableWidth = Math.min(100, Math.round((labelProductMetricValue(product, "allocatable") / total) * 100));
                const problemCount = labelProductMetricValue(product, "checkError") + labelProductMetricValue(product, "void") + rowNumber(product, "expiredCount");
                return (
                  <Link key={text(product.id)} to={`/admin/label-sources?labelProductId=${encodeURIComponent(text(product.id))}&status=all`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
                    <div className="flex items-start justify-between gap-3">
                      <NameStack title={text(product.name)} detail={platformLabel(text(product.carrier))} />
                      <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700">{numberText(labelProductMetricValue(product, metric))}</span>
                    </div>
                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
                      <div className={cx("h-full rounded-full", problemCount > 0 ? "bg-amber-400" : "bg-cyan-500")} style={{ width: `${allocatableWidth}%` }} />
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-x-3 gap-y-2 text-xs text-slate-500">
                      <span>Avail {numberText(labelProductMetricValue(product, "available"))}</span>
                      <span>Hazır {numberText(labelProductMetricValue(product, "allocatable"))}</span>
                      <span>Atanmış {numberText(labelProductMetricValue(product, "assigned"))}</span>
                      <span>Kullanıldı {numberText(labelProductMetricValue(product, "consumed"))}</span>
                      <span>Void {numberText(labelProductMetricValue(product, "void"))}</span>
                      <span>Hatalı {numberText(labelProductMetricValue(product, "checkError"))}</span>
                    </div>
                  </Link>
                );
              })}
              {visibleProducts.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm font-medium text-slate-500">
                  Bu sekmede ürün yok.
                </div>
              ) : null}
            </div>
          </section>

          <AdminPanel title="Ürün tablosu" caption={`${numberText(totalSources)} kaynak etiket; ${numberText(faultySources)} hatalı etiket, ${numberText(voidSources)} void, ${numberText(expiredSources)} süresi bitmiş kayıt.`} count={products.length}>
            <DataTable
              rows={products}
              empty="Kayıt yok."
              columns={[
                {
                  label: "Ürün",
                  value: (row) => (
                    <Link className="block hover:underline" to={`/admin/label-sources?labelProductId=${encodeURIComponent(text(row.id))}&status=all`}>
                      <NameStack title={text(row.name)} detail={text(row.carrier)} />
                    </Link>
                  ),
                },
                { label: "Gram", value: (row) => `${numberText(row.weightMinGrams)}-${numberText(row.weightMaxGrams)}` },
                { label: "Baz fiyat", value: (row) => moneyText(row.basePrice, "EUR"), align: "right" },
                { label: "Toplam", value: (row) => numberText(row.sourceCount), align: "right" },
                { label: "Kullanılabilir", value: (row) => numberText(labelProductMetricValue(row, "available")), align: "right" },
                { label: "Tahsis edilebilir", value: (row) => numberText(labelProductMetricValue(row, "allocatable")), align: "right" },
                { label: "Hatalı", value: (row) => numberText(labelProductMetricValue(row, "checkError")), align: "right" },
                { label: "Atanmış", value: (row) => numberText(labelProductMetricValue(row, "assigned")), align: "right" },
                { label: "Tüketilmiş", value: (row) => numberText(row.consumedCount), align: "right" },
                { label: "İptal", value: (row) => numberText(row.voidCount), align: "right" },
                { label: "Aktif", value: (row) => statusBadge(row.active ? "active" : "inactive") },
              ]}
            />
          </AdminPanel>

          <AdminPanel
            title="Son kaynak etiketleri"
            caption="Yüklenen ve müşterilere atanan son kaynak etiketleri."
            count={recentSources.length}
            action={
              <button
                type="button"
                disabled={bulkChecking}
                onClick={() => void checkVisibleCarriers()}
                className="inline-flex w-fit items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
              >
                {bulkChecking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                Görünen DHL etiketlerini kontrol et
              </button>
            }
          >
            {expiredSources > 0 ? (
              <div className="border-b border-slate-100 px-5 py-3 text-sm text-slate-600">
                Süresi biten etiketler için{" "}
                <Link className="font-semibold text-slate-950 hover:underline" to="/admin/expired-labels">
                  ayrı listeyi aç
                </Link>
                .
              </div>
            ) : null}
            <DataTable
              rows={recentSources}
              empty="Kayıt yok."
              columns={[
                { label: "Takip", value: (row) => text(row.trackingNumber) },
                { label: "Ürün", value: (row) => <NameStack title={text(row.labelProductName)} detail={text(row.labelProductCarrier)} /> },
                { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                { label: "Taşıyıcı", value: (row) => <NameStack title={text(row.carrierCheckStatus)} detail={dateText(row.carrierCheckCheckedAt)} /> },
                { label: "Müşteri", value: (row) => text(row.assignedToEmail) },
                {
                  label: "Kontrol",
                  value: (row) => {
                    const id = text(row.id);
                    return (
                      <button
                        type="button"
                        disabled={!id || checkingId === id}
                        onClick={() => void checkCarrier(id)}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
                      >
                        {checkingId === id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                        DHL kontrol
                      </button>
                    );
                  },
                },
              ]}
            />
          </AdminPanel>
        </>
      )}
    </div>
  );
}

const labelInventoryStatusOptions = [
  { value: "all", label: "Tüm durumlar" },
  { value: "available", label: "Available" },
  { value: "assigned", label: "Atanmış" },
  { value: "consumed", label: "Tüketilmiş" },
  { value: "void", label: "Void" },
  { value: "expired", label: "Süresi bitmiş" },
  { value: "checkError", label: "Hatalı etiketler" },
];

function labelSourceMatchesStatus(row: DataRow, status: string) {
  if (status === "all") return true;
  if (status === "checkError") {
    const carrierDeliveryStatus = text(row.carrierDeliveryStatus).toLowerCase();
    const inventoryStatus = text(row.status).toLowerCase();
    const hasCarrierMovementOrProblem = ["in_transit", "delivered", "return_in_progress", "returned", "problem"].includes(carrierDeliveryStatus);
    return (
      Boolean(text(row.carrierRemediationStatus)) ||
      (!["consumed", "expired"].includes(inventoryStatus) && hasCarrierMovementOrProblem)
    );
  }
  return text(row.status).toLowerCase() === status;
}

const defaultLabelInventoryStatus = "available";
const defaultLabelInventorySort = "expiry_soon";

const labelInventorySortOptions = [
  { value: "recent_activity", label: "Son hareket" },
  { value: "oldest_pdf", label: "En eski PDF önce" },
  { value: "expiry_soon", label: "Süresi önce dolacak" },
];

function normalizeLabelInventoryStatus(value: unknown) {
  const status = text(value);
  if (!status) return defaultLabelInventoryStatus;
  return labelInventoryStatusOptions.some((option) => option.value === status) ? status : "all";
}

function normalizeLabelInventorySort(value: unknown) {
  const sort = text(value);
  return labelInventorySortOptions.some((option) => option.value === sort) ? sort : defaultLabelInventorySort;
}

function labelInventoryPdfCreationCell(row: DataRow) {
  const sourceCreatedAt = labelInventoryPdfCreatedAt(row);
  return sourceCreatedAt
    ? <NameStack title={dateTextWithYear(sourceCreatedAt)} detail="PDF içinden okundu" />
    : <NameStack title="Bilinmiyor" detail="PDF oluşturma tarihi bulunamadı" />;
}

function labelInventoryValidityCell(row: DataRow) {
  const meta = labelInventoryExpiryMeta(row);
  if (!meta.known || !meta.expiresAt) {
    return <NameStack title="Bilinmiyor" detail={meta.detail} />;
  }
  return (
    <div
      className={cx(
        "min-w-[145px] rounded-xl border px-3 py-2",
        meta.urgency === "expired" && "border-red-200 bg-red-50 text-red-800",
        meta.urgency === "warning" && "border-amber-200 bg-amber-50 text-amber-900",
        meta.urgency === "known" && "border-emerald-200 bg-emerald-50 text-emerald-900"
      )}
    >
      <p className="font-semibold">{dateText(meta.expiresAt)}</p>
      <p className="mt-0.5 text-xs font-medium opacity-80">{meta.detail}</p>
    </div>
  );
}

function AdminLabelInventoryPage() {
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const labelProductId = text(searchParams.get("labelProductId"));
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(() => normalizeLabelInventoryStatus(searchParams.get("status")));
  const [sort, setSort] = useState(() => normalizeLabelInventorySort(searchParams.get("sort")));
  const [refreshKey, setRefreshKey] = useState(0);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [bulkChecking, setBulkChecking] = useState(false);
  const [verifyingPdfExpiries, setVerifyingPdfExpiries] = useState(false);
  const [releasingPending, setReleasingPending] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [customers, setCustomers] = useState<DataRow[]>([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [selectedLabelSourceIds, setSelectedLabelSourceIds] = useState<string[]>([]);
  const [targetCustomerId, setTargetCustomerId] = useState("");
  const [selectionSaving, setSelectionSaving] = useState(false);

  function updateStatus(nextStatus: string) {
    const normalizedStatus = normalizeLabelInventoryStatus(nextStatus);
    setStatus(normalizedStatus);
    const nextParams = new URLSearchParams(searchParams);
    if (normalizedStatus === defaultLabelInventoryStatus) {
      nextParams.delete("status");
    } else {
      nextParams.set("status", normalizedStatus);
    }
    setSearchParams(nextParams, { replace: true });
  }

  function updateSort(nextSort: string) {
    const normalizedSort = normalizeLabelInventorySort(nextSort);
    setSort(normalizedSort);
    const nextParams = new URLSearchParams(searchParams);
    if (normalizedSort === defaultLabelInventorySort) {
      nextParams.delete("sort");
    } else {
      nextParams.set("sort", normalizedSort);
    }
    setSearchParams(nextParams, { replace: true });
  }

  useEffect(() => {
    const nextStatus = normalizeLabelInventoryStatus(searchParams.get("status"));
    const nextSort = normalizeLabelInventorySort(searchParams.get("sort"));
    setStatus((current) => (current === nextStatus ? current : nextStatus));
    setSort((current) => (current === nextSort ? current : nextSort));
  }, [searchParams]);

  useEffect(() => {
    let active = true;
    setCustomersLoading(true);
    apiClient
      .resource<{ customers?: DataRow[] }>("/api/admin/customers?limit=250&status=active")
      .then((result) => {
        if (!active) return;
        setCustomers(Array.isArray(result.customers) ? result.customers : []);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Müşteri hesapları alınamadı.");
      })
      .finally(() => {
        if (active) setCustomersLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(
      () => {
        const params = new URLSearchParams();
        const trimmedQuery = query.trim();
        if (labelProductId) params.set("labelProductId", labelProductId);
        if (status !== "all") params.set("status", status);
        params.set("sort", sort);
        if (trimmedQuery) params.set("q", trimmedQuery);
        params.set("limit", labelProductId ? "10000" : status !== "all" || trimmedQuery ? "2000" : "500");

        setLoading(true);
        setError(null);
        apiClient
          .resource(`/api/admin/label-sources?${params.toString()}`)
          .then((result) => {
            if (!active) return;
            setData(result);
          })
          .catch((err) => {
            if (active) setError(err instanceof ApiError ? err.message : "Etiket envanteri alınamadı.");
          })
          .finally(() => {
            if (active) setLoading(false);
          });
      },
      query.trim() ? 250 : 0
    );

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [labelProductId, query, refreshKey, sort, status]);

  const labels = Array.isArray(data?.labels) ? (data.labels as DataRow[]) : [];
  const stats = data?.stats && typeof data.stats === "object" ? (data.stats as DataRow) : {};
  const filter = data?.filter && typeof data.filter === "object" ? (data.filter as DataRow) : {};
  const product = data?.product && typeof data.product === "object" ? (data.product as DataRow) : null;
  const normalizedQuery = query.trim().toLocaleLowerCase("tr-TR");
  const filteredLabels = labels.filter((row) => {
    const haystack = [
      row.trackingNumber,
      row.originalFileName,
      row.savedFileName,
      row.labelProductName,
      row.labelProductCarrier,
      row.assignedToEmail,
      row.assignedToCompanyName,
      row.consumedByShipmentId,
      row.carrierCheckMessage,
      row.externalUsageNote,
    ]
      .map(text)
      .join(" ")
      .toLocaleLowerCase("tr-TR");
    return (!normalizedQuery || haystack.includes(normalizedQuery)) && labelSourceMatchesStatus(row, status);
  });
  const filteredCount = rowNumber(filter, "filteredCount") || filteredLabels.length;
  const sourceCaption =
    filteredCount > filteredLabels.length
      ? `${numberText(filteredCount)} kayıt bulundu; ${numberText(filteredLabels.length)} kayıt listeleniyor.`
      : `${numberText(filteredLabels.length)} kayıt gösteriliyor.`;
  const pendingAvailableCount = rowNumber(stats, "pendingAvailableCount");
  const selectedLabelSourceIdSet = new Set(selectedLabelSourceIds);
  const selectedRows = labels.filter((row) => selectedLabelSourceIdSet.has(text(row.id)));
  const selectedStatuses = [...new Set(selectedRows.map((row) => text(row.status).toLowerCase()))];
  const selectedCustomerIds = [...new Set(selectedRows.map((row) => text(row.assignedToUserId)).filter(Boolean))];
  const selectedMode = selectedStatuses.length === 1 && selectedStatuses[0] === "available"
    ? "assign"
    : selectedStatuses.length === 1 && selectedStatuses[0] === "assigned" && selectedCustomerIds.length === 1
      ? "return"
      : "mixed";
  const targetCustomer = customers.find((row) => text(row.id) === targetCustomerId);

  useEffect(() => {
    const currentIds = new Set(labels.map((row) => text(row.id)).filter(Boolean));
    setSelectedLabelSourceIds((current) => {
      const next = current.filter((id) => currentIds.has(id));
      return next.length === current.length && next.every((id, index) => id === current[index]) ? current : next;
    });
  }, [data]);

  function customerSelectionLabel(row: DataRow) {
    const company = text(row.companyName);
    const name = [text(row.firstName), text(row.lastName)].filter(Boolean).join(" ");
    const email = text(row.email);
    return [company || name || email || text(row.id), email && email !== company ? email : ""].filter(Boolean).join(" · ");
  }

  function toggleLabelSelection(row: DataRow) {
    const id = text(row.id);
    const rowStatus = text(row.status).toLowerCase();
    if (!id || !["available", "assigned"].includes(rowStatus)) return;
    setSelectedLabelSourceIds((current) => {
      if (current.includes(id)) return current.filter((item) => item !== id);
      const currentRows = labels.filter((item) => current.includes(text(item.id)));
      const currentStatuses = new Set(currentRows.map((item) => text(item.status).toLowerCase()));
      if (currentStatuses.size > 0 && !currentStatuses.has(rowStatus)) {
        setNotice("Atanabilir ve atanmış etiketler aynı işlemde seçilemez. Önce mevcut seçimi temizleyin.");
        return current;
      }
      const currentProductIds = new Set(currentRows.map((item) => text(item.labelProductId)).filter(Boolean));
      const rowProductId = text(row.labelProductId);
      if (currentProductIds.size > 0 && (!rowProductId || !currentProductIds.has(rowProductId))) {
        setNotice("Aynı işlemde yalnızca tek ağırlık/etiket ürününün kayıtlarını seçebilirsiniz.");
        return current;
      }
      if (rowStatus === "assigned") {
        const assignedCustomerIds = new Set(currentRows.map((item) => text(item.assignedToUserId)).filter(Boolean));
        const rowCustomerId = text(row.assignedToUserId);
        if (!rowCustomerId || (assignedCustomerIds.size > 0 && !assignedCustomerIds.has(rowCustomerId))) {
          setNotice("Havuza iade için yalnızca aynı müşterinin etiketlerini birlikte seçebilirsiniz.");
          return current;
        }
      }
      if (current.length >= 100) {
        setNotice("Tek işlemde en fazla 100 etiket seçilebilir.");
        return current;
      }
      setNotice(null);
      return [...current, id];
    });
  }

  function selectVisibleAvailableLabels() {
    const availableRows = filteredLabels.filter((row) => text(row.status).toLowerCase() === "available");
    const firstProductId = text(availableRows[0]?.labelProductId);
    const ids = availableRows
      .filter((row) => !firstProductId || text(row.labelProductId) === firstProductId)
      .map((row) => text(row.id))
      .filter(Boolean)
      .slice(0, 100);
    setSelectedLabelSourceIds(ids);
    setNotice(
      ids.length > 0
        ? `${numberText(ids.length)} atanabilir etiket seçildi.${availableRows.length > ids.length && !labelProductId ? " Güvenlik için yalnızca ilk ağırlık/ürün grubu seçildi." : ""}`
        : "Görünen listede atanabilir etiket yok."
    );
  }

  async function assignSelectedLabels() {
    if (selectedMode !== "assign" || selectedRows.length === 0 || !targetCustomerId) return;
    const customerLabel = customerSelectionLabel(targetCustomer || {});
    const confirmed = await askConfirmation({
      title: `${numberText(selectedRows.length)} seçili etiket aktarılsın mı?`,
      description: `Etiketler ${customerLabel} hesabına test amacıyla, ücret alınmadan atanacak.`,
      details: [
        "Yalnızca ekranda tek tek seçtiğiniz etiketler aktarılır; sistem farklı bir takip numarası seçmez.",
        "PDF geçerliliği, stok sahipliği ve eş zamanlı kullanım sunucuda tekrar doğrulanır.",
        "Kullanılmayan etiketler DHL kontrolü temizlendikten sonra bu ekrandan tekrar ortak havuza alınabilir.",
      ],
      confirmLabel: "Seçili etiketleri aktar",
      tone: "warning",
    });
    if (!confirmed) return;

    setSelectionSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.manageAdminSelectedLabels({
        action: "assign",
        customerId: targetCustomerId,
        labelSourceIds: selectedRows.map((row) => text(row.id)),
        reason: "Admin etiket envanterinden müşteri hesabına kontrollü test aktarımı",
      });
      setNotice(`${numberText(result.quantity || selectedRows.length)} etiket ${customerLabel} hesabına aktarıldı. Müşteri bakiyesi ve stok anında güncellendi.`);
      setSelectedLabelSourceIds([]);
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Seçili etiketler müşteriye aktarılamadı.");
    } finally {
      setSelectionSaving(false);
    }
  }

  async function returnSelectedLabels() {
    const customerId = selectedCustomerIds[0];
    if (selectedMode !== "return" || selectedRows.length === 0 || !customerId) return;
    const customerRow = customers.find((row) => text(row.id) === customerId);
    const confirmed = await askConfirmation({
      title: `${numberText(selectedRows.length)} etiket ortak havuza alınsın mı?`,
      description: `${customerSelectionLabel(customerRow || selectedRows[0])} hesabındaki seçili test etiketleri geri alınacak.`,
      details: [
        "Gönderide kullanılmış, dışa aktarılmış veya DHL hareketi görülen etiketler geri alınamaz.",
        "Güvenlik nedeniyle etiketlerin DHL kontrol sonucu temiz olmalıdır.",
        "İşlem müşteri bakiyesini otomatik olarak yeniden hesaplar ve denetim kaydı bırakır.",
      ],
      confirmLabel: "Ortak havuza al",
      tone: "warning",
    });
    if (!confirmed) return;

    setSelectionSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.manageAdminSelectedLabels({
        action: "return",
        customerId,
        labelSourceIds: selectedRows.map((row) => text(row.id)),
        reason: "Kontrollü müşteri testi sonrası kullanılmamış etiketleri ortak havuza iade",
      });
      setNotice(`${numberText(result.returnedCount || selectedRows.length)} kullanılmamış etiket güvenle ortak havuza alındı.`);
      setSelectedLabelSourceIds([]);
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Seçili etiketler ortak havuza alınamadı.");
    } finally {
      setSelectionSaving(false);
    }
  }

  async function checkCarrier(labelSourceId: string) {
    setCheckingId(labelSourceId);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.checkLabelCarrier(labelSourceId);
      const labelSource = result.labelSource as { carrierCheckMessage?: unknown; carrierCheckStatus?: unknown };
      setNotice(`${text(labelSource.carrierCheckStatus)}: ${text(labelSource.carrierCheckMessage) || "DHL kontrolü tamamlandı."}`);
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "DHL kontrolü tamamlanamadı.");
    } finally {
      setCheckingId(null);
    }
  }

  async function checkPendingCarriers() {
    const ids = labels
      .filter((row) => text(row.status).toLowerCase() === "available" && text(row.carrierCheckStatus).toLowerCase() === "pending")
      .map((row) => text(row.id))
      .filter(Boolean)
      .slice(0, 100);
    if (ids.length === 0) {
      setNotice("Görünen listede kontrol bekleyen DHL etiketi yok.");
      return;
    }

    setBulkChecking(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.checkLabelCarriers(ids);
      setNotice(`${result.requestedCount} DHL etiketi yeniden kontrol kuyruğuna alındı.`);
      window.setTimeout(() => setRefreshKey((current) => current + 1), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Toplu DHL kontrolü başlatılamadı.");
    } finally {
      setBulkChecking(false);
    }
  }

  async function verifyPdfExpiryBatch() {
    setVerifyingPdfExpiries(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.verifyAdminLabelPdfExpiries({
        labelProductId: labelProductId || undefined,
        limit: 12,
      });
      const checked = numberText(result.checkedCount);
      const expired = numberText(result.expiredCount);
      const remaining = numberText(result.remainingCount);
      setNotice(
        result.errorCount > 0
          ? `${checked} PDF kontrol edildi, ${expired} süresi geçmiş etiket ayrıldı. ${numberText(result.errorCount)} dosya okunamadı; ${remaining} kayıt sırada.`
          : `${checked} PDF kontrol edildi, ${expired} süresi geçmiş etiket ayrıldı. ${remaining} kayıt sırada.`
      );
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "PDF geçerlilik kontrolü tamamlanamadı.");
    } finally {
      setVerifyingPdfExpiries(false);
    }
  }

  async function releasePendingLabels() {
    setReleasingPending(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.releasePendingLabels();
      setNotice(
        result.releasedCount > 0
          ? `${numberText(result.releasedCount)} geçici DHL kontrolünde bekleyen etiket yeniden satışa açıldı. Otomatik kontroller arka planda devam edecek.`
          : "Yayınlanabilecek geçici DHL kontrol kaydı bulunamadı."
      );
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Bekleyen etiketler yayınlanamadı.");
    } finally {
      setReleasingPending(false);
    }
  }

  async function removeFromInventory(row: DataRow) {
    const labelSourceId = text(row.id);
    const trackingNumber = text(row.trackingNumber) || labelSourceId;
    if (!labelSourceId) return;
    const confirmed = await askConfirmation({
      title: `${trackingNumber} etiketi silinsin mi?`,
      description: "Etiket aktif ürün stoğundan çıkarılıp Void durumuna alınacak.",
      details: [
        "Müşteriye atanmış, satın alma veya gönderi bağlantısı bulunan etiketler silinemez.",
        "PDF ve işlem kaydı denetim amacıyla korunur; etiket satışta ve atamada kullanılmaz.",
      ],
      confirmLabel: "Sil",
      tone: "danger",
    });
    if (!confirmed) return;

    setActingId(`remove:${labelSourceId}`);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.removeLabelSourceFromInventory(
        labelSourceId,
        "Admin ürün bazlı etiket yönetim ekranından etiketi sildi."
      );
      setNotice(result.message);
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etiket silinemedi.");
    } finally {
      setActingId(null);
    }
  }

  async function republishLabel(row: DataRow) {
    const labelSourceId = text(row.id);
    const trackingNumber = text(row.trackingNumber) || labelSourceId;
    const currentStatus = text(row.status).toLowerCase();
    if (!labelSourceId || !["consumed", "expired", "void"].includes(currentStatus) || row.reactivationEligible !== true) return;
    const confirmed = await askConfirmation({
      title: `${trackingNumber} yeniden yayınlansın mı?`,
      description: "Etiket güvenlik kuralları tekrar doğrulanarak ortak satış havuzuna alınacak.",
      details: [
        "Müşteri hesabında gönderilmemiş duran atanmış etiketler yeniden yayınlanamaz.",
        "Kullanılmış etiketlerde kullanımın üzerinden en az 3 ay geçmesi ve DHL durumunun açık/hareketsiz olması gerekir.",
        "PDF içindeki doğrulanmış geçerlilik süresi dolmuşsa işlem engellenir.",
      ],
      confirmLabel: "Yeniden yayınla",
      tone: "warning",
    });
    if (!confirmed) return;

    setActingId(`republish:${labelSourceId}`);
    setError(null);
    setNotice(null);
    try {
      const result = currentStatus === "void"
        ? await apiClient.releaseLabelToAvailable(labelSourceId)
        : currentStatus === "consumed"
          ? await apiClient.reactivateConsumedLabels(
              [labelSourceId],
              "Admin ürün bazlı etiket yönetim ekranından 3 aylık açık/hareketsiz etiketi yeniden yayınladı."
            )
          : await apiClient.reactivateAdminLabels(
            [labelSourceId],
            "Admin ürün bazlı etiket yönetim ekranından etiketi yeniden yayınladı."
          );
      setNotice(result.message);
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etiket yeniden yayınlanamadı.");
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title={product ? `${text(product.name)} Etiketleri` : "Etiket Envanteri"}
        caption={product
          ? `${text(product.name)} ürününe ait tüm kaynak etiketleri arayın, ekleyin, silin ve uygun kayıtları yeniden yayınlayın.`
          : "Yüklenen kaynak etiketleri, sahiplik durumunu, taşıyıcı kontrol sonucunu ve indirme bağlantılarını tek listede yönetin."}
        icon={<Tags className="h-5 w-5" />}
        action={
          <div className="flex flex-wrap gap-2">
            {product ? (
              <>
                <Link
                  to="/admin/label-products"
                  className="inline-flex w-fit items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Ürünlere dön
                </Link>
                <Link
                  to={`/admin/label-products/${encodeURIComponent(labelProductId)}`}
                  className="inline-flex w-fit items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  Ürün bilgileri
                </Link>
              </>
            ) : null}
            <button
              type="button"
              onClick={() => void verifyPdfExpiryBatch()}
              disabled={verifyingPdfExpiries}
              className="inline-flex w-fit items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 shadow-sm transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {verifyingPdfExpiries ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clock3 className="h-3.5 w-3.5" />}
              PDF sürelerini kontrol et
            </button>
            <button
              type="button"
              onClick={() => void releasePendingLabels()}
              disabled={releasingPending || pendingAvailableCount === 0}
              className="inline-flex w-fit items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 shadow-sm transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {releasingPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Bekleyenleri yayınla{pendingAvailableCount > 0 ? ` (${numberText(pendingAvailableCount)})` : ""}
            </button>
            <Link
              to="/admin/label-checks"
              className="inline-flex w-fit items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Etiket Kontrolleri
            </Link>
            <Link
              to={labelProductId ? `/admin/label-sources/upload?labelProductId=${encodeURIComponent(labelProductId)}` : "/admin/label-sources/upload"}
              className="inline-flex w-fit items-center justify-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              <Upload className="h-3.5 w-3.5" />
              Etiket Kaynağı Yükle
            </Link>
          </div>
        }
      />
      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}
      {loading ? (
        <LoadingPanel label="Etiket envanteri yükleniyor" />
      ) : (
        <>
          {product && rowNumber(stats, "expiredCount") > 0 ? (
            <section className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 shadow-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-semibold">{numberText(rowNumber(stats, "expiredCount"))} etiket süresi bitmiş durumda</p>
                <p className="mt-1 leading-6">Bu etiketler satışa ve atamaya kapalıdır. PDF içindeki süre dolmuşsa geçerli yeni PDF yüklenmeden yeniden yayınlanamaz.</p>
              </div>
            </section>
          ) : null}
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <AdminStatCard label="Toplam" value={rowNumber(stats, "totalCount")} tone="slate" icon={<Tags className="h-5 w-5" />} />
            <AdminStatCard label="Available" value={rowNumber(stats, "availableCount")} tone="cyan" icon={<Package className="h-5 w-5" />} />
            <AdminStatCard label="Atanabilir" value={rowNumber(stats, "allocatableCount")} tone="emerald" icon={<CheckCircle2 className="h-5 w-5" />} />
            <AdminStatCard label="Atanmış" value={rowNumber(stats, "assignedCount")} tone="violet" icon={<Users className="h-5 w-5" />} />
            <AdminStatCard label="Tüketilmiş" value={rowNumber(stats, "consumedCount")} tone="amber" icon={<Clock3 className="h-5 w-5" />} />
            <AdminStatCard label="Void" value={rowNumber(stats, "voidCount")} tone="red" icon={<History className="h-5 w-5" />} />
            <AdminStatCard label="Hatalı Etiket" value={rowNumber(stats, "checkErrorCount")} tone="red" icon={<AlertTriangle className="h-5 w-5" />} />
            <AdminStatCard label="Süresi bitmiş" value={rowNumber(stats, "expiredCount")} tone="amber" icon={<Clock3 className="h-5 w-5" />} />
          </section>

          <AdminPanel
            title="Kaynak etiketleri"
            caption={`${sourceCaption} Status ve arama filtreleri doğrudan envanter veritabanında uygulanır.`}
            count={filteredCount}
            action={
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => void checkPendingCarriers()}
                  disabled={bulkChecking || pendingAvailableCount === 0}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {bulkChecking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                  Bekleyenleri kontrol et
                </button>
                <button
                  type="button"
                  onClick={() => setRefreshKey((current) => current + 1)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Yenile
                </button>
              </div>
            }
          >
            <div className="grid gap-3 border-b border-slate-100 bg-slate-50/70 p-4 lg:grid-cols-[1fr_220px_220px]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={product ? `${text(product.name)} içinde takip, dosya veya müşteri ara...` : "Takip, dosya, müşteri veya ürün ara..."}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-950 outline-none transition focus:border-slate-400"
                />
              </label>
              <select
                value={status}
                onChange={(event) => updateStatus(event.target.value)}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-slate-400"
              >
                {labelInventoryStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                value={sort}
                onChange={(event) => updateSort(event.target.value)}
                aria-label="Sıralama"
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-slate-400"
              >
                {labelInventorySortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="border-b border-slate-100 bg-white p-4">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-2">
                    <Square className="h-4 w-4 text-slate-500" />
                    <p className="text-sm font-semibold text-slate-950">Kontrollü test aktarımı</p>
                  </div>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Takip numarası aramasını kullanın, test edeceğiniz etiketleri soldaki kutulardan seçin ve yalnızca bu kayıtları müşteri havuzuna aktarın.
                    En fazla 100 etiket tek işlemde işlenir.
                  </p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <button
                    type="button"
                    onClick={selectVisibleAvailableLabels}
                    disabled={filteredLabels.every((row) => text(row.status).toLowerCase() !== "available")}
                    className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Görünen atanabilirleri seç
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedLabelSourceIds([])}
                    disabled={selectedRows.length === 0}
                    className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Seçimi temizle
                  </button>
                  {selectedMode === "return" ? (
                    <button
                      type="button"
                      onClick={() => void returnSelectedLabels()}
                      disabled={selectionSaving || selectedRows.length === 0}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 text-xs font-semibold text-amber-900 shadow-sm transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {selectionSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                      {numberText(selectedRows.length)} etiketi havuza al
                    </button>
                  ) : (
                    <>
                      <label className="block min-w-[290px]">
                        <span className="sr-only">Aktarılacak müşteri</span>
                        <select
                          value={targetCustomerId}
                          onChange={(event) => setTargetCustomerId(event.target.value)}
                          disabled={customersLoading}
                          className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 outline-none transition focus:border-slate-400 disabled:opacity-60"
                        >
                          <option value="">{customersLoading ? "Müşteriler yükleniyor..." : "Aktarılacak müşteriyi seçin"}</option>
                          {[...customers]
                            .sort((left, right) => customerSelectionLabel(left).localeCompare(customerSelectionLabel(right), "tr"))
                            .map((row) => (
                              <option key={text(row.id)} value={text(row.id)}>{customerSelectionLabel(row)}</option>
                            ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        onClick={() => void assignSelectedLabels()}
                        disabled={selectionSaving || selectedMode !== "assign" || selectedRows.length === 0 || !targetCustomerId}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-950 px-3 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {selectionSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        {numberText(selectedRows.length)} etiketi aktar
                      </button>
                    </>
                  )}
                </div>
              </div>
              {selectedMode === "mixed" && selectedRows.length > 0 ? (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
                  Bu seçim tek işlemde kullanılamaz. Yalnızca atanabilir etiketleri veya aynı müşteriye atanmış etiketleri seçin.
                </p>
              ) : null}
            </div>
            <DataTable
              rows={filteredLabels}
              empty="Bu filtreye uygun etiket yok."
              rowClassName={(row) => selectedLabelSourceIdSet.has(text(row.id)) ? "bg-cyan-50/70" : ""}
              columns={[
                {
                  label: "Seçim",
                  value: (row) => {
                    const id = text(row.id);
                    const rowStatus = text(row.status).toLowerCase();
                    const selectable = Boolean(id) && ["available", "assigned"].includes(rowStatus);
                    return (
                      <input
                        type="checkbox"
                        aria-label={`${text(row.trackingNumber) || id} etiketini seç`}
                        checked={selectedLabelSourceIdSet.has(id)}
                        disabled={!selectable || selectionSaving}
                        onChange={() => toggleLabelSelection(row)}
                        className="h-4 w-4 rounded border-slate-300 text-slate-950 focus:ring-slate-400 disabled:cursor-not-allowed disabled:opacity-40"
                      />
                    );
                  },
                },
                {
                  label: "Takip",
                  value: (row) => <NameStack title={text(row.trackingNumber)} detail={text(row.originalFileName) || text(row.savedFileName)} />,
                },
                { label: "Ürün", value: (row) => <NameStack title={text(row.labelProductName)} detail={`${text(row.labelProductCarrier)} ${text(row.weightText)}`.trim()} /> },
                { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                { label: "Müşteri", value: (row) => text(row.assignedToCompanyName) || text(row.assignedToEmail) || "-" },
                {
                  label: "Kontrol",
                  value: (row) => {
                    const carrierHint = `${text(row.labelProductCarrier)} ${text(row.labelProductName)}`.toLowerCase();
                    const labelSourceId = text(row.id);
                    return (
                      <div className="min-w-[150px] space-y-2">
                        <NameStack title={text(row.carrierCheckStatus)} detail={text(row.carrierCheckMessage) || dateText(row.carrierCheckCheckedAt)} />
                        {carrierHint.includes("dhl") && labelSourceId ? (
                          <button
                            type="button"
                            onClick={() => void checkCarrier(labelSourceId)}
                            disabled={checkingId === labelSourceId}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {checkingId === labelSourceId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                            DHL kontrol
                          </button>
                        ) : null}
                      </div>
                    );
                  },
                },
                { label: "Carrier", value: (row) => <NameStack title={text(row.carrierDeliveryLabel) || text(row.carrierDeliveryStatus) || "-"} detail={dateText(row.carrierDeliveryLastEventAt)} /> },
                { label: "Atama", value: (row) => dateText(row.assignedAt) },
                { label: "Tüketim", value: (row) => dateText(row.consumedAt || row.externalUsedAt) },
                { label: "PDF oluşturma", value: labelInventoryPdfCreationCell },
                { label: "Yükleme", value: (row) => dateTextWithYear(row.createdAt) },
                { label: "Geçerlilik", value: labelInventoryValidityCell },
                {
                  label: "PDF",
                  value: (row) => {
                    if (text(row.status).toLowerCase() === "expired") {
                      return <span className="inline-flex rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700">PDF engelli</span>;
                    }
                    return text(row.downloadUrl) ? (
                      <a
                        href={text(row.downloadUrl)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                      >
                        <Download className="h-3.5 w-3.5" />
                        PDF
                      </a>
                    ) : (
                      "-"
                    );
                  },
                },
                {
                  label: "İşlemler",
                  value: (row) => {
                    const id = text(row.id);
                    const rowStatus = text(row.status).toLowerCase();
                    const canRemove = ["available", "expired"].includes(rowStatus)
                      && !text(row.assignedToUserId)
                      && !text(row.labelPurchaseId)
                      && !text(row.consumedAt)
                      && !text(row.consumedByShipmentId)
                      && !text(row.externalUsedAt)
                      && !text(row.reservedAt);
                    const canRepublish = ["consumed", "void", "expired"].includes(rowStatus) && row.reactivationEligible === true;
                    const republishReason = text(row.reactivationBlockedReason);
                    return (
                      <div className="flex min-w-[180px] flex-wrap gap-2">
                        {["consumed", "void", "expired"].includes(rowStatus) ? (
                          <button
                            type="button"
                            disabled={!canRepublish || actingId === `republish:${id}`}
                            title={republishReason || "Etiketi yeniden satış havuzuna al"}
                            onClick={() => void republishLabel(row)}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-800 shadow-sm transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {actingId === `republish:${id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                            Yeniden yayınla
                          </button>
                        ) : null}
                        {["available", "expired"].includes(rowStatus) ? (
                          <button
                            type="button"
                            disabled={!canRemove || actingId === `remove:${id}`}
                            title={canRemove ? "Etiketi ürün stoğundan sil" : "Bağlantılı veya kullanılmış etiket silinemez"}
                            onClick={() => void removeFromInventory(row)}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 shadow-sm transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {actingId === `remove:${id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            Sil
                          </button>
                        ) : null}
                      </div>
                    );
                  },
                },
              ]}
            />
          </AdminPanel>
        </>
      )}
      {confirmationDialog}
    </div>
  );
}

function rowNumber(row: DataRow | null | undefined, key: string) {
  return Number(row?.[key] || 0);
}

function platformSyncRate(row: DataRow | null | undefined) {
  const enabled = rowNumber(row, "syncEnabledCount");
  if (enabled <= 0) return 0;
  return Math.round((rowNumber(row, "syncSuccessCount") / enabled) * 100);
}

function platformHealth(row: DataRow | null | undefined) {
  if (!row || rowNumber(row, "storeCount") === 0) return { label: "Mağaza yok", tone: "slate" as const };
  if (rowNumber(row, "syncFailedCount") > 0 || rowNumber(row, "trackingFailedCount") > 0) return { label: "Kontrol gerekli", tone: "red" as const };
  if (rowNumber(row, "dueStoreCount") > 0) return { label: "Senkron sırası geldi", tone: "amber" as const };
  if (rowNumber(row, "staleStoreCount") > 0) return { label: "Senkron bekliyor", tone: "amber" as const };
  if (rowNumber(row, "credentialCount") < rowNumber(row, "activeStoreCount")) return { label: "Kimlik eksik", tone: "amber" as const };
  return { label: "Sağlıklı", tone: "emerald" as const };
}

function usePlatformInsights() {
  const [data, setData] = useState<PlatformInsightsPayload | null>(() => readCachedPlatformInsights());
  const [loading, setLoading] = useState(() => !readCachedPlatformInsights());
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.resource<PlatformInsightsPayload>("/api/admin/platforms");
      if (isPlatformInsightsPayload(result)) {
        setData(result);
        cachePlatformInsights(result);
      } else {
        throw new Error("Platform verisi beklenen biçimde değil.");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Platform verileri alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return { data, error, loading, refresh: load };
}

function PlatformInsightsSkeleton() {
  return (
    <div className="space-y-5">
      <AdminPageHeader title="Platform Analitiği" caption="Bağlı satış kanalları ve senkron verileri yükleniyor." icon={<Store className="h-5 w-5" />} />
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="h-3 w-24 animate-pulse rounded bg-slate-200" />
            <div className="mt-4 h-8 w-20 animate-pulse rounded bg-slate-100" />
          </div>
        ))}
      </section>
      <section className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="h-10 w-28 animate-pulse rounded bg-slate-100" />
            <div className="mt-5 h-24 animate-pulse rounded-xl bg-slate-100" />
          </div>
        ))}
      </section>
    </div>
  );
}

function PlatformCard({ row }: { row: DataRow }) {
  const key = text(row.platform);
  const health = platformHealth(row);
  const syncRate = platformSyncRate(row);
  const logoSrc = platformLogoSrc(key);

  return (
    <Link to={`/admin/platforms/${encodeURIComponent(key)}`} className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
            {logoSrc ? <img src={logoSrc} alt={platformLabel(key)} className="max-h-7 max-w-9 object-contain" /> : <Store className="h-5 w-5 text-slate-500" />}
          </div>
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-slate-950">{platformLabel(key)}</h2>
            <p className="mt-1 text-sm text-slate-500">{numberText(row.storeCount)} mağaza · {numberText(row.orderCount || row.cachedOrderCount)} sipariş</p>
          </div>
        </div>
        <span
          className={cx(
            "shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
            health.tone === "emerald" && "bg-emerald-50 text-emerald-700 ring-emerald-100",
            health.tone === "amber" && "bg-amber-50 text-amber-700 ring-amber-100",
            health.tone === "red" && "bg-red-50 text-red-700 ring-red-100",
            health.tone === "slate" && "bg-slate-100 text-slate-600 ring-slate-200"
          )}
        >
          {health.label}
        </span>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-2 text-sm">
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Aktif</p>
          <p className="mt-2 text-xl font-semibold text-slate-950">{numberText(row.activeStoreCount)}</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Etiket</p>
          <p className="mt-2 text-xl font-semibold text-slate-950">{numberText(row.shipmentCount)}</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Sync</p>
          <p className="mt-2 text-xl font-semibold text-slate-950">{percentText(syncRate)}</p>
        </div>
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
          <span>Son senkron</span>
          <span className="truncate">{dateText(row.lastSyncedAt)}</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
          <div className={cx("h-full rounded-full", health.tone === "red" ? "bg-red-400" : health.tone === "amber" ? "bg-amber-400" : "bg-emerald-400")} style={{ width: `${Math.min(Math.max(syncRate, 0), 100)}%` }} />
        </div>
      </div>
    </Link>
  );
}

function PlatformStoresTable({
  onSync,
  rows,
  syncingStoreId,
}: {
  onSync?: (row: DataRow) => void;
  rows: DataRow[];
  syncingStoreId?: string | null;
}) {
  const columns: DataColumn[] = [
    {
      label: "Mağaza",
      value: (row) => (
        <NameStack title={text(row.name)} detail={`${platformLabel(text(row.platform))}${text(row.externalStoreId) ? ` · ${text(row.externalStoreId)}` : ""}`} />
      ),
    },
    {
      label: "Müşteri",
      value: (row) =>
        text(row.customerId) ? (
          <Link className="font-semibold text-slate-700 hover:text-slate-950 hover:underline" to={`/admin/customers/${encodeURIComponent(text(row.customerId))}`}>
            {text(row.customerName) || text(row.customerEmail)}
          </Link>
        ) : (
          text(row.customerName) || "-"
        ),
    },
    { label: "Sipariş", value: (row) => numberText(row.orderCount || row.cachedOrderCount), align: "right" },
    { label: "Gönderi", value: (row) => numberText(row.shipmentCount), align: "right" },
    { label: "Kimlik", value: (row) => statusBadge(row.credentialConfigured ? "configured" : "missing") },
    {
      label: "Sync",
      value: (row) => (
        <div className="flex flex-col items-start gap-1">
          {statusBadge(text(row.orderSyncLastStatus) || (row.orderSyncEnabled ? "pending" : "disabled"))}
          {row.orderSyncDue ? <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-100">Sırası geldi</span> : null}
        </div>
      ),
    },
    { label: "Son senkron", value: (row) => dateText(row.ordersSyncedAt || row.orderSyncLastSuccessAt) },
  ];

  if (onSync) {
    columns.push({
      label: "İşlem",
      value: (row) => {
        const storeId = text(row.id);
        const busy = syncingStoreId === storeId;
        const disabled = busy || row.credentialConfigured === false || row.active === false;
        return (
          <button
            type="button"
            onClick={() => onSync(row)}
            disabled={disabled}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clock3 className="h-3.5 w-3.5" />}
            Senkronize et
          </button>
        );
      },
    });
  }

  return (
    <DataTable
      rows={rows}
      empty="Bağlı mağaza yok."
      columns={columns}
    />
  );
}

function AdminPlatformsPage() {
  const { data, error, loading, refresh } = usePlatformInsights();
  if (loading && !data) return <PlatformInsightsSkeleton />;

  const platforms = data?.platforms || [];
  const stores = data?.stores || [];
  const totalOrders = platforms.reduce((sum, row) => sum + rowNumber(row, "orderCount") + (rowNumber(row, "orderCount") ? 0 : rowNumber(row, "cachedOrderCount")), 0);
  const totalShipments = platforms.reduce((sum, row) => sum + rowNumber(row, "shipmentCount"), 0);
  const syncEnabled = platforms.reduce((sum, row) => sum + rowNumber(row, "syncEnabledCount"), 0);
  const syncSuccess = platforms.reduce((sum, row) => sum + rowNumber(row, "syncSuccessCount"), 0);
  const syncRate = syncEnabled > 0 ? Math.round((syncSuccess / syncEnabled) * 100) : 0;
  const issueCount = platforms.reduce((sum, row) => sum + rowNumber(row, "syncFailedCount") + rowNumber(row, "trackingFailedCount") + rowNumber(row, "staleStoreCount") + rowNumber(row, "dueStoreCount"), 0);

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Platform Analitiği"
        caption="Satış kanallarını, mağaza bağlantılarını, sipariş hacmini ve senkron sağlığını tek ekrandan yönetin."
        icon={<Store className="h-5 w-5" />}
        action={
          <button
            type="button"
            onClick={() => void refresh()}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock3 className="h-4 w-4" />}
            Yenile
          </button>
        }
      />
      {error ? <ErrorBanner message={error} /> : null}
      {loading && data ? (
        <div className="inline-flex items-center gap-2 rounded-full border border-cyan-100 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-800">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Platform verileri arka planda yenileniyor
        </div>
      ) : null}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Platform" value={platforms.length} tone="cyan" icon={<Store className="h-5 w-5" />} />
        <AdminStatCard label="Bağlı mağaza" value={stores.length} tone="emerald" icon={<Building2 className="h-5 w-5" />} />
        <AdminStatCard label="Sipariş izi" value={totalOrders} tone="violet" icon={<ShoppingCart className="h-5 w-5" />} />
        <AdminStatCard label="Sync sağlığı" value={percentText(syncRate)} tone={issueCount > 0 ? "amber" : "emerald"} icon={<ShieldCheck className="h-5 w-5" />} />
      </section>

      <section className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {platforms.map((row) => <PlatformCard key={text(row.platform)} row={row} />)}
      </section>

      <AdminPanel title="Bağlı mağazalar" caption="Platform, müşteri, sipariş ve senkron durumunu birlikte gösterir." count={stores.length}>
        <PlatformStoresTable rows={stores} />
      </AdminPanel>

      <section className="grid gap-5 lg:grid-cols-2">
        <AdminPanel title="eBay bağlantıları" count={(data?.ebayConnections || []).length}>
          <DataTable
            rows={data?.ebayConnections || []}
            empty="eBay bağlantısı yok."
            columns={[
              { label: "Durum", value: (row) => statusBadge(text(row.status)) },
              { label: "Adet", value: (row) => numberText(row.count), align: "right" },
            ]}
          />
        </AdminPanel>
        <AdminPanel title="Amazon bağlantıları" count={(data?.amazonConnections || []).length}>
          <DataTable
            rows={data?.amazonConnections || []}
            empty="Amazon bağlantısı yok."
            columns={[
              { label: "Bölge", value: (row) => text(row.region) || "-" },
              { label: "Adet", value: (row) => numberText(row.count), align: "right" },
              { label: "Son senkron", value: (row) => dateText(row.lastSyncAt) },
            ]}
          />
        </AdminPanel>
      </section>
    </div>
  );
}

function AdminPlatformDetailPage() {
  const { platform } = useParams();
  const { data, error: loadError, loading, refresh } = usePlatformInsights();
  const key = (platform || "manual").toLowerCase();
  const canRunInternalSync = internalOrderSyncPlatforms.includes(key as OrderSyncPlatform);
  const [syncingStoreId, setSyncingStoreId] = useState<string | null>(null);
  const [runningDue, setRunningDue] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  if (loading && !data) return <PlatformInsightsSkeleton />;

  const allPlatforms = data?.platforms || [];
  const allStores = data?.stores || [];
  const platformRow = allPlatforms.find((row) => text(row.platform).toLowerCase() === key) || null;
  const stores = allStores.filter((row) => text(row.platform).toLowerCase() === key);
  const health = platformHealth(platformRow);
  const syncRate = platformSyncRate(platformRow);
  const logoSrc = platformLogoSrc(key);

  async function syncStore(row: DataRow) {
    const storeId = text(row.id);
    if (!storeId || !canRunInternalSync) return;
    setSyncingStoreId(storeId);
    setActionError(null);
    setNotice(null);
    try {
      const result = await apiClient.adminSyncStoreOrders(storeId, key);
      setNotice(result.message || `${numberText(result.fetched)} sipariş senkronlandı. Yeni: ${numberText(result.inserted)}, güncellenen: ${numberText(result.updated)}.`);
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Mağaza senkronu başlatılamadı.");
    } finally {
      setSyncingStoreId(null);
    }
  }

  async function runDueForPlatform() {
    if (!canRunInternalSync) return;
    setRunningDue(true);
    setActionError(null);
    setNotice(null);
    try {
      const result = await apiClient.runDueOrderSync(5, key);
      setNotice(`${numberText(result.processed)} mağaza kontrol edildi. Başarılı: ${numberText(result.succeeded)}, hata: ${numberText(result.failed)}.`);
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Vadesi gelen senkronlar başlatılamadı.");
    } finally {
      setRunningDue(false);
    }
  }

  return (
    <div className="space-y-5">
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="bg-[#07142c] p-5 text-white sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <Link className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition hover:text-white" to="/admin/platforms">
                <ArrowLeft className="h-4 w-4" />
                Platformlara dön
              </Link>
              <div className="flex min-w-0 items-center gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white p-3">
                  {logoSrc ? <img src={logoSrc} alt={platformLabel(key)} className="max-h-10 max-w-full object-contain" /> : <Store className="h-6 w-6 text-slate-700" />}
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-cyan-100 ring-1 ring-white/10">{health.label}</span>
                    <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-slate-200 ring-1 ring-white/10">{percentText(syncRate)} sync</span>
                  </div>
                  <h1 className="mt-3 text-3xl font-semibold tracking-tight">{platformLabel(key)}</h1>
                  <p className="mt-2 text-sm text-slate-300">{numberText(rowNumber(platformRow, "storeCount"))} mağaza, {numberText(rowNumber(platformRow, "orderCount") || rowNumber(platformRow, "cachedOrderCount"))} sipariş izi ve {numberText(rowNumber(platformRow, "shipmentCount"))} gönderi.</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {canRunInternalSync ? (
                <button
                  type="button"
                  onClick={() => void runDueForPlatform()}
                  disabled={runningDue}
                  className="inline-flex w-fit items-center justify-center gap-2 rounded-xl bg-cyan-200 px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-sm transition hover:bg-cyan-100 disabled:opacity-60"
                >
                  {runningDue ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock3 className="h-4 w-4" />}
                  Sırası gelenleri çalıştır
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void refresh()}
                className="inline-flex w-fit items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-sm transition hover:bg-slate-100"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock3 className="h-4 w-4" />}
                Yenile
              </button>
            </div>
          </div>
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Son senkron</p>
              <p className="mt-2 text-lg font-semibold">{dateText(platformRow?.lastSyncedAt)}</p>
              <p className="mt-1 text-sm text-slate-300">Sıradaki: {dateText(platformRow?.nextSyncAt)}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Takip yazımı</p>
              <p className="mt-2 text-lg font-semibold">{numberText(platformRow?.trackingSuccessCount)} başarılı</p>
              <p className="mt-1 text-sm text-slate-300">{numberText(platformRow?.trackingFailedCount)} hata sinyali</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Kimlik bilgisi</p>
              <p className="mt-2 text-lg font-semibold">{numberText(platformRow?.credentialCount)} bağlı</p>
              <p className="mt-1 text-sm text-slate-300">{numberText(platformRow?.activeStoreCount)} aktif mağaza</p>
            </div>
          </div>
        </div>
      </section>

      {notice ? <AdminNotice message={notice} /> : null}
      {loadError ? <ErrorBanner message={loadError} /> : null}
      {actionError ? <ErrorBanner message={actionError} /> : null}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Mağaza" value={rowNumber(platformRow, "storeCount")} tone="cyan" icon={<Store className="h-5 w-5" />} />
        <AdminStatCard label="Aktif" value={rowNumber(platformRow, "activeStoreCount")} tone="emerald" icon={<CheckCircle2 className="h-5 w-5" />} />
        <AdminStatCard label="Sipariş" value={rowNumber(platformRow, "orderCount") || rowNumber(platformRow, "cachedOrderCount")} tone="violet" icon={<ShoppingCart className="h-5 w-5" />} />
        <AdminStatCard label="Sırası gelen" value={rowNumber(platformRow, "dueStoreCount")} tone={rowNumber(platformRow, "dueStoreCount") > 0 ? "amber" : "emerald"} icon={<Clock3 className="h-5 w-5" />} />
      </section>

      <AdminPanel title={`${platformLabel(key)} mağazaları`} caption="Eski sistemdeki platform detayı gibi mağaza, müşteri, sipariş ve sync sinyallerini aynı tabloda gösterir." count={stores.length}>
        <PlatformStoresTable rows={stores} onSync={canRunInternalSync ? syncStore : undefined} syncingStoreId={syncingStoreId} />
      </AdminPanel>
    </div>
  );
}

function AdminSettingsPage() {
  const items = [
    { title: "Profil", detail: "Admin profil ve şifre ayarları.", to: "/admin/settings/profile", icon: <User className="h-5 w-5" /> },
    { title: "Fatura ayarları", detail: "KDV, numara formatı ve kredi faturası kuralları.", to: "/admin/settings/invoices", icon: <FileText className="h-5 w-5" /> },
    { title: "Destek merkezi", detail: "Müşteri teknik talepleri ve günlük rapor.", to: "/admin/support", icon: <MessageCircle className="h-5 w-5" /> },
    { title: "Mağaza kontrol raporu", detail: "Otomatik sipariş alma hataları ve sıradaki kontroller.", to: "/admin/reports/order-sync", icon: <Clock3 className="h-5 w-5" /> },
    { title: "Eğitim videoları", detail: "Kullanıcı eğitim ve Almanca etiket indirme içerikleri.", to: "/admin/training", icon: <FileText className="h-5 w-5" /> },
    { title: "Hata kayıtları", detail: "Müşteri ve sistem hata referansları.", to: "/admin/settings/error-logs", icon: <AlertTriangle className="h-5 w-5" /> },
    { title: "Takip API", detail: "Sipariş senkronu ve platform servis durumu.", to: "/admin/settings/tracking-api", icon: <Truck className="h-5 w-5" /> },
    { title: "Versiyonlar", detail: "Cloudflare sürüm ve audit özeti.", to: "/admin/settings/versions", icon: <ShieldCheck className="h-5 w-5" /> },
  ];

  return (
    <div className="space-y-5">
      <AdminPageHeader title="Ayarlar" caption="Operasyon, hata ve entegrasyon ayarları." icon={<SlidersHorizontal className="h-5 w-5" />} />
      <section className="grid gap-4 md:grid-cols-2">
        {items.map((item) => (
          <Link key={item.to} to={item.to} className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 ring-1 ring-slate-200 transition group-hover:bg-slate-950 group-hover:text-cyan-200">{item.icon}</div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-slate-950">{item.title}</h2>
                <p className="mt-1 text-sm text-slate-500">{item.detail}</p>
              </div>
              <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-slate-700" />
            </div>
          </Link>
        ))}
      </section>
    </div>
  );
}

function AdminInvoiceSettingsPage() {
  const deferredItems = [
    "Eski veya yanlış faturaların toplu otomatik düzeltilmesi ayrıca ele alınacak.",
    "Shopify ödeme öncesi KDV davranışı Shopify checkout tarafıyla birlikte incelenecek.",
    "PayPal, Klarna, banka havalesi, SEPA ve eğitim dokümanları ödeme sağlayıcı kararından sonra bağlanacak.",
  ];

  return (
    <div className="space-y-5">
      <AdminPageHeader title="Fatura ayarları" caption="KenPost fatura numaraları, KDV oranı ve kredi faturası kuralları." icon={<FileText className="h-5 w-5" />} />
      <section className="grid gap-4 lg:grid-cols-3">
        <InfoLine label="Sipariş faturası" value="RE-{yıl}-{sıra}" />
        <InfoLine label="Shopify kredi faturası" value="MAK{yıl}-{Shopify sıra}" />
        <InfoLine label="Standart KDV" value="%19" />
      </section>
      <AdminPanel title="Aktif kurallar" caption="Bu değerler mevcut fatura PDF ve admin manuel fatura akışlarında kullanılır.">
        <div className="grid gap-3 p-5 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-950">Etiket kredi sepeti</p>
            <p className="mt-1 text-sm leading-6 text-slate-600">Admin, müşteriye birden fazla etiket ürününü tek işlemde tanımlar; bu işlem otomatik fatura oluşturmaz, notlar kredi hareketinde saklanır.</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-950">Bonus/telafi etiketi</p>
            <p className="mt-1 text-sm leading-6 text-slate-600">Bonus işaretlenen satırlar 0 EUR tutulur ve otomatik fiyat tamiri bu kayıtları ücretlendirmez.</p>
          </div>
        </div>
      </AdminPanel>
      <AdminPanel title="Sonra bakılacak notlar">
        <div className="space-y-2 p-5">
          {deferredItems.map((item) => (
            <div key={item} className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </AdminPanel>
    </div>
  );
}

function AdminAppErrorLogsPage() {
  return (
    <ResourcePage
      title="Hata kayıtları"
      caption="Uygulama içinde oluşan müşteri ve admin hata referansları."
      endpoint="/api/admin/app-error-logs?limit=500"
      sections={[
        {
          key: "logs",
          title: "Son kayıtlar",
          columns: [
            { label: "Referans", value: (row) => <NameStack title={text(row.referenceId)} detail={text(row.errorCode)} /> },
            { label: "Alan", value: (row) => <NameStack title={text(row.area)} detail={text(row.scope)} /> },
            { label: "Mesaj", value: (row) => text(row.message) },
            { label: "Sayfa", value: (row) => text(row.pagePath) },
            { label: "Tarih", value: (row) => dateText(row.createdAt) },
          ],
        },
      ]}
    />
  );
}

function activityActorTitle(row: DataRow) {
  const actor = row.actor && typeof row.actor === "object" ? (row.actor as Record<string, unknown>) : null;
  const owner = row.owner && typeof row.owner === "object" ? (row.owner as Record<string, unknown>) : null;
  const actorTitle = text(actor?.companyName) || text(actor?.email) || "-";
  const ownerTitle = text(owner?.companyName) || text(owner?.email);
  return <NameStack title={actorTitle} detail={ownerTitle && ownerTitle !== actorTitle ? `Hesap: ${ownerTitle}` : text(row.scope)} />;
}

function activityMetadataSummary(value: unknown) {
  if (!value || typeof value !== "object") return "-";
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, item]) => item !== null && item !== undefined && item !== "")
    .slice(0, 4)
    .map(([key, item]) => {
      const normalized = Array.isArray(item) ? `${item.length} kayıt` : typeof item === "object" ? JSON.stringify(item) : String(item);
      return `${key}: ${normalized}`.slice(0, 120);
    });
  return entries.length ? entries.join(" · ") : "-";
}

function AdminActivityLogsPage() {
  return (
    <ResourcePage
      title="İşlem logları"
      caption="Müşteri ve admin panelinde sayfa ziyaretleri, etiket oluşturma ve iptal gibi önemli işlemler."
      endpoint="/api/admin/activity-logs?limit=500"
      sections={[
        {
          key: "logs",
          title: "Son işlemler",
          empty: "Henüz işlem kaydı yok.",
          columns: [
            { label: "Kullanıcı", value: activityActorTitle },
            { label: "Aksiyon", value: (row) => <NameStack title={text(row.action)} detail={text(row.requestId)} /> },
            { label: "Kayıt", value: (row) => <NameStack title={text(row.entityLabel) || text(row.entityId) || "-"} detail={text(row.entityType)} /> },
            { label: "Sayfa", value: (row) => text(row.pagePath) || "-" },
            { label: "Detay", value: (row) => activityMetadataSummary(row.metadata) },
            { label: "Tarih", value: (row) => dateText(row.createdAt) },
          ],
        },
      ]}
    />
  );
}

function AdminAffiliatesPage() {
  function dateTimeInputValue(value: string | Date = new Date()) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  }

  function oneYearAfterInput(value: string | Date = new Date()) {
    const date = value instanceof Date ? new Date(value) : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    date.setFullYear(date.getFullYear() + 1);
    return dateTimeInputValue(date);
  }

  const [customers, setCustomers] = useState<DataRow[]>([]);
  const [affiliateUserId, setAffiliateUserId] = useState("");
  const [affiliateSearch, setAffiliateSearch] = useState("");
  const [referredUserIds, setReferredUserIds] = useState<string[]>([]);
  const [referredSearch, setReferredSearch] = useState("");
  const [commissionType, setCommissionType] = useState<"fixed" | "rate">("rate");
  const [commissionRate, setCommissionRate] = useState("5");
  const [usageCommissionAmount, setUsageCommissionAmount] = useState("0.07");
  const [effectiveFrom, setEffectiveFrom] = useState(() => dateTimeInputValue());
  const [effectiveUntil, setEffectiveUntil] = useState(() => oneYearAfterInput());
  const [backfill, setBackfill] = useState(false);
  const [historyReferral, setHistoryReferral] = useState<DataRow | null>(null);
  const [endingReferral, setEndingReferral] = useState<DataRow | null>(null);
  const [endReason, setEndReason] = useState("");
  const [voidUnpaid, setVoidUnpaid] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    setCustomersLoading(true);
    apiClient
      .resource<{ customers?: DataRow[] }>("/api/admin/customers?limit=250")
      .then((result) => {
        if (active) setCustomers(Array.isArray(result.customers) ? result.customers : []);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Müşteri hesapları alınamadı.");
      })
      .finally(() => {
        if (active) setCustomersLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  function customerOptionLabel(row: DataRow) {
    const company = text(row.companyName);
    const email = text(row.email);
    const name = [text(row.firstName), text(row.lastName)].filter(Boolean).join(" ");
    const customerId = text(row.id);
    return [
      customerId ? `#${customerId}` : "",
      company || name || email || customerId,
      email && email !== company ? email : "",
    ].filter(Boolean).join(" · ");
  }

  async function assignReferral(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setNotice(null);
    setError(null);
    try {
      const amount = Number(usageCommissionAmount.replace(",", "."));
      const rate = Number(commissionRate.replace(",", "."));
      if (!affiliateUserId || referredUserIds.length === 0) throw new Error("Affiliate ve en az bir yönlendirilen müşteri seçimi gerekli.");
      const parsedEffectiveFrom = new Date(effectiveFrom);
      const parsedEffectiveUntil = new Date(effectiveUntil);
      if (!effectiveFrom || Number.isNaN(parsedEffectiveFrom.getTime())) throw new Error("Komisyon başlangıç tarihi geçerli olmalı.");
      if (!effectiveUntil || Number.isNaN(parsedEffectiveUntil.getTime()) || parsedEffectiveUntil <= parsedEffectiveFrom) throw new Error("Komisyon bitiş tarihi başlangıçtan sonra olmalı.");
      if (commissionType === "fixed" && (!Number.isFinite(amount) || amount < 0)) throw new Error("Etiket başı komisyon geçerli bir EUR tutarı olmalı.");
      if (commissionType === "rate" && (!Number.isFinite(rate) || rate < 0 || rate > 100)) throw new Error("Komisyon oranı %0 ile %100 arasında olmalı.");
      const result = await apiClient.assignAdminAffiliateReferral({
        affiliateUserId,
        backfill,
        commissionType,
        commissionRate: rate / 100,
        effectiveFrom: parsedEffectiveFrom.toISOString(),
        effectiveUntil: parsedEffectiveUntil.toISOString(),
        referredUserIds,
        usageCommissionAmount: commissionType === "fixed" ? amount : undefined,
      });
      const affiliate = customers.find((row) => text(row.id) === affiliateUserId);
      const commissionLabel = commissionType === "rate" ? `%${numberText(rate)}` : `${moneyText(amount, "EUR")} / etiket`;
      setNotice(
        `${numberText(result.referrals.length)} müşteri, ${customerOptionLabel(affiliate || {})} affiliate hesabına ${commissionLabel} komisyonla bağlandı. Dönem: ${dateText(result.effectiveFrom)} – ${dateText(result.effectiveUntil)}. ${numberText(result.createdCount)} yeni, ${numberText(result.updatedCount)} güncellenen bağlantı.${backfill ? ` Geçmişte ${numberText(result.backfill.created)} yeni kullanım oluşturuldu, ${numberText(result.backfill.adjusted)} mevcut kullanım güncellendi; dönem komisyonu ${moneyText(result.backfill.amount, "EUR")}.` : ""}`
      );
      setRefreshKey((current) => current + 1);
      setReferredUserIds([]);
      setReferredSearch("");
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Affiliate bağlantısı kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  const normalizedAffiliateSearch = affiliateSearch.trim().toLocaleLowerCase("tr-TR");
  const affiliateCandidates = customers.filter((row) => {
    const id = text(row.id);
    if (!id || referredUserIds.includes(id)) return false;
    return !normalizedAffiliateSearch || id === affiliateUserId ||
      customerOptionLabel(row).toLocaleLowerCase("tr-TR").includes(normalizedAffiliateSearch);
  });
  const normalizedReferredSearch = referredSearch.trim().toLocaleLowerCase("tr-TR");
  const referredCandidates = customers.filter((row) => {
    const id = text(row.id);
    if (!id || id === affiliateUserId) return false;
    if (!normalizedReferredSearch) return true;
    return customerOptionLabel(row).toLocaleLowerCase("tr-TR").includes(normalizedReferredSearch);
  });

  function toggleReferredUser(id: string) {
    setReferredUserIds((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id].slice(0, 90));
  }

  function editReferral(row: DataRow) {
    const affiliateId = text(row.affiliateUserId);
    const referredId = text(row.referredUserId);
    if (!affiliateId || !referredId) return;
    setAffiliateSearch("");
    setAffiliateUserId(affiliateId);
    setReferredUserIds([referredId]);
    setCommissionType(text(row.calculationType) === "fixed" ? "fixed" : "rate");
    setCommissionRate(String(Number(row.commissionRate || 0) * 100));
    setUsageCommissionAmount(String(Number(row.commissionAmount || 0)));
    setEffectiveFrom(dateTimeInputValue());
    setEffectiveUntil(dateTimeInputValue(text(row.effectiveUntil) || oneYearAfterInput(text(row.effectiveFrom) || new Date())));
    setBackfill(false);
    setNotice("Bağlantı düzenleme formuna yüklendi. Yeni kural varsayılan olarak bugünden geçerlidir; önceki dönem ve mevcut kazançlar korunur.");
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function completeHistory() {
    if (!historyReferral) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.completeAdminAffiliateHistory(text(historyReferral.id));
      setNotice(`Geçmiş tamamlandı: ${numberText(result.scanned)} uygun kullanım incelendi; ${numberText(result.alreadyRecorded)} kullanım zaten kayıtlı. ${numberText(result.created)} eksik etiket için ${moneyText(result.amount, "EUR")} komisyon eklendi. Mevcut kazançlar ve ödemeler korundu.`);
      setHistoryReferral(null);
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Geçmiş komisyonlar tamamlanamadı.");
    } finally {
      setSaving(false);
    }
  }

  async function endReferral() {
    if (!endingReferral || !endReason.trim()) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      const result = await apiClient.endAdminAffiliateReferral(text(endingReferral.id), endReason, voidUnpaid);
      setNotice(`Bonus bağlantısı kaldırıldı. ${numberText(result.voided)} ödenmemiş hareket iptal edildi. Ödenmiş veya etikete çevrilmiş ${numberText(result.protectedEvents)} hareket korundu. Müşteri hesapları ve diğer bağlantılar korunur.`);
      setEndingReferral(null); setEndReason(""); setVoidUnpaid(false);
      setRefreshKey((value) => value + 1);
    } catch (err) { setError(err instanceof Error ? err.message : "Bağlantı kaldırılamadı."); }
    finally { setSaving(false); }
  }

  const assignmentPanel = (
    <AdminPanel
      title="Bonus bağlantısını yönet"
      caption="Referans veren ve getirilen müşteriyi seçin; ilişkiye özel komisyonu, başlangıç-bitiş dönemini ve geçmiş kullanımların hesaplanıp hesaplanmayacağını kod değişikliği olmadan yönetin."
    >
      <form onSubmit={assignReferral} className="grid gap-4 p-5 md:grid-cols-2 xl:grid-cols-6 xl:items-end">
        <div className="grid gap-2 text-sm font-semibold text-slate-700 xl:col-span-2">
          <label htmlFor="affiliate-winner">Komisyonu kazanacak firma</label>
          <input
            type="search"
            aria-label="Komisyonu kazanacak firmayı ara"
            value={affiliateSearch}
            onChange={(event) => setAffiliateSearch(event.target.value)}
            placeholder="Firma, ad, e-posta veya müşteri no ara"
            disabled={customersLoading || saving}
            className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm font-normal text-slate-950 outline-none focus:border-slate-400"
          />
          <select
            id="affiliate-winner"
            value={affiliateUserId}
            onChange={(event) => setAffiliateUserId(event.target.value)}
            disabled={customersLoading || saving}
            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5 disabled:opacity-60"
            required
          >
            <option value="">{customersLoading ? "Müşteriler yükleniyor..." : "Referans veren firmayı seçin"}</option>
            {affiliateCandidates.map((row) => (
                <option key={text(row.id)} value={text(row.id)}>
                  {customerOptionLabel(row)}
                </option>
              ))}
          </select>
          {!customersLoading && affiliateCandidates.length === 0 ? <span className="text-xs font-normal text-slate-500">Eşleşen firma bulunamadı.</span> : null}
        </div>
        <div className="grid gap-2 text-sm font-semibold text-slate-700 xl:col-span-4">
          <div className="flex items-center justify-between gap-3">
            <span>Getirilen müşteriler</span>
            <span className="text-xs font-medium text-slate-500">{numberText(referredUserIds.length)} seçili</span>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <input
              type="search"
              value={referredSearch}
              onChange={(event) => setReferredSearch(event.target.value)}
              placeholder="Firma, ad veya e-posta ara"
              disabled={customersLoading || saving}
              className="h-10 w-full border-b border-slate-100 px-3 text-sm font-normal text-slate-950 outline-none"
            />
            <div className="max-h-36 overflow-y-auto p-2">
              {customersLoading ? <p className="px-2 py-3 text-xs font-normal text-slate-500">Müşteriler yükleniyor...</p> : null}
              {!customersLoading && referredCandidates.length === 0 ? <p className="px-2 py-3 text-xs font-normal text-slate-500">Eşleşen müşteri bulunamadı.</p> : null}
              {referredCandidates.map((row) => {
                const id = text(row.id);
                return (
                  <label key={id} className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-2 font-normal hover:bg-slate-50">
                    <input type="checkbox" checked={referredUserIds.includes(id)} onChange={() => toggleReferredUser(id)} disabled={saving} className="mt-0.5" />
                    <span className="min-w-0 text-xs leading-5 text-slate-700">{customerOptionLabel(row)}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <label className="grid gap-2 text-sm font-semibold text-slate-700 xl:col-span-2">
          Komisyon tipi
          <select
            value={commissionType}
            onChange={(event) => setCommissionType(event.target.value === "fixed" ? "fixed" : "rate")}
            disabled={saving}
            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5 disabled:opacity-60"
          >
            <option value="rate">Yüzde</option>
            <option value="fixed">Etiket başı sabit</option>
          </select>
        </label>
        <label className="grid gap-2 text-sm font-semibold text-slate-700 xl:col-span-2">
          {commissionType === "rate" ? "Komisyon oranı" : "Etiket başı komisyon"}
          <div className="relative">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={commissionType === "rate" ? commissionRate : usageCommissionAmount}
              onChange={(event) => commissionType === "rate" ? setCommissionRate(event.target.value) : setUsageCommissionAmount(event.target.value)}
              disabled={saving}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-8 pr-3 text-sm font-normal text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5 disabled:opacity-60"
              required
            />
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">{commissionType === "rate" ? "%" : "€"}</span>
          </div>
        </label>
        <label className="grid gap-2 text-sm font-semibold text-slate-700 xl:col-span-2">
          Komisyon başlangıcı
          <input
            type="datetime-local"
            value={effectiveFrom}
            onChange={(event) => {
              setEffectiveFrom(event.target.value);
              setEffectiveUntil(oneYearAfterInput(event.target.value));
            }}
            disabled={saving}
            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5 disabled:opacity-60"
            required
          />
        </label>
        <label className="grid gap-2 text-sm font-semibold text-slate-700 xl:col-span-2">
          Komisyon bitişi
          <input
            type="datetime-local"
            value={effectiveUntil}
            min={effectiveFrom}
            onChange={(event) => setEffectiveUntil(event.target.value)}
            disabled={saving}
            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5 disabled:opacity-60"
            required
          />
          <span className="text-xs font-normal leading-4 text-slate-500">Varsayılan süre 1 yıldır; bitince komisyon otomatik durur.</span>
        </label>
        <label className="flex min-h-11 items-center gap-3 rounded-xl xl:col-span-2 border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
          <input type="checkbox" checked={backfill} onChange={(event) => setBackfill(event.target.checked)} disabled={saving} />
          <span>
            Geçmiş komisyonu oluştur
            <span className="mt-0.5 block text-xs font-normal leading-4 text-slate-500">Seçilen tarihten bugüne kadar uygun kullanımları hesaplar.</span>
          </span>
        </label>
        {affiliateUserId && referredUserIds.length ? <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950 md:col-span-2 xl:col-span-6" role="status">
          <strong>{text(customers.find(row => text(row.id) === affiliateUserId)?.companyName) || "Seçilen firma"}</strong>, seçilen {numberText(referredUserIds.length)} müşterinin uygun etiket kullanımından komisyon kazanacak. Yeni kural belirtilen başlangıç tarihinden itibaren geçerlidir. Mevcut kazançlar değişmez.
        </div> : null}
        <button
          type="submit"
          disabled={customersLoading || saving || !affiliateUserId || referredUserIds.length === 0}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 xl:col-span-2 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <HandCoins className="h-4 w-4" />}
          {saving ? "Kaydediliyor" : backfill ? "Bağla ve geçmişi hesapla" : "Bağla ve kaydet"}
        </button>
      </form>
      {endingReferral ? <div role="alertdialog" aria-labelledby="affiliate-end-title" className="border-t border-amber-200 bg-amber-50 p-5">
        <h3 id="affiliate-end-title" className="font-semibold">Bonus bağlantısını kaldır</h3>
        <p className="mt-2 text-sm">{text(endingReferral.affiliateCompanyName)} → {text(endingReferral.referredUserCompanyName)}. Yalnız bu bonus ilişkisi kapanır; müşteri hesapları, diğer referanslar ve ödeme geçmişi korunur.</p>
        <label className="mt-3 grid gap-1 text-sm">Açıklama<input aria-label="Bağlantıyı kaldırma açıklaması" value={endReason} onChange={event => setEndReason(event.target.value)} className="rounded-lg border p-2" /></label>
        <label className="mt-3 flex gap-2 text-sm"><input type="checkbox" checked={voidUnpaid} onChange={event => setVoidUnpaid(event.target.checked)} />Bu ilişkiye ait hatalı, ödenmemiş komisyonları da iptal et</label>
        <div className="mt-3 flex gap-3">
          <button type="button" disabled={saving || !endReason.trim()} onClick={() => void endReferral()} className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving ? "İşleniyor" : "Bağlantıyı kaldır"}</button>
          <button type="button" disabled={saving} onClick={() => setEndingReferral(null)} className="rounded-lg border bg-white px-4 py-2 text-sm">Vazgeç</button>
        </div>
      </div> : null}
      {historyReferral ? <div role="alertdialog" aria-labelledby="affiliate-history-title" className="border-t border-amber-200 bg-amber-50 p-5">
        <h3 id="affiliate-history-title" className="font-semibold">Tüm geçmişi tamamla</h3>
        <p className="mt-2 text-sm">{text(historyReferral.referredUserCompanyName) || text(historyReferral.referredUserEmail)}</p>
        <p className="mt-2 text-sm">İlk komisyon kuralı müşterinin ilk uygun etiketine kadar geriye alınır. Yalnız eksik kazançlar eklenir; ödenmiş veya etikete çevrilmiş tutarlar ve bitiş tarihi korunur. 24 saati geçen kazançlar otomatik onaylanır.</p>
        <div className="mt-3 flex gap-3">
          <button type="button" disabled={saving} onClick={completeHistory} className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving ? "Kaydediliyor" : "Geçmişi tamamla"}</button>
          <button type="button" disabled={saving} onClick={() => setHistoryReferral(null)} className="rounded-lg border bg-white px-4 py-2 text-sm">Vazgeç</button>
        </div>
      </div> : null}
      {notice ? <div className="border-t border-slate-100 p-5"><AdminNotice message={notice} /></div> : null}
      {error ? <div className="border-t border-slate-100 p-5"><ErrorBanner message={error} /></div> : null}
    </AdminPanel>
  );

  return (
    <ResourcePage
      key={refreshKey}
      title="Affiliate hesapları"
      caption="Referans kodları, yönlendirilen müşteriler ve kullanım komisyonları."
      endpoint="/api/admin/affiliates"
      beforeSections={assignmentPanel}
      sections={[
        {
          key: "affiliates",
          title: "Affiliate profilleri",
          columns: [
            { label: "Müşteri", value: (row) => <NameStack title={text(row.userCompanyName) || text(row.userEmail)} detail={text(row.userEmail)} /> },
            { label: "Kod", value: (row) => text(row.referralCode) },
            { label: "Referans", value: (row) => numberText(row.referralCount), align: "right" },
            { label: "Varsayılan", value: (row) => row.usageCommissionAmount == null ? `%${numberText(Number(row.defaultCommissionRate || 0) * 100)}` : `${moneyText(row.usageCommissionAmount, "EUR")} / etiket`, align: "right" },
            { label: "Bekleyen", value: (row) => moneyText(row.pendingCommission, "EUR"), align: "right" },
            { label: "Onaylanan", value: (row) => moneyText(row.approvedCommission, "EUR"), align: "right" },
            { label: "Ödenen", value: (row) => moneyText(row.paidCommission, "EUR"), align: "right" },
            { label: "Durum", value: (row) => statusBadge(text(row.status)) },
          ],
        },
        {
          key: "referrals",
          title: "Yönlendirilen müşteriler",
          columns: [
            { label: "Komisyonu kazanan", value: (row) => <NameStack title={text(row.affiliateCompanyName) || text(row.affiliateEmail)} detail={text(row.referralCode)} /> },
            { label: "Getirilen müşteri", value: (row) => <NameStack title={text(row.referredUserCompanyName) || text(row.referredUserEmail)} detail={text(row.referredUserEmail)} /> },
            { label: "Kullanılan kod", value: (row) => text(row.referralCodeUsed) || "-" },
            { label: "Kural", value: (row) => text(row.calculationType) === "fixed" ? `${moneyText(row.commissionAmount, "EUR")} / etiket` : `%${numberText(Number(row.commissionRate || 0) * 100)}` },
            { label: "Bu ay satın alma", value: (row) => <NameStack title={`${numberText(row.monthPurchasedQuantity)} kredi`} detail={`${moneyText(row.monthPurchasedTotal, "EUR")} · Tüm zamanlar: ${numberText(row.purchasedQuantity)}`} />, align: "right" },
            { label: "Bu ay kullanılan", value: (row) => <NameStack title={numberText(row.monthUsedLabels)} detail={`Tüm zamanlar: ${numberText(row.usedLabels)}`} />, align: "right" },
            { label: "Komisyona giren", value: (row) => numberText(row.commissionEventCount), align: "right" },
            { label: "Komisyon", value: (row) => <NameStack title={moneyText(row.totalCommission, "EUR")} detail={text(row.calculationType) === "fixed" ? `${numberText(row.commissionEventCount)} × ${moneyText(row.commissionAmount, "EUR")}` : "Dönem içindeki uygun hareketler"} />, align: "right" },
            { label: "Dönem", value: (row) => <NameStack title={dateText(row.effectiveFrom)} detail={`Bitiş: ${dateText(row.effectiveUntil || row.endedAt)}`} /> },
            { label: "Durum", value: (row) => statusBadge(text(row.status)) },
            {
              label: "Yönet",
              value: (row) => text(row.relationshipStatus || row.status) === "active" ? (
                <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => editReferral(row)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                  Düzenle
                </button>
                <button type="button" disabled={saving} onClick={() => { setHistoryReferral(row); setError(null); setNotice(null); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50">Tüm geçmişi tamamla</button>
                <button type="button" disabled={saving} onClick={() => { setEndingReferral(row); setEndReason(""); setVoidUnpaid(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 disabled:opacity-50">Bağlantıyı kaldır</button>
                </div>
              ) : "-",
            },
          ],
        },
      ]}
    />
  );
}

function AdminAffiliateLabelCreditPanel({ onChanged }: { onChanged: (message: string) => void }) {
  const [data, setData] = useState<AdminAffiliateLabelCreditResponse | null>(null);
  const [affiliateUserId, setAffiliateUserId] = useState("");
  const [labelProductId, setLabelProductId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [allowPending, setAllowPending] = useState(true);
  const [bonusAmount, setBonusAmount] = useState("");
  const [bonusReason, setBonusReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"bonus" | "redeem" | "">("");
  const [error, setError] = useState<string | null>(null);

  async function load(selectedId = affiliateUserId) {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.adminAffiliateLabelCredits(selectedId);
      setData(response);
      const nextAffiliateId = selectedId || response.selectedAffiliateUserId || "";
      setAffiliateUserId(nextAffiliateId);
      const products = response.labelCredit?.products || [];
      setLabelProductId((current) => products.some((product) => product.id === current) ? current : products[0]?.id || "");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kazanç ve etiket bilgileri alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load("");
  }, []);

  async function selectAffiliate(nextId: string) {
    setAffiliateUserId(nextId);
    setLabelProductId("");
    await load(nextId);
  }

  async function redeem() {
    const parsedQuantity = Number(quantity);
    if (!affiliateUserId || !labelProductId || !Number.isInteger(parsedQuantity) || parsedQuantity <= 0) {
      setError("Müşteri, etiket ürünü ve pozitif tam adet gerekli.");
      return;
    }
    setBusy("redeem");
    setError(null);
    try {
      const result = await apiClient.redeemAdminAffiliateLabelCredits({
        affiliateUserId,
        allowPending,
        idempotencyKey: `affiliate-label-credit:${crypto.randomUUID()}`,
        labelProductId,
        quantity: parsedQuantity,
      });
      onChanged(`${result.redemption.quantity} etiket müşterinin bakiyesine eklendi; ${moneyText(result.redemption.amount, "EUR")} kazanç kullanıldı.`);
      setQuantity("1");
      await load(affiliateUserId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kazanç etikete dönüştürülemedi.");
    } finally {
      setBusy("");
    }
  }

  async function grantBonus() {
    const amount = Number(bonusAmount.replace(",", "."));
    if (!affiliateUserId || !Number.isFinite(amount) || amount <= 0 || !bonusReason.trim()) {
      setError("Pozitif bonus tutarı ve açıklaması gerekli.");
      return;
    }
    setBusy("bonus");
    setError(null);
    try {
      const result = await apiClient.grantAdminAffiliateBonus({
        affiliateUserId,
        amount,
        idempotencyKey: `affiliate-bonus:${crypto.randomUUID()}`,
        reason: bonusReason.trim(),
      });
      onChanged(`${moneyText(result.amount, "EUR")} bonus kazanç müşterinin kullanılabilir bakiyesine eklendi.`);
      setBonusAmount("");
      setBonusReason("");
      await load(affiliateUserId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Bonus kazanç eklenemedi.");
    } finally {
      setBusy("");
    }
  }

  const selectedAffiliate = data?.affiliates.find((affiliate) => affiliate.id === affiliateUserId) || null;
  const credit = data?.labelCredit || null;
  const selectedProduct = credit?.products.find((product) => product.id === labelProductId) || null;
  const parsedQuantity = Math.max(0, Math.trunc(Number(quantity) || 0));
  const requiredAmount = Number(((selectedProduct?.grossUnitPrice || 0) * parsedQuantity).toFixed(2));
  const availableAmount = Number(((credit?.approvedAvailable || 0) + (allowPending ? (credit?.pendingAvailable || 0) : 0)).toFixed(2));
  const canRedeem = Boolean(selectedProduct && parsedQuantity > 0 && parsedQuantity <= selectedProduct.availableStock && requiredAmount <= availableAmount);

  return (
    <AdminPanel
      title="Kazancı etiket bakiyesine çevir"
      caption="Referans veya bonus kazancını gerçek etiket stokundan müşteriye aktarır; aynı kazanç ikinci kez kullanılamaz."
      action={loading ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" /> : null}
    >
      <div className="grid gap-5 p-5 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,1fr)]">
        <div className="space-y-4">
          <label className="grid gap-2 text-sm font-semibold text-slate-700">
            Müşteri
            <select
              value={affiliateUserId}
              disabled={loading || busy !== ""}
              onChange={(event) => void selectAffiliate(event.target.value)}
              className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 disabled:opacity-60"
            >
              {(data?.affiliates || []).map((affiliate) => (
                <option key={affiliate.id} value={affiliate.id}>
                  {affiliate.companyName || `${affiliate.firstName || ""} ${affiliate.lastName || ""}`.trim() || affiliate.email} · {affiliate.email}
                </option>
              ))}
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            <InfoLine label="Onaylı / kullanılabilir" value={moneyText(credit?.approvedAvailable || 0, "EUR")} />
            <InfoLine label="Bekleyen" value={moneyText(credit?.pendingAvailable || 0, "EUR")} />
            <InfoLine label="Etikete çevrilen" value={moneyText(credit?.convertedTotal || 0, "EUR")} />
          </div>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_130px]">
            <label className="grid gap-2 text-sm font-semibold text-slate-700">
              Etiket ürünü
              <select
                value={labelProductId}
                disabled={loading || busy !== ""}
                onChange={(event) => setLabelProductId(event.target.value)}
                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 disabled:opacity-60"
              >
                {(credit?.products || []).map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} · {moneyText(product.grossUnitPrice, "EUR")} / adet · stok {product.availableStock}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-2 text-sm font-semibold text-slate-700">
              Adet
              <input
                type="number"
                min="1"
                max={selectedProduct?.availableStock || 1000}
                step="1"
                value={quantity}
                disabled={busy !== ""}
                onChange={(event) => setQuantity(event.target.value)}
                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 disabled:opacity-60"
              />
            </label>
          </div>
          <label className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
            <input type="checkbox" className="mt-1" checked={allowPending} onChange={(event) => setAllowPending(event.target.checked)} disabled={busy !== ""} />
            <span><strong>Bekleyen kazancı da kullan.</strong> Gerekli tutar kadar bekleyen hareket yönetici kararıyla onaylanıp doğrudan etiket bakiyesine çevrilir.</span>
          </label>
          <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Kullanılacak kazanç</p>
              <p className="mt-1 text-xl font-bold text-slate-950">{moneyText(requiredAmount, "EUR")}</p>
              <p className="mt-1 text-xs text-slate-500">Kalan: {moneyText(Math.max(0, availableAmount - requiredAmount), "EUR")} · Fiyat KDV dahil hesaplanır.</p>
            </div>
            <button
              type="button"
              disabled={busy !== "" || !canRedeem}
              onClick={() => void redeem()}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
            >
              {busy === "redeem" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tags className="h-4 w-4" />}
              Etikete çevir
            </button>
          </div>
        </div>
        <div className="rounded-2xl border border-cyan-200 bg-cyan-50 p-4">
          <p className="text-sm font-bold text-cyan-950">Manuel bonus kazanç ekle</p>
          <p className="mt-1 text-sm leading-6 text-cyan-900">Örneğin 100 € bonus tanımlayın. Tutar onaylı kazanç olur; müşteri kendisi kullanabilir veya siz soldan etikete çevirebilirsiniz.</p>
          <div className="mt-4 grid gap-3">
            <label className="grid gap-2 text-sm font-semibold text-cyan-950">
              Bonus tutarı (€)
              <input value={bonusAmount} onChange={(event) => setBonusAmount(event.target.value)} inputMode="decimal" placeholder="100,00" disabled={busy !== ""} className="h-11 rounded-xl border border-cyan-200 bg-white px-3 text-sm font-normal outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100 disabled:opacity-60" />
            </label>
            <label className="grid gap-2 text-sm font-semibold text-cyan-950">
              Açıklama
              <input value={bonusReason} onChange={(event) => setBonusReason(event.target.value)} placeholder="Kampanya bonusu" disabled={busy !== ""} className="h-11 rounded-xl border border-cyan-200 bg-white px-3 text-sm font-normal outline-none focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100 disabled:opacity-60" />
            </label>
            <button type="button" disabled={busy !== "" || !selectedAffiliate || !bonusAmount || !bonusReason.trim()} onClick={() => void grantBonus()} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-cyan-700 px-4 text-sm font-semibold text-white shadow-sm hover:bg-cyan-800 disabled:opacity-50">
              {busy === "bonus" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Bonus kazanç ekle
            </button>
          </div>
        </div>
      </div>
      {error ? <div className="border-t border-slate-100 p-5"><ErrorBanner message={error} /></div> : null}
    </AdminPanel>
  );
}

function AdminCommissionsPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function updateStatus(id: string, action: "approve" | "paid" | "reject" | "void") {
    let reason = "";
    let paymentReference = "";
    if (action === "reject" || action === "void") {
      const response = window.prompt(action === "reject" ? "Ret gerekçesini yazın:" : "Geçersiz kılma gerekçesini yazın:");
      if (response === null) return;
      reason = response.trim();
      if (!reason) {
        setError("Bu işlem için gerekçe gerekli.");
        return;
      }
    }
    if (action === "paid") {
      const response = window.prompt("Ödeme referansı (isteğe bağlı):", "");
      if (response === null) return;
      paymentReference = response.trim();
    }
    setBusyId(id);
    setNotice(null);
    setError(null);
    try {
      await apiClient.updateCommissionStatus({
        action,
        commissionEventId: id,
        paymentReference: paymentReference || undefined,
        reason: reason || undefined,
      });
      const messages = {
        approve: "Komisyon onaylandı.",
        paid: "Komisyon ödendi olarak işaretlendi.",
        reject: "Komisyon reddedildi.",
        void: "Komisyon geçersiz kılındı.",
      };
      setNotice(messages[action]);
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Komisyon güncellenemedi.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-5">
      {notice ? <AdminNotice message={notice} /> : null}
      {error ? <ErrorBanner message={error} /> : null}
      <AdminAffiliateLabelCreditPanel
        onChanged={(message) => {
          setNotice(message);
          setRefreshKey((current) => current + 1);
        }}
      />
      <ResourcePage
        key={refreshKey}
        title="Komisyonlar"
        caption="Affiliate tahakkuk ve ödeme hareketleri."
        endpoint="/api/admin/commissions"
        sections={[
          {
            key: "commissions",
            title: "Hareketler",
            columns: [
              { label: "Affiliate", value: (row) => <NameStack title={text(row.affiliateCompanyName) || text(row.affiliateEmail)} detail={text(row.referralCode)} /> },
              { label: "Yönlendirilen", value: (row) => text(row.referredUserCompanyName) || text(row.referredUserEmail) },
              { label: "Kaynak", value: (row) => badge(text(row.source) || (text(row.eventKey).startsWith("usage:") ? "shipmentUsage" : "invoice")) },
              { label: "Kayıt", value: (row) => text(row.invoiceNumber) || text(row.externalOrderId) || text(row.eventKey) },
              { label: "Tutar", value: (row) => moneyText(row.amount, row.currency), align: "right" },
              {
                label: "Durum",
                value: (row) => (
                  <NameStack
                    title={statusBadge(text(row.status))}
                    detail={text(row.statusReason) || text(row.paymentReference)}
                  />
                ),
              },
              {
                label: "İşlem",
                value: (row) => {
                  const status = text(row.status);
                  const id = text(row.id);
                  if (["paid", "rejected", "void"].includes(status)) {
                    return <span className="text-xs font-semibold text-slate-500">İşlem tamamlandı</span>;
                  }
                  const actions: Array<{ action: "approve" | "paid" | "reject" | "void"; label: string }> = status === "pending"
                    ? [{ action: "approve", label: "Onayla" }, { action: "reject", label: "Reddet" }, { action: "void", label: "Geçersiz" }]
                    : [{ action: "paid", label: "Ödendi" }, { action: "reject", label: "Reddet" }, { action: "void", label: "Geçersiz" }];
                  return (
                    <div className="flex flex-wrap gap-1.5">
                      {actions.map((item) => (
                        <button
                          key={item.action}
                          type="button"
                          disabled={busyId === id}
                          onClick={() => void updateStatus(id, item.action)}
                          className={cx(
                            "inline-flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold shadow-sm transition disabled:opacity-60",
                            item.action === "approve" || item.action === "paid"
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                          )}
                        >
                          {busyId === id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : item.action === "approve" || item.action === "paid" ? <ShieldCheck className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                          {item.label}
                        </button>
                      ))}
                    </div>
                  );
                },
              },
            ],
          },
        ]}
      />
    </div>
  );
}

function labelCheckIssueTypeLabel(value: unknown) {
  const type = text(value);
  if (type === "carrier_error") return "DHL kontrol hatası";
  if (type === "compensation_pending") return "Telafi bekliyor";
  if (type === "delivered_faulty_mix") return "Delivered karışımı";
  if (type === "failed_run") return "Başarısız run";
  if (type === "stale_run") return "Süre aşımı";
  return type || "Operasyon kaydı";
}

function labelCheckIssueSeverityBadge(row: DataRow) {
  const severity = text(row.severity);
  const label = severity === "danger" ? "Acil" : severity === "warning" ? "Uyarı" : "Bilgi";
  return (
    <span
      className={cx(
        "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
        severity === "danger" && "border-red-200 bg-red-50 text-red-700",
        severity === "warning" && "border-amber-200 bg-amber-50 text-amber-700",
        !["danger", "warning"].includes(severity) && "border-slate-200 bg-slate-50 text-slate-600"
      )}
    >
      {label}
    </span>
  );
}

function AdminLabelChecksPage() {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [selectedMode, setSelectedMode] = useState("consumed");
  const [busyMode, setBusyMode] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function loadRuns() {
    setLoading(true);
    setError(null);
    try {
      setData(await apiClient.resource<Record<string, unknown>>("/api/admin/label-check-runs"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etiket kontrol kayıtları alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadRuns();
  }, []);

  async function runScenario(mode: "assigned" | "available" | "consumed") {
    setBusyMode(mode);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.runDueCarrierCheck(mode);
      const stats = (result.stats || {}) as DataRow;
      setNotice(`${text(result.mode)} kontrolü çalıştı: ${numberText(stats.completed)} / ${numberText(stats.total)} etiket işlendi.`);
      setSelectedMode(mode);
      await loadRuns();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etiket kontrolü başlatılamadı.");
    } finally {
      setBusyMode("");
    }
  }

  const scenarios = Array.isArray(data?.scenarios) ? (data.scenarios as DataRow[]) : [];
  const runs = Array.isArray(data?.runs) ? (data.runs as DataRow[]) : [];
  const issues = Array.isArray(data?.issues) ? (data.issues as DataRow[]) : [];
  const health = ((data?.health || {}) as DataRow) || {};
  const selectedScenario = scenarios.find((scenario) => text(scenario.mode) === selectedMode) || scenarios[0];
  const visibleRuns = runs.filter((run) => text(run.scenario) === selectedMode || (!text(run.scenario) && selectedMode === "consumed"));
  const pendingRuns = Number(health.pendingRuns || 0);
  const staleRuns = Number(health.staleRuns || 0);
  const compensationPending = Number(health.compensationPending || 0);
  const deliveredMix = Number(health.consumedDeliveredFaultyLike || 0);
  const failedRuns24h = Number(health.failedRuns24h || 0);
  const carrierCheckErrors = Number(health.carrierCheckErrors || 0);
  const transientCarrierCheckWarnings = Number(health.transientCarrierCheckWarnings || 0);
  const criticalCount = pendingRuns + staleRuns + compensationPending + deliveredMix;

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Etiket Kontrolleri"
        caption="DHL teslimat ve hatalı etiket otomasyonunun üç senaryolu çalışma kayıtları."
        icon={<ShieldCheck className="h-5 w-5" />}
        action={
          <button
            type="button"
            onClick={() => void loadRuns()}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            Yenile
          </button>
        }
      />
      {notice ? <AdminNotice message={notice} /> : null}
      {error ? <ErrorBanner message={error} /> : null}
      {loading ? (
        <LoadingPanel label="Etiket kontrol kayıtları yükleniyor" />
      ) : (
        <>
          <section className="grid gap-4 lg:grid-cols-3">
            {scenarios.map((scenario) => {
              const mode = text(scenario.mode) as "assigned" | "available" | "consumed";
              const active = selectedMode === mode;
              const pendingRuns = Number(scenario.pendingRunCount || 0);
              const staleRuns = Number(scenario.staleRunCount || 0);
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setSelectedMode(mode)}
                  className={cx(
                    "rounded-2xl border bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md",
                    active ? "border-slate-900 ring-2 ring-slate-900/10" : "border-slate-200"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">{numberText(scenario.cadenceMinutes)} dk</p>
                      <h2 className="mt-2 text-base font-semibold text-slate-950">{text(scenario.label)}</h2>
                    </div>
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 text-slate-700 ring-1 ring-slate-200">
                      {mode === "consumed" ? <Truck className="h-4 w-4" /> : mode === "assigned" ? <Users className="h-4 w-4" /> : <Package className="h-4 w-4" />}
                    </span>
                  </div>
                  <p className="mt-3 min-h-[40px] text-sm leading-5 text-slate-500">{text(scenario.description)}</p>
                  <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                    <div className="rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
                      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Due</p>
                      <p className="mt-1 text-lg font-semibold text-slate-950">{numberText(scenario.dueCount)}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
                      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Son durum</p>
                      <p className="mt-1 text-sm font-semibold text-slate-950">{text(scenario.lastStatus) || "-"}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
                      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Son başarılı</p>
                      <p className="mt-1 text-sm font-semibold text-slate-950">{dateText(scenario.lastSuccessfulAt || scenario.lastCompletedAt)}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
                      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Askıda</p>
                      <p className={cx("mt-1 text-sm font-semibold", pendingRuns > 0 ? "text-amber-700" : "text-slate-950")}>{numberText(pendingRuns)}</p>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">Son çalışma: {dateText(scenario.lastRunAt)}</p>
                  {staleRuns > 0 ? <p className="mt-1 text-xs font-semibold text-amber-700">{numberText(staleRuns)} eski çalışma süre aşımıyla kapatıldı.</p> : null}
                </button>
              );
            })}
          </section>

          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7">
            <AdminStatCard icon={<Clock3 className="h-5 w-5" />} label="Askıda run" value={pendingRuns} tone={pendingRuns > 0 ? "red" : "emerald"} />
            <AdminStatCard icon={<AlertTriangle className="h-5 w-5" />} label="Süre aşımı" value={staleRuns} tone={staleRuns > 0 ? "red" : "emerald"} />
            <AdminStatCard icon={<HandCoins className="h-5 w-5" />} label="Telafi bekleyen" value={compensationPending} tone={compensationPending > 0 ? "red" : "emerald"} />
            <AdminStatCard icon={<ShieldCheck className="h-5 w-5" />} label="Kalıcı check error" value={carrierCheckErrors} tone={carrierCheckErrors > 0 ? "amber" : "emerald"} />
            <AdminStatCard icon={<RotateCcw className="h-5 w-5" />} label="Geçici DHL uyarı" value={transientCarrierCheckWarnings} tone={transientCarrierCheckWarnings > 0 ? "amber" : "emerald"} />
            <AdminStatCard icon={<Truck className="h-5 w-5" />} label="Delivered karışım" value={deliveredMix} tone={deliveredMix > 0 ? "red" : "emerald"} />
            <AdminStatCard icon={<History className="h-5 w-5" />} label="24s failed run" value={failedRuns24h} tone={failedRuns24h > 0 ? "amber" : "emerald"} />
          </section>

          {criticalCount > 0 ? (
            <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 shadow-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <span>Acil aksiyon isteyen kayıt var. Olumsuz kayıtlar tablosundan detayını kontrol edin.</span>
            </div>
          ) : (
            <AdminNotice
              message={
                carrierCheckErrors > 0 || failedRuns24h > 0 || transientCarrierCheckWarnings > 0
                  ? "Ana risk yok; geçici DHL uyarıları otomatik tekrar denenecek, kalıcı aksiyon isteyen kayıtlar aşağıda ayrı gösterilir."
                  : "Ana risk yok; üç senaryo çalışıyor ve olumsuz kritik kayıt görünmüyor."
              }
              tone={carrierCheckErrors > 0 || failedRuns24h > 0 || transientCarrierCheckWarnings > 0 ? "warning" : "success"}
            />
          )}

          <AdminPanel
            title="Olumsuz kayıtlar"
            caption="Aksiyon isteyen telafi, kalıcı kontrol hatası ve veri karışıklığı kayıtları. Geçici DHL timeoutları bu listeyi kirletmez."
            count={issues.length}
          >
            <DataTable
              rows={issues}
              empty="Olumsuz kayıt yok."
              columns={[
                { label: "Önem", value: (row) => labelCheckIssueSeverityBadge(row) },
                { label: "Tür", value: (row) => <NameStack title={labelCheckIssueTypeLabel(row.issueType)} detail={text(row.title)} /> },
                {
                  label: "Kayıt",
                  value: (row) => (
                    <NameStack
                      title={text(row.subject)}
                      detail={[text(row.productName), text(row.customerEmail), text(row.scenario)].filter(Boolean).join(" · ")}
                    />
                  ),
                },
                { label: "Durum", value: (row) => statusBadge(text(row.status) || text(row.severity)) },
                {
                  label: "Mesaj",
                  value: (row) => (
                    <div className="max-w-xl">
                      <p className="break-words text-sm font-medium leading-5 text-slate-800">{text(row.detail) || "-"}</p>
                      {text(row.actionHint) ? <p className="mt-1 break-words text-xs leading-5 text-slate-500">{text(row.actionHint)}</p> : null}
                    </div>
                  ),
                },
                { label: "Tarih", value: (row) => dateText(row.occurredAt || row.finishedAt) },
              ]}
            />
          </AdminPanel>

          <AdminPanel
            title={text(selectedScenario?.label) || "Kontrol logları"}
            caption={`${numberText(visibleRuns.length)} çalışma kaydı. Due: ${numberText(selectedScenario?.dueCount)} · Son başarılı: ${dateText(selectedScenario?.lastSuccessfulAt || selectedScenario?.lastCompletedAt)}`}
            count={visibleRuns.length}
            action={
              <button
                type="button"
                disabled={Boolean(busyMode)}
                onClick={() => void runScenario(selectedMode as "assigned" | "available" | "consumed")}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
              >
                {busyMode === selectedMode ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Bu senaryoyu çalıştır
              </button>
            }
          >
            <DataTable
              rows={visibleRuns}
              empty="Bu senaryo için kayıt yok."
              columns={[
                { label: "Request", value: (row) => <NameStack title={text(row.requestId)} detail={text(row.queuedByEmail) || text(row.exchangeStatus)} /> },
                { label: "Durum", value: (row) => statusBadge(text(row.terminalState || row.status)) },
                { label: "İstenen", value: (row) => numberText(row.requestedCount), align: "right" },
                { label: "Tamamlanan", value: (row) => numberText(row.completedLabels), align: "right" },
                { label: "Open", value: (row) => numberText(row.carrierOpenCount), align: "right" },
                { label: "Used", value: (row) => numberText(row.confirmedUsedCount), align: "right" },
                { label: "Faulty", value: (row) => numberText(row.faultyCount), align: "right" },
                { label: "Error", value: (row) => numberText(row.errorCount), align: "right" },
                {
                  label: "Telafi",
                  value: (row) => {
                    const filters = (row.filters || {}) as DataRow;
                    const stats = (filters.stats || {}) as DataRow;
                    return <NameStack title={`${numberText(stats.compensated)} tamam`} detail={`${numberText(stats.pendingCompensation)} bekliyor`} />;
                  },
                },
                { label: "Tarih", value: (row) => <NameStack title={dateText(row.createdAt)} detail={text(row.lastError) || (row.finishedAt ? `Bitti: ${dateText(row.finishedAt)}` : "")} /> },
              ]}
            />
          </AdminPanel>
        </>
      )}
    </div>
  );
}

function AdminCanceledLabelsPage() {
  return (
    <ResourcePage
      title="Müşteri İptalleri"
      caption="Müşteri veya admin tarafından iptal edilen gönderiler. Bu liste stoktaki void kaynak etiketlerden ayrı tutulur."
      endpoint="/api/admin/canceled-labels"
      sections={[
        {
          key: "labels",
          title: "İptal edilen gönderiler",
          columns: [
            { label: "Takip", value: (row) => <NameStack title={text(row.trackingNumber)} detail={text(row.orderNumber)} /> },
            { label: "Müşteri", value: (row) => text(row.userCompanyName) || text(row.userEmail) },
            { label: "Ürün", value: (row) => text(row.labelProductName) },
            { label: "Kaynak", value: (row) => <NameStack title={text(row.labelSourceStatus) || "-"} detail={text(row.labelSourceId)} /> },
            { label: "Durum", value: (row) => statusBadge(text(row.status)) },
            { label: "Tarih", value: (row) => dateText(row.updatedAt) },
          ],
        },
      ]}
    />
  );
}

function faultyLabelCustomer(row: DataRow) {
  return text(row.assignedToCompanyName) || text(row.assignedToEmail) || "Atanmamış";
}

function faultyLabelProduct(row: DataRow) {
  return text(row.labelProductName) || "Etiket ürünü yok";
}

function faultyLabelProductDetail(row: DataRow) {
  return [text(row.labelProductCarrier), text(row.weightText)].filter(Boolean).join(" · ");
}

function faultyLabelProblem(row: DataRow) {
  return text(row.externalUsageNote) || text(row.carrierCheckMessage) || "Stok kaynak etiketi void durumunda.";
}

function faultyLabelUsedAt(row: DataRow) {
  return text(row.consumedAt) || text(row.externalUsedAt);
}

type FaultyLabelIssueBucket = "all" | "status_offen" | "delivered" | "in_transit" | "other";

const faultyLabelIssueMeta: Record<Exclude<FaultyLabelIssueBucket, "all">, { label: string; tone: keyof typeof adminStatToneClasses }> = {
  status_offen: { label: "Status offen", tone: "amber" },
  delivered: { label: "Teslim edildi", tone: "emerald" },
  in_transit: { label: "Yolda", tone: "cyan" },
  other: { label: "Diğer", tone: "slate" },
};

function faultyLabelIssueBucket(row: DataRow): Exclude<FaultyLabelIssueBucket, "all"> {
  const deliveryStatus = text(row.carrierDeliveryStatus).toLowerCase();
  const deliveryLabel = text(row.carrierDeliveryLabel).toLowerCase();
  const corpus = [
    row.carrierCheckMessage,
    row.carrierCheckStatus,
    row.carrierDeliveryStatus,
    row.carrierDeliveryLabel,
    row.carrierCheckSummary,
    row.externalUsageNote,
  ]
    .map(text)
    .join(" ")
    .toLowerCase();

  if (deliveryStatus === "delivered" || /delivered|zugestellt|teslim/.test(corpus)) return "delivered";
  if (/status\s+offen|paketank|pre[_\s-]?transit|pre[_\s-]?shipment|hareket\s+yok|no[-\s]?movement|announced|label[_\s-]?created/.test(corpus)) {
    return "status_offen";
  }
  if (
    deliveryStatus === "in_transit" ||
    deliveryLabel.includes("yolda") ||
    /in[_\s-]?transit|unterwegs|transport|in zustellung|out for delivery|yolda/.test(corpus)
  ) {
    return "in_transit";
  }
  return "other";
}

function faultyIssueBadge(issue: Exclude<FaultyLabelIssueBucket, "all">) {
  const meta = faultyLabelIssueMeta[issue];
  return <span className={cx("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1", adminStatToneClasses[meta.tone])}>{meta.label}</span>;
}

function FaultyLabelField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2">
      <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</dt>
      <dd className="mt-1 min-w-0 break-words text-sm font-semibold text-slate-900">{value || "-"}</dd>
    </div>
  );
}

function FaultyLabelTimelineItem({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 ring-1 ring-slate-200">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
        <p className="mt-1 min-w-0 break-words text-sm font-semibold text-slate-900">{value || "-"}</p>
      </div>
    </div>
  );
}

function FaultyLabelTrackingLink({ trackingNumber, className }: { trackingNumber: string; className?: string }) {
  const trackingUrl = shipmentTrackingHref(trackingNumber);
  if (!trackingUrl) return <span className={className}>{trackingNumber || "-"}</span>;
  return (
    <a
      href={trackingUrl}
      target="_blank"
      rel="noopener noreferrer"
      title="DHL takip sayfasını yeni sekmede aç"
      aria-label={`${trackingNumber} DHL takip sayfasını yeni sekmede aç`}
      className={cx("inline-flex max-w-full items-center gap-1.5 text-slate-950 transition hover:text-cyan-700", className)}
    >
      <span className="min-w-0 break-words">{trackingNumber}</span>
      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-slate-400" />
    </a>
  );
}

function FaultyLabelDetailDrawer({
  label,
  onClose,
  onRelease,
  releasing,
}: {
  label: DataRow | null;
  onClose: () => void;
  onRelease: (label: DataRow) => void;
  releasing: boolean;
}) {
  useEffect(() => {
    if (!label) return undefined;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [label, onClose]);

  if (!label) return null;

  const downloadUrl = text(label.downloadUrl);
  const trackingNumber = text(label.trackingNumber) || text(label.id);
  const usageDate = faultyLabelUsedAt(label);
  const externalExportCount = rowNumber(label, "externalExportCount");
  const carrierEvents = carrierEventRows(label);
  const issueBucket = faultyLabelIssueBucket(label);
  const reactivationEligible = label.reactivationEligible === true;
  const reactivationBlockedReason = text(label.reactivationBlockedReason);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/35">
      <button type="button" className="hidden flex-1 cursor-default sm:block" aria-label="Detay panelini kapat" onClick={onClose} />
      <aside className="flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl ring-1 ring-slate-200">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-red-500">Hatalı etiket detayı</p>
            <h2 className="mt-1 break-words text-xl font-semibold text-slate-950">
              <FaultyLabelTrackingLink trackingNumber={trackingNumber} className="text-xl font-semibold" />
            </h2>
            <p className="mt-1 text-sm text-slate-500">{faultyLabelProduct(label)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
            aria-label="Detay panelini kapat"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <section className="space-y-3">
            <div className="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold">Sorun / not</p>
                <p className="mt-1 break-words leading-6">{faultyLabelProblem(label)}</p>
              </div>
            </div>
            <dl className="grid gap-3 sm:grid-cols-2">
              <FaultyLabelField label="Durum" value={statusBadge(text(label.status))} />
              <FaultyLabelField label="Müşteri" value={faultyLabelCustomer(label)} />
              <FaultyLabelField label="Ürün" value={faultyLabelProduct(label)} />
              <FaultyLabelField label="Ağırlık / carrier" value={faultyLabelProductDetail(label) || "-"} />
              <FaultyLabelField label="Ayrım" value={faultyIssueBadge(issueBucket)} />
              <FaultyLabelField label="Carrier durum" value={carrierDeliveryBadge(label)} />
              <FaultyLabelField label="Telafi" value={text(label.carrierRemediationStatus) || "-"} />
              <FaultyLabelField label="Kaynak dosya" value={text(label.originalFileName) || text(label.savedFileName) || "-"} />
              <FaultyLabelField label="Kaynak ID" value={text(label.id)} />
            </dl>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-950">Kontrol geçmişi</h3>
            <div className="mt-4 grid gap-4">
              <FaultyLabelTimelineItem icon={<Upload className="h-4 w-4" />} label="Yüklendi" value={dateText(label.createdAt)} />
              <FaultyLabelTimelineItem icon={<User className="h-4 w-4" />} label="Atandı" value={dateText(label.assignedAt)} />
              <FaultyLabelTimelineItem icon={<Package className="h-4 w-4" />} label="Kullanım" value={usageDate ? dateText(usageDate) : text(label.consumedByShipmentId) ? `Shipment: ${text(label.consumedByShipmentId)}` : "-"} />
              <FaultyLabelTimelineItem icon={<ShieldCheck className="h-4 w-4" />} label="Taşıyıcı kontrolü" value={`${text(label.carrierCheckStatus) || "unchecked"} · ${dateText(label.carrierCheckCheckedAt)}`} />
              <FaultyLabelTimelineItem icon={<Clock3 className="h-4 w-4" />} label="Son güncelleme" value={dateText(label.updatedAt || label.createdAt)} />
            </div>
          </section>

          {!reactivationEligible ? (
            <section className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-semibold">Yeniden yayınlama engellendi</p>
              <p className="mt-1 leading-6">{reactivationBlockedReason || "Bu etiket müşterinin yeniden yayınlama koşullarını karşılamıyor."}</p>
            </section>
          ) : null}

          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-950">Teknik detay</h3>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2">
              <FaultyLabelField label="Shipment ID" value={text(label.consumedByShipmentId) || "-"} />
              <FaultyLabelField label="Dış kullanım" value={text(label.externalUsageNote) || "-"} />
              <FaultyLabelField label="Dışa aktarım" value={externalExportCount ? `${numberText(externalExportCount)} kez · ${dateText(label.externalExportedAt)}` : dateText(label.externalExportedAt)} />
              <FaultyLabelField label="Kontrol mesajı" value={text(label.carrierCheckMessage) || "-"} />
              <FaultyLabelField label="Telafi mesajı" value={text(label.carrierRemediationMessage) || "-"} />
              <FaultyLabelField label="Yedek etiket" value={text(label.replacementLabelSourceId) || "-"} />
              <FaultyLabelField label="Son carrier event" value={dateText(label.carrierDeliveryLastEventAt)} />
              <FaultyLabelField label="Bitiş" value={dateText(label.expiresAt || label.expiredAt)} />
              <FaultyLabelField label="R2 dosya" value={text(label.savedKey) || "-"} />
            </dl>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-950">DHL event timeline</h3>
            {carrierEvents.length === 0 ? (
              <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">Kaydedilmiş DHL event yok.</div>
            ) : (
              <div className="mt-4 space-y-4">
                {carrierEvents.map((event, index) => (
                  <div key={`${text(event.at)}-${index}`} className="flex gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-700 ring-1 ring-red-100">
                      <Truck className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="break-words text-sm font-semibold text-slate-950">{text(event.text) || text(event.statusText) || "DHL event"}</p>
                      <p className="mt-1 text-xs text-slate-500">{[dateText(event.at), text(event.location), text(event.statusCode)].filter((item) => item && item !== "-").join(" · ") || "-"}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button
            type="button"
            onClick={() => onRelease(label)}
            disabled={releasing || !reactivationEligible}
            title={reactivationBlockedReason || "Eski/temiz etiketi yeniden yayınla"}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {releasing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Kullanıma aç
          </button>
          {downloadUrl ? (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              <Download className="h-4 w-4" />
              PDF aç
            </a>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            Kapat
          </button>
        </div>
      </aside>
    </div>
  );
}

function AdminFaultyLabelsPage() {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<DataRow | null>(null);
  const [releaseActionId, setReleaseActionId] = useState("");
  const [issueFilter, setIssueFilter] = useState<FaultyLabelIssueBucket>("all");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    apiClient
      .resource<Record<string, unknown>>("/api/admin/label-sources?status=void&limit=10000")
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Hatalı etiketler alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const labels = Array.isArray(data?.labels) ? (data.labels as DataRow[]) : [];
  const stats = (data?.stats || {}) as DataRow;
  const filter = (data?.filter || {}) as DataRow;
  const listedCount = rowNumber(filter, "filteredCount") || labels.length;
  const pdfCount = labels.filter((row) => text(row.downloadUrl)).length;
  const assignedCount = labels.filter((row) => text(row.assignedToUserId) || text(row.assignedToEmail) || text(row.assignedToCompanyName)).length;
  const labelsWithIssue = labels.map((row) => ({ issue: faultyLabelIssueBucket(row), row }));
  const issueStats = labelsWithIssue.reduce(
    (counts, item) => {
      counts[item.issue] += 1;
      return counts;
    },
    { delivered: 0, in_transit: 0, other: 0, status_offen: 0 }
  );
  const visibleLabels = issueFilter === "all" ? labels : labelsWithIssue.filter((item) => item.issue === issueFilter).map((item) => item.row);
  const issueFilters: Array<{ key: FaultyLabelIssueBucket; label: string; count: number }> = [
    { key: "all", label: "Tümü", count: labels.length },
    { key: "status_offen", label: "Status offen", count: issueStats.status_offen },
    { key: "delivered", label: "Teslim edildi", count: issueStats.delivered },
    { key: "in_transit", label: "Yolda", count: issueStats.in_transit },
    { key: "other", label: "Diğer", count: issueStats.other },
  ];

  async function reloadFaultyLabels() {
    const result = await apiClient.resource<Record<string, unknown>>("/api/admin/label-sources?status=void&limit=10000");
    setData(result);
  }

  async function releaseLabelToAvailable(label: DataRow) {
    const labelSourceId = text(label.id);
    if (!labelSourceId) return;
    const trackingNumber = text(label.trackingNumber) || labelSourceId;
    if (label.reactivationEligible !== true) {
      setError(text(label.reactivationBlockedReason) || "Bu etiket yeniden yayınlama koşullarını karşılamıyor.");
      return;
    }
    const releaseConfirmation = translatedPanelText(
      "Bu eski etiketin DHL kontrolü temiz/hareket yok olarak doğrulandı. Admin seçimiyle ortak stok havuzuna alınacak; yeni bir altı aylık süre verilmeyecek. Devam edilsin mi?"
    );
    const confirmed = window.confirm(
      `${trackingNumber} ${releaseConfirmation}`
    );
    if (!confirmed) return;
    setError(null);
    setNotice(null);
    setReleaseActionId(labelSourceId);
    try {
      const result = await apiClient.releaseLabelToAvailable(labelSourceId);
      setNotice(result.message || `${trackingNumber} ${translatedPanelText("kullanıma açıldı.")}`);
      setSelectedLabel(null);
      await reloadFaultyLabels();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : translatedPanelText("Etiket kullanıma açılamadı."));
    } finally {
      setReleaseActionId("");
    }
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Hatalı Etiketler"
        caption="Stok kaynaklarında status=void olan etiketler. Yalnız eski, DHL kontrolü temiz/hareket yok olan etiketler admin seçimiyle yeniden yayınlanabilir; son 3 ayda müşteriye verilenler korunur."
        icon={<AlertTriangle className="h-5 w-5" />}
        action={
          <div className="flex flex-wrap justify-end gap-2">
            <a
              href="/api/admin/label-sources/pdf-zip"
              className="inline-flex w-fit items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <Download className="h-3.5 w-3.5" />
              PDF ZIP
            </a>
            <a
              href="/api/admin/label-sources?status=void&export=csv&all=1"
              className="inline-flex w-fit items-center justify-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              <FileText className="h-3.5 w-3.5" />
              CSV export
            </a>
          </div>
        }
      />

      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard icon={<AlertTriangle className="h-5 w-5" />} label="Toplam void" value={rowNumber(stats, "voidCount") || listedCount} tone="red" loading={loading} />
        <AdminStatCard icon={<Tags className="h-5 w-5" />} label="Listelenen" value={listedCount} tone="slate" loading={loading} />
        <AdminStatCard icon={<FileText className="h-5 w-5" />} label="PDF olan" value={pdfCount} tone="cyan" loading={loading} />
        <AdminStatCard icon={<Users className="h-5 w-5" />} label="Müşteriye bağlı" value={assignedCount} tone="amber" loading={loading} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard icon={<Clock3 className="h-5 w-5" />} label="Status offen" value={issueStats.status_offen} tone="amber" loading={loading} />
        <AdminStatCard icon={<CheckCircle2 className="h-5 w-5" />} label="Teslim edildi" value={issueStats.delivered} tone="emerald" loading={loading} />
        <AdminStatCard icon={<Truck className="h-5 w-5" />} label="Yolda" value={issueStats.in_transit} tone="cyan" loading={loading} />
        <AdminStatCard icon={<ShieldCheck className="h-5 w-5" />} label="Diğer" value={issueStats.other} tone="slate" loading={loading} />
      </div>

      {loading ? (
        <LoadingPanel label="Hatalı etiketler yükleniyor" />
      ) : (
        <AdminPanel
          title="Void kaynak etiketleri"
          count={visibleLabels.length}
          caption="Filtrelerden Status offen seçildiğinde teslim edildi veya yolda olmayan açık DHL bildirimli etiketler listelenir. Yeniden yayınlama uygunluğu sunucuda yaş, sahiplik, kullanım ve DHL hareketiyle kontrol edilir."
        >
          {labels.length === 0 ? (
            <div className="flex min-h-48 flex-col items-center justify-center gap-3 bg-slate-50/60 p-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-400 shadow-sm">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <p className="text-sm font-medium text-slate-600">Hatalı etiket yok.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              <div className="flex flex-wrap gap-2 px-5 py-4">
                {issueFilters.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setIssueFilter(item.key)}
                    className={cx(
                      "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition",
                      issueFilter === item.key ? "border-slate-950 bg-slate-950 text-white shadow-sm" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                    )}
                  >
                    <span>{item.label}</span>
                    <span className={cx("rounded-full px-1.5 py-0.5 text-[11px]", issueFilter === item.key ? "bg-white/15 text-white" : "bg-slate-100 text-slate-500")}>{numberText(item.count)}</span>
                  </button>
                ))}
              </div>
              {visibleLabels.length === 0 ? (
                <div className="flex min-h-40 flex-col items-center justify-center gap-2 bg-slate-50/60 p-8 text-center">
                  <CheckCircle2 className="h-6 w-6 text-slate-400" />
                  <p className="text-sm font-medium text-slate-600">Bu filtrede etiket yok.</p>
                </div>
              ) : null}
              {visibleLabels.length > 0 ? (
                <>
              <div className="hidden grid-cols-[minmax(180px,1.2fr)_minmax(180px,1.2fr)_minmax(150px,1fr)_150px_150px] gap-4 bg-slate-50/90 px-5 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 xl:grid">
                <span>Takip</span>
                <span>Ürün</span>
                <span>Müşteri</span>
                <span>Durum</span>
                <span className="text-right">İşlem</span>
              </div>
              {visibleLabels.map((row, index) => {
                const rowKey = text(row.id) || text(row.trackingNumber) || String(index);
                const downloadUrl = text(row.downloadUrl);
                const issueBucket = faultyLabelIssueBucket(row);
                return (
                  <div key={rowKey} className="grid gap-4 px-5 py-4 transition hover:bg-slate-50/70 xl:grid-cols-[minmax(180px,1.2fr)_minmax(180px,1.2fr)_minmax(150px,1fr)_150px_150px] xl:items-center">
                    <div className="min-w-0">
                      <p className="break-words text-sm font-semibold text-slate-950">
                        <FaultyLabelTrackingLink trackingNumber={text(row.trackingNumber)} className="text-sm font-semibold" />
                      </p>
                      <p className="mt-1 break-words text-xs text-slate-500">{text(row.originalFileName) || text(row.savedFileName) || text(row.id)}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="break-words text-sm font-semibold text-slate-950">{faultyLabelProduct(row)}</p>
                      <p className="mt-1 break-words text-xs text-slate-500">{faultyLabelProductDetail(row) || "-"}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="break-words text-sm font-semibold text-slate-950">{faultyLabelCustomer(row)}</p>
                      <p className="mt-1 break-words text-xs text-slate-500">{dateText(row.updatedAt || row.createdAt)}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {statusBadge(text(row.status))}
                      {faultyIssueBadge(issueBucket)}
                      {carrierDeliveryBadge(row)}
                    </div>
                    <div className="flex flex-wrap gap-2 xl:justify-end">
                      {downloadUrl ? (
                        <a
                          href={downloadUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                        >
                          <Download className="h-3.5 w-3.5" />
                          PDF
                        </a>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setSelectedLabel(row)}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-slate-950 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Detay
                      </button>
                    </div>
                  </div>
                );
              })}
                </>
              ) : null}
            </div>
          )}
        </AdminPanel>
      )}

      <FaultyLabelDetailDrawer
        label={selectedLabel}
        onClose={() => setSelectedLabel(null)}
        onRelease={releaseLabelToAvailable}
        releasing={Boolean(selectedLabel && releaseActionId === text(selectedLabel.id))}
      />
    </div>
  );
}

function AdminMissingLabelsPage() {
  return (
    <ResourcePage
      title="Taşıyıcı Kontrol Hataları"
      caption="DHL/taşıyıcı kontrolünde used veya error dönen kaynak etiketler. Stok void etiketleri Hatalı Etiketler ekranında izlenir."
      endpoint="/api/admin/missing-labels"
      sections={[
        {
          key: "labels",
          title: "Kontrol hataları",
          columns: [
            { label: "Takip", value: (row) => text(row.trackingNumber) },
            { label: "Müşteri", value: (row) => text(row.assignedToCompanyName) || text(row.assignedToEmail) },
            { label: "Ürün", value: (row) => <NameStack title={text(row.labelProductName)} detail={text(row.labelProductCarrier)} /> },
            { label: "Neden", value: (row) => <NameStack title={text(row.carrierCheckStatus)} detail={text(row.carrierCheckMessage)} /> },
            { label: "Envanter", value: (row) => statusBadge(text(row.status)) },
            { label: "Tarih", value: (row) => dateText(row.carrierCheckCheckedAt || row.createdAt) },
          ],
        },
      ]}
    />
  );
}

function AdminExpiredLabelsPage() {
  const pageSize = 90;
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();
  const [labels, setLabels] = useState<DataRow[]>([]);
  const [offset, setOffset] = useState(0);
  const [summary, setSummary] = useState({ checkedCount: 0, previouslyAssignedCount: 0, sourceDatedCount: 0, totalCount: 0 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [compensation, setCompensation] = useState<DataRow[]>([]);
  const [compensating, setCompensating] = useState(false);
  const [checking, setChecking] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const checkGeneration = useRef(0);

  useEffect(() => () => { checkGeneration.current += 1; }, []);

  async function loadExpiredLabels() {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.resource<Record<string, unknown>>(`/api/admin/expired-labels?offset=${offset}&limit=${pageSize}`);
      const nextLabels = Array.isArray(result.labels) ? (result.labels as DataRow[]) : [];
      const summaryRow = result.summary && typeof result.summary === "object" ? result.summary as DataRow : {};
      setLabels(nextLabels);
      const nextIds = new Set(nextLabels.map(row => text(row.id)));
      setSelectedIds(current => new Set([...current].filter(id => nextIds.has(id))));
      if (offset > 0 && nextLabels.length === 0) setOffset(Math.max(0, offset - pageSize));
      const nextCompensation = result.compensation as { pending?: DataRow[] } | undefined;
      setCompensation(nextCompensation?.pending || []);
      setSummary({
        checkedCount: rowNumber(summaryRow, "checkedCount"),
        previouslyAssignedCount: rowNumber(summaryRow, "previouslyAssignedCount"),
        sourceDatedCount: rowNumber(summaryRow, "sourceDatedCount"),
        totalCount: rowNumber(summaryRow, "totalCount") || nextLabels.length,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Süresi bitmiş etiketler alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadExpiredLabels();
  }, [offset]);

  function toggleSelected(labelSourceId: string) {
    const row = labels.find((label) => text(label.id) === labelSourceId);
    if (!row) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(labelSourceId)) next.delete(labelSourceId);
      else if (next.size < 90) next.add(labelSourceId);
      return next;
    });
  }

  function toggleAll() {
    const selectableIds = labels.map((row) => text(row.id)).filter(Boolean).slice(0, 90);
    setSelectedIds((current) => current.size === selectableIds.length ? new Set() : new Set(selectableIds));
  }

  async function checkSelectedLabels(requestedIds = Array.from(selectedIds)) {
    const ids = requestedIds.slice(0, 90);
    if (ids.length === 0 || checking) return;
    const generation = ++checkGeneration.current;
    setChecking(true);
    setError(null);
    setNotice(null);
    let nextIndex = 0;
    let completed = 0;
    let cleanCount = 0;
    const failures: string[] = [];
    // Each check finishes in its own request; a 90-label selection must not
    // depend on one short-lived background request finishing the whole batch.
    await Promise.all(Array.from({ length: Math.min(3, ids.length) }, async () => {
      while (generation === checkGeneration.current && nextIndex < ids.length) {
        const id = ids[nextIndex++];
        try {
          const result = await apiClient.checkLabelCarrier(id);
          if (result.labelSource?.carrierCheckStatus === "clean") cleanCount += 1;
          if (!result.ok || result.labelSource?.carrierCheckStatus === "error") failures.push(id);
        } catch { failures.push(id); }
        completed += 1;
        if (generation === checkGeneration.current) {
          setNotice(`DHL kontrolü: ${numberText(completed)} / ${numberText(ids.length)} tamamlandı. Lütfen bu ekranı açık tutun.`);
        }
      }
    }));
    if (generation !== checkGeneration.current) return;
    await loadExpiredLabels();
    setNotice(`DHL kontrolü tamamlandı: ${numberText(completed)} etiket incelendi, ${numberText(cleanCount)} temiz sonuç alındı.${failures.length ? ` ${numberText(failures.length)} kontrol sonuçlanamadı; bu kayıtlar tekrar kontrol edilebilir.` : ""} Liste güncellendi; uygun seçili etiketleri havuza alabilirsiniz.`);
    setChecking(false);
  }

  async function reactivateLabels(requestedIds: string[]) {
    const eligibleIds = new Set(labels.filter((row) => row.reactivationEligible === true).map((row) => text(row.id)));
    const ids = requestedIds.filter((id) => eligibleIds.has(id)).slice(0, 90);
    if (ids.length === 0) { setNotice("Seçilen kayıtların hiçbiri havuza dönüşe uygun değil. Satırdaki engel nedenini kontrol edin."); return; }
    const selectedRows = labels.filter((row) => ids.includes(text(row.id)));
    const pdfExpiryOverrideCount = selectedRows.filter((row) => row.requiresPdfExpiryOverride === true).length;
    const allowExpiredPdfOverride = pdfExpiryOverrideCount > 0;
    const confirmed = await askConfirmation({
      title: ids.length === 1 ? "Etiket yeniden yayınlansın mı?" : "Seçili etiketler yeniden yayınlansın mı?",
      description: `${numberText(ids.length)} uygun eski/temiz etiket admin seçimiyle ortak satış havuzuna yeniden açılacak.`,
      details: [
        "Müşterideki veya dışa aktarılmış etiketlerde 3 aylık koruma sürer. Telafisi tamamlanıp müşteriden ayrılan etiket ayrıca değerlendirilir.",
        "Müşteriye daha önce verilmiş eski etiketlerde gerçek DHL temiz/hareket yok sonucu zorunludur.",
        allowExpiredPdfOverride
          ? `${numberText(pdfExpiryOverrideCount)} süresi dolmuş PDF için eski dosya korunacak ve süre kilidi olmayan güvenli yeni bir PDF kopyası üretilecek.`
          : "PDF tarihi bilgi amaçlı kalır; etikete yeni bir yapay 6 aylık süre verilmez.",
      ],
      confirmLabel: "Yeniden yayınla",
      tone: "warning",
    });
    if (!confirmed) return;

    setReactivating(true);
    setError(null);
    setNotice(null);
    let completedCount = 0;
    let sanitizedCount = 0;
    const failed: Array<{ id: string; message?: string }> = [];
    let operationError: string | null = null;
    try {
      const requestSize = 10;
      for (let offset = 0; offset < ids.length; offset += requestSize) {
        const batch = ids.slice(offset, offset + requestSize);
        const result = await apiClient.reactivateAdminLabels(
          batch,
          allowExpiredPdfOverride
            ? "Admin süresi geçmiş etiketleri güvenli ve süre kilitsiz yeni PDF kopyaları oluşturarak yeniden kullanıma aldı."
            : "Admin süresi bitmiş etiketler ekranından seçili etiketleri yeniden kullanıma aldı.",
          allowExpiredPdfOverride, true
        );
        completedCount += result.reactivatedCount;
        sanitizedCount += result.pdfExpirySanitizedCount;
        failed.push(...(result.outcomes || []).filter(row => !row.ok));
        if (ids.length > requestSize) {
          setNotice(`${numberText(completedCount)} / ${numberText(ids.length)} etiket güvenli biçimde yeniden yayınlandı.`);
        }
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Seçili etiketler yeniden kullanıma alınamadı.";
      operationError = completedCount > 0
        ? `${numberText(completedCount)} etiket yeniden yayınlandı; kalan ${numberText(ids.length - completedCount)} etiket tamamlanamadı. ${message}`
        : message;
    } finally {
      setSelectedIds(new Set());
      await loadExpiredLabels();
      if (failed.length) {
        operationError = `${numberText(completedCount)} etiket havuza alındı; ${numberText(failed.length)} kayıt engellendi. ${failed.slice(0, 5).map(row => `${row.id}: ${row.message}`).join(" · ")}`;
      }
      if (operationError) {
        setError(operationError);
        if (completedCount > 0) setNotice(`${numberText(completedCount)} etiket ortak satış havuzuna alındı.`);
      } else {
        setNotice(sanitizedCount > 0
          ? `${numberText(completedCount)} etiket ortak satış havuzuna alındı. ${numberText(sanitizedCount)} etiket için güvenli yeni PDF kopyası oluşturuldu; eski dosyalar korundu.`
          : `${numberText(completedCount)} etiket ortak satış havuzuna alındı.`);
      }
      setReactivating(false);
    }
  }

  async function reactivateSelectedLabels() {
    await reactivateLabels(Array.from(selectedIds));
  }

  async function compensatePending() {
    setCompensating(true); setError(null);
    try {
      const result = await apiClient.compensateAdminExpiredLabels();
      await loadExpiredLabels();
      setNotice(`${numberText(result.compensated)} geçerli yedek etiket müşterilere atandı. ${numberText(result.remaining)} kayıt otomatik işlem sırasında bekliyor.${result.errors?.length ? ` ${result.errors[0].message}` : ""}`);
    } catch (err) { setError(err instanceof Error ? err.message : "Yenileme tamamlanamadı."); }
    finally { setCompensating(false); }
  }

  const pagination = <nav aria-label="Karantina sayfaları" className="flex flex-wrap items-center gap-2">
    <span className="text-xs text-slate-600">Sayfa {numberText(Math.floor(offset / pageSize) + 1)} / {numberText(Math.max(1, Math.ceil(summary.totalCount / pageSize)))} · {numberText(offset + (labels.length ? 1 : 0))}–{numberText(offset + labels.length)} / {numberText(summary.totalCount)}</span>
    <button type="button" disabled={loading || checking || reactivating || offset === 0} onClick={() => { setSelectedIds(new Set()); setOffset(Math.max(0, offset - pageSize)); }} className="rounded-lg border px-3 py-2 text-xs disabled:opacity-50">Önceki 90</button>
    <button type="button" disabled={loading || checking || reactivating || offset + labels.length >= summary.totalCount} onClick={() => { setSelectedIds(new Set()); setOffset(offset + pageSize); }} className="rounded-lg border px-3 py-2 text-xs disabled:opacity-50">Sonraki 90</button>
  </nav>;

  return (
    <div className="space-y-5">
      {confirmationDialog}
      <AdminPageHeader
        title="Karantinadaki Etiketler"
        caption="Eski karantina kayıtları yalnız admin seçimiyle yeniden yayınlanır. Süresi dolmuş PDF'ler için eski dosya korunur ve süre kilitsiz güvenli yeni kopya oluşturulur; normal müşteri ve otomatik akışlar bu admin yetkisini kullanamaz."
        icon={<Clock3 className="h-5 w-5" />}
        action={
          <div className="flex flex-wrap justify-end gap-2">
            <a href="/api/admin/label-sources/pdf-zip?status=expired" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
              <Download className="h-3.5 w-3.5" /> PDF ZIP
            </a>
            <a href="/api/admin/label-sources?status=expired&export=csv&all=1" className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-slate-800">
              <FileText className="h-3.5 w-3.5" /> CSV export
            </a>
          </div>
        }
      />

      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard icon={<Clock3 className="h-5 w-5" />} label="Karantinada" value={summary.totalCount} tone="amber" loading={loading} />
        <AdminStatCard icon={<FileText className="h-5 w-5" />} label="PDF tarihi bulunan" value={summary.sourceDatedCount} tone="cyan" loading={loading} />
        <AdminStatCard icon={<Users className="h-5 w-5" />} label="Önceki müşterili" value={summary.previouslyAssignedCount} tone="violet" loading={loading} />
        <AdminStatCard icon={<ShieldCheck className="h-5 w-5" />} label="Taşıyıcı kontrolü" value={summary.checkedCount} tone="emerald" loading={loading} />
      </div>

      <AdminPanel title="Müşteriye yeni etiket atamaları" count={compensation.reduce((sum, row) => sum + Number(row.count || 0), 0)}
        caption="Süresi biten müşteri etiketleri aynı üründen geçerli bir yedekle değiştirilir. Stok veya doğrulama bekleyenler otomatik tekrar denenir."
        action={<button type="button" disabled={compensating || compensation.length === 0} onClick={() => void compensatePending()} className="rounded-lg bg-slate-950 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">{compensating ? "İşleniyor" : "Bekleyen yenilemeleri işle"}</button>}>
        <div className="p-4 text-sm">{compensation.length ? compensation.map(row => <p key={`${row.userId}-${row.labelProductId}`}>{text(row.companyName)} · {text(row.productName)}: {numberText(row.count)} yeni etiket bekliyor</p>) : "Bekleyen müşteri yenilemesi yok."}</div>
      </AdminPanel>

      <AdminPanel
        title="Karantinadaki etiketler"
        count={summary.totalCount}
        caption={`${numberText(labels.length)} kayıt listeleniyor. Tüm karantina kayıtlarına 90'lı sayfalardan ulaşabilirsiniz. DHL kontrolü bitince liste otomatik yenilenir; uygun etiketler 10'lu paketlerle havuza alınır.`}
        action={
          <div className="flex flex-wrap gap-2">
            {pagination}
            <button type="button" onClick={() => void loadExpiredLabels()} disabled={loading || checking || reactivating} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm disabled:opacity-50">
              <RefreshCw className={cx("h-3.5 w-3.5", loading && "animate-spin")} /> Yenile
            </button>
            <button type="button" onClick={() => void checkSelectedLabels()} disabled={checking || reactivating || loading || selectedIds.size === 0} className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-xs font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50">
              {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              Seçilenleri kontrol et ({numberText(selectedIds.size)})
            </button>
            <button type="button" onClick={() => void reactivateSelectedLabels()} disabled={reactivating || checking || !labels.some(row => selectedIds.has(text(row.id)) && row.reactivationEligible === true)} className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 shadow-sm transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50">
              {reactivating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
              Uygun olanları havuza al ({numberText(labels.filter(row => selectedIds.has(text(row.id)) && row.reactivationEligible === true).length)})
            </button>
          </div>
        }
      >
        {loading && labels.length === 0 ? (
          <LoadingPanel label="Süresi bitmiş etiketler yükleniyor" />
        ) : labels.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">Süresi bitmiş etiket bulunmuyor.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1120px] divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                <tr>
                  <th className="w-12 px-5 py-3.5"><input type="checkbox" aria-label="Kontrol için ilk 90 etiketi seç" disabled={checking || reactivating || loading} checked={selectedIds.size > 0 && selectedIds.size === Math.min(labels.length, 90)} onChange={toggleAll} /></th>
                  <th className="px-5 py-3.5">Takip</th>
                  <th className="px-5 py-3.5">Ürün</th>
                  <th className="px-5 py-3.5">Önceki müşteri</th>
                  <th className="px-5 py-3.5">PDF tarihi</th>
                  <th className="px-5 py-3.5">Eski karantina kaydı</th>
                  <th className="px-5 py-3.5">Sisteme yükleme</th>
                  <th className="px-5 py-3.5">Kontrol</th>
                  <th className="px-5 py-3.5 text-right">PDF</th>
                  <th className="sticky right-0 z-10 bg-slate-50 px-5 py-3.5 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {labels.map((row) => {
                  const id = text(row.id);
                  const downloadUrl = text(row.downloadUrl);
                  const reactivationEligible = row.reactivationEligible === true;
                  const reactivationBlockedReason = text(row.reactivationBlockedReason);
                  const requiresPdfExpiryOverride = row.requiresPdfExpiryOverride === true;
                  return (
                    <tr key={id} className="align-top hover:bg-slate-50/70">
                      <td className="px-5 py-4"><input type="checkbox" aria-label={`${text(row.trackingNumber)} etiketini seç`} checked={selectedIds.has(id)} disabled={checking || reactivating} title={reactivationBlockedReason || "Kontrol için seçilebilir"} onChange={() => toggleSelected(id)} /></td>
                      <td className="px-5 py-4 font-semibold text-slate-950">{text(row.trackingNumber) || "-"}</td>
                      <td className="px-5 py-4"><NameStack title={text(row.labelProductName) || "-"} detail={text(row.labelProductCarrier)} /></td>
                      <td className="px-5 py-4"><NameStack title={text(row.assignedToCompanyName) || text(row.assignedToEmail) || "-"} detail={text(row.assignedToCompanyName) ? text(row.assignedToEmail) : ""} /></td>
                      <td className="px-5 py-4">{dateText(row.sourceCreatedAt || row.sourceModifiedAt) || "-"}</td>
                      <td className="px-5 py-4"><NameStack title={dateText(row.expiresAt || row.expiredAt)} detail={row.expiresAt ? `Karantina: ${dateText(row.expiredAt)}` : ""} /></td>
                      <td className="px-5 py-4">{dateText(row.createdAt)}</td>
                      <td className="min-w-48 max-w-72 px-5 py-4">
                        <p className="font-medium text-slate-950">{text(row.carrierCheckStatus) || "unchecked"}</p>
                        <p className="mt-1 whitespace-normal break-words text-xs text-slate-500">{`${reactivationEligible ? "Havuza alınabilir" : "Havuza kapalı"} · ${requiresPdfExpiryOverride ? "PDF süresi dolmuş; yeni dosya işlemi gerekir. " : ""}${reactivationBlockedReason || dateText(row.carrierCheckCheckedAt)}`}</p>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {downloadUrl ? <a href={downloadUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm"><Download className="h-3.5 w-3.5" /> PDF</a> : "-"}
                      </td>
                      <td className="sticky right-0 bg-white px-5 py-4 text-right shadow-[-4px_0_8px_-6px_#94a3b8]">
                        <button type="button" onClick={() => void checkSelectedLabels([id])} disabled={checking || reactivating || loading} className="mb-2 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50">
                          <ShieldCheck className="h-3.5 w-3.5" /> Kontrol et
                        </button>
                        <button
                          type="button"
                          onClick={() => void reactivateLabels([id])}
                          disabled={!reactivationEligible || reactivating || checking}
                          title={reactivationBlockedReason || "Etiketi doğrudan yeniden yayınla"}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-800 shadow-sm transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {reactivating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                          Yeniden yayınla
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AdminPanel>
      <div className="rounded-xl border border-slate-200 bg-white p-4">{pagination}</div>
    </div>
  );
}

function AdminPlansPage() {
  const [form, setForm] = useState({ active: true, description: "", name: "" });
  const [refreshKey, setRefreshKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function createPlan(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await apiClient.createAdminPlan(form);
      setForm({ active: true, description: "", name: "" });
      setNotice("Plan oluşturuldu.");
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Plan oluşturulamadı.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader title="Planlar" caption="Müşteri plan kayıtları ve abonelik segmentleri." icon={<CreditCard className="h-5 w-5" />} />
      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}
      <AdminPanel title="Yeni plan" caption="Müşteriye atanabilecek yeni plan tanımı oluşturun.">
        <form className="grid gap-3 p-5 lg:grid-cols-[1fr_1fr_auto_auto]" onSubmit={createPlan}>
          <input
            required
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
            placeholder="Plan adı"
          />
          <input
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
            placeholder="Açıklama"
          />
          <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))}
            />
            Aktif
          </label>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusSquare className="h-4 w-4" />}
            Oluştur
          </button>
        </form>
      </AdminPanel>
      <ResourcePage
        key={refreshKey}
        title="Plan listesi"
        caption="Aktif ve pasif planlar."
        endpoint="/api/admin/plans"
        sections={[
          {
            key: "plans",
            title: "Planlar",
            columns: [
              { label: "Plan", value: (row) => <NameStack title={text(row.name)} detail={text(row.description)} /> },
              { label: "Durum", value: (row) => statusBadge(row.active ? "active" : "inactive") },
              { label: "Oluşturulma", value: (row) => dateText(row.createdAt) },
            ],
          },
        ]}
      />
    </div>
  );
}

function AdminReportSlugPage() {
  const { slug } = useParams();
  if (slug === "order-sync") return <AdminOrderSyncReportPage />;
  if (slug === "error-logs") return <AdminAppErrorLogsPage />;
  if (slug === "activity-logs") return <AdminActivityLogsPage />;
  if (slug === "label-checks") return <AdminLabelChecksPage />;
  if (slug === "faulty-labels") return <AdminFaultyLabelsPage />;
  if (slug === "missing-labels") return <AdminMissingLabelsPage />;
  if (slug === "canceled-labels") return <AdminCanceledLabelsPage />;
  if (slug === "expired-labels") return <AdminExpiredLabelsPage />;
  if (slug === "affiliates") return <AdminAffiliatesPage />;
  if (slug === "commissions") return <AdminCommissionsPage />;
  return <AdminGenericReportPage slug={slug || "daily-sales"} />;
}

function AdminTrackingApiPage() {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [health, setHealth] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    Promise.all([apiClient.resource<Record<string, unknown>>("/api/admin/platforms"), apiClient.resource<Record<string, unknown>>("/api/order-sync/health")])
      .then(([platforms, syncHealth]) => {
        if (!active) return;
        setData(platforms);
        setHealth(syncHealth);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Takip API durumu alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) return <LoadingPanel label="Takip API durumu yükleniyor" />;
  if (error) return <ErrorPanel title="Takip API açılamadı" message={error} />;

  const env = (data?.env || {}) as DataRow;
  const platforms = Array.isArray(health?.platforms) ? (health?.platforms as unknown[]) : [];

  return (
    <div className="space-y-5">
      <AdminPageHeader title="Takip API" caption="Sipariş senkron servisi Cloudflare içine alınmış durumda." icon={<Truck className="h-5 w-5" />} />
      <section className="grid gap-4 md:grid-cols-3">
        <MetricCard label="İç servis" value={health?.externalWorkerRequired ? 0 : 1} icon={<ShieldCheck className="h-5 w-5" />} />
        <MetricCard label="Platform" value={platforms.length} icon={<Store className="h-5 w-5" />} />
        <MetricCard label="Dış servis ihtiyacı" value={health?.externalWorkerRequired ? 1 : 0} icon={<Truck className="h-5 w-5" />} />
      </section>
      <section className="grid gap-5 xl:grid-cols-2">
        <DetailCard
          title="Ortam durumu"
          items={[
            ["eBay ortamı", env.ebayConfigured ? "Hazır" : "Eksik"],
            ["S3 ortamı", env.s3Configured ? "Hazır" : "Eksik"],
            ["Servis", text(health?.service) || "-"],
            ["Zaman", dateText(health?.time)],
          ]}
        />
        <DetailCard title="İç platformlar" items={platforms.map((item, index) => [`#${index + 1}`, text(item)])} />
      </section>
    </div>
  );
}

function releaseAudienceLabel(value: string) {
  if (value === "admin") return "Admin";
  if (value === "customer") return "Customer";
  if (value === "system") return "System";
  return value;
}

function releaseConfidenceLabel(value: string) {
  if (value === "verified") return "Verified";
  if (value === "implemented") return "Implemented";
  if (value === "operational") return "Operational";
  return "Reconstructed";
}

function AdminVersionsPage() {
  const location = useLocation();
  const standalone = location.pathname === "/versions";
  const content = (
    <div className="space-y-5">
      {standalone ? (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <KenpostLogo />
          <div className="flex flex-wrap items-center gap-3">
            <VersionBadge />
            <Link className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800" to="/sign-in">
              Panele dön
            </Link>
          </div>
        </div>
      ) : null}
      <AdminPageHeader
        title={standalone ? "Sürüm Notları" : "Versiyonlar"}
        caption="Sürüm geçmişi, dağıtım versiyonu ve son ürün değişiklikleri."
        eyebrow={standalone ? "KenPost" : "Yönetim"}
        icon={<ShieldCheck className="h-5 w-5" />}
      />
      <section className="grid gap-4 md:grid-cols-3">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-slate-500">Mevcut Ürün Sürümü</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">{getCurrentProductVersionLabel()}</p>
        </section>
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-slate-500">Zaman Çizelgesi Kayıtları</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">{releaseNotes.length}</p>
        </section>
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-slate-500">Son Sürüm</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">{latestRelease.date}</p>
        </section>
      </section>
      <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 p-5 text-white shadow-sm lg:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-950">Son</span>
          <span className="text-sm text-slate-300">{latestRelease.date}</span>
        </div>
        <h2 className="mt-3 text-2xl font-semibold tracking-tight">
          {getCurrentProductVersionLabel()} · {latestRelease.title}
        </h2>
        <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-300">{latestRelease.summary}</p>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {latestRelease.highlights.map((item) => (
            <div key={item} className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-slate-100">
              {item}
            </div>
          ))}
        </div>
      </section>
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-2xl font-semibold text-slate-950">Sürüm Notları</h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">Mevcut sürüm geçmişi aynı eski yapıdan okunur; yeni kayıtlar son sürümden devam eder.</p>
        </div>
        <div className="divide-y divide-slate-100 px-4">
          {releaseNotes.map((entry) => (
            <details key={entry.version} className="group py-1">
              <summary className="flex cursor-pointer list-none flex-col gap-3 py-5 text-left md:flex-row md:items-start md:justify-between">
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-lg font-semibold text-slate-950">v{entry.version}</span>
                    <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                      {releaseConfidenceLabel(entry.confidence)}
                    </span>
                    {entry.audience.map((audience) => (
                      <span key={audience} className="inline-flex rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                        {releaseAudienceLabel(audience)}
                      </span>
                    ))}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-950">{entry.title}</h3>
                    <p className="mt-1 text-sm leading-6 text-slate-500">{entry.summary}</p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2 text-sm text-slate-500">
                  <Clock3 className="h-4 w-4" />
                  {entry.date}
                </div>
              </summary>
              <div className="space-y-5 pb-6">
                <div className="grid gap-3 md:grid-cols-2">
                  {entry.highlights.map((item) => (
                    <div key={item} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                      {item}
                    </div>
                  ))}
                </div>
                <div className="space-y-4">
                  {entry.details.map((section) => (
                    <section key={section.title} className="rounded-xl border border-slate-200 p-4">
                      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                        {section.title.includes("Safety") || section.title.includes("Security") ? <ShieldCheck className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                        {section.title}
                      </div>
                      <ul className="space-y-2 text-sm leading-6 text-slate-700">
                        {section.items.map((item) => (
                          <li key={item} className="flex gap-2">
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              </div>
            </details>
          ))}
        </div>
      </section>
    </div>
  );

  if (standalone) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">{content}</div>
      </main>
    );
  }

  return content;
}

type LabelUploadStatus = "pending" | "uploading" | "saved" | "skipped" | "error";

type LabelUploadFileStatus = {
  alreadyExists?: boolean;
  attempts?: number;
  autoRetryBlocked?: boolean;
  detectedProductId?: string;
  detectedProductName?: string;
  downloadUrl?: string;
  existingState?: string;
  message?: string;
  recordId?: string;
  reactivationBlockedReason?: string;
  reactivationEligible?: boolean;
  republished?: boolean;
  soldAt?: string;
  status: LabelUploadStatus;
  trackingNumber?: string;
};

type LabelUploadLogLevel = "info" | "success" | "warning" | "error";

type LabelUploadLogEntry = {
  detail?: string;
  fileName?: string;
  id: string;
  level: LabelUploadLogLevel;
  message: string;
  timestamp: number;
};

const LABEL_UPLOAD_ENDPOINT = "/api/upload-labels";
const LABEL_UPLOAD_MAX_QUEUE_FILES = 1000;
const LABEL_UPLOAD_MAX_LOOKUP_BATCH = 250;
const LABEL_UPLOAD_MAX_ATTEMPTS = 3;
const LABEL_UPLOAD_AUTO_RETRY_ROUNDS = 1;
const LABEL_UPLOAD_REQUEST_TIMEOUT_MS = 60_000;
const LABEL_UPLOAD_CONCURRENCY_OPTIONS = [2, 4, 6, 10];
const LABEL_UPLOAD_DEFAULT_CONCURRENCY = "4";

function labelUploadFileKey(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function extractLabelUploadTrackingNumber(fileName: string) {
  return fileName.match(/\d{12}/)?.[0] || "";
}

function detectLabelUploadProductFromFileName(fileName: string, products: DataRow[]) {
  const normalized = fileName.toLocaleLowerCase("de-DE").replace(/\s+/g, " ");
  const weightMatch = normalized.match(/(?:kleinpaket|paket)[^0-9]{0,12}([0-9]+(?:[,.][0-9]+)?)\s*kg/i);
  const weightKg = Number.parseFloat(text(weightMatch?.[1]).replace(",", "."));
  if (!Number.isFinite(weightKg)) return null;
  const grams = Math.round(weightKg * 1000);
  const matches = products.filter((product) => Number(product.weightMaxGrams ?? product.weight_max_grams ?? 0) === grams);
  return matches.length === 1 ? matches[0] : null;
}

function labelUploadDuplicateMessage(state: unknown) {
  const normalized = text(state).toLowerCase();
  if (normalized === "assigned") return "Bu takip numarası müşteriye atanmış; yükleme atlanacak.";
  if (normalized === "consumed") return "Bu takip numarası daha önce kullanılmış; yükleme atlanacak.";
  if (normalized === "void") return "Bu takip numarası void kayıtta duruyor; yükleme atlanacak.";
  return "Bu takip numarası stokta zaten var; isterseniz güvenli biçimde yeniden yayınlayabilirsiniz.";
}

function canRepublishLabelUploadStatus(status: LabelUploadFileStatus | undefined) {
  const existingState = text(status?.existingState).toLowerCase();
  if (!status?.alreadyExists) return false;
  if (existingState === "assigned" || existingState === "consumed" || existingState === "expired") return status.reactivationEligible === true;
  return existingState === "available";
}

function labelUploadDuplicateStateLabel(state: unknown) {
  const normalized = text(state).toLowerCase();
  if (normalized === "assigned") return "Atanmış";
  if (normalized === "consumed") return "Tüketilmiş";
  if (normalized === "void") return "Void";
  if (normalized === "expired") return "Süresi bitmiş";
  return "Mevcut";
}

function labelUploadPoolOutcome(status: LabelUploadFileStatus | undefined) {
  const normalized = text(status?.existingState).toLowerCase();
  if (!status?.alreadyExists) return { label: "Havuza eklenecek", tone: "emerald" as const };
  if ((normalized === "consumed" || normalized === "expired") && status.reactivationEligible === true) {
    return { label: "Uygunsa havuza alınacak", tone: "cyan" as const };
  }
  if (normalized === "available") return { label: "Zaten havuzda", tone: "emerald" as const };
  if (normalized === "consumed") return { label: "Havuza eklenmez", tone: "red" as const };
  if (normalized === "void") return { label: "Havuza eklenmez", tone: "red" as const };
  return { label: "Stok değişmez", tone: "slate" as const };
}

function labelUploadRepublishActionText(state: unknown) {
  const normalized = text(state).toLowerCase();
  if (normalized === "consumed" || normalized === "expired") return "Yeniden aktifleştir ve havuza al";
  return "PDF'yi güncelle";
}

function labelUploadStatusLabel(status: LabelUploadStatus) {
  if (status === "pending") return "Bekliyor";
  if (status === "uploading") return "Yükleniyor";
  if (status === "saved") return "Kaydedildi";
  if (status === "skipped") return "Atlandı";
  return "Hata";
}

function labelUploadStatusIcon(status: LabelUploadStatus) {
  if (status === "uploading") return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
  if (status === "saved") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === "skipped") return <AlertTriangle className="h-4 w-4 text-amber-500" />;
  if (status === "error") return <X className="h-4 w-4 text-red-600" />;
  return <Clock3 className="h-4 w-4 text-slate-400" />;
}

function labelUploadStatusBadge(status: LabelUploadStatus) {
  return (
    <span
      className={cx(
        "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
        status === "saved" && "border-emerald-200 bg-emerald-50 text-emerald-700",
        status === "uploading" && "border-blue-200 bg-blue-50 text-blue-700",
        status === "skipped" && "border-amber-200 bg-amber-50 text-amber-700",
        status === "error" && "border-red-200 bg-red-50 text-red-700",
        status === "pending" && "border-slate-200 bg-slate-50 text-slate-600"
      )}
    >
      {labelUploadStatusLabel(status)}
    </span>
  );
}

function labelUploadFileSize(file: File) {
  const kb = file.size / 1024;
  if (kb < 1024) return `${numberText(Math.round(kb))} KB`;
  return `${numberText((kb / 1024).toFixed(1))} MB`;
}

function labelUploadResultRow(payload: DataRow): DataRow {
  const results = Array.isArray(payload.results) ? (payload.results as DataRow[]) : [];
  return results[0] || payload;
}

function labelUploadPayloadMessage(payload: DataRow, fallback: string) {
  return text(payload.message) || text(payload.error) || fallback;
}

function labelUploadLogTime(timestamp: number) {
  try {
    return new Intl.DateTimeFormat("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(new Date(timestamp));
  } catch {
    return "";
  }
}

function labelUploadLogTone(level: LabelUploadLogLevel) {
  if (level === "success") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (level === "warning") return "border-amber-200 bg-amber-50 text-amber-700";
  if (level === "error") return "border-red-200 bg-red-50 text-red-700";
  return "border-slate-200 bg-slate-50 text-slate-600";
}

function AdminLabelUploadPage() {
  const [uploadSearchParams] = useSearchParams();
  const requestedLabelProductId = text(uploadSearchParams.get("labelProductId"));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileStatusesRef = useRef<Map<string, LabelUploadFileStatus>>(new Map());
  const activeControllersRef = useRef<Map<string, AbortController>>(new Map());
  const stopRequestedRef = useRef(false);
  const uploadThrottleUntilRef = useRef(0);
  const [products, setProducts] = useState<DataRow[]>([]);
  const [labelProductId, setLabelProductId] = useState(requestedLabelProductId);
  const [files, setFiles] = useState<File[]>([]);
  const [fileStatuses, setFileStatuses] = useState<Map<string, LabelUploadFileStatus>>(new Map());
  const [selectedRepublishKeys, setSelectedRepublishKeys] = useState<Set<string>>(new Set());
  const [uploadConcurrency, setUploadConcurrency] = useState(LABEL_UPLOAD_DEFAULT_CONCURRENCY);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ completed: number; total: number } | null>(null);
  const [uploadLogs, setUploadLogs] = useState<LabelUploadLogEntry[]>([]);
  const [uploadLogOpen, setUploadLogOpen] = useState(true);
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  useEffect(() => {
    let active = true;
    setLoadingProducts(true);
    apiClient
      .resource<{ products?: DataRow[] }>("/api/admin/labels")
      .then((result) => {
        if (!active) return;
        const nextProducts = result.products || [];
        setProducts(nextProducts);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Etiket ürünleri alınamadı.");
      })
      .finally(() => {
        if (active) setLoadingProducts(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const selectedProduct = products.find((product) => text(product.id) === labelProductId);
  const stats = Array.from(fileStatuses.values()).reduce(
    (sum, item) => {
      sum[item.status] += 1;
      return sum;
    },
    { error: 0, pending: 0, saved: 0, skipped: 0, uploading: 0 } as Record<LabelUploadStatus, number>
  );
  const processableCount = files.filter((file) => {
    const status = fileStatuses.get(labelUploadFileKey(file));
    return !status || status.status === "pending" || status.status === "error";
  }).length;
  const republishableFiles = files.filter((file) => canRepublishLabelUploadStatus(fileStatuses.get(labelUploadFileKey(file))));
  const selectedRepublishCount = republishableFiles.filter((file) => selectedRepublishKeys.has(labelUploadFileKey(file))).length;
  const uploadPercent = uploadProgress?.total ? Math.round((uploadProgress.completed / uploadProgress.total) * 100) : 0;

  function appendUploadLog(level: LabelUploadLogLevel, message: string, detail?: string, fileName?: string) {
    const entry: LabelUploadLogEntry = {
      detail,
      fileName,
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      level,
      message,
      timestamp: Date.now(),
    };
    setUploadLogs((current) => [entry, ...current].slice(0, 120));
    if (level === "error") setUploadLogOpen(true);
  }

  function replaceFileStatuses(updater: (previous: Map<string, LabelUploadFileStatus>) => Map<string, LabelUploadFileStatus>) {
    setFileStatuses((previous) => {
      const next = updater(previous);
      fileStatusesRef.current = next;
      return next;
    });
  }

  function upsertFileStatus(key: string, patch: Partial<LabelUploadFileStatus>) {
    replaceFileStatuses((previous) => {
      const next = new Map(previous);
      const current = next.get(key) || { status: "pending" as const };
      next.set(key, { ...current, ...patch });
      return next;
    });
  }

  async function lookupExistingLabels(trackingNumbers: string[]) {
    const existing = new Map<string, DataRow>();
    const unique = Array.from(new Set(trackingNumbers.filter(Boolean)));
    for (let index = 0; index < unique.length; index += LABEL_UPLOAD_MAX_LOOKUP_BATCH) {
      const batch = unique.slice(index, index + LABEL_UPLOAD_MAX_LOOKUP_BATCH);
      const result = await apiClient.resource<{ labels?: DataRow[] }>(
        `/api/admin/label-sources?trackingNumbers=${encodeURIComponent(batch.join(","))}`
      );
      for (const label of result.labels || []) {
        const trackingNumber = text(label.trackingNumber);
        if (trackingNumber) existing.set(trackingNumber, label);
      }
    }
    return existing;
  }

  async function addFiles(nextFiles: File[]) {
    const pdfFiles = nextFiles.filter((file) => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf"));
    if (pdfFiles.length === 0) {
      setError("Sadece PDF etiket dosyaları yüklenebilir.");
      return;
    }

    setError(null);
    const queuedKeys = new Set(files.map(labelUploadFileKey));
    const uniqueFiles = pdfFiles.filter((file) => !queuedKeys.has(labelUploadFileKey(file)));
    const remainingCapacity = Math.max(0, LABEL_UPLOAD_MAX_QUEUE_FILES - files.length);
    const acceptedFiles = uniqueFiles.slice(0, remainingCapacity);
    const rejectedCount = uniqueFiles.length - acceptedFiles.length;
    if (acceptedFiles.length === 0) {
      setNotice("Seçilen dosyalar zaten kuyrukta veya kuyruk limiti dolu.");
      return;
    }

    const detectedProductsByKey = new Map<string, DataRow>();
    for (const file of acceptedFiles) {
      const product = detectLabelUploadProductFromFileName(file.name, products);
      if (product) detectedProductsByKey.set(labelUploadFileKey(file), product);
    }
    const queuedProductIds = Array.from(fileStatuses.values())
      .map((status) => text(status.detectedProductId))
      .filter(Boolean);
    const distinctDetectedProducts = Array.from(
      new Map(
        [
          ...queuedProductIds.map((productId) => products.find((product) => text(product.id) === productId)),
          ...detectedProductsByKey.values(),
        ]
          .filter((product): product is DataRow => Boolean(product))
          .map((product) => [text(product.id), product])
      ).values()
    );
    if (distinctDetectedProducts.length > 1) {
      setError(
        `Kuyrukta birden fazla etiket ürünü algılandı (${distinctDetectedProducts.map((product) => text(product.name)).join(", ")}). Her ürünü ayrı yükleyin.`
      );
      return;
    }

    const detectedProduct = distinctDetectedProducts[0] || null;
    const effectiveProductId = text(detectedProduct?.id) || labelProductId;
    if (!effectiveProductId) {
      setError("Etiket ürünü dosya adından otomatik tanınamadı. Lütfen ürünü seçip dosyaları yeniden ekleyin.");
      return;
    }
    if (detectedProduct && text(detectedProduct.id) !== labelProductId) {
      setLabelProductId(text(detectedProduct.id));
      setNotice(`${text(detectedProduct.name)} dosya adından otomatik tanındı ve seçildi.`);
    }

    const trackingByKey = new Map<string, string>();
    const trackingNumbers: string[] = [];
    for (const file of acceptedFiles) {
      const trackingNumber = extractLabelUploadTrackingNumber(file.name);
      if (!trackingNumber) continue;
      trackingByKey.set(labelUploadFileKey(file), trackingNumber);
      trackingNumbers.push(trackingNumber);
    }

    let existing = new Map<string, DataRow>();
    try {
      existing = await lookupExistingLabels(trackingNumbers);
    } catch (err) {
      setNotice(`Ön duplicate kontrolü tamamlanamadı; yükleme sırasında tekrar kontrol edilecek. ${err instanceof ApiError ? err.message : ""}`.trim());
      appendUploadLog("warning", "Ön duplicate kontrolü tamamlanamadı.", err instanceof ApiError ? err.message : undefined);
    }

    setFiles((current) => [...current, ...acceptedFiles]);
    replaceFileStatuses((previous) => {
      const next = new Map(previous);
      for (const file of acceptedFiles) {
        const key = labelUploadFileKey(file);
        const trackingNumber = trackingByKey.get(key) || "";
        const existingLabel = trackingNumber ? existing.get(trackingNumber) : null;
        const existingState = text(existingLabel?.status) || (existingLabel ? "available" : "");
        const detectedFileProduct = detectedProductsByKey.get(key) || detectedProduct;
        next.set(key, {
          alreadyExists: Boolean(existingLabel),
          detectedProductId: text(detectedFileProduct?.id) || effectiveProductId,
          detectedProductName: text(detectedFileProduct?.name) || text(products.find((product) => text(product.id) === effectiveProductId)?.name),
          existingState: existingState || undefined,
          message: existingLabel
            ? labelUploadDuplicateMessage(existingState)
            : trackingNumber
              ? undefined
              : "Dosya adında 12 haneli takip no yok; PDF içinden okunacak.",
          status: existingLabel ? "skipped" : "pending",
          recordId: text(existingLabel?.id) || undefined,
          reactivationBlockedReason: text(existingLabel?.reactivationBlockedReason) || undefined,
          reactivationEligible: existingLabel?.reactivationEligible === true,
          soldAt: text(existingLabel?.soldAt || existingLabel?.assignedAt || existingLabel?.externalExportedAt) || undefined,
          trackingNumber: trackingNumber || undefined,
        });
      }
      return next;
    });

    const duplicateCount = acceptedFiles.filter((file) => {
      const trackingNumber = trackingByKey.get(labelUploadFileKey(file));
      return trackingNumber ? existing.has(trackingNumber) : false;
    }).length;
    const consumedDuplicateCount = acceptedFiles.filter((file) => {
      const trackingNumber = trackingByKey.get(labelUploadFileKey(file));
      return trackingNumber ? text(existing.get(trackingNumber)?.status).toLowerCase() === "consumed" : false;
    }).length;
    setNotice(
      `${numberText(acceptedFiles.length)} PDF kuyruğa eklendi${detectedProduct ? `; ürün ${text(detectedProduct.name)} olarak otomatik tanındı` : ""}${duplicateCount ? `, ${numberText(duplicateCount)} mevcut takip no atlandı` : ""}${consumedDuplicateCount ? `. ${numberText(consumedDuplicateCount)} etiket daha önce kullanılmış; PDF güncellense bile satış havuzuna eklenmeyecek` : ""}${rejectedCount ? `, ${numberText(rejectedCount)} dosya limit nedeniyle alınmadı` : ""}.`
    );
    appendUploadLog(
      duplicateCount ? "warning" : "info",
      `${numberText(acceptedFiles.length)} PDF kuyruğa eklendi.`,
      `${duplicateCount ? `${numberText(duplicateCount)} mevcut takip no atlandı. ` : ""}${rejectedCount ? `${numberText(rejectedCount)} dosya limit nedeniyle alınmadı.` : ""}`.trim()
    );
  }

  function handleLabelProductChange(nextProductId: string) {
    const conflicting = Array.from(fileStatuses.values()).find(
      (status) => status.detectedProductId && nextProductId && status.detectedProductId !== nextProductId
    );
    if (conflicting) {
      setError(`Kuyruktaki PDF ${conflicting.detectedProductName || "farklı bir ürün"} olarak tanındı. Yanlış ürün seçilemez; önce kuyruğu temizleyin.`);
      return;
    }
    setError(null);
    setLabelProductId(nextProductId);
  }

  function removeFile(file: File) {
    const key = labelUploadFileKey(file);
    setFiles((current) => current.filter((item) => labelUploadFileKey(item) !== key));
    setSelectedRepublishKeys((current) => {
      const next = new Set(current);
      next.delete(key);
      return next;
    });
    replaceFileStatuses((previous) => {
      const next = new Map(previous);
      next.delete(key);
      return next;
    });
    appendUploadLog("info", "Dosya kuyruktan kaldırıldı.", file.name, file.name);
  }

  function waitForUploadThrottle() {
    return new Promise<boolean>((resolve) => {
      const tick = () => {
        if (stopRequestedRef.current) {
          resolve(false);
          return;
        }
        const remaining = uploadThrottleUntilRef.current - Date.now();
        if (remaining <= 0) {
          resolve(true);
          return;
        }
        window.setTimeout(tick, Math.min(remaining, 250));
      };
      tick();
    });
  }

  function registerTransientBackoff(attempt: number) {
    uploadThrottleUntilRef.current = Math.max(uploadThrottleUntilRef.current, Date.now() + Math.min(5000, 1200 * attempt));
  }

  async function uploadSingleFile(file: File, republishExisting = false) {
    const key = labelUploadFileKey(file);
    const currentStatus = fileStatusesRef.current.get(key);
    if (!republishExisting && (currentStatus?.status === "skipped" || currentStatus?.status === "saved")) return;
    if (stopRequestedRef.current) {
      upsertFileStatus(key, { message: "Yükleme durduruldu.", status: "pending" });
      appendUploadLog("warning", "Dosya beklemeye alındı.", "Yükleme durduruldu.", file.name);
      return;
    }

    let lastError = "Yükleme başarısız.";
    for (let attempt = 1; attempt <= LABEL_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
      if (stopRequestedRef.current) {
        upsertFileStatus(key, { message: "Yükleme durduruldu.", status: "pending" });
        appendUploadLog("warning", "Dosya beklemeye alındı.", "Yükleme durduruldu.", file.name);
        return;
      }

      upsertFileStatus(key, {
        attempts: attempt,
        autoRetryBlocked: false,
        message: attempt > 1 ? `Tekrar deneniyor (${attempt}/${LABEL_UPLOAD_MAX_ATTEMPTS})` : undefined,
        status: "uploading",
      });
      appendUploadLog("info", attempt > 1 ? "Dosya tekrar deneniyor." : "Dosya yükleniyor.", `Deneme ${attempt}/${LABEL_UPLOAD_MAX_ATTEMPTS}`, file.name);

      let requestTimedOut = false;
      try {
        const ready = await waitForUploadThrottle();
        if (!ready) {
          upsertFileStatus(key, { message: "Yükleme durduruldu.", status: "pending" });
          appendUploadLog("warning", "Dosya beklemeye alındı.", "Yükleme durduruldu.", file.name);
          return;
        }

        const formData = new FormData();
        formData.set("labelProductId", labelProductId);
        if (republishExisting) {
          formData.set("republishExisting", "1");
          if (text(currentStatus?.existingState).toLowerCase() === "assigned") formData.set("reactivateAssigned", "1");
          if (text(currentStatus?.existingState).toLowerCase() === "consumed") formData.set("reactivateConsumed", "1");
          if (text(currentStatus?.existingState).toLowerCase() === "expired") formData.set("reactivateExpired", "1");
        }
        formData.append("file", file, file.name);
        const controller = new AbortController();
        activeControllersRef.current.set(key, controller);
        const timeoutId = window.setTimeout(() => {
          requestTimedOut = true;
          controller.abort();
        }, LABEL_UPLOAD_REQUEST_TIMEOUT_MS);
        const response = await fetch(LABEL_UPLOAD_ENDPOINT, {
          body: formData,
          credentials: "same-origin",
          method: "POST",
          signal: controller.signal,
        }).finally(() => {
          window.clearTimeout(timeoutId);
          activeControllersRef.current.delete(key);
        });
        const responseText = await response.text();
        const payload = (responseText ? JSON.parse(responseText) : {}) as DataRow;
        const resultRow = labelUploadResultRow(payload);

        if (!response.ok || payload.ok === false) {
          lastError = labelUploadPayloadMessage(payload, responseText || "Yükleme başarısız.");
          const retryable = response.status === 429 || response.status >= 500;
          if (retryable && attempt < LABEL_UPLOAD_MAX_ATTEMPTS) {
            registerTransientBackoff(attempt);
            appendUploadLog("warning", "Geçici hata, tekrar denenecek.", `HTTP ${response.status}: ${lastError}`, file.name);
            continue;
          }
          upsertFileStatus(key, { autoRetryBlocked: true, message: lastError, status: "error" });
          appendUploadLog("error", "Yükleme hatası.", lastError, file.name);
          return;
        }

        const trackingNumber = text(resultRow.sendungsnr) || text(resultRow.trackingNumber) || currentStatus?.trackingNumber;
        if (resultRow.duplicate || resultRow.action === "skipped" || Number(payload.skipped || 0) > 0) {
          const existingState = text(resultRow.duplicateState) || text(resultRow.status) || "available";
          upsertFileStatus(key, {
            alreadyExists: true,
            downloadUrl: text(resultRow.downloadUrl) || undefined,
            existingState,
            message: labelUploadPayloadMessage(resultRow, labelUploadDuplicateMessage(existingState)),
            recordId: text(resultRow.recordId) || currentStatus?.recordId,
            reactivationBlockedReason: text(resultRow.reactivationBlockedReason) || currentStatus?.reactivationBlockedReason,
            reactivationEligible: resultRow.reactivationEligible === true || currentStatus?.reactivationEligible === true,
            soldAt: text(resultRow.soldAt) || currentStatus?.soldAt,
            status: "skipped",
            trackingNumber: trackingNumber || undefined,
          });
          appendUploadLog("warning", "Mevcut takip no atlandı.", `${trackingNumber || file.name} · ${labelUploadDuplicateStateLabel(existingState)}`, file.name);
          return;
        }

        if (!trackingNumber) {
          upsertFileStatus(key, { message: "PDF içinden takip numarası okunamadı.", status: "error" });
          appendUploadLog("error", "Takip numarası okunamadı.", "PDF içinden takip no bulunamadı.", file.name);
          return;
        }

        if (resultRow.action === "republished" || resultRow.republished === true || Number(payload.republished || 0) > 0) {
          const existingState = text(resultRow.duplicateState) || currentStatus?.existingState || "available";
          const resultingState = text(resultRow.status).toLowerCase();
          const resultMessage =
            resultRow.action === "reactivated" || (resultingState === "available" && ["consumed", "expired"].includes(existingState.toLowerCase()))
              ? "Etiket yeniden aktifleştirildi ve satış havuzuna eklendi."
              : existingState.toLowerCase() === "consumed"
                ? "PDF güncellendi; etiket daha önce kullanıldığı için satış havuzuna eklenmedi."
                : "PDF güncellendi; etiket zaten satış havuzundaydı.";
          upsertFileStatus(key, {
            alreadyExists: true,
            downloadUrl: text(resultRow.downloadUrl) || currentStatus?.downloadUrl,
            existingState,
            message: resultMessage,
            recordId: text(resultRow.recordId) || currentStatus?.recordId,
            republished: true,
            soldAt: text(resultRow.soldAt) || currentStatus?.soldAt,
            status: "saved",
            trackingNumber,
          });
          appendUploadLog("success", resultMessage, trackingNumber, file.name);
          return;
        }

        upsertFileStatus(key, {
          downloadUrl: text(resultRow.downloadUrl) || undefined,
          message: labelUploadPayloadMessage(resultRow, "Yeni etiket kaydedildi."),
          republished: false,
          status: "saved",
          trackingNumber,
        });
        appendUploadLog("success", "Etiket kaydedildi.", trackingNumber, file.name);
        return;
      } catch (err) {
        if (stopRequestedRef.current && err instanceof DOMException && err.name === "AbortError") {
          upsertFileStatus(key, { message: "Yükleme durduruldu.", status: "pending" });
          appendUploadLog("warning", "Dosya beklemeye alındı.", "Yükleme durduruldu.", file.name);
          return;
        }
        if (requestTimedOut) {
          lastError = "Yükleme isteği 60 saniyede tamamlanmadı. Dosya otomatik tekrar gönderilmedi; envanteri yenileyip sonucu kontrol edin.";
          upsertFileStatus(key, { autoRetryBlocked: true, message: lastError, status: "error" });
          appendUploadLog("error", "Yükleme zaman aşımına uğradı.", lastError, file.name);
          return;
        }
        lastError = err instanceof Error ? err.message : "Ağ hatası.";
        if (attempt < LABEL_UPLOAD_MAX_ATTEMPTS) {
          registerTransientBackoff(attempt);
          appendUploadLog("warning", "Ağ hatası, tekrar denenecek.", lastError, file.name);
          continue;
        }
        upsertFileStatus(key, { message: lastError, status: "error" });
        appendUploadLog("error", "Ağ hatası.", lastError, file.name);
        return;
      }
    }
  }

  async function processFiles(targetFiles: File[], republishKeys = new Set<string>()) {
    let completed = 0;
    let nextIndex = 0;
    const configured = Number(uploadConcurrency);
    const workerCount = Math.min(Number.isFinite(configured) && configured > 0 ? Math.trunc(configured) : Number(LABEL_UPLOAD_DEFAULT_CONCURRENCY), targetFiles.length);
    const worker = async () => {
      while (!stopRequestedRef.current) {
        const index = nextIndex;
        nextIndex += 1;
        if (index >= targetFiles.length) return;
        const file = targetFiles[index];
        await uploadSingleFile(file, republishKeys.has(labelUploadFileKey(file)));
        completed += 1;
        setUploadProgress({ completed, total: targetFiles.length });
        if (completed % 25 === 0) await new Promise((resolve) => window.setTimeout(resolve, 150));
      }
    };
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
  }

  async function startUpload(targetFiles?: File[], republishKeys = new Set<string>()) {
    if (!labelProductId) {
      setError("Önce etiket ürünü seçin.");
      return;
    }
    const filesToProcess =
      targetFiles ||
      files.filter((file) => {
        const status = fileStatuses.get(labelUploadFileKey(file));
        return !status || status.status === "pending" || status.status === "error";
      });
    if (filesToProcess.length === 0) {
      setNotice("Yüklenecek bekleyen veya hatalı dosya yok.");
      return;
    }

    setError(null);
    setNotice(null);
    stopRequestedRef.current = false;
    uploadThrottleUntilRef.current = 0;
    activeControllersRef.current.clear();
    setIsStopping(false);
    setIsUploading(true);
    setUploadProgress({ completed: 0, total: filesToProcess.length });
    setUploadLogOpen(true);
    appendUploadLog(
      "info",
      "Yükleme başladı.",
      `${numberText(filesToProcess.length)} dosya · ${numberText(uploadConcurrency)} paralel işlem`
    );

    await processFiles(filesToProcess, republishKeys);
    if (!stopRequestedRef.current) {
      for (let round = 1; round <= LABEL_UPLOAD_AUTO_RETRY_ROUNDS; round += 1) {
        const failed = filesToProcess.filter((file) => {
          const status = fileStatusesRef.current.get(labelUploadFileKey(file));
          return status?.status === "error" && status.autoRetryBlocked !== true;
        });
        if (failed.length === 0) break;
        setNotice(`${numberText(failed.length)} hatalı dosya otomatik tekrar deneniyor.`);
        appendUploadLog("warning", "Hatalı dosyalar otomatik tekrar deneniyor.", `${numberText(failed.length)} dosya`);
        setUploadProgress({ completed: 0, total: failed.length });
        await processFiles(failed, republishKeys);
        if (stopRequestedRef.current) break;
      }
    }

    const stopped = stopRequestedRef.current;
    stopRequestedRef.current = false;
    activeControllersRef.current.clear();
    setIsStopping(false);
    setIsUploading(false);
    setUploadProgress(null);
    const finalStatuses = Array.from(fileStatusesRef.current.values());
    const saved = finalStatuses.filter((item) => item.status === "saved").length;
    const skipped = finalStatuses.filter((item) => item.status === "skipped").length;
    const failed = finalStatuses.filter((item) => item.status === "error").length;
    const poolAdditions = finalStatuses.filter((item) => {
      if (item.status !== "saved") return false;
      if (!item.alreadyExists) return true;
      return ["consumed", "expired"].includes(text(item.existingState).toLowerCase());
    }).length;
    setNotice(
      stopped
        ? "Yükleme durduruldu. Bekleyen dosyalar kuyrukta kaldı."
        : `${numberText(saved)} işlem tamamlandı, ${numberText(poolAdditions)} etiket havuza eklendi, ${numberText(skipped)} atlandı, ${numberText(failed)} hatalı.`
    );
    appendUploadLog(
      failed > 0 ? "error" : stopped ? "warning" : "success",
      stopped ? "Yükleme durduruldu." : "Yükleme tamamlandı.",
      `${numberText(saved)} kaydedildi · ${numberText(skipped)} atlandı · ${numberText(failed)} hatalı`
    );
  }

  function stopUpload() {
    if (!isUploading || isStopping) return;
    stopRequestedRef.current = true;
    setIsStopping(true);
    for (const controller of activeControllersRef.current.values()) controller.abort();
    appendUploadLog("warning", "Durdurma istendi.", "Aktif istekler iptal ediliyor.");
  }

  function retryFailed() {
    const failed = files.filter((file) => fileStatusesRef.current.get(labelUploadFileKey(file))?.status === "error");
    if (failed.length === 0) return;
    replaceFileStatuses((previous) => {
      const next = new Map(previous);
      for (const file of failed) {
        const key = labelUploadFileKey(file);
        next.set(key, { ...(next.get(key) || { status: "pending" as const }), autoRetryBlocked: false, message: undefined, status: "pending" });
      }
      return next;
    });
    appendUploadLog("info", "Hatalı dosyalar tekrar kuyruğa alındı.", `${numberText(failed.length)} dosya`);
    void startUpload(failed);
  }

  async function republishFiles(targetFiles: File[]) {
    if (!labelProductId) {
      setError("Yeniden yayınlamak için etiket ürünü seçili olmalı.");
      return;
    }
    const eligible = targetFiles.filter((file) => canRepublishLabelUploadStatus(fileStatusesRef.current.get(labelUploadFileKey(file))));
    if (eligible.length === 0) {
      setError("Yeniden yayınlanabilecek mevcut PDF veya admin kuralına uygun eski/temiz etiket seçilmedi.");
      return;
    }
    const poolReturnCount = eligible.filter((file) => {
      const state = text(fileStatusesRef.current.get(labelUploadFileKey(file))?.existingState).toLowerCase();
      return state === "consumed" || state === "expired";
    }).length;
    const confirmed = await askConfirmation({
      title: eligible.length === 1 ? "Etiket işlemi onaylansın mı?" : "Etiket işlemleri onaylansın mı?",
      description: `${numberText(eligible.length)} PDF güncellenecek. ${numberText(poolReturnCount)} etiket güvenlik kontrolü uygunsa havuza alınacak.`,
      details: [
        "Müşteri hesabında gönderilmemiş olarak duran atanmış etiket yeniden yüklenemez.",
        "Kullanılmış etiket yalnız kullanımından en az 3 ay geçmişse ve DHL sonucu açık/hareketsizse havuza alınır.",
        "PDF oluşturma tarihi artık etikete otomatik altı aylık süre veya karantina uygulamaz.",
      ],
      confirmLabel: eligible.length === 1 ? "İşlemi uygula" : "Seçilenleri işle",
    });
    if (!confirmed) return;

    const republishKeys = new Set(eligible.map(labelUploadFileKey));
    setSelectedRepublishKeys((current) => {
      const next = new Set(current);
      for (const key of republishKeys) next.delete(key);
      return next;
    });
    await startUpload(eligible, republishKeys);
  }

  function toggleRepublishSelection(file: File, checked: boolean) {
    const key = labelUploadFileKey(file);
    if (!canRepublishLabelUploadStatus(fileStatuses.get(key))) return;
    setSelectedRepublishKeys((current) => {
      const next = new Set(current);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function toggleAllRepublishable(checked: boolean) {
    setSelectedRepublishKeys(checked ? new Set(republishableFiles.map(labelUploadFileKey)) : new Set());
  }

  function clearQueue() {
    if (isUploading) return;
    setFiles([]);
    setSelectedRepublishKeys(new Set());
    replaceFileStatuses(() => new Map());
    setUploadProgress(null);
    setNotice("Yükleme kuyruğu temizlendi.");
    setError(null);
    setUploadLogs([]);
  }

  function handleFileInput(event: ChangeEvent<HTMLInputElement>) {
    void addFiles(Array.from(event.target.files || []));
    event.target.value = "";
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Etiket kaynağı yükle"
        caption="Müşterilerin kullanacağı PDF etiket stoklarını ürün bazında kuyruğa alın, duplicate kontrolünden geçirin ve toplu yükleyin."
        icon={<Package className="h-5 w-5" />}
        action={
          <Link
            to={requestedLabelProductId ? `/admin/label-sources?labelProductId=${encodeURIComponent(requestedLabelProductId)}&status=all` : "/admin/label-sources"}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Envantere dön
          </Link>
        }
      />
      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} tone={stats.error > 0 ? "warning" : "success"} /> : null}
      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <AdminStatCard label="Kuyruk" value={files.length} tone="slate" icon={<Upload className="h-5 w-5" />} />
        <AdminStatCard label="Kaydedildi" value={stats.saved} tone="emerald" icon={<CheckCircle2 className="h-5 w-5" />} />
        <AdminStatCard label="Atlandı" value={stats.skipped} tone="amber" icon={<AlertTriangle className="h-5 w-5" />} />
        <AdminStatCard label="Hatalı" value={stats.error} tone="red" icon={<X className="h-5 w-5" />} />
        <AdminStatCard label="Bekleyen" value={stats.pending + stats.uploading} tone="cyan" icon={<Clock3 className="h-5 w-5" />} />
      </section>

      <AdminPanel title="Yükleme ayarları" caption="Ürün dosya adından otomatik tanınır; gerekirse manuel seçim yapabilirsiniz. PDF içeriği seçime uymuyorsa yükleme engellenir.">
        <div className="grid gap-4 p-5 lg:grid-cols-[minmax(0,1fr)_220px]">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm font-semibold text-slate-700">
              Etiket ürünü
              <select
                required
                value={labelProductId}
                disabled={isUploading || loadingProducts}
                onChange={(event) => handleLabelProductChange(event.target.value)}
                className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400 disabled:opacity-60"
              >
                <option value="">Otomatik tanı veya ürün seç</option>
                {products.map((product) => (
                  <option key={text(product.id)} value={text(product.id)}>
                    {text(product.name)} · {text(product.carrier)} · {numberText(product.weightMinGrams)}-{numberText(product.weightMaxGrams)} g
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-semibold text-slate-700">
              Paralel yükleme
              <select
                value={uploadConcurrency}
                disabled={isUploading}
                onChange={(event) => setUploadConcurrency(event.target.value)}
                className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400 disabled:opacity-60"
              >
                {LABEL_UPLOAD_CONCURRENCY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option} dosya aynı anda
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
            <InfoLine label="Mevcut stok" value={numberText(selectedProduct?.availableCount)} />
            <InfoLine label="Toplam kaynak" value={numberText(selectedProduct?.sourceCount)} />
            <InfoLine label="Atanmış" value={numberText(selectedProduct?.assignedCount)} />
            <InfoLine label="Hatalı" value={numberText(selectedProduct?.checkErrorCount)} />
          </div>
        </div>
      </AdminPanel>

      <AdminPanel title="PDF kuyruğu" caption="Dosyaları bu alana sürükleyin veya tıklayıp seçin. Bir müşteride gönderilmemiş olarak duran etiket yeniden yüklenemez. En az 3 ay önce kullanılmış, DHL'de hareketi olmayan açık etiketler yeniden satış havuzuna alınabilir; DHL'de kullanılmış veya teslim edilmiş etiketler alınmaz.">
        <input ref={fileInputRef} type="file" multiple accept="application/pdf,.pdf" onChange={handleFileInput} className="hidden" />
        <div className="p-5">
          <button
            type="button"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              if (!isUploading) setIsDragging(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setIsDragging(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragging(false);
              if (!isUploading) void addFiles(Array.from(event.dataTransfer.files || []));
            }}
            className={cx(
              "flex min-h-44 w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 text-center transition",
              isDragging ? "border-cyan-400 bg-cyan-50 text-cyan-900" : "border-slate-200 bg-slate-50 text-slate-600",
              isUploading ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-slate-300 hover:bg-white"
            )}
          >
            <Upload className="mb-3 h-10 w-10 text-slate-400" />
            <span className="text-base font-semibold text-slate-950">PDF etiketleri buraya bırakın</span>
            <span className="mt-1 text-sm text-slate-500">En fazla {numberText(LABEL_UPLOAD_MAX_QUEUE_FILES)} dosya kuyruğa alınır; yükleme sırasında dosya bazında sonuç görünür.</span>
          </button>

          {uploadProgress ? (
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2 font-semibold">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {numberText(uploadProgress.completed)} / {numberText(uploadProgress.total)} dosya işlendi
                </span>
                <span className="font-semibold">{numberText(uploadPercent)}%</span>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-slate-950 transition-all" style={{ width: `${uploadPercent}%` }} />
              </div>
            </div>
          ) : null}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={isUploading || processableCount === 0 || !labelProductId}
              onClick={() => void startUpload()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {isUploading ? "Yükleniyor" : `Yüklemeyi başlat (${numberText(processableCount)})`}
            </button>
            <button
              type="button"
              disabled={!isUploading}
              onClick={stopUpload}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 shadow-sm transition hover:bg-red-100 disabled:opacity-60"
            >
              {isStopping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
              Durdur
            </button>
            <button
              type="button"
              disabled={isUploading || stats.error === 0}
              onClick={retryFailed}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
            >
              <History className="h-4 w-4" />
              Hatalıları tekrar dene ({numberText(stats.error)})
            </button>
            <button
              type="button"
              disabled={isUploading || selectedRepublishCount === 0}
              onClick={() => void republishFiles(republishableFiles.filter((file) => selectedRepublishKeys.has(labelUploadFileKey(file))))}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-2.5 text-sm font-semibold text-cyan-800 shadow-sm transition hover:bg-cyan-100 disabled:opacity-60"
            >
              <RefreshCw className="h-4 w-4" />
              PDF güncelle / havuza al ({numberText(selectedRepublishCount)})
            </button>
            <button
              type="button"
              disabled={isUploading || files.length === 0}
              onClick={clearQueue}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
            >
              <Trash2 className="h-4 w-4" />
              Kuyruğu temizle
            </button>
          </div>
        </div>
      </AdminPanel>

      {files.length > 0 ? (
        <AdminPanel title="Dosya sonuçları" count={files.length}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1360px] divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                <tr>
                  <th className="w-14 px-5 py-3.5">
                    <input
                      type="checkbox"
                      checked={republishableFiles.length > 0 && selectedRepublishCount === republishableFiles.length}
                      disabled={isUploading || republishableFiles.length === 0}
                      onChange={(event) => toggleAllRepublishable(event.target.checked)}
                      aria-label="PDF'i güncellenebilir veya havuza alınabilir etiketlerin tümünü seç"
                      className="h-4 w-4 rounded border-slate-300 text-slate-950 disabled:opacity-40"
                    />
                  </th>
                  <th className="px-5 py-3.5">Dosya</th>
                  <th className="px-5 py-3.5">Duplicate</th>
                  <th className="px-5 py-3.5">Havuz etkisi</th>
                  <th className="px-5 py-3.5">Satış tarihi</th>
                  <th className="px-5 py-3.5">Durum</th>
                  <th className="px-5 py-3.5">Mesaj</th>
                  <th className="px-5 py-3.5">Takip</th>
                  <th className="px-5 py-3.5">PDF</th>
                  <th className="px-5 py-3.5 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {files.map((file) => {
                  const key = labelUploadFileKey(file);
                  const status = fileStatuses.get(key) || { status: "pending" as const };
                  const trackingUrl = shipmentTrackingHref(text(status.trackingNumber));
                  const canRepublish = canRepublishLabelUploadStatus(status);
                  const poolOutcome = labelUploadPoolOutcome(status);
                  return (
                    <tr key={key} className="align-top transition hover:bg-slate-50/70">
                      <td className="px-5 py-4">
                        <input
                          type="checkbox"
                          checked={selectedRepublishKeys.has(key)}
                          disabled={isUploading || !canRepublish}
                          onChange={(event) => toggleRepublishSelection(file, event.target.checked)}
                          aria-label={`${file.name}: ${labelUploadRepublishActionText(status.existingState)} işlemi için seç`}
                          title={canRepublish ? labelUploadRepublishActionText(status.existingState) : status.reactivationBlockedReason || "Bu etiket güvenli biçimde güncellenemez veya havuza alınamaz"}
                          className="h-4 w-4 rounded border-slate-300 text-slate-950 disabled:opacity-40"
                        />
                      </td>
                      <td className="px-5 py-4">
                        <NameStack
                          title={file.name}
                          detail={[labelUploadFileSize(file), status.detectedProductName ? `Ürün: ${status.detectedProductName}` : ""].filter(Boolean).join(" · ")}
                        />
                      </td>
                      <td className="px-5 py-4">
                        {status.alreadyExists ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {labelUploadDuplicateStateLabel(status.existingState)}
                          </span>
                        ) : (
                          <span className="text-xs font-semibold text-slate-400">Yeni</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={cx(
                            "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
                            poolOutcome.tone === "emerald" && "border-emerald-200 bg-emerald-50 text-emerald-700",
                            poolOutcome.tone === "cyan" && "border-cyan-200 bg-cyan-50 text-cyan-700",
                            poolOutcome.tone === "red" && "border-red-200 bg-red-50 text-red-700",
                            poolOutcome.tone === "slate" && "border-slate-200 bg-slate-50 text-slate-600"
                          )}
                        >
                          {poolOutcome.label}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-700">
                        {status.soldAt ? orderDateText(status.soldAt) : "-"}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          {labelUploadStatusIcon(status.status)}
                          {labelUploadStatusBadge(status.status)}
                        </div>
                      </td>
                      <td className="max-w-sm px-5 py-4 text-slate-600">
                        <span className="line-clamp-2">{status.message || "-"}</span>
                      </td>
                      <td className="px-5 py-4">
                        {trackingUrl ? (
                          <a href={trackingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-slate-950 hover:text-cyan-700">
                            {text(status.trackingNumber)}
                            <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                          </a>
                        ) : (
                          text(status.trackingNumber) || "-"
                        )}
                      </td>
                      <td className="px-5 py-4">
                        {status.downloadUrl ? (
                          <a href={status.downloadUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                            <FileText className="h-3.5 w-3.5" />
                            Aç
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {canRepublish ? (
                            <button
                              type="button"
                              disabled={isUploading}
                              onClick={() => void republishFiles([file])}
                              className="inline-flex items-center justify-center rounded-lg border border-cyan-200 bg-cyan-50 p-2 text-cyan-700 shadow-sm transition hover:bg-cyan-100 disabled:opacity-50"
                              aria-label={`${file.name}: ${labelUploadRepublishActionText(status.existingState)}`}
                              title={labelUploadRepublishActionText(status.existingState)}
                            >
                              <RefreshCw className="h-3.5 w-3.5" />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            disabled={isUploading}
                            onClick={() => removeFile(file)}
                            className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white p-2 text-slate-500 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                            aria-label={`${file.name} dosyasını kuyruktan kaldır`}
                            title="Kuyruktan kaldır"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AdminPanel>
      ) : null}

      {uploadLogs.length > 0 || isUploading ? (
        <aside className="fixed bottom-5 right-5 z-40 w-[min(430px,calc(100vw-2rem))] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-950 px-4 py-3 text-white">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold">
                {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Yükleme günlüğü
              </div>
              <div className="mt-0.5 text-xs text-slate-300">
                {isUploading
                  ? `${numberText(uploadProgress?.completed)} / ${numberText(uploadProgress?.total)} dosya işlendi`
                  : stats.error > 0
                    ? "Kontrol gerekli"
                    : stats.saved || stats.skipped
                      ? "Son işlem tamamlandı"
                      : "Hazır"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setUploadLogOpen((current) => !current)}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/10 text-white transition hover:bg-white/20"
              aria-label={uploadLogOpen ? "Yükleme günlüğünü küçült" : "Yükleme günlüğünü aç"}
            >
              <ChevronDown className={cx("h-4 w-4 transition", uploadLogOpen ? "" : "rotate-180")} />
            </button>
          </div>

          {uploadLogOpen ? (
            <div className="p-4">
              {uploadProgress ? (
                <div className="mb-3">
                  <div className="mb-1 flex items-center justify-between text-xs font-semibold text-slate-500">
                    <span>İlerleme</span>
                    <span>{numberText(uploadPercent)}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-slate-950 transition-all" style={{ width: `${uploadPercent}%` }} />
                  </div>
                </div>
              ) : null}

              <div className="mb-3 grid grid-cols-4 gap-2 text-center text-xs">
                <div className="rounded-lg bg-emerald-50 px-2 py-2 font-semibold text-emerald-700">
                  <div>{numberText(stats.saved)}</div>
                  <div className="font-medium">Kayıt</div>
                </div>
                <div className="rounded-lg bg-amber-50 px-2 py-2 font-semibold text-amber-700">
                  <div>{numberText(stats.skipped)}</div>
                  <div className="font-medium">Atlandı</div>
                </div>
                <div className="rounded-lg bg-red-50 px-2 py-2 font-semibold text-red-700">
                  <div>{numberText(stats.error)}</div>
                  <div className="font-medium">Hata</div>
                </div>
                <div className="rounded-lg bg-slate-50 px-2 py-2 font-semibold text-slate-700">
                  <div>{numberText(stats.pending + stats.uploading)}</div>
                  <div className="font-medium">Bekleyen</div>
                </div>
              </div>

              <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                {uploadLogs.length > 0 ? (
                  uploadLogs.map((entry) => (
                    <div key={entry.id} className={cx("rounded-lg border px-3 py-2 text-xs", labelUploadLogTone(entry.level))}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold">{entry.message}</div>
                          {entry.fileName ? <div className="mt-0.5 truncate text-[11px] opacity-80">{entry.fileName}</div> : null}
                          {entry.detail ? <div className="mt-1 break-words text-[11px] opacity-90">{entry.detail}</div> : null}
                        </div>
                        <span className="shrink-0 text-[11px] opacity-70">{labelUploadLogTime(entry.timestamp)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">
                    Henüz kayıt yok. Yüklemeyi başlatınca dosya bazında log burada görünecek.
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </aside>
      ) : null}
      {confirmationDialog}
    </div>
  );
}

function AdminCustomerPricesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedCustomerId = searchParams.get("customerId") || "";
  const [customers, setCustomers] = useState<DataRow[]>([]);
  const [customerId, setCustomerId] = useState(requestedCustomerId);
  const [prices, setPrices] = useState<Array<{ active: boolean; basePrice: number; labelProductId: string; labelProductName: string; price: number | null }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    apiClient
      .resource<{ customers?: DataRow[] }>("/api/admin/customers")
      .then((result) => {
        if (!active) return;
        const nextCustomers = result.customers || [];
        const requestedExists = nextCustomers.some((customer) => text(customer.id) === requestedCustomerId);
        setCustomers(nextCustomers);
        setCustomerId(requestedExists ? requestedCustomerId : text(nextCustomers[0]?.id));
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Müşteriler alınamadı.");
      });
    return () => {
      active = false;
    };
  }, [requestedCustomerId]);

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    setError(null);
    apiClient
      .adminCustomerLabelPrices(customerId)
      .then((result) => {
        if (active) setPrices(result.prices.map((item) => ({ ...item, labelProductName: item.labelProductName || "-", labelProductId: item.labelProductId })));
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Müşteri fiyatları alınamadı.");
      });
    return () => {
      active = false;
    };
  }, [customerId]);

  function updatePrice(labelProductId: string, value: string) {
    setPrices((current) =>
      current.map((item) => (item.labelProductId === labelProductId ? { ...item, price: value === "" ? null : Number(value) } : item))
    );
  }

  function selectCustomer(nextCustomerId: string) {
    setCustomerId(nextCustomerId);
    const next = new URLSearchParams(searchParams);
    if (nextCustomerId) next.set("customerId", nextCustomerId);
    else next.delete("customerId");
    setSearchParams(next, { replace: true });
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.saveAdminCustomerLabelPrices({
        customerId,
        items: prices.map((item) => ({ active: item.active, labelProductId: item.labelProductId, price: item.price })),
      });
      setNotice(`Fiyatlar kaydedildi. Yeni: ${result.createdCount}, güncellenen: ${result.updatedCount}, silinen: ${result.deletedCount}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Fiyatlar kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader title="Müşteri fiyatları" caption="Etiket ürünleri için müşteri bazlı fiyat tanımları." icon={<Tags className="h-5 w-5" />} />
      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}
      <AdminPanel title="Fiyat matrisi" caption="Seçilen müşteri için ürün bazlı özel fiyatları düzenleyin.">
        <form className="space-y-4 p-5" onSubmit={save}>
          <select
            required
            value={customerId}
            onChange={(event) => selectCustomer(event.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
          >
            {customers.map((customer) => (
              <option key={text(customer.id)} value={text(customer.id)}>
                {displayRowName(customer)} · {text(customer.email)}
              </option>
            ))}
          </select>
          <div className="grid gap-3">
            {prices.map((item) => (
              <label key={item.labelProductId} className="grid gap-2 rounded-xl border border-slate-100 bg-slate-50 p-3 sm:grid-cols-[1fr_160px] sm:items-center">
                <span>
                  <span className="block text-sm font-semibold text-slate-900">{item.labelProductName}</span>
                  <span className="block text-xs text-slate-500">Baz fiyat: {moneyText(item.basePrice, "EUR")}</span>
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.price ?? ""}
                  onChange={(event) => updatePrice(item.labelProductId, event.target.value)}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                  placeholder="Baz fiyat"
                />
              </label>
            ))}
          </div>
          <button
            type="submit"
            disabled={saving || !customerId}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Kaydet
          </button>
        </form>
      </AdminPanel>
    </div>
  );
}

function AdminCreateCustomerPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", firstName: "", lastName: "", companyName: "", password: "", confirmPassword: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (form.password && form.password !== form.confirmPassword) {
      setError("Şifreler eşleşmiyor.");
      return;
    }
    setSaving(true);
    try {
      await apiClient.createAdminCustomer({
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        companyName: form.companyName,
        password: form.password || undefined,
      });
      navigate("/admin/customers");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Müşteri oluşturulamadı.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="p-5 lg:p-6">
        <Link className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900" to="/admin/customers">
          <ArrowLeft className="h-4 w-4" />
          Müşterilere dön
        </Link>
        <div className="flex min-w-0 items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-cyan-200">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-950">Yeni müşteri</h1>
            <p className="mt-1 text-sm text-slate-500">Cloudflare D1 içinde müşteri hesabı oluşturur.</p>
          </div>
        </div>
        </div>
      </section>
      {error ? <ErrorBanner message={error} /> : null}
      <AdminPanel title="Hesap bilgileri" caption="Müşteri hesabını oluşturmak için temel bilgileri girin.">
        <form className="grid gap-4 p-5 lg:grid-cols-2" onSubmit={submit}>
          <ProfileInput label="E-posta" value={form.email} onChange={(value) => setForm((current) => ({ ...current, email: value }))} />
          <ProfileInput label="Firma" value={form.companyName} onChange={(value) => setForm((current) => ({ ...current, companyName: value }))} />
          <ProfileInput label="Ad" value={form.firstName} onChange={(value) => setForm((current) => ({ ...current, firstName: value }))} />
          <ProfileInput label="Soyad" value={form.lastName} onChange={(value) => setForm((current) => ({ ...current, lastName: value }))} />
          <ProfileInput label="Başlangıç şifresi" type="password" value={form.password} onChange={(value) => setForm((current) => ({ ...current, password: value }))} />
          <ProfileInput label="Şifre tekrar" type="password" value={form.confirmPassword} onChange={(value) => setForm((current) => ({ ...current, confirmPassword: value }))} />
          <button
            type="submit"
            disabled={saving}
            className="inline-flex w-fit items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60 lg:col-span-2"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusSquare className="h-4 w-4" />}
            Müşteri oluştur
          </button>
        </form>
      </AdminPanel>
    </div>
  );
}

function AdminLabelProductFormPage({ mode }: { mode: "create" | "edit" }) {
  const { labelProductId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ active: "true", basePrice: "", carrier: "dhl", name: "", weightMaxGrams: "", weightMinGrams: "" });
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "edit" || !labelProductId) return;
    let active = true;
    setLoading(true);
    apiClient
      .adminLabelProduct(labelProductId)
      .then((result) => {
        if (!active) return;
        const product = result.product;
        setForm({
          active: product.active === false ? "false" : "true",
          basePrice: text(product.basePrice),
          carrier: text(product.carrier) || "dhl",
          name: text(product.name),
          weightMaxGrams: text(product.weightMaxGrams),
          weightMinGrams: text(product.weightMinGrams),
        });
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Etiket ürünü alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [labelProductId, mode]);

  function setField(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      active: form.active === "true",
      basePrice: Number(form.basePrice),
      carrier: form.carrier,
      name: form.name,
      weightMaxGrams: Number(form.weightMaxGrams),
      weightMinGrams: Number(form.weightMinGrams),
    };
    try {
      if (mode === "edit" && labelProductId) {
        await apiClient.updateAdminLabelProduct(labelProductId, payload);
      } else {
        await apiClient.createAdminLabelProduct(payload);
      }
      navigate("/admin/labels");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etiket ürünü kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingPanel label="Etiket ürünü yükleniyor" />;

  return (
    <div className="space-y-5">
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="p-5 lg:p-6">
        <Link className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900" to="/admin/labels">
          <ArrowLeft className="h-4 w-4" />
          Etiketlere dön
        </Link>
        <div className="flex min-w-0 items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-cyan-200">
            <Package className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-950">{mode === "edit" ? "Etiket ürünü" : "Yeni etiket ürünü"}</h1>
            <p className="mt-1 text-sm text-slate-500">Ürün, taşıyıcı, ağırlık ve baz fiyat bilgileri.</p>
          </div>
        </div>
        </div>
      </section>
      {error ? <ErrorBanner message={error} /> : null}
      <AdminPanel title="Ürün bilgileri">
        <form className="grid gap-4 p-5 lg:grid-cols-2" onSubmit={submit}>
          <ProfileInput label="Ürün adı" value={form.name} onChange={(value) => setField("name", value)} />
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">Taşıyıcı</span>
            <select
              value={form.carrier}
              onChange={(event) => setField("carrier", event.target.value)}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5"
            >
              <option value="dhl">DHL</option>
              <option value="ups">UPS</option>
              <option value="gls">GLS</option>
              <option value="other">Diğer</option>
            </select>
          </label>
          <ProfileInput label="Min gram" type="number" value={form.weightMinGrams} onChange={(value) => setField("weightMinGrams", value)} />
          <ProfileInput label="Max gram" type="number" value={form.weightMaxGrams} onChange={(value) => setField("weightMaxGrams", value)} />
          <ProfileInput label="Baz fiyat" type="number" value={form.basePrice} onChange={(value) => setField("basePrice", value)} />
          <label className="inline-flex w-fit items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={form.active === "true"}
              onChange={(event) => setField("active", event.target.checked ? "true" : "false")}
              className="h-4 w-4 rounded border-slate-300 text-slate-900"
            />
            Aktif
          </label>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex w-fit items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60 lg:col-span-2"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Kaydet
          </button>
        </form>
      </AdminPanel>
    </div>
  );
}

function AdminBillingPage() {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [customers, setCustomers] = useState<DataRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expenseSaving, setExpenseSaving] = useState(false);
  const [billingReconciling, setBillingReconciling] = useState(false);
  const [deletingExpenseId, setDeletingExpenseId] = useState("");
  const [manualInvoiceOpen, setManualInvoiceOpen] = useState(false);
  const [usagePeriod, setUsagePeriod] = useState<"allTime" | "day" | "month" | "week" | "year">("day");
  const [purchasePeriod, setPurchasePeriod] = useState<"all" | "day" | "month" | "week">("day");
  const [purchaseProductId, setPurchaseProductId] = useState("");
  const [purchaseSource, setPurchaseSource] = useState<"all" | "bonus" | "customer" | "manual">("all");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({
    customerId: "",
    note: "",
    paymentMethodName: "Byzone",
  });
  const [manualLines, setManualLines] = useState([
    { id: "manual_1", quantity: "1", taxRate: "0.19", title: "Versand- und Logistikdienstleistung", unitPrice: "0.00" },
  ]);
  const manualInvoicePreview = useMemo(
    () =>
      manualLines.reduce(
        (totals, line) => {
          const quantity = Math.max(0, Number(line.quantity) || 0);
          const unitNet = Math.max(0, Number(String(line.unitPrice).replace(",", ".")) || 0);
          const rawTaxRate = Math.max(0, Number(String(line.taxRate).replace(",", ".")) || 0);
          const taxRate = rawTaxRate > 1 ? rawTaxRate / 100 : rawTaxRate;
          const net = Math.round(unitNet * quantity * 100) / 100;
          const tax = Math.round(net * taxRate * 100) / 100;
          return {
            gross: Math.round((totals.gross + net + tax) * 100) / 100,
            net: Math.round((totals.net + net) * 100) / 100,
            tax: Math.round((totals.tax + tax) * 100) / 100,
          };
        },
        { gross: 0, net: 0, tax: 0 }
      ),
    [manualLines]
  );
  const [expenseForm, setExpenseForm] = useState({
    amount: "",
    category: "return_package" as "lost_package" | "other" | "penalty" | "return_package",
    description: "",
    expenseDate: new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin" }).format(new Date()),
  });

  async function loadBilling() {
    setLoading(true);
    setError(null);
    try {
      const purchaseQuery = new URLSearchParams({
        purchasePeriod,
        purchaseSource,
      });
      if (purchaseProductId) purchaseQuery.set("purchaseProductId", purchaseProductId);
      const [billing, customerResult] = await Promise.all([
        apiClient.resource<Record<string, unknown>>(`/api/admin/billing?${purchaseQuery.toString()}`),
        apiClient.resource<{ customers?: DataRow[] }>("/api/admin/customers?limit=250&status=active"),
      ]);
      setData(billing);
      setCustomers(Array.isArray(customerResult.customers) ? customerResult.customers : []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Faturalama verisi alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadBilling();
  }, [purchasePeriod, purchaseProductId, purchaseSource]);

  function setField(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function updateManualLine(index: number, key: keyof (typeof manualLines)[number], value: string) {
    setManualLines((current) => current.map((line, lineIndex) => (lineIndex === index ? { ...line, [key]: value } : line)));
  }

  function addManualLine() {
    setManualLines((current) => [
      ...current,
      { id: `manual_${Date.now()}_${current.length}`, quantity: "1", taxRate: "0.19", title: "Versand- und Logistikdienstleistung", unitPrice: "0.00" },
    ]);
  }

  function removeManualLine(index: number) {
    setManualLines((current) => (current.length <= 1 ? current : current.filter((_, lineIndex) => lineIndex !== index)));
  }

  async function createInvoice(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (!form.customerId) throw new Error("Müşteri seçimi gerekli.");
      const result = await apiClient.createAdminManualShopifyInvoice({
        customerId: form.customerId,
        lines: manualLines.map((line) => ({
          quantity: Number(line.quantity || 1),
          taxRate: Number(line.taxRate || 0.19),
          title: line.title,
          unitPrice: Number(line.unitPrice || 0),
        })),
        note: form.note,
        paymentMethodName: form.paymentMethodName,
        priceMode: "net",
      });
      setNotice(`Shopify faturası oluşturuldu: ${result.invoiceNumber || result.shopifyOrderName}`);
      setForm((current) => ({ ...current, note: "" }));
      setManualLines([{ id: "manual_1", quantity: "1", taxRate: "0.19", title: "Versand- und Logistikdienstleistung", unitPrice: "0.00" }]);
      await loadBilling();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Fatura kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  async function createExpense(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setExpenseSaving(true);
    setError(null);
    setNotice(null);
    try {
      const amount = Number(expenseForm.amount.replace(",", "."));
      if (!Number.isFinite(amount) || amount <= 0) throw new Error("Masraf tutarı 0'dan büyük olmalı.");
      await apiClient.createAdminBillingExpense({
        amount,
        category: expenseForm.category,
        description: expenseForm.description,
        expenseDate: expenseForm.expenseDate,
      });
      setExpenseForm((current) => ({ ...current, amount: "", description: "" }));
      setNotice("Günlük masraf kaydedildi ve dönem cirolarından düşüldü.");
      await loadBilling();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Masraf kaydedilemedi.");
    } finally {
      setExpenseSaving(false);
    }
  }

  async function reconcileHistoricalShopifyPurchases() {
    setBillingReconciling(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.syncAdminShopifyBilling({
        mode: "recent",
        reconcileSource: "admin_historical_audit",
        sinceDays: 365,
      });
      const allocatedCount = Number(result.allocatedCount || 0);
      const matchedCount = Number(result.matchedCount || 0);
      const scannedCount = Number(result.scannedCount || 0);
      setNotice(
        allocatedCount > 0
          ? `${allocatedCount} eksik ödenmiş Shopify siparişi güvenli şekilde krediye çevrildi. ${matchedCount} KenPost ödemesi / ${scannedCount} Shopify siparişi kontrol edildi.`
          : `Geçmiş ödeme kontrolü tamamlandı. Eksik kredi bulunmadı; ${matchedCount} KenPost ödemesi / ${scannedCount} Shopify siparişi doğrulandı.`
      );
      await loadBilling();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Geçmiş Shopify ödemeleri mutabıklaştırılamadı.");
    } finally {
      setBillingReconciling(false);
    }
  }

  async function deleteExpense(expenseId: string) {
    if (!expenseId || !window.confirm("Bu masraf kaydını silmek istediğinizden emin misiniz?")) return;
    setDeletingExpenseId(expenseId);
    setError(null);
    setNotice(null);
    try {
      await apiClient.deleteAdminBillingExpense(expenseId);
      setNotice("Masraf kaydı silindi; dönem ciroları yeniden hesaplandı.");
      await loadBilling();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Masraf silinemedi.");
    } finally {
      setDeletingExpenseId("");
    }
  }

  const invoices = Array.isArray(data?.invoices) ? (data?.invoices as DataRow[]) : [];
  const purchases = Array.isArray(data?.purchases) ? (data?.purchases as DataRow[]) : [];
  const purchaseMovements = Array.isArray(data?.purchaseMovements) ? (data?.purchaseMovements as DataRow[]) : [];
  const purchaseMovementProducts = Array.isArray(data?.purchaseMovementProducts) ? (data?.purchaseMovementProducts as DataRow[]) : [];
  const purchaseMovementSummary = (data?.purchaseMovementSummary || {}) as DataRow;
  const totals = (data?.totals || {}) as DataRow;
  const revenue = (data?.revenue || {}) as Record<string, DataRow>;
  const usageRevenue = (data?.usageRevenue || {}) as Record<string, DataRow>;
  const usagePeriodData = usageRevenue[usagePeriod] || {};
  const monthlyBonusUsage = usageRevenue.month || {};
  const usageProducts = Array.isArray(usagePeriodData.products) ? (usagePeriodData.products as DataRow[]) : [];
  const billingExpenses = (data?.billingExpenses || {}) as DataRow;
  const expenseSummary = (billingExpenses.summary || {}) as Record<string, DataRow>;
  const expensePeriodData = expenseSummary[usagePeriod] || {};
  const expenses = Array.isArray(billingExpenses.expenses) ? (billingExpenses.expenses as DataRow[]) : [];
  const expenseAdjustedGrossTotal = Number(usagePeriodData.grossTotal || 0) - Number(expensePeriodData.total || 0);
  const invoiceTotal = Number(
    totals.invoiceGrossTotal ?? invoices.filter((row) => text(row.status).toLowerCase() === "final").reduce((sum, row) => sum + Number(row.grossTotal || 0), 0)
  );
  const purchaseTotal = Number(totals.purchaseTotalPrice ?? purchases.reduce((sum, row) => sum + Number(row.totalPrice || 0), 0));
  const mergedPurchaseRows = Math.max(0, Number(totals.mergedDuplicatePurchaseCount || 0));
  const revenuePeriods: Array<{
    key: "allTime" | "day" | "month" | "week" | "year";
    label: string;
    tone: "amber" | "cyan" | "emerald" | "slate" | "violet";
  }> = [
    { key: "day", label: "Bugün", tone: "cyan" as const },
    { key: "week", label: "Bu hafta", tone: "emerald" as const },
    { key: "month", label: "Bu ay", tone: "violet" as const },
    { key: "year", label: "Bu yıl", tone: "amber" as const },
    { key: "allTime", label: "Tüm zamanlar", tone: "slate" as const },
  ];

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Faturalama"
        caption="Fatura cirosu, kullanılan etiketlerin gerçek satış cirosu, manuel Shopify order oluşturma ve PDF üretimi."
        icon={<CreditCard className="h-5 w-5" />}
        action={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={billingReconciling}
              onClick={() => void reconcileHistoricalShopifyPurchases()}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-800 shadow-sm transition hover:bg-emerald-100 disabled:opacity-60"
            >
              {billingReconciling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Geçmiş ödemeleri kontrol et
            </button>
            <a href="/api/admin/billing?export=csv&section=invoices" className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">
              <Download className="h-4 w-4" />
              Fatura CSV
            </a>
            <a href="/api/admin/billing?export=csv&section=purchases" className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800">
              <Download className="h-4 w-4" />
              Alım CSV
            </a>
          </div>
        }
      />
      {notice ? <AdminNotice message={notice} /> : null}
      {error ? <ErrorBanner message={error} /> : null}
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Fatura kaydı" value={totals.invoiceCount || invoices.length} tone="cyan" icon={<FileText className="h-5 w-5" />} />
        <AdminStatCard label="Final fatura" value={totals.finalInvoices || 0} tone="emerald" icon={<CreditCard className="h-5 w-5" />} />
        <AdminStatCard label="Listelenen Shopify sipariş" value={purchases.length} tone="slate" icon={<ShoppingCart className="h-5 w-5" />} />
        <AdminStatCard label="Listelenen alım toplamı" value={moneyText(purchaseTotal, "EUR")} tone="amber" icon={<Wallet className="h-5 w-5" />} />
      </section>
      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {revenuePeriods.map((item) => {
          const period = revenue[item.key] || {};
          return (
            <AdminStatCard
              key={item.key}
              label={`${item.label} fatura cirosu`}
              value={moneyText(period.grossTotal ?? (item.key === "allTime" ? invoiceTotal : 0), "EUR")}
              detail={`${numberText(period.count || 0)} final fatura`}
              tone={item.tone}
              icon={<BarChart3 className="h-5 w-5" />}
            />
          );
        })}
      </section>
      <AdminPanel
        title="Etiket kullanım cirosu"
        caption="Yalnız gerçekten kullanılan etiketler hesaplanır. Her etiket müşteriye tanımlanan net birim fiyatıyla değerlendirilir; bonus/telafi etiketleri de adede ve ciroya aynı fiyatla dahil edilir."
        action={
          <div className="flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
            {revenuePeriods.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setUsagePeriod(item.key)}
                className={cx(
                  "rounded-lg px-3 py-1.5 text-xs font-semibold transition",
                  usagePeriod === item.key ? "bg-slate-950 text-white shadow-sm" : "text-slate-600 hover:bg-white"
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        }
      >
        <div className="space-y-4 p-5">
          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
            <AdminStatCard label="Kullanılan etiket" value={usagePeriodData.quantity || 0} detail={`${numberText(usagePeriodData.bonusQuantity || 0)} bonus dahil`} tone="cyan" icon={<Tags className="h-5 w-5" />} />
            <AdminStatCard label="Net kullanım cirosu" value={moneyText(usagePeriodData.netTotal, "EUR")} tone="emerald" icon={<Wallet className="h-5 w-5" />} />
            <AdminStatCard label="KDV %19" value={moneyText(usagePeriodData.taxTotal, "EUR")} tone="amber" icon={<CreditCard className="h-5 w-5" />} />
            <AdminStatCard label="Brüt kullanım cirosu" value={moneyText(usagePeriodData.grossTotal, "EUR")} tone="violet" icon={<BarChart3 className="h-5 w-5" />} />
            <AdminStatCard label="Masraflar" value={moneyText(expensePeriodData.total, "EUR")} detail={`${numberText(expensePeriodData.count || 0)} kayıt`} tone="red" icon={<Archive className="h-5 w-5" />} />
            <AdminStatCard label="Masraf sonrası ciro" value={moneyText(expenseAdjustedGrossTotal, "EUR")} detail="Brüt ciro − masraf" tone="slate" icon={<Wallet className="h-5 w-5" />} />
          </section>
          <section className="rounded-2xl border border-violet-200 bg-violet-50/60 p-4">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="text-sm font-bold text-violet-950">Bu ay kullanılan affiliate bonusları</h3>
                <p className="mt-1 text-xs font-medium text-violet-700">Affiliate kazancından etikete çevrilen ve müşteri tarafından gerçekten kullanılan bonuslar, tanımlanan müşteri net fiyatıyla hesaplanır.</p>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <AdminStatCard label="Kullanılan affiliate bonusu" value={monthlyBonusUsage.bonusQuantity || 0} detail="Bu ay tüketilen" tone="violet" icon={<Sparkles className="h-5 w-5" />} />
              <AdminStatCard label="Ortalama birim net" value={moneyText(monthlyBonusUsage.bonusAverageUnitPrice, "EUR")} detail="Kullanılan bonus başına" tone="cyan" icon={<Tags className="h-5 w-5" />} />
              <AdminStatCard label="Toplam net karşılık" value={moneyText(monthlyBonusUsage.bonusNetTotal, "EUR")} detail="Bu ay kullanılan bonuslar" tone="emerald" icon={<Wallet className="h-5 w-5" />} />
            </div>
          </section>
          <DataTable
            rows={usageProducts}
            empty="Seçili dönemde kullanılan etiket yok."
            columns={[
              { label: "Etiket ürünü", value: (row) => <NameStack title={text(row.labelProductName) || text(row.labelProductId)} detail={text(row.labelProductId)} /> },
              { label: "Kullanım adedi", value: (row) => numberText(row.quantity), align: "right" },
              {
                label: "Ortalama net",
                value: (row) => moneyText(Number(row.quantity || 0) > 0 ? Number(row.netTotal || 0) / Number(row.quantity || 1) : 0, "EUR"),
                align: "right",
              },
              { label: "Net ciro", value: (row) => moneyText(row.netTotal, "EUR"), align: "right" },
              { label: "KDV", value: (row) => moneyText(row.taxTotal, "EUR"), align: "right" },
              { label: "Brüt ciro", value: (row) => moneyText(row.grossTotal, "EUR"), align: "right" },
            ]}
          />
        </div>
      </AdminPanel>
      <AdminPanel
        title="Günlük masraflar"
        caption="İade paket, ceza, kayıp paket ve diğer giderleri tarihe göre kaydedin. Tutarlar bugün, hafta, ay, yıl ve tüm zamanlar kullanım cirosundan otomatik düşülür."
        count={expenses.length}
      >
        <div className="space-y-5 p-5">
          <form className="grid gap-3 lg:grid-cols-[170px_190px_170px_minmax(220px,1fr)_auto]" onSubmit={createExpense}>
            <label className="block space-y-1.5">
              <span className="text-xs font-semibold uppercase text-slate-500">Tarih</span>
              <input
                type="date"
                required
                value={expenseForm.expenseDate}
                onChange={(event) => setExpenseForm((current) => ({ ...current, expenseDate: event.target.value }))}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-semibold uppercase text-slate-500">Masraf türü</span>
              <select
                value={expenseForm.category}
                onChange={(event) => setExpenseForm((current) => ({ ...current, category: event.target.value as typeof current.category }))}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5"
              >
                <option value="return_package">İade paket</option>
                <option value="penalty">Ceza</option>
                <option value="lost_package">Kayıp paket</option>
                <option value="other">Diğer</option>
              </select>
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-semibold uppercase text-slate-500">Tutar (€)</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                required
                value={expenseForm.amount}
                onChange={(event) => setExpenseForm((current) => ({ ...current, amount: event.target.value }))}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-right text-sm font-semibold text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-semibold uppercase text-slate-500">Açıklama</span>
              <input
                type="text"
                maxLength={500}
                value={expenseForm.description}
                onChange={(event) => setExpenseForm((current) => ({ ...current, description: event.target.value }))}
                placeholder="İsteğe bağlı not"
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5"
              />
            </label>
            <button
              type="submit"
              disabled={expenseSaving}
              className="mt-auto inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              {expenseSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusSquare className="h-4 w-4" />}
              Masraf ekle
            </button>
          </form>
          <DataTable
            rows={expenses}
            empty="Henüz masraf kaydı yok."
            columns={[
              { label: "Tarih", value: (row) => dateText(row.expenseDate) },
              { label: "Tür", value: (row) => badge(text(row.categoryLabel)) },
              { label: "Açıklama", value: (row) => text(row.description) || "-" },
              { label: "Tutar", value: (row) => moneyText(row.amount, "EUR"), align: "right" },
              {
                label: "İşlem",
                align: "right",
                value: (row) => (
                  <button
                    type="button"
                    disabled={deletingExpenseId === text(row.id)}
                    onClick={() => void deleteExpense(text(row.id))}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60"
                  >
                    {deletingExpenseId === text(row.id) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    Sil
                  </button>
                ),
              },
            ]}
          />
        </div>
      </AdminPanel>
      <section className="grid gap-3 sm:grid-cols-3">
        <AdminStatCard label="Listeleme sınırı" value="150" tone="emerald" icon={<Eye className="h-5 w-5" />} />
        <AdminStatCard label="Listelenen etiket adedi" value={totals.purchaseQuantity || 0} tone="cyan" icon={<Tags className="h-5 w-5" />} />
        <AdminStatCard label="Birleştirilen eski satır" value={mergedPurchaseRows} tone="violet" icon={<Sparkles className="h-5 w-5" />} />
      </section>
      <AdminPanel
        title="Manuel Shopify faturası"
        caption="Raporlardan ayrı tutulur. Gerektiğinde müşteri seçip Shopify'da manuel ödenmiş order oluşturun."
        action={
          <button
            type="button"
            aria-expanded={manualInvoiceOpen}
            onClick={() => setManualInvoiceOpen((current) => !current)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <FileText className="h-4 w-4" />
            {manualInvoiceOpen ? "Formu kapat" : "Manuel fatura oluştur"}
            <ChevronDown className={cx("h-4 w-4 transition", manualInvoiceOpen ? "rotate-180" : "")} />
          </button>
        }
      >
        {manualInvoiceOpen ? <form className="space-y-5 p-5" onSubmit={createInvoice}>
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_260px]">
            <AdminCustomerPicker customers={customers} value={form.customerId} onChange={(value) => setField("customerId", value)} />
            <label className="block space-y-1.5">
              <span className="text-xs font-semibold uppercase text-slate-500">Ödeme yöntemi</span>
              <select value={form.paymentMethodName} onChange={(event) => setField("paymentMethodName", event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5">
                <option value="Byzone">Byzone / banka transferi</option>
                <option value="Manual">Manuel ödendi</option>
                <option value="PayPal">PayPal</option>
                <option value="Credit card">Kredi kartı</option>
              </select>
            </label>
          </div>
          <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
            {manualLines.map((line, index) => (
              <div key={line.id} className="grid gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm lg:grid-cols-[minmax(0,1.6fr)_130px_160px_150px_auto]">
                <ProfileInput label={`Satır ${index + 1}`} value={line.title} onChange={(value) => updateManualLine(index, "title", value)} />
                <ProfileInput label="Adet" type="number" value={line.quantity} onChange={(value) => updateManualLine(index, "quantity", value)} />
                <ProfileInput label="Birim net" type="number" value={line.unitPrice} onChange={(value) => updateManualLine(index, "unitPrice", value)} />
                <ProfileInput label="Vergi oranı" type="number" value={line.taxRate} onChange={(value) => updateManualLine(index, "taxRate", value)} />
                <button type="button" onClick={() => removeManualLine(index)} disabled={manualLines.length <= 1} className="self-end rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-40">
                  Sil
                </button>
              </div>
            ))}
            <button type="button" onClick={addManualLine} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
              <PlusSquare className="h-3.5 w-3.5" />
              Satır ekle
            </button>
            <div className="grid gap-3 rounded-xl border border-emerald-200 bg-emerald-50/70 p-3 text-sm sm:grid-cols-3">
              <div><p className="text-xs font-semibold uppercase text-emerald-700">Net toplam</p><p className="mt-1 font-bold text-slate-950">{moneyText(manualInvoicePreview.net, "EUR")}</p></div>
              <div><p className="text-xs font-semibold uppercase text-emerald-700">KDV</p><p className="mt-1 font-bold text-slate-950">{moneyText(manualInvoicePreview.tax, "EUR")}</p></div>
              <div><p className="text-xs font-semibold uppercase text-emerald-700">Brüt toplam</p><p className="mt-1 font-bold text-slate-950">{moneyText(manualInvoicePreview.gross, "EUR")}</p></div>
              <p className="text-xs text-emerald-800 sm:col-span-3">Girilen tutar nettir; KDV seçilen orana göre üzerine eklenir.</p>
            </div>
          </div>
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">Not</span>
            <textarea
              value={form.note}
              onChange={(event) => setField("note", event.target.value)}
              rows={3}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5"
            />
          </label>
          <div className="flex justify-end pb-7 pr-16 sm:pb-2">
            <button type="submit" disabled={saving} className="inline-flex w-fit items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Shopify'da fatura oluştur
            </button>
          </div>
        </form> : null}
      </AdminPanel>
      {loading ? (
        <LoadingPanel label="Faturalama verisi yükleniyor" />
      ) : (
        <>
          <AdminPanel title="Faturalar" caption="Yerel manuel geçmiş ve Shopify kaynaklı pazar faturaları. Eski kredi sepeti faturaları gizlenir. Son 150 kayıt." count={invoices.length}>
            <DataTable
              rows={invoices}
              empty="Görüntülenecek fatura yok."
              columns={[
                {
                  label: "Fatura no",
                  value: (row) => (
                    <Link className="font-semibold text-slate-950 hover:underline" to={`/admin/billing/invoices/${encodeURIComponent(text(row.id))}`}>
                      <NameStack title={text(row.invoiceNumber) || "-"} detail={text(row.shopifyOrderNumber) || text(row.orderNumber) || text(row.externalOrderId)} />
                    </Link>
                  ),
                },
                { label: "Mağaza", value: (row) => <NameStack title={text(row.storeName) || "Shopify"} detail={text(row.sourcePlatform)} /> },
                { label: "Müşteri", value: (row) => <NameStack title={text(row.userCompanyName) || text(row.userEmail)} detail={text(row.userEmail)} /> },
                { label: "Tutar", value: (row) => moneyText(row.grossTotal, row.currency), align: "right" },
                { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                { label: "Tarih", value: (row) => dateText(row.createdAt) },
                { label: "PDF", value: (row) => <InvoicePdfButton invoiceId={text(row.id)} admin onDone={loadBilling} />, align: "right" },
              ]}
            />
          </AdminPanel>
          <AdminPanel
            title="Etiket alım ve atamaları"
            caption={`${numberText(purchaseMovementSummary.movementCount || 0)} hareket · ${numberText(purchaseMovementSummary.quantity || 0)} etiket. Affiliate bonusu, manuel admin ataması ve müşterinin doğrudan alımı ayrı kategorilerde gösterilir.`}
            count={purchaseMovements.length}
            action={
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-violet-700"><span className="h-2 w-2 rounded-full bg-violet-500" />Affiliate bonusu</span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500" />Manuel admin</span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-orange-700"><span className="h-2 w-2 rounded-full bg-orange-500" />Müşteri alımı</span>
              </div>
            }
          >
            <div className="grid gap-3 border-b border-slate-100 p-4 xl:grid-cols-[minmax(0,1fr)_220px_220px]">
              <div className="flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
                {[
                  { key: "day", label: "Bugün" },
                  { key: "week", label: "Bu hafta" },
                  { key: "month", label: "Bu ay" },
                  { key: "all", label: "Tüm zamanlar" },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setPurchasePeriod(item.key as typeof purchasePeriod)}
                    className={cx(
                      "rounded-lg px-3 py-2 text-xs font-semibold transition",
                      purchasePeriod === item.key ? "bg-slate-950 text-white shadow-sm" : "text-slate-600 hover:bg-white"
                    )}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <select
                value={purchaseProductId}
                onChange={(event) => setPurchaseProductId(event.target.value)}
                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-slate-400"
              >
                <option value="">Tüm etiket türleri</option>
                {purchaseMovementProducts.map((product) => <option key={text(product.id)} value={text(product.id)}>{text(product.name) || text(product.id)}</option>)}
              </select>
              <select
                value={purchaseSource}
                onChange={(event) => setPurchaseSource(event.target.value as typeof purchaseSource)}
                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-slate-400"
              >
                <option value="all">Tüm alım kaynakları</option>
                <option value="bonus">Affiliate bonusları</option>
                <option value="manual">Manuel admin atamaları</option>
                <option value="customer">Müşteri alımları</option>
              </select>
            </div>
            <div className="grid gap-3 border-b border-slate-100 bg-slate-50/60 p-4 sm:grid-cols-2 xl:grid-cols-4">
              <AdminStatCard label="Toplam etiket" value={purchaseMovementSummary.quantity || 0} tone="cyan" icon={<Tags className="h-5 w-5" />} />
              <AdminStatCard label="Affiliate bonusu" value={purchaseMovementSummary.bonusQuantity || 0} tone="violet" icon={<Sparkles className="h-5 w-5" />} />
              <AdminStatCard label="Manuel admin" value={purchaseMovementSummary.manualQuantity || 0} tone="emerald" icon={<HandCoins className="h-5 w-5" />} />
              <AdminStatCard label="Müşteri alımı" value={purchaseMovementSummary.customerQuantity || 0} tone="amber" icon={<ShoppingCart className="h-5 w-5" />} />
            </div>
            <DataTable
              rows={purchaseMovements}
              empty="Seçili filtrelerde etiket alımı veya ataması yok."
              rowClassName={(row) => text(row.source) === "bonus" ? "bg-violet-50/45" : text(row.source) === "manual" ? "bg-emerald-50/35" : "bg-orange-50/35"}
              columns={[
                {
                  label: "Etiket türü",
                  value: (row) => {
                    const lines = lineItemsFromRow(row);
                    if (lines.length <= 1) return <NameStack title={text(row.labelProductName) || text(row.labelProductId) || "Etiket"} detail={text(row.labelProductId)} />;
                    return <NameStack title={`${numberText(lines.length)} etiket türü`} detail={lines.map((line) => `${text(line.labelProductName) || text(line.labelProductId)} × ${numberText(line.quantity)}`).join(" · ")} />;
                  },
                },
                { label: "Kaynak", value: (row) => <PurchaseMovementSourceBadge row={row} /> },
                { label: "Müşteri", value: (row) => <NameStack title={text(row.userCompanyName) || text(row.userEmail) || "-"} detail={text(row.userEmail)} /> },
                { label: "Adet", value: (row) => numberText(row.quantity), align: "right" },
                { label: "Birim net", value: (row) => lineItemsFromRow(row).length > 1 ? "-" : moneyText(row.unitPrice, "EUR"), align: "right" },
                { label: "Net toplam", value: (row) => moneyText(row.totalPrice, "EUR"), align: "right" },
                { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                { label: "Tarih", value: (row) => dateText(row.createdAt) },
              ]}
            />
            {Number(purchaseMovementSummary.movementCount || 0) > Number(purchaseMovementSummary.returnedCount || 0) ? (
              <p className="border-t border-slate-100 bg-slate-50 px-5 py-3 text-xs font-medium text-slate-500">İlk {numberText(purchaseMovementSummary.returnedCount || 0)} hareket gösteriliyor. Filtreleri daraltarak diğer kayıtları görebilirsiniz.</p>
            ) : null}
          </AdminPanel>
          <AdminPanel title="Etiket satın alma faturaları" caption="Sadece Shopify siparişleri; aynı siparişin ürünleri birlikte gösterilir. Son 150 kayıt." count={purchases.length}>
            <DataTable
              rows={purchases}
              empty="Shopify etiket satın alma faturası yok."
              columns={[
                { label: "Fatura no", value: (row) => <ShopifyOrderCell row={row} /> },
                { label: "Ürünler", value: (row) => <ShopifyPurchaseProductsCell row={row} /> },
                { label: "Müşteri", value: (row) => <NameStack title={text(row.userCompanyName) || text(row.userEmail)} detail={text(row.userEmail)} /> },
                { label: "Ödeme", value: (row) => <PaymentMethodCell row={row} /> },
                { label: "Fatura adet", value: (row) => numberText(row.invoiceQuantity || row.quantity), align: "right" },
                { label: "Net tutar", value: (row) => moneyText(row.totalPrice, "EUR"), align: "right" },
                { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                { label: "İşlem", value: (row) => <PurchaseInvoiceActions row={row} admin onDone={loadBilling} />, align: "right" },
              ]}
            />
          </AdminPanel>
        </>
      )}
    </div>
  );
}

function PurchaseMovementSourceBadge({ row }: { row: DataRow }) {
  const source = text(row.source);
  const manual = source === "manual";
  const bonus = source === "bonus" || row.bonus === true;
  return (
    <div className="space-y-1">
      <span className={cx(
        "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
        bonus ? "border-violet-200 bg-violet-50 text-violet-700" : manual ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-orange-200 bg-orange-50 text-orange-700"
      )}>
        {bonus ? "Affiliate bonusu" : manual ? "Manuel admin ataması" : "Müşteri alımı"}
      </span>
      {bonus ? <p className="text-[11px] font-medium text-violet-700">Affiliate kazancından çevrildi</p> : null}
    </div>
  );
}

function lineItemsFromRow(row: DataRow) {
  return Array.isArray(row.lineItems) ? (row.lineItems as DataRow[]) : [];
}

function ShopifyOrderCell({ row }: { row: DataRow }) {
  const orderTitle = text(row.invoiceNumber) || text(row.shopifyOrderName) || text(row.shopifyOrderId) || "-";
  const orderDetail = [
    text(row.shopifyOrderNumber) ? `Shopify ${text(row.shopifyOrderNumber)}` : "",
    text(row.shopifyOrderId) ? `ID ${text(row.shopifyOrderId)}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  return <NameStack title={orderTitle} detail={orderDetail || "Shopify"} />;
}

function ShopifyPurchaseProductsCell({ row }: { row: DataRow }) {
  const lineItems = lineItemsFromRow(row);
  if (row.invoiceSingleServiceLine === true || text(row.invoiceServiceDescription)) {
    return (
      <div className="min-w-0 max-w-[360px]" title={text(row.invoiceServiceDescription) || text(row.productSummary)}>
        <p className="truncate font-semibold text-slate-950">{text(row.invoiceServiceDescription) || text(row.productSummary) || "-"}</p>
        <p className="mt-1 truncate text-xs text-slate-500">{text(row.productDetail) || `${numberText(row.invoiceQuantity || 1)} etiket`}</p>
      </div>
    );
  }
  const detail = text(row.productDetail) || lineItems.map((item) => `${text(item.labelProductName)}: ${numberText(item.quantity)} adet`).join(" · ");
  const summary = text(row.productSummary) || text(row.labelProductName) || "-";
  return (
    <div className="min-w-0 max-w-[360px]" title={detail || summary}>
      <p className="truncate font-semibold text-slate-950">{summary}</p>
      {lineItems.length > 1 ? (
        <div className="mt-1 flex max-w-full flex-wrap gap-1">
          {lineItems.slice(0, 3).map((item) => (
            <span key={text(item.id) || `${text(item.labelProductName)}-${text(item.quantity)}`} className="inline-flex max-w-full items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
              <span className="max-w-28 truncate">{text(item.labelProductName) || "Etiket"}</span>
              <span className="ml-1 text-slate-500">x{numberText(item.quantity)}</span>
            </span>
          ))}
          {lineItems.length > 3 ? <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">+{lineItems.length - 3}</span> : null}
        </div>
      ) : detail && detail !== summary ? (
        <p className="mt-1 truncate text-xs text-slate-500">{detail}</p>
      ) : null}
    </div>
  );
}

function PaymentMethodCell({ row }: { row: DataRow }) {
  const method = text(row.paymentMethodName) || (row.manualInvoiceOnly ? "Manuel" : "Shopify");
  return <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">{method}</span>;
}

function InvoicePdfButton({ admin = false, invoiceId, onDone }: { admin?: boolean; invoiceId: string; onDone?: () => void | Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function generate() {
    if (!invoiceId) return;
    setBusy(true);
    setError(null);
    try {
      const result = admin ? await apiClient.generateAdminInvoicePdf(invoiceId) : await apiClient.generateInvoicePdf(invoiceId);
      if (result.pdfDownloadUrl) window.open(result.pdfDownloadUrl, "_blank", "noopener,noreferrer");
      await onDone?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "PDF üretilemedi.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-1">
      <button
        type="button"
        disabled={busy || !invoiceId}
        onClick={() => void generate()}
        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
        PDF
      </button>
      {error ? <p className="max-w-52 text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}

function PurchaseInvoiceActions({ admin = false, onDone, row }: { admin?: boolean; onDone?: () => void | Promise<void>; row: DataRow }) {
  const [busy, setBusy] = useState<"cancel" | "pdf" | "save" | null>(null);
  const [editing, setEditing] = useState(false);
  const [quantity, setQuantity] = useState(String(Number(row.quantity || 1) || 1));
  const [unitPrice, setUnitPrice] = useState(Number(row.unitPrice || 0).toFixed(2));
  const [error, setError] = useState<string | null>(null);
  const labelPurchaseId = text(row.id);
  const lineItems = lineItemsFromRow(row);
  const canEdit = admin && lineItems.length <= 1 && row.manualInvoiceOnly !== true && row.invoiceSingleServiceLine !== true;
  const cancelled = text(row.status).toLowerCase() === "cancelled";
  const canCancel = admin && row.manualInvoiceOnly === true && !cancelled;
  const invoiceNumberReady = row.invoiceNumberReady !== false && !cancelled;
  const total = Math.round((Number(quantity || 0) * Number(unitPrice || 0)) * 100) / 100;

  async function generatePdf() {
    if (!labelPurchaseId) return;
    setBusy("pdf");
    setError(null);
    try {
      const result = await apiClient.generateLabelPurchaseInvoicePdf(labelPurchaseId, admin);
      const url = result.downloadUrl || result.signedDownloadUrl;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      await onDone?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Satın alma PDF'i üretilemedi.");
    } finally {
      setBusy(null);
    }
  }

  async function saveOverride() {
    if (!labelPurchaseId) return;
    setBusy("save");
    setError(null);
    try {
      await apiClient.updateLabelPurchaseInvoiceOverride({
        labelPurchaseId,
        quantity: Number(quantity),
        unitPrice: Number(unitPrice),
        totalPrice: total,
      });
      setEditing(false);
      await onDone?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Satın alma faturası güncellenemedi.");
    } finally {
      setBusy(null);
    }
  }

  async function cancelManualInvoice() {
    if (!labelPurchaseId || !canCancel) return;
    if (!window.confirm("Bu manuel Shopify faturası iptal edilecek. Devam edilsin mi?")) return;
    setBusy("cancel");
    setError(null);
    try {
      await apiClient.cancelAdminManualShopifyInvoice({ labelPurchaseId, shopifyOrderId: text(row.shopifyOrderId) || undefined });
      await onDone?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Manuel Shopify faturası iptal edilemedi.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex min-w-56 flex-col items-end gap-2">
      {editing && canEdit ? (
        <div className="grid w-full gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-left">
          <ProfileInput label="Adet" type="number" value={quantity} onChange={setQuantity} />
          <ProfileInput label="Birim fiyat" type="number" value={unitPrice} onChange={setUnitPrice} />
          <p className="text-xs font-semibold text-slate-600">Toplam: EUR {total.toFixed(2)}</p>
          <div className="flex justify-end gap-2">
            <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700" onClick={() => setEditing(false)}>
              Vazgeç
            </button>
            <button type="button" disabled={busy === "save"} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60" onClick={() => void saveOverride()}>
              {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              Kaydet
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end gap-2">
          {canCancel ? (
            <button type="button" disabled={busy === "cancel"} className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 shadow-sm transition hover:bg-rose-100 disabled:opacity-60" onClick={() => void cancelManualInvoice()}>
              {busy === "cancel" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
              İptal
            </button>
          ) : null}
          {canEdit ? (
            <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50" onClick={() => setEditing(true)}>
              Düzenle
            </button>
          ) : null}
          <button type="button" disabled={busy === "pdf" || !invoiceNumberReady} className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60" onClick={() => void generatePdf()} title={invoiceNumberReady ? "PDF" : "Shopify sipariş no eksik"}>
            {busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            PDF
          </button>
        </div>
      )}
      {error ? <p className="max-w-56 text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}

function AdminOrdersPage() {
  return <OrdersResourcePage title="Siparişler" endpoint="/api/admin/orders" admin />;
}

function AdminShipmentsPage() {
  return <ShipmentsResourcePage title="Gönderiler" endpoint="/api/admin/shipments" admin />;
}

const adminReportCatalog = [
  { slug: "daily-sales", title: "Günlük satış", detail: "Gün bazında satış, KDV, manual ve bonus hareketleri." },
  { slug: "customer-sales", title: "Müşteri bazlı satış", detail: "Müşteri geliri, kullanım, bakiye ve son hareketler." },
  { slug: "product-sales", title: "Ürün/Kg bazlı satış", detail: "Ürün satışları, kullanım ve stok bitiş tahmini." },
  { slug: "revenue-margin", title: "Gelir ve marj", detail: "Maliyet verisi yoksa açık uyarılı gelir analizi." },
  { slug: "credit-movements", title: "Kredi hareketleri", detail: "Satın alma, manual, bonus ve kalan etiket hareketleri." },
  { slug: "low-credit", title: "Düşük bakiye", detail: "Kritik bakiyeli müşteri ve önerilen aksiyonlar." },
  { slug: "stock-inventory", title: "Stok ve label inventory", detail: "Upload, atanmış, kullanılan, void ve carrier hata sayıları." },
  { slug: "upload-errors", title: "Upload hataları", detail: "Hatalı veya tekrar işlenebilir kontrol kayıtları." },
  { slug: "carrier-shipments", title: "Carrier bazlı gönderiler", detail: "Teslim, yolda, iade ve problem oranları." },
  { slug: "problem-shipments", title: "Sorunlu gönderiler", detail: "İade, exception, carrier check error ve takip hataları." },
  { slug: "delivery-performance", title: "Teslimat performansı", detail: "Günlük teslim, iade ve exception oranları." },
  { slug: "customer-health", title: "Müşteri sağlığı", detail: "Kullanım, gelir, bakiye, mağaza ve health score." },
  { slug: "invoice-vat", title: "Fatura ve KDV", detail: "Net, KDV, brüt ve fatura durumları." },
];

const customerReportCatalog = [
  { slug: "shipping-health", title: "Kargo sağlığı", detail: "Teslim, yolda, geciken, sorunlu ve müdahale gereken gönderiler." },
  { slug: "monthly-usage", title: "Aylık kullanım", detail: "Kullanılan etiket, teslim, yolda, iade ve sorun görünümü." },
  { slug: "shipment-performance", title: "Teslimat performansı", detail: "Carrier ve platform bazında paket performansı." },
  { slug: "label-balances", title: "Etiket bakiyeleri", detail: "Ürün/kg bazında mevcut bakiye ve tahmini bitiş." },
  { slug: "problem-shipments", title: "Sorunlu gönderiler", detail: "İade, carrier hatası ve takip senkronu sorunları." },
  { slug: "orders-awaiting-label", title: "Etiket bekleyen siparişler", detail: "Açık kalan ve etiketi oluşturulmamış siparişler." },
  { slug: "purchase-history", title: "Satın alma raporu", detail: "Etiket alımları, tutar ve ürün kırılımı." },
];

function AdminReportsPage() {
  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Raporlar"
        caption="Satış, müşteri sağlığı, carrier durumları, stok, fatura ve operasyon kontrol raporları."
        icon={<BarChart3 className="h-5 w-5" />}
        action={
          <Link className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800" to="/admin/reports/order-sync">
            <Clock3 className="h-4 w-4" />
            Kontrol raporu
          </Link>
        }
      />
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {adminReportCatalog.map((report) => (
          <Link
            key={report.slug}
            to={`/admin/reports/${report.slug}`}
            className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-slate-950 group-hover:underline">{report.title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-500">{report.detail}</p>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />
            </div>
          </Link>
        ))}
      </section>
      <ResourcePage
        title="Operasyon rapor özeti"
        caption="Hata kayıtları ve arka plan işlem izleri."
        endpoint="/api/admin/reports"
        sections={[
          {
            key: "trackingPerformance",
            title: "Takip ve gönderi performansı",
            columns: [
              { label: "Metrik", value: (row) => <NameStack title={text(row.metric)} detail={text(row.detail)} /> },
              { label: "Değer", value: (row) => numberText(row.value), align: "right" },
            ],
          },
          {
            key: "dhlPerformance",
            title: "DHL kontrol performansı",
            columns: [
              { label: "Metrik", value: (row) => <NameStack title={text(row.metric)} detail={text(row.detail)} /> },
              { label: "Değer", value: (row) => numberText(row.value), align: "right" },
            ],
          },
          {
            key: "performanceRisks",
            title: "Performans risk göstergeleri",
            columns: [
              { label: "Alan", value: (row) => <NameStack title={text(row.area)} detail={text(row.detail)} /> },
              { label: "Kayıt", value: (row) => numberText(row.value), align: "right" },
            ],
          },
          {
            key: "orderSyncHealth",
            title: "Otomatik sipariş kontrolü",
            columns: [
              { label: "Metrik", value: (row) => <NameStack title={text(row.metric)} detail={text(row.detail)} /> },
              { label: "Değer", value: (row) => numberText(row.value), align: "right" },
            ],
          },
        ]}
      />
    </div>
  );
}

function reportValue(row: DataRow, column: DataRow) {
  const value = row[text(column.key)];
  const type = text(column.type);
  if (type === "money") return moneyText(value, "EUR");
  if (type === "number") return numberText(value);
  if (type === "percent") return percentText(value);
  if (type === "date") return dateText(value);
  if (type === "status") return statusBadge(text(value));
  return text(value) || "-";
}

function ReportWorkspacePage({
  apiBase,
  basePath,
  catalog,
  defaultSlug,
  eyebrow,
  showCustomerFilter = false,
  slug,
}: {
  apiBase: string;
  basePath: string;
  catalog: Array<{ slug: string; title: string; detail: string }>;
  defaultSlug: string;
  eyebrow: string;
  showCustomerFilter?: boolean;
  slug?: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<DataRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activeSlug = slug || defaultSlug;
  const activeReport = catalog.find((report) => report.slug === activeSlug) || catalog[0];
  const page = Math.max(0, Number(searchParams.get("page") || 0));
  const dateFrom = searchParams.get("dateFrom") || "";
  const dateTo = searchParams.get("dateTo") || "";
  const customerId = searchParams.get("customerId") || "";
  const productId = searchParams.get("productId") || "";
  const platform = searchParams.get("platform") || "";
  const status = searchParams.get("status") || "";

  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("page", "0");
    setSearchParams(next);
  }

  function updatePage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(Math.max(0, nextPage)));
    setSearchParams(next);
  }

  const query = useMemo(() => {
    const params = new URLSearchParams({ limit: "50", offset: String(page * 50) });
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    if (customerId) params.set("customerId", customerId);
    if (productId) params.set("productId", productId);
    if (platform) params.set("platform", platform);
    if (status) params.set("status", status);
    return params;
  }, [customerId, dateFrom, dateTo, page, platform, productId, status]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setData(null);
    apiClient
      .resource<DataRow>(`${apiBase}/${encodeURIComponent(activeSlug)}?${query.toString()}`)
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Rapor alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [activeSlug, apiBase, query]);

  const columns = Array.isArray(data?.columns) ? (data?.columns as DataRow[]) : [];
  const rows = Array.isArray(data?.rows) ? (data?.rows as DataRow[]) : [];
  const cards = Array.isArray(data?.summaryCards) ? (data?.summaryCards as DataRow[]) : [];
  const meta = (data?.meta || {}) as DataRow;
  const csvParams = new URLSearchParams(query);
  csvParams.set("export", "csv");
  const excelParams = new URLSearchParams(query);
  excelParams.set("export", "xls");
  const reportTitle = text(data?.title) || activeReport?.title || "Rapor";
  const reportCaption = text(data?.caption) || activeReport?.detail || "Filtrelenebilir rapor detayı.";

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title={reportTitle}
        caption={reportCaption}
        icon={<BarChart3 className="h-5 w-5" />}
        eyebrow={eyebrow}
        action={
          <div className="flex flex-wrap justify-end gap-2">
            <a
              href={`${apiBase}/${encodeURIComponent(activeSlug)}?${csvParams.toString()}`}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <Download className="h-4 w-4" />
              CSV indir
            </a>
            <a
              href={`${apiBase}/${encodeURIComponent(activeSlug)}?${excelParams.toString()}`}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              <FileText className="h-4 w-4" />
              Excel indir
            </a>
          </div>
        }
      />
      {error ? <ErrorBanner message={error} /> : null}
      <div className="grid gap-5 xl:grid-cols-[280px_minmax(0,1fr)]">
        <AdminPanel title="Raporlar" count={catalog.length}>
          <nav className="grid gap-2 p-3" aria-label="Rapor listesi">
            {catalog.map((report) => {
              const selected = report.slug === activeSlug;
              return (
                <Link
                  key={report.slug}
                  to={`${basePath}/${report.slug}`}
                  className={`rounded-xl border px-3 py-3 text-left transition ${
                    selected ? "border-slate-900 bg-slate-950 text-white shadow-sm" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <span className="block text-sm font-semibold">{report.title}</span>
                  <span className={`mt-1 block text-xs leading-5 ${selected ? "text-slate-300" : "text-slate-500"}`}>{report.detail}</span>
                </Link>
              );
            })}
          </nav>
        </AdminPanel>
        <div className="min-w-0 space-y-5">
          <AdminPanel title="Filtreler">
            <div className="grid gap-3 p-5 md:grid-cols-2 xl:grid-cols-3">
              <ProfileInput label="Başlangıç tarihi" type="date" value={dateFrom} onChange={(value) => updateFilter("dateFrom", value)} />
              <ProfileInput label="Bitiş tarihi" type="date" value={dateTo} onChange={(value) => updateFilter("dateTo", value)} />
              {showCustomerFilter ? <ProfileInput label="Müşteri / e-posta" value={customerId} onChange={(value) => updateFilter("customerId", value)} /> : null}
              <ProfileInput label="Ürün / kg" value={productId} onChange={(value) => updateFilter("productId", value)} />
              <ProfileInput label="Platform" value={platform} onChange={(value) => updateFilter("platform", value)} />
              <ProfileInput label="Hareket / durum" value={status} onChange={(value) => updateFilter("status", value)} />
            </div>
          </AdminPanel>
          {loading ? (
            <LoadingPanel label="Rapor yükleniyor" />
          ) : (
            <>
              <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {cards.map((card, index) => (
                  <AdminStatCard
                    key={`${text(card.key)}-${index}`}
                    icon={<BarChart3 className="h-5 w-5" />}
                    label={text(card.label)}
                    value={reportValue(card, { key: "value", type: card.type })}
                    tone={index === 0 ? "emerald" : index === 1 ? "cyan" : index === 2 ? "amber" : "slate"}
                  />
                ))}
              </section>
              <AdminPanel title="Rapor önizleme" count={Number(meta.totalCount || rows.length)}>
                <DataTable
                  rows={rows}
                  empty="Bu filtreye uygun rapor kaydı yok."
                  columns={columns.map((column) => ({
                    label: text(column.label),
                    value: (row: DataRow) => reportValue(row, column),
                    align: ["money", "number", "percent"].includes(text(column.type)) ? "right" : "left",
                  }))}
                />
                <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50 px-5 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                  <span>
                    {rows.length === 0
                      ? "Kayıt yok"
                      : `${numberText(Number(meta.offset || 0) + 1)}-${numberText(Number(meta.offset || 0) + rows.length)} / ${numberText(meta.totalCount)} kayıt`}
                  </span>
                  <div className="flex gap-2">
                    <button type="button" disabled={loading || page === 0} onClick={() => updatePage(page - 1)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50">
                      Önceki
                    </button>
                    <button type="button" disabled={loading || meta.hasMore !== true} onClick={() => updatePage(page + 1)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50">
                      Sonraki
                    </button>
                  </div>
                </div>
              </AdminPanel>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminGenericReportPage({ slug }: { slug: string }) {
  return <ReportWorkspacePage apiBase="/api/admin/reports" basePath="/admin/reports" catalog={adminReportCatalog} defaultSlug="daily-sales" eyebrow="Yönetim" showCustomerFilter slug={slug} />;
}

function CustomerReportsPage() {
  const { slug } = useParams();
  return <ReportWorkspacePage apiBase="/api/reports" basePath="/customer/reports" catalog={customerReportCatalog} defaultSlug="monthly-usage" eyebrow="Müşteri paneli" slug={slug} />;
}

type ShippingHealthTone = "emerald" | "cyan" | "amber" | "red" | "slate" | "violet";

type ShippingHealthKpi = {
  detail: string;
  displayValue: string;
  key: string;
  label: string;
  percentValue?: number;
  tone: ShippingHealthTone;
  value: number;
};

type ShippingHealthFunnelStep = {
  count: number;
  key: string;
  label: string;
  rate: number;
  tone: ShippingHealthTone;
};

type ShippingHealthIntervention = {
  action?: string;
  ageDays: number;
  carrier?: string;
  carrierMessage?: string;
  city?: string;
  countryCode?: string;
  createdAt?: string | null;
  hasPdf?: boolean;
  id: string;
  orderNumber?: string;
  platform?: string;
  problemType: string;
  productName?: string;
  recipientName?: string;
  severity: "critical" | "warning" | "info" | string;
  shipmentId: string;
  storeName?: string;
  title: string;
  trackingNumber?: string;
};

type ShippingHealthAlert = {
  detail: string;
  id: string;
  severity: "critical" | "warning" | "info" | string;
  title: string;
};

type ShippingHealthComparisonRow = {
  avgAgeDays?: number;
  delivered?: number;
  deliveredRate?: number;
  inTransit?: number;
  label: string;
  noFirstScan?: number;
  problem?: number;
  problemRate?: number;
  returnRate?: number;
  returns?: number;
  risk?: number;
  riskRate?: number;
  total: number;
};

type ShippingHealthResponse = {
  alerts: ShippingHealthAlert[];
  comparisons: {
    cities: ShippingHealthComparisonRow[];
    dhlProducts: ShippingHealthComparisonRow[];
    platforms: ShippingHealthComparisonRow[];
    stores: ShippingHealthComparisonRow[];
  };
  funnel: ShippingHealthFunnelStep[];
  generatedAt?: string | null;
  interventions: ShippingHealthIntervention[];
  kpis: ShippingHealthKpi[];
  ok?: boolean;
  rangeDays: number;
  totals: {
    carrierChecked?: number;
    delivered: number;
    healthScore: number;
    inTransit: number;
    noFirstScan: number;
    outgoing: number;
    problem: number;
    returns: number;
    risk: number;
    trackingEligible?: number;
    trackingSuccess?: number;
  };
};

function emptyShippingHealth(rangeDays: number): ShippingHealthResponse {
  return {
    alerts: [
      {
        id: "ready",
        severity: "info",
        title: "DHL sağlık ekranı hazır",
        detail: "Gönderi ve carrier check kayıtları geldikçe alarm, oran ve müdahale listeleri otomatik dolacak.",
      },
    ],
    comparisons: { cities: [], dhlProducts: [], platforms: [], stores: [] },
    funnel: [
      { key: "label", label: "Etiket oluşturuldu", count: 0, rate: 100, tone: "cyan" },
      { key: "scan", label: "İlk carrier scan", count: 0, rate: 0, tone: "slate" },
      { key: "transit", label: "Yolda", count: 0, rate: 0, tone: "amber" },
      { key: "delivered", label: "Teslim edildi", count: 0, rate: 0, tone: "emerald" },
      { key: "exception", label: "İade / Sorunlu", count: 0, rate: 0, tone: "red" },
    ],
    generatedAt: null,
    kpis: [
      { key: "outgoing", label: "Çıkan kargo", value: 0, displayValue: "0", detail: `${rangeDays} günlük DHL etiketi`, tone: "cyan", percentValue: 100 },
      { key: "delivered", label: "Teslim oranı", value: 0, displayValue: "0%", detail: "0 teslim", tone: "emerald", percentValue: 0 },
      { key: "inTransit", label: "Yolda", value: 0, displayValue: "0%", detail: "0 kargo", tone: "cyan", percentValue: 0 },
      { key: "risk", label: "Riskli", value: 0, displayValue: "0%", detail: "0 kargo", tone: "amber", percentValue: 0 },
      { key: "problem", label: "Sorunlu", value: 0, displayValue: "0%", detail: "0 exception", tone: "red", percentValue: 0 },
      { key: "returns", label: "İade", value: 0, displayValue: "0%", detail: "0 gönderi", tone: "violet", percentValue: 0 },
      { key: "tracking", label: "Takip senkron", value: 0, displayValue: "0%", detail: "0/0 başarılı", tone: "emerald", percentValue: 0 },
      { key: "firstScan", label: "İlk scan yok", value: 0, displayValue: "0", detail: "2 günü aşan kayıt", tone: "slate", percentValue: 0 },
    ],
    interventions: [],
    ok: true,
    rangeDays,
    totals: { delivered: 0, healthScore: 0, inTransit: 0, noFirstScan: 0, outgoing: 0, problem: 0, returns: 0, risk: 0 },
  };
}

const shippingHealthToneStyles: Record<ShippingHealthTone, { accent: string; bg: string; border: string; text: string }> = {
  amber: { accent: "#f59e0b", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700" },
  cyan: { accent: "#06b6d4", bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-700" },
  emerald: { accent: "#10b981", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
  red: { accent: "#ef4444", bg: "bg-red-50", border: "border-red-200", text: "text-red-700" },
  slate: { accent: "#64748b", bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-600" },
  violet: { accent: "#8b5cf6", bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700" },
};

function shippingHealthKpiIcon(key: string) {
  if (key === "outgoing") return <Package className="h-4 w-4" />;
  if (key === "delivered") return <CheckCircle2 className="h-4 w-4" />;
  if (key === "inTransit") return <Truck className="h-4 w-4" />;
  if (key === "risk") return <Clock3 className="h-4 w-4" />;
  if (key === "problem") return <AlertTriangle className="h-4 w-4" />;
  if (key === "returns") return <History className="h-4 w-4" />;
  if (key === "tracking") return <Send className="h-4 w-4" />;
  return <Search className="h-4 w-4" />;
}

function ShippingHealthKpiCard({ item }: { item: ShippingHealthKpi }) {
  const tone = shippingHealthToneStyles[item.tone] || shippingHealthToneStyles.slate;
  const percent = Math.max(0, Math.min(100, Number(item.percentValue ?? 0)));
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-slate-500">{item.label}</p>
          <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{item.displayValue}</p>
          <p className="mt-1 truncate text-xs font-medium text-slate-500">{item.detail}</p>
        </div>
        <div className={cx("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border", tone.bg, tone.border, tone.text)}>
          {shippingHealthKpiIcon(item.key)}
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <div
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full"
          style={{ background: `conic-gradient(${tone.accent} ${percent}%, #e2e8f0 0)` }}
          aria-hidden="true"
        >
          <div className="h-6 w-6 rounded-full bg-white" />
        </div>
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full" style={{ width: `${percent}%`, backgroundColor: tone.accent }} />
        </div>
      </div>
    </article>
  );
}

function ShippingHealthFunnel({ steps }: { steps: ShippingHealthFunnelStep[] }) {
  return (
    <AdminPanel title="Gönderi akışı" caption="Etiket oluşturulduktan teslim ya da sorun sinyaline kadar DHL operasyon hunisi.">
      <div className="grid gap-3 p-4 lg:grid-cols-5">
        {steps.map((step, index) => {
          const tone = shippingHealthToneStyles[step.tone] || shippingHealthToneStyles.slate;
          return (
            <div key={step.key} className="flex min-w-0 items-stretch gap-3">
              <div className={cx("min-w-0 flex-1 rounded-xl border px-4 py-3", tone.bg, tone.border)}>
                <p className={cx("truncate text-xs font-semibold", tone.text)}>{step.label}</p>
                <p className="mt-2 text-xl font-semibold text-slate-950">{numberText(step.count)}</p>
                <p className="mt-1 text-xs font-medium text-slate-500">{percentText(step.rate)}</p>
              </div>
              {index < steps.length - 1 ? <div className="hidden items-center text-slate-300 lg:flex"><ChevronRight className="h-5 w-5" /></div> : null}
            </div>
          );
        })}
      </div>
    </AdminPanel>
  );
}

function ShippingHealthAlertPanel({ alerts }: { alerts: ShippingHealthAlert[] }) {
  const iconFor = (severity: string) => (severity === "critical" ? <AlertTriangle className="h-4 w-4" /> : severity === "warning" ? <Clock3 className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />);
  const classFor = (severity: string) =>
    severity === "critical"
      ? "border-red-200 bg-red-50 text-red-700"
      : severity === "warning"
        ? "border-amber-200 bg-amber-50 text-amber-700"
        : "border-cyan-200 bg-cyan-50 text-cyan-700";
  return (
    <AdminPanel title="Operasyon alarmı" count={alerts.length}>
      <div className="divide-y divide-slate-100">
        {alerts.map((alert) => (
          <article key={alert.id} className="flex items-start gap-3 px-4 py-4">
            <div className={cx("mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border", classFor(alert.severity))}>{iconFor(alert.severity)}</div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-950">{alert.title}</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">{alert.detail}</p>
            </div>
          </article>
        ))}
      </div>
    </AdminPanel>
  );
}

function ShippingHealthComparisonPanel({
  rows,
  title,
  valueLabel = "Teslim oranı",
  valueKey = "deliveredRate",
}: {
  rows: ShippingHealthComparisonRow[];
  title: string;
  valueKey?: "deliveredRate" | "riskRate" | "problemRate" | "returnRate";
  valueLabel?: string;
}) {
  return (
    <AdminPanel title={title} count={rows.length}>
      {rows.length === 0 ? (
        <div className="flex min-h-44 flex-col items-center justify-center gap-2 bg-slate-50/60 p-6 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400 shadow-sm">
            <BarChart3 className="h-4 w-4" />
          </div>
          <p className="text-sm font-medium text-slate-600">Bu dönem için karşılaştırma verisi yok.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((row) => {
            const value = Number(row[valueKey] || 0);
            const tone = valueKey === "riskRate" || valueKey === "problemRate" ? (value >= 20 ? shippingHealthToneStyles.red : value >= 8 ? shippingHealthToneStyles.amber : shippingHealthToneStyles.emerald) : shippingHealthToneStyles.emerald;
            return (
              <div key={row.label} className="px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-950">{row.label}</p>
                    <p className="mt-1 text-xs text-slate-500">{numberText(row.total)} çıkan · {numberText(row.problem || 0)} sorun · {numberText(row.returns || 0)} iade</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-950">{percentText(value)}</p>
                    <p className="mt-1 text-xs text-slate-500">{valueLabel}</p>
                  </div>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, value))}%`, backgroundColor: tone.accent }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminPanel>
  );
}

function ShippingHealthInterventions({
  acknowledgedIds,
  actionId,
  interventions,
  onAcknowledge,
  onSync,
}: {
  acknowledgedIds: Set<string>;
  actionId: string;
  interventions: ShippingHealthIntervention[];
  onAcknowledge: (id: string) => void;
  onSync: (item: ShippingHealthIntervention) => void;
}) {
  const severityClass = (severity: string) =>
    severity === "critical"
      ? "border-red-200 bg-red-50 text-red-700"
      : severity === "warning"
        ? "border-amber-200 bg-amber-50 text-amber-700"
        : "border-cyan-200 bg-cyan-50 text-cyan-700";
  return (
    <AdminPanel title="Müdahale Gerekenler" count={interventions.length} caption="Günlük operasyon için ilk bakılması gereken DHL gönderileri.">
      {interventions.length === 0 ? (
        <div className="flex min-h-56 flex-col items-center justify-center gap-3 bg-slate-50/60 p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-emerald-500 shadow-sm">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <p className="text-sm font-semibold text-slate-700">Müdahale gerektiren gönderi görünmüyor.</p>
          <p className="max-w-xl text-sm leading-6 text-slate-500">DHL carrier check, tracking senkronu ve ilk scan kayıtları geldikçe bu liste otomatik önceliklenecek.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {interventions.map((item) => {
            const acknowledged = acknowledgedIds.has(item.id);
            const working = actionId === item.id;
            return (
              <article key={item.id} className={cx("grid gap-4 px-4 py-4 transition lg:grid-cols-[minmax(0,1fr)_minmax(220px,auto)]", acknowledged && "bg-slate-50/80 opacity-70")}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cx("rounded-full border px-2.5 py-1 text-xs font-semibold", severityClass(item.severity))}>{item.problemType}</span>
                    {item.platform ? badge(item.platform) : null}
                    <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-500">{numberText(item.ageDays)} gün</span>
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,0.8fr)]">
                    <NameStack title={item.trackingNumber || item.shipmentId} detail={item.orderNumber || item.title} />
                    <NameStack title={item.recipientName || "-"} detail={compactAddress(item.city, item.countryCode)} />
                    <NameStack title={item.storeName || "-"} detail={`${item.carrier || "DHL"} · ${item.productName || "-"}`} />
                  </div>
                  {item.carrierMessage ? <p className="mt-3 line-clamp-2 text-xs leading-5 text-slate-500">{item.carrierMessage}</p> : null}
                </div>
                <div className="flex flex-wrap items-center justify-start gap-2 lg:justify-end">
                  <button
                    type="button"
                    onClick={() => onSync(item)}
                    disabled={working || !item.shipmentId}
                    className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-slate-950 px-3 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
                  >
                    {working ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Takibi yenile
                  </button>
                  <Link to={`/customer/shipments/${encodeURIComponent(item.shipmentId)}`} className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">
                    Detay
                  </Link>
                  {item.hasPdf ? (
                    <a href={shipmentLabelHref(item.shipmentId, "normal", true)} download className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">
                      PDF indir
                    </a>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onAcknowledge(item.id)}
                    className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    {acknowledged ? "İşaretlendi" : "İşaretle"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </AdminPanel>
  );
}

function ShippingHealthPage() {
  const [rangeDays, setRangeDays] = useState(30);
  const [data, setData] = useState<ShippingHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [actionId, setActionId] = useState("");
  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<string>>(() => new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    apiClient
      .resource<ShippingHealthResponse>(`/api/reports/shipping-health?rangeDays=${encodeURIComponent(String(rangeDays))}`)
      .then((result) => {
        if (active) setData(result && Array.isArray(result.kpis) ? result : null);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Kargo sağlığı alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [rangeDays, refreshTick]);

  async function syncIntervention(item: ShippingHealthIntervention) {
    if (!item.shipmentId) return;
    setActionId(item.id);
    setNotice(null);
    setError(null);
    try {
      const result = await apiClient.syncShipmentTracking(item.shipmentId);
      setNotice(result.message || "Takip senkronu başlatıldı.");
      setRefreshTick((value) => value + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Takip senkronu başlatılamadı.");
    } finally {
      setActionId("");
    }
  }

  function toggleAcknowledged(id: string) {
    setAcknowledgedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const health = data || emptyShippingHealth(rangeDays);
  const hasOperationalData = Number(health.totals.outgoing || 0) > 0;
  const rangeOptions = [30, 90, 180];

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Kargo Sağlığı"
        caption="DHL gönderilerinizin teslim, yolda, geciken, sorunlu, iade ve tracking senkron durumunu tek operasyonda görün."
        eyebrow="Müşteri paneli"
        icon={<ShieldCheck className="h-5 w-5" />}
        action={
          <div className="flex flex-wrap justify-end gap-2">
            <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
              {rangeOptions.map((days) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => setRangeDays(days)}
                  className={cx(
                    "h-9 rounded-lg px-3 text-xs font-semibold transition",
                    rangeDays === days ? "bg-slate-950 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                  )}
                >
                  {days} gün
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setRefreshTick((value) => value + 1)}
              disabled={loading}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
              Yenile
            </button>
          </div>
        }
      />

      {notice ? <AdminNotice message={notice} /> : null}
      {error ? <ErrorBanner message={error} /> : null}

      <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 text-white shadow-sm">
        <div className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-center">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex h-9 items-center gap-2 rounded-full bg-yellow-300 px-3 text-xs font-black tracking-wide text-slate-950">DHL</span>
              <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-100">
                {hasOperationalData ? "Sistem izleniyor" : "Veri bekleniyor"}
              </span>
              <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold text-slate-200">
                SLA: 2 gün ilk scan · 5/7 gün risk
              </span>
            </div>
            <h2 className="mt-4 text-2xl font-semibold tracking-tight">Bugün operasyon sağlığı</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
              {hasOperationalData
                ? `${numberText(health.totals.outgoing)} DHL gönderisi izleniyor; ${numberText(health.interventions.length)} kayıt müdahale listesinde.`
                : "DHL kayıtları geldikçe teslimat, gecikme ve exception sinyalleri bu ekranda dolacak."}
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Sağlık skoru</p>
              <p className="mt-2 text-3xl font-semibold">{hasOperationalData ? percentText(health.totals.healthScore) : "-"}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Carrier check kapsamı</p>
              <p className="mt-2 text-3xl font-semibold">{hasOperationalData ? percentText((Number(health.totals.carrierChecked || 0) / Math.max(1, Number(health.totals.outgoing || 0))) * 100) : "-"}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Son güncelleme</p>
              <p className="mt-2 text-lg font-semibold">{health.generatedAt ? dateText(health.generatedAt) : "Başlangıç"}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-8">
        {health.kpis.map((item) => (
          <ShippingHealthKpiCard key={item.key} item={item} />
        ))}
      </section>

      <ShippingHealthFunnel steps={health.funnel} />

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_390px]">
        <ShippingHealthInterventions
          acknowledgedIds={acknowledgedIds}
          actionId={actionId}
          interventions={health.interventions || []}
          onAcknowledge={toggleAcknowledged}
          onSync={(item) => void syncIntervention(item)}
        />
        <ShippingHealthAlertPanel alerts={health.alerts || []} />
      </section>

      <section className="grid gap-5 xl:grid-cols-2 2xl:grid-cols-4">
        <ShippingHealthComparisonPanel title="DHL ürün performansı" rows={health.comparisons.dhlProducts || []} valueLabel="Teslim oranı" valueKey="deliveredRate" />
        <ShippingHealthComparisonPanel title="Mağaza performansı" rows={health.comparisons.stores || []} valueLabel="Sorun oranı" valueKey="problemRate" />
        <ShippingHealthComparisonPanel title="Platform performansı" rows={health.comparisons.platforms || []} valueLabel="Teslim oranı" valueKey="deliveredRate" />
        <ShippingHealthComparisonPanel title="Şehir / bölge gecikmeleri" rows={health.comparisons.cities || []} valueLabel="Risk oranı" valueKey="riskRate" />
      </section>
    </div>
  );
}

function AdminOrderSyncReportPage() {
  const [status, setStatus] = useState("failed");
  const [platform, setPlatform] = useState("all");
  const [search, setSearch] = useState("");
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function loadReport() {
    const params = new URLSearchParams({ status, platform, limit: "100" });
    const normalizedSearch = search.trim();
    if (normalizedSearch) params.set("search", normalizedSearch);
    setLoading(true);
    setError(null);
    try {
      setData(await apiClient.resource<Record<string, unknown>>(`/api/admin/order-sync-runs?${params.toString()}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kontrol raporu alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const handle = window.setTimeout(() => void loadReport(), 220);
    return () => window.clearTimeout(handle);
  }, [status, platform, search]);

  async function runDueNow() {
    setRunning(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.runDueOrderSync(10);
      setNotice(`${numberText(result.processed)} mağaza kontrol edildi.`);
      await loadReport();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Otomatik kontrol başlatılamadı.");
    } finally {
      setRunning(false);
    }
  }

  const summary = (data?.summary || {}) as DataRow;
  const runs = Array.isArray(data?.runs) ? (data?.runs as DataRow[]) : [];
  const storeStatus = Array.isArray(data?.storeStatus) ? (data?.storeStatus as DataRow[]) : [];

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Mağaza kontrol raporu"
        caption="Otomatik sipariş alma denemeleri, hatalar ve sıradaki kontroller."
        icon={<Clock3 className="h-5 w-5" />}
        action={
          <button
            type="button"
            onClick={() => void runDueNow()}
            disabled={running}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
            Şimdi kontrol et
          </button>
        }
      />
      {notice ? <AdminNotice message={notice} /> : null}
      {error ? <ErrorBanner message={error} /> : null}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Hata" value={summary.failed || 0} tone="red" icon={<AlertTriangle className="h-5 w-5" />} />
        <AdminStatCard label="Başarılı" value={summary.success || 0} tone="emerald" icon={<CheckCircle2 className="h-5 w-5" />} />
        <AdminStatCard label="Çalışıyor" value={summary.running || 0} tone="cyan" icon={<Loader2 className="h-5 w-5" />} />
        <AdminStatCard label="Toplam deneme" value={summary.total || 0} tone="slate" icon={<BarChart3 className="h-5 w-5" />} />
      </section>

      <AdminPanel title="Filtreler">
        <div className="grid gap-3 p-5 lg:grid-cols-[180px_180px_minmax(0,1fr)_auto]">
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">Durum</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
              <option value="failed">Hatalar</option>
              <option value="success">Başarılı</option>
              <option value="running">Çalışıyor</option>
              <option value="skipped">Atlandı</option>
              <option value="all">Tüm durumlar</option>
            </select>
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">Platform</span>
            <select value={platform} onChange={(event) => setPlatform(event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
              <option value="all">Tümü</option>
              {storePlatformOptions.filter((item) => internalOrderSyncPlatforms.includes(item.value as OrderSyncPlatform)).map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">Arama</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400"
              placeholder="Mağaza, müşteri veya e-posta"
            />
          </label>
          <button type="button" onClick={() => void loadReport()} className="self-end rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
            Filtrele
          </button>
        </div>
      </AdminPanel>

      {loading ? (
        <LoadingPanel label="Kontrol raporu yükleniyor" />
      ) : (
        <>
          <AdminPanel title="Son kontrol denemeleri" count={runs.length}>
            <DataTable
              rows={runs}
              empty="Bu filtreye uygun kontrol kaydı yok."
              columns={[
                { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                { label: "Mağaza", value: (row) => <NameStack title={text(row.storeName)} detail={text(row.userCompanyName) || text(row.userEmail)} /> },
                { label: "Platform", value: (row) => badge(text(row.platform)) },
                { label: "Sonuç", value: (row) => <NameStack title={`${numberText(row.fetchedCount)} çekildi`} detail={`Yeni ${numberText(row.insertedCount)}, güncellenen ${numberText(row.updatedCount)}`} /> },
                { label: "Hata", value: (row) => <span className="block max-w-sm whitespace-normal break-words">{text(row.errorMessage) || "-"}</span> },
                { label: "Başlangıç", value: (row) => dateText(row.startedAt) },
              ]}
            />
          </AdminPanel>

          <AdminPanel title="Mağaza otomatik kontrol durumu" count={storeStatus.length}>
            <DataTable
              rows={storeStatus}
              empty="Otomatik kontrol açık mağaza yok."
              columns={[
                { label: "Mağaza", value: (row) => <NameStack title={text(row.name)} detail={text(row.userCompanyName) || text(row.userEmail)} /> },
                { label: "Platform", value: (row) => badge(text(row.platform)) },
                { label: "Sıklık", value: (row) => orderSyncIntervalOptions.find((option) => option.value === Number(row.orderSyncIntervalMinutes))?.label || `${numberText(row.orderSyncIntervalMinutes)} dk` },
                { label: "Durum", value: (row) => statusBadge(text(row.orderSyncLastStatus)) },
                { label: "Hata", value: (row) => numberText(row.orderSyncConsecutiveFailures), align: "right" },
                { label: "Sıradaki", value: (row) => dateText(row.orderSyncNextRunAt) },
              ]}
            />
          </AdminPanel>
        </>
      )}
    </div>
  );
}

function StoresPage() {
  const [stores, setStores] = useState<StoreSummary[]>([]);
  const [showInactiveStores, setShowInactiveStores] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncingStoreId, setSyncingStoreId] = useState<string | null>(null);
  const [storeActionId, setStoreActionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  async function loadStores() {
    setError(null);
    setLoading(true);
    try {
      const result = await apiClient.stores({ includeInactive: true });
      setStores(result.stores);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mağazalar alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadStores();
  }, []);

  async function syncEbay(storeId: string) {
    setSyncingStoreId(storeId);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.syncEbay(storeId);
      setNotice(`${result.fetched} eBay siparişi alındı. Yeni: ${result.inserted}, güncellenen: ${result.updated}.`);
      await loadStores();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "eBay siparişleri alınamadı.");
    } finally {
      setSyncingStoreId(null);
    }
  }

  async function testJtl(storeId: string) {
    const actionKey = `jtl-test:${storeId}`;
    setSyncingStoreId(actionKey);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.testJtl(storeId);
      setNotice(result.message || (result.ok ? "JTL bağlantısı hazır." : "JTL bağlantısı kontrol edilemedi."));
      await loadStores();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "JTL bağlantısı test edilemedi.");
    } finally {
      setSyncingStoreId(null);
    }
  }

  async function syncJtl(storeId: string) {
    const actionKey = `jtl-sync:${storeId}`;
    setSyncingStoreId(actionKey);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.syncJtl(storeId);
      setNotice(result.message || `${result.imported || 0} JTL siparişi işlendi.`);
      await loadStores();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "JTL siparişleri alınamadı.");
    } finally {
      setSyncingStoreId(null);
    }
  }

  async function syncInternalPlatformOrders(storeId: string, platform: OrderSyncPlatform) {
    if (platform === "amazon") { window.location.assign("/customer/stores/amazon/new"); return; }
    const actionKey = `orders:${platform}:${storeId}`;
    setSyncingStoreId(actionKey);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.syncStoreOrders(storeId, platform);
      setNotice(
        result.message ||
          `${result.fetched} ${platform} siparişi alındı. Yeni: ${result.inserted}, güncellenen: ${result.updated}.`
      );
      await loadStores();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `${platform} siparişleri alınamadı.`);
    } finally {
      setSyncingStoreId(null);
    }
  }

  async function toggleStoreStatus(store: StoreSummary) {
    if (store.platform === "amazon") { window.location.assign("/customer/stores/amazon-v2/new"); return; }
    const actionKey = `status:${store.id}`;
    setStoreActionId(actionKey);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.updateStore(store.id, { active: !store.active });
      setNotice(`${result.store.name} mağazası ${result.store.active ? "aktif" : "pasif"} yapıldı.`);
      await loadStores();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mağaza durumu güncellenemedi.");
    } finally {
      setStoreActionId(null);
    }
  }

  async function deleteStore(store: StoreSummary) {
    if (Number(store.ordersCount || 0) > 0) {
      setError("Siparişi olan mağazalar silinemez. Önce siparişleri kontrol edin.");
      return;
    }
    const confirmed = await askConfirmation({
      title: "Mağaza silinsin mi?",
      description: `${store.name} mağazası sistemden kaldırılacak.`,
      details: ["Bu işlem mağaza bağlantısını kapatır. Sipariş içeren mağazalar silinemez."],
      confirmLabel: "Mağazayı sil",
      tone: "danger",
    });
    if (!confirmed) return;
    const actionKey = `delete:${store.id}`;
    setStoreActionId(actionKey);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.deleteCustomerStore(store.id);
      if (result.ok === false || result.deleted === false) {
        throw new Error(result.error || "Mağaza silinemedi.");
      }
      setNotice(`${result.storeName || store.name} mağazası silindi.`);
      await loadStores();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Mağaza silinemedi.");
    } finally {
      setStoreActionId(null);
    }
  }

  const {
    activeStoreRows,
    connectedIntegrationCounts,
    connectedStores,
    inactiveStoreRows,
    syncedStores,
    totalOrders,
  } = summarizeCustomerStores(stores);

  function startStoreSync(store: StoreSummary) {
    if (store.active === false) return;
    if (store.platform === "ebay") {
      void syncEbay(store.id);
    } else if (store.platform === "jtl") {
      void syncJtl(store.id);
    } else if (internalOrderSyncPlatforms.includes(store.platform as OrderSyncPlatform)) {
      void syncInternalPlatformOrders(store.id, store.platform as OrderSyncPlatform);
    }
  }

  function storeRow(store: StoreSummary) {
    const logoSrc = platformLogoSrc(store.platform);
    const syncActionKey = storeSyncActionKey(store);
    const active = store.active !== false;
    const reauthorizationRequired = ebayConnectionRequiresReauthorization(store);
    const canSync = active && store.syncReady !== false && Boolean(syncActionKey);
    const syncing = Boolean(syncActionKey && syncingStoreId === syncActionKey);
    const connectionDetail = store.ebayUsername || store.storeUrl || "Bağlantı detayı yok";

    return (
      <div
        key={store.id}
        className="grid gap-4 px-4 py-4 transition hover:bg-slate-50/70 lg:grid-cols-[minmax(0,1fr)_minmax(220px,auto)] lg:items-center 2xl:grid-cols-[minmax(0,1.45fr)_120px_180px_minmax(220px,auto)]"
      >
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
            {logoSrc ? <img src={logoSrc} alt={platformLabel(store.platform)} className="max-h-7 max-w-full object-contain" /> : <Store className="h-5 w-5 text-slate-500" />}
          </div>
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h3 className="max-w-full truncate text-sm font-semibold text-slate-950">{store.name}</h3>
              {badge(store.platform)}
              <span
                className={cx(
                  "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
                  reauthorizationRequired
                    ? "border-amber-200 bg-amber-50 text-amber-800"
                    : active
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-red-200 bg-red-50 text-red-700"
                )}
              >
                {reauthorizationRequired ? "Yeniden bağlantı gerekli" : active ? "Aktif" : "Pasif"}
              </span>
            </div>
            <p className="mt-1 truncate text-xs text-slate-500">{connectionDetail}</p>
            {store.ebayStatus && !reauthorizationRequired ? <div className="mt-2">{statusBadge(store.ebayStatus)}</div> : null}
            {reauthorizationRequired ? (
              <p className="mt-2 max-w-xl text-xs font-medium leading-5 text-amber-800">
                eBay bağlantı yetkisi sona erdi. Yeni siparişleri almak için bağlantıyı yenileyin.
              </p>
            ) : null}
          </div>
        </div>
        <div className="min-w-0 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Sipariş</p>
          <p className="mt-1 text-sm font-semibold text-slate-950">{numberText(store.ordersCount)}</p>
        </div>
        <div className="min-w-0 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Son senkron</p>
          <p className="mt-1 truncate text-sm font-semibold text-slate-950">{store.ordersSyncedAt ? formatDate(store.ordersSyncedAt) : "Henüz yok"}</p>
        </div>
        <div className="flex min-w-0 flex-wrap gap-2 lg:justify-end">
          <Link
            to={`/customer/orders?storeId=${encodeURIComponent(store.id)}`}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <ShoppingCart className="h-3.5 w-3.5" />
            Siparişler
          </Link>
          {canSync ? (
            <button
              type="button"
              disabled={syncing}
              onClick={() => startStoreSync(store)}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
            >
              {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clock3 className="h-3.5 w-3.5" />}
              Senkron
            </button>
          ) : null}
          {reauthorizationRequired ? (
            <a
              href={ebayReconnectHref(store.id)}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 text-xs font-semibold text-amber-900 shadow-sm transition hover:bg-amber-100"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              eBay bağlantısını yenile
            </a>
          ) : null}
          <StoreActionsMenu
            store={store}
            syncingStoreId={syncingStoreId}
            storeActionId={storeActionId}
            onDelete={deleteStore}
            onSyncEbay={syncEbay}
            onSyncInternal={syncInternalPlatformOrders}
            onSyncJtl={syncJtl}
            onTestJtl={testJtl}
            onToggleStatus={toggleStoreStatus}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {confirmationDialog}
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-950">Mağazalar</h1>
        <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
          Pazar yeri bağlantılarınızı, sipariş senkronunu ve mağaza ayarlarını tek yerden yönetin.
        </p>
      </div>

      {stores.some(store => store.platform === "amazon") ? <AmazonV1UpgradeNotice compact /> : null}
      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Mağaza" value={activeStoreRows.length} tone="cyan" icon={<Store className="h-5 w-5" />} />
        <AdminStatCard label="Aktif" value={activeStoreRows.length} tone="emerald" icon={<CheckCircle2 className="h-5 w-5" />} />
        <AdminStatCard label="Bağlantı" value={connectedStores} tone="violet" icon={<Globe2 className="h-5 w-5" />} />
        <AdminStatCard label="Sipariş" value={totalOrders} tone="amber" icon={<ShoppingCart className="h-5 w-5" />} />
      </section>

      <section className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <AdminPanel title="Bağlı mağazalar" caption={`${numberText(syncedStores)} aktif mağazada sipariş senkronu çalışmış görünüyor.`} count={activeStoreRows.length} allowOverflow>
          {loading ? (
            <div className="p-5"><LoadingPanel label="Mağazalar yükleniyor" /></div>
          ) : activeStoreRows.length === 0 ? (
            <div className="flex min-h-40 items-center justify-center p-8 text-sm font-medium text-slate-500">Henüz aktif mağaza yok.</div>
          ) : (
            <div className="divide-y divide-slate-100">{activeStoreRows.map(storeRow)}</div>
          )}
        </AdminPanel>

        <AdminPanel title="Yeni mağaza bağla" caption="Desteklenen pazar yerleri kompakt listede." count={customerStoreIntegrations.length}>
          {customerStoreIntegrations.length === 0 ? (
            <div className="flex min-h-32 items-center justify-center p-8 text-sm font-medium text-slate-500">
              Desteklenen pazar yeri bulunamadı.
            </div>
          ) : (
            <div className="grid gap-2 p-3 sm:grid-cols-2 xl:grid-cols-1">
              {[...customerStoreIntegrations].sort((a, b) => a.label.localeCompare(b.label, "en", { sensitivity: "base" })).map((integration) => {
                const logoSrc = platformLogoSrc(integration.label);
                const connectedCount = connectedIntegrationCounts[integration.platform] || 0;
                const content = (
                  <>
                    <div className="flex h-10 w-20 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-2 py-1.5">
                      {logoSrc ? <img src={logoSrc} alt={integration.label} className="max-h-7 max-w-full object-contain" loading="lazy" /> : <Store className="h-5 w-5 text-slate-600" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <h2 className="truncate text-sm font-semibold text-slate-950">{integration.label}</h2>
                        {connectedCount > 0 ? (
                          <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                            {numberText(connectedCount)}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-slate-500">{integration.detail}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-slate-700" />
                  </>
                );
                const cardClass = "group flex min-w-0 items-center gap-3 rounded-lg border border-slate-200 bg-white p-2.5 transition hover:border-slate-300 hover:bg-slate-50";
                return integration.href ? (
                  <a
                    key={integration.label}
                    href={integration.href}
                    className={cardClass}
                    target={integration.href.startsWith("https://") ? "_blank" : undefined}
                    rel={integration.href.startsWith("https://") ? "noreferrer" : undefined}
                  >
                    {content}
                  </a>
                ) : (
                  <Link key={integration.label} to={integration.to || "/customer/stores/new"} className={cardClass}>
                    {content}
                  </Link>
                );
              })}
            </div>
          )}
        </AdminPanel>
      </section>

      {!loading && inactiveStoreRows.length > 0 ? (
        <AdminPanel
          title="Pasif mağazalar"
          caption="Pasif mağazalar ana listeden ayrılır; geçmiş ve yeniden aktifleştirme erişimi korunur."
          count={inactiveStoreRows.length}
          allowOverflow
        >
          <div className="p-4">
            <button
              type="button"
              aria-expanded={showInactiveStores}
              onClick={() => setShowInactiveStores((current) => !current)}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <Archive className="h-4 w-4" />
              {showInactiveStores ? "Pasif mağazaları gizle" : "Pasif mağazaları göster"}
              <ChevronDown className={cx("h-4 w-4 transition", showInactiveStores ? "rotate-180" : "")} />
            </button>
          </div>
          {showInactiveStores ? <div className="divide-y divide-slate-100 border-t border-slate-100">{inactiveStoreRows.map(storeRow)}</div> : null}
        </AdminPanel>
      ) : null}
    </div>
  );
}

function storeSyncActionKey(store: StoreSummary | StoreDetail) {
  if (store.platform === "ebay") return store.id;
  if (store.platform === "jtl") return `jtl-sync:${store.id}`;
  if (internalOrderSyncPlatforms.includes(store.platform as OrderSyncPlatform)) return `orders:${store.platform}:${store.id}`;
  return "";
}

function ebayConnectionRequiresReauthorization(store: Pick<StoreSummary, "ebayStatus" | "platform">) {
  return store.platform === "ebay" && store.ebayStatus === "reconnect_required";
}

function ebayReconnectHref(storeId: string) {
  return `/api/ebay/connect?storeId=${encodeURIComponent(storeId)}&returnUrl=${encodeURIComponent("/customer/stores")}`;
}

function StoreActionsMenu({
  onDelete,
  onSyncEbay,
  onSyncInternal,
  onSyncJtl,
  onTestJtl,
  onToggleStatus,
  store,
  storeActionId,
  syncingStoreId,
}: {
  onDelete: (store: StoreSummary) => void | Promise<void>;
  onSyncEbay: (storeId: string) => void | Promise<void>;
  onSyncInternal: (storeId: string, platform: OrderSyncPlatform) => void | Promise<void>;
  onSyncJtl: (storeId: string) => void | Promise<void>;
  onTestJtl: (storeId: string) => void | Promise<void>;
  onToggleStatus: (store: StoreSummary) => void | Promise<void>;
  store: StoreSummary;
  storeActionId: string | null;
  syncingStoreId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const canSync =
    store.platform !== "amazon" &&
    store.active !== false &&
    store.syncReady !== false &&
    (store.platform === "ebay" ||
      store.platform === "jtl" ||
      internalOrderSyncPlatforms.includes(store.platform as OrderSyncPlatform));
  const syncActionKey = storeSyncActionKey(store);
  const syncing = Boolean(syncActionKey && syncingStoreId === syncActionKey);
  const statusBusy = storeActionId === `status:${store.id}`;
  const deleteBusy = storeActionId === `delete:${store.id}`;
  const deleteDisabled = deleteBusy || Number(store.ordersCount || 0) > 0;
  const itemClass =
    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";
  const dangerClass =
    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50";
  const close = () => setOpen(false);

  function startSync() {
    close();
    if (store.platform === "ebay") {
      void onSyncEbay(store.id);
    } else if (store.platform === "jtl") {
      void onSyncJtl(store.id);
    } else if (internalOrderSyncPlatforms.includes(store.platform as OrderSyncPlatform)) {
      void onSyncInternal(store.id, store.platform as OrderSyncPlatform);
    }
  }

  return (
    <div
      className="relative shrink-0 self-start lg:self-center"
      onBlur={(event) => {
        const nextTarget = event.relatedTarget as Node | null;
        if (!nextTarget || !event.currentTarget.contains(nextTarget)) setOpen(false);
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((current) => !current)}
        className="inline-flex min-w-32 items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
      >
        <SlidersHorizontal className="h-4 w-4" />
        İşlemler
        <ChevronDown className={cx("h-4 w-4 transition", open ? "rotate-180" : "")} />
      </button>
      {open ? (
        <div role="menu" className="absolute left-0 top-full z-[80] mt-2 w-64 rounded-lg border border-slate-200 bg-white p-2 shadow-xl lg:left-auto lg:right-0">
          <Link className={itemClass} to={`/customer/orders?storeId=${encodeURIComponent(store.id)}`} onClick={close}>
            <ShoppingCart className="h-4 w-4" />
            Siparişleri görüntüle
          </Link>
          <Link className={itemClass} to={`/customer/stores/${encodeURIComponent(store.id)}`} onClick={close}>
            <ChevronRight className="h-4 w-4" />
            Detay
          </Link>
          <Link className={itemClass} to={`/customer/stores/${encodeURIComponent(store.id)}/logs`} onClick={close}>
            <History className="h-4 w-4" />
            Log kayıtları
          </Link>
          {store.platform === "jtl" ? (
            <button
              type="button"
              disabled={syncingStoreId === `jtl-test:${store.id}`}
              onClick={() => {
                close();
                void onTestJtl(store.id);
              }}
              className={itemClass}
            >
              {syncingStoreId === `jtl-test:${store.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Bağlantıyı test et
            </button>
          ) : null}
          {store.platform === "ebay" ? (
            <a href={ebayReconnectHref(store.id)} onClick={close} className={itemClass}>
              <ExternalLink className="h-4 w-4" />
              {ebayConnectionRequiresReauthorization(store) ? "eBay bağlantısını yenile" : "eBay bağlantısını yönet"}
            </a>
          ) : null}
          {canSync ? (
            <button type="button" disabled={syncing} onClick={startSync} className={itemClass}>
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock3 className="h-4 w-4" />}
              Senkronizasyonu başlat
            </button>
          ) : null}
          {store.platform === "amazon" ? (
            <Link className={itemClass} to="/customer/stores/amazon-v2/new" onClick={close}>
              <ExternalLink className="h-4 w-4" />
              Amazon V2’ye bağlan
            </Link>
          ) : <button
            type="button"
            disabled={statusBusy}
            onClick={() => {
              close();
              void onToggleStatus(store);
            }}
            className={itemClass}
          >
            {statusBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
            {store.active !== false ? "Pasifleştir" : "Aktifleştir"}
          </button>}
          <button
            type="button"
            disabled={deleteDisabled}
            onClick={() => {
              close();
              void onDelete(store);
            }}
            title={Number(store.ordersCount || 0) > 0 ? "Siparişi olan mağazalar silinemez." : "Mağazayı sil"}
            className={dangerClass}
          >
            {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Sil
          </button>
        </div>
      ) : null}
    </div>
  );
}

function StoreDetailActionsMenu({
  canSync,
  deleting,
  onDelete,
  onSync,
  store,
  syncing,
}: {
  canSync: boolean;
  deleting: boolean;
  onDelete: () => void | Promise<void>;
  onSync: () => void | Promise<void>;
  store: StoreDetail;
  syncing: boolean;
}) {
  const [open, setOpen] = useState(false);
  const deleteDisabled = deleting || Number(store.ordersCount || 0) > 0;
  const itemClass =
    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";
  const dangerClass =
    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50";
  const close = () => setOpen(false);

  return (
    <div
      className="relative"
      onBlur={(event) => {
        const nextTarget = event.relatedTarget as Node | null;
        if (!nextTarget || !event.currentTarget.contains(nextTarget)) setOpen(false);
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((current) => !current)}
        className="inline-flex min-w-32 items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
      >
        <SlidersHorizontal className="h-4 w-4" />
        İşlemler
        <ChevronDown className={cx("h-4 w-4 transition", open ? "rotate-180" : "")} />
      </button>
      {open ? (
        <div role="menu" className="absolute left-0 top-full z-[80] mt-2 w-64 rounded-lg border border-slate-200 bg-white p-2 shadow-xl sm:left-auto sm:right-0">
          <Link className={itemClass} to={`/customer/orders?storeId=${encodeURIComponent(store.id)}`} onClick={close}>
            <ShoppingCart className="h-4 w-4" />
            Siparişleri görüntüle
          </Link>
          <Link className={itemClass} to={`/customer/stores/${encodeURIComponent(store.id)}/logs`} onClick={close}>
            <History className="h-4 w-4" />
            Log kayıtları
          </Link>
          {store.platform === "amazon_v2" ? (<Link className={itemClass} to={`/customer/stores/amazon-v2/new?storeId=${encodeURIComponent(store.id)}`} onClick={close}>Amazon V2 bağlantısını yönet</Link>) : null}
          {store.platform === "amazon" ? (
            <Link className={itemClass} to="/customer/stores/amazon-v2/new" onClick={close}>
              <ExternalLink className="h-4 w-4" />
              Amazon V2’ye bağlan
            </Link>
          ) : null}
          {store.platform === "etsy" ? (
            <Link className={itemClass} to={`/customer/stores/${store.platform}/new?storeId=${encodeURIComponent(store.id)}`} onClick={close}>
              <ExternalLink className="h-4 w-4" />
              {platformLabel(store.platform)} bağlantısını yönet
            </Link>
          ) : null}
          {store.platform === "ebay" ? (
            <a href={ebayReconnectHref(store.id)} onClick={close} className={itemClass}>
              <ExternalLink className="h-4 w-4" />
              {ebayConnectionRequiresReauthorization(store) ? "eBay bağlantısını yenile" : "eBay bağlantısını yönet"}
            </a>
          ) : null}
          {canSync ? (
            <button
              type="button"
              disabled={syncing}
              onClick={() => {
                close();
                void onSync();
              }}
              className={itemClass}
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock3 className="h-4 w-4" />}
              Senkronizasyonu başlat
            </button>
          ) : null}
          <button
            type="button"
            disabled={deleteDisabled}
            onClick={() => {
              close();
              void onDelete();
            }}
            title={Number(store.ordersCount || 0) > 0 ? "Siparişi olan mağazalar silinemez." : "Mağazayı sil"}
            className={dangerClass}
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Sil
          </button>
        </div>
      ) : null}
    </div>
  );
}

const marketplaceConnectionConfig = {
  amazon: {
    platform: "amazon" as const,
    title: "Amazon mağazası bağla",
    caption: "Amazon bağlantısını server link akışıyla başlatın; siparişler Shopify kaynak senkronundan alınır.",
    fieldLabel: "Amazon mağaza adı",
    startLabel: "Amazon yetkilendirmeyi başlat",
    authLinkLabel: "Amazon onay linkini aç",
    storeMissing: "Amazon store was not found.",
    nameRequired: "Amazon mağaza adı gerekli.",
    statusError: "Amazon durumu alınamadı.",
    startError: "Amazon bağlantısı başlatılamadı.",
    confirmMissing: "Tamamlanacak aktif Amazon yetkilendirme işi bulunamadı.",
    confirmError: "Amazon bağlantısı tamamlanamadı.",
    disconnectMissing: "Kapatılacak Amazon mağazası bulunamadı.",
    disconnectError: "Amazon bağlantısı kapatılamadı.",
    bridgeUnavailable: "Amazon ara servisine ulaşılamıyor.",
    startingTitle: "Amazon bağlantısı hazırlanıyor",
    startingDescription: "Yetkilendirme linki server tarafında hazırlanıyor. Açılan sekme birazdan Amazon onay ekranına yönlenecek; lütfen bu ekranı kapatmayın.",
    popupOpened: "Amazon yetkilendirme penceresi açıldı. Onaydan sonra buradan tamamlayın.",
    authReady: "Yetkilendirme bağlantısı hazır; aşağıdaki bağlantıdan açın.",
    startedNoUrl: "Amazon bağlantı işi başlatıldı, fakat yetkilendirme URL'i dönmedi.",
    completedSyncPrefix: "Amazon bağlantısı tamamlandı.",
    completedRedirectNotice: "Amazon bağlantısı başarıyla tamamlandı. Mağazalar sayfasına yönlendiriliyorsunuz.",
    completedStatusNotice: "Amazon bağlantısı aktif. Bu mağaza artık sipariş senkronu için hazır.",
    waitingNotice: "Amazon bağlantısı gönderildi; durum bridge tarafında bekliyor.",
    waitingCountdownTitle: "Amazon onayı bekleniyor",
    waitingCountdownDescription: "Amazon penceresinde onayı tamamlayıp süre bitmeden bu ekrandan bağlantıyı tamamlayın.",
    expiredNotice: "Amazon yetkilendirme süresi doldu. Lütfen bağlantıyı yeniden başlatın.",
    expiredTitle: "Amazon yetkilendirme süresi doldu",
    expiredDescription: "Bu onay linki artık güvenli şekilde kullanılamaz. Yeni bir yetkilendirme bağlantısı başlatın.",
    restartLabel: "Yeni Amazon bağlantısı başlat",
    disconnectedNotice: "Amazon bağlantısı kapatıldı.",
    emptyStore: "Henüz Amazon mağazası oluşmadı.",
    sourceDescription: "Gadget tarafındaki server link senaryosuyla aynı başlat/onayla akışını kullanır.",
    externalLabel: "Satıcı",
  },
  etsy: {
    platform: "etsy" as const,
    title: "Etsy mağazası bağla",
    caption: "Etsy bağlantısını server link akışıyla başlatın; siparişler Shopify kaynak senkronundan alınır.",
    fieldLabel: "Etsy mağaza adı",
    startLabel: "Etsy yetkilendirmeyi başlat",
    authLinkLabel: "Etsy onay linkini aç",
    storeMissing: "Etsy store was not found.",
    nameRequired: "Etsy mağaza adı gerekli.",
    statusError: "Etsy durumu alınamadı.",
    startError: "Etsy bağlantısı başlatılamadı.",
    confirmMissing: "Tamamlanacak aktif Etsy yetkilendirme işi bulunamadı.",
    confirmError: "Etsy bağlantısı tamamlanamadı.",
    disconnectMissing: "Kapatılacak Etsy mağazası bulunamadı.",
    disconnectError: "Etsy bağlantısı kapatılamadı.",
    bridgeUnavailable: "Etsy ara servisine ulaşılamıyor.",
    startingTitle: "Etsy bağlantısı hazırlanıyor",
    startingDescription: "Yetkilendirme linki server tarafında hazırlanıyor. Açılan sekme birazdan Etsy onay ekranına yönlenecek; lütfen bu ekranı kapatmayın.",
    popupOpened: "Etsy yetkilendirme penceresi açıldı. Onaydan sonra buradan tamamlayın.",
    authReady: "Yetkilendirme bağlantısı hazır; aşağıdaki bağlantıdan açın.",
    startedNoUrl: "Etsy bağlantı işi başlatıldı, fakat yetkilendirme URL'i dönmedi.",
    completedSyncPrefix: "Etsy bağlantısı tamamlandı.",
    completedRedirectNotice: "Etsy bağlantısı başarıyla tamamlandı. Mağazalar sayfasına yönlendiriliyorsunuz.",
    completedStatusNotice: "Etsy bağlantısı aktif. Bu mağaza artık sipariş senkronu için hazır.",
    waitingNotice: "Etsy bağlantısı gönderildi; durum bridge tarafında bekliyor.",
    waitingCountdownTitle: "Etsy onayı bekleniyor",
    waitingCountdownDescription: "Etsy penceresinde onayı tamamlayıp süre bitmeden bu ekrandan bağlantıyı tamamlayın.",
    expiredNotice: "Etsy yetkilendirme süresi doldu. Lütfen bağlantıyı yeniden başlatın.",
    expiredTitle: "Etsy yetkilendirme süresi doldu",
    expiredDescription: "Bu onay linki artık güvenli şekilde kullanılamaz. Yeni bir yetkilendirme bağlantısı başlatın.",
    restartLabel: "Yeni Etsy bağlantısı başlat",
    disconnectedNotice: "Etsy bağlantısı kapatıldı.",
    emptyStore: "Henüz Etsy mağazası oluşmadı.",
    sourceDescription: "Amazon ile aynı Salestio bridge senaryosuyla Etsy hesabını ekler.",
    externalLabel: "Etsy shop",
  },
};

function bridgeTimestampMs(value: number | null | undefined) {
  if (!value || !Number.isFinite(value)) return null;
  return value < 1_000_000_000_000 ? value * 1000 : value;
}

const MARKETPLACE_AUTH_WINDOW_FALLBACK_MS = 2 * 60 * 1000;

function marketplaceCountdown(createdAt: number | null | undefined, expiresAt: number | null | undefined, now: number) {
  const createdAtMs = bridgeTimestampMs(createdAt);
  const expiresAtMs = bridgeTimestampMs(expiresAt) || (createdAtMs ? createdAtMs + MARKETPLACE_AUTH_WINDOW_FALLBACK_MS : null);
  const totalMs = expiresAtMs && createdAtMs && expiresAtMs > createdAtMs ? expiresAtMs - createdAtMs : MARKETPLACE_AUTH_WINDOW_FALLBACK_MS;
  const remainingMs = expiresAtMs ? Math.max(expiresAtMs - now, 0) : null;
  const progress = remainingMs === null ? 0 : Math.max(0, Math.min(100, (remainingMs / totalMs) * 100));
  return { expiresAtMs, progress, remainingMs, totalMs };
}

function comparableMarketplaceSiteName(value: string, fallback: "amazon" | "etsy") {
  return (
    value
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase()
      .slice(0, 32) || fallback
  );
}

function marketplaceBridgeJobMatchesSite(job: AmazonStatusResponse["bridge"]["activeJob"] | null | undefined, siteName: string, platform: "amazon" | "etsy") {
  if (!job?.siteName || !siteName.trim()) return false;
  if (job.platform && job.platform !== platform) return false;
  const expected = comparableMarketplaceSiteName(siteName, platform);
  const actual = comparableMarketplaceSiteName(job.siteName, platform);
  return actual === expected || actual.startsWith(`${expected}-`);
}

function formatMarketplaceCountdown(remainingMs: number | null) {
  if (remainingMs === null) return "--:--";
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function marketplaceBridgeJobBlocks(job: AmazonStatusResponse["bridge"]["activeJob"] | null | undefined) {
  if (!job) return false;
  const status = String(job.status || "").toLowerCase();
  if (["completed", "submitted", "success", "succeeded", "done", "failed", "error", "cancelled", "canceled", "expired"].includes(status)) return false;
  const now = Date.now();
  const expiresAt = bridgeTimestampMs(job.expiresAt);
  if (expiresAt && expiresAt <= now) return false;
  const createdAt = bridgeTimestampMs(job.createdAt);
  if (!expiresAt && createdAt && now - createdAt > 30 * 60 * 1000) return false;
  return true;
}

function marketplaceBridgeIsBusy(bridge: AmazonStatusResponse["bridge"] | null | undefined) {
  return Boolean(bridge?.busy && marketplaceBridgeJobBlocks(bridge.activeJob));
}

type MarketplaceConnectionConfig = (typeof marketplaceConnectionConfig)[keyof typeof marketplaceConnectionConfig];

function closeMarketplacePopup(popup: Window | null) {
  try {
    if (popup && !popup.closed) popup.close();
  } catch {
    // Ignore browser popup lifecycle errors.
  }
}

function openMarketplacePreparingPopup(config: MarketplaceConnectionConfig): Window | null {
  try {
    const popup = window.open("about:blank", "_blank");
    if (!popup) return null;

    const platformName = platformLabel(config.platform);
    popup.document.write(`
      <!doctype html>
      <html lang="tr">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${platformName} bağlantısı hazırlanıyor</title>
          <style>
            body {
              margin: 0;
              min-height: 100vh;
              display: grid;
              place-items: center;
              background: #f8fafc;
              color: #0f172a;
              font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            }
            .card {
              width: min(92vw, 420px);
              border: 1px solid #e2e8f0;
              border-radius: 18px;
              background: #fff;
              padding: 28px;
              box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
            }
            .spinner {
              width: 28px;
              height: 28px;
              border-radius: 9999px;
              border: 3px solid #cbd5e1;
              border-top-color: #0f172a;
              animation: spin 0.9s linear infinite;
            }
            h1 {
              margin: 16px 0 8px;
              font-size: 20px;
            }
            p {
              margin: 0;
              color: #475569;
              line-height: 1.5;
            }
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="spinner"></div>
            <h1>${platformName} bağlantısı hazırlanıyor</h1>
            <p>Yetkilendirme linki alınıyor. Bu pencere birazdan otomatik yönlenecek.</p>
          </div>
        </body>
      </html>
    `);
    popup.document.close();
    popup.focus();
    return popup;
  } catch {
    return null;
  }
}

function redirectMarketplacePopupToAuthUrl(popup: Window | null, url: string): boolean {
  try {
    if (popup && !popup.closed) {
      popup.location.replace(url);
      popup.focus();
      return true;
    }
  } catch {
    // Fall through to direct open.
  }

  const reopened = window.open(url, "_blank");
  if (reopened) {
    reopened.focus();
    return true;
  }

  return false;
}

function redirectPreparedMarketplacePopupToAuthUrl(popup: Window | null, url: string): boolean {
  try {
    if (!popup || popup.closed) return false;
    popup.location.replace(url);
    popup.focus();
    return true;
  } catch {
    return false;
  }
}

function MarketplaceConnectionPage({ platform }: { platform: "amazon" | "etsy" }) {
  const config = marketplaceConnectionConfig[platform];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedStoreId = String(searchParams.get("storeId") || "").trim();
  const initialSiteName = String(searchParams.get("siteName") || "").trim();
  const [status, setStatus] = useState<AmazonStatusResponse | null>(null);
  const [siteName, setSiteName] = useState(initialSiteName);
  const [busy, setBusy] = useState<"load" | "start" | "confirm" | "disconnect" | "reset" | null>("load");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [authWindowNeedsManualOpen, setAuthWindowNeedsManualOpen] = useState(false);
  const [authLinkPreparing, setAuthLinkPreparing] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [pendingAuthUrl, setPendingAuthUrl] = useState("");
  const [countdownNow, setCountdownNow] = useState(() => Date.now());
  const [completionRedirecting, setCompletionRedirecting] = useState(false);
  const completionRedirectTimerRef = useRef<number | null>(null);
  const authPopupRef = useRef<Window | null>(null);
  const authUrlCapturedRef = useRef(false);
  const authUrlRedirectedRef = useRef(false);
  const startingSiteNameRef = useRef("");
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  function scheduleStoresRedirect() {
    if (completionRedirectTimerRef.current) window.clearTimeout(completionRedirectTimerRef.current);
    setCompletionRedirecting(true);
    completionRedirectTimerRef.current = window.setTimeout(() => {
      navigate("/customer/stores", { replace: true });
    }, 1800);
  }

  async function loadStatus(options: { silent?: boolean } = {}) {
    if (!options.silent) {
      setError(null);
      setBusy((current) => current || "load");
    }
    try {
      const result = await apiClient.marketplaceStatus(platform, requestedStoreId || undefined, { newConnection: !requestedStoreId });
      setStatus(result);
      if (result.connection?.active) {
        setPendingAuthUrl("");
        setAuthWindowNeedsManualOpen(false);
      }
      const nextSiteName = result.connection?.siteName || result.store?.name || initialSiteName;
      if (nextSiteName && !siteName) setSiteName(nextSiteName);
    } catch (err) {
      if (!options.silent) setError(err instanceof ApiError ? err.message : config.statusError);
    } finally {
      if (!options.silent) setBusy((current) => (current === "load" ? null : current));
    }
  }

  useEffect(() => {
    void loadStatus();
  }, [platform, requestedStoreId]);

  useEffect(() => () => {
    if (completionRedirectTimerRef.current) window.clearTimeout(completionRedirectTimerRef.current);
    closeMarketplacePopup(authPopupRef.current);
    authPopupRef.current = null;
  }, []);

  async function beginMarketplaceConnection(preferredSiteName: string) {
    const normalizedSiteName = preferredSiteName.trim();
    if (!normalizedSiteName) {
      setError(config.nameRequired);
      return;
    }
    if (bridgeBusy) return;

    closeMarketplacePopup(authPopupRef.current);
    const preparingPopup = openMarketplacePreparingPopup(config);
    authPopupRef.current = preparingPopup;
    authUrlCapturedRef.current = false;
    authUrlRedirectedRef.current = false;
    startingSiteNameRef.current = normalizedSiteName;
    setAuthLinkPreparing(true);
    setBusy("start");
    setError(null);
    setNotice(null);
    setAuthWindowNeedsManualOpen(false);
    setPendingAuthUrl("");
    setCompletionRedirecting(false);
    try {
      const result = await apiClient.startMarketplaceConnection(platform, {
        siteName: normalizedSiteName,
        ...(requestedStoreId ? { storeId: requestedStoreId } : {}),
      });
      const authUrl = String(result.job?.authorizationUrl || result.job?.amazonAuthUrl || result.job?.etsyAuthUrl || "").trim();
      if (authUrl) {
        setAuthLinkPreparing(false);
        authUrlCapturedRef.current = true;
        setPendingAuthUrl(authUrl);
        const opened = redirectMarketplacePopupToAuthUrl(preparingPopup, authUrl);
        authUrlRedirectedRef.current = opened;
        if (opened) authPopupRef.current = null;
        setAuthWindowNeedsManualOpen(!opened);
        setConfirmationModalOpen(true);
        setNotice(opened ? config.popupOpened : config.authReady);
      } else if (!authUrlCapturedRef.current) {
        setConfirmationModalOpen(true);
        setNotice(config.startingDescription);
      }
      if (result.storeId && result.storeId !== requestedStoreId) {
        navigate(`/customer/stores/${platform}/new?storeId=${encodeURIComponent(result.storeId)}`, { replace: true });
      } else {
        await loadStatus();
      }
    } catch (err) {
      if (authUrlCapturedRef.current) {
        setConfirmationModalOpen(true);
        setNotice(authUrlRedirectedRef.current ? config.popupOpened : config.authReady);
      } else {
        setAuthLinkPreparing(false);
        closeMarketplacePopup(preparingPopup);
        if (authPopupRef.current === preparingPopup) authPopupRef.current = null;
        setError(err instanceof ApiError ? err.message : config.startError);
      }
    } finally {
      setBusy(null);
    }
  }

  async function startConnection(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await beginMarketplaceConnection(siteName);
  }

  async function confirmConnection() {
    const storeId = status?.store?.id || requestedStoreId;
    const jobId = status?.connection?.jobId || status?.bridge.activeJob?.jobId || "";
    if (authorizationExpired) {
      setError(null);
      setNotice(config.expiredNotice);
      setConfirmationModalOpen(true);
      return;
    }
    if (!storeId || !jobId) {
      setError(config.confirmMissing);
      return;
    }

    setBusy("confirm");
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.confirmMarketplaceConnection(platform, { storeId, jobId });
      const active =
        result.job.status === "completed" ||
        result.job.result?.active === true ||
        result.job.result?.success === true;
      if (active) {
        setConfirmationModalOpen(false);
        setAuthWindowNeedsManualOpen(false);
        setPendingAuthUrl("");
        setAuthLinkPreparing(false);
        closeMarketplacePopup(authPopupRef.current);
        authPopupRef.current = null;
        try {
          const syncResult = await apiClient.syncStoreOrders(storeId, platform);
          const syncMessage = syncResult.message || `${config.completedSyncPrefix} ${syncResult.fetched || 0} sipariş alındı.`;
          setNotice(`${syncMessage} Mağazalar sayfasına yönlendiriliyorsunuz.`);
        } catch (syncError) {
          setNotice(
            syncError instanceof ApiError
              ? `${config.completedSyncPrefix} Fakat sipariş senkronu başlatılamadı: ${syncError.message} Mağazalar sayfasına yönlendiriliyorsunuz.`
              : `${config.completedSyncPrefix} Fakat sipariş senkronu başlatılamadı. Mağazalar sayfasına yönlendiriliyorsunuz.`
          );
        }
        scheduleStoresRedirect();
      } else {
        setNotice(config.waitingNotice);
      }
      await loadStatus();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : config.confirmError);
    } finally {
      setBusy(null);
    }
  }

  async function disconnectMarketplace() {
    const storeId = status?.store?.id || requestedStoreId;
    if (!storeId) {
      setError(config.disconnectMissing);
      return;
    }
    const confirmed = await askConfirmation({
      title: `${platformLabel(platform)} bağlantısı kapatılsın mı?`,
      description: `${connection?.siteName || store?.name || siteName || platformLabel(platform)} mağazasının bağlantısı kapatılacak.`,
      details: ["Bağlantı kapatılınca bu mağaza için otomatik sipariş senkronu durur."],
      confirmLabel: "Bağlantıyı kapat",
      tone: "danger",
    });
    if (!confirmed) return;

    setBusy("disconnect");
    setError(null);
    setNotice(null);
    try {
      await apiClient.disconnectMarketplace(platform, storeId);
      setPendingAuthUrl("");
      setAuthLinkPreparing(false);
      authUrlCapturedRef.current = false;
      authUrlRedirectedRef.current = false;
      startingSiteNameRef.current = "";
      setAuthWindowNeedsManualOpen(false);
      closeMarketplacePopup(authPopupRef.current);
      authPopupRef.current = null;
      setConfirmationModalOpen(false);
      setNotice(config.disconnectedNotice);
      await loadStatus();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : config.disconnectError);
    } finally {
      setBusy(null);
    }
  }

  async function resetMarketplaceSetup() {
    const storeId = status?.store?.id || requestedStoreId;
    const currentConnection = status?.connection;
    const canDisconnectPendingStore = Boolean(storeId && currentConnection && !currentConnection.active);
    const confirmed = await askConfirmation({
      title: `${platformLabel(platform)} bağlantısı baştan başlatılsın mı?`,
      description: canDisconnectPendingStore
        ? "Yarım kalan bağlantı kapatılacak ve yeni mağaza bağlantısı için ekran temizlenecek."
        : "Yeni mağaza bağlantısı için ekran temizlenecek.",
      details: canDisconnectPendingStore ? ["Aktif hale gelmiş mağazalara dokunulmaz; sadece yarım kalmış yetkilendirme işi temizlenir."] : undefined,
      confirmLabel: "Baştan başlat",
      tone: "warning",
    });
    if (!confirmed) return;

    setBusy("reset");
    setError(null);
    setNotice(null);
    closeMarketplacePopup(authPopupRef.current);
    authPopupRef.current = null;
    try {
      if (canDisconnectPendingStore) {
        await apiClient.disconnectMarketplace(platform, storeId);
      }
      setPendingAuthUrl("");
      authUrlCapturedRef.current = false;
      authUrlRedirectedRef.current = false;
      startingSiteNameRef.current = "";
      setAuthWindowNeedsManualOpen(false);
      setConfirmationModalOpen(false);
      setCompletionRedirecting(false);
      setSiteName("");
      setStatus((current) =>
        current
          ? {
              ...current,
              store: null,
              connection: null,
              bridge: {
                ...current.bridge,
                busy: false,
                activeJob: null,
              },
            }
          : current
      );
      setNotice(`${platformLabel(platform)} bağlantısı baştan başlatıldı. Yeni mağaza adını yazıp tekrar başlatabilirsiniz.`);
      if (requestedStoreId) {
        navigate(`/customer/stores/${platform}/new`, { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `${platformLabel(platform)} bağlantısı baştan başlatılamadı.`);
    } finally {
      setBusy(null);
    }
  }

  const bridge = status?.bridge;
  const store = status?.store;
  const connection = status?.connection;
  const activeBridgeJob = bridge?.activeJob || null;
  const activeBridgeJobId = activeBridgeJob?.jobId || "";
  const currentConnectionJobId = connection?.jobId || "";
  const bridgeJobMatchesConnection = Boolean(currentConnectionJobId && activeBridgeJobId === currentConnectionJobId);
  const bridgeJobMatchesStart = Boolean(busy === "start" && marketplaceBridgeJobMatchesSite(activeBridgeJob, startingSiteNameRef.current || siteName, platform));
  const relevantBridgeJob = bridgeJobMatchesConnection || bridgeJobMatchesStart ? activeBridgeJob : null;
  const bridgeBusy = Boolean(marketplaceBridgeIsBusy(bridge) && !bridgeJobMatchesConnection && !bridgeJobMatchesStart);
  const authUrl =
    connection?.authorizationUrl ||
    connection?.amazonAuthUrl ||
    connection?.etsyAuthUrl ||
    relevantBridgeJob?.authorizationUrl ||
    relevantBridgeJob?.amazonAuthUrl ||
    relevantBridgeJob?.etsyAuthUrl ||
    "";
  const activeAuthUrl = authUrl || pendingAuthUrl;
  const pendingStatus = String(relevantBridgeJob?.status || connection?.status || "").toLowerCase();
  const authorizationPending = Boolean(
    !connection?.active &&
      (connection?.waitingForConfirmation ||
        pendingStatus === "awaiting_confirmation" ||
        (activeAuthUrl && pendingStatus !== "completed"))
  );
  const countdown = marketplaceCountdown(
    relevantBridgeJob?.createdAt ?? connection?.createdAt ?? null,
    relevantBridgeJob?.expiresAt ?? connection?.expiresAt ?? null,
    countdownNow
  );
  const countdownExpired = authorizationPending && countdown.remainingMs !== null && countdown.remainingMs <= 0;
  const capturedAuthJobGone = Boolean(pendingAuthUrl && bridge?.reachable && currentConnectionJobId && !relevantBridgeJob && !connection?.active);
  const authorizationExpired = Boolean(!connection?.active && (connection?.expired || pendingStatus === "expired" || countdownExpired || capturedAuthJobGone));
  const waiting = authorizationPending && !authorizationExpired;
  const waitingForAuthUrl = Boolean(!connection?.active && !authorizationExpired && !activeAuthUrl && (authLinkPreparing || waiting || busy === "start"));
  const countdownText = formatMarketplaceCountdown(countdown.remainingMs);
  const modalStoreName = connection?.siteName || store?.name || siteName || platformLabel(platform);
  const canResetMarketplaceSetup = Boolean(!connection?.active && (requestedStoreId || connection || pendingAuthUrl || waiting || authorizationExpired));

  useEffect(() => {
    if (waiting || authorizationExpired) {
      setConfirmationModalOpen(true);
      return;
    }
    setConfirmationModalOpen(false);
  }, [waiting, authorizationExpired, connection?.jobId]);

  useEffect(() => {
    if (!activeAuthUrl || authorizationExpired || connection?.active) return;
    setAuthLinkPreparing(false);
    authUrlCapturedRef.current = true;
    setPendingAuthUrl(activeAuthUrl);
    const redirected = redirectPreparedMarketplacePopupToAuthUrl(authPopupRef.current, activeAuthUrl);
    authUrlRedirectedRef.current = redirected;
    if (!redirected) {
      setAuthWindowNeedsManualOpen(true);
      setConfirmationModalOpen(true);
      setNotice(config.authReady);
      return;
    }
    authPopupRef.current = null;
    setAuthWindowNeedsManualOpen(false);
    setConfirmationModalOpen(true);
    setNotice(config.popupOpened);
  }, [activeAuthUrl, authorizationExpired, connection?.active, config.authReady, config.popupOpened]);

  useEffect(() => {
    if (busy !== "start" && !waitingForAuthUrl) return;
    void loadStatus({ silent: true });
    const intervalId = window.setInterval(() => {
      void loadStatus({ silent: true });
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [busy, waitingForAuthUrl, platform, requestedStoreId, siteName]);

  useEffect(() => {
    if (!authorizationPending) return;
    setCountdownNow(Date.now());
    const intervalId = window.setInterval(() => setCountdownNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [authorizationPending, connection?.expiresAt, relevantBridgeJob?.expiresAt]);

  useEffect(() => {
    if (!authorizationExpired && !connection?.active) return;
    setAuthLinkPreparing(false);
  }, [authorizationExpired, connection?.active]);

  useEffect(() => {
    if (!waiting) return;
    void loadStatus({ silent: true });
    const intervalId = window.setInterval(() => {
      void loadStatus({ silent: true });
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [waiting, connection?.jobId, relevantBridgeJob?.jobId, requestedStoreId]);

  useEffect(() => {
    if (!connection?.active || completionRedirecting) return;
    setAuthLinkPreparing(false);
    setNotice(config.completedRedirectNotice);
    scheduleStoresRedirect();
  }, [connection?.active, completionRedirecting, config.completedRedirectNotice]);

  return (
    <div className="space-y-5">
      {confirmationDialog}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <Link className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900" to="/customer/stores">
          <ArrowLeft className="h-4 w-4" />
          Mağazalara dön
        </Link>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-950">{config.title}</h1>
            <p className="mt-1 text-sm text-slate-500">{config.caption}</p>
          </div>
          <button
            type="button"
            onClick={() => void loadStatus()}
            disabled={busy !== null}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
          >
            {busy === "load" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Durumu yenile
          </button>
        </div>
      </section>

      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{notice}</div> : null}
      {connection?.active || completionRedirecting ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold">{config.completedStatusNotice}</p>
              {completionRedirecting ? <p className="mt-1 text-emerald-800">Mağazalar sayfasına yönlendiriliyorsunuz.</p> : null}
            </div>
          </div>
        </div>
      ) : null}
      {waiting || authorizationExpired ? (
        <div className={cx("rounded-xl border px-4 py-3 text-sm", authorizationExpired ? "border-red-200 bg-red-50 text-red-900" : "border-blue-200 bg-blue-50 text-blue-900")}>
          <div className="flex items-start gap-3">
            <Clock3 className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold">{authorizationExpired ? config.expiredTitle : config.waitingCountdownTitle}</p>
                <span className="rounded-full bg-white px-2.5 py-1 font-mono text-xs font-semibold tabular-nums shadow-sm">
                  {authorizationExpired ? "00:00" : countdownText}
                </span>
              </div>
              <p className="mt-1 leading-5">{authorizationExpired ? config.expiredDescription : config.waitingCountdownDescription}</p>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/80">
                <div
                  className={cx("h-full rounded-full transition-all", authorizationExpired ? "bg-red-500" : "bg-blue-600")}
                  style={{ width: `${authorizationExpired ? 100 : countdown.progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {bridge && !bridge.reachable ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{bridge.userMessage || bridge.error || config.bridgeUnavailable}</span>
          </div>
        </div>
      ) : null}
      {bridgeBusy ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Sistem şu an meşgul. Başka bir Amazon/Etsy bağlantısı çalışıyor; birkaç dakika sonra tekrar deneyin.</span>
          </div>
        </div>
      ) : null}

      <div className="grid gap-5 xl:grid-cols-[1fr_1fr]">
        <form className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" onSubmit={startConnection}>
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Bağlantı başlat</h2>
            <p className="mt-1 text-sm text-slate-500">{config.sourceDescription}</p>
          </div>
          <ProfileInput label={config.fieldLabel} value={siteName} onChange={setSiteName} />
          <button
            type="submit"
            disabled={busy === "start" || authLinkPreparing || bridgeBusy || waiting}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
          >
            {busy === "start" || authLinkPreparing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
            {waiting ? "Onay bekleniyor" : config.startLabel}
          </button>
        </form>

        <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Bağlantı durumu</h2>
            <p className="mt-1 text-sm text-slate-500">
              {store ? `${store.name} · ${store.ordersCount} sipariş` : config.emptyStore}
            </p>
          </div>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            {[
              ["Ara servis", bridge?.reachable ? (bridgeBusy ? "Meşgul" : "Hazır") : "Ulaşılamıyor"],
              ["Durum", connection?.active ? "Bağlı" : connection?.status || "Yeni"],
              ["Site adı", connection?.siteName || store?.name || "-"],
              [config.externalLabel, String(connection?.result?.sellerId || connection?.result?.shopId || store?.externalStoreId || "-")],
              ["Kalan süre", waiting ? countdownText : authorizationExpired ? "Süre doldu" : "-"],
              ["Son senkron", store?.lastSyncedAt ? formatDate(store.lastSyncedAt) : "-"],
              ["Güncelleme", connection?.updatedAt ? formatDate(connection.updatedAt) : "-"],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                <dt className="text-xs font-semibold uppercase text-slate-500">{label}</dt>
                <dd className="mt-1 break-words font-medium text-slate-900">{value}</dd>
              </div>
            ))}
          </dl>
          {activeAuthUrl && !authorizationExpired ? (
            <a
              href={activeAuthUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <ExternalLink className="h-4 w-4" />
              {config.authLinkLabel}
            </a>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {authorizationExpired ? (
              <button
                type="button"
                onClick={() => void beginMarketplaceConnection(connection?.siteName || store?.name || siteName)}
                disabled={busy === "start" || bridgeBusy}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
              >
                {busy === "start" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                {config.restartLabel}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => void confirmConnection()}
              disabled={busy === "confirm" || !waiting}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              {busy === "confirm" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Bağlantıyı tamamla
            </button>
            {canResetMarketplaceSetup ? (
              <button
                type="button"
                onClick={() => void resetMarketplaceSetup()}
                disabled={busy === "reset" || busy === "disconnect"}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm font-semibold text-amber-700 shadow-sm transition hover:bg-amber-50 disabled:opacity-60"
              >
                {busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                Baştan başlat
              </button>
            ) : null}
            {store?.id ? (
              <button
                type="button"
                onClick={() => void disconnectMarketplace()}
                disabled={busy === "disconnect"}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 shadow-sm transition hover:bg-red-50 disabled:opacity-60"
              >
                {busy === "disconnect" ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                Bağlantıyı kapat
              </button>
            ) : null}
          </div>
        </section>
      </div>

      {confirmationModalOpen && (waiting || authorizationExpired) ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div className="min-w-0">
                <p className="text-base font-semibold text-slate-950">
                  {authorizationExpired ? config.expiredTitle : `${platformLabel(platform)} bağlantısını tamamla`}
                </p>
                <p className="mt-1 text-sm leading-5 text-slate-500">
                  {authorizationExpired ? config.expiredDescription : "Yetkilendirme penceresinde onayı tamamladıktan sonra bağlantıyı buradan tamamlayın."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setConfirmationModalOpen(false)}
                disabled={busy === "confirm"}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
                aria-label="Bağlantı tamamla penceresini kapat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Mağaza</span>
                  <span className="break-words text-right font-semibold text-slate-950">{modalStoreName}</span>
                </div>
                <div className="mt-2 flex justify-between gap-4">
                  <span className="text-slate-500">Durum</span>
                  <span className="break-words text-right font-semibold text-slate-950">{connection?.status || relevantBridgeJob?.status || "waiting"}</span>
                </div>
              </div>
              <div className={cx("rounded-xl border p-4 text-sm", authorizationExpired ? "border-red-200 bg-red-50 text-red-900" : "border-blue-200 bg-blue-50 text-blue-900")}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide opacity-75">{authorizationExpired ? "Süre" : "Kalan süre"}</p>
                    <p className="mt-1 font-mono text-3xl font-semibold tabular-nums">{authorizationExpired ? "00:00" : countdownText}</p>
                  </div>
                  <Clock3 className="h-5 w-5 shrink-0" />
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/80">
                  <div
                    className={cx("h-full rounded-full transition-all", authorizationExpired ? "bg-red-500" : "bg-blue-600")}
                    style={{ width: `${authorizationExpired ? 100 : countdown.progress}%` }}
                  />
                </div>
                <p className="mt-3 leading-5">{authorizationExpired ? config.expiredNotice : config.waitingCountdownDescription}</p>
              </div>
              {authWindowNeedsManualOpen && activeAuthUrl && !authorizationExpired ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-semibold">Tarayıcı otomatik pencereyi engelledi.</p>
                      <p className="mt-1 leading-5">Yetkilendirme linkini manuel açıp onaydan sonra bu pencereye dönün.</p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="flex flex-col-reverse gap-2 border-t border-slate-100 px-5 py-4 sm:flex-row sm:justify-between">
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                {activeAuthUrl && !authorizationExpired ? (
                  <a
                    href={activeAuthUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {config.authLinkLabel}
                  </a>
                ) : null}
                {canResetMarketplaceSetup ? (
                  <button
                    type="button"
                    onClick={() => void resetMarketplaceSetup()}
                    disabled={busy === "reset" || busy === "disconnect"}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-amber-200 bg-white px-4 text-sm font-semibold text-amber-700 shadow-sm transition hover:bg-amber-50 disabled:opacity-60"
                  >
                    {busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                    Baştan başlat
                  </button>
                ) : null}
              </div>
              {authorizationExpired ? (
                <button
                  type="button"
                  onClick={() => void beginMarketplaceConnection(connection?.siteName || store?.name || siteName)}
                  disabled={busy === "start" || bridgeBusy}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                >
                  {busy === "start" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  {config.restartLabel}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void confirmConnection()}
                  disabled={busy === "confirm" || !waiting}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                >
                  {busy === "confirm" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Bağlantıyı tamamla
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
      {busy === "start" || waitingForAuthUrl ? (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-950/40 p-4" role="status" aria-live="polite">
          <section className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-slate-200">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-700">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-slate-950">{config.startingTitle}</h2>
                <p className="mt-1 text-sm leading-6 text-slate-500">{config.startingDescription}</p>
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                  <span className="text-slate-500">Mağaza</span>
                  <span className="ml-2 break-words font-semibold text-slate-900">{siteName.trim() || platformLabel(platform)}</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function etsyPortFromProfile(profile: string) {
  const match = profile.trim().match(/^shop(\d+)$/i);
  if (!match) return "";
  return String(6100 + Number(match[1]));
}

function EtsyBrowserConnectionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedStoreId = String(searchParams.get("storeId") || "").trim();
  const initialProfile = String(searchParams.get("profile") || "shop1").trim() || "shop1";
  const [status, setStatus] = useState<(AmazonStatusResponse & { source?: AmazonStatusResponse["bridge"] }) | null>(null);
  const [form, setForm] = useState<EtsyBrowserConnectionPayload>({
    siteName: String(searchParams.get("siteName") || "").trim(),
    username: "",
    password: "",
    browserProfile: initialProfile,
    browserPort: String(searchParams.get("port") || etsyPortFromProfile(initialProfile)).trim(),
    ...(requestedStoreId ? { storeId: requestedStoreId } : {}),
  });
  const [busy, setBusy] = useState<"load" | "save" | "sync" | "disconnect" | null>("load");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  function updateField(key: keyof EtsyBrowserConnectionPayload, value: string) {
    setForm((current) => ({
      ...current,
      [key]: value,
      ...(key === "browserProfile" && !current.browserPort ? { browserPort: etsyPortFromProfile(value) } : {}),
    }));
  }

  async function loadStatus(options: { silent?: boolean } = {}) {
    if (!options.silent) {
      setBusy((current) => current || "load");
      setError(null);
    }
    try {
      const result = await apiClient.etsyStatus(requestedStoreId || undefined, { newConnection: !requestedStoreId });
      setStatus(result);
      const connection = (result.connection || {}) as Record<string, unknown>;
      setForm((current) => {
        const nextProfile = String(connection.browserProfile || current.browserProfile || initialProfile).trim();
        return {
          ...current,
          storeId: requestedStoreId || result.store?.id || current.storeId,
          siteName: current.siteName || String(connection.siteName || result.store?.name || "").trim(),
          browserProfile: nextProfile,
          browserPort: current.browserPort || String(connection.browserPort || etsyPortFromProfile(nextProfile)).trim(),
        };
      });
    } catch (err) {
      if (!options.silent) setError(err instanceof ApiError ? err.message : "Etsy durumu alınamadı.");
    } finally {
      if (!options.silent) setBusy((current) => (current === "load" ? null : current));
    }
  }

  useEffect(() => {
    void loadStatus();
  }, [requestedStoreId]);

  async function saveConnection(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy("save");
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.startEtsyConnection({
        ...form,
        siteName: form.siteName.trim(),
        username: form.username.trim(),
        password: form.password,
        browserProfile: form.browserProfile.trim(),
        browserPort: form.browserPort?.trim() || undefined,
        ...(requestedStoreId ? { storeId: requestedStoreId } : {}),
      });
      setNotice("Etsy hesabı kaydedildi.");
      setStatus((current) =>
        current
          ? {
              ...current,
              store: result.store,
              connection: result.connection as AmazonStatusResponse["connection"],
            }
          : current
      );
      if (result.storeId && result.storeId !== requestedStoreId) {
        navigate(`/customer/stores/etsy/new?storeId=${encodeURIComponent(result.storeId)}`, { replace: true });
      } else {
        await loadStatus({ silent: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etsy hesabı kaydedilemedi.");
    } finally {
      setBusy(null);
    }
  }

  async function syncOrders() {
    const storeId = status?.store?.id || requestedStoreId || form.storeId;
    if (!storeId) {
      setError("Önce Etsy hesabını kaydedin.");
      return;
    }
    setBusy("sync");
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.syncStoreOrders(storeId, "etsy");
      setNotice(result.message || `${result.fetched} Etsy siparişi alındı.`);
      await loadStatus({ silent: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etsy siparişleri çekilemedi.");
    } finally {
      setBusy(null);
    }
  }

  async function disconnect() {
    const storeId = status?.store?.id || requestedStoreId || form.storeId;
    if (!storeId) return;
    const confirmed = await askConfirmation({
      title: "Etsy bağlantısı kapatılsın mı?",
      description: `${status?.store?.name || form.siteName || "Etsy"} mağazası pasifleştirilecek.`,
      details: ["Otomatik Etsy sipariş senkronu durur."],
      confirmLabel: "Bağlantıyı kapat",
      tone: "danger",
    });
    if (!confirmed) return;
    setBusy("disconnect");
    setError(null);
    setNotice(null);
    try {
      await apiClient.disconnectEtsy(storeId);
      setNotice("Etsy bağlantısı kapatıldı.");
      await loadStatus({ silent: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etsy bağlantısı kapatılamadı.");
    } finally {
      setBusy(null);
    }
  }

  const source = status?.source || status?.bridge;
  const sourceShops = (source as Record<string, any> | undefined)?.shops || {};
  const currentShopStatus = sourceShops[form.browserProfile] || null;
  const store = status?.store;
  const connection = (status?.connection || {}) as Record<string, any>;

  return (
    <div className="space-y-5">
      {confirmationDialog}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <Link className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900" to="/customer/stores">
          <ArrowLeft className="h-4 w-4" />
          Mağazalara dön
        </Link>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-950">Etsy mağazası bağla</h1>
            <p className="mt-1 text-sm text-slate-500">Etsy hesabı ve browser profiliyle sipariş kaynağını eşleştirin.</p>
          </div>
          <button
            type="button"
            onClick={() => void loadStatus()}
            disabled={busy !== null}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
          >
            {busy === "load" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Durumu yenile
          </button>
        </div>
      </section>

      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}

      <div className="grid gap-5 xl:grid-cols-[1fr_420px]">
        <AdminPanel title="Etsy hesap bilgileri" caption="Bu bilgiler KenPost tarafında saklanır; server profili hazır olduğunda siparişler JSON kaynağından alınır.">
          <form className="space-y-5 p-5" onSubmit={saveConnection}>
            <div className="grid gap-4 lg:grid-cols-2">
              <ProfileInput label="Etsy mağaza adı" value={form.siteName} onChange={(value) => updateField("siteName", value)} required />
              <ProfileInput label="Etsy kullanıcı adı / e-posta" value={form.username} onChange={(value) => updateField("username", value)} required />
              <ProfileInput label="Etsy şifre" type="password" value={form.password} onChange={(value) => updateField("password", value)} required />
              <ProfileInput label="Browser profili" value={form.browserProfile} onChange={(value) => updateField("browserProfile", value)} required />
              <ProfileInput label="Browser portu" value={form.browserPort || ""} onChange={(value) => updateField("browserPort", value)} />
            </div>
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-5">
              <button
                type="submit"
                disabled={busy === "save"}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
              >
                {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Etsy hesabını kaydet
              </button>
              <button
                type="button"
                onClick={() => void syncOrders()}
                disabled={busy === "sync" || !(store?.id || requestedStoreId || form.storeId)}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
              >
                {busy === "sync" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Siparişleri çek
              </button>
              {store?.id ? (
                <button
                  type="button"
                  onClick={() => void disconnect()}
                  disabled={busy === "disconnect"}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-700 shadow-sm transition hover:bg-red-50 disabled:opacity-60"
                >
                  {busy === "disconnect" ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                  Bağlantıyı kapat
                </button>
              ) : null}
            </div>
          </form>
        </AdminPanel>

        <aside className="space-y-5">
          <AdminPanel title="Kaynak durumu">
            <dl className="space-y-3 p-5 text-sm">
              {[
                ["JSON kaynağı", source?.reachable ? "Ulaşılıyor" : "Ulaşılamıyor"],
                ["Profil", form.browserProfile || "-"],
                ["Port", form.browserPort || connection.browserPort || "-"],
                ["Profil durumu", currentShopStatus ? (currentShopStatus.ok ? "Hazır" : "Hata") : "-"],
                ["Kaynak çalışması", (source as Record<string, any> | undefined)?.runAt ? formatDate(String((source as Record<string, any>).runAt)) : "-"],
                ["Kaynak sipariş", String((currentShopStatus && currentShopStatus.order_count) || 0)],
                ["Son KenPost sync", store?.lastSyncedAt ? formatDate(store.lastSyncedAt) : "-"],
                ["KenPost sipariş", String(store?.ordersCount || 0)],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  <dt className="text-slate-500">{label}</dt>
                  <dd className="break-words text-right font-semibold text-slate-900">{value}</dd>
                </div>
              ))}
              {currentShopStatus && !currentShopStatus.ok ? (
                <div className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-red-700">
                  <dt className="text-xs font-semibold uppercase">Profil hatası</dt>
                  <dd className="mt-1 break-words text-sm">{String(currentShopStatus.error || "Profil hazır değil.")}</dd>
                </div>
              ) : null}
            </dl>
          </AdminPanel>

          <AdminPanel title="Kayıt">
            <div className="space-y-3 p-5 text-sm">
              <ProfileSummaryRow icon={<Store className="h-4 w-4" />} label="Mağaza" value={store?.name || form.siteName || "-"} />
              <ProfileSummaryRow icon={<ShieldCheck className="h-4 w-4" />} label="Durum" value={store?.active ? "Aktif" : connection.active ? "Aktif" : "Yeni"} />
              <ProfileSummaryRow icon={<KeyRound className="h-4 w-4" />} label="Kullanıcı" value={String(connection.usernamePreview || "-")} />
            </div>
          </AdminPanel>
        </aside>
      </div>
    </div>
  );
}

function AmazonConnectionPage() {
  return <Navigate to="/customer/stores/amazon-v2/new" replace />;
}

function syncTriggerLabel(value: unknown) {
  const normalized = text(value).toLowerCase();
  if (normalized === "scheduled" || normalized === "scheduled_due") return "Otomatik";
  if (normalized === "admin_due") return "Admin otomatik";
  if (normalized === "admin_platform_view") return "Admin sayfa kontrolü";
  if (normalized === "admin_manual") return "Admin manuel";
  if (normalized === "manual") return "Manuel";
  if (normalized === "customer") return "Müşteri";
  if (normalized === "admin") return "Admin";
  return normalized || "-";
}

function syncRangeLabel(value: unknown) {
  const normalized = text(value).toLowerCase();
  if (normalized === "1d") return "1 gün";
  if (normalized === "3d") return "3 gün";
  if (normalized === "7d") return "7 gün";
  if (normalized === "30d") return "30 gün";
  if (normalized === "1m") return "1 ay";
  if (normalized === "3m") return "3 ay";
  if (normalized === "all") return "Hepsi";
  return normalized;
}

function syncLogResultDetail(row: DataRow) {
  const parts = [`Yeni ${numberText(row.insertedCount)}, güncellenen ${numberText(row.updatedCount)}`];
  const range = syncRangeLabel(row.syncRange);
  if (range) parts.push(`Aralık ${range}`);
  const scanned = rowNumber(row, "scannedCount");
  const matched = rowNumber(row, "matchedCount");
  const pages = rowNumber(row, "pagesFetched");
  if (scanned > 0) {
    parts.push(matched > 0 && matched !== scanned ? `Kaynak ${numberText(scanned)} tarandı, ${numberText(matched)} eşleşti` : `Kaynak ${numberText(scanned)} tarandı`);
  } else if (pages > 0) {
    parts.push(`${numberText(pages)} sayfa tarandı`);
  }
  return parts.join(" · ");
}

function syncStatusLabel(value: unknown) {
  const normalized = text(value);
  if (normalized === "success" || normalized === "ok") return "Başarılı";
  if (normalized === "failed" || normalized === "error") return "Hata";
  if (normalized === "running") return "Çalışıyor";
  if (normalized === "skipped") return "Atlandı";
  if (normalized === "pending") return "Bekliyor";
  return normalized || "-";
}

function durationText(value: unknown) {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return "-";
  if (parsed < 1000) return `${Math.round(parsed)} ms`;
  const seconds = parsed / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)} sn`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  return `${minutes} dk ${remainder} sn`;
}

function StoreSyncLogsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const storeId = params.storeId || "";
  const [store, setStore] = useState<StoreSummary | null>(null);
  const [logs, setLogs] = useState<StoreSyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function loadLogs() {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.storeSyncLogs(storeId);
      setStore(result.store);
      setLogs(result.logs || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Senkron logları alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (storeId) void loadLogs();
  }, [storeId]);

  if (loading) return <LoadingPanel label="Senkron logları yükleniyor" />;

  if (error && !store) {
    return (
      <div className="space-y-4">
        <ErrorBanner message={error} />
        <button
          type="button"
          onClick={() => navigate("/customer/stores")}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm"
        >
          Mağazalara dön
        </button>
      </div>
    );
  }

  const latest = logs[0] || null;
  const successCount = logs.filter((log) => log.status === "success").length;
  const failedCount = logs.filter((log) => log.status === "failed").length;
  const intervalLabel =
    store && orderSyncIntervalOptions.find((option) => option.value === Number(store.orderSyncIntervalMinutes))?.label;

  return (
    <div className="space-y-5">
      <Link className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-slate-950" to="/customer/stores">
        <ArrowLeft className="h-4 w-4" />
        Mağazalara dön
      </Link>

      <CustomerPageHeader
        title="Senkron logları"
        caption={store ? `${store.name} · ${platformLabel(store.platform)} · ${numberText(store.ordersCount)} sipariş` : "Mağaza senkron geçmişi"}
        icon={<History className="h-5 w-5" />}
        action={
          store ? (
            <Link
              to={`/customer/stores/${encodeURIComponent(store.id)}`}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              Mağaza detayı
              <ChevronRight className="h-4 w-4" />
            </Link>
          ) : null
        }
      />

      {error ? <ErrorBanner message={error} /> : null}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Son durum" value={latest ? syncStatusLabel(latest.status) : "-"} tone={latest?.status === "failed" ? "red" : latest?.status === "success" ? "emerald" : "slate"} icon={<Clock3 className="h-5 w-5" />} />
        <AdminStatCard label="Başarılı" value={successCount} tone="emerald" icon={<CheckCircle2 className="h-5 w-5" />} />
        <AdminStatCard label="Hata" value={failedCount} tone="red" icon={<AlertTriangle className="h-5 w-5" />} />
        <AdminStatCard label="Toplam log" value={logs.length} tone="cyan" icon={<BarChart3 className="h-5 w-5" />} />
      </section>

      <AdminPanel title="Otomatik kontrol" caption="Pasif mağazalar otomatik kontrolde denenmez.">
        <dl className="grid gap-3 p-5 text-sm md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-slate-200 px-4 py-3">
            <dt className="text-slate-500">Durum</dt>
            <dd className="mt-1 font-semibold text-slate-950">{store?.active !== false ? "Aktif" : "Pasif"}</dd>
          </div>
          <div className="rounded-xl border border-slate-200 px-4 py-3">
            <dt className="text-slate-500">Kontrol</dt>
            <dd className="mt-1 font-semibold text-slate-950">{store?.orderSyncEnabled === false ? "Kapalı" : "Açık"}</dd>
          </div>
          <div className="rounded-xl border border-slate-200 px-4 py-3">
            <dt className="text-slate-500">Sıklık</dt>
            <dd className="mt-1 font-semibold text-slate-950">{intervalLabel || `${numberText(store?.orderSyncIntervalMinutes || 30)} dk`}</dd>
          </div>
          <div className="rounded-xl border border-slate-200 px-4 py-3">
            <dt className="text-slate-500">Sıradaki</dt>
            <dd className="mt-1 font-semibold text-slate-950">{store?.orderSyncNextRunAt ? formatDate(store.orderSyncNextRunAt) : "-"}</dd>
          </div>
        </dl>
      </AdminPanel>

      <AdminPanel title="Log kayıtları" count={logs.length}>
        <DataTable
          rows={logs as unknown as DataRow[]}
          empty="Henüz senkron logu yok."
          columns={[
            { label: "Durum", value: (row) => statusBadge(text(row.status)) },
            { label: "Tür", value: (row) => syncTriggerLabel(row.trigger) },
            { label: "Platform", value: (row) => badge(text(row.platform)) },
            {
              label: "Sonuç",
              value: (row) => (
                <NameStack
                  title={text(row.summary) || `${numberText(row.fetchedCount)} çekildi`}
                  detail={syncLogResultDetail(row)}
                />
              ),
            },
            { label: "Başlangıç", value: (row) => dateText(row.startedAt) },
            { label: "Süre", value: (row) => durationText(row.durationMs), align: "right" },
            { label: "Hata", value: (row) => <span className="block max-w-sm whitespace-normal break-words">{text(row.errorMessage) || "-"}</span> },
          ]}
        />
      </AdminPanel>
    </div>
  );
}

function storeConnectionProfileFor(platform: StorePlatform) {
  return storeConnectionProfiles[platform] || defaultStoreConnectionProfile;
}

function storeConnectionFieldsFor(platform: StorePlatform, mode: string) {
  const fields = storeCredentialFields[platform] || [];
  if (platform === "opencart") {
    if (mode === "admin") {
      return fields.map((field) => {
        if (field.key === "opencartAdminUrl") return { ...field, label: "Admin URL" };
        if (field.key === "opencartUsername") return { ...field, label: "Admin kullanıcı adı" };
        if (field.key === "opencartPassword") return { ...field, label: "Admin şifre" };
        return field;
      });
    }
    return fields;
  }
  if (platform !== "jtl") return fields;
  const baseKeys = new Set(["jtlStoreUrl", "jtlClientId", "jtlClientSecret"]);
  const databaseKeys = new Set(["jtlDbHost", "jtlDbPort", "jtlDbName", "jtlDbUser", "jtlDbPassword"]);
  return fields.filter((field) => baseKeys.has(field.key) || (mode === "ip" && databaseKeys.has(field.key)));
}

function isOptionalStoreConnectionField(platform: StorePlatform, fieldKey: string, mode: string) {
  if (platform === "manual" && fieldKey === "storeUrl") return true;
  if (fieldKey === "shopifyClientId" || fieldKey === "kauflandStorefront") return true;
  if (platform === "jtl" && fieldKey.startsWith("jtlDb")) return mode !== "ip";
  return false;
}

function StoreCreatePage() {
  const { platform } = useParams();
  const navigate = useNavigate();
  const normalizedPlatform = (platform || "manual").toLowerCase();
  const supportedPlatform = storePlatformOptions.some((option) => option.value === normalizedPlatform);
  const initialPlatform = supportedPlatform ? (normalizedPlatform as StorePlatform) : "manual";
  const [form, setForm] = useState<Record<string, string>>({
    displayName: "",
    jtlConnectionMode: "platform",
    kauflandStorefront: "de",
    opencartConnectionMode: "api",
    platform: initialPlatform,
    woocommerceCountryFilterEnabled: "false",
    woocommerceCountryFilterCountryCodes: "DE",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function updateField(key: string, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function updatePlatform(value: string) {
    if (value === "shopify") {
      window.open("https://apps.shopify.com/connector-for-kenpost", "_blank", "noopener,noreferrer");
      navigate("/customer/stores");
      return;
    }
    if (value === "amazon_v2") {
      navigate("/customer/stores/amazon-v2/new");
      return;
    }
    if (value === "amazon" || value === "etsy") {
      navigate(`/customer/stores/${value}/new`);
      return;
    }
    const nextPlatform = (storePlatformOptions.some((option) => option.value === value) ? value : "manual") as StorePlatform;
    setForm((current) => ({
      ...current,
      platform: nextPlatform,
      ...(nextPlatform === "jtl" && !current.jtlConnectionMode ? { jtlConnectionMode: "platform" } : {}),
      ...(nextPlatform === "kaufland" && !current.kauflandStorefront ? { kauflandStorefront: "de" } : {}),
      ...(nextPlatform === "opencart" && !current.opencartConnectionMode ? { opencartConnectionMode: "api" } : {}),
      ...(nextPlatform === "woocommerce" && !current.woocommerceCountryFilterCountryCodes
        ? { woocommerceCountryFilterCountryCodes: "DE" }
        : {}),
    }));
  }

  async function createStore(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        ...(form.platform === "jtl" && !form.jtlConnectionMode ? { jtlConnectionMode: "platform" } : {}),
        ...(form.platform === "opencart" && !form.opencartConnectionMode ? { opencartConnectionMode: "api" } : {}),
      };
      const result = await apiClient.createStore(payload as CreateStorePayload);
      navigate(`/customer/stores/${encodeURIComponent(result.store.id)}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mağaza oluşturulamadı.");
    } finally {
      setSaving(false);
    }
  }

  if (normalizedPlatform === "amazon_v2" || normalizedPlatform === "amazon-v2") return <AmazonV2ConnectionPage />;

  if (normalizedPlatform === "amazon") {
    return <AmazonConnectionPage />;
  }

  if (normalizedPlatform === "etsy") {
    return <EtsyBrowserConnectionPage />;
  }

  if (normalizedPlatform === "ebay") {
    return (
      <div className="space-y-5">
        <Link className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900" to="/customer/stores">
          <ArrowLeft className="h-4 w-4" />
          Mağazalara dön
        </Link>
        <CustomerPageHeader title="eBay mağazası bağla" caption="eBay hesabınızı güvenli bağlantı akışıyla ekleyin." icon={<Store className="h-5 w-5" />} />
        <a
          href="/api/ebay/connect?returnUrl=/customer/stores"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
        >
          <Store className="h-4 w-4" />
          eBay OAuth başlat
        </a>
      </div>
    );
  }

  if (normalizedPlatform === "shopify") {
    return (
      <div className="space-y-5">
        <Link className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900" to="/customer/stores">
          <ArrowLeft className="h-4 w-4" />
          Mağazalara dön
        </Link>
        <CustomerPageHeader
          title="Shopify mağazası bağla"
          caption="Yeni Shopify bağlantıları güvenli kurulum ve ücretlendirme için Shopify App Store üzerinden yapılır."
          icon={<Store className="h-5 w-5" />}
        />
        <AdminPanel title="Connector for KenPost" caption="Manuel API anahtarı veya Shopify şifresi gerekmez.">
          <div className="space-y-4 p-5">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-900">
              Shopify mağazanızda uygulamayı kurun, Shopify planını onaylayın ve ardından KenPost hesabınızı tek tıkla bağlayın.
            </div>
            <a
              href="https://apps.shopify.com/connector-for-kenpost"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              <ExternalLink className="h-4 w-4" />
              Shopify App Store'da aç
            </a>
          </div>
        </AdminPanel>
      </div>
    );
  }

  const currentPlatform = ((form.platform || initialPlatform) as StorePlatform) || "manual";
  const connectionMode = currentPlatform === "opencart" ? form.opencartConnectionMode || "api" : form.jtlConnectionMode || "platform";
  const profile = storeConnectionProfileFor(currentPlatform);
  const fields = storeConnectionFieldsFor(currentPlatform, connectionMode);
  const currentPlatformLabel = platformLabel(currentPlatform);
  const logoSrc = platformLogoSrc(currentPlatformLabel);
  const jtlModeOptions = [
    {
      value: "platform",
      label: "Platform Bağlantısı",
      detail: "Önerilen yöntem; client bilgileriyle çalışır.",
    },
    {
      value: "ip",
      label: "IP / DB Bağlantısı",
      detail: "Özel JTL kurulumları için gelişmiş alanlar.",
    },
  ];
  const opencartModeOptions = [
    {
      value: "api",
      label: "API anahtarı / Beyaz IP",
      detail: "OpenCart API kullanıcı adı ve anahtarıyla çalışır; gerekiyorsa sabit IP relay kullanılır.",
    },
    {
      value: "admin",
      label: "Admin erişimi",
      detail: "Sipariş listesini admin panelinden okumak için kullanıcı adı ve şifre kullanır.",
    },
  ];

  return (
    <div className="space-y-5">
      <Link className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900" to="/customer/stores">
        <ArrowLeft className="h-4 w-4" />
        Mağazalara dön
      </Link>
      <CustomerPageHeader
        title={`${currentPlatformLabel} mağazası bağla`}
        caption={profile.caption}
        icon={<Store className="h-5 w-5" />}
        action={
          logoSrc ? (
            <span className="flex h-12 min-w-32 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 shadow-sm">
              <img src={logoSrc} alt={currentPlatformLabel} className="max-h-8 max-w-full object-contain" />
            </span>
          ) : (
            badge(currentPlatform)
          )
        }
      />
      {error ? <ErrorBanner message={error} /> : null}
      {currentPlatform === "shopify" ? (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-900">
          <p className="font-semibold">KenPost Billing Bridge runs inside Shopify Admin.</p>
          <p className="mt-1">
            The Shopify App Store app home uses installed app permissions, App Bridge session tokens, token exchange, and Shopify Admin API. This customer connector form is only for existing KenPost account integrations.
          </p>
        </div>
      ) : null}

      <section className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <AdminPanel title="Bağlantı profili" caption="Platforma göre gerekli alanlar ve çalışma biçimi ayrıştırıldı.">
          <form className="space-y-5 p-5" onSubmit={createStore}>
            <div className="grid gap-4 lg:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">Platform</span>
                <select
                  value={currentPlatform}
                  onChange={(event) => updatePlatform(event.target.value)}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5"
                >
                  {storePlatformOptions.filter((option) => option.value !== "amazon").map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <ProfileInput label="Mağaza adı" value={form.displayName || ""} onChange={(value) => updateField("displayName", value)} required />
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              {profile.summary.map((item) => (
                <div key={item.label} className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{item.label}</p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">{item.value}</p>
                </div>
              ))}
            </div>

            {currentPlatform === "jtl" ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
                <p className="px-1 pb-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">JTL bağlantı modu</p>
                <div className="grid gap-2 md:grid-cols-2">
                  {jtlModeOptions.map((option) => {
                    const active = connectionMode === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateField("jtlConnectionMode", option.value)}
                        className={cx(
                          "rounded-xl border px-4 py-3 text-left transition",
                          active ? "border-slate-950 bg-white shadow-sm" : "border-slate-200 bg-white/70 hover:bg-white"
                        )}
                      >
                        <span className="block text-sm font-semibold text-slate-950">{option.label}</span>
                        <span className="mt-1 block text-xs leading-5 text-slate-500">{option.detail}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {currentPlatform === "opencart" ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
                <p className="px-1 pb-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">OpenCart bağlantı modu</p>
                <div className="grid gap-2 md:grid-cols-2">
                  {opencartModeOptions.map((option) => {
                    const active = connectionMode === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateField("opencartConnectionMode", option.value)}
                        className={cx(
                          "rounded-xl border px-4 py-3 text-left transition",
                          active ? "border-slate-950 bg-white shadow-sm" : "border-slate-200 bg-white/70 hover:bg-white"
                        )}
                      >
                        <span className="block text-sm font-semibold text-slate-950">{option.label}</span>
                        <span className="mt-1 block text-xs leading-5 text-slate-500">{option.detail}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-2">
              {fields.map((field) => {
                const optional = isOptionalStoreConnectionField(currentPlatform, field.key, connectionMode);
                const help = profile.fieldHelp?.[field.key];
                const placeholder = profile.fieldPlaceholder?.[field.key] || field.label;
                return (
                  <label key={field.key} className="block space-y-1.5">
                    <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                      {field.label}
                      {optional ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">Opsiyonel</span> : null}
                    </span>
                    <input
                      type={field.type || "text"}
                      value={form[field.key] || ""}
                      onChange={(event) => updateField(field.key, event.target.value)}
                      required={!optional}
                      placeholder={placeholder}
                      className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5"
                    />
                    {help ? <span className="block text-xs leading-5 text-slate-500">{help}</span> : null}
                  </label>
                );
              })}
            </div>

            {currentPlatform === "woocommerce" ? (
              <WooCommerceCountryFilterFields
                enabled={booleanFormValue(form.woocommerceCountryFilterEnabled)}
                countryCode={form.woocommerceCountryFilterCountryCodes || "DE"}
                onEnabledChange={(value) => updateField("woocommerceCountryFilterEnabled", value ? "true" : "false")}
                onCountryCodeChange={(value) => updateField("woocommerceCountryFilterCountryCodes", value)}
              />
            ) : null}

            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-5">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusSquare className="h-4 w-4" />}
                {profile.submitLabel}
              </button>
              <button
                type="button"
                onClick={() =>
                  setForm({
                    displayName: "",
                    jtlConnectionMode: "platform",
                    kauflandStorefront: "de",
                    opencartConnectionMode: "api",
                    platform: currentPlatform,
                    woocommerceCountryFilterEnabled: "false",
                    woocommerceCountryFilterCountryCodes: "DE",
                  })
                }
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Temizle
              </button>
            </div>
          </form>
        </AdminPanel>

        <aside className="space-y-5">
          <AdminPanel title="Gerekli bilgiler" count={profile.requirements.length}>
            <div className="space-y-2 p-5">
              {profile.requirements.map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <span className="text-sm font-medium text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </AdminPanel>

          <AdminPanel title="Kurulum akışı">
            <ol className="space-y-3 p-5">
              {profile.steps.map((step, index) => (
                <li key={step} className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-950 text-xs font-semibold text-white">
                    {index + 1}
                  </span>
                  <span className="pt-1 text-sm leading-6 text-slate-600">{step}</span>
                </li>
              ))}
            </ol>
          </AdminPanel>
        </aside>
      </section>
    </div>
  );
}

function storeEditFormFrom(store: StoreDetail) {
  const next: Record<string, string> = {
    name: store.name || "",
    storeUrl: store.storeUrl || "",
    active: store.active ? "true" : "false",
    skuOnLabelEnabled: store.skuOnLabelEnabled ? "true" : "false",
    onlineSellerLabelPrefixEnabled: store.onlineSellerLabelPrefixEnabled ? "true" : "false",
    shopifyFulfillmentSyncEnabled: store.shopifyFulfillmentSyncEnabled ? "true" : "false",
    shopwareMarkShipped: store.shopwareMarkShipped ? "true" : "false",
    plentyTrackingWriteEnabled: store.plentyTrackingWriteEnabled ? "true" : "false",
    plentySyncRange: store.plentySyncRange || "3d",
    afterbuyInvoiceAutomationEnabled: store.afterbuyInvoiceAutomationEnabled ? "true" : "false",
    afterbuyInvoiceTriggerTag: store.afterbuyInvoiceTriggerTag || "KenPost-Rechnung",
    autoLabelProductByWeightEnabled: store.autoLabelProductByWeightEnabled ? "true" : "false",
    orderDisplayCountryCodes: (store.orderDisplayCountryCodes || []).join(", "),
    hideCompletedOrders: store.hideCompletedOrders ? "true" : "false",
    woocommerceCountryFilterEnabled: store.woocommerceCountryFilterEnabled ? "true" : "false",
    woocommerceCountryFilterCountryCodes: (store.woocommerceCountryFilterCountryCodes || ["DE"])[0] || "DE",
    orderSyncEnabled: store.orderSyncEnabled === false ? "false" : "true",
    orderSyncIntervalMinutes: String(store.orderSyncIntervalMinutes || 30),
  };
  for (const field of storeCredentialFields[store.platform as StorePlatform] || []) {
    if (field.key === "storeUrl") continue;
    const current = store.credentials.fields[field.key];
    next[field.key] = current && !current.secret ? current.preview || "" : "";
  }
  return next;
}

function booleanFormValue(value: string) {
  return value === "true";
}

function platformLabel(value: string) {
  const normalized = value.toLowerCase();
  return storePlatformOptions.find((option) => option.value === normalized || option.label.toLowerCase() === normalized)?.label || value;
}

function platformLogoSrc(value: string) {
  const normalized = value.toLowerCase().replace(/\s+/g, "");
  if (normalized === "amazonv2" || normalized === "amazon_v2") return "/logos/platforms/amazon.png";
  return authMarketplaceTiles.find((item) => item.label.toLowerCase().replace(/\s+/g, "") === normalized)?.logoSrc || null;
}

function formatBytes(value: number) {
  if (!value) return "-";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function emptySenderAddressForm(): StoreSenderAddressPayload {
  return {
    senderName: "",
    street1: "",
    postalCode: "",
    city: "",
    country: "",
  };
}

function senderAddressFormFrom(address?: StoreSenderAddressSummary | null, senderNameOverride?: string): StoreSenderAddressPayload {
  return {
    senderName: text(senderNameOverride) || text(address?.senderName),
    street1: text(address?.street1),
    postalCode: text(address?.postalCode),
    city: text(address?.city),
    country: text(address?.country),
  };
}

function senderAddressFormForStore(store: StoreDetail): StoreSenderAddressPayload {
  if (store.activeSenderAddress) return senderAddressFormFrom(store.activeSenderAddress);
  return senderAddressFormFrom(store.globalSenderAddress, store.name);
}

function senderAddressLine(address?: StoreSenderAddressSummary | StoreSenderAddressPayload | null) {
  return compactAddress(address?.street1, address?.postalCode, address?.city, address?.country);
}

function senderAddressHasRequired(address?: StoreSenderAddressSummary | StoreSenderAddressPayload | null) {
  return Boolean(text(address?.senderName) && text(address?.street1) && text(address?.postalCode) && text(address?.city) && text(address?.country));
}

function StoreSenderAddressPanel({
  busy,
  editorOpen,
  form,
  onActivate,
  onCancelEditor,
  onChange,
  onCopyGlobal,
  onReset,
  onStartEditor,
  onSubmit,
  onUseGlobal,
  store,
}: {
  busy: string | null;
  editorOpen: boolean;
  form: StoreSenderAddressPayload;
  onActivate: (addressId: string) => void;
  onCancelEditor: () => void;
  onChange: (key: keyof StoreSenderAddressPayload, value: string) => void;
  onCopyGlobal: () => void;
  onReset: () => void;
  onStartEditor: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onUseGlobal: () => void;
  store: StoreDetail;
}) {
  const activeAddress = store.activeSenderAddress;
  const globalAddress = store.globalSenderAddress;
  const currentAddress = activeAddress || globalAddress;
  const usingStoreOverride = store.senderAddressMode === "store_override" && Boolean(activeAddress);
  const globalReady = senderAddressHasRequired(globalAddress);
  const formReady = senderAddressHasRequired(form);
  const history = Array.isArray(store.senderAddressHistory) ? store.senderAddressHistory : [];
  const modeLabel = usingStoreOverride ? "Bu mağaza özel gönderici adresi kullanıyor" : "Bu mağaza genel profil adresini kullanıyor";
  const inactiveHistory = history.filter((address) => !address.active);

  return (
    <AdminPanel
      title="Mağaza gönderici adresi"
      caption="Varsayılan olarak profilinizdeki genel gönderici adresi kullanılır; sadece bu mağaza farklı adresten gönderiyorsa özel adres tanımlayın."
      count={usingStoreOverride ? 1 : undefined}
      action={
        <span
          className={cx(
            "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
            usingStoreOverride ? "border-cyan-200 bg-cyan-50 text-cyan-700" : "border-slate-200 bg-slate-50 text-slate-600"
          )}
        >
          {usingStoreOverride ? "Mağaza özel adresi" : "Varsayılan genel adres"}
        </span>
      }
    >
      <div className="space-y-5 p-5">
        <div className={cx("rounded-xl border p-4", usingStoreOverride ? "border-cyan-200 bg-cyan-50" : "border-slate-200 bg-slate-50")}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-700 ring-1 ring-slate-200">
                <Truck className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-950">{modeLabel}</p>
                <p className="mt-1 text-sm leading-6 text-slate-700">{text(currentAddress?.senderName) || "Gönderici adı eksik"}</p>
                <p className="mt-1 break-words text-sm leading-6 text-slate-500">{senderAddressLine(currentAddress)}</p>
                {!usingStoreOverride && !globalReady ? (
                  <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                    Genel gönderici adresi eksik. Etiket oluşturmak için profil gönderici adresini veya bu mağaza için özel adresi tamamlayın.
                  </p>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {!editorOpen ? (
                <button
                  type="button"
                  onClick={onStartEditor}
                  disabled={Boolean(busy)}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
                >
                  <PlusSquare className="h-4 w-4" />
                  {usingStoreOverride ? "Özel adresi değiştir" : "Bu mağaza için farklı adres kullan"}
                </button>
              ) : null}
              {usingStoreOverride ? (
                <button
                  type="button"
                  onClick={onUseGlobal}
                  disabled={Boolean(busy)}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                >
                  {busy === "use-global" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  Genel profil adresine dön
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {editorOpen ? (
          <form className="space-y-4 rounded-xl border border-slate-200 bg-white p-4" onSubmit={onSubmit}>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-950">{usingStoreOverride ? "Özel gönderici adresini değiştir" : "Mağazaya özel gönderici adresi tanımla"}</p>
                <p className="mt-1 text-sm leading-6 text-slate-500">Kaydettiğiniz adres yalnızca bu mağazada etiket oluştururken kullanılır.</p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <button
                  type="button"
                  onClick={onCopyGlobal}
                  disabled={!globalReady || Boolean(busy)}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                >
                  <Copy className="h-4 w-4" />
                  Genel adresi forma kopyala
                </button>
                <button
                  type="button"
                  onClick={onCancelEditor}
                  disabled={Boolean(busy)}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                  Vazgeç
                </button>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <ProfileInput label="Gönderici adı" value={form.senderName} onChange={(value) => onChange("senderName", value)} required />
              <div className="md:col-span-2">
                <ProfileInput label="Gönderici adresi" value={form.street1} onChange={(value) => onChange("street1", value)} required />
              </div>
              <ProfileInput label="Posta kodu" value={form.postalCode} onChange={(value) => onChange("postalCode", value)} required />
              <ProfileInput label="Şehir" value={form.city} onChange={(value) => onChange("city", value)} required />
              <ProfileInput label="Ülke" value={form.country} onChange={(value) => onChange("country", value)} required />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={!formReady || Boolean(busy)}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
              >
                {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Bu mağazada özel adresi kullan
              </button>
              <button
                type="button"
                onClick={onReset}
                disabled={Boolean(busy)}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
              >
                <RotateCcw className="h-4 w-4" />
                Formu boşalt
              </button>
            </div>
          </form>
        ) : null}

        {!usingStoreOverride && !editorOpen ? (
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-sm font-semibold text-slate-950">Profildeki genel gönderici adresi</p>
            <p className="mt-1 text-sm leading-6 text-slate-600">{text(globalAddress?.senderName) || "-"}</p>
            <p className="mt-1 break-words text-sm leading-6 text-slate-500">{senderAddressLine(globalAddress)}</p>
          </div>
        ) : null}

        {inactiveHistory.length > 0 ? (
          <div className="border-t border-slate-100 pt-5">
            <p className="text-sm font-semibold text-slate-950">Daha önce kaydedilen özel adresler</p>
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              {inactiveHistory.map((address) => (
                <article key={address.id || `${address.senderName}-${address.updatedAt}`} className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="break-words text-sm font-semibold text-slate-950">{text(address.senderName) || "-"}</p>
                      <p className="mt-1 break-words text-sm leading-6 text-slate-500">{senderAddressLine(address)}</p>
                    </div>
                    {statusBadge("inactive")}
                  </div>
                  {address.id ? (
                    <button
                      type="button"
                      onClick={() => onActivate(address.id!)}
                      disabled={Boolean(busy)}
                      className="mt-3 inline-flex min-h-9 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                    >
                      {busy === `activate:${address.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                      Aktif yap
                    </button>
                  ) : null}
                </article>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </AdminPanel>
  );
}

function StoreDetailPage() {
  const params = useParams();
  const storeId = params.storeId || "";
  const navigate = useNavigate();
  const [store, setStore] = useState<StoreDetail | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [senderAddressForm, setSenderAddressForm] = useState<StoreSenderAddressPayload>(() => emptySenderAddressForm());
  const [senderAddressBusy, setSenderAddressBusy] = useState<string | null>(null);
  const [senderAddressEditorOpen, setSenderAddressEditorOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  async function loadStore() {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.store(storeId);
      setStore(result.store);
      setForm(storeEditFormFrom(result.store));
      setSenderAddressForm(senderAddressFormForStore(result.store));
      setSenderAddressEditorOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mağaza detayı alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadStore();
  }, [storeId]);

  function updateField(key: string, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function updateSenderAddressField(key: keyof StoreSenderAddressPayload, value: string) {
    setSenderAddressForm((current) => ({ ...current, [key]: value }));
  }

  function applyStoreResult(nextStore: StoreDetail, message: string) {
    setStore(nextStore);
    setForm(storeEditFormFrom(nextStore));
    setSenderAddressForm(senderAddressFormForStore(nextStore));
    setSenderAddressEditorOpen(false);
    setNotice(message);
  }

  async function saveStoreSenderAddress(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!store) return;
    setSenderAddressBusy("save");
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.createStoreSenderAddress(store.id, senderAddressForm);
      applyStoreResult(result.store, "Mağaza özel gönderici adresi aktif edildi.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mağaza gönderici adresi kaydedilemedi.");
    } finally {
      setSenderAddressBusy(null);
    }
  }

  function copyGlobalSenderAddressToForm() {
    if (!store?.globalSenderAddress) return;
    setSenderAddressForm(senderAddressFormFrom(store.globalSenderAddress, store.name));
    setSenderAddressEditorOpen(true);
    setNotice(null);
  }

  function resetSenderAddressForm() {
    setSenderAddressForm(emptySenderAddressForm());
    setSenderAddressEditorOpen(true);
    setNotice(null);
  }

  function openSenderAddressEditor() {
    if (!store) return;
    setSenderAddressForm(senderAddressFormForStore(store));
    setSenderAddressEditorOpen(true);
    setError(null);
    setNotice(null);
  }

  function closeSenderAddressEditor() {
    if (!store) return;
    setSenderAddressForm(senderAddressFormForStore(store));
    setSenderAddressEditorOpen(false);
    setNotice(null);
  }

  async function useGlobalSenderAddress() {
    if (!store) return;
    const confirmed = await askConfirmation({
      title: "Genel gönderici adresine dönülsün mü?",
      description: `${store.name} mağazasındaki özel gönderici adresi pasife alınacak.`,
      details: ["Etiketler bundan sonra profilinizdeki genel gönderici adresini kullanır."],
      confirmLabel: "Genel adrese dön",
      tone: "warning",
    });
    if (!confirmed) return;
    setSenderAddressBusy("use-global");
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.useGlobalStoreSenderAddress(store.id);
      applyStoreResult(result.store, "Mağaza yeniden genel gönderici adresini kullanıyor.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Genel gönderici adresine dönülemedi.");
    } finally {
      setSenderAddressBusy(null);
    }
  }

  async function activateSenderAddress(addressId: string) {
    if (!store) return;
    setSenderAddressBusy(`activate:${addressId}`);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.activateStoreSenderAddress(store.id, addressId);
      applyStoreResult(result.store, "Seçilen mağaza gönderici adresi aktif edildi.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mağaza gönderici adresi aktif edilemedi.");
    } finally {
      setSenderAddressBusy(null);
    }
  }

  async function saveStore(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!store) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const payload: Record<string, unknown> = {
        name: form.name || "",
        storeUrl: form.storeUrl || "",
        active: booleanFormValue(form.active),
        skuOnLabelEnabled: booleanFormValue(form.skuOnLabelEnabled),
        onlineSellerLabelPrefixEnabled: booleanFormValue(form.onlineSellerLabelPrefixEnabled),
        shopifyFulfillmentSyncEnabled: booleanFormValue(form.shopifyFulfillmentSyncEnabled),
        shopwareMarkShipped: booleanFormValue(form.shopwareMarkShipped),
        plentyTrackingWriteEnabled:
          store.platform === "plenty" ? booleanFormValue(form.plentyTrackingWriteEnabled) : false,
        plentySyncRange: store.platform === "plenty" ? form.plentySyncRange || "3d" : "3d",
        afterbuyInvoiceAutomationEnabled: store.platform === "afterbuy" ? booleanFormValue(form.afterbuyInvoiceAutomationEnabled) : false,
        afterbuyInvoiceTriggerTag: store.platform === "afterbuy" ? form.afterbuyInvoiceTriggerTag || null : null,
        autoLabelProductByWeightEnabled: booleanFormValue(form.autoLabelProductByWeightEnabled),
        orderDisplayCountryCodes: form.orderDisplayCountryCodes || "",
        hideCompletedOrders: booleanFormValue(form.hideCompletedOrders),
        woocommerceCountryFilterEnabled:
          store.platform === "woocommerce" ? booleanFormValue(form.woocommerceCountryFilterEnabled) : false,
        woocommerceCountryFilterCountryCodes:
          store.platform === "woocommerce" ? form.woocommerceCountryFilterCountryCodes || "DE" : "",
        orderSyncEnabled: booleanFormValue(form.orderSyncEnabled),
        orderSyncIntervalMinutes: Number(form.orderSyncIntervalMinutes || 30),
      };

      for (const field of storeCredentialFields[store.platform as StorePlatform] || []) {
        if (field.key === "storeUrl") continue;
        const value = form[field.key] || "";
        if (field.type === "password" && !value.trim()) continue;
        payload[field.key] = value;
      }

      const result = await apiClient.updateStore(store.id, payload);
      setStore(result.store);
      setForm(storeEditFormFrom(result.store));
      setNotice("Mağaza ayarları güncellendi.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mağaza güncellenemedi.");
    } finally {
      setSaving(false);
    }
  }

  async function syncOrders() {
    if (!store) return;
    setSyncing(true);
    setError(null);
    setNotice(null);
    try {
      if (store.platform === "ebay") {
        const result = await apiClient.syncEbay(store.id);
        setNotice(`${result.fetched} eBay siparişi alındı. Yeni: ${result.inserted}, güncellenen: ${result.updated}.`);
      } else if (store.platform === "jtl") {
        const result = await apiClient.syncJtl(store.id);
        setNotice(result.message || `${result.imported || 0} JTL siparişi işlendi.`);
      } else if (internalOrderSyncPlatforms.includes(store.platform as OrderSyncPlatform)) {
        const result = await apiClient.syncStoreOrders(store.id, store.platform as OrderSyncPlatform);
        setNotice(result.message || `${result.fetched} ${store.platform} siparişi alındı.`);
      }
      await loadStore();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sipariş senkronu tamamlanamadı.");
    } finally {
      setSyncing(false);
    }
  }

  async function testStoreConnection() {
    if (!store || store.platform !== "shopware") return;
    setTestingConnection(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.testShopware(store.id);
      setNotice(`Shopware bağlantısı doğrulandı. API erişiminde ${numberText(result.orderCount || 0)} sipariş görünüyor.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Shopware bağlantısı doğrulanamadı.");
    } finally {
      setTestingConnection(false);
    }
  }

  async function deleteCurrentStore() {
    if (!store) return;
    if (Number(store.ordersCount || 0) > 0) {
      setError("Siparişi olan mağazalar silinemez. Önce siparişleri kontrol edin.");
      return;
    }
    const confirmed = await askConfirmation({
      title: "Mağaza silinsin mi?",
      description: `${store.name} mağazası sistemden kaldırılacak.`,
      details: ["Bu işlem mağaza bağlantısını kapatır. Sipariş içeren mağazalar silinemez."],
      confirmLabel: "Mağazayı sil",
      tone: "danger",
    });
    if (!confirmed) return;
    setDeleting(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.deleteCustomerStore(store.id);
      if (result.ok === false || result.deleted === false) {
        throw new Error(result.error || "Mağaza silinemedi.");
      }
      navigate("/customer/stores", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Mağaza silinemedi.");
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return <LoadingPanel label="Mağaza yükleniyor" />;
  }

  if (error && !store) {
    return (
      <div className="space-y-4">
        <ErrorBanner message={error} />
        <button
          type="button"
          onClick={() => navigate("/customer/stores")}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm"
        >
          Mağazalara dön
        </button>
      </div>
    );
  }

  if (!store) return null;

  const fields = (storeCredentialFields[store.platform as StorePlatform] || []).filter((item) => item.key !== "storeUrl");
  const canSync =
    store.platform !== "amazon" &&
    store.active !== false &&
    store.syncReady !== false &&
    (store.platform === "ebay" ||
      store.platform === "jtl" ||
      internalOrderSyncPlatforms.includes(store.platform as OrderSyncPlatform));
  const storeLogo = platformLogoSrc(store.platform);
  const storeTitle = platformLabel(store.platform);

  return (
    <div className="space-y-5">
      {confirmationDialog}
      <Link className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-slate-950" to="/customer/stores">
        <ArrowLeft className="h-4 w-4" />
        Mağazalara dön
      </Link>

      {store.platform === "amazon" ? <AmazonV1UpgradeNotice compact /> : null}

      <CustomerPageHeader
        title={store.name}
        caption={`${storeTitle} · ${store.storeUrl || store.ebayUsername || "Bağlantı bilgisi yok"} · ${numberText(store.ordersCount)} sipariş`}
        icon={<Store className="h-5 w-5" />}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {storeLogo ? (
              <span className="flex h-10 w-28 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                <img src={storeLogo} alt={storeTitle} className="max-h-7 max-w-full object-contain" />
              </span>
            ) : null}
            {statusBadge(store.active !== false ? "active" : "inactive")}
            {badge(store.platform)}
            <StoreDetailActionsMenu
              canSync={canSync}
              deleting={deleting}
              onDelete={deleteCurrentStore}
              onSync={syncOrders}
              store={store}
              syncing={syncing}
            />
          </div>
        }
      />

      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}

      <div className="grid gap-5 xl:grid-cols-[1.2fr_0.8fr]">
        <AdminPanel title="Mağaza ayarları" caption="Eski sistemdeki temel store ayarları ve platform credential bilgileri.">
        <form className="space-y-5 p-5" onSubmit={saveStore}>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Mağaza adı</span>
              <input
                required
                value={form.name || ""}
                onChange={(event) => updateField("name", event.target.value)}
                className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Mağaza URL</span>
              <input
                value={form.storeUrl || ""}
                onChange={(event) => updateField("storeUrl", event.target.value)}
                className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                placeholder="https://..."
              />
            </label>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {[
              ["active", "Mağaza aktif"],
              ...(store.platform === "shopify" ? [["shopifyFulfillmentSyncEnabled", "Takip geri yazma"]] : []),
              ...(store.platform === "shopware" ? [["shopwareMarkShipped", "Takip sonrası gönderildi yap"]] : []),
              ...(store.platform === "plenty" ? [["plentyTrackingWriteEnabled", "Takip ve gönderimi PlentyONE’a geri yaz"]] : []),
              ...(store.platform === "afterbuy" ? [["afterbuyInvoiceAutomationEnabled", "Afterbuy fatura otomasyonunu tetikle"]] : []),
              ["skuOnLabelEnabled", "Etikette SKU"],
              ["onlineSellerLabelPrefixEnabled", "Online seller prefix"],
              ["autoLabelProductByWeightEnabled", "Ağırlığa göre otomatik etiket"],
              ["hideCompletedOrders", "Tamamlanan siparişleri gizle"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-3">
                <span className="text-sm font-medium text-slate-700">{label}</span>
                <input
                  type="checkbox"
                  checked={booleanFormValue(form[key])}
                  onChange={(event) => updateField(key, event.target.checked ? "true" : "false")}
                  className="h-4 w-4 rounded border-slate-300"
                />
              </label>
            ))}
          </div>

          {store.platform === "woocommerce" ? (
            <WooCommerceCountryFilterFields
              enabled={booleanFormValue(form.woocommerceCountryFilterEnabled)}
              countryCode={form.woocommerceCountryFilterCountryCodes || "DE"}
              onEnabledChange={(value) => updateField("woocommerceCountryFilterEnabled", value ? "true" : "false")}
              onCountryCodeChange={(value) => updateField("woocommerceCountryFilterCountryCodes", value)}
            />
          ) : (
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Siparişlerde gösterilecek ülkeler</span>
              <input
                value={form.orderDisplayCountryCodes || ""}
                onChange={(event) => updateField("orderDisplayCountryCodes", event.target.value.toUpperCase())}
                className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                placeholder="Boş: tüm ülkeler · Örnek: DE, AT"
              />
              <span className="block text-xs leading-5 text-slate-500">İki harfli ülke kodlarını virgülle ayırın. Boş bırakırsanız tüm ülkeler görünür.</span>
            </label>
          )}

          {store.platform === "afterbuy" ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
              <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px] md:items-end">
                <div>
                  <p className="text-sm font-semibold text-amber-950">Afterbuy yerel fatura akışı</p>
                  <p className="mt-1 text-sm leading-6 text-amber-900/80">
                    Takip numarası yazıldıktan sonra bu TAG mevcut TAG'ler korunarak Afterbuy siparişine eklenir. Faturayı ve PDF e-postasını Afterbuy'ın kendi iş akışı oluşturur; KenPost fatura numarası ile tarihini doğruladıktan sonra siparişi çalışma listesinden gizler. Amazon siparişleri hesap güvenliği için bu fatura e-posta tetikleyicisinden kesin olarak hariç tutulur.
                  </p>
                </div>
                <label className="block space-y-1.5">
                  <span className="text-sm font-semibold text-amber-950">Tetik TAG'i</span>
                  <input
                    value={form.afterbuyInvoiceTriggerTag || ""}
                    onChange={(event) => updateField("afterbuyInvoiceTriggerTag", event.target.value)}
                    disabled={!booleanFormValue(form.afterbuyInvoiceAutomationEnabled)}
                    required={booleanFormValue(form.afterbuyInvoiceAutomationEnabled)}
                    className="h-11 w-full rounded-xl border border-amber-200 bg-white px-3 text-sm outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100 disabled:bg-amber-100/60 disabled:text-amber-500"
                    placeholder="KenPost-Rechnung"
                  />
                </label>
              </div>
              <p className="mt-3 text-xs leading-5 text-amber-800">
                Bu TAG, Afterbuy TAG Manager'da yalnız KenPost fatura iş akışı için oluşturulmalı ve Workflow Manager filtresinde aynı adla seçilmelidir.
              </p>
            </div>
          ) : null}

          {canSync ? (
            <div className="rounded-2xl border border-cyan-100 bg-cyan-50/60 p-4">
              <div className={cx("grid gap-4 md:items-end", store.platform === "plenty" ? "md:grid-cols-[minmax(0,1fr)_220px_220px]" : "md:grid-cols-[minmax(0,1fr)_220px]")}>
                <label className="flex items-start justify-between gap-4 rounded-xl border border-cyan-100 bg-white px-4 py-3">
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-900">Otomatik sipariş alma</span>
                    <span className="mt-1 block text-sm leading-6 text-slate-600">
                      Açık olduğunda sistem bu mağazadan siparişleri arka planda düzenli çeker.
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={booleanFormValue(form.orderSyncEnabled)}
                    onChange={(event) => updateField("orderSyncEnabled", event.target.checked ? "true" : "false")}
                    className="mt-1 h-4 w-4 rounded border-slate-300"
                  />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-sm font-semibold text-slate-700">Kontrol sıklığı</span>
                  <select
                    value={form.orderSyncIntervalMinutes || "30"}
                    onChange={(event) => updateField("orderSyncIntervalMinutes", event.target.value)}
                    disabled={!booleanFormValue(form.orderSyncEnabled)}
                    className="h-11 w-full rounded-xl border border-cyan-100 bg-white px-3 text-sm text-slate-950 outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100 disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    {orderSyncIntervalOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                {store.platform === "plenty" ? (
                  <label className="block space-y-1.5">
                    <span className="text-sm font-semibold text-slate-700">Çekilecek sipariş dönemi</span>
                    <select
                      value={form.plentySyncRange || "3d"}
                      onChange={(event) => updateField("plentySyncRange", event.target.value)}
                      disabled={!booleanFormValue(form.orderSyncEnabled)}
                      className="h-11 w-full rounded-xl border border-cyan-100 bg-white px-3 text-sm text-slate-950 outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100 disabled:bg-slate-100 disabled:text-slate-400"
                    >
                      {plentySyncRangeOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>
            </div>
          ) : null}

          {fields.length ? (
            <div className="border-t border-slate-100 pt-5">
              <h3 className="text-base font-semibold text-slate-950">Bağlantı bilgileri</h3>
              <p className="mt-1 text-sm text-slate-500">Şifreli alanlar boş bırakılırsa mevcut kayıt korunur.</p>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {fields.map((field) => {
                  const current = store.credentials.fields[field.key];
                  return (
                    <label key={field.key} className="block space-y-1.5">
                      <span className="text-sm font-medium text-slate-700">{field.label}</span>
                      <input
                        type={field.type || "text"}
                        value={form[field.key] || ""}
                        onChange={(event) => updateField(field.key, event.target.value)}
                        className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                        placeholder={field.type === "password" && current?.configured ? "Kayıtlı. Değiştirmek için yeni değer girin." : field.label}
                      />
                      {current?.configured ? (
                        <span className="text-xs text-slate-500">
                          Kayıtlı{current.preview && current.secret ? ` (${current.preview})` : ""}
                        </span>
                      ) : null}
                    </label>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Kaydet
            </button>
            <button
              type="button"
              onClick={() => setForm(storeEditFormFrom(store))}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Sıfırla
            </button>
            {store.platform === "shopware" ? (
              <button
                type="button"
                onClick={() => void testStoreConnection()}
                disabled={testingConnection}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-2.5 text-sm font-semibold text-cyan-800 shadow-sm transition hover:bg-cyan-100 disabled:opacity-60"
              >
                {testingConnection ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Bağlantıyı test et
              </button>
            ) : null}
          </div>
        </form>
        </AdminPanel>

        <aside className="space-y-5">
          <AdminPanel title="Senkron durumu">
            <dl className="space-y-3 p-5 text-sm">
              <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Son senkron</dt>
                <dd className="font-medium text-slate-900">{store.ordersSyncedAt ? formatDate(store.ordersSyncedAt) : "-"}</dd>
              </div>
              {canSync ? (
                <>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Otomatik kontrol</dt>
                    <dd className="font-medium text-slate-900">{store.orderSyncEnabled ? "Açık" : "Kapalı"}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Kontrol sıklığı</dt>
                    <dd className="font-medium text-slate-900">
                      {orderSyncIntervalOptions.find((option) => option.value === store.orderSyncIntervalMinutes)?.label || `${numberText(store.orderSyncIntervalMinutes)} dk`}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Son deneme</dt>
                    <dd className="font-medium text-slate-900">{store.orderSyncLastAttemptAt ? formatDate(store.orderSyncLastAttemptAt) : "-"}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Son başarılı</dt>
                    <dd className="font-medium text-slate-900">{store.orderSyncLastSuccessAt ? formatDate(store.orderSyncLastSuccessAt) : "-"}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Sıradaki kontrol</dt>
                    <dd className="font-medium text-slate-900">{store.orderSyncNextRunAt ? formatDate(store.orderSyncNextRunAt) : "-"}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Durum</dt>
                    <dd className="font-medium text-slate-900">{store.orderSyncLastStatus || "-"}</dd>
                  </div>
                  {store.orderSyncLastError ? (
                    <div className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-red-700">
                      <dt className="text-xs font-semibold uppercase">Son hata</dt>
                      <dd className="mt-1 break-words text-sm">{store.orderSyncLastError}</dd>
                    </div>
                  ) : null}
                </>
              ) : null}
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">Sipariş sayısı</dt>
                <dd className="font-medium text-slate-900">{numberText(store.ordersCount)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">Cache boyutu</dt>
                <dd className="font-medium text-slate-900">{formatBytes(store.ordersJsonSizeBytes)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">Kimlik bilgisi</dt>
                <dd className="font-medium text-slate-900">{store.credentials.configured ? "Kayıtlı" : "Eksik"}</dd>
              </div>
              {store.ebayStatus ? (
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">eBay</dt>
                  <dd className="font-medium text-slate-900">{store.ebayStatus}</dd>
                </div>
              ) : null}
            </dl>
          </AdminPanel>

          <AdminPanel title="Operasyon notu">
            <p className="p-5 text-sm leading-6 text-slate-600">
              Bağlantı bilgileri güvenli şekilde saklanır. Gizli alanlar gösterilmez; yeni değer girilirse güncellenir, boş bırakılırsa mevcut değer korunur.
            </p>
          </AdminPanel>
        </aside>
      </div>

      <StoreSenderAddressPanel
        busy={senderAddressBusy}
        editorOpen={senderAddressEditorOpen}
        form={senderAddressForm}
        onActivate={(addressId) => void activateSenderAddress(addressId)}
        onCancelEditor={closeSenderAddressEditor}
        onChange={updateSenderAddressField}
        onCopyGlobal={copyGlobalSenderAddressToForm}
        onReset={resetSenderAddressForm}
        onStartEditor={openSenderAddressEditor}
        onSubmit={(event) => void saveStoreSenderAddress(event)}
        onUseGlobal={() => void useGlobalSenderAddress()}
        store={store}
      />
    </div>
  );
}

function CustomerOnboardingPage() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [stores, setStores] = useState<StoreSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([apiClient.dashboard(), apiClient.stores()])
      .then(([dashboardResult, storesResult]) => {
        if (!active) return;
        setSummary(dashboardResult);
        setStores(storesResult.stores);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Onboarding bilgisi alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) return <LoadingPanel label="Başlangıç durumu yükleniyor" />;
  if (error) return <ErrorPanel title="Onboarding açılamadı" message={error} />;

  const steps = [
    { label: "Profil", done: true, detail: "Hesap aktif." },
    { label: "Mağaza bağlantısı", done: stores.length > 0, detail: stores.length ? `${stores.length} mağaza bağlı.` : "İlk mağazanızı bağlayın." },
    {
      label: "Sipariş verisi",
      done: Boolean(summary?.totals.orders),
      detail: `${numberText(summary?.totals.orders)} sipariş görünüyor.`,
    },
    {
      label: "Etiket bakiyesi",
      done: Boolean(summary?.totals.availableLabels),
      detail: `${numberText(summary?.totals.availableLabels)} kullanılabilir etiket.`,
    },
  ];

  return (
    <div className="space-y-5">
      <CustomerPageHeader title="Başlangıç" caption="Taşıma sonrası müşteri hesabı hazırlık adımları." icon={<CheckCircle2 className="h-5 w-5" />} />
      <section className="grid gap-4 md:grid-cols-2">
        {steps.map((step) => (
          <div key={step.label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className={cx("mt-0.5 h-3 w-3 shrink-0 rounded-full", step.done ? "bg-emerald-500" : "bg-amber-400")} />
              <div>
                <h2 className="text-base font-semibold text-slate-950">{step.label}</h2>
                <p className="mt-1 text-sm text-slate-500">{step.detail}</p>
              </div>
            </div>
          </div>
        ))}
      </section>
      <div className="flex flex-wrap gap-3">
        <Link className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800" to="/customer/stores">
          Mağazalara git
        </Link>
        <Link className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50" to="/customer/buy-labels">
          Etiket satın al
        </Link>
      </div>
    </div>
  );
}

function CustomerPlanPage() {
  return (
    <div className="space-y-5">
      <CustomerPageHeader title="Plan" caption="Plan ve etiket satın alma durumu." icon={<CreditCard className="h-5 w-5" />} />
      <section className="grid gap-5 xl:grid-cols-2">
        <DetailCard
          title="Geçiş planı"
          items={[
            ["Sürüm", KENPOST_VERSION_LABEL],
            ["Veri kaynağı", "KenPost operasyon verisi"],
            ["Etiket satın alma", "Shopify checkout bağlantılı"],
            ["Final eşitleme", "Bakım penceresinde yeniden import"],
          ]}
        />
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-950">Etiket paketleri</h2>
          <p className="mt-2 text-sm text-slate-500">Aktif fiyat ve stok bilgileri satın alma ekranında gösterilir.</p>
          <Link className="mt-4 inline-flex rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800" to="/customer/buy-labels">
            Etiket satın al
          </Link>
        </section>
      </section>
    </div>
  );
}

type CustomerOrdersDateRange = "1d" | "3d" | "7d" | "30d" | "3m" | "all";

const customerOrdersDefaultPageSize = 250;
const customerOrdersPageSizeOptions = [150, 250, 500, 1000, 1500, 2000];
const customerOrdersPageSizeStorageKey = "customer_orders_page_size";
const customerOrdersPageSizeCookieName = "customer_orders_page_size";
const customerOrdersViewRangeStorageKey = "customer_orders_view_date_range";
const customerOrdersViewRangeCookieName = "customer_orders_view_date_range";
const customerOrderSyncPlatforms = new Set<string>(["afterbuy", "amazon", "amazon_v2", "etsy", "shopify", "shopware", "billbee", "aboutyou", "otto", "woocommerce", "kaufland", "opencart", "plenty"]);

function normalizeCustomerOrdersDateRange(value: string | null): CustomerOrdersDateRange {
  return value === "1d" || value === "3d" || value === "7d" || value === "30d" || value === "3m" || value === "all" ? value : "3m";
}

function normalizeCustomerOrdersPageSize(value: unknown) {
  const parsed = Number(value || 0);
  return customerOrdersPageSizeOptions.includes(parsed) ? parsed : customerOrdersDefaultPageSize;
}

function customerOrdersPageSizeCookieValue() {
  if (typeof window === "undefined") return "";
  const cookie = window.document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${customerOrdersPageSizeCookieName}=`));
  if (!cookie) return "";
  try {
    return decodeURIComponent(cookie.slice(customerOrdersPageSizeCookieName.length + 1));
  } catch {
    return "";
  }
}

function readCustomerOrdersPageSizePreference() {
  if (typeof window === "undefined") return customerOrdersDefaultPageSize;
  const cookieValue = customerOrdersPageSizeCookieValue();
  const storedValue = cookieValue || window.localStorage.getItem(customerOrdersPageSizeStorageKey);
  return normalizeCustomerOrdersPageSize(storedValue);
}

function persistCustomerOrdersPageSizePreference(value: unknown) {
  if (typeof window === "undefined") return;
  const pageSize = normalizeCustomerOrdersPageSize(value);
  try {
    window.localStorage.setItem(customerOrdersPageSizeStorageKey, String(pageSize));
  } catch {
    // Preference storage is best-effort; the current page state still works.
  }
  window.document.cookie = `${customerOrdersPageSizeCookieName}=${encodeURIComponent(String(pageSize))}; path=/; max-age=31536000; SameSite=Lax`;
}

function customerOrdersViewRangeCookieValue() {
  if (typeof window === "undefined") return "";
  const cookie = window.document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${customerOrdersViewRangeCookieName}=`));
  if (!cookie) return "";
  try {
    return decodeURIComponent(cookie.slice(customerOrdersViewRangeCookieName.length + 1));
  } catch {
    return "";
  }
}

function readCustomerOrdersViewRangePreference(): CustomerOrdersViewRange {
  if (typeof window === "undefined") return "all";
  const cookieValue = customerOrdersViewRangeCookieValue();
  const storedValue = cookieValue || window.localStorage.getItem(customerOrdersViewRangeStorageKey);
  return normalizeCustomerOrdersViewRange(storedValue);
}

function persistCustomerOrdersViewRangePreference(value: CustomerOrdersViewRange) {
  if (typeof window === "undefined") return;
  const range = normalizeCustomerOrdersViewRange(value);
  try {
    window.localStorage.setItem(customerOrdersViewRangeStorageKey, range);
  } catch {
    // Preference storage is best-effort; the current page state still works.
  }
  window.document.cookie = `${customerOrdersViewRangeCookieName}=${encodeURIComponent(range)}; path=/; max-age=31536000; SameSite=Lax`;
}

function customerOrdersPaginationItems(currentPage: number, totalPages: number) {
  const safeTotal = Math.max(1, Math.floor(totalPages || 1));
  const safeCurrent = Math.min(Math.max(1, Math.floor(currentPage || 1)), safeTotal);
  if (safeTotal <= 7) {
    return Array.from({ length: safeTotal }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, safeTotal, safeCurrent - 1, safeCurrent, safeCurrent + 1]);
  if (safeCurrent <= 4) {
    [2, 3, 4, 5].forEach((pageNumber) => pages.add(pageNumber));
  }
  if (safeCurrent >= safeTotal - 3) {
    [safeTotal - 4, safeTotal - 3, safeTotal - 2, safeTotal - 1].forEach((pageNumber) => pages.add(pageNumber));
  }

  const sortedPages = Array.from(pages)
    .filter((pageNumber) => pageNumber >= 1 && pageNumber <= safeTotal)
    .sort((a, b) => a - b);
  const items: Array<number | "ellipsis"> = [];
  sortedPages.forEach((pageNumber, index) => {
    const previous = sortedPages[index - 1];
    if (previous && pageNumber - previous > 1) items.push("ellipsis");
    items.push(pageNumber);
  });
  return items;
}

function customerOrdersDateRangeLabel(range: CustomerOrdersDateRange) {
  if (range === "1d") return "son 1 gün";
  if (range === "3d") return "son 3 gün";
  if (range === "7d") return "son 7 gün";
  if (range === "30d") return "son 30 gün";
  if (range === "3m") return "son 3 ay";
  return "tüm dönem";
}

function customerStoreSyncRange(platform: string, range: CustomerOrdersDateRange) {
  if (range === "1d" && platform !== "afterbuy") return "3d";
  if (range !== "3m") return range;
  if (platform === "ebay" || platform === "shopify" || platform === "woocommerce" || platform === "plenty") return "3m";
  return "30d";
}

function labelProductSortBucket(product: LabelProductSortable) {
  const name = text(product.name).toLocaleLowerCase("tr-TR").replace(",", ".");
  const weight = labelProductSortWeight(product);
  const buckets = [
    { grams: null, rank: 0, pattern: /klein\s*-?\s*paket|kleinpaket/ },
    { grams: 1000, rank: 1, pattern: /\b1\s*(kg|kilo)\b/ },
    { grams: 2000, rank: 2, pattern: /\b2\s*(kg|kilo)\b/ },
    { grams: 5000, rank: 3, pattern: /\b5\s*(kg|kilo)\b/ },
    { grams: 10000, rank: 4, pattern: /\b10\s*(kg|kilo)\b/ },
    { grams: 20000, rank: 5, pattern: /\b20\s*(kg|kilo)\b/ },
    { grams: 31500, rank: 6, pattern: /\b31\.5\s*(kg|kilo)\b/ },
  ];
  const nameMatch = buckets.find((bucket) => bucket.pattern.test(name));
  if (nameMatch) return nameMatch.rank;
  const weightMatch = buckets.find((bucket) => bucket.grams !== null && Math.abs(weight - bucket.grams) <= 100);
  return weightMatch?.rank ?? 100;
}

function sortLabelProductsForOrders<T extends LabelProductSortable>(products: T[]): T[] {
  return [...products].sort((a, b) => {
    const bucketDiff = labelProductSortBucket(a) - labelProductSortBucket(b);
    if (bucketDiff) return bucketDiff;
    const weightDiff = labelProductSortWeight(a) - labelProductSortWeight(b);
    if (weightDiff) return weightDiff;
    return text(a.name).localeCompare(text(b.name), currentLocaleTag());
  });
}

function customerOrdersSyncMessage(storeName: string, result: Record<string, unknown>) {
  const explicitMessage = text(result.message);
  if (explicitMessage) return explicitMessage;
  const parts = [
    result.fetched !== undefined ? `${numberText(result.fetched)} alındı` : "",
    result.imported !== undefined ? `${numberText(result.imported)} içe aktarıldı` : "",
    result.inserted !== undefined ? `${numberText(result.inserted)} yeni` : "",
    result.updated !== undefined ? `${numberText(result.updated)} güncellendi` : "",
  ].filter(Boolean);
  return parts.length ? `${storeName} senkron tamamlandı: ${parts.join(", ")}.` : `${storeName} siparişleri senkronize edildi.`;
}

function orderWeightText(value: unknown) {
  const grams = Number(value || 0);
  if (!Number.isFinite(grams) || grams <= 0) return "-";
  if (grams < 1000) return `${numberText(grams)} g`;
  const kg = grams / 1000;
  return `${new Intl.NumberFormat(currentLocaleTag(), { maximumFractionDigits: kg < 10 ? 2 : 1 }).format(kg)} kg`;
}

function orderWeightRangeText(minValue: unknown, maxValue: unknown) {
  const min = Number(minValue || 0);
  const max = Number(maxValue || 0);
  if ((!Number.isFinite(min) || min <= 0) && (!Number.isFinite(max) || max <= 0)) return "-";
  if (!Number.isFinite(min) || min <= 0) return `${orderWeightText(max)}'a kadar`;
  if (!Number.isFinite(max) || max <= 0) return `${orderWeightText(min)}+`;
  return `${orderWeightText(min)} - ${orderWeightText(max)}`;
}

function orderItemsText(value: unknown) {
  if (value === null || value === undefined || value === "") return "-";
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return "-";
  return `${numberText(parsed)} ${parsed === 1 ? "item" : "items"}`;
}

function orderItemsPreviewText(value: unknown) {
  if (!Array.isArray(value)) return "";
  return value
    .map((item) => {
      if (!item || typeof item !== "object") return "";
      const record = item as Record<string, unknown>;
      const title = text(record.title);
      if (!title) return "";
      const quantity = Number(record.quantity || 0);
      const suffix = Number.isFinite(quantity) && quantity > 1 ? ` x${numberText(quantity)}` : "";
      return `${title}${suffix}`;
    })
    .filter(Boolean)
    .slice(0, 3)
    .join("; ");
}

function orderItemsPreviewDetails(value: unknown, currency: unknown) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      const title = text(record.title) || text(record.name) || text(record.sku) || `Ürün ${index + 1}`;
      const quantity = Number(record.quantity || 0);
      const sku = text(record.sku);
      const asin = text(record.asin);
      const grossTotal = record.grossTotal ?? record.totalGross ?? record.totalPrice ?? record.total;
      const unitGross = record.unitGross ?? record.unitPrice;
      const amountParts = [
        unitGross !== undefined && unitGross !== null && unitGross !== "" ? `Birim ${orderMoneyText(unitGross, currency)}` : "",
        grossTotal !== undefined && grossTotal !== null && grossTotal !== "" ? `Toplam ${orderMoneyText(grossTotal, currency)}` : "",
      ].filter(Boolean);
      return {
        amount: amountParts.join(" · "),
        key: `${title}-${sku || asin || index}`,
        meta: [sku ? `SKU: ${sku}` : "", asin ? `ASIN: ${asin}` : ""].filter(Boolean).join(" · "),
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 0,
        title,
      };
    })
    .filter((item): item is { amount: string; key: string; meta: string; quantity: number; title: string } => Boolean(item));
}

function orderItemsDetailTitle(value: unknown, currency: unknown) {
  return orderItemsPreviewDetails(value, currency)
    .map((item) => `${item.quantity > 0 ? `${numberText(item.quantity)} x ` : ""}${item.title}${item.meta ? ` (${item.meta})` : ""}${item.amount ? ` - ${item.amount}` : ""}`)
    .join("\n");
}

function OrderItemsSummary({
  className,
  currency,
  itemCount,
  itemsPreview,
  preview = true,
}: {
  className?: string;
  currency: unknown;
  itemCount: unknown;
  itemsPreview: unknown;
  preview?: boolean;
}) {
  const details = orderItemsPreviewDetails(itemsPreview, currency);
  const summary = orderItemsText(itemCount);
  const previewText = orderItemsPreviewText(itemsPreview);
  const detailTitle = orderItemsDetailTitle(itemsPreview, currency);

  if (details.length === 0) {
    return <span className={cx("font-semibold text-slate-900", className)}>{summary}</span>;
  }

  return (
    <div className={cx("group/order-items relative max-w-[260px] outline-none", className)} tabIndex={0} aria-label={`${summary} ürün detayları`}>
      <span
        className="inline-flex cursor-help border-b border-dotted border-slate-400 font-semibold text-slate-900 transition group-hover/order-items:border-slate-700 group-focus-within/order-items:border-slate-700"
        title={detailTitle || undefined}
      >
        {summary}
      </span>
      {preview && previewText ? <p className="mt-1 truncate text-xs text-slate-500">{previewText}</p> : null}
      <div className="pointer-events-none absolute left-0 top-full z-50 mt-2 hidden w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 bg-white p-3 text-left shadow-xl ring-1 ring-slate-900/5 group-hover/order-items:block group-focus-within/order-items:block">
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Ürün detayları</p>
          <span className="shrink-0 text-xs font-semibold text-slate-700">{summary}</span>
        </div>
        <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
          {details.map((item) => (
            <div key={item.key} className="rounded-lg bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
              <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 text-sm font-semibold leading-5 text-slate-950">{item.title}</p>
                {item.quantity > 0 ? <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">x{numberText(item.quantity)}</span> : null}
              </div>
              {item.meta ? <p className="mt-1 text-xs leading-5 text-slate-500">{item.meta}</p> : null}
              {item.amount ? <p className="mt-1 text-xs font-semibold leading-5 text-slate-600">{item.amount}</p> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function orderMoneyText(value: unknown, currency: unknown) {
  if (value === null || value === undefined || value === "") return "-";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return moneyText(parsed, text(currency) || "EUR");
}

function orderDateText(value: unknown) {
  const normalized = text(value);
  if (!normalized) return "-";
  const sourceDate = normalizeSourceOrderDate(normalized);
  const date = new Date(sourceDate || normalized);
  if (Number.isNaN(date.getTime())) return normalized;
  return new Intl.DateTimeFormat(currentLocaleTag(), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function orderStatusBadge(value: unknown) {
  const label = text(value);
  return label ? statusBadge(label) : <span className="text-sm text-slate-400">-</span>;
}

const nonShippableOrderPaymentStatusTokens = new Set([
  "cancelled",
  "cancelled_by_marketplace",
  "canceled",
  "canceled_by_marketplace",
  "chargeback",
  "failed",
  "fully_refund",
  "fully_refunded",
  "refund",
  "refunded",
  "void",
  "voided",
]);

function normalizeOrderPaymentStatusToken(value: unknown) {
  return text(value).toLowerCase().replace(/[\s-]+/g, "_");
}

function isOrderPaymentNonShippable(value: unknown) {
  const token = normalizeOrderPaymentStatusToken(value);
  if (!token) return false;
  if (token.includes("partial")) return false;
  if (nonShippableOrderPaymentStatusTokens.has(token)) return true;
  return (
    token.includes("fully_refund") ||
    token.includes("chargeback") ||
    token.includes("cancelled") ||
    token.includes("canceled") ||
    token === "payment_failed" ||
    token === "failed_payment"
  );
}

function orderPaymentStatusValue(row: DataRow) {
  return text(row.paymentStatus) || text(row.status);
}

function isOrderBlockedForShipment(row: DataRow) {
  return isOrderPaymentNonShippable(orderPaymentStatusValue(row));
}

function isDhlSpecialAddress(...parts: Array<unknown>) {
  const haystack = parts.map(text).join(" ").toLowerCase();
  return haystack.includes("packstation") || haystack.includes("postfiliale");
}

function isOrderDhlSpecialAddress(row: DataRow) {
  return isDhlSpecialAddress(row.recipientName, row.address1, row.address2);
}

function stringList(value: unknown) {
  return Array.isArray(value) ? value.map(text).filter(Boolean) : [];
}

function orderAddressWarnings(row: DataRow) {
  return stringList(row.addressWarnings);
}

function orderAddressAdjustments(row: DataRow) {
  return stringList(row.addressAdjustments);
}

function dhlAddressInputWarnings(address: Partial<OrderLabelDialogAddress>) {
  const recipientLines = text(address.recipientName)
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);
  const fields = [
    ["Sokak ve kapı numarası", text(address.address1)],
    ["Adres eki", text(address.address2)],
  ] as const;
  const warnings = fields
    .filter(([, value]) => value.replace(/\s+/g, " ").trim().length > 35)
    .map(([label]) => `${label} DHL'nin 35 karakter sınırını aşıyor. Etiket oluşturmadan önce kısaltın.`);
  const receiverLineCount =
    recipientLines.length +
    (text(address.address2) ? 1 : 0) +
    (text(address.address1) ? 1 : 0) +
    (text(address.postalCode) || text(address.city) ? 1 : 0) +
    1;
  if (receiverLineCount > 5) {
    warnings.push("Alıcı adresi DHL'nin 5 satır sınırını aşıyor. Adres ekini veya alıcı satırlarını kısaltın.");
  }
  return Array.from(new Set(warnings));
}

function dhlAddressInputAdjustments(address: Partial<OrderLabelDialogAddress>) {
  const recipientLines = text(address.recipientName)
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);
  const cityLine = [text(address.postalCode), text(address.city)].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  const adjustments: string[] = [];
  if (recipientLines.some((line) => line.length > 35)) {
    adjustments.push("Alıcı adı, baştaki bölümü korunarak DHL'nin 35 karakter sınırına otomatik kısaltılacak.");
  }
  if (cityLine.length > 35) {
    adjustments.push("Şehir adı, baştaki bölümü korunarak posta koduyla birlikte DHL'nin 35 karakter sınırına otomatik kısaltılacak.");
  }
  return adjustments;
}

function dhlSpecialAddressBlockReason() {
  return "Packstation/Postfiliale adresleri toplu etiket işleminde seçilemez. Tekli etiket oluşturmayı kullanın.";
}

function orderShipmentHardBlockReason(row: DataRow) {
  if (row.previewOnly === true) return "Amazon V2 test kaydından etiket oluşturulamaz.";
  if (row.sourcePlatform === "amazon_v2" && text(row.fulfillmentStatus) !== "Unshipped" && row.amazonTrackingAmendmentAvailable !== true) return "Bu Amazon siparişi henüz gönderime uygun değil.";
  if (isOrderBlockedForShipment(row)) return `Ödeme/iade durumu: ${orderPaymentStatusValue(row)}.`;
  if (isOrderDhlSpecialAddress(row)) return dhlSpecialAddressBlockReason();
  return "";
}

function orderShipmentBlockReason(row: DataRow) {
  const hardBlockReason = orderShipmentHardBlockReason(row);
  if (hardBlockReason) return hardBlockReason;
  const addressWarning = orderAddressWarnings(row)[0];
  if (row.addressHasBlockingWarning === true) return addressWarning || "Adres DHL etiket formatına uygun değil.";
  return "";
}

function canSelectOrderForLabel(row: DataRow) {
  return Boolean(text(row.id)) && !text(row.archivedAt) && rowNumber(row, "shipmentCount") <= 0 && !orderShipmentHardBlockReason(row);
}

function orderCustomerAddress(row: DataRow) {
  return compactAddress(row.address2, row.address1, row.postalCode, row.city, row.countryCode);
}

type OrderLabelDialogAddress = {
  address1: string;
  address2: string;
  city: string;
  countryCode: string;
  postalCode: string;
  recipientName: string;
};

type BulkOrderLabelDraft = OrderLabelDialogAddress & {
  addressSource: string;
  currency: unknown;
  customerName: string;
  itemCount: unknown;
  itemsPreview: unknown;
  labelProductId: string;
  orderId: string;
  orderNumber: string;
  storeName: string;
  weightGrams: unknown;
};

type BulkOrderLabelResultRow = Partial<CreateShipmentResponse> & {
  error?: string;
  labelProductId?: string;
  ok: boolean;
  orderId: string;
  recovered?: boolean;
  skipped?: boolean;
  skipReason?: string;
};

type BulkCreateShipmentApiResponse = {
  error?: string;
  ok: boolean;
  results: BulkOrderLabelResultRow[];
  summary: {
    failed: number;
    recovered?: number;
    skipped: number;
    success: number;
    total: number;
  };
};

const bulkCreateRequestChunkSize = 10;
const bulkCreateRequestMaxAttempts = 3;
const maxBulkLabelOrders = bulkOrderLabelPdfChunkSize;
const maxBulkArchiveOrders = 250;
const combinedPdfBrowserBatchSize = 50;
const combinedPdfFetchMaxAttempts = 5;

function waitForBulkRetry(milliseconds: number) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function shouldRetryBulkCreateError(error: unknown) {
  return !(error instanceof ApiError) || error.status === 408 || error.status === 429 || error.status >= 500;
}

function defaultOrderLabelAddress(row: DataRow): OrderLabelDialogAddress {
  return {
    address1: text(row.address1),
    address2: text(row.address2),
    city: text(row.city),
    countryCode: text(row.countryCode) || "DE",
    postalCode: text(row.postalCode),
    recipientName: text(row.recipientName),
  };
}

function labelDialogAddressReady(address: OrderLabelDialogAddress) {
  return Boolean(
    text(address.recipientName) &&
      text(address.address1) &&
      text(address.city) &&
      text(address.postalCode) &&
      (text(address.countryCode) || "DE").toUpperCase() === "DE"
  );
}

function bulkOrderLabelDraftError(row: BulkOrderLabelDraft) {
  if (!text(row.labelProductId)) return "Etiket ürünü seçilmeli.";
  if (!text(row.recipientName)) return "Alıcı adı eksik.";
  if (!text(row.address1)) return "Adres 1 eksik.";
  if (!text(row.city)) return "Şehir eksik.";
  if (!text(row.postalCode)) return "Posta kodu eksik.";
  if ((text(row.countryCode) || "DE").toUpperCase() !== "DE") return "Şimdilik sadece DE gönderileri destekleniyor.";
  if (isDhlSpecialAddress(row.recipientName, row.address1, row.address2)) return dhlSpecialAddressBlockReason();
  const formatWarning = dhlAddressInputWarnings(row)[0];
  if (formatWarning) return formatWarning;
  return null;
}

function orderLabelDialogTitle(row: DataRow | null) {
  if (!row) return "Sipariş etiketi";
  return text(row.orderNumber) || text(row.externalOrderId) || text(row.id) || "Sipariş etiketi";
}

function triggerShipmentLabelDownload(shipmentId: string) {
  const href = shipmentLabelHref(shipmentId, "normal", true);
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }
  const link = document.createElement("a");
  link.href = href;
  link.download = "";
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  window.setTimeout(() => link.remove(), 0);
}

function OrderRowActions({
  archivedView,
  archiveUpdatingOrderId,
  cancelingShipmentId,
  creatingOrderId,
  onArchiveOrder,
  onCancelShipment,
  onOpenCreateLabelDialog,
  onRestoreOrder,
  paymentBlocked,
  row,
}: {
  archivedView?: boolean;
  archiveUpdatingOrderId?: string | null;
  cancelingShipmentId?: string | null;
  creatingOrderId: string | null;
  onArchiveOrder?: (row: DataRow) => void;
  onCancelShipment?: (row: DataRow) => void;
  onOpenCreateLabelDialog: (row: DataRow) => void;
  onRestoreOrder?: (row: DataRow) => void;
  paymentBlocked?: boolean;
  row: DataRow;
}) {
  const orderId = text(row.id);
  if (row.previewOnly === true) return (
    <div className="mt-3 flex flex-wrap gap-2">
      <Link to={text(row.previewUrl)} className="inline-flex h-8 items-center rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-700">Amazon V2 bağlantısı</Link>
      <span className="inline-flex h-8 items-center px-2.5 text-[11px] text-slate-500">Test kaydı</span>
    </div>
  );
  const orderNumber = text(row.orderNumber) || text(row.externalOrderId) || orderId;
  const lastShipmentId = text(row.lastShipmentId);
  const paymentBlockLabel = orderPaymentStatusValue(row);
  const specialAddressBlocked = isOrderDhlSpecialAddress(row);
  const archiveBusy = archiveUpdatingOrderId === orderId;
  const cancelBusy = cancelingShipmentId === lastShipmentId;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      <Link
        to={`/customer/orders/${encodeURIComponent(orderId)}`}
        className="inline-flex h-8 items-center justify-center rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
        aria-label={`${orderNumber} sipariş detayını aç`}
      >
        Detay
      </Link>
      {lastShipmentId ? (
        <a
          href={shipmentLabelHref(lastShipmentId, "normal", true)}
          download
          className="inline-flex h-8 items-center justify-center rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          aria-label={`${orderNumber} etiketi indir`}
        >
          PDF
        </a>
      ) : null}
      {lastShipmentId && !archivedView ? (
        <button
          type="button"
          onClick={() => onCancelShipment?.(row)}
          disabled={cancelBusy}
          className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 text-[11px] font-semibold text-red-700 shadow-sm transition hover:bg-red-100 disabled:opacity-60"
          aria-label={`${orderNumber} gönderisini iptal et`}
          title="Sadece DHL hareketi olmayan / Status offen etiketler iptal edilebilir."
        >
          {cancelBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
          İptal
        </button>
      ) : archivedView ? null : (
        <button
          type="button"
          onClick={() => onOpenCreateLabelDialog(row)}
          disabled={creatingOrderId === orderId || paymentBlocked}
          className={cx(
            "inline-flex h-8 items-center justify-center gap-1.5 rounded-lg px-2.5 text-[11px] font-semibold shadow-sm transition disabled:opacity-60",
            paymentBlocked ? "border border-red-200 bg-red-50 text-red-700" : "bg-slate-950 text-white hover:bg-slate-800"
          )}
          aria-label={`${orderNumber} için etiket oluştur`}
          title={
            paymentBlocked
              ? `Bu sipariş için etiket oluşturulamaz: ${paymentBlockLabel}`
              : specialAddressBlocked
              ? "Özel DHL adresi; gerekirse adresi normal teslimat adresiyle değiştirerek deneyin."
              : undefined
          }
        >
          {paymentBlocked ? <AlertTriangle className="h-3.5 w-3.5" /> : creatingOrderId === orderId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          {paymentBlocked ? "Engelli" : "Etiket"}
        </button>
      )}
      {archivedView ? (
        <button
          type="button"
          onClick={() => onRestoreOrder?.(row)}
          disabled={archiveBusy}
          className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 text-[11px] font-semibold text-emerald-800 shadow-sm transition hover:bg-emerald-100 disabled:opacity-60"
          aria-label={`${orderNumber} siparişini arşivden geri al`}
        >
          {archiveBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
          Geri al
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onArchiveOrder?.(row)}
          disabled={archiveBusy}
          className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 disabled:opacity-60"
          aria-label={`${orderNumber} siparişini arşivle`}
        >
          {archiveBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
          Arşivle
        </button>
      )}
    </div>
  );
}

function OrdersPage({ user }: { user: SessionUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [initialSelection] = useState(() => initialCustomerOrderSelection(readCustomerOrderSelection(user.id), searchParams));
  const [selection, setSelection] = useState(initialSelection);
  const { platform, storeId, sourceStoreId } = selection;
  function setStoreId(value: string | ((current: string) => string)) {
    setSelection(current => ({ ...current, storeId: typeof value === "function" ? value(current.storeId) : value }));
  }
  function setSourceStoreId(value: string) { setSelection(current => ({ ...current, sourceStoreId: value })); }
  function setPlatform(value: string) { setSelection(current => ({ ...current, platform: value })); }
  const [rows, setRows] = useState<DataRow[]>([]);
  const [stores, setStores] = useState<StoreSummary[]>([]);
  const [products, setProducts] = useState<LabelProductSummary[]>([]);
  const [balances, setBalances] = useState<LabelBalanceSummary[]>([]);
  const [availableSourceStores, setAvailableSourceStores] = useState<DataRow[]>([]);
  const [meta, setMeta] = useState({ hasMore: false, limit: customerOrdersDefaultPageSize, offset: 0, returnedCount: 0, totalCount: 0 });
  const [summary, setSummary] = useState<DataRow>({});
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("open");
  const [labelState, setLabelState] = useState("waiting");
  const [syncRange, setSyncRange] = useState<CustomerOrdersDateRange>(() => normalizeCustomerOrdersDateRange(searchParams.get("dateRange")));
  const [onlyUnshipped, setOnlyUnshipped] = useState(true);
  const [showArchivedOrders, setShowArchivedOrders] = useState(false);
  const [pageSize, setPageSize] = useState(readCustomerOrdersPageSizePreference);
  const [viewDateRange, setViewDateRange] = useState<CustomerOrdersViewRange>(readCustomerOrdersViewRangePreference);
  const [page, setPage] = useState(0);
  const [selectedLabelProductId, setSelectedLabelProductId] = useState("");
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(true);
  const [supportLoading, setSupportLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkArchiving, setBulkArchiving] = useState(false);
  const [bulkArchivesOpen, setBulkArchivesOpen] = useState(false);
  const [bulkArchivesLoading, setBulkArchivesLoading] = useState(false);
  const [bulkArchives, setBulkArchives] = useState<BulkShipmentArchiveRecord[]>([]);
  const [bulkArchivesError, setBulkArchivesError] = useState<string | null>(null);
  const [lastBulkDownloadShipmentIds, setLastBulkDownloadShipmentIds] = useState<string[]>([]);
  const [bulkLabelDialogOpen, setBulkLabelDialogOpen] = useState(false);
  const [bulkLabelDrafts, setBulkLabelDrafts] = useState<BulkOrderLabelDraft[]>([]);
  const [bulkLabelResults, setBulkLabelResults] = useState<BulkOrderLabelResultRow[]>([]);
  const [bulkProgressOpen, setBulkProgressOpen] = useState(false);
  const [bulkProgressStage, setBulkProgressStage] = useState<"processing" | "combining" | "finalizing">("processing");
  const [bulkProgressCompleted, setBulkProgressCompleted] = useState(0);
  const [bulkProgressTotal, setBulkProgressTotal] = useState(0);
  const [creatingOrderId, setCreatingOrderId] = useState<string | null>(null);
  const [archiveUpdatingOrderId, setArchiveUpdatingOrderId] = useState<string | null>(null);
  const [cancelingShipmentId, setCancelingShipmentId] = useState<string | null>(null);
  const [labelDialogOrder, setLabelDialogOrder] = useState<DataRow | null>(null);
  const [labelDialogProductId, setLabelDialogProductId] = useState("");
  const [labelDialogPackageCount, setLabelDialogPackageCount] = useState(1);
  const [labelDialogAddress, setLabelDialogAddress] = useState<OrderLabelDialogAddress>(() => defaultOrderLabelAddress({}));
  const [reloadToken, setReloadToken] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ currentStore: "", failed: 0, finished: 0, skipped: 0, succeeded: 0, total: 0 });
  const [syncDetails, setSyncDetails] = useState<{ failures: string[]; skipped: string[] }>({ failures: [], skipped: [] });
  const ordersLoadedRef = useRef(false);
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  const activeStores = customerOrderSyncTargetStores(stores, "all");
  const selectableStores = customerOrderSelectableStores(stores);
  const selectedStore = selectableStores.find((store) => store.id === storeId) || null;
  const supportsOneDaySync = selectedStore?.platform === "afterbuy" || selectedStore?.platform === "plenty";
  const aggregatorSourceStores = useMemo(
    () =>
      availableSourceStores
        .map((store) => ({
          count: Number(store.count || 0),
          id: text(store.id),
          name: text(store.name) || text(store.id),
          salesChannel: text(store.salesChannel),
        }))
        .filter((store) => store.id && store.name),
    [availableSourceStores]
  );
  const isAggregatorStore = selectedStore?.platform === "billbee" || selectedStore?.platform === "plenty";
  const showAggregatorSourceStoreFilter = isAggregatorStore && aggregatorSourceStores.length > 0;
  const sortedProducts = useMemo(() => sortLabelProductsForOrders(products), [products]);
  const selectedProduct = sortedProducts.find((product) => product.id === selectedLabelProductId) || null;
  const selectedBalance = balances.find((balance) => balance.labelProductId === selectedLabelProductId) || null;
  const selectedStoreAutoWeightEnabled = selectedStore?.platform === "jtl" || selectedStore?.autoLabelProductByWeightEnabled === true;
  const totalCredits = balances.reduce((sum, balance) => sum + Number(balance.quantityAvailable || 0), 0);
  const selectedStoreLastSync = selectedStore?.ordersSyncedAt || selectedStore?.orderSyncLastSuccessAt || null;
  const latestStoreSyncAt =
    activeStores
      .map((store) => store.ordersSyncedAt || store.orderSyncLastSuccessAt || "")
      .filter(Boolean)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || null;
  const selectedStoreOrdersCount = selectedStore?.platform === "amazon_v2" && summary.previewOnly === true
    ? Number(summary.total || 0)
    : selectedStore ? Number(selectedStore.ordersCount || 0) : activeStores.reduce((sum, store) => sum + Number(store.ordersCount || 0), 0);
  const selectedProductWeightText =
    selectedStoreAutoWeightEnabled
      ? selectedStore?.platform === "jtl"
        ? "JTL ağırlığına göre otomatik"
        : "Sipariş ağırlığına göre otomatik"
      : (selectedProduct || selectedBalance)
      ? orderWeightRangeText(selectedProduct?.weightMinGrams ?? selectedBalance?.weightMinGrams, selectedProduct?.weightMaxGrams ?? selectedBalance?.weightMaxGrams)
      : "Ürün seçin";
  const selectedProductLabel = selectedProduct?.name || selectedBalance?.labelProductName || "Etiket ürünü seçili değil";
  const syncProgressPercent = syncProgress.total > 0 ? Math.round((syncProgress.finished / syncProgress.total) * 100) : 0;
  const labelDialogSelectedProduct = sortedProducts.find((product) => product.id === labelDialogProductId) || null;
  const labelDialogAvailableCredits = Number(balances.find((balance) => balance.labelProductId === labelDialogProductId)?.quantityAvailable || 0);
  const labelDialogOrderWeightText = orderWeightText(labelDialogOrder?.weightGrams);
  const labelDialogMissingSourceWeight =
    Number(labelDialogOrder?.weightGrams || 0) <= 0 &&
    (text(labelDialogOrder?.sourcePlatform).toLowerCase() === "jtl" || selectedStoreAutoWeightEnabled);
  const labelDialogAddressSource = labelDialogOrder ? orderAddressSourceLabel(labelDialogOrder.selectedAddressSource) : "Kargo adresi";
  const labelDialogAddressFormatWarnings = dhlAddressInputWarnings(labelDialogAddress);
  const labelDialogAddressFormatAdjustments = dhlAddressInputAdjustments(labelDialogAddress);
  const labelDialogCanSubmit =
    Boolean(labelDialogOrder) &&
    Boolean(labelDialogProductId) &&
    labelDialogAddressReady(labelDialogAddress) &&
    labelDialogAddressFormatWarnings.length === 0 &&
    labelDialogAvailableCredits >= labelDialogPackageCount &&
    creatingOrderId !== text(labelDialogOrder?.id);
  const pendingBulkLabelDrafts = useMemo(
    () => pendingBulkOrderLabelDrafts(bulkLabelDrafts, bulkLabelResults),
    [bulkLabelDrafts, bulkLabelResults]
  );
  const bulkLabelCreditSummary = useMemo(() => {
    const requestedByProduct = pendingBulkLabelDrafts.reduce<Record<string, number>>((acc, row) => {
      const productId = text(row.labelProductId);
      if (!productId) return acc;
      acc[productId] = (acc[productId] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(requestedByProduct).map(([productId, requested]) => {
      const product = sortedProducts.find((entry) => entry.id === productId);
      const balance = balances.find((entry) => entry.labelProductId === productId);
      const available = Number(balance?.quantityAvailable || 0);
      return {
        available,
        insufficient: requested > available,
        labelProductId: productId,
        labelProductName: product?.name || balance?.labelProductName || productId,
        requested,
      };
    });
  }, [balances, pendingBulkLabelDrafts, sortedProducts]);
  const bulkLabelHasValidationErrors = pendingBulkLabelDrafts.some((row) => Boolean(bulkOrderLabelDraftError(row)));
  const bulkLabelHasInsufficientCredits = bulkLabelCreditSummary.some((entry) => entry.insufficient);
  const bulkLabelResultSummary = useMemo(() => {
    const success = bulkLabelResults.filter((row) => row.ok && !row.skipped).length;
    const skipped = bulkLabelResults.filter((row) => row.skipped).length;
    const failed = bulkLabelResults.filter((row) => !row.ok).length;
    return { failed, skipped, success, total: bulkLabelResults.length };
  }, [bulkLabelResults]);
  const totalPages = Math.max(1, Math.ceil(Number(meta.totalCount || 0) / pageSize));
  const currentPageNumber = Math.min(page + 1, totalPages);
  const paginationItems = useMemo(() => customerOrdersPaginationItems(currentPageNumber, totalPages), [currentPageNumber, totalPages]);

  useEffect(() => {
    setPage(0);
  }, [labelState, onlyUnshipped, pageSize, platform, search, showArchivedOrders, sourceStoreId, status, storeId, viewDateRange]);

  useEffect(() => {
    if (selectedStore?.platform !== "amazon_v2") return;
    // Operational stores open on orders awaiting labels; trials show history.
    const dispatchView = selectedStore.amazonV2Mode === "active" || selectedStore.amazonV2Mode === "pilot";
    setPlatform("all");
    setSourceStoreId("all");
    setSearch("");
    setStatus(dispatchView ? "open" : "all");
    setLabelState(dispatchView ? "waiting" : "all");
    setOnlyUnshipped(dispatchView);
    setShowArchivedOrders(false);
    setViewDateRange("all");
    setSelectedOrderIds(new Set());
  }, [selectedStore?.id, selectedStore?.platform, selectedStore?.amazonV2Mode]);

  useEffect(() => {
    if (supportLoading) return;
    if (!isAggregatorStore && sourceStoreId !== "all") {
      setSourceStoreId("all");
    }
  }, [isAggregatorStore, sourceStoreId, supportLoading]);

  useEffect(() => {
    if (!showAggregatorSourceStoreFilter || sourceStoreId === "all") return;
    if (!aggregatorSourceStores.some((store) => store.id === sourceStoreId)) {
      setSourceStoreId("all");
    }
  }, [aggregatorSourceStores, showAggregatorSourceStoreFilter, sourceStoreId]);

  useEffect(() => {
    if (showArchivedOrders) setSelectedOrderIds(new Set());
  }, [showArchivedOrders]);

  useEffect(() => {
    persistCustomerOrdersPageSizePreference(pageSize);
  }, [pageSize]);

  useEffect(() => {
    persistCustomerOrdersViewRangePreference(viewDateRange);
  }, [viewDateRange]);

  useEffect(() => {
    if (supportLoading) return;
    persistCustomerOrderSelection(user.id, { storeId, sourceStoreId, platform });
  }, [user.id, storeId, sourceStoreId, platform, supportLoading]);

  const queryKey = useMemo(() => {
    const params = new URLSearchParams({ limit: String(pageSize), offset: String(page * pageSize) });
    const normalizedSearch = search.trim();
    const searchMode = Boolean(normalizedSearch);
    const effectiveOnlyUnshipped = showArchivedOrders ? false : onlyUnshipped;
    const effectiveStatus = showArchivedOrders ? "all" : searchMode && !effectiveOnlyUnshipped ? "all" : effectiveOnlyUnshipped ? "open" : status;
    const effectiveLabelState = showArchivedOrders ? "all" : searchMode && !effectiveOnlyUnshipped ? "all" : effectiveOnlyUnshipped ? "waiting" : labelState;

    if (normalizedSearch) params.set("search", normalizedSearch);
    if (effectiveOnlyUnshipped) params.set("onlyUnshipped", "1");
    if (effectiveStatus !== "all") params.set("status", effectiveStatus);
    if (platform !== "all") params.set("platform", platform);
    if (storeId !== "all") params.set("storeId", storeId);
    if (isAggregatorStore && sourceStoreId !== "all") params.set("sourceStoreId", sourceStoreId);
    if (effectiveLabelState !== "all") params.set("labelState", effectiveLabelState);
    const dateFrom = customerOrdersViewDateFrom(viewDateRange);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (showArchivedOrders) params.set("archived", "1");
    return params.toString();
  }, [isAggregatorStore, labelState, onlyUnshipped, page, pageSize, platform, search, showArchivedOrders, sourceStoreId, status, storeId, viewDateRange]);

  async function loadOrderSupportData() {
    setSupportLoading(true);
    try {
      const [storesResult, productsResult, balancesResult] = await Promise.all([
        apiClient.stores({ includeInactive: true }),
        apiClient.labelProducts(),
        apiClient.balances(),
      ]);
      const nextStores = storesResult.stores || [];
      const nextProducts = productsResult.products || [];
      const nextBalances = balancesResult.balances || [];
      const sortedNextProducts = sortLabelProductsForOrders(nextProducts);
      const selectableStores = customerOrderSelectableStores(nextStores);
      setStores(nextStores);
      setProducts(nextProducts);
      setBalances(nextBalances);
      setSelection(current => resolveCustomerOrderSelection(current, selectableStores));
      setSelectedLabelProductId((current) => {
        if (current && sortedNextProducts.some((product) => product.id === current)) return current;
        const productWithCredit = sortedNextProducts.find((product) =>
          nextBalances.some((balance) => balance.labelProductId === product.id && Number(balance.quantityAvailable || 0) > 0)
        );
        return productWithCredit?.id || sortedNextProducts[0]?.id || "";
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sipariş destek verileri alınamadı.");
    } finally {
      setSupportLoading(false);
    }
  }

  useEffect(() => {
    void loadOrderSupportData();
  }, []);

  useEffect(() => {
    const refreshOrders = () => setReloadToken((value) => value + 1);
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshOrders();
    };
    const interval = window.setInterval(refreshOrders, 30_000);
    window.addEventListener("focus", refreshOrders);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshOrders);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    let active = true;
    const handle = window.setTimeout(() => {
      if (!ordersLoadedRef.current) setLoading(true);
      setError(null);
      apiClient
        .resource<{ availableSourceStores?: DataRow[]; meta?: DataRow; orders?: DataRow[]; summary?: DataRow }>(`/api/orders?${queryKey}`)
        .then((result) => {
          if (!active) return;
          setRows(Array.isArray(result.orders) ? result.orders : []);
          setAvailableSourceStores(Array.isArray(result.availableSourceStores) ? result.availableSourceStores : []);
          setMeta({
            hasMore: result.meta?.hasMore === true,
            limit: Number(result.meta?.limit || pageSize),
            offset: Number(result.meta?.offset || page * pageSize),
            returnedCount: Number(result.meta?.returnedCount || 0),
            totalCount: Number(result.meta?.totalCount || 0),
          });
          setSummary(result.summary || {});
        })
        .catch((err) => {
          if (active) setError(err instanceof ApiError ? err.message : "Siparişler alınamadı.");
        })
        .finally(() => {
          if (active) {
            ordersLoadedRef.current = true;
            setLoading(false);
          }
        });
    }, 180);
    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [page, pageSize, queryKey, reloadToken]);

  useEffect(() => {
    if (!loading && meta.totalCount > 0 && page > 0 && page >= totalPages) {
      setPage(totalPages - 1);
    }
  }, [loading, meta.totalCount, page, totalPages]);

  const exportParams = new URLSearchParams(queryKey);
  exportParams.set("export", "csv");

  function toggleOrderSelection(row: DataRow) {
    const orderId = text(row.id);
    if (!orderId || row.previewOnly === true) return;
    if (!selectedOrderIds.has(orderId) && selectedOrderIds.size >= maxBulkArchiveOrders) {
      setError(`Tek toplu arşiv işleminde en fazla ${numberText(maxBulkArchiveOrders)} sipariş seçilebilir.`);
      return;
    }
    setSelectedOrderIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else if (next.size < maxBulkArchiveOrders) {
        next.add(orderId);
      }
      return next;
    });
  }

  function toggleCurrentPageSelection() {
    const currentIds = rows.filter((row) => row.previewOnly !== true).map((row) => text(row.id)).filter(Boolean);
    const allSelected = currentIds.length > 0 && currentIds.every((id) => selectedOrderIds.has(id));
    const newlySelectedCount = currentIds.filter((id) => !selectedOrderIds.has(id)).length;
    if (!allSelected && selectedOrderIds.size + newlySelectedCount > maxBulkArchiveOrders) {
      setNotice(`Güvenli işlem sınırı nedeniyle en fazla ${numberText(maxBulkArchiveOrders)} sipariş seçildi.`);
    }
    setSelectedOrderIds((current) => {
      const next = new Set(current);
      if (allSelected) {
        currentIds.forEach((id) => next.delete(id));
      } else {
        currentIds.forEach((id) => {
          if (next.size < maxBulkArchiveOrders) next.add(id);
        });
      }
      return next;
    });
  }

  async function refreshOrdersView() {
    await loadOrderSupportData();
    setReloadToken((current) => current + 1);
  }

  async function archiveOrder(row: DataRow) {
    const orderId = text(row.id);
    if (!orderId) return;
    const orderLabel = text(row.orderNumber) || text(row.externalOrderId) || orderId;
    const confirmed = await askConfirmation({
      title: "Sipariş arşivlensin mi?",
      description: `${orderLabel} siparişi ana listeden kaldırılacak.`,
      details: ["Arşivlenmiş siparişler görünümünden tekrar geri alınabilir."],
      confirmLabel: "Arşivle",
      tone: "warning",
    });
    if (!confirmed) return;

    setArchiveUpdatingOrderId(orderId);
    setError(null);
    setNotice(null);
    try {
      await apiClient.archiveOrder(orderId);
      setSelectedOrderIds((current) => {
        const next = new Set(current);
        next.delete(orderId);
        return next;
      });
      setNotice(`${orderLabel} arşivlendi. Arşivlenmiş siparişler görünümünden geri alınabilir.`);
      setReloadToken((current) => current + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sipariş arşivlenemedi.");
    } finally {
      setArchiveUpdatingOrderId(null);
    }
  }

  async function bulkArchiveSelectedOrders() {
    if (bulkArchiving || showArchivedOrders) return;
    const orderIds = Array.from(selectedOrderIds);
    if (orderIds.length === 0) {
      setError("Arşivlenecek siparişleri seçin.");
      return;
    }
    const confirmed = await askConfirmation({
      title: "Seçili siparişler arşivlensin mi?",
      description: `${numberText(orderIds.length)} sipariş ana listeden kaldırılacak.`,
      details: ["Arşivlenmiş siparişler görünümünden tek tek geri alınabilir."],
      confirmLabel: "Seçilenleri arşivle",
      tone: "warning",
    });
    if (!confirmed) return;

    setBulkArchiving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.bulkArchiveOrders(orderIds);
      setSelectedOrderIds(new Set());
      setNotice(`${numberText(result.archived)} sipariş arşivlendi.${result.skipped > 0 ? ` ${numberText(result.skipped)} sipariş zaten arşivdeydi veya erişilebilir değildi.` : ""}`);
      setReloadToken((current) => current + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Seçili siparişler arşivlenemedi.");
    } finally {
      setBulkArchiving(false);
    }
  }

  async function restoreOrder(row: DataRow) {
    const orderId = text(row.id);
    if (!orderId) return;
    const orderLabel = text(row.orderNumber) || text(row.externalOrderId) || orderId;

    setArchiveUpdatingOrderId(orderId);
    setError(null);
    setNotice(null);
    try {
      await apiClient.restoreOrder(orderId);
      setNotice(`${orderLabel} aktif sipariş listesine geri alındı.`);
      setReloadToken((current) => current + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sipariş arşivden geri alınamadı.");
    } finally {
      setArchiveUpdatingOrderId(null);
    }
  }

  async function cancelOrderShipment(row: DataRow) {
    const shipmentId = text(row.lastShipmentId);
    if (!shipmentId) return;
    const orderId = text(row.id);
    const orderLabel = text(row.orderNumber) || text(row.externalOrderId) || orderId || "Bu sipariş";
    const trackingLabel = text(row.trackingNumber) || shipmentId;
    const confirmed = await askConfirmation({
      title: "Etiket/gönderi iptal edilsin mi?",
      description: `${orderLabel} için oluşturulan ${trackingLabel} etiketi iptal edilecek.`,
      details: [
        "Sadece DHL hareketi olmayan / Status offen etiketler iptal edilebilir.",
        "İptal başarılı olursa etiket kredisi müşteri bakiyesine geri döner ve sipariş tekrar etiket bekleyen duruma gelir.",
      ],
      confirmLabel: "Gönderiyi iptal et",
      tone: "danger",
    });
    if (!confirmed) return;

    setCancelingShipmentId(shipmentId);
    setError(null);
    setNotice(null);
    try {
      await apiClient.cancelShipment(shipmentId);
      setSelectedOrderIds((current) => {
        const next = new Set(current);
        if (orderId) next.delete(orderId);
        return next;
      });
      setNotice("Etiket iptal edildi; kredi bakiyenize geri döndü ve sipariş tekrar etiket bekliyor.");
      await refreshOrdersView();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Gönderi iptal edilemedi.");
    } finally {
      setCancelingShipmentId(null);
    }
  }

  async function syncCustomerStoreOrders(storeForSync: StoreSummary, range: CustomerOrdersDateRange) {
    if (storeForSync.platform === "amazon") return {result:{ok:true,message:"Amazon V1 artık yeni sipariş almıyor. Amazon V2’ye bağlanın."},skipped:true};
    if (storeForSync.platform === "manual") {
      return { result: { ok: true, message: `${storeForSync.name} manuel mağaza olduğu için senkron atlandı.` }, skipped: true };
    }

    const supportedForOrderSync =
      storeForSync.platform === "ebay" ||
      storeForSync.platform === "jtl" ||
      customerOrderSyncPlatforms.has(storeForSync.platform);
    if (supportedForOrderSync && storeForSync.syncReady === false) {
      return {
        result: { ok: true, message: `${storeForSync.name} bağlantı bilgisi tamamlanmadığı için senkron atlandı.` },
        skipped: true,
      };
    }

    if (storeForSync.platform === "ebay") {
      return { result: await apiClient.syncEbay(storeForSync.id, customerStoreSyncRange(storeForSync.platform, range)), skipped: false };
    }
    if (storeForSync.platform === "jtl") {
      return { result: await apiClient.syncJtl(storeForSync.id, range === "all" ? "all" : range === "3d" ? "3d" : range === "7d" ? "7d" : "30d"), skipped: false };
    }
    if (customerOrderSyncPlatforms.has(storeForSync.platform)) {
      return {
        result: await apiClient.syncStoreOrders(
          storeForSync.id,
          storeForSync.platform as OrderSyncPlatform,
          customerStoreSyncRange(storeForSync.platform, range)
        ),
        skipped: false,
      };
    }
    throw new Error(`${platformLabel(storeForSync.platform)} için otomatik senkron hazır değil.`);
  }

  async function syncSelectedStore() {
    if (selectedStore?.platform === "amazon") {
      navigate("/customer/stores/amazon-v2/new");
      return;
    }
    const targetStores = customerOrderSyncTargetStores(stores, storeId);
    if (targetStores.length === 0) {
      setError("Senkron için aktif mağaza bulunamadı.");
      return;
    }

    const syncAllMode = !selectedStore && targetStores.length > 1;
    setSyncing(true);
    setError(null);
    setNotice(null);
    setSyncProgress({ currentStore: syncAllMode ? "Tüm mağazalar" : targetStores[0].name, failed: 0, finished: 0, skipped: 0, succeeded: 0, total: targetStores.length });
    setSyncDetails({ failures: [], skipped: [] });
    if (syncAllMode) setSyncDialogOpen(true);
    setSyncStatus(
      syncAllMode
        ? `Tüm mağazalar için ${customerOrdersDateRangeLabel(syncRange)} senkronu başlatıldı. Pencere kapatılsa da işlem bu ekranda arka planda devam eder.`
        : `${targetStores[0].name} için ${customerOrdersDateRangeLabel(syncRange)} sipariş senkronu başlatıldı. Sonuç geldiğinde liste otomatik yenilenecek.`
    );
    try {
      let failed = 0;
      let skipped = 0;
      let succeeded = 0;
      const failures: string[] = [];
      const skippedMessages: string[] = [];
      const messages: string[] = [];

      for (const storeForSync of targetStores) {
        setSyncProgress((current) => ({ ...current, currentStore: storeForSync.name }));
        try {
          const outcome = await syncCustomerStoreOrders(storeForSync, syncRange);
          const result = outcome.result as Record<string, unknown>;
          if (result.ok === false) throw new Error(text(result.message) || "Senkron tamamlanamadı.");
          if (outcome.skipped) {
            skipped += 1;
            skippedMessages.push(customerOrdersSyncMessage(storeForSync.name, result));
          } else {
            succeeded += 1;
          }
          messages.push(customerOrdersSyncMessage(storeForSync.name, result));
        } catch (err) {
          failed += 1;
          const message = storeForSync.platform === "amazon_v2" ? amazonV2ErrorMessage(err) : err instanceof Error ? err.message : "Senkron başlatılamadı.";
          failures.push(`${storeForSync.name}: ${message}`);
        } finally {
          setSyncDetails({ failures: failures.slice(0, 3), skipped: skippedMessages.slice(0, 3) });
          setSyncProgress({
            currentStore: storeForSync.name,
            failed,
            finished: failed + skipped + succeeded,
            skipped,
            succeeded,
            total: targetStores.length,
          });
        }
      }

      const message =
        targetStores.length === 1
          ? failed > 0 && succeeded === 0 && skipped === 0
            ? failures[0] || `${targetStores[0].name} senkronizasyonu hata verdi.`
            : messages[0] || `${targetStores[0].name} siparişleri senkronize edildi.`
          : `Senkron tamamlandı: ${numberText(succeeded)} mağaza başarılı, ${numberText(skipped)} atlandı, ${numberText(failed)} hata.`;
      const onlyFailed = failed > 0 && succeeded === 0 && skipped === 0;
      setNotice(onlyFailed ? null : message);
      setSyncStatus(onlyFailed ? null : message);
      if (failed > 0) setError(failures.slice(0, 2).join(" · "));
      await refreshOrdersView();
    } catch (err) {
      setSyncStatus(null);
      setError(err instanceof Error ? err.message : "Senkron başlatılamadı.");
    } finally {
      setSyncing(false);
    }
  }

  function availableCreditsForProduct(productId: string, sourceBalances: LabelBalanceSummary[] = balances) {
    return Number(sourceBalances.find((balance) => balance.labelProductId === productId)?.quantityAvailable || 0);
  }

  function firstAvailableLabelProductId(sourceBalances: LabelBalanceSummary[] = balances) {
    return sortedProducts.find((product) => availableCreditsForProduct(product.id, sourceBalances) > 0)?.id || sortedProducts[0]?.id || "";
  }

  function resolveWeightBasedLabelProductId(weightGrams: unknown, sourceBalances: LabelBalanceSummary[] = balances) {
    const grams = Number(weightGrams || 0);
    if (!Number.isFinite(grams) || grams <= 0) return "";
    const candidates = sortedProducts
      .filter((product) => {
        const productId = text(product.id);
        const carrier = text(product.carrier).toLowerCase();
        const min = Number(product.weightMinGrams || 0);
        const max = Number(product.weightMaxGrams || 0);
        return (
          productId &&
          carrier === "dhl" &&
          max > 0 &&
          grams >= min &&
          grams <= max &&
          availableCreditsForProduct(productId, sourceBalances) > 0
        );
      })
      .sort((a, b) => {
        const maxDiff = Number(a.weightMaxGrams || 0) - Number(b.weightMaxGrams || 0);
        if (maxDiff) return maxDiff;
        return Number(a.weightMinGrams || 0) - Number(b.weightMinGrams || 0);
      });
    return candidates[0]?.id || "";
  }

  function orderUsesWeightBasedBulkProduct(row: DataRow) {
    const grams = Number(row.weightGrams || 0);
    if (Number.isFinite(grams) && grams > 0) return true;
    const platform = text(row.sourcePlatform || selectedStore?.platform).toLowerCase();
    return platform === "jtl" || selectedStore?.autoLabelProductByWeightEnabled === true;
  }

  function resolveBulkLabelProductId(row: DataRow, sourceBalances: LabelBalanceSummary[] = balances) {
    if (orderUsesWeightBasedBulkProduct(row)) {
      return resolveWeightBasedLabelProductId(row.weightGrams, sourceBalances);
    }
    if (selectedLabelProductId && sortedProducts.some((product) => product.id === selectedLabelProductId)) {
      return selectedLabelProductId;
    }
    return firstAvailableLabelProductId(sourceBalances);
  }

  function suggestedLabelProductIdForOrder(row: DataRow, sourceBalances: LabelBalanceSummary[] = balances) {
    const weightMatch = resolveWeightBasedLabelProductId(row.weightGrams, sourceBalances);
    if (weightMatch) return weightMatch;
    if (text(row.sourcePlatform).toLowerCase() === "jtl" || selectedStore?.autoLabelProductByWeightEnabled === true) return "";
    if (selectedLabelProductId && sortedProducts.some((product) => product.id === selectedLabelProductId)) {
      return selectedLabelProductId;
    }
    return firstAvailableLabelProductId(sourceBalances);
  }

  function openCreateLabelDialog(row: DataRow) {
    if (isOrderBlockedForShipment(row)) {
      setError(`Bu sipariş için etiket oluşturulamaz. Ödeme/iade durumu: ${orderPaymentStatusValue(row)}.`);
      return;
    }
    const productId = suggestedLabelProductIdForOrder(row);
    setLabelDialogOrder(row);
    setLabelDialogProductId(productId);
    setLabelDialogPackageCount(1);
    setLabelDialogAddress(defaultOrderLabelAddress(row));
    setError(null);
    const addressNotices = [
      ...(isOrderDhlSpecialAddress(row)
        ? ["Bu sipariş Packstation/Postfiliale adresli. Toplu işlemde seçilmez; tekli etiket oluşturabilirsiniz. Packstation için Postnummer bilgisi adres içinde olmalı."]
        : []),
      ...orderAddressAdjustments(row),
      ...orderAddressWarnings(row),
    ];
    setNotice(addressNotices.length > 0 ? addressNotices.join(" ") : null);
  }

  function closeLabelDialog() {
    if (creatingOrderId) return;
    setLabelDialogOrder(null);
    setLabelDialogProductId("");
    setLabelDialogPackageCount(1);
    setLabelDialogAddress(defaultOrderLabelAddress({}));
  }

  function updateLabelDialogAddress(field: keyof OrderLabelDialogAddress, value: string) {
    setLabelDialogAddress((current) => ({
      ...current,
      [field]: field === "countryCode" ? value.toUpperCase() : value,
    }));
  }

  async function createLabelForOrder(
    row: DataRow,
    options?: {
      addressOverride?: OrderLabelDialogAddress;
      labelProductId?: string;
      packageCount?: number;
    }
  ) {
    const orderId = text(row.id);
    const labelProductId = text(options?.labelProductId) || selectedLabelProductId;
    const packageCount = Math.max(1, Math.min(50, Math.trunc(Number(options?.packageCount || 1))));
    if (!orderId || !labelProductId) {
      setError("Etiket oluşturmak için sipariş ve etiket ürünü gerekli.");
      return false;
    }
    if (isOrderBlockedForShipment(row)) {
      setError(`Bu sipariş için etiket oluşturulamaz. Ödeme/iade durumu: ${orderPaymentStatusValue(row)}.`);
      return false;
    }
    setCreatingOrderId(orderId);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.createShipmentForOrder({
        addressOverride: options?.addressOverride,
        confirmed: true,
        labelProductId,
        orderId,
        packageCount,
        shipmentType: "outbound",
        storeId: selectedStore?.id,
      });
      const createdShipments = (result.shipments?.length ? result.shipments : [result]).filter((shipment) => shipment.ok && shipment.shipmentId);
      if (createdShipments.length === 0) throw new Error(result.error || "Etiket oluşturulamadı.");
      setSelectedLabelProductId(labelProductId);
      const shipmentIds = createdShipments.map((shipment) => shipment.shipmentId);
      const trackingNumbers = createdShipments.map((shipment) => shipment.trackingNumber).filter(Boolean);
      let downloadWarning = "";
      try {
        if (shipmentIds.length > 1) {
          await triggerCombinedShipmentPdfDownload(shipmentIds);
        } else {
          triggerShipmentLabelDownload(shipmentIds[0]);
        }
      } catch (downloadError) {
        downloadWarning = ` PDF indirme başlatılamadı: ${downloadError instanceof Error ? downloadError.message : "Gönderiler ekranından tekrar indirebilirsiniz."}`;
      }
      const creationSummary =
        createdShipments.length > 1
          ? `${numberText(createdShipments.length)} koli etiketi, birbirinden farklı ${numberText(trackingNumbers.length)} takip numarasıyla oluşturuldu.`
          : `Etiket oluşturuldu${trackingNumbers[0] ? `, takip no: ${trackingNumbers[0]}` : ""}.`;
      const partialWarning = result.ok
        ? ""
        : ` İstenen ${numberText(result.requestedCount || packageCount)} etiketin ${numberText(createdShipments.length)} tanesi tamamlandı${result.error ? `: ${result.error}` : "."}`;
      setNotice(`${creationSummary}${result.orderArchived ? " Sipariş otomatik arşivlendi." : ""}${partialWarning}${downloadWarning}`);
      await refreshOrdersView();
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Etiket oluşturulamadı.");
      return false;
    } finally {
      setCreatingOrderId(null);
    }
  }

  async function submitLabelDialog() {
    if (!labelDialogOrder) return;
    if (!labelDialogCanSubmit) {
      setError("Etiket oluşturmadan önce ürün, kredi ve kargo adresini kontrol edin.");
      return;
    }
    const ok = await createLabelForOrder(labelDialogOrder, {
      addressOverride: {
        ...labelDialogAddress,
        countryCode: (text(labelDialogAddress.countryCode) || "DE").toUpperCase(),
      },
      labelProductId: labelDialogProductId,
      packageCount: labelDialogPackageCount,
    });
    if (ok) closeLabelDialog();
  }

  function shipmentIdsFromBulkCreateResult(result: { results?: Array<Partial<CreateShipmentResponse> & { ok?: boolean; shipmentId?: string }> }) {
    return Array.from(
      new Set(
        (result.results || [])
          .filter((row) => row.ok && text(row.shipmentId))
          .map((row) => text(row.shipmentId))
      )
    );
  }

  async function loadBulkArchives() {
    setBulkArchivesLoading(true);
    setBulkArchivesError(null);
    try {
      const result = await apiClient.resource<{ archives?: BulkShipmentArchiveRecord[] }>("/api/shipments/bulk-archives?source=orders&limit=25");
      setBulkArchives(Array.isArray(result?.archives) ? result.archives : []);
    } catch (err) {
      setBulkArchives([]);
      setBulkArchivesError(err instanceof ApiError ? err.message : "Toplu etiket geçmişi alınamadı.");
    } finally {
      setBulkArchivesLoading(false);
    }
  }

  async function openBulkArchivesDialog() {
    setBulkArchivesOpen(true);
    await loadBulkArchives();
  }

  async function downloadBulkArchive(shipmentIds: string[], message = "Toplu etiketler tek PDF olarak yeniden hazırlanıyor.") {
    const uniqueIds = Array.from(new Set(shipmentIds.map(text).filter(Boolean)));
    if (uniqueIds.length === 0) {
      setError("Bu arşivde indirilebilir gönderi bulunamadı.");
      return;
    }
    if (uniqueIds.length > maxBulkOrderLabelOrders) {
      setError(`Tek toplu işlemde en fazla ${numberText(maxBulkOrderLabelOrders)} sipariş oluşturulabilir.`);
      return;
    }
    setBulkProgressStage("combining");
    setBulkProgressCompleted(0);
    setBulkProgressTotal(uniqueIds.length);
    setBulkProgressOpen(true);
    setError(null);
    setNotice(message);
    try {
      const downloadResult = await triggerOrderBulkShipmentPdfDownload(uniqueIds, setBulkProgressCompleted);
      setNotice(
        `${numberText(downloadResult.downloadedShipmentIds.length)} etiket ${numberText(downloadResult.pdfCount)} PDF dosyası olarak indirildi.` +
          (downloadResult.failedShipmentIds.length > 0
            ? ` ${numberText(downloadResult.failedShipmentIds.length)} etiket henüz hazır olmadığı için atlandı; yeniden indirmeyi deneyebilirsiniz.`
            : "")
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Etiketler tek PDF olarak hazırlanamadı.");
    } finally {
      setBulkProgressOpen(false);
    }
  }

  function updateBulkLabelDraft(orderId: string, field: keyof OrderLabelDialogAddress | "labelProductId", value: string) {
    setBulkLabelDrafts((current) =>
      current.map((row) =>
        row.orderId === orderId
          ? {
              ...row,
              [field]: field === "countryCode" ? value.toUpperCase() : value,
            }
          : row
      )
    );
  }

  function removeBulkLabelDraft(orderId: string) {
    setBulkLabelDrafts((current) => current.filter((row) => row.orderId !== orderId));
    setSelectedOrderIds((current) => {
      const next = new Set(current);
      next.delete(orderId);
      return next;
    });
  }

  function closeBulkLabelDialog() {
    if (bulkCreating) return;
    setBulkLabelDialogOpen(false);
    setBulkLabelResults([]);
  }

  async function bulkCreateLabels() {
    const orderIds = Array.from(selectedOrderIds);
    if (orderIds.length === 0) {
      setError("Toplu etiket için sipariş seçin.");
      return;
    }
    if (orderIds.length > maxBulkOrderLabelOrders) {
      setError(`Tek toplu işlemde en fazla ${numberText(maxBulkOrderLabelOrders)} sipariş oluşturulabilir.`);
      return;
    }
    const blockedSelectedRows = rows.filter((row) => selectedOrderIds.has(text(row.id)) && !canSelectOrderForLabel(row));
    if (blockedSelectedRows.length > 0) {
      const blockedIds = new Set(blockedSelectedRows.map((row) => text(row.id)));
      setSelectedOrderIds((current) => {
        const next = new Set(current);
        blockedIds.forEach((id) => next.delete(id));
        return next;
      });
      const specialAddressRows = blockedSelectedRows.filter(isOrderDhlSpecialAddress);
      setError(
        specialAddressRows.length > 0
          ? "Packstation/Postfiliale adresli siparişler toplu etiket işlemine seçilemez; tekli etiket oluşturmayı kullanın."
          : "İade/iptal veya mevcut etiketi olan siparişler toplu etiket işleminden çıkarıldı."
      );
      return;
    }
    setBulkCreating(true);
    setError(null);
    setNotice(null);
    setBulkLabelResults([]);
    try {
      const latestBalancesResult = await apiClient.balances();
      const latestBalances = latestBalancesResult.balances || [];
      setBalances(latestBalances);

      const rowsById = new Map(rows.map((row) => [text(row.id), row]));
      const unresolvedRows: string[] = [];
      const drafts = orderIds
        .map((orderId) => {
          const row = rowsById.get(orderId);
          if (!row) return null;
          const labelProductId = resolveBulkLabelProductId(row, latestBalances);
          if (!labelProductId) {
            unresolvedRows.push(text(row.orderNumber) || text(row.externalOrderId) || orderId);
          }
          const address = defaultOrderLabelAddress(row);
          return {
            ...address,
            addressSource: orderAddressSourceLabel(row.selectedAddressSource),
            countryCode: (text(address.countryCode) || "DE").toUpperCase(),
            currency: row.currency,
            customerName: text(row.recipientName) || "-",
            itemCount: row.itemCount,
            itemsPreview: row.itemsPreview,
            labelProductId,
            orderId,
            orderNumber: text(row.orderNumber) || text(row.externalOrderId) || orderId,
            storeName: text(row.storeName) || platformLabel(text(row.sourcePlatform)),
            weightGrams: row.weightGrams,
          } satisfies BulkOrderLabelDraft;
        })
        .filter((row): row is BulkOrderLabelDraft => Boolean(row));

      const missingRows = orderIds.length - drafts.length;
      if (drafts.length === 0) {
        throw new Error("Seçili siparişler bu sayfada bulunamadı. Lütfen siparişleri tekrar seçin.");
      }
      if (missingRows > 0) {
        setNotice(`${numberText(missingRows)} seçili sipariş görünür listede olmadığı için toplu kontrolden çıkarıldı.`);
      }
      if (unresolvedRows.length > 0) {
        setError(
          `Bazı siparişlerde ağırlığa uygun kredili DHL ürünü otomatik seçilemedi: ${unresolvedRows.slice(0, 5).join(", ")}${unresolvedRows.length > 5 ? " ..." : ""}. Popup içinde ürünü seçin.`
        );
      }

      setBulkLabelDrafts(drafts);
      setBulkLabelDialogOpen(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Toplu etiket kontrolü hazırlanamadı.");
    } finally {
      setBulkCreating(false);
    }
  }

  async function submitBulkLabelDialog() {
    if (bulkLabelDrafts.length === 0) {
      setError("Toplu etiket için hazırlanmış sipariş bulunamadı.");
      return;
    }
    if (bulkLabelDrafts.length > maxBulkOrderLabelOrders) {
      setError(`Tek toplu işlemde en fazla ${numberText(maxBulkOrderLabelOrders)} sipariş oluşturulabilir.`);
      return;
    }
    const draftsToCreate = pendingBulkOrderLabelDrafts(bulkLabelDrafts, bulkLabelResults);
    if (draftsToCreate.length === 0) {
      setNotice("Seçili tüm siparişlerin etiketleri zaten oluşturuldu.");
      return;
    }
    const invalidRow = draftsToCreate.find((row) => bulkOrderLabelDraftError(row));
    if (invalidRow) {
      setError(`${invalidRow.orderNumber}: ${bulkOrderLabelDraftError(invalidRow)}`);
      return;
    }
    const insufficient = bulkLabelCreditSummary.find((entry) => entry.insufficient);
    if (insufficient) {
      setError(
        `${insufficient.labelProductName} için kredi yetersiz. Gerekli ${numberText(insufficient.requested)}, mevcut ${numberText(insufficient.available)}.`
      );
      return;
    }

    setBulkCreating(true);
    setBulkProgressStage("processing");
    setBulkProgressCompleted(0);
    setBulkProgressTotal(draftsToCreate.length);
    setBulkProgressOpen(true);
    setError(null);
    setNotice(null);
    try {
      const orderConfigs = draftsToCreate.map((row) => ({
        addressOverride: {
          address1: text(row.address1),
          address2: text(row.address2),
          city: text(row.city),
          countryCode: (text(row.countryCode) || "DE").toUpperCase(),
          postalCode: text(row.postalCode),
          recipientName: text(row.recipientName),
        },
        labelProductId: row.labelProductId,
        orderId: row.orderId,
      }));
      const resultRows: BulkOrderLabelResultRow[] = [];

      for (let index = 0; index < orderConfigs.length; index += bulkCreateRequestChunkSize) {
        const orderConfigChunk = orderConfigs.slice(index, index + bulkCreateRequestChunkSize);
        let chunkResult: BulkCreateShipmentApiResponse | null = null;
        let lastChunkError: unknown = null;

        for (let attempt = 1; attempt <= bulkCreateRequestMaxAttempts; attempt += 1) {
          try {
            chunkResult = await apiClient.bulkCreateShipmentsForOrders({
              autoSelectByWeight: false,
              concurrency: 2,
              labelProductId: draftsToCreate[0]?.labelProductId || selectedLabelProductId || firstAvailableLabelProductId(),
              orderConfigs: orderConfigChunk,
              storeId: selectedStore?.id,
            });
            break;
          } catch (error) {
            lastChunkError = error;
            if (!shouldRetryBulkCreateError(error) || attempt >= bulkCreateRequestMaxAttempts) throw error;
            await waitForBulkRetry(750 * attempt);
          }
        }

        if (!chunkResult) throw lastChunkError || new Error("Toplu etiket parçası tamamlanamadı.");
        resultRows.push(...(Array.isArray(chunkResult.results) ? chunkResult.results : []));
        setBulkLabelResults((current) => {
          const merged = new Map(current.filter((row) => row.ok).map((row) => [row.orderId, row] as const));
          resultRows.forEach((row) => merged.set(row.orderId, row));
          return Array.from(merged.values());
        });
        setLastBulkDownloadShipmentIds(shipmentIdsFromBulkCreateResult({ results: resultRows }));
        setBulkProgressCompleted(Math.min(index + orderConfigChunk.length, orderConfigs.length));

        if (!chunkResult.ok && chunkResult.summary.success === 0 && chunkResult.summary.skipped === 0) {
          throw new Error(chunkResult.error || `${chunkResult.summary.failed} sipariş için etiket oluşturulamadı.`);
        }
      }

      const result: BulkCreateShipmentApiResponse = {
        ok: resultRows.every((row) => row.ok),
        results: resultRows,
        summary: {
          failed: resultRows.filter((row) => !row.ok).length,
          recovered: resultRows.filter((row) => row.recovered).length,
          skipped: resultRows.filter((row) => row.skipped).length,
          success: resultRows.filter((row) => row.ok && !row.skipped).length,
          total: resultRows.length,
        },
      };
      setBulkLabelResults((current) => {
        const merged = new Map(current.filter((row) => row.ok).map((row) => [row.orderId, row] as const));
        resultRows.forEach((row) => merged.set(row.orderId, row));
        return Array.from(merged.values());
      });
      if (!result.ok && result.summary.success === 0 && result.summary.skipped === 0) {
        throw new Error(result.error || `${result.summary.failed} sipariş için etiket oluşturulamadı.`);
      }
      const downloadableShipmentIds = shipmentIdsFromBulkCreateResult(result);
      const archivedOrderCount = resultRows.filter((row) => row.ok && !row.skipped && row.orderArchived).length;
      setLastBulkDownloadShipmentIds(downloadableShipmentIds);
      let combinedPdfError: unknown = null;
      let combinedPdfResult: (CombinedShipmentPdfDownloadResult & { pdfCount: number }) | null = null;
      const completeBatchReady =
        resultRows.length === orderConfigs.length &&
        resultRows.every((row) => row.ok && text(row.shipmentId));
      if (!completeBatchReady) {
        combinedPdfError = new Error("Seçili siparişlerin tüm etiketleri hazır olmadığı için eksik toplu PDF indirilmedi.");
      } else if (downloadableShipmentIds.length > 0) {
        setBulkProgressStage("combining");
        setBulkProgressCompleted(0);
        setBulkProgressTotal(downloadableShipmentIds.length);
        try {
          combinedPdfResult = await triggerOrderBulkShipmentPdfDownload(downloadableShipmentIds, setBulkProgressCompleted);
        } catch (mergeError) {
          combinedPdfError = mergeError;
        }
      }
      setBulkProgressStage("finalizing");
      setNotice(
        `${numberText(result.summary.success)} etiket oluşturuldu, ${numberText(result.summary.skipped)} mevcut gönderi atlandı, ${numberText(result.summary.failed)} hata.` +
          (archivedOrderCount > 0 ? ` ${numberText(archivedOrderCount)} sipariş otomatik arşivlendi.` : "") +
          (Number(result.summary.recovered || 0) > 0
            ? ` ${numberText(result.summary.recovered)} yarım kalmış etiket PDF'i otomatik tamamlandı.`
            : "") +
          (combinedPdfResult
            ? ` ${numberText(combinedPdfResult.downloadedShipmentIds.length)} etiket ${numberText(combinedPdfResult.pdfCount)} PDF dosyası olarak indirildi.` +
              (combinedPdfResult.failedShipmentIds.length > 0
                ? ` ${numberText(combinedPdfResult.failedShipmentIds.length)} etiket henüz hazır olmadığı için atlandı ve son toplu işlemden yeniden denenebilir.`
                : "")
            : "")
      );
      setSelectedOrderIds(new Set());
      await refreshOrdersView();
      if (bulkArchivesOpen) await loadBulkArchives();
      if (combinedPdfError) {
        throw new Error("Etiketler oluşturuldu fakat PDF dosyalarının tamamı hazırlanamadı. Son toplu işlemden tekrar indirmeyi deneyin.");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Toplu etiket oluşturulamadı.");
    } finally {
      setBulkCreating(false);
      setBulkProgressOpen(false);
    }
  }

  const selectablePageRows = rows.filter((row) => row.previewOnly !== true);
  const pageRowsAllSelected = selectablePageRows.length > 0 && selectablePageRows.every((row) => selectedOrderIds.has(text(row.id)));

  return (
    <div className="space-y-5">
      {confirmationDialog}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950">Siparişler</h1>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
            Tüm mağazalarınızdaki siparişleri filtreleyin, senkronlayın, CSV alın ve etiket akışını aynı ekrandan yönetin.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowArchivedOrders((current) => !current)}
            className={cx(
              "inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold shadow-sm transition",
              showArchivedOrders
                ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            )}
          >
            {showArchivedOrders ? <RotateCcw className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
            {showArchivedOrders ? "Aktif siparişler" : "Arşivlenmiş siparişler"}
          </button>
          <button
            type="button"
            onClick={() => void openBulkArchivesDialog()}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <History className="h-4 w-4" />
            Toplu etiket geçmişi
          </button>
          <a
            href={`/api/orders?${exportParams.toString()}`}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <Download className="h-4 w-4" />
            CSV dışa aktar
          </a>
          <Link
            to="/customer/orders/new"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
          >
            <PlusSquare className="h-4 w-4" />
            Manuel etiket
          </Link>
        </div>
      </div>

      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}
      {lastBulkDownloadShipmentIds.length > 0 ? (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-emerald-950">Son toplu etiket PDF hazır</p>
              <p className="mt-1 text-xs font-medium text-emerald-800">
                {numberText(lastBulkDownloadShipmentIds.length)} etiketi PDF dosyaları olarak tekrar indirebilirsiniz.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void downloadBulkArchive(lastBulkDownloadShipmentIds, "Toplu etiket PDF dosyaları hazırlanıyor.")}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800"
              >
                <Download className="h-4 w-4" />
                PDF dosyalarını indir
              </button>
              <button
                type="button"
                onClick={() => void openBulkArchivesDialog()}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-semibold text-emerald-900 shadow-sm transition hover:bg-emerald-50"
              >
                <History className="h-4 w-4" />
                Geçmişi aç
              </button>
            </div>
          </div>
        </section>
      ) : null}

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <AdminStatCard icon={<ShoppingCart className="h-5 w-5" />} label="Toplam sipariş" value={summary.total || 0} tone="slate" />
        <AdminStatCard icon={<Clock3 className="h-5 w-5" />} label="Açık" value={summary.open || 0} tone="amber" />
        <AdminStatCard icon={<Truck className="h-5 w-5" />} label="Etiketli" value={summary.withLabel || 0} tone="emerald" />
        <AdminStatCard icon={<AlertTriangle className="h-5 w-5" />} label="Etiket bekleyen" value={summary.waitingForLabel || 0} tone="red" />
        <AdminStatCard icon={<Wallet className="h-5 w-5" />} label="Kredi" value={totalCredits} tone="cyan" />
      </section>

      {selectedStore?.platform === "amazon" || platform === "amazon" ? <AmazonV1UpgradeNotice compact /> : null}

      <AdminPanel title="Sipariş kontrolü" caption="Senkron aralığı sipariş çekme işlemini belirler; liste filtreleri ayrıca aşağıdan yönetilir.">
        <div className="grid divide-y divide-slate-100 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(340px,0.9fr)] lg:divide-x lg:divide-y-0">
          <section className="min-w-0 p-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700 ring-1 ring-cyan-100">
                <Store className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-slate-950">Mağaza</h2>
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  {selectedStore ? platformLabel(selectedStore.platform) : `${numberText(activeStores.length)} aktif mağaza`}
                </p>
              </div>
            </div>
            <label className="block min-w-0">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Seçim</span>
              <select
                aria-label="Sipariş mağazası"
                value={storeId}
                onChange={(event) => {
                  setStoreId(event.target.value);
                  setSourceStoreId("all");
                }}
                className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5"
              >
                {selectableStores.length === 0 ? <option value="all">Aktif mağaza yok</option> : null}
                {selectableStores.length > 1 ? <option value="all">Tüm mağazalar</option> : null}
                {selectableStores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name} · {store.platform === "amazon" ? "Amazon V1 (pasif)" : platformLabel(store.platform)}
                  </option>
                ))}
              </select>
            </label>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="min-w-0 rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
                <span className="block text-[11px] font-semibold uppercase text-slate-400">Son senkron</span>
                <span className="mt-1 block truncate text-sm font-semibold text-slate-950">{dateText(selectedStore ? selectedStoreLastSync : latestStoreSyncAt)}</span>
              </div>
              <div className="min-w-0 rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
                <span className="block text-[11px] font-semibold uppercase text-slate-400">Kayıt</span>
                <span className="mt-1 block truncate text-sm font-semibold text-slate-950">{numberText(selectedStoreOrdersCount)}</span>
              </div>
            </div>
          </section>

          <section className="min-w-0 p-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
                <Tags className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-slate-950">Etiket ürünü</h2>
                <p className="mt-0.5 truncate text-xs text-slate-500">{selectedProductWeightText}</p>
              </div>
            </div>
            <label className="block min-w-0">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Ürün</span>
              <select
                value={selectedLabelProductId}
                onChange={(event) => setSelectedLabelProductId(event.target.value)}
                className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5"
                disabled={supportLoading || sortedProducts.length === 0}
              >
                <option value="">Ürün seçin</option>
                {sortedProducts.map((product) => {
                  const balance = balances.find((item) => item.labelProductId === product.id);
                  return (
                    <option key={product.id} value={product.id}>
                      {product.name} · {numberText(balance?.quantityAvailable || 0)} kredi
                    </option>
                  );
                })}
              </select>
            </label>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="min-w-0 rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
                <span className="block text-[11px] font-semibold uppercase text-slate-400">Mevcut kredi</span>
                <span className="mt-1 block truncate text-sm font-semibold text-slate-950">{numberText(selectedBalance?.quantityAvailable || 0)}</span>
              </div>
              <div className="min-w-0 rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
                <span className="block text-[11px] font-semibold uppercase text-slate-400">Ağırlık</span>
                <span className="mt-1 block truncate text-sm font-semibold text-slate-950">{selectedProductWeightText}</span>
              </div>
            </div>
          </section>

          <section className="min-w-0 p-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                <Clock3 className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-slate-950">Senkronizasyon</h2>
                <p className="mt-0.5 truncate text-xs text-slate-500">{syncing ? "İşlem devam ediyor" : "Hazır"}</p>
              </div>
            </div>
            <div>
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Aralık</span>
              <div className={cx("grid rounded-xl border border-slate-200 bg-slate-50 p-1", supportsOneDaySync ? "grid-cols-6" : "grid-cols-5")}>
                {([
                  ...(supportsOneDaySync ? [["1d", "1 gün"]] : []),
                  ["3d", "3 gün"],
                  ["7d", "7 gün"],
                  ["30d", "30 gün"],
                  ["3m", "3 ay"],
                  ["all", selectedStore?.platform === "amazon_v2" ? "Hepsi (2 yıl)" : "Hepsi"],
                ] as Array<[CustomerOrdersDateRange, string]>).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setSyncRange(value)}
                    className={cx(
                      "h-10 rounded-lg px-3 text-xs font-semibold transition",
                      syncRange === value ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-900"
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
              {selectedStore?.platform === "amazon" ? (
                <Link to="/customer/stores/amazon-v2/new" className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800">
                  <ExternalLink className="h-4 w-4" />
                  Amazon V2’ye bağlan
                </Link>
              ) : <button
                type="button"
                onClick={() => void syncSelectedStore()}
                disabled={syncing || activeStores.length === 0}
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
              >
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
                {syncing ? "Senkronizasyon sürüyor" : selectedStore ? "Senkronizasyonu başlat" : "Tüm mağazaları senkronla"}
              </button>}
              <button
                type="button"
                onClick={() => void refreshOrdersView()}
                disabled={loading || supportLoading}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
              >
                {supportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
                Listeyi yenile
              </button>
            </div>
            {syncStatus ? (
              <p className={cx("mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-xs font-semibold leading-5", syncing ? "bg-cyan-50 text-cyan-800" : "bg-emerald-50 text-emerald-800")}>
                {syncing ? <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" /> : <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                <span>{syncStatus}</span>
              </p>
            ) : null}
          </section>
        </div>
      </AdminPanel>

      {summary.previewOnly === true ? (
        <p className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">Amazon V2 siparişleri test modunda görüntülenir. Etiket oluşturma ve takip gönderimi henüz kapalıdır.</p>
      ) : null}

      {syncDialogOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div>
                <p className="text-sm font-semibold text-slate-950">Tüm mağazalar senkronize ediliyor</p>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  {syncing ? `${text(syncProgress.currentStore) || "Mağaza"} işleniyor.` : "Senkronizasyon tamamlandı."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSyncDialogOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
                aria-label="Senkron penceresini kapat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div>
                <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-500">
                  <span>{numberText(syncProgress.finished)} / {numberText(syncProgress.total)} mağaza</span>
                  <span>{numberText(syncProgressPercent)}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-slate-950 transition-all" style={{ width: `${syncProgressPercent}%` }} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
                  <span className="block text-[11px] font-semibold uppercase text-emerald-700">Başarılı</span>
                  <span className="mt-1 block text-lg font-semibold text-emerald-900">{numberText(syncProgress.succeeded)}</span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <span className="block text-[11px] font-semibold uppercase text-slate-500">Atlandı</span>
                  <span className="mt-1 block text-lg font-semibold text-slate-900">{numberText(syncProgress.skipped)}</span>
                </div>
                <div className="rounded-xl border border-red-100 bg-red-50 p-3">
                  <span className="block text-[11px] font-semibold uppercase text-red-700">Hata</span>
                  <span className="mt-1 block text-lg font-semibold text-red-900">{numberText(syncProgress.failed)}</span>
                </div>
              </div>
              {!syncing && (syncDetails.failures.length > 0 || syncDetails.skipped.length > 0) ? (
                <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-600">
                  {syncDetails.failures.map((item) => (
                    <p key={`failure-${item}`} className="text-red-700">{item}</p>
                  ))}
                  {syncDetails.skipped.map((item) => (
                    <p key={`skipped-${item}`}>{item}</p>
                  ))}
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => setSyncDialogOpen(false)}
                className="inline-flex h-11 w-full items-center justify-center rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
              >
                {syncing ? "Arka planda devam et" : "Kapat"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AdminPanel
        title={showArchivedOrders ? "Arşivlenmiş siparişler" : "Filtreler"}
        count={meta.totalCount}
        caption={showArchivedOrders ? "Bu görünümde sadece arşive gönderilen siparişler gösterilir; geri alınan siparişler ana listeye döner." : undefined}
      >
        <div
          className={cx(
            "grid gap-3 border-b border-slate-200 p-4",
            showAggregatorSourceStoreFilter
              ? "xl:grid-cols-[minmax(220px,1fr)_150px_150px_190px_180px_160px_190px]"
              : "xl:grid-cols-[minmax(240px,1fr)_150px_150px_180px_160px_190px]"
          )}
        >
          <div className="relative min-w-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Sipariş no, müşteri, e-posta, şehir ara"
              className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-950 outline-none focus:border-slate-400 focus:bg-white"
            />
          </div>
          <select value={status} onChange={(event) => setStatus(event.target.value)} disabled={onlyUnshipped} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50 disabled:text-slate-400">
            <option value="all">Tüm durumlar</option>
            <option value="open">Açık</option>
            <option value="shipped">Gönderilenler</option>
            <option value="cancelled">İptal</option>
          </select>
          <select aria-label="Sipariş platformu" value={platform} onChange={(event) => setPlatform(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
            <option value="all">Platform</option>
            {storePlatformOptions.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          {showAggregatorSourceStoreFilter ? (
            <select
              value={sourceStoreId}
              onChange={(event) => setSourceStoreId(event.target.value)}
              className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400"
              aria-label={`${platformLabel(selectedStore?.platform || "")} kaynak mağazası`}
            >
              <option value="all">Tüm {platformLabel(selectedStore?.platform || "")} kanalları</option>
              {aggregatorSourceStores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}{store.salesChannel ? ` · ${store.salesChannel}` : ""}{store.count ? ` (${numberText(store.count)})` : ""}
                </option>
              ))}
            </select>
          ) : null}
          <select value={labelState} onChange={(event) => setLabelState(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
            <option value="all">Etiket durumu</option>
            <option value="waiting">Etiket bekliyor</option>
            <option value="with_label">Etiket var</option>
          </select>
          <select value={pageSize} onChange={(event) => setPageSize(normalizeCustomerOrdersPageSize(event.target.value))} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
            {customerOrdersPageSizeOptions.map((size) => (
              <option key={size} value={size}>{size} satır</option>
            ))}
          </select>
          <label className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={onlyUnshipped}
              onChange={(event) => {
                const checked = event.target.checked;
                setOnlyUnshipped(checked);
                setStatus(checked ? "open" : "all");
                setLabelState(checked ? "waiting" : "all");
              }}
              className="h-4 w-4 rounded border-slate-300 text-slate-900"
            />
            Sadece gönderilmemiş
          </label>
        </div>

        <div className="flex flex-col gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <select
              value={viewDateRange}
              onChange={(event) => setViewDateRange(normalizeCustomerOrdersViewRange(event.target.value))}
              aria-label="Sipariş gösterim aralığı"
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm outline-none focus:border-slate-400"
            >
              <option value="all">Tüm tarihler</option>
              <option value="7d">Son 7 gün</option>
              <option value="30d">Son 30 gün</option>
              <option value="3m">Son 3 ay</option>
            </select>
            <button type="button" onClick={() => { setShowArchivedOrders(false); setOnlyUnshipped(true); setStatus("open"); setLabelState("waiting"); }} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">Etiket bekleyen açık siparişler</button>
            <button type="button" onClick={() => { setShowArchivedOrders(false); setOnlyUnshipped(false); setStatus("all"); setLabelState("with_label"); }} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">Etiketi olanlar</button>
            <button
              type="button"
              onClick={() => { setShowArchivedOrders(false); setOnlyUnshipped(false); setStatus("shipped"); setLabelState("all"); }}
              className={cx(
                "rounded-lg border px-3 py-1.5 text-xs font-semibold shadow-sm",
                !showArchivedOrders && status === "shipped" ? "border-cyan-200 bg-cyan-50 text-cyan-800" : "border-slate-200 bg-white text-slate-700"
              )}
            >
              Gönderilenler
            </button>
            <button
              type="button"
              onClick={() => setShowArchivedOrders((current) => !current)}
              className={cx(
                "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold shadow-sm",
                showArchivedOrders ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-700"
              )}
            >
              {showArchivedOrders ? <RotateCcw className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
              {showArchivedOrders ? "Aktif listeye dön" : "Arşivlenmiş siparişler"}
            </button>
            <button type="button" onClick={() => { setSearch(""); setStatus("open"); setPlatform("all"); setSourceStoreId("all"); setLabelState("waiting"); setStoreId(activeStores.length === 1 ? activeStores[0].id : "all"); setOnlyUnshipped(true); setShowArchivedOrders(false); setViewDateRange("all"); }} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">Temizle</button>
          </div>
          {selectedOrderIds.size > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-slate-500">{numberText(selectedOrderIds.size)} seçili</span>
              <button
                type="button"
                onClick={() => void bulkCreateLabels()}
                disabled={bulkCreating || bulkArchiving || sortedProducts.length === 0}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
              >
                {bulkCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Toplu etiket kontrolü
              </button>
              {!showArchivedOrders ? (
                <button
                  type="button"
                  onClick={() => void bulkArchiveSelectedOrders()}
                  disabled={bulkArchiving || bulkCreating}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                >
                  {bulkArchiving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                  Seçilenleri arşivle
                </button>
              ) : null}
              <button type="button" onClick={() => setSelectedOrderIds(new Set())} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm">Seçimi temizle</button>
            </div>
          ) : null}
        </div>

        {selectedOrderIds.size > 0 ? (
          <div className="border-b border-cyan-100 bg-cyan-50/80 px-4 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-cyan-700 ring-1 ring-cyan-100">
                  <Send className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-950">
                    {numberText(selectedOrderIds.size)} sipariş seçildi
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-600">
                    {selectedProductLabel} · {selectedProductWeightText}
                    {selectedBalance ? ` · mevcut kredi ${numberText(selectedBalance.quantityAvailable)}` : ""}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void bulkCreateLabels()}
                  disabled={bulkCreating || bulkArchiving || sortedProducts.length === 0}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
                >
                  {bulkCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Toplu etiket kontrolü
                </button>
                {!showArchivedOrders ? (
                  <button
                    type="button"
                    onClick={() => void bulkArchiveSelectedOrders()}
                    disabled={bulkArchiving || bulkCreating}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    {bulkArchiving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                    Seçilenleri arşivle
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setSelectedOrderIds(new Set())}
                  className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  Seçimi temizle
                </button>
              </div>
            </div>
            <p className="mt-2 text-xs text-cyan-800">En fazla 250 sipariş kontrol edilir; etiketler 100 etiketlik PDF dosyalarına ayrılır.</p>
          </div>
        ) : null}

        {loading ? (
          <div className="p-5"><LoadingPanel label="Siparişler yükleniyor" /></div>
        ) : rows.length === 0 ? (
          <div className="flex min-h-56 flex-col items-center justify-center gap-3 bg-slate-50/60 p-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-400 shadow-sm">
              <ShoppingCart className="h-5 w-5" />
            </div>
            <p className="text-sm font-semibold text-slate-700">Sipariş bulunamadı</p>
            <p className="max-w-lg text-sm text-slate-500">Filtreleri temizleyin, mağaza senkronu çalıştırın veya ilk mağazanızı bağlayın.</p>
            <Link to="/customer/stores" className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800">
              <Store className="h-4 w-4" />
              Mağazalara git
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1500px] divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                <tr>
                  <th className="w-12 px-5 py-3.5">
                    <input
                      type="checkbox"
                      checked={pageRowsAllSelected}
                      disabled={selectablePageRows.length === 0}
                      onChange={toggleCurrentPageSelection}
                      className="h-4 w-4 rounded border-slate-300 text-slate-900 disabled:opacity-40"
                      aria-label="Sayfadaki siparişleri seç"
                    />
                  </th>
                  <th className="px-5 py-3.5">Sipariş</th>
                  <th className="px-5 py-3.5">Tarih</th>
                  <th className="px-5 py-3.5">Müşteri</th>
                  <th className="px-5 py-3.5">Mağaza</th>
                  <th className="px-5 py-3.5">Items</th>
                  <th className="px-5 py-3.5 text-right">Toplam</th>
                  <th className="px-5 py-3.5">Ödeme</th>
                  <th className="px-5 py-3.5">Fulfillment</th>
                  <th className="px-5 py-3.5">Ülke</th>
                  <th className="px-5 py-3.5">Tracking</th>
                  <th className="px-5 py-3.5">Etiket</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const orderId = text(row.id);
                  const shipmentCount = rowNumber(row, "shipmentCount");
                  const hasShipment = shipmentCount > 0;
                  const paymentBlocked = isOrderBlockedForShipment(row);
                  const shipmentBlockReason = orderShipmentBlockReason(row);
                  const rowPlatformLabel = platformLabel(text(row.sourcePlatform));
                  const rowSourceStoreName = text(row.sourceStoreName);
                  const rowSalesChannel = text(row.salesChannel);
                  const rowStoreDetail = [rowPlatformLabel, rowSourceStoreName, rowSalesChannel].filter(Boolean).join(" · ");
                  return (
                    <tr key={orderId} className="group bg-white transition hover:bg-slate-50/80">
                      <td className="px-5 py-4 align-top">
                        <input
                          type="checkbox"
                          checked={selectedOrderIds.has(orderId)}
                          disabled={row.previewOnly === true}
                          onChange={() => toggleOrderSelection(row)}
                          title={shipmentBlockReason || undefined}
                          className="h-4 w-4 rounded border-slate-300 text-slate-900 disabled:opacity-40"
                          aria-label={`${text(row.orderNumber)} siparişini seç`}
                        />
                      </td>
                      <td className="px-5 py-4 align-top">
                        <div className="min-w-[150px]">
                          <NameStack title={text(row.orderNumber)} detail={text(row.externalOrderId)} />
                          <OrderRowActions
                            archivedView={showArchivedOrders}
                            archiveUpdatingOrderId={archiveUpdatingOrderId}
                            cancelingShipmentId={cancelingShipmentId}
                            creatingOrderId={creatingOrderId}
                            onArchiveOrder={archiveOrder}
                            onCancelShipment={cancelOrderShipment}
                            onOpenCreateLabelDialog={openCreateLabelDialog}
                            onRestoreOrder={restoreOrder}
                            paymentBlocked={paymentBlocked}
                            row={row}
                          />
                        </div>
                      </td>
                      <td className="px-5 py-4 align-top">
                        <span className="whitespace-nowrap text-slate-700">{orderDateText(row.orderedAt || row.createdAt)}</span>
                      </td>
                      <td className="px-5 py-4 align-top">
                        <div className="max-w-[260px]">
                          <p className="font-medium text-slate-950">{text(row.recipientName) || "-"}</p>
                          <p className="mt-1 flex items-start gap-1.5 text-xs leading-5 text-slate-500">
                            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                            <span>{orderCustomerAddress(row)}</span>
                          </p>
                          <p className="mt-1 text-xs text-slate-400">
                            {orderAddressSourceLabel(row.selectedAddressSource)}
                            {row.billingAddressDiffers ? " · Fatura farklı" : ""}
                          </p>
                          {orderAddressWarnings(row)[0] ? (
                            <p className="mt-1 text-xs font-semibold leading-5 text-red-700">{orderAddressWarnings(row)[0]}</p>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-5 py-4 align-top">
                        <NameStack title={text(row.storeName) || rowPlatformLabel} detail={rowStoreDetail || rowPlatformLabel} />
                      </td>
                      <td className="px-5 py-4 align-top">
                        <OrderItemsSummary itemCount={row.itemCount} itemsPreview={row.itemsPreview} currency={row.currency} />
                      </td>
                      <td className="px-5 py-4 text-right align-top font-semibold text-slate-900">{orderMoneyText(row.totalPrice, row.currency)}</td>
                      <td className="px-5 py-4 align-top">{orderStatusBadge(row.paymentStatus)}</td>
                      <td className="px-5 py-4 align-top">{orderStatusBadge(row.fulfillmentStatus || row.status)}</td>
                      <td className="px-5 py-4 align-top font-semibold text-slate-900">{text(row.countryCode) || "-"}</td>
                      <td className="px-5 py-4 align-top">
                        <span className="whitespace-nowrap text-slate-600">{text(row.trackingNumber) || "-"}</span>
                      </td>
                      <td className="px-5 py-4 align-top">
                        {hasShipment ? (
                          <NameStack title={text(row.trackingNumber) || `${numberText(shipmentCount)} gönderi`} detail={text(row.labelProductName) || text(row.labelGenerationStatus) || dateText(row.lastShipmentAt)} />
                        ) : shipmentBlockReason ? (
                          <NameStack
                            title={row.previewOnly === true ? "Test kaydı" : isOrderDhlSpecialAddress(row) ? "Özel adres" : "Etiket engelli"}
                            detail={shipmentBlockReason}
                          />
                        ) : (
                          <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">Etiket bekliyor</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50 px-5 py-3 text-sm text-slate-600 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <span className="font-medium">
              {rows.length === 0
                ? "Kayıt yok"
                : `${numberText(meta.offset + 1)}-${numberText(meta.offset + rows.length)} / ${numberText(meta.totalCount)} kayıt`}
            </span>
            <label className="inline-flex w-fit items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm">
              <span className="whitespace-nowrap">Sayfa başı</span>
              <select
                value={pageSize}
                onChange={(event) => setPageSize(normalizeCustomerOrdersPageSize(event.target.value))}
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-950 outline-none focus:border-slate-400"
                aria-label="Sayfa başı order sayısı"
              >
                {customerOrdersPageSizeOptions.map((size) => (
                  <option key={size} value={size}>{size} order</option>
                ))}
              </select>
            </label>
          </div>
          <nav className="flex flex-wrap items-center gap-1.5" aria-label="Sipariş sayfaları">
            <button
              type="button"
              disabled={loading || page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
            >
              Önceki
            </button>
            {paginationItems.map((item, index) =>
              item === "ellipsis" ? (
                <span key={`ellipsis-${index}`} className="inline-flex h-9 w-9 items-center justify-center text-xs font-semibold text-slate-400">
                  ...
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  disabled={loading || item === currentPageNumber}
                  onClick={() => setPage(item - 1)}
                  className={cx(
                    "inline-flex h-9 min-w-9 items-center justify-center rounded-lg border px-3 text-xs font-semibold shadow-sm transition disabled:cursor-default",
                    item === currentPageNumber
                      ? "border-slate-950 bg-slate-950 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  )}
                  aria-current={item === currentPageNumber ? "page" : undefined}
                >
                  {numberText(item)}
                </button>
              )
            )}
            <button
              type="button"
              disabled={loading || currentPageNumber >= totalPages}
              onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
              className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
            >
              Sonraki
            </button>
          </nav>
        </div>
      </AdminPanel>

      {bulkLabelDialogOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
            <div className="border-b border-slate-100 px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3">
                  <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
                    <Package className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-lg font-semibold text-slate-950">Toplu etiket kontrolü</p>
                    <p className="mt-1 text-sm leading-5 text-slate-500">
                      Seçili siparişlerde paket, kilo ve adres bilgisini kontrol edin; onaydan sonra etiketler sırayla oluşturulur.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeBulkLabelDialog}
                  disabled={bulkCreating}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
                  aria-label="Toplu etiket kontrolünü kapat"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-4 grid gap-3 lg:grid-cols-[0.8fr_1fr_2fr]">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase text-emerald-800">Seçili sipariş</p>
                  <p className="mt-1 text-2xl font-semibold text-emerald-950">{numberText(bulkLabelDrafts.length)}</p>
                </div>
                <div className={cx("rounded-xl border px-4 py-3", bulkLabelHasValidationErrors ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50")}>
                  <p className={cx("text-[11px] font-semibold uppercase", bulkLabelHasValidationErrors ? "text-red-800" : "text-emerald-800")}>Kontrol</p>
                  <p className={cx("mt-2 flex items-center gap-2 text-sm font-semibold", bulkLabelHasValidationErrors ? "text-red-700" : "text-emerald-700")}>
                    {bulkLabelHasValidationErrors ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                    {bulkLabelHasValidationErrors ? "Düzenlenmesi gereken satır var" : "Satırlar hazır"}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[11px] font-semibold uppercase text-slate-500">Kredi özeti</p>
                  <div className="mt-2 space-y-1.5 text-sm">
                    {bulkLabelCreditSummary.length === 0 ? (
                      <span className="text-slate-500">
                        {bulkLabelResults.some((result) => result.ok) ? "Kredi işlemi tamamlandı" : "Henüz ürün seçilmedi"}
                      </span>
                    ) : (
                      bulkLabelCreditSummary.map((entry) => (
                        <div key={entry.labelProductId} className="flex items-center justify-between gap-3">
                          <span className={cx("min-w-0 truncate", entry.insufficient ? "text-red-700" : "text-slate-800")}>{entry.labelProductName}</span>
                          <span className={cx("shrink-0 font-semibold", entry.insufficient ? "text-red-700" : "text-slate-600")}>
                            {numberText(entry.requested)} gerekli / {numberText(entry.available)} mevcut
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {bulkLabelResults.length > 0 ? (
                <div className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-950">Son toplu işlem sonucu</p>
                      <p className="mt-1 text-xs leading-5 text-slate-500">
                        {numberText(bulkLabelResultSummary.success)} oluşturuldu, {numberText(bulkLabelResultSummary.skipped)} mevcut gönderi atlandı, {numberText(bulkLabelResultSummary.failed)} hata.
                      </p>
                    </div>
                    {lastBulkDownloadShipmentIds.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => void downloadBulkArchive(lastBulkDownloadShipmentIds, "Son toplu etiketler tek PDF olarak yeniden hazırlanıyor.")}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800"
                      >
                        <Download className="h-4 w-4" />
                        Tek PDF'i indir
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {bulkLabelHasInsufficientCredits ? (
                <p className="mt-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                  Seçili ürünlerden en az birinde kredi yetersiz. Ürünleri değiştirin veya kredi tanımlayın.
                </p>
              ) : null}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 px-5 py-4">
              {bulkLabelDrafts.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm font-semibold text-slate-500">
                  Toplu kontrol için satır kalmadı.
                </div>
              ) : (
                <div className="space-y-3">
                  {bulkLabelDrafts.map((row) => {
                    const rowError = bulkOrderLabelDraftError(row);
                    const resultRow = bulkLabelResults.find((result) => result.orderId === row.orderId);
                    const product = sortedProducts.find((entry) => entry.id === row.labelProductId);
                    const productLabel = product
                      ? `${product.name} · ${product.carrier} · ${orderWeightRangeText(product.weightMinGrams, product.weightMaxGrams)}`
                      : row.labelProductId
                      ? row.labelProductId
                      : "Ürün seçilmedi";
                    return (
                      <section
                        key={row.orderId}
                        className={cx(
                          "overflow-hidden rounded-xl border border-l-4 bg-white shadow-sm",
                          rowError ? "border-red-200 border-l-red-500" : "border-slate-200 border-l-emerald-500"
                        )}
                      >
                        <div className="flex flex-col gap-3 border-b border-slate-100 bg-white px-4 py-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-slate-950">{row.orderNumber}</span>
                              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">{row.customerName}</span>
                              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">{row.storeName}</span>
                              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">{orderWeightText(row.weightGrams)}</span>
                              <span className="max-w-full truncate rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">{productLabel}</span>
                              {resultRow ? (
                                <span
                                  className={cx(
                                    "rounded-full border px-2.5 py-1 text-xs font-semibold",
                                    resultRow.ok
                                      ? resultRow.skipped
                                        ? "border-amber-200 bg-amber-50 text-amber-700"
                                        : "border-emerald-200 bg-emerald-50 text-emerald-700"
                                      : "border-red-200 bg-red-50 text-red-700"
                                  )}
                                >
                                  {resultRow.ok ? (resultRow.skipped ? "Zaten vardı" : "Oluşturuldu") : "Hata"}
                                </span>
                              ) : null}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs leading-5 text-slate-500">
                              <span>{row.addressSource}</span>
                              <OrderItemsSummary className="max-w-none" itemCount={row.itemCount} itemsPreview={row.itemsPreview} currency={row.currency} />
                              {resultRow?.trackingNumber ? <span>Takip: {text(resultRow.trackingNumber)}</span> : null}
                            </div>
                            {rowError ? <p className="mt-2 text-xs font-semibold text-red-700">{rowError}</p> : null}
                            {resultRow && !resultRow.ok ? <p className="mt-2 text-xs font-semibold text-red-700">{text(resultRow.error) || "Etiket oluşturulamadı."}</p> : null}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeBulkLabelDraft(row.orderId)}
                            disabled={bulkCreating}
                            className="inline-flex h-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                          >
                            Satırı çıkar
                          </button>
                        </div>

                        <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-12">
                          <label className="grid gap-1 text-xs font-semibold text-slate-600 xl:col-span-4">
                            Etiket ürünü
                            <select
                              value={row.labelProductId}
                              onChange={(event) => updateBulkLabelDraft(row.orderId, "labelProductId", event.target.value)}
                              disabled={bulkCreating}
                              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                            >
                              <option value="">Etiket ürünü seçin</option>
                              {sortedProducts.map((productOption) => {
                                const available = availableCreditsForProduct(productOption.id);
                                return (
                                  <option key={productOption.id} value={productOption.id} disabled={available <= 0 && productOption.id !== row.labelProductId}>
                                    {productOption.name} · {productOption.carrier} · {orderWeightRangeText(productOption.weightMinGrams, productOption.weightMaxGrams)} · {numberText(available)} kredi
                                  </option>
                                );
                              })}
                            </select>
                          </label>
                          <label className="grid gap-1 text-xs font-semibold text-slate-600 xl:col-span-3">
                            Alıcı
                            <input
                              value={row.recipientName}
                              onChange={(event) => updateBulkLabelDraft(row.orderId, "recipientName", event.target.value)}
                              disabled={bulkCreating}
                              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                            />
                          </label>
                          <label className="grid gap-1 text-xs font-semibold text-slate-600 xl:col-span-5">
                            Adres 1
                            <input
                              value={row.address1}
                              onChange={(event) => updateBulkLabelDraft(row.orderId, "address1", event.target.value)}
                              disabled={bulkCreating}
                              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                            />
                          </label>
                          <label className="grid gap-1 text-xs font-semibold text-slate-600 xl:col-span-4">
                            Adres 2
                            <input
                              value={row.address2}
                              onChange={(event) => updateBulkLabelDraft(row.orderId, "address2", event.target.value)}
                              disabled={bulkCreating}
                              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                            />
                          </label>
                          <label className="grid gap-1 text-xs font-semibold text-slate-600 xl:col-span-2">
                            Posta kodu
                            <input
                              value={row.postalCode}
                              onChange={(event) => updateBulkLabelDraft(row.orderId, "postalCode", event.target.value)}
                              disabled={bulkCreating}
                              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                            />
                          </label>
                          <label className="grid gap-1 text-xs font-semibold text-slate-600 xl:col-span-4">
                            Şehir
                            <input
                              value={row.city}
                              onChange={(event) => updateBulkLabelDraft(row.orderId, "city", event.target.value)}
                              disabled={bulkCreating}
                              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                            />
                          </label>
                          <label className="grid gap-1 text-xs font-semibold text-slate-600 xl:col-span-2">
                            Ülke
                            <input
                              value={row.countryCode}
                              onChange={(event) => updateBulkLabelDraft(row.orderId, "countryCode", event.target.value)}
                              disabled={bulkCreating}
                              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                            />
                          </label>
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-100 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">
                {pendingBulkLabelDrafts.length === 0 && bulkLabelResults.some((result) => result.ok)
                  ? "Seçili siparişlerin etiket işlemi tamamlandı."
                  : `${numberText(pendingBulkLabelDrafts.length)} sipariş hazır. En fazla ${numberText(maxBulkOrderLabelOrders)} sipariş tek toplu işlemde sıraya alınır.`}
              </p>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeBulkLabelDialog}
                  disabled={bulkCreating}
                  className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Kapat
                </button>
                <button
                  type="button"
                  onClick={() => void submitBulkLabelDialog()}
                  disabled={bulkCreating || pendingBulkLabelDrafts.length === 0 || bulkLabelHasValidationErrors || bulkLabelHasInsufficientCredits}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-emerald-800 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
                >
                  {bulkCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
                  Toplu etiketleri oluştur
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {bulkProgressOpen ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-slate-200">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <Loader2 className="h-5 w-5 animate-spin" />
              </span>
              <div>
                <p className="text-base font-semibold text-slate-950">
                  {bulkProgressStage === "combining" ? "Etiketler tek PDF olarak hazırlanıyor" : "Toplu etiket oluşturuluyor"}
                </p>
                <p className="mt-1 text-sm leading-5 text-slate-500">
                  {bulkProgressStage === "finalizing"
                    ? "Etiketler oluşturuldu, liste ve toplu etiket geçmişi yenileniyor."
                    : bulkProgressStage === "combining"
                      ? `${numberText(bulkProgressCompleted)} / ${numberText(bulkProgressTotal)} etiket PDF'e ekleniyor. Sistem 50'şer adımlık güvenli parçalar halinde çalışıyor.`
                      : `${numberText(bulkProgressCompleted)} / ${numberText(bulkProgressTotal)} sipariş güvenli gruplar halinde işleniyor. Pencereyi kapatmayın.`}
                </p>
              </div>
            </div>
            <div className="mt-5 h-2.5 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-emerald-600 transition-all"
                style={{
                  width:
                    bulkProgressStage === "finalizing"
                      ? "96%"
                      : `${Math.max(4, Math.round((bulkProgressCompleted / Math.max(1, bulkProgressTotal)) * 90))}%`,
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {labelDialogOrder ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div className="min-w-0">
                <p className="text-base font-semibold text-slate-950">Etiket oluştur</p>
                <p className="mt-1 truncate text-sm text-slate-500">{orderLabelDialogTitle(labelDialogOrder)} · {text(labelDialogOrder.storeName) || platformLabel(text(labelDialogOrder.sourcePlatform))}</p>
              </div>
              <button
                type="button"
                onClick={closeLabelDialog}
                disabled={Boolean(creatingOrderId)}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
                aria-label="Etiket oluşturma penceresini kapat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Sipariş</p>
                  <p className="mt-1 truncate text-sm font-semibold text-slate-950">{text(labelDialogOrder.orderNumber) || text(labelDialogOrder.externalOrderId) || "-"}</p>
                  <p className="mt-1 text-xs text-slate-500">{orderDateText(labelDialogOrder.orderedAt || labelDialogOrder.createdAt)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Items</p>
                  <OrderItemsSummary
                    className="mt-1 block"
                    currency={labelDialogOrder.currency}
                    itemCount={labelDialogOrder.itemCount}
                    itemsPreview={labelDialogOrder.itemsPreview}
                  />
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Ağırlık / Toplam</p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">{labelDialogOrderWeightText}</p>
                  <p className="mt-1 text-xs text-slate-500">{orderMoneyText(labelDialogOrder.totalPrice, labelDialogOrder.currency)}</p>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-slate-400" htmlFor="order-label-product">
                  Etiket ürünü
                </label>
                <select
                  id="order-label-product"
                  value={labelDialogProductId}
                  onChange={(event) => setLabelDialogProductId(event.target.value)}
                  disabled={Boolean(creatingOrderId) || sortedProducts.length === 0}
                  className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50 disabled:text-slate-400"
                >
                  <option value="">Etiket ürünü seçin</option>
                  {sortedProducts.map((product) => {
                    const available = availableCreditsForProduct(product.id);
                    return (
                      <option key={product.id} value={product.id}>
                        {product.name} · {product.carrier} · {orderWeightRangeText(product.weightMinGrams, product.weightMaxGrams)} · {numberText(available)} kredi
                      </option>
                    );
                  })}
                </select>
                <div className="mt-3 grid gap-3 sm:grid-cols-4">
                  <label className="rounded-lg bg-slate-50 px-3 py-2">
                    <span className="block text-[11px] font-semibold uppercase text-slate-400">Koli sayısı</span>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      step={1}
                      value={labelDialogPackageCount}
                      onChange={(event) => {
                        const next = Number(event.target.value);
                        setLabelDialogPackageCount(Number.isFinite(next) ? Math.max(1, Math.min(50, Math.trunc(next))) : 1);
                      }}
                      disabled={Boolean(creatingOrderId)}
                      className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-100"
                    />
                  </label>
                  <div className="rounded-lg bg-slate-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase text-slate-400">Mevcut kredi</p>
                    <p className={cx("mt-1 text-sm font-semibold", labelDialogAvailableCredits > 0 ? "text-slate-950" : "text-red-600")}>{numberText(labelDialogAvailableCredits)}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase text-slate-400">Ürün aralığı</p>
                    <p className="mt-1 text-sm font-semibold text-slate-950">
                      {labelDialogSelectedProduct ? orderWeightRangeText(labelDialogSelectedProduct.weightMinGrams, labelDialogSelectedProduct.weightMaxGrams) : "-"}
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase text-slate-400">Kaynak</p>
                    <p className="mt-1 text-sm font-semibold text-slate-950">{labelDialogAddressSource}</p>
                  </div>
                </div>
                {labelDialogProductId && labelDialogAvailableCredits < labelDialogPackageCount ? (
                  <p className="mt-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                    {numberText(labelDialogPackageCount)} koli için {numberText(labelDialogPackageCount)} kredi gerekiyor; kullanılabilir kredi {numberText(labelDialogAvailableCredits)}.
                  </p>
                ) : null}
                {labelDialogPackageCount > 1 ? (
                  <p className="mt-3 rounded-lg border border-cyan-100 bg-cyan-50 px-3 py-2 text-xs font-semibold text-cyan-800">
                    Her koli için ayrı etiket ve ayrı takip numarası oluşturulur. Etiketler tek PDF dosyası halinde indirilir.
                  </p>
                ) : null}
                {labelDialogMissingSourceWeight ? (
                  <p className="mt-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                    Kaynak siparişte ağırlık bilgisi eksik. Yanlış DHL ürünü seçilmemesi için sistem otomatik seçim yapmadı; ürünü manuel doğrulayın.
                  </p>
                ) : null}
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-950">Kargo adresi</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {labelDialogAddressSource}
                      {labelDialogOrder.billingAddressDiffers ? " · fatura adresi farklı" : ""}
                    </p>
                  </div>
                  {labelDialogOrder.selectedAddressSource === "billing_fallback" ? (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">Fatura adresi yedeği</span>
                  ) : null}
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-1 text-sm font-semibold text-slate-700">
                    Alıcı adı
                    <input
                      value={labelDialogAddress.recipientName}
                      onChange={(event) => updateLabelDialogAddress("recipientName", event.target.value)}
                      disabled={Boolean(creatingOrderId)}
                      className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                    />
                  </label>
                  <label className="grid gap-1 text-sm font-semibold text-slate-700">
                    Ülke
                    <input
                      value={labelDialogAddress.countryCode}
                      onChange={(event) => updateLabelDialogAddress("countryCode", event.target.value)}
                      disabled={Boolean(creatingOrderId)}
                      className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                    />
                  </label>
                  <label className="grid gap-1 text-sm font-semibold text-slate-700">
                    Adres 1
                    <input
                      value={labelDialogAddress.address1}
                      onChange={(event) => updateLabelDialogAddress("address1", event.target.value)}
                      disabled={Boolean(creatingOrderId)}
                      className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                    />
                  </label>
                  <label className="grid gap-1 text-sm font-semibold text-slate-700">
                    Adres 2
                    <input
                      value={labelDialogAddress.address2}
                      onChange={(event) => updateLabelDialogAddress("address2", event.target.value)}
                      disabled={Boolean(creatingOrderId)}
                      className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                    />
                  </label>
                  <label className="grid gap-1 text-sm font-semibold text-slate-700">
                    Posta kodu
                    <input
                      value={labelDialogAddress.postalCode}
                      onChange={(event) => updateLabelDialogAddress("postalCode", event.target.value)}
                      disabled={Boolean(creatingOrderId)}
                      className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                    />
                  </label>
                  <label className="grid gap-1 text-sm font-semibold text-slate-700">
                    Şehir
                    <input
                      value={labelDialogAddress.city}
                      onChange={(event) => updateLabelDialogAddress("city", event.target.value)}
                      disabled={Boolean(creatingOrderId)}
                      className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-950 outline-none focus:border-slate-400 disabled:bg-slate-50"
                    />
                  </label>
                </div>
                {!labelDialogAddressReady(labelDialogAddress) ? (
                  <p className="mt-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">Alıcı, adres, posta kodu, şehir ve DE ülke kodu tamamlanmalı.</p>
                ) : null}
                {labelDialogAddressFormatWarnings.length > 0 ? (
                  <div className="mt-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs font-semibold leading-5 text-red-700">
                    {labelDialogAddressFormatWarnings.map((warning) => <p key={warning}>{warning}</p>)}
                  </div>
                ) : null}
                {labelDialogAddressFormatAdjustments.length > 0 ? (
                  <div className="mt-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs font-semibold leading-5 text-amber-800">
                    {labelDialogAddressFormatAdjustments.map((adjustment) => <p key={adjustment}>{adjustment}</p>)}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex flex-col gap-2 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={closeLabelDialog}
                disabled={Boolean(creatingOrderId)}
                className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={() => void submitLabelDialog()}
                disabled={!labelDialogCanSubmit}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
              >
                {creatingOrderId === text(labelDialogOrder.id) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {labelDialogPackageCount > 1 ? `${numberText(labelDialogPackageCount)} etiketi oluştur` : "Etiketi oluştur"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {bulkArchivesOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 p-4">
          <div className="flex max-h-[86vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div>
                <p className="text-base font-semibold text-slate-950">Toplu etiket geçmişi</p>
                <p className="mt-1 text-sm leading-5 text-slate-500">Siparişlerden oluşturulan etiketleri tek PDF olarak tekrar indirebilirsiniz.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setBulkArchivesOpen(false);
                  setBulkArchivesError(null);
                }}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
                aria-label="Toplu etiket geçmişini kapat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {bulkArchivesError ? <ErrorBanner message={bulkArchivesError} /> : null}
              {bulkArchivesLoading && bulkArchives.length === 0 ? (
                <LoadingPanel label="Toplu etiket geçmişi yükleniyor" />
              ) : bulkArchives.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                  <p className="text-sm font-semibold text-slate-700">Henüz toplu etiket kaydı yok</p>
                  <p className="mt-1 text-sm text-slate-500">Siparişleri seçip toplu etiket oluşturduğunuzda PDF kayıtları burada görünür.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bulkArchives.map((archive) => {
                    const shipmentIds = Array.isArray(archive.shipmentIds) ? archive.shipmentIds : [];
                    const trackingPreview = (archive.trackingNumbers || []).slice(0, 3).join(", ");
                    const filePreview = (archive.fileNames || []).slice(0, 4).join(", ");
                    return (
                      <article key={archive.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-950">
                              {text(archive.fileName).toLowerCase().endsWith(".pdf") ? archive.fileName : "kenpost-labels.pdf"}
                            </p>
                            <p className="mt-1 text-xs leading-5 text-slate-500">
                              {dateText(archive.createdAt)} · {numberText(archive.shipmentCount || shipmentIds.length)} gönderi · PDF
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">Tek PDF olarak indirilebilir</span>
                              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-500">Sipariş toplu indirme</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => void downloadBulkArchive(shipmentIds, "Geçmişteki etiketler tek PDF olarak yeniden hazırlanıyor.")}
                            disabled={shipmentIds.length === 0}
                            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-slate-950 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
                          >
                            <Download className="h-4 w-4" />
                            Tekrar indir
                          </button>
                        </div>
                        {trackingPreview ? <p className="mt-3 text-xs leading-5 text-slate-500">Takip: {trackingPreview}{(archive.trackingNumbers || []).length > 3 ? ` +${numberText((archive.trackingNumbers || []).length - 3)}` : ""}</p> : null}
                        {filePreview ? <p className="mt-1 break-all text-xs leading-5 text-slate-400">Dosyalar: {filePreview}{(archive.fileNames || []).length > 4 ? ` +${numberText((archive.fileNames || []).length - 4)}` : ""}</p> : null}
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={() => void loadBulkArchives()}
                disabled={bulkArchivesLoading}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
              >
                {bulkArchivesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
                Yenile
              </button>
              <button
                type="button"
                onClick={() => setBulkArchivesOpen(false)}
                className="inline-flex items-center justify-center rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function OrderDetailPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    apiClient
      .order(orderId || "")
      .then((result) => {
        if (active) setOrder(result.order);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Sipariş detayı alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [orderId]);

  if (loading) return <LoadingPanel label="Sipariş detayı yükleniyor" />;
  if (error) return <ErrorPanel title="Sipariş detayı açılamadı" message={error} />;
  if (!order) return <ErrorPanel title="Sipariş bulunamadı" message="Kayıt bulunamadı." />;

  const orderItems = Array.isArray(order.itemsPreview) ? order.itemsPreview : [];
  const orderTotalText = orderMoneyText(order.totalPrice, order.currency);

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <Link className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900" to="/customer/orders">
          <ArrowLeft className="h-4 w-4" />
          Siparişlere dön
        </Link>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-sm">
              <ShoppingCart className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-2xl font-semibold text-slate-950">{order.orderNumber}</h1>
              <p className="mt-1 text-sm text-slate-500">
                {order.externalOrderId || "Harici ID yok"} · {[platformLabel(order.sourcePlatform), text(order.sourceStoreName), text(order.salesChannel)].filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {statusBadge(order.status)}
            {order.paymentStatus ? statusBadge(order.paymentStatus) : null}
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Tarih</p>
            <p className="mt-1 text-sm font-semibold text-slate-950">{orderDateText(order.orderedAt || order.createdAt)}</p>
          </div>
          <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Items</p>
            <OrderItemsSummary className="mt-1" itemCount={order.itemCount} itemsPreview={order.itemsPreview} currency={order.currency} preview={false} />
          </div>
          <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Toplam</p>
            <p className="mt-1 text-sm font-semibold text-slate-950">{orderTotalText}</p>
          </div>
          <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Mağaza</p>
            <p className="mt-1 truncate text-sm font-semibold text-slate-950">{order.store?.name || "-"}</p>
          </div>
        </div>
      </section>

      <section className="grid gap-5 xl:grid-cols-2">
        <DetailCard
          title="Kargo adresi"
          items={[
            ["Adres kaynağı", orderAddressSourceLabel(order.selectedAddressSource)],
            ["Alıcı", order.recipientName],
            ["Adres", compactAddress(order.address1, order.address2, order.postalCode, order.city, order.countryCode)],
            ["E-posta", order.email || "-"],
            ["Telefon", order.phone || "-"],
          ]}
        />
        <DetailCard
          title="Sipariş bilgisi"
          items={[
            ["Mağaza", order.store?.name || "-"],
            ["Platform", platformLabel(order.store?.platform || order.sourcePlatform)],
            ["Kaynak mağaza", order.sourceStoreName || "-"],
            ["Kaynak kanal", order.salesChannel || "-"],
            ["Tarih", orderDateText(order.orderedAt || order.createdAt)],
            ["Items", <OrderItemsSummary itemCount={order.itemCount} itemsPreview={order.itemsPreview} currency={order.currency} preview={false} />],
            ["Toplam", orderTotalText],
            ["Ödeme", order.paymentStatus ? statusBadge(order.paymentStatus) : "-"],
            ["Fulfillment", order.fulfillmentStatus ? statusBadge(order.fulfillmentStatus) : statusBadge(order.status)],
            ["Ağırlık", `${numberText(order.weightGrams)} g`],
            ["Oluşturulma", dateText(order.createdAt)],
            ["Güncelleme", dateText(order.updatedAt)],
            ["Gönderim", dateText(order.shippedAt)],
          ]}
        />
        {order.billingAddressDiffers && order.billingAddress ? (
          <DetailCard
            title="Fatura adresi"
            items={[
              ["Durum", "Kargo adresinden farklı"],
              ["Alıcı", order.billingAddress.recipientName || "-"],
              [
                "Adres",
                compactAddress(
                  order.billingAddress.address1,
                  order.billingAddress.address2,
                  order.billingAddress.postalCode,
                  order.billingAddress.city,
                  order.billingAddress.countryCode
                ),
              ],
              ["E-posta", order.billingAddress.email || "-"],
              ["Telefon", order.billingAddress.phone || "-"],
            ]}
          />
        ) : null}
      </section>

      <DataSection
        title="Satılan ürünler"
        rows={orderItems}
        empty="Bu sipariş için ürün bilgisi bulunamadı."
        columns={[
          {
            label: "Ürün",
            value: (row) => {
              const sku = text(row.sku);
              const asin = text(row.asin);
              const detail = [sku ? `SKU: ${sku}` : "", asin ? `ASIN: ${asin}` : ""].filter(Boolean).join(" · ");
              return <NameStack title={text(row.title)} detail={detail} />;
            },
          },
          { label: "Adet", value: (row) => <span className="font-semibold text-slate-950">{numberText(row.quantity || 0)}</span> },
          { label: "Birim", value: (row) => orderMoneyText(row.unitGross, order.currency) },
          { label: "Toplam", value: (row) => <span className="font-semibold text-slate-950">{orderMoneyText(row.grossTotal, order.currency)}</span> },
        ]}
      />

      <DataSection
        title="Bu siparişe bağlı gönderiler"
        rows={order.shipments}
        empty="Bu sipariş için gönderi yok."
        columns={[
          {
            label: "Takip",
            value: (row) => (
              <Link className="font-semibold text-slate-950 hover:underline" to={`/customer/shipments/${encodeURIComponent(text(row.id))}`}>
                {text(row.trackingNumber) || "-"}
              </Link>
            ),
          },
          { label: "Etiket", value: (row) => <NameStack title={text(row.labelProductName)} detail={text(row.labelProductCarrier)} /> },
          { label: "Etiket durumu", value: (row) => statusBadge(text(row.labelGenerationStatus)) },
          { label: "Kaynak senkron", value: (row) => statusBadge(text(row.sourcePlatformTrackingSyncStatus)) },
          { label: "Durum", value: (row) => statusBadge(text(row.status)) },
          { label: "Tarih", value: (row) => dateText(row.createdAt) },
        ]}
      />
    </div>
  );
}

function TeamPage() {
  const [teamMembers, setTeamMembers] = useState<TeamMemberSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyMemberId, setBusyMemberId] = useState<string | null>(null);
  const [passwordDrafts, setPasswordDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({ email: "", firstName: "", lastName: "", password: "" });

  async function loadTeam() {
    setError(null);
    setLoading(true);
    try {
      const result = await apiClient.team();
      setTeamMembers(result.teamMembers || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Ekip üyeleri alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadTeam();
  }, []);

  async function createMember(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.createTeamMember(form);
      setNotice(result.message || "Ekip üyesi oluşturuldu.");
      setForm({ email: "", firstName: "", lastName: "", password: "" });
      await loadTeam();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Ekip üyesi oluşturulamadı.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleMember(member: TeamMemberSummary) {
    setBusyMemberId(member.id);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.updateTeamMember({ teamMemberId: member.id, active: !member.active });
      setNotice(result.message || "Ekip üyesi güncellendi.");
      await loadTeam();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Ekip üyesi güncellenemedi.");
    } finally {
      setBusyMemberId(null);
    }
  }

  async function saveMemberPassword(member: TeamMemberSummary) {
    const newPassword = String(passwordDrafts[member.id] || "");
    if (newPassword.length < 6) {
      setError("Yeni ekip üyesi şifresi en az 6 karakter olmalı.");
      return;
    }
    setBusyMemberId(`password:${member.id}`);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.setTeamMemberPassword({ teamMemberId: member.id, newPassword });
      setNotice(result.message || "Şifre güncellendi.");
      setPasswordDrafts((current) => ({ ...current, [member.id]: "" }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Şifre güncellenemedi.");
    } finally {
      setBusyMemberId(null);
    }
  }

  return (
    <div className="space-y-5">
      <CustomerPageHeader title="Ekip" caption="Alt müşteri hesaplarını, erişim durumlarını ve son giriş hareketlerini yönetin." icon={<Users className="h-5 w-5" />} />
      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}

      <AdminPanel title="Yeni ekip üyesi" caption="Ekibiniz için sınırlı müşteri hesabı oluşturun.">
        <form className="grid gap-3 p-5 lg:grid-cols-4" onSubmit={createMember}>
          <input
            type="email"
            required
            value={form.email}
            onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
            placeholder="E-posta"
          />
          <input
            value={form.firstName}
            onChange={(event) => setForm((current) => ({ ...current, firstName: event.target.value }))}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
            placeholder="Ad"
          />
          <input
            value={form.lastName}
            onChange={(event) => setForm((current) => ({ ...current, lastName: event.target.value }))}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
            placeholder="Soyad"
          />
          <div className="flex gap-2">
            <input
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
              className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
              placeholder="Şifre"
            />
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusSquare className="h-4 w-4" />}
              Ekle
            </button>
          </div>
        </form>
      </AdminPanel>

      <AdminPanel title="Ekip üyeleri" count={teamMembers.length}>
        {loading ? (
          <div className="p-5"><LoadingPanel label="Ekip üyeleri yükleniyor" /></div>
        ) : teamMembers.length === 0 ? (
          <div className="flex min-h-40 items-center justify-center p-8 text-sm font-medium text-slate-500">Henüz ekip üyesi yok.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {teamMembers.map((member) => (
              <div key={member.id} className="flex flex-col gap-3 p-5 xl:flex-row xl:items-center xl:justify-between">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-950">
                    {[member.firstName, member.lastName].filter(Boolean).join(" ") || member.email}
                  </p>
                  <p className="mt-1 truncate text-sm text-slate-500">
                    {member.email} · {member.active ? "Aktif" : "Pasif"}
                    {member.lastSignedIn ? ` · Son giriş ${formatDate(member.lastSignedIn)}` : ""}
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="flex min-w-0 gap-2">
                    <input
                      type="password"
                      minLength={6}
                      value={passwordDrafts[member.id] || ""}
                      onChange={(event) => setPasswordDrafts((current) => ({ ...current, [member.id]: event.target.value }))}
                      className="h-10 min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-slate-400"
                      placeholder="Yeni şifre"
                    />
                    <button
                      type="button"
                      disabled={busyMemberId === `password:${member.id}` || !passwordDrafts[member.id]}
                      onClick={() => void saveMemberPassword(member)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
                    >
                      {busyMemberId === `password:${member.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                      Şifre
                    </button>
                  </div>
                  <button
                    type="button"
                    disabled={busyMemberId === member.id}
                    onClick={() => void toggleMember(member)}
                    className="inline-flex w-fit items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
                  >
                    {busyMemberId === member.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                    {member.active ? "Pasifleştir" : "Aktifleştir"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminPanel>
    </div>
  );
}

type ProfileFormState = {
  billingCity: string;
  billingCountry: string;
  billingPostalCode: string;
  billingStreet1: string;
  companyName: string;
  emailTwoFactorEnabled: boolean;
  firstName: string;
  lastName: string;
  phone: string;
  senderCity: string;
  senderCountry: string;
  senderNameOverride: string;
  senderPostalCode: string;
  senderStreet1: string;
  taxId: string;
};

const emptyProfileForm: ProfileFormState = {
  billingCity: "",
  billingCountry: "",
  billingPostalCode: "",
  billingStreet1: "",
  companyName: "",
  emailTwoFactorEnabled: false,
  firstName: "",
  lastName: "",
  phone: "",
  senderCity: "",
  senderCountry: "",
  senderNameOverride: "",
  senderPostalCode: "",
  senderStreet1: "",
  taxId: "",
};

function fallbackProfileForm(user: SessionUser): ProfileFormState {
  return {
    ...emptyProfileForm,
    companyName: user.companyName || "",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
  };
}

function toProfileForm(user: SessionUser, profile: ProfileDetails): ProfileFormState {
  return {
    billingCity: profile.billingCity || "",
    billingCountry: profile.billingCountry || "",
    billingPostalCode: profile.billingPostalCode || "",
    billingStreet1: profile.billingStreet1 || "",
    companyName: user.companyName || "",
    emailTwoFactorEnabled: profile.emailTwoFactorEnabled,
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    phone: profile.phone || "",
    senderCity: profile.senderCity || "",
    senderCountry: profile.senderCountry || "",
    senderNameOverride: profile.senderNameOverride || "",
    senderPostalCode: profile.senderPostalCode || "",
    senderStreet1: profile.senderStreet1 || "",
    taxId: profile.taxId || "",
  };
}

function ProfileInput({
  label,
  onChange,
  required = false,
  type = "text",
  value,
}: {
  label: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  value: string;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold uppercase text-slate-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5"
      />
    </label>
  );
}

function profileInitials(user: SessionUser) {
  const parts = [user.firstName, user.lastName].map((part) => text(part)).filter(Boolean);
  if (parts.length > 0) return parts.map((part) => part.charAt(0)).join("").slice(0, 2).toLocaleUpperCase("tr-TR");
  return text(user.email).slice(0, 2).toLocaleUpperCase("tr-TR") || "KP";
}

function profileAddressLine(...parts: Array<string | null | undefined>) {
  const value = parts.map((part) => text(part)).filter(Boolean).join(", ");
  return value || "Henüz girilmedi";
}

function ProfileSummaryRow({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="flex min-w-0 items-start gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500 ring-1 ring-slate-200">{icon}</span>
      <span className="min-w-0">
        <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</span>
        <span className="mt-0.5 block break-words text-sm font-semibold text-slate-800">{value || "-"}</span>
      </span>
    </div>
  );
}

function ProfileSectionIntro({ icon, title, text: detail }: { icon: ReactNode; title: string; text: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-200/70">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-slate-600 shadow-sm ring-1 ring-slate-200">{icon}</span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-950">{title}</span>
        <span className="mt-0.5 block text-sm leading-5 text-slate-500">{detail}</span>
      </span>
    </div>
  );
}

function ProfilePage({ currentUser, onUserUpdated }: { currentUser: SessionUser; onUserUpdated: (user: SessionUser) => void }) {
  const [form, setForm] = useState<ProfileFormState>(() => fallbackProfileForm(currentUser));
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function setProfileField<K extends keyof ProfileFormState>(key: K, value: ProfileFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function loadProfile() {
    setError(null);
    setLoading(true);
    try {
      const result = await apiClient.profile();
      onUserUpdated(result.user);
      setForm(toProfileForm(result.user, result.profile));
    } catch (err) {
      setForm((current) => ({ ...fallbackProfileForm(currentUser), ...current }));
      setError(err instanceof ApiError ? err.message : "Profil bilgileri alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadProfile();
  }, []);

  async function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (currentUser.role === "customer" && form.phone.replace(/\D/g, "").length < 6) {
      setError("Telefon numarası zorunludur.");
      return;
    }
    setSavingProfile(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.updateProfile(form);
      onUserUpdated(result.user);
      setForm(toProfileForm(result.user, result.profile));
      setNotice("Profil güncellendi.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Profil güncellenemedi.");
    } finally {
      setSavingProfile(false);
    }
  }

  async function savePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError("Yeni şifreler eşleşmiyor.");
      return;
    }
    setSavingPassword(true);
    try {
      const result = await apiClient.updateProfilePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setNotice(result.message || "Şifre güncellendi.");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Şifre güncellenemedi.");
    } finally {
      setSavingPassword(false);
    }
  }

  const isCustomerOwner = currentUser.role === "customer";
  const profileName = displayName(currentUser);
  const billingAddress = profileAddressLine(form.billingStreet1, form.billingPostalCode, form.billingCity, form.billingCountry);
  const senderAddress = profileAddressLine(form.senderStreet1, form.senderPostalCode, form.senderCity, form.senderCountry);
  const headerAction = (
    <span
      className={cx(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold",
        currentUser.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"
      )}
    >
      <span className={cx("h-2 w-2 rounded-full", currentUser.active ? "bg-emerald-500" : "bg-red-500")} />
      {currentUser.active ? "Aktif hesap" : "Pasif hesap"}
    </span>
  );

  return (
    <div className="space-y-5">
      {currentUser.role === "admin" ? (
        <AdminPageHeader title="Profil" caption={currentUser.email} icon={<User className="h-5 w-5" />} action={headerAction} />
      ) : (
        <CustomerPageHeader title="Profil" caption={`${currentUser.email} hesabı için fatura, gönderici, güvenlik ve şifre ayarları.`} icon={<User className="h-5 w-5" />} action={headerAction} />
      )}
      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}

      {loading ? (
        <LoadingPanel label="Profil yükleniyor" />
      ) : (
        <>
          <form className="space-y-5" onSubmit={saveProfile}>
            <div className="grid gap-5 md:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)]">
              <aside className="space-y-5 xl:sticky xl:top-5 xl:self-start">
                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-lg font-semibold text-white shadow-sm">
                      {profileInitials(currentUser)}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-lg font-semibold text-slate-950">{profileName}</p>
                      <p className="mt-1 truncate text-sm text-slate-500">{form.companyName || roleLabels[currentUser.role]}</p>
                    </div>
                  </div>
                  <div className="mt-5 grid gap-2">
                    <ProfileSummaryRow icon={<Mail className="h-4 w-4" />} label="E-posta" value={currentUser.email} />
                    <ProfileSummaryRow icon={<Phone className="h-4 w-4" />} label="Telefon" value={form.phone || "Eksik"} />
                    <ProfileSummaryRow icon={<Building2 className="h-4 w-4" />} label="Firma" value={form.companyName || "-"} />
                    {isCustomerOwner ? <ProfileSummaryRow icon={<MapPin className="h-4 w-4" />} label="Fatura" value={billingAddress} /> : null}
                    {isCustomerOwner ? <ProfileSummaryRow icon={<FileText className="h-4 w-4" />} label="Vergi no" value={form.taxId || "Eksik"} /> : null}
                    {isCustomerOwner ? <ProfileSummaryRow icon={<Truck className="h-4 w-4" />} label="Gönderici" value={senderAddress} /> : null}
                  </div>
                </section>

                {isCustomerOwner ? (
                  <AdminPanel title="Güvenlik" caption="Giriş doğrulama tercihleri.">
                    <div className="space-y-4 p-5">
                      <ProfileSectionIntro icon={<ShieldCheck className="h-4 w-4" />} title="E-posta 2FA" text="Girişte e-posta doğrulama kodu istenir." />
                      <button
                        type="button"
                        role="switch"
                        aria-checked={form.emailTwoFactorEnabled}
                        onClick={() => setProfileField("emailTwoFactorEnabled", !form.emailTwoFactorEnabled)}
                        className="flex w-full items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition hover:border-slate-300 hover:bg-white"
                      >
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-slate-900">Durum</span>
                          <span className="mt-0.5 block text-xs text-slate-500">{form.emailTwoFactorEnabled ? "Aktif" : "Kapalı"}</span>
                        </span>
                        <span className={cx("relative h-7 w-12 rounded-full transition", form.emailTwoFactorEnabled ? "bg-slate-950" : "bg-slate-300")}>
                          <span className={cx("absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition", form.emailTwoFactorEnabled ? "left-6" : "left-1")} />
                        </span>
                      </button>
                    </div>
                  </AdminPanel>
                ) : null}
              </aside>

              <div className="space-y-5">
                <AdminPanel title="Hesap bilgileri" caption="Panelde görünen iletişim ve firma bilgileri.">
                  <div className="grid gap-4 p-5 md:grid-cols-2">
                    <ProfileInput label="Ad" value={form.firstName} onChange={(value) => setProfileField("firstName", value)} />
                    <ProfileInput label="Soyad" value={form.lastName} onChange={(value) => setProfileField("lastName", value)} />
                    <ProfileInput label="Firma" value={form.companyName} onChange={(value) => setProfileField("companyName", value)} />
                    <ProfileInput label="Telefon" value={form.phone} onChange={(value) => setProfileField("phone", value)} required={currentUser.role === "customer"} />
                  </div>
                </AdminPanel>

                {isCustomerOwner ? (
                  <div className="grid gap-5 xl:grid-cols-2">
                    <AdminPanel title="Fatura bilgileri" caption="Fatura adresi ve ülke bilgisi.">
                      <div className="space-y-4 p-5">
                        <ProfileSectionIntro icon={<Building2 className="h-4 w-4" />} title="Fatura adresi" text={billingAddress} />
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                          <div className="md:col-span-2 xl:col-span-1 2xl:col-span-2">
                            <ProfileInput label="Fatura adresi" value={form.billingStreet1} onChange={(value) => setProfileField("billingStreet1", value)} />
                          </div>
                          <ProfileInput label="Posta kodu" value={form.billingPostalCode} onChange={(value) => setProfileField("billingPostalCode", value)} />
                          <ProfileInput label="Şehir" value={form.billingCity} onChange={(value) => setProfileField("billingCity", value)} />
                          <ProfileInput label="Ülke" value={form.billingCountry} onChange={(value) => setProfileField("billingCountry", value)} />
                          <div className="md:col-span-2 xl:col-span-1 2xl:col-span-2">
                            <ProfileInput label="Vergi no" value={form.taxId} onChange={(value) => setProfileField("taxId", value)} />
                          </div>
                        </div>
                      </div>
                    </AdminPanel>

                    <AdminPanel title="Gönderici bilgileri" caption="Etiketlerde kullanılacak gönderici adresi.">
                      <div className="space-y-4 p-5">
                        <ProfileSectionIntro icon={<Truck className="h-4 w-4" />} title={form.senderNameOverride || "Gönderici"} text={senderAddress} />
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                          <ProfileInput label="Gönderici adı" value={form.senderNameOverride} onChange={(value) => setProfileField("senderNameOverride", value)} />
                          <div className="md:col-span-2 xl:col-span-1 2xl:col-span-2">
                            <ProfileInput label="Gönderici adresi" value={form.senderStreet1} onChange={(value) => setProfileField("senderStreet1", value)} />
                          </div>
                          <ProfileInput label="Posta kodu" value={form.senderPostalCode} onChange={(value) => setProfileField("senderPostalCode", value)} />
                          <ProfileInput label="Şehir" value={form.senderCity} onChange={(value) => setProfileField("senderCity", value)} />
                          <ProfileInput label="Ülke" value={form.senderCountry} onChange={(value) => setProfileField("senderCountry", value)} />
                        </div>
                      </div>
                    </AdminPanel>
                  </div>
                ) : null}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={savingProfile}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                  >
                    {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                    Profili kaydet
                  </button>
                </div>
              </div>
            </div>
          </form>

          <AdminPanel title="Şifre" caption="Hesap şifrenizi güvenli şekilde güncelleyin.">
            <form className="space-y-4 p-5" onSubmit={savePassword}>
              <ProfileSectionIntro icon={<KeyRound className="h-4 w-4" />} title="Şifre güncelle" text="Mevcut şifrenizi doğrulayarak yeni şifre belirleyin." />
              <div className="grid gap-4 md:grid-cols-3">
                <ProfileInput
                  label="Mevcut şifre"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(value) => setPasswordForm((current) => ({ ...current, currentPassword: value }))}
                />
                <ProfileInput
                  label="Yeni şifre"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(value) => setPasswordForm((current) => ({ ...current, newPassword: value }))}
                />
                <ProfileInput
                  label="Yeni şifre tekrar"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(value) => setPasswordForm((current) => ({ ...current, confirmPassword: value }))}
                />
              </div>
              <button
                type="submit"
                disabled={savingPassword}
                className="inline-flex min-h-11 w-fit items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
              >
                {savingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Şifreyi güncelle
              </button>
            </form>
          </AdminPanel>
        </>
      )}
    </div>
  );
}

function CustomerSupportPage() {
  const location = useLocation();
  const [form, setForm] = useState({ category: "technical", message: "", priority: "normal", subject: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function submitSupportRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.contactSupport({
        ...form,
        pageUrl: location.pathname,
      });
      setNotice(result.warning || `Talebiniz alındı. Referans: ${result.referenceId}`);
      setForm({ category: "technical", message: "", priority: "normal", subject: "" });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Destek talebi gönderilemedi.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <CustomerPageHeader title="Bize ulaşın" caption="Destek talepleri admin rapor ekranına referans numarasıyla düşer." icon={<MessageCircle className="h-5 w-5" />} />
      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}

      <AdminPanel title="Destek talebi" caption="Konu ve mesajı yazın; ekip bu referansla takip eder.">
        <form className="space-y-4 p-5" onSubmit={submitSupportRequest}>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-700">Kategori</span>
              <select
                value={form.category}
                onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-slate-400"
              >
                <option value="technical">Teknik sorun</option>
                <option value="label">Etiket / indirme</option>
                <option value="store">Mağaza bağlantısı</option>
                <option value="billing">Fatura / ödeme</option>
                <option value="account">Hesap</option>
                <option value="other">Diğer</option>
              </select>
            </label>
            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-700">Öncelik</span>
              <select
                value={form.priority}
                onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-slate-400"
              >
                <option value="normal">Normal</option>
                <option value="high">Yüksek</option>
                <option value="urgent">Acil</option>
              </select>
            </label>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700" htmlFor="support-subject">
              Konu
            </label>
            <input
              id="support-subject"
              required
              minLength={3}
              value={form.subject}
              onChange={(event) => setForm((current) => ({ ...current, subject: event.target.value }))}
              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700" htmlFor="support-message">
              Mesaj
            </label>
            <textarea
              id="support-message"
              required
              minLength={10}
              rows={7}
              value={form.message}
              onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))}
              className="mt-2 w-full resize-y rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
            Gönder
          </button>
        </form>
      </AdminPanel>
    </div>
  );
}

function AdminSupportPage() {
  const [data, setData] = useState<{
    dailyReport: DataRow[];
    requests: SupportRequestSummary[];
    summary: { last24hCount: number; last7dCount: number; priorityCount: number; todayCount: number; totalCount: number };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function loadSupport() {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.adminSupport();
      setData({
        dailyReport: Array.isArray(result.dailyReport) ? (result.dailyReport as unknown as DataRow[]) : [],
        requests: result.requests || [],
        summary: result.summary || { last24hCount: 0, last7dCount: 0, priorityCount: 0, todayCount: 0, totalCount: 0 },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Destek raporu alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadSupport();
  }, []);

  const summary = data?.summary;

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Destek merkezi"
        caption="Müşteri teknik destek talepleri, günlük rapor ve öncelikli kayıtlar."
        icon={<MessageCircle className="h-5 w-5" />}
        action={
          <button
            type="button"
            onClick={() => void loadSupport()}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
          >
            <Clock3 className="h-4 w-4" />
            Yenile
          </button>
        }
      />
      {error ? <ErrorBanner message={error} /> : null}
      {loading ? (
        <LoadingPanel label="Destek raporu yükleniyor" />
      ) : (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <AdminStatCard label="Bugün" value={summary?.todayCount || 0} tone="cyan" icon={<MessageCircle className="h-5 w-5" />} />
            <AdminStatCard label="Son 24 saat" value={summary?.last24hCount || 0} tone="emerald" icon={<Clock3 className="h-5 w-5" />} />
            <AdminStatCard label="Son 7 gün" value={summary?.last7dCount || 0} tone="violet" icon={<BarChart3 className="h-5 w-5" />} />
            <AdminStatCard label="Öncelikli" value={summary?.priorityCount || 0} tone="amber" icon={<AlertTriangle className="h-5 w-5" />} />
            <AdminStatCard label="Toplam" value={summary?.totalCount || 0} tone="slate" icon={<FileText className="h-5 w-5" />} />
          </section>

          <AdminPanel title="Günlük teknik talep raporu" count={data?.dailyReport?.length || 0}>
            <DataTable
              rows={data?.dailyReport || []}
              empty="Günlük destek raporu yok."
              columns={[
                { label: "Gün", value: (row) => text(row.day) || "-" },
                { label: "Talep", value: (row) => numberText(row.requestCount), align: "right" },
                { label: "Müşteri", value: (row) => numberText(row.customerCount), align: "right" },
                { label: "Yüksek", value: (row) => numberText(row.highCount), align: "right" },
                { label: "Acil", value: (row) => numberText(row.urgentCount), align: "right" },
                { label: "Son kayıt", value: (row) => dateText(row.latestAt) },
              ]}
            />
          </AdminPanel>

          <AdminPanel title="Son destek talepleri" count={data?.requests?.length || 0}>
            <DataTable
              rows={(data?.requests || []) as unknown as DataRow[]}
              empty="Destek talebi yok."
              columns={[
                { label: "Referans", value: (row) => <NameStack title={text(row.referenceId)} detail={dateText(row.createdAt)} /> },
                { label: "Müşteri", value: (row) => <NameStack title={text(row.companyName) || text(row.senderName) || text(row.email)} detail={text(row.email)} /> },
                { label: "Konu", value: (row) => <NameStack title={text(row.subject)} detail={text(row.message)} /> },
                { label: "Kategori", value: (row) => badge(text(row.category)) },
                { label: "Öncelik", value: (row) => statusBadge(text(row.priority)) },
                { label: "Sayfa", value: (row) => text(row.pagePath) || "-" },
              ]}
            />
          </AdminPanel>
        </>
      )}
    </div>
  );
}

function TrainingVideosPage({ admin = false }: { admin?: boolean }) {
  const [videos, setVideos] = useState<TrainingVideoSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiClient
      .trainingVideos()
      .then((result) => {
        if (active) setVideos(result.videos || []);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Eğitim videoları alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const Header = admin ? AdminPageHeader : CustomerPageHeader;

  return (
    <div className="space-y-5">
      <Header
        title="Eğitimler"
        caption="Kullanıcı video eğitimleri ve Almanca etiket indirme anlatımları."
        icon={<FileText className="h-5 w-5" />}
      />
      {error ? <ErrorBanner message={error} /> : null}
      {loading ? (
        <LoadingPanel label="Eğitimler yükleniyor" />
      ) : (
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {videos.map((video) => (
            <div key={video.id} className="flex min-h-56 flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="rounded-full border border-cyan-100 bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-700">{video.language}</span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-500">{video.category}</span>
              </div>
              <h2 className="mt-4 text-base font-semibold text-slate-950">{video.title}</h2>
              <p className="mt-2 flex-1 text-sm leading-6 text-slate-600">{video.description}</p>
              {video.available ? (
                <a
                  href={video.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
                >
                  <ExternalLink className="h-4 w-4" />
                  Videoyu aç
                </a>
              ) : (
                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
                  Video linki admin ortam değişkenine eklendiğinde burada açılacak.
                </div>
              )}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

function OrdersResourcePage({ title, endpoint, admin = false }: { title: string; endpoint: string; admin?: boolean }) {
  const [rows, setRows] = useState<DataRow[]>([]);
  const [meta, setMeta] = useState({ hasMore: false, limit: 50, offset: 0, returnedCount: 0, totalCount: 0 });
  const [summary, setSummary] = useState<DataRow>({});
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [labelState, setLabelState] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPage(0);
  }, [dateFrom, dateTo, labelState, platform, search, status]);

  const query = useMemo(() => {
    const params = new URLSearchParams({ limit: "50", offset: String(page * 50) });
    const normalizedSearch = search.trim();
    if (normalizedSearch) params.set("search", normalizedSearch);
    if (status !== "all") params.set("status", status);
    if (platform !== "all") params.set("platform", platform);
    if (labelState !== "all") params.set("labelState", labelState);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    return params;
  }, [dateFrom, dateTo, labelState, page, platform, search, status]);

  useEffect(() => {
    let active = true;
    const handle = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      apiClient
        .resource<{ meta?: DataRow; orders?: DataRow[]; summary?: DataRow }>(`${endpoint}?${query.toString()}`)
        .then((result) => {
          if (!active) return;
          setRows(Array.isArray(result.orders) ? result.orders : []);
          setMeta({
            hasMore: result.meta?.hasMore === true,
            limit: Number(result.meta?.limit || 50),
            offset: Number(result.meta?.offset || page * 50),
            returnedCount: Number(result.meta?.returnedCount || 0),
            totalCount: Number(result.meta?.totalCount || 0),
          });
          setSummary(result.summary || {});
        })
        .catch((err) => {
          if (active) setError(err instanceof ApiError ? err.message : "Siparişler alınamadı.");
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 180);
    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [endpoint, page, query]);

  const exportParams = new URLSearchParams(query);
  exportParams.set("export", "csv");

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title={title}
        caption="Sipariş kayıtları; arama, filtre, server-side sayfalama ve filtreli CSV export ile yönetilir."
        eyebrow={admin ? "Yönetim" : "Müşteri paneli"}
        icon={<ShoppingCart className="h-5 w-5" />}
        action={
          <a
            href={`${endpoint}?${exportParams.toString()}`}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
          >
            <Download className="h-4 w-4" />
            Tüm filtreli CSV
          </a>
        }
      />
      {error ? <ErrorBanner message={error} /> : null}
      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <AdminStatCard icon={<ShoppingCart className="h-5 w-5" />} label="Toplam sipariş" value={summary.total || 0} tone="slate" />
        <AdminStatCard icon={<Clock3 className="h-5 w-5" />} label="Açık sipariş" value={summary.open || 0} tone="amber" />
        <AdminStatCard icon={<Truck className="h-5 w-5" />} label="Etiketli" value={summary.withLabel || 0} tone="emerald" />
        <AdminStatCard icon={<AlertTriangle className="h-5 w-5" />} label="Etiket bekleyen" value={summary.waitingForLabel || 0} tone="red" />
        <AdminStatCard icon={<Store className="h-5 w-5" />} label={admin ? "İptal" : "Aktif mağaza"} value={admin ? summary.cancelled || 0 : summary.activeStores || 0} tone="cyan" />
      </section>
      <AdminPanel title="Siparişler" count={meta.totalCount}>
        <div className="grid gap-3 border-b border-slate-200 p-4 xl:grid-cols-[minmax(0,1fr)_160px_160px_170px_160px_160px]">
          <div className="relative min-w-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Sipariş no, alıcı veya dış ID ara"
              className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-950 outline-none focus:border-slate-400 focus:bg-white"
            />
          </div>
          <select value={status} onChange={(event) => setStatus(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
            <option value="all">Tüm durumlar</option>
            <option value="open">Açık</option>
            <option value="shipped">Etiketli</option>
            <option value="cancelled">İptal</option>
          </select>
          <select value={platform} onChange={(event) => setPlatform(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
            <option value="all">Tüm platformlar</option>
            {storePlatformOptions.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          <select value={labelState} onChange={(event) => setLabelState(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
            <option value="all">Tüm etiket durumları</option>
            <option value="waiting">Etiket bekliyor</option>
            <option value="with_label">Etiket oluşturuldu</option>
          </select>
          <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400" />
          <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400" />
        </div>
        <div className="flex flex-wrap gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
          <button type="button" onClick={() => setStatus("open")} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">Açık siparişler</button>
          <button type="button" onClick={() => setLabelState("with_label")} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">Etiket oluşturulan</button>
          <button type="button" onClick={() => setLabelState("waiting")} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">Etiket bekliyor</button>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Siparişler yükleniyor
          </div>
        ) : (
          <DataTable
            rows={rows}
            empty="Sipariş yok."
            columns={[
              { label: "Sipariş", value: (row) => <NameStack title={text(row.orderNumber)} detail={text(row.externalOrderId)} /> },
              { label: "Alıcı", value: (row) => <NameStack title={text(row.recipientName)} detail={`${text(row.city)}, ${text(row.countryCode)}`} /> },
              { label: "Platform", value: (row) => badge(text(row.sourcePlatform)) },
              { label: "Adres kaynağı", value: (row) => <NameStack title={orderAddressSourceLabel(text(row.selectedAddressSource))} detail={row.billingAddressDiffers ? "Fatura adresi farklı" : ""} /> },
              ...(admin ? [{ label: "Müşteri", value: (row: DataRow) => text(row.userCompanyName) || text(row.userEmail) }] : []),
              { label: "Gönderi", value: (row) => numberText(row.shipmentCount), align: "right" },
              { label: "Durum", value: (row) => statusBadge(text(row.status)) },
              { label: "Tarih", value: (row) => dateText(row.createdAt) },
            ]}
          />
        )}
        <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50 px-5 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
          <span>
            {rows.length === 0
              ? "Kayıt yok"
              : `${numberText(meta.offset + 1)}-${numberText(meta.offset + rows.length)} / ${numberText(meta.totalCount)} kayıt`}
          </span>
          <div className="flex gap-2">
            <button type="button" disabled={loading || page === 0} onClick={() => setPage((current) => Math.max(0, current - 1))} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50">
              Önceki
            </button>
            <button type="button" disabled={loading || !meta.hasMore} onClick={() => setPage((current) => current + 1)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50">
              Sonraki
            </button>
          </div>
        </div>
      </AdminPanel>
    </div>
  );
}

const shipmentPageSizeOptions = [50, 100, 250];

function shipmentHasDownload(row: DataRow) {
  return Boolean(text(row.id) && (text(row.labelDownloadUrl) || text(row.labelPdfDownloadUrl) || text(row.labelZipDownloadUrl)));
}

function isCancelledShipmentRow(row: DataRow) {
  const status = text(row.status).toLowerCase();
  if (status === "void") return text(row.labelGenerationStatus).toLowerCase() !== "failed";
  return ["cancelled", "canceled"].includes(status);
}

function isTechnicalVoidShipmentRow(row: DataRow) {
  return text(row.status).toLowerCase() === "void" && text(row.labelGenerationStatus).toLowerCase() === "failed";
}

function shipmentCancelBlockReason(row: DataRow) {
  if (!text(row.id)) return "Gönderi kaydı bulunamadı.";
  if (isTechnicalVoidShipmentRow(row)) return "Teknik hata kayıtları müşteri iptaliyle krediye alınamaz.";
  if (isCancelledShipmentRow(row)) return "Bu gönderi zaten iptal edilmiş veya kapalı.";
  return "";
}

function shipmentCanCancel(row: DataRow) {
  return !shipmentCancelBlockReason(row);
}

type BulkShipmentArchiveRecord = {
  createdAt?: string | null;
  fileName?: string | null;
  fileNames?: string[];
  format?: "zip" | "pdf" | string;
  hasCombinedPdf?: boolean;
  id: string;
  includeRasterArtifacts?: boolean;
  shipmentCount?: number;
  shipmentIds: string[];
  source?: "orders" | "manual" | "other" | string;
  trackingNumbers?: string[];
};

function shipmentBulkHref(shipmentIds: string[], source?: "orders" | "manual" | "other") {
  const params = new URLSearchParams({
    combined: "1",
    shipmentIds: shipmentIds.join(","),
    variant: "zip",
  });
  if (source) params.set("source", source);
  return `/api/shipments/label-download?${params.toString()}`;
}

function triggerBulkShipmentDownload(shipmentIds: string[], source: "orders" | "manual" | "other" = "other") {
  const uniqueIds = Array.from(new Set(shipmentIds.map(text).filter(Boolean)));
  if (uniqueIds.length === 0 || typeof document === "undefined") return;
  const link = document.createElement("a");
  link.href = shipmentBulkHref(uniqueIds, source);
  link.download = "";
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  window.setTimeout(() => link.remove(), 0);
}

async function bulkDownloadError(response: Response, fallback: string) {
  const payload = await response
    .clone()
    .json()
    .catch(() => null) as { error?: unknown; message?: unknown } | null;
  return text(payload?.error) || text(payload?.message) || fallback;
}

async function fetchShipmentPdfForMerge(shipmentId: string) {
  let lastError: unknown = null;
  for (let attempt = 1; attempt <= combinedPdfFetchMaxAttempts; attempt += 1) {
    try {
      const response = await fetch(shipmentLabelHref(shipmentId, "normal", true), { credentials: "same-origin" });
      if (!response.ok) {
        const message = await bulkDownloadError(response, `Etiket PDF alınamadı (${response.status}).`);
        const error = new ApiError(message, response.status);
        if (![404, 408, 409, 425, 429].includes(response.status) && response.status < 500) throw error;
        lastError = error;
      } else {
        const bytes = new Uint8Array(await response.arrayBuffer());
        if (bytes.byteLength === 0) throw new Error("Etiket PDF boş döndü.");
        return bytes;
      }
    } catch (error) {
      lastError = error;
      if (error instanceof ApiError && ![404, 408, 409, 425, 429].includes(error.status) && error.status < 500) throw error;
    }
    if (attempt < combinedPdfFetchMaxAttempts) await waitForBulkRetry(600 * attempt);
  }
  throw lastError || new Error("Etiket PDF alınamadı.");
}

type CombinedShipmentPdfDownloadResult = {
  downloadedShipmentIds: string[];
  failedShipmentIds: string[];
};

async function triggerCombinedShipmentPdfDownload(
  shipmentIds: string[],
  onProgress?: (completed: number) => void,
  filePart = ""
): Promise<CombinedShipmentPdfDownloadResult> {
  const uniqueIds = Array.from(new Set(shipmentIds.map(text).filter(Boolean)));
  if (uniqueIds.length === 0) throw new Error("Birleştirilecek etiket bulunamadı.");
  if (uniqueIds.length > maxBulkLabelOrders) {
    throw new Error(`Tek PDF içinde en fazla ${numberText(maxBulkLabelOrders)} etiket indirilebilir.`);
  }
  if (typeof document === "undefined" || typeof window === "undefined") throw new Error("PDF indirme yalnızca tarayıcıda yapılabilir.");

  const { PDFDocument } = await import("pdf-lib");
  const combinedPdf = await PDFDocument.create();
  const downloadedShipmentIds: string[] = [];
  const failedShipmentIds: string[] = [];
  for (let index = 0; index < uniqueIds.length; index += 1) {
    const shipmentId = uniqueIds[index];
    try {
      const sourceBytes = await fetchShipmentPdfForMerge(shipmentId);
      const sourcePdf = await PDFDocument.load(sourceBytes, { ignoreEncryption: true, updateMetadata: false });
      const copiedPages = await combinedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
      if (copiedPages.length === 0) throw new Error("Etiket PDF içinde sayfa bulunamadı.");
      copiedPages.forEach((page) => combinedPdf.addPage(page));
      downloadedShipmentIds.push(shipmentId);
    } catch (error) {
      console.warn("Shipment PDF could not be included in combined download", { shipmentId, error });
      failedShipmentIds.push(shipmentId);
    } finally {
      onProgress?.(index + 1);
    }
    if ((index + 1) % combinedPdfBrowserBatchSize === 0) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
    }
  }
  if (combinedPdf.getPageCount() === 0) {
    throw new Error("Etiket PDF'leri henüz hazır değil. Son toplu işlemden kısa süre sonra yeniden indirmeyi deneyin.");
  }
  assertCompleteBulkLabelPdf(failedShipmentIds);

  const mergedBytes = await combinedPdf.save({ objectsPerTick: 20 });
  const mergedBuffer = mergedBytes.buffer.slice(mergedBytes.byteOffset, mergedBytes.byteOffset + mergedBytes.byteLength) as ArrayBuffer;
  const blobUrl = URL.createObjectURL(new Blob([mergedBuffer], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = `kenpost-labels-${new Date().toISOString().slice(0, 10)}${filePart}.pdf`;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  window.setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(blobUrl);
  }, 30_000);
  return { downloadedShipmentIds, failedShipmentIds };
}

async function triggerOrderBulkShipmentPdfDownload(shipmentIds: string[], onProgress?: (completed: number) => void) {
  return downloadBulkOrderLabelPdfBatches(shipmentIds, async (ids, part, parts, progress) => {
    const result = await triggerCombinedShipmentPdfDownload(ids, progress, parts > 1 ? `-part-${part}-of-${parts}` : "");
    await recordCombinedBulkArchive(result.downloadedShipmentIds, "orders").catch((error) => {
      console.warn("Combined bulk PDF history could not be recorded", error);
    });
    return result;
  }, onProgress);
}

async function recordCombinedBulkArchive(shipmentIds: string[], source: "orders" | "manual" | "other") {
  const response = await fetch("/api/shipments/bulk-archives", {
    body: JSON.stringify({ shipmentIds, source }),
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    method: "POST",
  });
  if (!response.ok) {
    throw new ApiError(await bulkDownloadError(response, "Toplu PDF geçmişe kaydedilemedi."), response.status);
  }
}

function shipmentTrackingHref(trackingNumber: string) {
  const code = trackingNumber.trim();
  const upperCode = code.toUpperCase();
  if (!code || upperCode.startsWith("VOID-") || upperCode.startsWith("CANCELLED-") || upperCode.startsWith("FAILED-")) return "";
  return `https://www.dhl.de/de/privatkunden/dhl-sendungsverfolgung.html?piececode=${encodeURIComponent(code)}`;
}

function shipmentTypeLabel(value: unknown) {
  return text(value).toLowerCase() === "return" ? "İade" : "Giden";
}

function regenerateShipmentConfirmation(row: DataRow): ConfirmationDialogRequest {
  const tracking = text(row.trackingNumber) || text(row.orderNumber) || text(row.id) || "Bu gönderi";
  const productText = [text(row.labelProductName), text(row.labelProductCarrier)].filter(Boolean).join(" · ");
  return {
    title: "Etiket PDF’si yeniden oluşturulsun mu?",
    description: `${tracking} için PDF/ZIP dosyaları yeniden oluşturulacak.`,
    details: [
      "Bu işlem yeni etiket ürünü seçmez; aynı takip numarası ve mevcut etiket kaynağı kullanılır.",
      ...(productText ? [`Mevcut etiket: ${productText}`] : []),
      "Yeni takip numarası gerekiyorsa gönderi menüsündeki ‘Yeni takip numarasıyla değiştir’ işlemini kullanın.",
    ],
    confirmLabel: "PDF’yi yeniden oluştur",
    tone: "warning",
  };
}

function ShipmentsResourcePage({ title, endpoint, admin = false }: { title: string; endpoint: string; admin?: boolean }) {
  const [rows, setRows] = useState<DataRow[]>([]);
  const [meta, setMeta] = useState({ hasMore: false, limit: 50, offset: 0, returnedCount: 0, totalCount: 0 });
  const [summary, setSummary] = useState<DataRow>({});
  const [labelProducts, setLabelProducts] = useState<LabelProductSummary[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [syncStatus, setSyncStatus] = useState("all");
  const [carrierStatus, setCarrierStatus] = useState("all");
  const [shipmentType, setShipmentType] = useState("all");
  const [carrier, setCarrier] = useState("all");
  const [labelProductId, setLabelProductId] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [amazonCarrierRepairing, setAmazonCarrierRepairing] = useState(false);
  const [amazonCarrierRepairStatus, setAmazonCarrierRepairStatus] = useState<{
    failed: number;
    pending: number;
    remaining: number;
    repaired: number;
    retryableFailed: number;
    total: number;
  } | null>(null);
  const [bulkCanceling, setBulkCanceling] = useState(false);
  const [bulkCancelProgress, setBulkCancelProgress] = useState({ completed: 0, failed: 0, total: 0 });
  const [rowActionId, setRowActionId] = useState("");
  const [shipmentActionMenuState, setShipmentActionMenuState] = useState<{
    left: number;
    maxHeight: number;
    openAbove: boolean;
    row: DataRow;
    top: number;
  } | null>(null);
  const [carrierDetailsRow, setCarrierDetailsRow] = useState<DataRow | null>(null);
  const [selectedShipmentIds, setSelectedShipmentIds] = useState<string[]>([]);
  const [selectingAllShipments, setSelectingAllShipments] = useState(false);
  const [combinedShipmentPdfProgress, setCombinedShipmentPdfProgress] = useState({ completed: 0, total: 0 });
  const [combiningShipmentPdf, setCombiningShipmentPdf] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const amazonCarrierRepairStopRef = useRef(false);
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  useEffect(() => {
    setPage(0);
    setSelectedShipmentIds([]);
    setShipmentActionMenuState(null);
  }, [carrier, carrierStatus, dateFrom, dateTo, labelProductId, pageSize, platform, search, shipmentType, sortBy, status, syncStatus]);

  useEffect(() => {
    if (!shipmentActionMenuState) return;
    const close = () => setShipmentActionMenuState(null);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [shipmentActionMenuState]);

  useEffect(() => {
    let active = true;
    const request = admin ? apiClient.adminLabelProducts() : apiClient.labelProducts();
    request
      .then((result) => {
        if (active) setLabelProducts((result.products || []) as LabelProductSummary[]);
      })
      .catch(() => {
        if (active) setLabelProducts([]);
      });
    return () => {
      active = false;
    };
  }, [admin]);

  useEffect(() => {
    if (!admin) return;
    let active = true;
    apiClient
      .amazonDhlGermanyRepairStatus()
      .then((result) => {
        if (active) setAmazonCarrierRepairStatus(result.status);
      })
      .catch(() => undefined);
    return () => {
      active = false;
      amazonCarrierRepairStopRef.current = true;
    };
  }, [admin]);

  function shipmentListParams(limit: number, offset: number) {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    const normalizedSearch = search.trim();
    if (normalizedSearch) params.set("search", normalizedSearch);
    if (status !== "all") params.set("status", status);
    if (syncStatus !== "all") params.set("syncStatus", syncStatus);
    if (carrierStatus !== "all") params.set("carrierStatus", carrierStatus);
    if (labelProductId !== "all") params.set("labelProductId", labelProductId);
    if (sortBy !== "newest") params.set("sortBy", sortBy);
    if (!admin) {
      if (shipmentType !== "all") params.set("shipmentType", shipmentType);
      if (carrier !== "all") params.set("carrier", carrier);
      if (platform !== "all") params.set("platform", platform);
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
    }
    return params;
  }

  async function loadShipments() {
    setLoading(true);
    setError(null);
    try {
      const params = shipmentListParams(pageSize, page * pageSize);
      const result = await apiClient.resource<{ meta?: DataRow; shipments?: DataRow[]; summary?: DataRow }>(`${endpoint}?${params.toString()}`);
      setRows(Array.isArray(result.shipments) ? result.shipments : []);
      setMeta({
        hasMore: result.meta?.hasMore === true,
        limit: Number(result.meta?.limit || pageSize),
        offset: Number(result.meta?.offset || page * pageSize),
        returnedCount: Number(result.meta?.returnedCount || 0),
        totalCount: Number(result.meta?.totalCount || 0),
      });
      setSummary(result.summary || {});
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Gönderiler alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let active = true;
    const handle = window.setTimeout(() => {
      if (active) void loadShipments();
    }, 180);
    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [admin, carrier, carrierStatus, dateFrom, dateTo, endpoint, labelProductId, page, pageSize, platform, refreshTick, search, shipmentType, sortBy, status, syncStatus]);

  async function queueBulkTracking() {
    setBulkSyncing(true);
    setError(null);
    setNotice(null);
    try {
      const payload =
        selectedShipmentIds.length > 0
          ? { shipmentIds: selectedShipmentIds, syncStatus: syncStatus === "all" ? undefined : syncStatus }
          : { limit: 100, syncStatus: syncStatus === "all" ? undefined : syncStatus };
      const result = await apiClient.bulkSyncShipmentTracking(payload, admin);
      setNotice(
        selectedShipmentIds.length > 0
          ? `${numberText(result.requestedCount)} seçili gönderi takip senkron kuyruğuna alındı.`
          : `${numberText(result.requestedCount)} takip kaydı arka planda senkron kuyruğuna alındı.`
      );
      window.setTimeout(() => void loadShipments(), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Toplu takip senkronu başlatılamadı.");
    } finally {
      setBulkSyncing(false);
    }
  }

  async function runAmazonDhlGermanyRepair() {
    setError(null);
    setNotice(null);
    try {
      const current = await apiClient.amazonDhlGermanyRepairStatus();
      setAmazonCarrierRepairStatus(current.status);
      if (current.status.remaining === 0) {
        setNotice(
          current.status.failed > 0
            ? `Amazon DHL Germany taraması tamamlandı; ${numberText(current.status.failed)} kayıt inceleme bekliyor.`
            : "Aktif Amazon gönderilerinin DHL Germany kontrolü tamamlanmış."
        );
        return;
      }
      const confirmed = await askConfirmation({
        title: "Amazon takip kayıtları onarılsın mı?",
        description: `${numberText(current.status.remaining)} aktif Amazon gönderisi ortak Shopify mağazasında kontrol edilecek.`,
        details: [
          "Taşıyıcı tam olarak DHL Germany yapılır ve Salestio bu bilgiyi Amazon'a iletir.",
          "İptal, iade, teknik void ve hatalı üretim kayıtları kapsam dışındadır.",
          "Mevcut fulfillment yerinde güncellenir; müşteriye tekrar kargo e-postası gönderilmez.",
        ],
        confirmLabel: "Kontrolü başlat",
        tone: "warning",
      });
      if (!confirmed) return;

      amazonCarrierRepairStopRef.current = false;
      setAmazonCarrierRepairing(true);
      let latestStatus = current.status;
      while (!amazonCarrierRepairStopRef.current && latestStatus.remaining > 0) {
        const result = await apiClient.repairAmazonDhlGermanyBatch({ batchSize: 25 });
        latestStatus = result.status;
        setAmazonCarrierRepairStatus(latestStatus);
        if (result.processed === 0) break;
        await new Promise((resolve) => window.setTimeout(resolve, 150));
      }

      setNotice(
        amazonCarrierRepairStopRef.current
          ? `Amazon DHL Germany kontrolü durduruldu. ${numberText(latestStatus.remaining)} kayıt daha bekliyor.`
          : latestStatus.failed > 0
            ? `Amazon DHL Germany kontrolü tamamlandı. ${numberText(latestStatus.repaired)} kayıt doğrulandı, ${numberText(latestStatus.failed)} kayıt inceleme bekliyor.`
            : `${numberText(latestStatus.repaired)} aktif Amazon takip kaydı DHL Germany olarak doğrulandı.`
      );
      setRefreshTick((value) => value + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Amazon DHL Germany kontrolü tamamlanamadı.");
    } finally {
      setAmazonCarrierRepairing(false);
    }
  }

  async function retryFailedAmazonDhlGermanyRepairs() {
    amazonCarrierRepairStopRef.current = false;
    setAmazonCarrierRepairing(true);
    setError(null);
    setNotice(null);
    try {
      let processed = 0;
      let latestStatus = await apiClient.amazonDhlGermanyRepairStatus().then((result) => result.status);
      setAmazonCarrierRepairStatus(latestStatus);
      while (!amazonCarrierRepairStopRef.current && latestStatus.retryableFailed > 0) {
        const result = await apiClient.repairAmazonDhlGermanyBatch({ batchSize: 25, retryFailed: true });
        processed += result.processed;
        latestStatus = result.status;
        setAmazonCarrierRepairStatus(latestStatus);
        if (result.processed === 0) break;
        await new Promise((resolve) => window.setTimeout(resolve, 250));
      }
      setNotice(
        processed > 0
          ? `${numberText(processed)} geçici Shopify hız limiti kaydı yeniden kontrol edildi. ${numberText(latestStatus.failed - latestStatus.retryableFailed)} kayıt manuel incelemede kaldı.`
          : "Yeniden kontrol edilecek sorunlu Amazon kaydı bulunamadı."
      );
      setRefreshTick((value) => value + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sorunlu Amazon kayıtları yeniden kontrol edilemedi.");
    } finally {
      setAmazonCarrierRepairing(false);
    }
  }

  async function syncSingleShipment(row: DataRow) {
    const shipmentId = text(row.id);
    if (!shipmentId) return;
    setRowActionId(`sync:${shipmentId}`);
    setError(null);
    setNotice(null);
    try {
      const result = admin ? await apiClient.adminSyncShipmentTracking(shipmentId) : await apiClient.syncShipmentTracking(shipmentId);
      setNotice(result.message || `Takip senkron sonucu: ${result.status}`);
      setRefreshTick((value) => value + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Takip senkronu başlatılamadı.");
    } finally {
      setRowActionId("");
    }
  }

  async function regenerateSingleShipment(row: DataRow) {
    const shipmentId = text(row.id);
    if (!shipmentId || admin) return;
    if (!(await askConfirmation(regenerateShipmentConfirmation(row)))) return;
    setRowActionId(`regenerate:${shipmentId}`);
    setError(null);
    setNotice(null);
    try {
      await apiClient.regenerateShipment({ shipmentId, confirmed: true });
      setNotice("Aynı takip numaralı etiket PDF/ZIP dosyaları yeniden oluşturuldu.");
      setRefreshTick((value) => value + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etiket yeniden oluşturulamadı.");
    } finally {
      setRowActionId("");
    }
  }

  async function replaceSingleShipmentTracking(row: DataRow) {
    const shipmentId = text(row.id);
    if (!shipmentId || admin) return;
    const blockReason = shipmentCancelBlockReason(row);
    if (blockReason) {
      setError(blockReason);
      return;
    }
    const currentTracking = text(row.trackingNumber) || shipmentId;
    const confirmed = await askConfirmation({
      title: "Yeni takip numaralı etiket oluşturulsun mu?",
      description: `${currentTracking} iptal edilip aynı sipariş için farklı takip numaralı yeni etiket oluşturulacak.`,
      details: [
        "DHL durumu işlem sırasında yeniden kontrol edilir; taşıyıcı hareketi bulunan etiket değiştirilemez.",
        "Eski etiket kredisi hesaba geri döner ve yeni etiket farklı bir kaynaktan oluşturulur.",
        "Yeni takip numarası bağlı satış kanalına otomatik gönderilir.",
      ],
      confirmLabel: "Yeni takip numarası oluştur",
      tone: "warning",
    });
    if (!confirmed) return;

    setRowActionId(`replace:${shipmentId}`);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.replaceShipmentTracking({ confirmed: true, shipmentId });
      triggerShipmentLabelDownload(result.newShipmentId);
      setNotice(
        `${result.oldTrackingNumber} iptal edildi; ${result.newTrackingNumber} takip numaralı yeni etiket oluşturuldu ve indirme başlatıldı.` +
          (result.warning ? ` Uyarı: ${result.warning}` : "")
      );
      setSelectedShipmentIds((current) => current.filter((id) => id !== shipmentId));
      setRefreshTick((value) => value + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Yeni takip numaralı etiket oluşturulamadı.");
    } finally {
      setRowActionId("");
    }
  }

  async function cancelSingleShipment(row: DataRow) {
    const shipmentId = text(row.id);
    if (!shipmentId) return;
    const blockReason = shipmentCancelBlockReason(row);
    if (blockReason) {
      setError(blockReason);
      return;
    }
    const label = text(row.trackingNumber) || shipmentId;
    const confirmed = await askConfirmation({
      title: "Gönderi iptal edilsin mi?",
      description: `${label} gönderisi iptal edilecek.`,
      details: [
        "DHL durumu iptal sırasında canlı olarak yeniden kontrol edilir.",
        "Sadece DHL hareketi olmayan / Status offen etiketler iptal edilebilir.",
        "Etiket kredisi stoğa geri döner; sipariş yalnızca gidiş gönderisi iptalinde yeniden etiket bekleyen duruma alınır.",
        ...(text(row.sourcePlatform) === "amazon_v2" ? ["Amazon siparişi iptal edilmez. Mevcut takip bilgisi aynı sipariş için yeni etiket oluşturulduğunda güncellenir."] : []),
      ],
      confirmLabel: "Gönderiyi iptal et",
      tone: "danger",
    });
    if (!confirmed) return;
    setRowActionId(`cancel:${shipmentId}`);
    setError(null);
    setNotice(null);
    try {
      const result = admin ? await apiClient.adminCancelShipment(shipmentId) : await apiClient.cancelShipment(shipmentId);
      setNotice(result.warning || (admin ? "Gönderi iptal edildi; etiket kredisi ilgili müşteri hesabına geri döndü." : "Gönderi iptal edildi; etiket kredisi hesabınıza geri döndü ve sipariş tekrar etiket bekliyor."));
      setSelectedShipmentIds((current) => current.filter((id) => id !== shipmentId));
      setRefreshTick((value) => value + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Gönderi iptal edilemedi.");
    } finally {
      setRowActionId("");
    }
  }

  async function cancelSelectedShipments() {
    if (admin || bulkCanceling) return;
    const candidates = selectedShipmentIds.filter(Boolean);
    if (candidates.length === 0) return;
    const visibleVoidedIds = new Set(rows.filter((row) => !shipmentCanCancel(row)).map((row) => text(row.id)).filter(Boolean));
    const shipmentIds = candidates.filter((id) => !visibleVoidedIds.has(id));
    if (shipmentIds.length === 0) {
      setError("İptal edilecek aktif gönderi seçilmedi.");
      return;
    }
    const confirmed = await askConfirmation({
      title: "Seçili gönderiler iptal edilsin mi?",
      description: `${numberText(shipmentIds.length)} seçili gönderi sırayla iptal edilecek.`,
      details: [
        "Her etiketin DHL durumu iptal sırasında canlı olarak yeniden kontrol edilir.",
        "Sadece DHL hareketi olmayan / Status offen etiketler iptal edilebilir.",
        "Her başarılı iptalden sonra etiket kredisi stoğa geri döner; yalnızca gidiş siparişleri yeniden açılır. Hata alan kayıtlar seçimde bırakılır.",
      ],
      confirmLabel: "Seçili gönderileri iptal et",
      tone: "danger",
    });
    if (!confirmed) return;

    setBulkCanceling(true);
    setBulkCancelProgress({ completed: 0, failed: 0, total: shipmentIds.length });
    setError(null);
    setNotice(`${numberText(shipmentIds.length)} gönderi için iptal kuyruğu başlatıldı.`);

    const successfulIds: string[] = [];
    const failureMessages: string[] = [];
    const cancellationWarnings = new Set<string>();
    let failed = 0;
    for (const shipmentId of shipmentIds) {
      try {
        const result = await apiClient.cancelShipment(shipmentId);
        if (result.warning) cancellationWarnings.add(result.warning);
        successfulIds.push(shipmentId);
      } catch (err) {
        failed += 1;
        failureMessages.push(err instanceof ApiError ? err.message : "Gönderi iptal edilemedi.");
      }
      setBulkCancelProgress((current) => ({
        ...current,
        completed: Math.min(current.completed + 1, shipmentIds.length),
        failed,
      }));
    }

    setSelectedShipmentIds((current) => current.filter((id) => !successfulIds.includes(id)));
    setBulkCanceling(false);
    setNotice(
      (failed > 0
        ? `${numberText(successfulIds.length)} gönderi iptal edildi, ${numberText(failed)} gönderi iptal edilemedi. Kalan seçimleri kontrol edin.`
        : `${numberText(successfulIds.length)} gönderi başarıyla iptal edildi ve etiketler stoğa geri döndü.`) +
          (cancellationWarnings.size ? ` ${[...cancellationWarnings].join(" ")}` : "")
    );
    if (failureMessages.length > 0) {
      const uniqueMessages = Array.from(new Set(failureMessages));
      setError(
        uniqueMessages.length > 1
          ? `${uniqueMessages[0]} (+${numberText(uniqueMessages.length - 1)} farklı hata)`
          : uniqueMessages[0]
      );
    }
    setRefreshTick((value) => value + 1);
  }

  function toggleShipmentSelection(row: DataRow, checked: boolean) {
    const shipmentId = text(row.id);
    if (!shipmentId) return;
    if (checked && (!shipmentCanCancel(row) || !shipmentHasDownload(row))) return;
    setSelectedShipmentIds((current) => {
      if (checked) return Array.from(new Set([...current, shipmentId]));
      return current.filter((id) => id !== shipmentId);
    });
  }

  function selectPageRows() {
    const pageIds = rows.filter((row) => shipmentCanCancel(row) && shipmentHasDownload(row)).map((row) => text(row.id)).filter(Boolean);
    if (pageIds.length === 0) return;
    setSelectedShipmentIds((current) => Array.from(new Set([...current, ...pageIds])));
  }

  async function selectAllFilteredShipments() {
    if (admin || selectingAllShipments) return;
    setSelectingAllShipments(true);
    setError(null);
    setNotice(null);
    try {
      const selectedIds: string[] = [];
      const seenIds = new Set<string>();
      let offset = 0;
      let totalCount = 0;
      let hasMore = true;
      while (hasMore && selectedIds.length < maxBulkLabelOrders) {
        const params = shipmentListParams(250, offset);
        const result = await apiClient.resource<{ meta?: DataRow; shipments?: DataRow[] }>(`${endpoint}?${params.toString()}`);
        const batch = Array.isArray(result.shipments) ? result.shipments : [];
        totalCount = Number(result.meta?.totalCount || totalCount || batch.length);
        for (const row of batch) {
          const shipmentId = text(row.id);
          if (!shipmentId || seenIds.has(shipmentId)) continue;
          seenIds.add(shipmentId);
          if (shipmentCanCancel(row) && shipmentHasDownload(row)) selectedIds.push(shipmentId);
          if (selectedIds.length >= maxBulkLabelOrders) break;
        }
        const returnedCount = Number(result.meta?.returnedCount || batch.length);
        hasMore = result.meta?.hasMore === true && returnedCount > 0;
        offset += returnedCount;
      }
      setSelectedShipmentIds(selectedIds.slice(0, maxBulkLabelOrders));
      if (selectedIds.length === 0) {
        setError("Filtrelenen sonuçlarda indirilebilir aktif gönderi bulunamadı.");
      } else if (totalCount > maxBulkLabelOrders) {
        setNotice(
          `${numberText(totalCount)} filtrelenmiş sonuçtan tek PDF güvenlik sınırı olan ilk ${numberText(maxBulkLabelOrders)} indirilebilir gönderi seçildi.`
        );
      } else if (totalCount > selectedIds.length) {
        setNotice(
          `${numberText(selectedIds.length)} indirilebilir aktif gönderi seçildi; ${numberText(totalCount - selectedIds.length)} uygun olmayan kayıt atlandı.`
        );
      } else {
        setNotice(`${numberText(selectedIds.length)} filtrelenmiş gönderinin tamamı seçildi.`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Filtrelenen gönderilerin tamamı seçilemedi.");
    } finally {
      setSelectingAllShipments(false);
    }
  }

  async function downloadSelectedShipmentsAsOnePdf() {
    if (combiningShipmentPdf || selectedShipmentIds.length === 0) return;
    setCombiningShipmentPdf(true);
    setCombinedShipmentPdfProgress({ completed: 0, total: selectedShipmentIds.length });
    setError(null);
    setNotice("Seçili etiketler tek PDF dosyasında birleştiriliyor.");
    try {
      const result = await triggerCombinedShipmentPdfDownload(selectedShipmentIds, (completed) => {
        setCombinedShipmentPdfProgress({ completed, total: selectedShipmentIds.length });
      });
      await recordCombinedBulkArchive(result.downloadedShipmentIds, "other").catch((archiveError) => {
        console.warn("Combined shipment PDF history could not be recorded", archiveError);
      });
      setNotice(`${numberText(result.downloadedShipmentIds.length)} etiket tek PDF dosyası olarak indirildi.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Seçili etiketler tek PDF olarak hazırlanamadı.");
    } finally {
      setCombiningShipmentPdf(false);
    }
  }

  function clearShipmentFilters() {
    setSearch("");
    setStatus("all");
    setSyncStatus("all");
    setCarrierStatus("all");
    setShipmentType("all");
    setCarrier("all");
    setLabelProductId("all");
    setPlatform("all");
    setSortBy("newest");
    setDateFrom("");
    setDateTo("");
    setPageSize(50);
    setSelectedShipmentIds([]);
  }

  async function refreshShipmentList() {
    setNotice(null);
    await loadShipments();
    setNotice("Gönderi listesi güncellendi.");
  }

  const exportParams = shipmentListParams(pageSize, page * pageSize);
  exportParams.set("export", "csv");

  const selectedDownloadIds = selectedShipmentIds;
  const pageSelectableShipmentIds = rows.filter((row) => shipmentCanCancel(row) && shipmentHasDownload(row)).map((row) => text(row.id)).filter(Boolean);
  const selectedDownloadCount = selectedDownloadIds.length;
  const selectedShipmentCount = selectedShipmentIds.length;
  const selectedCancelableCount = selectedShipmentIds.length;
  const bulkCancelPercent = bulkCancelProgress.total > 0 ? Math.round((bulkCancelProgress.completed / bulkCancelProgress.total) * 100) : 0;
  const amazonCarrierRepairCompleted = Number(amazonCarrierRepairStatus?.repaired || 0) + Number(amazonCarrierRepairStatus?.failed || 0);
  const amazonCarrierRepairPercent = amazonCarrierRepairStatus?.total
    ? Math.min(100, Math.round((amazonCarrierRepairCompleted / amazonCarrierRepairStatus.total) * 100))
    : 0;
  const carrierOptions = Array.from(new Set(labelProducts.map((product) => text(product.carrier)).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const visiblePageSizeOptions = admin ? shipmentPageSizeOptions.filter((option) => option <= 100) : shipmentPageSizeOptions;
  const fallbackProblemCount = Number(summary.trackingFailed || 0) + Number(summary.labelFailed || 0) + Number(summary.carrierCheckError || 0);
  const problemCount = summary.problemShipments === undefined || summary.problemShipments === null ? fallbackProblemCount : Number(summary.problemShipments || 0);
  const hasSort = sortBy !== "newest";
  const hasCustomerFilters =
    !admin &&
    (shipmentType !== "all" || carrier !== "all" || labelProductId !== "all" || platform !== "all" || Boolean(dateFrom) || Boolean(dateTo));
  const hasAdminFilters = admin && labelProductId !== "all";

  function carrierBadge(row: DataRow) {
    return carrierDeliveryBadge(row);
  }

  function shipmentStatusBadge(row: DataRow) {
    const raw = text(row.status).toLowerCase();
    const cancelled = isCancelledShipmentRow(row);
    const technicalVoid = isTechnicalVoidShipmentRow(row);
    const label = raw === "printed" ? "Yazdırıldı" : raw === "created" ? "Oluşturuldu" : cancelled ? "İptal" : technicalVoid ? "Teknik hata" : text(row.status) || "-";
    const positive = raw === "printed";
    const negative = cancelled || technicalVoid;
    return (
      <span
        className={cx(
          "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
          positive && "border-emerald-200 bg-emerald-50 text-emerald-700",
          negative && "border-red-200 bg-red-50 text-red-700",
          !positive && !negative && "border-amber-200 bg-amber-50 text-amber-700"
        )}
      >
        {label}
      </span>
    );
  }

  function trackingCell(row: DataRow, detail: string) {
    const trackingNumber = text(row.trackingNumber);
    const title = trackingNumber || text(row.id);
    const stack = <NameStack title={title} detail={detail} />;
    const inactive = isCancelledShipmentRow(row) || isTechnicalVoidShipmentRow(row);
    const trackingUrl = inactive ? "" : text(row.carrierTrackingLink) || shipmentTrackingHref(trackingNumber);
    if (!trackingUrl) return stack;
    return (
      <a
        href={trackingUrl}
        target="_blank"
        rel="noreferrer"
        title="DHL takip sayfasını aç"
        className="inline-flex max-w-[240px] items-start gap-1.5 text-slate-950 transition hover:text-cyan-700"
      >
        <span className="min-w-0">{stack}</span>
        <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
      </a>
    );
  }

  function shipmentActionItems(row: DataRow, close: () => void) {
    const shipmentId = text(row.id);
    const working = rowActionId.endsWith(`:${shipmentId}`);
    const inactive = isCancelledShipmentRow(row) || isTechnicalVoidShipmentRow(row);
    const ready = shipmentHasDownload(row);
    const itemClass = "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50";
    return (
      <>
        <Link to={`/customer/shipments/${encodeURIComponent(shipmentId)}`} onClick={close} className={itemClass} role="menuitem">
          <Eye className="h-3.5 w-3.5" />
          Detay
        </Link>
        {ready ? (
          <>
            <a href={shipmentLabelHref(shipmentId, "normal", true)} download onClick={close} className={itemClass} role="menuitem">
              <FileText className="h-3.5 w-3.5" />
              PDF indir
            </a>
            <a href={shipmentLabelHref(shipmentId, "zip")} download onClick={close} className={itemClass} role="menuitem">
              <Download className="h-3.5 w-3.5" />
              ZIP paket indir
            </a>
          </>
        ) : null}
        <button
          type="button"
          disabled={working}
          onClick={() => {
            close();
            void syncSingleShipment(row);
          }}
          className={itemClass}
          role="menuitem"
        >
          {rowActionId === `sync:${shipmentId}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Takip senkronu
        </button>
        <button
          type="button"
          onClick={() => {
            close();
            setCarrierDetailsRow(row);
          }}
          className={itemClass}
          role="menuitem"
        >
          <Clock3 className="h-3.5 w-3.5" />
          Takip olayları
        </button>
        <button
          type="button"
          disabled={working || inactive}
          onClick={() => {
            close();
            void regenerateSingleShipment(row);
          }}
          className={itemClass}
          role="menuitem"
        >
          {rowActionId === `regenerate:${shipmentId}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <WandSparkles className="h-3.5 w-3.5" />}
          PDF’yi yeniden oluştur
        </button>
        <button
          type="button"
          disabled={working || bulkCanceling || !shipmentCanCancel(row)}
          onClick={() => {
            close();
            void replaceSingleShipmentTracking(row);
          }}
          title={shipmentCanCancel(row) ? "Farklı takip numaralı yeni etiket oluştur" : shipmentCancelBlockReason(row)}
          className={itemClass}
          role="menuitem"
        >
          {rowActionId === `replace:${shipmentId}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Yeni takip numarasıyla değiştir
        </button>
        <button
          type="button"
          disabled={working || bulkCanceling || !shipmentCanCancel(row)}
          onClick={() => {
            close();
            void cancelSingleShipment(row);
          }}
          title={shipmentCanCancel(row) ? "Gönderiyi iptal et" : shipmentCancelBlockReason(row)}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
          role="menuitem"
        >
          {rowActionId === `cancel:${shipmentId}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
          İptal et
        </button>
      </>
    );
  }

  function shipmentActionMenu(row: DataRow) {
    const shipmentId = text(row.id);
    const open = text(shipmentActionMenuState?.row.id) === shipmentId;
    return (
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={(event: ReactMouseEvent<HTMLButtonElement>) => {
          if (open) {
            setShipmentActionMenuState(null);
            return;
          }
          const rect = event.currentTarget.getBoundingClientRect();
          const menuWidth = 208;
          const desiredHeight = shipmentHasDownload(row) ? 300 : 220;
          const roomBelow = Math.max(0, window.innerHeight - rect.bottom - 12);
          const roomAbove = Math.max(0, rect.top - 12);
          const openAbove = roomBelow < desiredHeight && roomAbove > roomBelow;
          setShipmentActionMenuState({
            left: Math.max(12, Math.min(window.innerWidth - menuWidth - 12, rect.right - menuWidth)),
            maxHeight: Math.max(160, openAbove ? roomAbove : roomBelow),
            openAbove,
            row,
            top: openAbove ? rect.top - 8 : rect.bottom + 8,
          });
        }}
        className="inline-flex h-9 items-center gap-2 rounded-lg bg-slate-950 px-3 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
        Aksiyon
        <ChevronDown className={cx("h-3.5 w-3.5 transition", open && "rotate-180")} />
      </button>
    );
  }

  const shipmentActionPortal =
    shipmentActionMenuState && typeof document !== "undefined"
      ? createPortal(
          <>
            <div className="fixed inset-0 z-[80]" aria-hidden="true" onMouseDown={() => setShipmentActionMenuState(null)} />
            <div
              role="menu"
              aria-label="Gönderi aksiyonları"
              className="fixed z-[81] w-52 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl"
              style={{
                left: shipmentActionMenuState.left,
                maxHeight: shipmentActionMenuState.maxHeight,
                top: shipmentActionMenuState.top,
                transform: shipmentActionMenuState.openAbove ? "translateY(-100%)" : undefined,
              }}
            >
              {shipmentActionItems(shipmentActionMenuState.row, () => setShipmentActionMenuState(null))}
            </div>
          </>,
          document.body
        )
      : null;

  const columns: DataColumn[] = admin
    ? [
        {
          label: "Takip",
          value: (row) => trackingCell(row, text(row.orderNumber)),
        },
        { label: "Alıcı", value: (row) => text(row.recipientName) || "-" },
        { label: "Etiket", value: (row) => <NameStack title={text(row.labelProductName)} detail={shipmentTypeLabel(row.shipmentType)} /> },
        { label: "Müşteri", value: (row) => text(row.userCompanyName) || text(row.userEmail) || "-" },
        { label: "Carrier durum", value: (row) => carrierBadge(row) },
        { label: "Takip senkronu", value: (row) => statusBadge(text(row.sourcePlatformTrackingSyncStatus)) },
        { label: "Son kontrol", value: (row) => dateText(row.carrierCheckCheckedAt) },
        { label: "Tarih", value: (row) => dateText(row.createdAt) },
        {
          label: "Olaylar",
          value: (row) => (
            <button
              type="button"
              onClick={() => setCarrierDetailsRow(row)}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <Clock3 className="h-3.5 w-3.5" />
              Event
            </button>
          ),
        },
      ]
    : [
        {
          label: "Seç",
          value: (row) => {
            const shipmentId = text(row.id);
            return (
              <input
                type="checkbox"
                checked={selectedShipmentIds.includes(shipmentId)}
                disabled={!shipmentCanCancel(row) || !shipmentHasDownload(row) || bulkCanceling}
                onChange={(event) => toggleShipmentSelection(row, event.target.checked)}
                title={!shipmentCanCancel(row) ? shipmentCancelBlockReason(row) : shipmentHasDownload(row) ? "Gönderiyi seç" : "Etiket dosyası henüz hazır değil."}
                aria-label={`${text(row.trackingNumber) || shipmentId} gönderisini seç`}
                className="h-4 w-4 rounded border-slate-300 text-slate-950 disabled:opacity-40"
              />
            );
          },
        },
        {
          label: "Takip",
          value: (row) => trackingCell(row, `${text(row.orderNumber) || "-"} · ${platformLabel(text(row.sourcePlatform)) || "-"}`),
        },
        { label: "Alıcı", value: (row) => <NameStack title={text(row.recipientName)} detail={compactAddress(row.city, row.countryCode)} /> },
        { label: "Etiket", value: (row) => <NameStack title={text(row.labelProductName)} detail={`${text(row.labelProductCarrier) || "-"} · ${shipmentTypeLabel(row.shipmentType)}`} /> },
        { label: "Carrier durum", value: (row) => carrierBadge(row) },
        { label: "Durum", value: (row) => shipmentStatusBadge(row) },
        { label: "Takip senkronu", value: (row) => statusBadge(text(row.sourcePlatformTrackingSyncStatus)) },
        { label: "Tarih", value: (row) => dateText(row.createdAt) },
        {
          label: "İşlemler",
          value: (row) => {
            return <div className="flex min-w-[130px] justify-end">{shipmentActionMenu(row)}</div>;
          },
        },
      ];

  return (
    <div className="space-y-5">
      {confirmationDialog}
      {shipmentActionPortal}
      <AdminPageHeader
        title={title}
        caption={admin ? "Oluşturulan gönderi ve etiket kayıtlarını arama, sayfalama ve arka plan takip senkronu ile yönetin." : "Etiketli gönderileri eski operasyon akışındaki gibi filtreleyin, indirin, yenileyin ve takip senkronlarını yönetin."}
        eyebrow={admin ? "Yönetim" : "Müşteri paneli"}
        icon={<Truck className="h-5 w-5" />}
        action={
          <div className="flex flex-wrap gap-2">
            {!admin ? (
              <button
                type="button"
                disabled={selectedDownloadCount === 0 || combiningShipmentPdf || bulkCanceling}
                onClick={() => void downloadSelectedShipmentsAsOnePdf()}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
              >
                {combiningShipmentPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                {combiningShipmentPdf
                  ? `Tek PDF hazırlanıyor (${numberText(combinedShipmentPdfProgress.completed)}/${numberText(combinedShipmentPdfProgress.total)})`
                  : selectedDownloadCount > 0
                    ? `Seçili tek PDF indir (${numberText(selectedDownloadCount)})`
                    : "Seçili tek PDF indir"}
              </button>
            ) : null}
            {!admin ? (
              selectedDownloadCount > 0 ? (
                <a
                  href={shipmentBulkHref(selectedDownloadIds)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
                >
                  <Download className="h-4 w-4" />
                  Seçili ZIP indir ({numberText(selectedDownloadCount)})
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-400 shadow-sm"
                >
                  <Download className="h-4 w-4" />
                  Seçili ZIP indir
                </button>
              )
            ) : null}
            <a
              href={`${endpoint}?${exportParams.toString()}`}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <Download className="h-4 w-4" />
              CSV export
            </a>
            {admin ? (
              <button
                type="button"
                disabled={bulkSyncing || bulkCanceling}
                onClick={() => {
                  if (amazonCarrierRepairing) {
                    amazonCarrierRepairStopRef.current = true;
                  } else {
                    void runAmazonDhlGermanyRepair();
                  }
                }}
                className={cx(
                  "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition disabled:opacity-60",
                  amazonCarrierRepairing
                    ? "border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                    : "border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                )}
              >
                {amazonCarrierRepairing ? <Square className="h-4 w-4" /> : <WandSparkles className="h-4 w-4" />}
                {amazonCarrierRepairing ? "DHL kontrolünü durdur" : "Amazon DHL Germany kontrolü"}
              </button>
            ) : null}
            <button
              type="button"
              disabled={bulkSyncing || bulkCanceling || amazonCarrierRepairing}
              onClick={() => void queueBulkTracking()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              {bulkSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {selectedShipmentCount > 0 ? `Seçili takipleri senkronla (${numberText(selectedShipmentCount)})` : "100 takibi senkronla"}
            </button>
            {!admin && selectedShipmentCount > 0 ? (
              <button
                type="button"
                disabled={bulkCanceling || selectedCancelableCount === 0}
                onClick={() => void cancelSelectedShipments()}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:opacity-60"
              >
                {bulkCanceling ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                Seçili gönderileri iptal et ({numberText(selectedCancelableCount)})
              </button>
            ) : null}
          </div>
        }
      />
      {notice ? <AdminNotice message={notice} /> : null}
      {error ? <ErrorBanner message={error} /> : null}
      {admin && amazonCarrierRepairStatus && (amazonCarrierRepairing || amazonCarrierRepairStatus.repaired > 0 || amazonCarrierRepairStatus.failed > 0) ? (
        <div className="rounded-xl border border-emerald-200 bg-white p-4 text-sm text-slate-700 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2 font-semibold text-slate-950">
              {amazonCarrierRepairing ? <Loader2 className="h-4 w-4 animate-spin text-emerald-600" /> : <ShieldCheck className="h-4 w-4 text-emerald-600" />}
              Amazon kaynak Shopify taşıyıcı kontrolü
            </span>
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
              <span className="rounded-lg bg-emerald-50 px-2.5 py-1 text-emerald-700">Doğrulandı: {numberText(amazonCarrierRepairStatus.repaired)}</span>
              <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-slate-600">Bekleyen: {numberText(amazonCarrierRepairStatus.remaining)}</span>
              {amazonCarrierRepairStatus.retryableFailed > 0 ? (
                <button
                  type="button"
                  disabled={amazonCarrierRepairing}
                  onClick={() => void retryFailedAmazonDhlGermanyRepairs()}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-2.5 py-1 text-red-700 transition hover:bg-red-100 disabled:opacity-50"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Geçici hata: {numberText(amazonCarrierRepairStatus.retryableFailed)}
                </button>
              ) : null}
              {amazonCarrierRepairStatus.failed - amazonCarrierRepairStatus.retryableFailed > 0 ? (
                <span className="rounded-lg bg-amber-50 px-2.5 py-1 text-amber-700">
                  İnceleme: {numberText(amazonCarrierRepairStatus.failed - amazonCarrierRepairStatus.retryableFailed)}
                </span>
              ) : null}
            </div>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-emerald-600 transition-all" style={{ width: `${amazonCarrierRepairPercent}%` }} />
          </div>
        </div>
      ) : null}
      {!admin && bulkCanceling ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="inline-flex items-center gap-2 font-semibold">
              <Loader2 className="h-4 w-4 animate-spin" />
              İptal ediliyor
            </span>
            <span className="font-semibold">
              {numberText(bulkCancelProgress.completed)} / {numberText(bulkCancelProgress.total)}
              {bulkCancelProgress.failed > 0 ? ` · ${numberText(bulkCancelProgress.failed)} hata` : ""}
            </span>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-red-100">
            <div className="h-full rounded-full bg-red-600 transition-all" style={{ width: `${bulkCancelPercent}%` }} />
          </div>
        </div>
      ) : null}
      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <AdminStatCard icon={<Truck className="h-5 w-5" />} label="Toplam" value={summary.total || 0} tone="slate" />
        <AdminStatCard icon={<CheckCircle2 className="h-5 w-5" />} label={admin ? "Teslim sinyali" : "Yazdırılan"} value={admin ? summary.deliveredHint || 0 : summary.printed || summary.trackingSuccess || 0} tone="emerald" />
        <AdminStatCard icon={<AlertTriangle className="h-5 w-5" />} label={admin ? "Takip hatalı" : "Sorunlu"} value={admin ? summary.trackingFailed || 0 : problemCount} tone="red" />
        <AdminStatCard icon={<Clock3 className="h-5 w-5" />} label={admin ? "Yolda sinyali" : "Oluşturulan"} value={admin ? summary.inTransitHint || 0 : summary.created || 0} tone="amber" />
        <AdminStatCard icon={<Package className="h-5 w-5" />} label="Retoure" value={summary.returns || 0} tone="violet" />
      </section>
      {!admin ? (
        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <AdminStatCard icon={<Tags className="h-5 w-5" />} label="Kullanılan ürün" value={summary.productsInUse || 0} tone="cyan" />
          <AdminStatCard icon={<X className="h-5 w-5" />} label="İptal" value={summary.voided || 0} tone="red" />
          <AdminStatCard icon={<Send className="h-5 w-5" />} label="Takip başarılı" value={summary.trackingSuccess || 0} tone="emerald" />
          <AdminStatCard icon={<Clock3 className="h-5 w-5" />} label="Takip bekleyen" value={summary.trackingPending || 0} tone="amber" />
          <AdminStatCard icon={<AlertTriangle className="h-5 w-5" />} label="Carrier hata" value={summary.carrierCheckError || 0} tone="red" />
        </section>
      ) : null}
      <AdminPanel title={admin ? "Gönderiler" : "Gönderilerim"} count={meta.totalCount}>
        <div
          className={cx(
            "grid gap-3 border-b border-slate-200 p-4 sm:grid-cols-2 lg:grid-cols-3",
            admin
              ? "xl:grid-cols-4 2xl:grid-cols-7"
              : "xl:grid-cols-4 2xl:grid-cols-5"
          )}
        >
          <div className="relative min-w-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Takip, sipariş, alıcı, şehir veya ürün ara"
              className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-950 outline-none focus:border-slate-400 focus:bg-white"
            />
          </div>
          <select value={status} onChange={(event) => setStatus(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
            <option value="all">Tüm durumlar</option>
            <option value="created">Oluşturuldu</option>
            <option value="printed">Yazdırıldı</option>
            <option value="cancelled">İptal</option>
          </select>
          {!admin ? (
            <select value={shipmentType} onChange={(event) => setShipmentType(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
              <option value="all">Tüm türler</option>
              <option value="outbound">Giden</option>
              <option value="return">İade</option>
            </select>
          ) : null}
          <select value={syncStatus} onChange={(event) => setSyncStatus(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
            <option value="all">Tüm takip senkronları</option>
            <option value="failed">Hatalı</option>
            <option value="pending">Bekleyen</option>
            <option value="success">Başarılı</option>
            <option value="not_started">Başlamadı</option>
            <option value="disabled">Devre dışı</option>
          </select>
          <select value={carrierStatus} onChange={(event) => setCarrierStatus(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
            <option value="all">Tüm carrier durumları</option>
            <option value="delivered">Teslim edildi</option>
            <option value="in_transit">Yolda</option>
            <option value="return_in_progress">İade sürecinde</option>
            <option value="problem">Sorunlu</option>
            <option value="carrier_check_error">DHL durumu alınamadı</option>
            <option value="printed">Yazdırıldı</option>
            <option value="label_created">Etiket oluştu</option>
            <option value="cancelled">İptal</option>
          </select>
          <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
            <option value="newest">Sıralama: en yeni</option>
            <option value="label_product">Sıralama: etiket ürünü</option>
          </select>
          {admin ? (
            <select value={labelProductId} onChange={(event) => setLabelProductId(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
              <option value="all">Tüm etiket ürünleri</option>
              {labelProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          ) : null}
          {!admin ? (
            <>
              <select value={carrier} onChange={(event) => setCarrier(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
                <option value="all">Tüm taşıyıcılar</option>
                {carrierOptions.map((option) => (
                  <option key={option} value={option.toLowerCase()}>
                    {option}
                  </option>
                ))}
              </select>
              <select value={labelProductId} onChange={(event) => setLabelProductId(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
                <option value="all">Tüm etiket ürünleri</option>
                {labelProducts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
              <select value={platform} onChange={(event) => setPlatform(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
                <option value="all">Tüm platformlar</option>
                {storePlatformOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value) || 50)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
                {visiblePageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option} satır
                  </option>
                ))}
              </select>
              <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400" />
              <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400" />
            </>
          ) : (
            <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value) || 50)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
              {visiblePageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option} satır
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="flex flex-wrap gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
          <button type="button" onClick={() => setCarrierStatus("delivered")} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">Teslim edildi</button>
          <button type="button" onClick={() => setCarrierStatus("in_transit")} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">Yolda</button>
          <button type="button" onClick={() => setCarrierStatus("return_in_progress")} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">İade</button>
          <button type="button" onClick={() => setCarrierStatus("problem")} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">Sorunlu</button>
          <button type="button" onClick={() => setCarrierStatus("carrier_check_error")} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">DHL durumu alınamadı</button>
          {!admin ? (
            <>
              <button type="button" onClick={() => setStatus(status === "cancelled" ? "all" : "cancelled")} className={cx("rounded-lg border px-3 py-1.5 text-xs font-semibold shadow-sm", status === "cancelled" ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 bg-white text-slate-700")}>Sadece iptaller</button>
              <button type="button" onClick={() => void selectAllFilteredShipments()} disabled={selectingAllShipments || combiningShipmentPdf || meta.totalCount === 0} className="rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-800 shadow-sm disabled:opacity-50">
                {selectingAllShipments ? "Tümü seçiliyor" : "Tüm filtrelenen gönderileri seç"}
              </button>
              <button type="button" onClick={selectPageRows} disabled={pageSelectableShipmentIds.length === 0} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm disabled:opacity-50">Sayfadaki gönderileri seç</button>
              <button type="button" onClick={() => setSelectedShipmentIds([])} disabled={selectedShipmentIds.length === 0} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm disabled:opacity-50">Seçimi temizle</button>
              <button type="button" onClick={() => void cancelSelectedShipments()} disabled={bulkCanceling || selectedCancelableCount === 0} className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 shadow-sm disabled:opacity-50">
                {bulkCanceling ? "İptal ediliyor" : `Seçili iptal (${numberText(selectedCancelableCount)})`}
              </button>
            </>
          ) : null}
          <button type="button" onClick={() => void refreshShipmentList()} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">Listeyi yenile</button>
          <button type="button" onClick={clearShipmentFilters} disabled={!search && status === "all" && syncStatus === "all" && carrierStatus === "all" && !hasSort && !hasCustomerFilters && !hasAdminFilters} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm disabled:opacity-50">Filtreleri sıfırla</button>
          {!admin && selectedShipmentCount > 0 ? (
            <span className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500">{numberText(selectedDownloadCount)} indirilebilir / {numberText(selectedShipmentCount)} seçili</span>
          ) : null}
        </div>
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Gönderiler yükleniyor
          </div>
        ) : !admin ? (
          rows.length === 0 ? (
            <div className="flex min-h-48 flex-col items-center justify-center gap-3 bg-slate-50/60 p-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-400 shadow-sm">
                <Package className="h-5 w-5" />
              </div>
              <p className="text-sm font-medium text-slate-600">Gönderi yok.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((row) => {
                const shipmentId = text(row.id);
                const trackingNumber = text(row.trackingNumber);
                const inactive = isCancelledShipmentRow(row) || isTechnicalVoidShipmentRow(row);
                const trackingUrl = inactive ? "" : shipmentTrackingHref(trackingNumber);
                const address = compactAddress(row.address1, row.address2, row.postalCode, row.city, row.countryCode);
                const locality = compactAddress(row.postalCode, row.city, row.countryCode);
                const rowSelected = selectedShipmentIds.includes(shipmentId);
                return (
                  <article
                    key={shipmentId || text(row.trackingNumber)}
                    className="grid gap-4 p-4 transition hover:bg-slate-50/70 lg:grid-cols-[44px_minmax(0,1.15fr)_minmax(0,1.35fr)] xl:grid-cols-[44px_minmax(0,1.05fr)_minmax(0,1.15fr)_minmax(0,0.9fr)_minmax(252px,auto)] xl:items-center"
                  >
                    <label className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
                      <input
                        type="checkbox"
                        checked={rowSelected}
                        disabled={!shipmentCanCancel(row) || !shipmentHasDownload(row) || bulkCanceling}
                        onChange={(event) => toggleShipmentSelection(row, event.target.checked)}
                        title={!shipmentCanCancel(row) ? shipmentCancelBlockReason(row) : shipmentHasDownload(row) ? "Gönderiyi seç" : "Etiket dosyası henüz hazır değil."}
                        aria-label={`${trackingNumber || shipmentId} gönderisini seç`}
                        className="h-4 w-4 rounded border-slate-300 text-slate-950 disabled:opacity-40"
                      />
                    </label>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase text-slate-400">Takip / sipariş</p>
                      <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
                        {trackingUrl ? (
                          <a
                            href={trackingUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex max-w-full items-center gap-1 truncate font-semibold text-slate-950 hover:text-cyan-700"
                          >
                            <span className="truncate">{trackingNumber}</span>
                            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                          </a>
                        ) : (
                          <span className="truncate font-semibold text-slate-950">{trackingNumber || shipmentId || "-"}</span>
                        )}
                        {badge(text(row.sourcePlatform))}
                      </div>
                      <p className="mt-1 truncate text-xs text-slate-500">
                        {text(row.orderNumber) || "-"} · {dateText(row.createdAt)}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase text-slate-400">Alıcı ve adres</p>
                      <p className="mt-1 truncate font-semibold text-slate-950">{text(row.recipientName) || "-"}</p>
                      <p className="mt-1 flex min-w-0 items-start gap-1.5 text-xs leading-5 text-slate-500">
                        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                        <span className="min-w-0 break-words">{address || locality || "Adres detayı yok"}</span>
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase text-slate-400">Etiket ve durum</p>
                      <p className="mt-1 truncate font-semibold text-slate-950">{text(row.labelProductName) || "-"}</p>
                      <p className="mt-1 truncate text-xs text-slate-500">{text(row.labelProductCarrier) || "-"} · {shipmentTypeLabel(row.shipmentType)}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {shipmentStatusBadge(row)}
                        {carrierBadge(row)}
                        {statusBadge(text(row.sourcePlatformTrackingSyncStatus))}
                      </div>
                    </div>
                    <div className="flex min-w-0 justify-start xl:justify-end">
                      {shipmentActionMenu(row)}
                    </div>
                  </article>
                );
              })}
            </div>
          )
        ) : (
          <DataTable
            rows={rows}
            empty="Gönderi yok."
            columns={columns}
          />
        )}
        <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50 px-5 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
          <span>
            {rows.length === 0
              ? "Kayıt yok"
              : `${numberText(meta.offset + 1)}-${numberText(meta.offset + rows.length)} / ${numberText(meta.totalCount)} kayıt`}
          </span>
          <div className="flex gap-2">
            <button type="button" disabled={loading || page === 0} onClick={() => setPage((current) => Math.max(0, current - 1))} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50">
              Önceki
            </button>
            <button type="button" disabled={loading || !meta.hasMore} onClick={() => setPage((current) => current + 1)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50">
              Sonraki
            </button>
          </div>
        </div>
      </AdminPanel>
      <CarrierEventsDrawer row={carrierDetailsRow} onClose={() => setCarrierDetailsRow(null)} />
    </div>
  );
}

function CustomerShipmentsPage() {
  return <ShipmentsResourcePage title="Gönderilerim" endpoint="/api/shipments" />;
}

function ShipmentDetailPage() {
  const { shipmentId } = useParams();
  const [shipment, setShipment] = useState<ShipmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [syncingTracking, setSyncingTracking] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncNotice, setSyncNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(null);
    apiClient
      .shipment(shipmentId || "")
      .then((result) => {
        if (active) setShipment(result.shipment);
      })
      .catch((err) => {
        if (active) setLoadError(err instanceof ApiError ? err.message : "Gönderi detayı alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [shipmentId, refreshTick]);

  async function onSyncTracking() {
    if (!shipment?.id) return;
    setSyncingTracking(true);
    setSyncError(null);
    setSyncNotice(null);
    try {
      const result = await apiClient.syncShipmentTracking(shipment.id);
      setSyncNotice(result.message || `Kaynak platform sync sonucu: ${result.status}`);
      setRefreshTick((value) => value + 1);
    } catch (err) {
      setSyncError(err instanceof ApiError ? err.message : "Tracking sync başlatılamadı.");
    } finally {
      setSyncingTracking(false);
    }
  }

  if (loading) return <LoadingPanel label="Gönderi detayı yükleniyor" />;
  if (loadError) return <ErrorPanel title="Gönderi detayı açılamadı" message={loadError} />;
  if (!shipment) return <ErrorPanel title="Gönderi bulunamadı" message="Kayıt bulunamadı." />;

  const hasLabelOutput = Boolean(shipment.labelOutputKey || shipment.labelPdfDownloadUrl || shipment.labelDownloadUrl || shipment.labelZipDownloadUrl);
  const normalLabelUrl = shipmentLabelHref(shipment.id, "normal", true);
  const adobeLabelUrl = shipmentLabelHref(shipment.id, "adobe", true);
  const zipLabelUrl = shipmentLabelHref(shipment.id, "zip");
  const shipmentCarrierRow = shipment as unknown as DataRow;
  const trackingUrl = isCancelledShipmentRow(shipmentCarrierRow) || isTechnicalVoidShipmentRow(shipmentCarrierRow) ? "" : shipmentTrackingHref(shipment.trackingNumber || "");
  const shipmentCarrierEvents = carrierEventRows(shipmentCarrierRow);

  return (
    <div className="space-y-5">
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="bg-slate-950 p-5 text-white sm:p-6">
          <Link className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition hover:text-white" to="/customer/shipments">
            <ArrowLeft className="h-4 w-4" />
            Gönderilere dön
          </Link>
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-cyan-100 ring-1 ring-white/10">{shipment.status || "-"}</span>
                <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-slate-200 ring-1 ring-white/10">{shipment.labelGenerationStatus || "label"}</span>
              </div>
              <h1 className="mt-3 truncate text-3xl font-semibold tracking-tight">{shipment.trackingNumber || shipment.id}</h1>
              <p className="mt-2 text-sm text-slate-300">
                {shipment.order?.orderNumber || "-"} · {platformLabel(shipment.order?.sourcePlatform || "") || "-"} · {dateText(shipment.createdAt)}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 lg:justify-end">
              {hasLabelOutput ? (
                <>
                  <a href={normalLabelUrl} download className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-sm transition hover:bg-slate-100">
                    <Download className="h-4 w-4" />
                    PDF indir
                  </a>
                  <a href={zipLabelUrl} download className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-white/15">
                    <Package className="h-4 w-4" />
                    ZIP paket
                  </a>
                </>
              ) : null}
              <button
                type="button"
                onClick={onSyncTracking}
                disabled={syncingTracking || !shipment.trackingNumber}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-white/15 disabled:opacity-60"
              >
                {syncingTracking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Takip senkronu
              </button>
            </div>
          </div>
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Alıcı</p>
              <p className="mt-2 truncate text-lg font-semibold">{shipment.order?.recipientName || "-"}</p>
              <p className="mt-1 truncate text-sm text-slate-300">{compactAddress(shipment.order?.postalCode, shipment.order?.city, shipment.order?.countryCode)}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Etiket ürünü</p>
              <p className="mt-2 truncate text-lg font-semibold">{shipment.labelProduct?.name || "-"}</p>
              <p className="mt-1 truncate text-sm text-slate-300">{shipment.labelProduct?.carrier || "-"}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Takip</p>
              {trackingUrl ? (
                <a href={trackingUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex max-w-full items-center gap-2 truncate text-lg font-semibold text-white hover:text-cyan-100">
                  <span className="truncate">DHL takip aç</span>
                  <ExternalLink className="h-4 w-4 shrink-0" />
                </a>
              ) : (
                <p className="mt-2 text-lg font-semibold">Takip linki yok</p>
              )}
              <p className="mt-1 truncate text-sm text-slate-300">{shipment.sourcePlatformTrackingSyncStatus || "-"}</p>
            </div>
          </div>
        </div>
      </section>

      {syncNotice ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{syncNotice}</div> : null}
      {syncError ? <ErrorBanner message={syncError} /> : null}

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">DHL teslimat durumu</h2>
            <p className="mt-1 text-sm text-slate-500">Son kontrol: {dateText(shipmentCarrierRow.carrierCheckCheckedAt || shipmentCarrierRow.carrierDeliveryStatusAt)} · Son event: {dateText(shipmentCarrierRow.carrierDeliveryLastEventAt)}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {carrierDeliveryBadge(shipmentCarrierRow)}
            {text(shipmentCarrierRow.carrierTrackingLink) ? (
              <a href={text(shipmentCarrierRow.carrierTrackingLink)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">
                <ExternalLink className="h-3.5 w-3.5" />
                DHL
              </a>
            ) : null}
          </div>
        </div>
        {shipmentCarrierEvents.length === 0 ? (
          <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm text-slate-500">Kaydedilmiş carrier event yok.</div>
        ) : (
          <div className="mt-5 grid gap-3">
            {shipmentCarrierEvents.map((event, index) => (
              <div key={`${text(event.at)}-${index}`} className="flex gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-cyan-700 ring-1 ring-slate-200">
                  <Truck className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="break-words text-sm font-semibold text-slate-950">{text(event.text) || text(event.statusText) || "DHL event"}</p>
                  <p className="mt-1 text-xs text-slate-500">{[dateText(event.at), text(event.location), text(event.statusCode)].filter((item) => item && item !== "-").join(" · ") || "-"}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="grid gap-5 xl:grid-cols-3">
        <DetailCard
          title="Gönderi bilgisi"
          items={[
            ["Takip no", shipment.trackingNumber || "-"],
            ["Gönderi tipi", shipment.shipmentType || "-"],
            ["Etiket sağlayıcı", shipment.labelProvider || "-"],
            ["Oluşturulma", dateText(shipment.createdAt)],
            ["Yazdırılma/Gönderim", dateText(shipment.shippedAt)],
          ]}
        />
        <DetailCard
          title="Sipariş"
          items={[
            ["Sipariş", shipment.order?.orderNumber || "-"],
            ["Adres kaynağı", orderAddressSourceLabel(shipment.order?.selectedAddressSource)],
            ["Alıcı", shipment.order?.recipientName || "-"],
            ["Adres", compactAddress(shipment.order?.address1, shipment.order?.address2, shipment.order?.postalCode, shipment.order?.city, shipment.order?.countryCode)],
            ["Fatura adresi", shipment.order?.billingAddressDiffers ? "Fatura adresi farklı" : "-"],
            ["Mağaza", shipment.order?.store?.name || "-"],
          ]}
        />
        <DetailCard
          title="Kaynak platform senkronu"
          items={[
            ["Takip senkronu", shipment.sourcePlatformTrackingSyncStatus || "-"],
            ["Takip mesajı", shipment.sourcePlatformTrackingSyncMessage || "-"],
            ["Takip zamanı", dateText(shipment.sourcePlatformTrackingSyncedAt)],
            ["Shopify gönderim durumu", shipment.shopifyFulfillmentSyncStatus || "-"],
            ["Shopify mesajı", shipment.shopifyFulfillmentSyncError || "-"],
          ]}
        />
      </section>

      <section className="grid gap-5 xl:grid-cols-2">
        <DetailCard
          title="Etiket ürünü"
          items={[
            ["Ürün", shipment.labelProduct?.name || "-"],
            ["Taşıyıcı", shipment.labelProduct?.carrier || "-"],
            [
              "Ağırlık aralığı",
              shipment.labelProduct
                ? `${numberText(shipment.labelProduct.weightMinGrams)}-${numberText(shipment.labelProduct.weightMaxGrams)} g`
                : "-",
            ],
          ]}
        />
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-950">Etiket indirmeleri</h2>
          <p className="mt-1 text-sm text-slate-500">PDF ve ZIP dosyaları tarayıcıda açılmadan indirilecek şekilde hazırlanır.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <InfoLine label="PDF" value={shipment.hasEmbeddedPdf || shipment.labelPdfDownloadUrl || shipment.labelOutputKey ? "Hazır" : "Kayıt yok"} />
            <InfoLine label="HTML" value={shipment.labelHtmlAvailable ? "Hazır" : "Kayıt yok"} />
          </div>
          {hasLabelOutput ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href={normalLabelUrl}
                download
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
              >
                <Download className="h-4 w-4" />
                PDF indir
              </a>
              <a
                href={adobeLabelUrl}
                download
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                <FileText className="h-4 w-4" />
                Adobe PDF indir
              </a>
              <a
                href={zipLabelUrl}
                download
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                <Download className="h-4 w-4" />
                ZIP paket
              </a>
            </div>
          ) : null}
          {shipment.labelGenerationError ? <ErrorBanner message={shipment.labelGenerationError} /> : null}
        </section>
      </section>
    </div>
  );
}

function CustomerInvoicesPage() {
  const [data, setData] = useState<DataRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [creditTypeFilter, setCreditTypeFilter] = useState("all");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    apiClient
      .resource<DataRow>("/api/invoices")
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Faturalar alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const invoices = Array.isArray(data?.invoices) ? (data?.invoices as DataRow[]) : [];
  const purchases = Array.isArray(data?.purchases) ? (data?.purchases as DataRow[]) : [];
  const summary = (data?.summary || {}) as DataRow;
  const invoiceSummary = (summary.invoices || {}) as DataRow;
  const purchaseSummary = (summary.purchases || {}) as DataRow;
  const normalizedInvoiceSearch = search.trim().toLocaleLowerCase("tr-TR");
  const filteredInvoices = useMemo(
    () =>
      invoices.filter((invoice) => {
        if (orderStatusFilter !== "all" && text(invoice.status).toLowerCase() !== orderStatusFilter) return false;
        if (!normalizedInvoiceSearch) return true;
        return [invoice.shopifyOrderNumber, invoice.invoiceNumber, invoice.orderNumber, invoice.externalOrderId, invoice.sourcePlatform, invoice.storeName]
          .map(text)
          .join(" ")
          .toLocaleLowerCase("tr-TR")
          .includes(normalizedInvoiceSearch);
      }),
    [invoices, normalizedInvoiceSearch, orderStatusFilter]
  );
  const filteredPurchases = useMemo(
    () =>
      purchases.filter((purchase) => {
        if (creditTypeFilter !== "all" && text(purchase.type).toLowerCase() !== creditTypeFilter) return false;
        if (!normalizedInvoiceSearch) return true;
        return [purchase.invoiceNumber, purchase.shopifyOrderName, purchase.shopifyOrderId, purchase.productSummary, purchase.productDetail, purchase.labelProductName, purchase.paymentMethodName, purchase.type, purchase.status]
          .map(text)
          .join(" ")
          .toLocaleLowerCase("tr-TR")
          .includes(normalizedInvoiceSearch);
      }),
    [creditTypeFilter, normalizedInvoiceSearch, purchases]
  );

  return (
    <div className="space-y-5">
      <CustomerPageHeader
        title="Faturalar"
        caption="Manuel oluşturulan faturalarınız ve Shopify satın alma kayıtlarınız."
        icon={<FileText className="h-5 w-5" />}
        action={
          <div className="flex flex-wrap gap-2">
            <a href="/api/invoices?export=csv&section=invoices" className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">
              <Download className="h-4 w-4" />
              Fatura CSV
            </a>
            <a href="/api/invoices?export=csv&section=purchases" className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800">
              <Download className="h-4 w-4" />
              Alım CSV
            </a>
          </div>
        }
      />
      {error ? <ErrorBanner message={error} /> : null}
      <AdminPanel title="Filtreler" caption="Fatura numarası, sipariş, mağaza veya etiket satın alma kaydı arayın.">
        <div className="grid gap-3 p-5 lg:grid-cols-[1fr_200px_220px]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
              placeholder="Fatura no, sipariş, mağaza veya ürün ara"
            />
          </label>
          <select
            value={orderStatusFilter}
            onChange={(event) => setOrderStatusFilter(event.target.value)}
            className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
          >
            <option value="all">Tüm fatura durumları</option>
            <option value="draft">Taslak</option>
            <option value="open">Açık</option>
            <option value="final">Final</option>
            <option value="paid">Ödendi</option>
            <option value="cancelled">İptal</option>
          </select>
          <select
            value={creditTypeFilter}
            onChange={(event) => setCreditTypeFilter(event.target.value)}
            className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
          >
            <option value="all">Tüm Shopify alımları</option>
            <option value="purchase">Normal purchase</option>
          </select>
        </div>
      </AdminPanel>
      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Fatura" value={invoiceSummary.count || invoices.length} tone="cyan" icon={<FileText className="h-5 w-5" />} />
        <AdminStatCard label="Net" value={moneyText(invoiceSummary.netTotal || 0, "EUR")} tone="emerald" icon={<CreditCard className="h-5 w-5" />} />
        <AdminStatCard label="KDV" value={moneyText(invoiceSummary.taxTotal || 0, "EUR")} tone="amber" icon={<FileText className="h-5 w-5" />} />
        <AdminStatCard label="Brüt" value={moneyText(invoiceSummary.grossTotal || 0, "EUR")} tone="violet" icon={<Wallet className="h-5 w-5" />} />
      </section>
      <section className="grid gap-3 sm:grid-cols-3">
        <AdminStatCard label="Shopify alım" value={purchaseSummary.purchase || purchases.length} tone="emerald" icon={<ShoppingCart className="h-5 w-5" />} />
        <AdminStatCard label="Etiket adedi" value={purchaseSummary.quantity || purchases.reduce((sum, row) => sum + Number(row.quantity || 0), 0)} tone="cyan" icon={<Tags className="h-5 w-5" />} />
        <AdminStatCard label="Alım toplamı" value={moneyText(purchaseSummary.totalPrice || purchases.reduce((sum, row) => sum + Number(row.totalPrice || 0), 0), "EUR")} tone="violet" icon={<Wallet className="h-5 w-5" />} />
      </section>
      {loading ? (
        <LoadingPanel label="Faturalar yükleniyor" />
      ) : (
        <>
          <AdminPanel title="Faturalar" caption={`${numberText(filteredInvoices.length)} kayıt gösteriliyor.`} count={filteredInvoices.length}>
            <DataTable
              rows={filteredInvoices}
              empty="Fatura yok."
              columns={[
                {
                  label: "Fatura no",
                  value: (row) => (
                    <Link className="font-semibold text-slate-950 hover:underline" to={`/customer/invoices/${encodeURIComponent(text(row.id))}`}>
                      <NameStack title={text(row.invoiceNumber) || "-"} detail={text(row.shopifyOrderNumber) || text(row.orderNumber) || text(row.externalOrderId)} />
                    </Link>
                  ),
                },
                { label: "Mağaza", value: (row) => text(row.storeName) },
                { label: "Net", value: (row) => moneyText(row.netTotal, row.currency), align: "right" },
                { label: "KDV", value: (row) => moneyText(row.taxTotal, row.currency), align: "right" },
                { label: "Brüt", value: (row) => moneyText(row.grossTotal, row.currency), align: "right" },
                { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                { label: "Tarih", value: (row) => dateText(row.createdAt) },
                {
                  label: "PDF",
                  value: (row) =>
                    text(row.pdfDownloadUrl) ? (
                      <a
                        href={text(row.pdfDownloadUrl)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        Aç
                      </a>
                    ) : (
                      <InvoicePdfButton invoiceId={text(row.id)} />
                    ),
                  align: "right",
                },
              ]}
            />
          </AdminPanel>
          <AdminPanel title="Etiket satın almaları" caption={`${numberText(filteredPurchases.length)} kayıt gösteriliyor.`} count={filteredPurchases.length}>
            <DataTable
              rows={filteredPurchases}
              empty="Satın alma yok."
              columns={[
                { label: "Fatura no", value: (row) => <ShopifyOrderCell row={row} /> },
                { label: "Ürünler", value: (row) => <ShopifyPurchaseProductsCell row={row} /> },
                { label: "Ödeme", value: (row) => <PaymentMethodCell row={row} /> },
                { label: "Fatura adet", value: (row) => numberText(row.invoiceQuantity || row.quantity), align: "right" },
                { label: "Net tutar", value: (row) => moneyText(row.totalPrice, "EUR"), align: "right" },
                { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                { label: "Tarih", value: (row) => dateText(row.createdAt) },
                { label: "PDF", value: (row) => <PurchaseInvoiceActions row={row} />, align: "right" },
              ]}
            />
          </AdminPanel>
        </>
      )}
    </div>
  );
}

function InvoiceDetailPage({ admin = false }: { admin?: boolean }) {
  const { invoiceId } = useParams();
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const endpoint = admin ? `/api/admin/invoices/${encodeURIComponent(invoiceId || "")}` : `/api/invoices/${encodeURIComponent(invoiceId || "")}`;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    apiClient
      .resource(endpoint)
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Fatura detayı alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [endpoint, reloadTick]);

  const invoice = (data?.invoice || {}) as DataRow;
  const lines = Array.isArray(data?.lines) ? (data?.lines as DataRow[]) : [];
  const backPath = admin ? "/admin/billing" : "/customer/invoices";

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <Link className="text-sm font-semibold text-slate-500 hover:text-slate-950" to={backPath}>
              Faturalara dön
            </Link>
            <h1 className="mt-2 text-2xl font-semibold text-slate-950">{text(invoice.invoiceNumber) || "Fatura detayı"}</h1>
            <p className="mt-1 text-sm text-slate-500">
              {admin ? text(invoice.userCompanyName) || text(invoice.userEmail) : text(invoice.storeName)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {text(invoice.pdfDownloadUrl) ? (
              <a
                href={text(invoice.pdfDownloadUrl)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-fit items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
              >
                <FileText className="h-4 w-4" />
                PDF aç
              </a>
            ) : null}
            {!loading && text(invoice.id) ? <InvoicePdfButton invoiceId={text(invoice.id)} admin={admin} /> : null}
          </div>
        </div>
      </section>

      {error ? <ErrorBanner message={error} /> : null}
      {loading ? (
        <section className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500 shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Yükleniyor
        </section>
      ) : (
        <>
          <section className="grid gap-4 lg:grid-cols-4">
            {[
              ["Durum", statusBadge(text(invoice.status))],
              ["Tutar", moneyText(invoice.grossTotal, invoice.currency)],
              ["Vergi", moneyText(invoice.taxTotal, invoice.currency)],
              ["Tarih", dateText(invoice.issuedAt || invoice.createdAt)],
            ].map(([label, value]) => (
              <div key={String(label)} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
                <div className="mt-2 text-lg font-semibold text-slate-950">{value}</div>
              </div>
            ))}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-950">Fatura bilgileri</h2>
            <div className="mt-4 grid gap-3 text-sm text-slate-700 md:grid-cols-2">
              <InfoLine label="Fatura no" value={text(invoice.invoiceNumber)} />
              {text(invoice.orderNumber) && text(invoice.orderNumber) !== text(invoice.invoiceNumber) ? <InfoLine label="Referans" value={text(invoice.orderNumber)} /> : null}
              <InfoLine label="Harici sipariş" value={text(invoice.externalOrderId)} />
              <InfoLine label="Platform" value={text(invoice.sourcePlatform)} />
              <InfoLine label="Mağaza" value={text(invoice.storeName)} />
              <InfoLine label="PDF tarihi" value={dateText(invoice.pdfGeneratedAt)} />
              <InfoLine label="PDF boyutu" value={text(invoice.pdfSizeBytes) ? `${numberText(invoice.pdfSizeBytes)} byte` : "-"} />
            </div>
          </section>

          {admin ? <AdminInvoiceEditPanel invoice={invoice} lines={lines} onSaved={() => setReloadTick((current) => current + 1)} /> : null}

          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-slate-950">Satırlar</h2>
            </div>
            <DataTable
              rows={lines}
              empty="Fatura satırı yok."
              columns={[
                { label: "Açıklama", value: (row) => <NameStack title={text(row.title)} detail={text(row.sku)} /> },
                { label: "Adet", value: (row) => numberText(row.quantity), align: "right" },
                { label: "Birim net", value: (row) => moneyText(row.unitNet, invoice.currency), align: "right" },
                { label: "Net", value: (row) => moneyText(row.netTotal, invoice.currency), align: "right" },
                { label: "Vergi", value: (row) => moneyText(row.taxTotal, invoice.currency), align: "right" },
                { label: "Brüt", value: (row) => moneyText(row.grossTotal, invoice.currency), align: "right" },
              ]}
            />
          </section>
        </>
      )}
    </div>
  );
}

function AdminInvoiceEditPanel({ invoice, lines, onSaved }: { invoice: DataRow; lines: DataRow[]; onSaved: () => void }) {
  const meta = (invoice.meta && typeof invoice.meta === "object" ? invoice.meta : {}) as Record<string, any>;
  const buyer = (meta.buyerSnapshot && typeof meta.buyerSnapshot === "object" ? meta.buyerSnapshot : {}) as Record<string, unknown>;
  const [form, setForm] = useState({
    buyerCity: text(buyer.city),
    buyerCountry: text(buyer.country),
    buyerEmail: text(buyer.email),
    buyerName: text(buyer.name) || text(invoice.userCompanyName) || text(invoice.userEmail),
    buyerPostalCode: text(buyer.postalCode),
    buyerStreet: text(buyer.street),
    currency: text(invoice.currency) || "EUR",
    issuedAt: text(invoice.issuedAt),
    note: text((meta.adminEditor || {}).note),
    orderNumber: text(invoice.orderNumber),
    status: text(invoice.status) || "final",
  });
  const [editLines, setEditLines] = useState(() =>
    (lines.length > 0 ? lines : [{ title: "Versand- und Logistikdienstleistung", quantity: 1, grossTotal: 0, taxRate: 0.19 }]).map((line) => {
      const quantity = Number(line.quantity || 1) || 1;
      const gross = Number(line.grossTotal || 0);
      return {
        id: text(line.id),
        quantity: String(quantity),
        sku: text(line.sku),
        taxRate: String(line.taxRate || 0.19),
        title: text(line.title) || "Versand- und Logistikdienstleistung",
        unitPrice: (quantity > 0 ? gross / quantity : gross).toFixed(2),
      };
    })
  );
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function setField(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function updateLine(index: number, key: keyof (typeof editLines)[number], value: string) {
    setEditLines((current) => current.map((line, lineIndex) => (lineIndex === index ? { ...line, [key]: value } : line)));
  }

  function addLine() {
    setEditLines((current) => [...current, { id: "", quantity: "1", sku: "", taxRate: "0.19", title: "Versand- und Logistikdienstleistung", unitPrice: "0.00" }]);
  }

  function removeLine(index: number) {
    setEditLines((current) => (current.length <= 1 ? current : current.filter((_, lineIndex) => lineIndex !== index)));
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.upsertAdminInvoice({
        ...form,
        customerId: text(invoice.userId),
        invoiceId: text(invoice.id),
        lines: editLines.map((line) => ({
          id: line.id || undefined,
          quantity: Number(line.quantity || 1),
          sku: line.sku || null,
          taxRate: Number(line.taxRate || 0.19),
          title: line.title,
          unitPrice: Number(line.unitPrice || 0),
        })),
      });
      setNotice(`Fatura güncellendi: ${result.invoiceNumber}`);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Fatura güncellenemedi.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-950">Admin fatura düzenleme</h2>
      <p className="mt-1 text-sm text-slate-500">Satır değişiklikleri kaydedildiğinde PDF yeniden üretilir ve bağlı komisyon tutarları güncellenir.</p>
      {notice ? <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{notice}</div> : null}
      {error ? <div className="mt-4"><ErrorBanner message={error} /></div> : null}
      <form className="mt-4 space-y-4" onSubmit={save}>
        <div className="grid gap-3 lg:grid-cols-4">
          <ProfileInput label="Alıcı adı" value={form.buyerName} onChange={(value) => setField("buyerName", value)} />
          <ProfileInput label="E-posta" value={form.buyerEmail} onChange={(value) => setField("buyerEmail", value)} />
          <ProfileInput label="Sokak" value={form.buyerStreet} onChange={(value) => setField("buyerStreet", value)} />
          <ProfileInput label="Posta kodu" value={form.buyerPostalCode} onChange={(value) => setField("buyerPostalCode", value)} />
          <ProfileInput label="Şehir" value={form.buyerCity} onChange={(value) => setField("buyerCity", value)} />
          <ProfileInput label="Ülke" value={form.buyerCountry} onChange={(value) => setField("buyerCountry", value)} />
          <ProfileInput label="Sipariş no" value={form.orderNumber} onChange={(value) => setField("orderNumber", value)} />
          <ProfileInput label="Tarih" type="datetime-local" value={form.issuedAt ? form.issuedAt.slice(0, 16) : ""} onChange={(value) => setField("issuedAt", value)} />
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">Durum</span>
            <select value={form.status} onChange={(event) => setField("status", event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none focus:border-slate-400">
              <option value="final">Final</option>
              <option value="draft">Taslak</option>
              <option value="cancelled">İptal</option>
            </select>
          </label>
          <ProfileInput label="Not" value={form.note} onChange={(value) => setField("note", value)} />
        </div>
        <div className="space-y-3">
          {editLines.map((line, index) => (
            <div key={`${line.id || "new"}-${index}`} className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 lg:grid-cols-[1.5fr_0.7fr_0.7fr_0.7fr_auto]">
              <ProfileInput label="Açıklama" value={line.title} onChange={(value) => updateLine(index, "title", value)} />
              <ProfileInput label="Adet" type="number" value={line.quantity} onChange={(value) => updateLine(index, "quantity", value)} />
              <ProfileInput label="Birim brüt" type="number" value={line.unitPrice} onChange={(value) => updateLine(index, "unitPrice", value)} />
              <ProfileInput label="Vergi" type="number" value={line.taxRate} onChange={(value) => updateLine(index, "taxRate", value)} />
              <button type="button" onClick={() => removeLine(index)} className="self-end rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
                Sil
              </button>
            </div>
          ))}
          <button type="button" onClick={addLine} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700">
            <PlusSquare className="h-3.5 w-3.5" />
            Satır ekle
          </button>
        </div>
        <button type="submit" disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          Kaydet ve PDF üret
        </button>
      </form>
    </section>
  );
}

function InfoLine({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <div className="mt-1 break-words text-slate-900">{value || "-"}</div>
    </div>
  );
}

type LabelBalanceMovement = {
  amount: number | null;
  carrier: string | null;
  createdAt: string | null;
  id: string;
  note: string | null;
  productName: string | null;
  quantity: number;
  source: "adjustment" | "purchase";
  status: string | null;
  title: string;
  type: string | null;
  unitPrice: number | null;
  weightMaxGrams: number;
  weightMinGrams: number;
};

type LabelBalanceFilter = "all" | "purchase" | "adjustment";

function balanceProductLabel(item: Pick<LabelBalanceSummary, "carrier" | "labelProductName" | "weightMaxGrams" | "weightMinGrams">) {
  const name = text(item.labelProductName);
  if (name) return name;
  const carrier = text(item.carrier).toUpperCase();
  const weight = orderWeightRangeText(item.weightMinGrams, item.weightMaxGrams);
  const fallback = weight !== "-" ? weight : "Etiket ürünü";
  return [carrier, fallback].filter(Boolean).join(" · ");
}

function balanceSortBucket(item: Pick<LabelBalanceSummary | LabelPurchaseSummary | LabelBalanceAdjustmentSummary, "labelProductName" | "weightMaxGrams" | "weightMinGrams">) {
  return labelProductSortBucket({
    name: text(item.labelProductName),
    weightMaxGrams: Number(item.weightMaxGrams || 0),
    weightMinGrams: Number(item.weightMinGrams || 0),
  });
}

function labelPurchaseTypeText(value: unknown) {
  const normalized = text(value).toLowerCase();
  if (normalized === "manual") return "Manuel yükleme";
  if (normalized === "purchase") return "Satın alma";
  if (normalized === "credit") return "Kredi";
  if (normalized === "debit") return "Düşüm";
  if (normalized === "refund") return "İade";
  return text(value) || "Hareket";
}

function labelPurchaseStatusText(value: unknown) {
  const normalized = text(value).toLowerCase();
  if (normalized === "completed") return "Tamamlandı";
  if (normalized === "cancelled" || normalized === "canceled") return "İptal";
  if (normalized === "pending") return "Bekliyor";
  if (normalized === "refunded") return "İade";
  return text(value) || "Tamamlandı";
}

function labelBalanceStatusBadge(value: unknown) {
  const normalized = text(value).toLowerCase() || "completed";
  const positive = ["completed", "paid", "success"].includes(normalized);
  const negative = ["cancelled", "canceled", "failed", "error"].includes(normalized);
  return (
    <span
      className={cx(
        "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
        positive && "border-emerald-200 bg-emerald-50 text-emerald-700",
        negative && "border-red-200 bg-red-50 text-red-700",
        !positive && !negative && "border-amber-200 bg-amber-50 text-amber-700"
      )}
    >
      {labelPurchaseStatusText(value)}
    </span>
  );
}

function CustomerBalancesPage() {
  const [data, setData] = useState<LabelBalancesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState<LabelBalanceFilter>("all");
  const [search, setSearch] = useState("");

  async function loadBalances() {
    setLoading(true);
    setError(null);
    try {
      const reconciliation = await apiClient.reconcileLabelPurchases({ sinceDays: 14 }).catch(() => null);
      const allocatedQuantity = reconciledLabelQuantity(reconciliation);
      if (allocatedQuantity > 0) {
        setNotice(`${numberText(allocatedQuantity)} etiketlik tamamlanmış Shopify ödemeniz bakiyeye işlendi.`);
      }
      const result = await apiClient.balances();
      setData(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etiket bakiyeleri alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadBalances();
  }, []);

  const balances = useMemo(() => {
    return [...(data?.balances || [])].sort((a, b) => {
      const bucketDiff = balanceSortBucket(a) - balanceSortBucket(b);
      if (bucketDiff) return bucketDiff;
      const weightDiff = Number(a.weightMaxGrams || 0) - Number(b.weightMaxGrams || 0);
      if (weightDiff) return weightDiff;
      return text(a.labelProductName).localeCompare(text(b.labelProductName), currentLocaleTag());
    });
  }, [data?.balances]);

  const productPurchases = useMemo(() => {
    const map = new Map<string, { lastPurchaseAt: string | null; purchasedQuantity: number; purchasedTotal: number }>();
    for (const productTotal of data?.productTotals || []) {
      const key = text(productTotal.labelProductId) || text(productTotal.labelProductName);
      if (!key) continue;
      map.set(key, {
        lastPurchaseAt: text(productTotal.lastPurchaseAt) || null,
        purchasedQuantity: Number(productTotal.purchasedQuantity || 0),
        purchasedTotal: Number(productTotal.purchasedTotalPrice || 0),
      });
    }
    return map;
  }, [data?.productTotals]);

  const movements = useMemo<LabelBalanceMovement[]>(() => {
    const purchaseRows: LabelBalanceMovement[] = (data?.recentPurchases || []).map((purchase) => ({
      amount: Number(purchase.totalPrice || 0),
      carrier: purchase.carrier,
      createdAt: purchase.createdAt,
      id: `purchase-${purchase.id}`,
      note: purchase.notes,
      productName: purchase.labelProductName,
      quantity: Number(purchase.quantity || 0),
      source: "purchase",
      status: purchase.status,
      title: purchase.bonus ? "Bonus etiket" : labelPurchaseTypeText(purchase.type),
      type: purchase.type,
      unitPrice: Number(purchase.unitPrice || 0),
      weightMaxGrams: Number(purchase.weightMaxGrams || 0),
      weightMinGrams: Number(purchase.weightMinGrams || 0),
    }));
    const adjustmentRows: LabelBalanceMovement[] = (data?.recentAdjustments || []).map((adjustment) => ({
      amount: null,
      carrier: adjustment.carrier,
      createdAt: adjustment.createdAt,
      id: `adjustment-${adjustment.id}`,
      note: adjustment.reason,
      productName: adjustment.labelProductName,
      quantity: Number(adjustment.delta || 0),
      source: "adjustment",
      status: "completed",
      title: Number(adjustment.delta || 0) >= 0 ? "Bakiye artışı" : "Bakiye düşümü",
      type: "adjustment",
      unitPrice: null,
      weightMaxGrams: Number(adjustment.weightMaxGrams || 0),
      weightMinGrams: Number(adjustment.weightMinGrams || 0),
    }));
    return [...purchaseRows, ...adjustmentRows].sort((a, b) => new Date(text(b.createdAt)).getTime() - new Date(text(a.createdAt)).getTime());
  }, [data?.recentAdjustments, data?.recentPurchases]);

  const visibleMovements = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase("tr-TR");
    return movements.filter((movement) => {
      if (filter !== "all" && movement.source !== filter) return false;
      if (!normalizedSearch) return true;
      const haystack = [movement.title, movement.productName, movement.carrier, movement.note, movement.status, movement.type]
        .map((value) => text(value).toLocaleLowerCase("tr-TR"))
        .join(" ");
      return haystack.includes(normalizedSearch);
    });
  }, [filter, movements, search]);

  const totals = data?.totals || {
    expiredLabels: 0,
    lastActivityAt: null,
    lastBalanceUpdateAt: null,
    lastPurchaseAt: null,
    productCount: balances.length,
    purchaseCount: 0,
    purchasedQuantity: 0,
    purchasedTotalPrice: 0,
    totalAvailable: balances.reduce((sum, balance) => sum + Number(balance.quantityAvailable || 0), 0),
    totalLabels: 0,
    usedLabels: 0,
    voidedLabels: 0,
  };
  const labelInventory = data?.labelInventory;
  const totalSourceLabels = Number(labelInventory?.total ?? totals.totalLabels ?? totals.purchasedQuantity ?? 0);
  const usedSourceLabels = Number(labelInventory?.consumed ?? totals.usedLabels ?? 0);
  const voidedSourceLabels = Number(labelInventory?.voided ?? totals.voidedLabels ?? 0);

  if (loading) return <LoadingPanel label="Etiket bakiyeleri yükleniyor" />;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950">Etiket bakiyeleri</h1>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
            Satın alınan, yüklenen ve kalan etiket kredilerini ürün ve hareket bazında yönetin.
          </p>
        </div>
        <div className="shrink-0">
          <Link
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            to="/customer/buy-labels"
          >
            <ShoppingCart className="h-4 w-4" />
            Etiket satın al
          </Link>
        </div>
      </div>

      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}

      <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 shadow-sm">
        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
        <div>
          <p className="font-semibold">Bonus etiketleri nasıl kullanırım?</p>
          <p className="mt-1 leading-6">Bonus etiketler ilgili ürünün normal bakiyesine otomatik eklenir. Ayrı bir kod girmeniz veya ödeme yapmanız gerekmez; siparişten etiket oluşturduğunuzda uygun Kleinpaket, 2 kg, 5 kg ya da diğer ürün bakiyesinden otomatik kullanılır.</p>
        </div>
      </div>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Kullanılabilir" value={totals.totalAvailable} icon={<Tags className="h-5 w-5" />} tone="emerald" />
        <AdminStatCard label="Toplam etiket" value={totalSourceLabels} icon={<Package className="h-5 w-5" />} tone="cyan" />
        <AdminStatCard label="Kullanıldı" value={usedSourceLabels} icon={<HandCoins className="h-5 w-5" />} tone="violet" />
        <AdminStatCard label="Void" value={voidedSourceLabels} icon={<AlertTriangle className="h-5 w-5" />} tone="red" />
      </section>

      <section className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <AdminPanel
          title="Ürün bazlı bakiye"
          caption={`${numberText(totals.productCount)} ürün için kalan etiket kredileri.`}
          count={balances.length}
        >
          {balances.length === 0 ? (
            <div className="flex min-h-56 flex-col items-center justify-center gap-3 bg-slate-50/70 p-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 shadow-sm">
                <Wallet className="h-5 w-5" />
              </div>
              <p className="text-sm font-medium text-slate-600">Henüz etiket bakiyesi yok.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {balances.map((balance, index) => {
                const productStats =
                  productPurchases.get(text(balance.labelProductId)) || productPurchases.get(text(balance.labelProductName)) || {
                    lastPurchaseAt: null,
                    purchasedQuantity: 0,
                    purchasedTotal: 0,
                  };
                const available = Number(balance.quantityAvailable || 0);
                const purchasedQuantity = Number(productStats.purchasedQuantity || 0);
                const ratio = purchasedQuantity > 0 ? Math.max(0, Math.min(100, Math.round((available / purchasedQuantity) * 100))) : available > 0 ? 100 : 0;
                const lowBalance = available > 0 && available <= 10;
                const emptyBalance = available <= 0;
                const balanceKey = text(balance.id) || text(balance.labelProductId) || text(balance.labelProductName) || `balance-${index}`;
                return (
                  <div
                    key={balanceKey}
                    className="grid gap-4 px-4 py-4 transition hover:bg-slate-50/70 lg:grid-cols-[minmax(0,1fr)_minmax(150px,auto)] lg:items-center 2xl:grid-cols-[minmax(0,1.25fr)_110px_260px_minmax(145px,auto)]"
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <div
                        className={cx(
                          "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border p-2 shadow-sm",
                          emptyBalance && "border-red-100 bg-red-50 text-red-600",
                          lowBalance && "border-amber-100 bg-amber-50 text-amber-600",
                          !emptyBalance && !lowBalance && "border-emerald-100 bg-emerald-50 text-emerald-600"
                        )}
                      >
                        <Tags className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          <h3 className="max-w-full truncate text-sm font-semibold text-slate-950">
                            {text(balance.labelProductName) || text(balance.labelProductId) || "Etiket ürünü"}
                          </h3>
                          <span className="inline-flex whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                            {text(balance.carrier) || "DHL"}
                          </span>
                          <span
                            className={cx(
                              "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
                              emptyBalance && "border-red-200 bg-red-50 text-red-700",
                              lowBalance && "border-amber-200 bg-amber-50 text-amber-700",
                              !emptyBalance && !lowBalance && "border-emerald-200 bg-emerald-50 text-emerald-700"
                            )}
                          >
                            {emptyBalance ? "Yok" : lowBalance ? "Düşük" : "Müsait"}
                          </span>
                        </div>
                        <p className="mt-1 truncate text-xs text-slate-500">{orderWeightRangeText(balance.weightMinGrams, balance.weightMaxGrams)}</p>
                        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className={cx("h-full rounded-full", emptyBalance ? "bg-red-400" : lowBalance ? "bg-amber-400" : "bg-emerald-500")}
                            style={{ width: `${ratio}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Kalan</p>
                      <p className="mt-1 text-sm font-semibold text-slate-950">{numberText(available)}</p>
                    </div>
                    <div className="grid min-w-0 gap-3 sm:grid-cols-2">
                      <div className="min-w-0 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Alınan</p>
                        <p className="mt-1 text-sm font-semibold text-slate-950">{numberText(purchasedQuantity)}</p>
                      </div>
                      <div className="min-w-0 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Son alım</p>
                        <p className="mt-1 truncate text-sm font-semibold text-slate-950">{dateText(productStats.lastPurchaseAt)}</p>
                      </div>
                    </div>
                    <div className="flex min-w-0 flex-wrap gap-2 lg:justify-end">
                      <Link
                        to="/customer/buy-labels"
                        className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-slate-900 px-3 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
                      >
                        <ShoppingCart className="h-3.5 w-3.5" />
                        Satın al
                      </Link>
                      <Link
                        to="/customer/label-inventory"
                        className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                      >
                        <Package className="h-3.5 w-3.5" />
                        Envanter
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </AdminPanel>

        <AdminPanel title="Kredi hareketleri" caption="Son satın alma ve manuel bakiye işlemleri." count={visibleMovements.length}>
          <div className="grid gap-2 border-b border-slate-100 p-3">
            <label className="relative block min-w-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Ürün, not veya durum ara"
                className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-950 outline-none transition focus:border-slate-400"
              />
            </label>
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value as LabelBalanceFilter)}
              className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-slate-400"
            >
              <option value="all">Tüm hareketler</option>
              <option value="purchase">Satın alma/yükleme</option>
              <option value="adjustment">Manuel hareketler</option>
            </select>
          </div>
          {visibleMovements.length === 0 ? (
            <div className="flex min-h-48 flex-col items-center justify-center gap-3 bg-slate-50/70 p-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 shadow-sm">
                <History className="h-5 w-5" />
              </div>
              <p className="text-sm font-medium text-slate-600">Gösterilecek hareket yok.</p>
            </div>
          ) : (
            <div className="max-h-[760px] overflow-y-auto p-3">
              <div className="grid gap-2">
                {visibleMovements.map((movement, index) => {
                  const positive = movement.quantity >= 0;
                  return (
                    <div
                      key={text(movement.id) || `${movement.source}-${index}`}
                      className="group rounded-lg border border-slate-200 bg-white p-3 transition hover:border-slate-300 hover:bg-slate-50"
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        <div
                          className={cx(
                            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ring-1",
                            movement.source === "purchase" ? "bg-cyan-50 text-cyan-700 ring-cyan-100" : "bg-amber-50 text-amber-700 ring-amber-100"
                          )}
                        >
                          {movement.source === "purchase" ? <ShoppingCart className="h-4 w-4" /> : <History className="h-4 w-4" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-slate-950">{movement.title}</p>
                              <p className="mt-0.5 truncate text-xs text-slate-500">
                                {text(movement.productName) || "Etiket ürünü"} · {orderWeightRangeText(movement.weightMinGrams, movement.weightMaxGrams)}
                              </p>
                            </div>
                            <span className={cx("shrink-0 text-sm font-semibold", positive ? "text-emerald-700" : "text-red-700")}>
                              {positive ? "+" : ""}
                              {numberText(movement.quantity)}
                            </span>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            {labelBalanceStatusBadge(movement.status)}
                            <span className="text-xs font-medium text-slate-500">{dateText(movement.createdAt)}</span>
                            {movement.amount !== null ? <span className="text-xs font-semibold text-slate-700">{moneyText(movement.amount, "EUR")}</span> : null}
                          </div>
                          {movement.note ? <p className="mt-2 text-xs leading-5 text-slate-500">{movement.note}</p> : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </AdminPanel>
      </section>
    </div>
  );
}

type LabelInventoryResponse = {
  labels?: DataRow[];
  meta?: {
    canAdminManage?: boolean;
    hasMore?: boolean;
    limit?: number;
    offset?: number;
    returnedCount?: number;
    totalCount?: number;
  };
  products?: DataRow[];
  summary?: DataRow;
};

const customerLabelInventoryStatusOptions = [
  { label: "Tüm durumlar", value: "all" },
  { label: "Müsait", value: "assigned" },
  { label: "Kullanıldı", value: "consumed" },
  { label: "Void", value: "void" },
  { label: "Süresi doldu", value: "expired" },
];

const labelInventoryExternalOptions = [
  { label: "Tüm dışa aktarımlar", value: "all" },
  { label: "Export edilmemiş", value: "unexported" },
  { label: "Export edilmiş", value: "exported" },
  { label: "Dış sistemde kullanılmamış", value: "unused" },
  { label: "Dış sistemde kullanılmış", value: "used" },
];

function labelInventoryStatusLabel(value: unknown) {
  const normalized = text(value).toLowerCase();
  if (normalized === "assigned") return "Müsait";
  if (normalized === "available") return "Stokta";
  if (normalized === "consumed" || normalized === "used") return "Kullanıldı";
  if (normalized === "void") return "Void";
  if (normalized === "expired") return "Süresi doldu";
  return text(value) || "-";
}

function labelInventoryPill(value: string, tone: "amber" | "emerald" | "red" | "slate" | "violet") {
  return (
    <span
      className={cx(
        "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
        tone === "amber" && "border-amber-200 bg-amber-50 text-amber-700",
        tone === "emerald" && "border-emerald-200 bg-emerald-50 text-emerald-700",
        tone === "red" && "border-red-200 bg-red-50 text-red-700",
        tone === "slate" && "border-slate-200 bg-slate-50 text-slate-600",
        tone === "violet" && "border-violet-200 bg-violet-50 text-violet-700"
      )}
    >
      {value || "-"}
    </span>
  );
}

function labelInventoryStatusBadge(value: unknown) {
  const normalized = text(value).toLowerCase();
  if (normalized === "assigned" || normalized === "available") return labelInventoryPill(labelInventoryStatusLabel(value), "emerald");
  if (normalized === "consumed" || normalized === "used") return labelInventoryPill(labelInventoryStatusLabel(value), "violet");
  if (normalized === "void" || normalized === "expired") return labelInventoryPill(labelInventoryStatusLabel(value), "red");
  return labelInventoryPill(labelInventoryStatusLabel(value), "amber");
}

function labelInventoryExternalBadges(row: DataRow) {
  const exported = Boolean(row.externalExportedAt) || Number(row.externalExportCount || 0) > 0;
  return (
    <div className="flex flex-wrap gap-1.5">
      {exported ? labelInventoryPill("Export edildi", "violet") : labelInventoryPill("Export yok", "slate")}
      {row.externalUsedAt ? labelInventoryPill("Dış sistemde kullanıldı", "emerald") : null}
    </div>
  );
}

function labelInventoryCanExport(row: DataRow) {
  return text(row.id) && text(row.status).toLowerCase() === "assigned" && Boolean(text(row.savedKey));
}

function labelInventoryCanAdminManage(row: DataRow) {
  const carrierCheckStatus = text(row.carrierCheckStatus || "unchecked").toLowerCase();
  return (
    Boolean(text(row.id)) &&
    text(row.status).toLowerCase() === "assigned" &&
    !text(row.consumedAt) &&
    !text(row.externalUsedAt) &&
    (carrierCheckStatus === "unchecked" || carrierCheckStatus === "clean")
  );
}

function CustomerLabelInventoryPage() {
  const [labels, setLabels] = useState<DataRow[]>([]);
  const [products, setProducts] = useState<DataRow[]>([]);
  const [summary, setSummary] = useState<DataRow>({});
  const [meta, setMeta] = useState<NonNullable<LabelInventoryResponse["meta"]>>({});
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [externalState, setExternalState] = useState("all");
  const [labelProductId, setLabelProductId] = useState("all");
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState<DataRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [carrierDetailsRow, setCarrierDetailsRow] = useState<DataRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<"selected-zip" | "filtered-zip" | "filtered-csv" | null>(null);
  const [inventoryManaging, setInventoryManaging] = useState<"replace" | "return" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [exportResult, setExportResult] = useState<{ downloadUrl: string; exportedCount: number; format: "csv" | "zip"; skippedCount?: number } | null>(null);
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  const offset = page * pageSize;
  const canAdminManage = Boolean(meta.canAdminManage);
  const currentReadyIds = useMemo(
    () => labels.filter(canAdminManage ? labelInventoryCanAdminManage : labelInventoryCanExport).map((row) => text(row.id)),
    [canAdminManage, labels]
  );
  const selectedReadyIds = useMemo(() => currentReadyIds.filter((id) => selectedIds.has(id)), [currentReadyIds, selectedIds]);
  const selectedReadyRows = useMemo(
    () => labels.filter((row) => selectedReadyIds.includes(text(row.id))),
    [labels, selectedReadyIds]
  );
  const selectedExportIds = useMemo(
    () => selectedReadyRows.filter(labelInventoryCanExport).map((row) => text(row.id)),
    [selectedReadyRows]
  );
  const selectedPreviouslyExportedCount = useMemo(
    () => selectedReadyRows.filter((row) => Boolean(row.externalExportedAt) || Number(row.externalExportCount || 0) > 0).length,
    [selectedReadyRows]
  );
  const allCurrentReadySelected = currentReadyIds.length > 0 && currentReadyIds.every((id) => selectedIds.has(id));

  function inventoryEndpoint() {
    const params = new URLSearchParams();
    params.set("limit", String(pageSize));
    params.set("offset", String(offset));
    if (search.trim()) params.set("search", search.trim());
    if (status !== "all") params.set("status", status);
    if (externalState !== "all") params.set("externalState", externalState);
    if (labelProductId !== "all") params.set("labelProductId", labelProductId);
    return `/api/label-inventory?${params.toString()}`;
  }

  function resetPageAndSelection() {
    setPage(0);
    setSelectedIds(new Set());
  }

  useEffect(() => {
    let active = true;
    const timeout = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      apiClient
        .resource<LabelInventoryResponse>(inventoryEndpoint())
        .then((result) => {
          if (!active) return;
          const nextLabels = Array.isArray(result.labels) ? result.labels : [];
          setLabels(nextLabels);
          setProducts(Array.isArray(result.products) ? result.products : []);
          setSummary(result.summary || {});
          setMeta(result.meta || {});
          const visibleIds = new Set(nextLabels.map((row) => text(row.id)).filter(Boolean));
          setSelectedIds((current) => new Set([...current].filter((id) => visibleIds.has(id))));
        })
        .catch((err) => {
          if (active) setError(err instanceof ApiError ? err.message : "Etiket envanteri alınamadı.");
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, search.trim() ? 250 : 0);
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [search, status, externalState, labelProductId, page, pageSize]);

  async function loadHistory() {
    setHistoryLoading(true);
    setError(null);
    try {
      const result = await apiClient.resource<{ exports?: DataRow[] }>("/api/label-inventory-export-history");
      setHistoryRows(Array.isArray(result.exports) ? result.exports : []);
      setHistoryOpen(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Export geçmişi alınamadı.");
    } finally {
      setHistoryLoading(false);
    }
  }

  function toggleSelected(id: string, checked: boolean) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function toggleCurrentPage(checked: boolean) {
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const id of currentReadyIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  async function onExport(mode: "selected" | "filtered", format: "csv" | "zip") {
    const actionKey = `${mode}-${format}` as "selected-zip" | "filtered-zip" | "filtered-csv";
    const selectedModeIds = mode === "selected" ? selectedExportIds : [];
    if (mode === "selected" && selectedModeIds.length === 0) {
      setError("Export için en az bir müsait etiket seçin.");
      return;
    }

    setExporting(actionKey);
    setError(null);
    setNotice(null);
    setExportResult(null);

    const filterPayload = {
      externalState: externalState !== "all" ? externalState : undefined,
      labelProductId: labelProductId !== "all" ? labelProductId : undefined,
      labelSourceIds: mode === "selected" ? selectedModeIds : undefined,
      search: search.trim() || undefined,
      status: mode === "filtered" ? (status === "all" ? "assigned" : status) : undefined,
    };

    try {
      const preview = await apiClient.previewLabelInventoryExport(filterPayload);
      if (preview.requestedCount === 0) {
        throw new Error("Bu seçim veya filtre için export edilecek etiket yok.");
      }
      if (preview.requestedCount > preview.maxExportLabels) {
        throw new Error(`Tek seferde en fazla ${preview.maxExportLabels} etiket export edilebilir. Filtreleri daraltın veya seçim yapın.`);
      }
      if (preview.externallyExportedCount > 0) {
        const confirmed = await askConfirmation({
          title: "Etiketler tekrar export edilsin mi?",
          description: `${numberText(preview.externallyExportedCount)} etiket daha önce export edilmiş.`,
          details: ["Devam ederseniz bu etiketler tekrar export dosyasına alınır ve export geçmişi güncellenir."],
          confirmLabel: "Tekrar export et",
          tone: "warning",
        });
        if (!confirmed) return;
      }
      const result = await apiClient.exportLabelInventory({
        ...filterPayload,
        allowRepeatedExternalExport: preview.externallyExportedCount > 0,
        format,
        markExported: true,
      });
      setExportResult({ downloadUrl: result.downloadUrl, exportedCount: result.exportedCount, format: result.format || format, skippedCount: result.skippedCount });
      setNotice(`${numberText(result.exportedCount)} etiket ${format.toUpperCase()} olarak hazırlandı.`);
      setSelectedIds(new Set());
      await apiClient.resource<LabelInventoryResponse>(inventoryEndpoint()).then((resultAfterExport) => {
        setLabels(Array.isArray(resultAfterExport.labels) ? resultAfterExport.labels : []);
        setProducts(Array.isArray(resultAfterExport.products) ? resultAfterExport.products : []);
        setSummary(resultAfterExport.summary || {});
        setMeta(resultAfterExport.meta || {});
      });
      if (historyOpen) await loadHistory();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Envanter export oluşturulamadı.");
    } finally {
      setExporting(null);
    }
  }

  async function onManageInventory(action: "replace" | "return") {
    if (selectedReadyIds.length === 0) {
      setError("İşlem için en az bir atanmış ve kullanılmamış etiket seçin.");
      return;
    }
    if (action === "return" && selectedPreviouslyExportedCount > 0) {
      setError("Daha önce indirilen etiketler doğrudan havuza iade edilemez. Bu etiketleri yeni etiketlerle değiştirin.");
      return;
    }

    const confirmed = await askConfirmation({
      title: action === "replace" ? "Seçili etiketler değiştirilsin mi?" : "Seçili etiketler havuza iade edilsin mi?",
      description:
        action === "replace"
          ? `${numberText(selectedReadyIds.length)} etiket, aynı ürün grubundaki temiz etiketlerle değiştirilecek.`
          : `${numberText(selectedReadyIds.length)} kullanılmamış etiket müşteriden alınarak stoğa döndürülecek.`,
      details:
        action === "replace"
          ? ["Eski etiketler hatalı stok olarak ayrılacak.", "Müşterinin toplam kullanılabilir etiketi ve satın alma geçmişi değişmeyecek."]
          : [
              "Bu admin onayı, müşteriye son 3 ay içinde verilen fakat hiç indirilmemiş ve kullanılmamış etiketlerde bekleme kuralını aşar.",
              "İndirilmiş, dış sistemde kullanılmış veya gerçek DHL hareketi bulunan etiketler yine kesinlikle engellenir.",
              "Müşterinin kullanılabilir etiketi seçilen adet kadar azalacak.",
            ],
      confirmLabel: action === "replace" ? "Etiketleri değiştir" : "Onayla ve havuza iade et",
      tone: "warning",
    });
    if (!confirmed) return;

    setInventoryManaging(action);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.manageAssignedLabelSelection({
        action,
        labelSourceIds: selectedReadyIds,
        overrideRecentCustomerHold: action === "return",
        overrideReason:
          action === "return"
            ? "Admin destek modunda açık onay vererek son 3 aylık müşteri koruma süresini aştı."
            : undefined,
      });
      setNotice(
        action === "replace"
          ? `${numberText(result.replacedCount)} etiket temiz etiketlerle değiştirildi.`
          : `${numberText(result.returnedCount)} etiket güvenli biçimde havuza iade edildi${result.recentCustomerHoldOverrideCount > 0 ? `; ${numberText(result.recentCustomerHoldOverrideCount)} etiket için 3 aylık bekleme kuralı admin onayıyla aşıldı` : ""}.`
      );
      setSelectedIds(new Set());
      const refreshed = await apiClient.resource<LabelInventoryResponse>(inventoryEndpoint());
      setLabels(Array.isArray(refreshed.labels) ? refreshed.labels : []);
      setProducts(Array.isArray(refreshed.products) ? refreshed.products : []);
      setSummary(refreshed.summary || {});
      setMeta(refreshed.meta || {});
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Etiket işlemi tamamlanamadı.");
    } finally {
      setInventoryManaging(null);
    }
  }

  return (
    <div className="space-y-5">
      {confirmationDialog}
      <CustomerPageHeader
        title="Etiket envanteri"
        caption="Atanmış etiketleri takip edin, tekil PDF indirin ve seçili ya da filtreli kayıtları ZIP/CSV olarak dışa aktarın."
        icon={<Tags className="h-5 w-5" />}
        action={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void onExport("selected", "zip")}
              disabled={exporting !== null || selectedExportIds.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              {exporting === "selected-zip" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
              Seçili ZIP
            </button>
            <button
              type="button"
              onClick={() => void onExport("filtered", "zip")}
              disabled={exporting !== null}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
            >
              {exporting === "filtered-zip" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Filtre ZIP
            </button>
            <button
              type="button"
              onClick={() => void onExport("filtered", "csv")}
              disabled={exporting !== null}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
            >
              {exporting === "filtered-csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              CSV
            </button>
            <button
              type="button"
              onClick={() => void loadHistory()}
              disabled={historyLoading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
            >
              {historyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
              İndirme geçmişi
            </button>
          </div>
        }
      />

      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}
      {exportResult ? (
        <div className="flex flex-col gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 sm:flex-row sm:items-center sm:justify-between">
          <span>
            {numberText(exportResult.exportedCount)} etiket {exportResult.format.toUpperCase()} dosyasına yazıldı
            {exportResult.skippedCount ? `, ${numberText(exportResult.skippedCount)} kayıt rapora alındı` : ""}.
          </span>
          <a className="inline-flex items-center gap-2 font-semibold text-emerald-900 hover:underline" href={exportResult.downloadUrl} target="_blank" rel="noreferrer">
            <Download className="h-4 w-4" />
            Dosyayı indir
          </a>
        </div>
      ) : null}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <AdminStatCard label="Toplam" value={summary.total || 0} tone="cyan" icon={<Tags className="h-5 w-5" />} />
        <AdminStatCard label="Müsait" value={summary.assigned || 0} tone="emerald" icon={<CheckCircle2 className="h-5 w-5" />} />
        <AdminStatCard label="Kullanıldı" value={summary.consumed || 0} tone="violet" icon={<Package className="h-5 w-5" />} />
        <AdminStatCard label="Export" value={summary.exported || 0} tone="amber" icon={<Download className="h-5 w-5" />} />
        <AdminStatCard label="Void" value={summary.voided || 0} tone="red" icon={<AlertTriangle className="h-5 w-5" />} />
        <AdminStatCard label="Yolda" value={summary.inTransit || 0} tone="amber" icon={<Truck className="h-5 w-5" />} />
      </section>

      {meta.canAdminManage ? (
        <AdminPanel
          title="Admin etiket işlemi"
          caption="Müşteriye atanmış fakat henüz kullanılmamış etiketleri güvenli biçimde iade edin veya temiz etiketlerle değiştirin."
          action={
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void onManageInventory("return")}
                disabled={inventoryManaging !== null || selectedReadyIds.length === 0 || selectedPreviouslyExportedCount > 0}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
              >
                {inventoryManaging === "return" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                Havuza iade
              </button>
              <button
                type="button"
                onClick={() => void onManageInventory("replace")}
                disabled={inventoryManaging !== null || selectedReadyIds.length === 0}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
              >
                {inventoryManaging === "replace" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Yeni etiketle değiştir
              </button>
            </div>
          }
        >
          <div className="grid gap-3 p-5 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">Seçili etiket</p>
              <p className="mt-1 text-xl font-bold text-slate-900">{numberText(selectedReadyIds.length)}</p>
            </div>
            <div className={cx("rounded-xl border px-4 py-3", selectedPreviouslyExportedCount > 0 ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50")}>
              <p className={cx("text-xs font-semibold uppercase tracking-[0.08em]", selectedPreviouslyExportedCount > 0 ? "text-amber-700" : "text-emerald-700")}>
                Daha önce indirilen
              </p>
              <p className={cx("mt-1 text-xl font-bold", selectedPreviouslyExportedCount > 0 ? "text-amber-900" : "text-emerald-900")}>
                {numberText(selectedPreviouslyExportedCount)}
              </p>
              {selectedPreviouslyExportedCount > 0 ? <p className="mt-1 text-xs font-medium text-amber-700">Bu seçim yalnızca değiştirilebilir.</p> : null}
            </div>
          </div>
        </AdminPanel>
      ) : null}

      <AdminPanel title="Filtreler" caption="Takip numarası, ürün, durum ve dış sistem kullanımına göre listeyi daraltın.">
        <div className="grid gap-3 p-5 lg:grid-cols-[minmax(220px,1fr)_180px_220px_220px_140px]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                resetPageAndSelection();
              }}
              className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
              placeholder="Takip no, dosya adı veya ürün ara"
            />
          </label>
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              resetPageAndSelection();
            }}
            className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
          >
            {customerLabelInventoryStatusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={externalState}
            onChange={(event) => {
              setExternalState(event.target.value);
              resetPageAndSelection();
            }}
            className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
          >
            {labelInventoryExternalOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={labelProductId}
            onChange={(event) => {
              setLabelProductId(event.target.value);
              resetPageAndSelection();
            }}
            className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
          >
            <option value="all">Tüm ürünler</option>
            {products.map((product) => (
              <option key={text(product.id)} value={text(product.id)}>
                {text(product.name) || "-"} ({numberText(product.count)})
              </option>
            ))}
          </select>
          <select
            value={pageSize}
            onChange={(event) => {
              setPageSize(Number(event.target.value));
              resetPageAndSelection();
            }}
            className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
          >
            {[50, 100, 250].map((size) => (
              <option key={size} value={size}>
                {size}/{translatedPanelText("sayfa")}
              </option>
            ))}
          </select>
        </div>
      </AdminPanel>

      {historyOpen ? (
        <AdminPanel
          title="Download geçmişi"
          caption="Önceden oluşturulan CSV/ZIP export dosyaları."
          count={historyRows.length}
          action={
            <button
              type="button"
              onClick={() => setHistoryOpen(false)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <X className="h-4 w-4" />
              Kapat
            </button>
          }
        >
          <DataTable
            rows={historyRows}
            empty="Henüz export geçmişi yok."
            columns={[
              { label: "Tarih", value: (row) => dateText(row.finishedAt || row.createdAt) },
              { label: "Kaynak", value: (row) => text(row.source) || "-" },
              { label: "Durum", value: (row) => statusBadge(text(row.status || row.terminalState)) },
              { label: "Adet", value: (row) => numberText(row.totalLabels || row.requestedCount), align: "right" },
              {
                label: "Dosya",
                value: (row) =>
                  text(row.resultKey) ? (
                    <a
                      href={`/api/label-inventory-export-download?key=${encodeURIComponent(text(row.resultKey))}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 font-semibold text-cyan-700 hover:underline"
                    >
                      <Download className="h-4 w-4" />
                      İndir
                    </a>
                  ) : (
                    "-"
                  ),
              },
            ]}
          />
        </AdminPanel>
      ) : null}

      <AdminPanel
        title="Etiketler"
        caption={`${numberText(meta.totalCount || 0)} kayıt içinde ${numberText(offset + 1)}-${numberText(offset + labels.length)} aralığı gösteriliyor. ${numberText(selectedReadyIds.length)} müsait etiket seçili.`}
        count={labels.length}
      >
        {loading ? (
          <div className="p-5"><LoadingPanel label="Etiket envanteri yükleniyor" /></div>
        ) : labels.length === 0 ? (
          <div className="flex min-h-48 flex-col items-center justify-center gap-3 bg-slate-50/60 p-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-400 shadow-sm">
              <Tags className="h-5 w-5" />
            </div>
            <p className="text-sm font-medium text-slate-600">Bu filtreye uygun etiket yok.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1180px] divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                  <tr>
                    <th className="w-12 px-5 py-3.5">
                      <input
                        type="checkbox"
                        checked={allCurrentReadySelected}
                        disabled={currentReadyIds.length === 0}
                        onChange={(event) => toggleCurrentPage(event.target.checked)}
                        className="h-4 w-4 rounded border-slate-300"
                        aria-label="Sayfadaki müsait etiketleri seç"
                      />
                    </th>
                    <th className="px-5 py-3.5">Takip no</th>
                    <th className="px-5 py-3.5">Etiket ürünü</th>
                    <th className="px-5 py-3.5">Durum</th>
                    <th className="px-5 py-3.5">Carrier</th>
                    <th className="px-5 py-3.5">Dış sistem</th>
                    <th className="px-5 py-3.5">Atandı</th>
                    <th className="px-5 py-3.5">Kullanıldı</th>
                    <th className="px-5 py-3.5 text-right">İndir</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {labels.map((row) => {
                    const id = text(row.id);
                    const canExport = labelInventoryCanExport(row);
                    const canSelect = canAdminManage ? labelInventoryCanAdminManage(row) : canExport;
                    return (
                      <tr key={id} className="align-top transition hover:bg-slate-50/70">
                        <td className="px-5 py-4">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(id)}
                            disabled={!canSelect}
                            onChange={(event) => toggleSelected(id, event.target.checked)}
                            className="h-4 w-4 rounded border-slate-300 disabled:opacity-40"
                            aria-label={`${text(row.trackingNumber) || id} seç`}
                          />
                        </td>
                        <td className="px-5 py-4">
                          <NameStack title={text(row.trackingNumber) || "-"} detail={text(row.originalFileName || row.savedFileName)} />
                        </td>
                        <td className="px-5 py-4">
                          <NameStack title={text(row.labelProductName) || "-"} detail={[text(row.carrier), text(row.weightText)].filter(Boolean).join(" · ")} />
                        </td>
                        <td className="px-5 py-4">{labelInventoryStatusBadge(row.status)}</td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap items-center gap-2">
                            {carrierDeliveryBadge(row)}
                            <button
                              type="button"
                              onClick={() => setCarrierDetailsRow(row)}
                              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                            >
                              <Clock3 className="h-3.5 w-3.5" />
                              Event
                            </button>
                          </div>
                        </td>
                        <td className="px-5 py-4">{labelInventoryExternalBadges(row)}</td>
                        <td className="px-5 py-4 text-slate-700">{dateText(row.assignedAt || row.createdAt)}</td>
                        <td className="px-5 py-4 text-slate-700">{dateText(row.consumedAt)}</td>
                        <td className="px-5 py-4 text-right">
                          {text(row.downloadUrl) ? (
                            <a
                              href={text(row.downloadUrl)}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                            >
                              <Download className="h-4 w-4" />
                              PDF
                            </a>
                          ) : (
                            <span className="text-xs font-medium text-slate-400">PDF yok</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col gap-3 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">
                {numberText(meta.totalCount || 0)} kayıttan {numberText(offset + 1)}-{numberText(offset + labels.length)} gösteriliyor.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Önceki
                </button>
                <button
                  type="button"
                  disabled={!meta.hasMore}
                  onClick={() => setPage((current) => current + 1)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Sonraki
                </button>
              </div>
            </div>
          </>
        )}
      </AdminPanel>
      <CarrierEventsDrawer row={carrierDetailsRow} onClose={() => setCarrierDetailsRow(null)} />
    </div>
  );
}

function CustomerAffiliatePage() {
  const [data, setData] = useState<AffiliateDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [referralSearch, setReferralSearch] = useState("");
  const [eventStatusFilter, setEventStatusFilter] = useState("all");
  const [labelProductId, setLabelProductId] = useState("");
  const [labelQuantity, setLabelQuantity] = useState("1");
  const [redeemingLabels, setRedeemingLabels] = useState(false);

  async function loadAffiliate() {
    setLoading(true);
    setError(null);
    try {
      setData(await apiClient.affiliate());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Affiliate bilgileri alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAffiliate();
  }, []);

  useEffect(() => {
    const products = data?.labelCredit?.products || [];
    if (!products.length) {
      setLabelProductId("");
      return;
    }
    setLabelProductId((current) => products.some((product) => product.id === current) ? current : products[0].id);
  }, [data?.labelCredit?.products]);

  async function joinProgram() {
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.createAffiliateProfile();
      setNotice(result.created ? "Affiliate programı açıldı." : "Affiliate profiliniz zaten aktif.");
      await loadAffiliate();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Affiliate profili oluşturulamadı.");
    } finally {
      setCreating(false);
    }
  }

  async function copyReferralLink() {
    const link = data?.profile?.referralLink;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setNotice("Referans linki kopyalandı.");
    } catch {
      setNotice(link);
    }
  }

  async function redeemLabelCredits() {
    const quantity = Number(labelQuantity);
    if (!labelProductId || !Number.isInteger(quantity) || quantity <= 0) {
      setError("Etiket ürünü ve pozitif tam adet seçin.");
      return;
    }
    setRedeemingLabels(true);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.redeemAffiliateLabelCredits({
        idempotencyKey: `affiliate-label-credit:${crypto.randomUUID()}`,
        labelProductId,
        quantity,
      });
      setNotice(`${result.redemption.quantity} etiket bakiyenize eklendi. Kullanılan kazanç: ${moneyText(result.redemption.amount, "EUR")}.`);
      setLabelQuantity("1");
      await loadAffiliate();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kazanç etikete dönüştürülemedi.");
    } finally {
      setRedeemingLabels(false);
    }
  }

  const profile = data?.profile || null;
  const totals = data?.totals || { approved: 0, paid: 0, pending: 0, referrals: 0, rejected: 0, voided: 0 };
  const referrals = ((data?.referrals || []) as unknown as DataRow[]);
  const events = ((data?.events || []) as unknown as DataRow[]);
  const labelCredit = data?.labelCredit || null;
  const selectedLabelProduct = labelCredit?.products.find((product) => product.id === labelProductId) || null;
  const parsedLabelQuantity = Math.max(0, Math.trunc(Number(labelQuantity) || 0));
  const labelRedemptionAmount = Number(((selectedLabelProduct?.grossUnitPrice || 0) * parsedLabelQuantity).toFixed(2));
  const customerAffiliateAvailable = Number(((labelCredit?.approvedAvailable || 0) + (labelCredit?.pendingAvailable || 0)).toFixed(2));
  const maximumAffordableLabels = selectedLabelProduct
    ? Math.min(selectedLabelProduct.availableStock, Math.floor(customerAffiliateAvailable / selectedLabelProduct.grossUnitPrice), 1000)
    : 0;
  const canRedeemLabels = Boolean(
    selectedLabelProduct
    && parsedLabelQuantity > 0
    && parsedLabelQuantity <= selectedLabelProduct.availableStock
    && labelRedemptionAmount <= customerAffiliateAvailable
  );
  const normalizedReferralSearch = referralSearch.trim().toLocaleLowerCase("tr-TR");
  const filteredReferrals = useMemo(
    () =>
      referrals.filter((row) => {
        if (!normalizedReferralSearch) return true;
        return [row.referredUserCompanyName, row.referredUserEmail, row.referralCodeUsed]
          .map(text)
          .join(" ")
          .toLocaleLowerCase("tr-TR")
          .includes(normalizedReferralSearch);
      }),
    [normalizedReferralSearch, referrals]
  );
  const filteredEvents = useMemo(
    () =>
      events.filter((row) => {
        if (eventStatusFilter !== "all" && text(row.status).toLowerCase() !== eventStatusFilter) return false;
        if (!normalizedReferralSearch) return true;
        return [row.referredUserEmail, row.externalOrderId, row.eventKey, row.source, row.status]
          .map(text)
          .join(" ")
          .toLocaleLowerCase("tr-TR")
          .includes(normalizedReferralSearch);
      }),
    [eventStatusFilter, normalizedReferralSearch, events]
  );

  return (
    <div className="space-y-5">
      <CustomerPageHeader
        title="Referans"
        caption="Affiliate linkinizle gelen müşteriler ve kullanım başına oluşan komisyonlar."
        icon={<HandCoins className="h-5 w-5" />}
        action={
          profile ? (
            <button
              type="button"
              onClick={() => void copyReferralLink()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
            >
              <Copy className="h-4 w-4" />
              Linki kopyala
            </button>
          ) : null
        }
      />
      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}
      {loading ? (
        <LoadingPanel label="Affiliate bilgileri yükleniyor" />
      ) : !profile ? (
        <AdminPanel title="Affiliate programı" caption="Referans linki oluşturup yönlendirdiğiniz müşterilerin kullanım komisyonlarını takip edin.">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-950">Henüz affiliate profiliniz yok.</p>
              <p className="mt-1 text-sm leading-6 text-slate-600">Katıldığınızda size özel referans kodu ve kayıt linki oluşur.</p>
            </div>
            <button
              type="button"
              disabled={creating}
              onClick={() => void joinProgram()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusSquare className="h-4 w-4" />}
              Programa katıl
            </button>
          </div>
        </AdminPanel>
      ) : (
        <>
          <AdminPanel title="Referans kontrolü" caption="Hesabınıza bağlı müşteri, kod veya komisyon hareketlerini filtreleyin.">
            <div className="grid gap-3 p-5 lg:grid-cols-[minmax(0,1fr)_220px]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={referralSearch}
                  onChange={(event) => setReferralSearch(event.target.value)}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                  placeholder="Bağlı referanslarda müşteri, e-posta veya kod ara"
                />
              </label>
              <select
                value={eventStatusFilter}
                onChange={(event) => setEventStatusFilter(event.target.value)}
                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
              >
                <option value="all">Tüm komisyon durumları</option>
                <option value="pending">Bekleyen</option>
                <option value="approved">Onaylanan</option>
                <option value="paid">Ödenen</option>
                <option value="rejected">Reddedilen</option>
                <option value="void">Geçersiz</option>
              </select>
            </div>
            <div className="border-t border-slate-100 px-5 py-4 text-sm leading-6 text-slate-600">
              Bu alan yalnızca hesabınıza zaten bağlı referansları filtreler; yeni bir müşteri bağlantısı oluşturmaz. Yeni kayıtlar referans linkinizle otomatik bağlanır. Mevcut bir müşteri hesabının sonradan bağlanması için destek ekibi yönetici panelindeki Affiliate hesapları ekranını kullanır.
            </div>
          </AdminPanel>
          <section className="grid gap-4 md:grid-cols-5">
            <AdminStatCard label="Referans" value={totals.referrals} tone="cyan" icon={<Users className="h-5 w-5" />} />
            <AdminStatCard label="Bekleyen (24 saat)" value={moneyText(totals.pending, "EUR")} tone="amber" icon={<Clock3 className="h-5 w-5" />} />
            <AdminStatCard label="Onaylanan" value={moneyText(totals.approved, "EUR")} tone="emerald" icon={<ShieldCheck className="h-5 w-5" />} />
            <AdminStatCard label="Etikete çevrilen" value={moneyText(labelCredit?.convertedTotal || 0, "EUR")} tone="cyan" icon={<Tags className="h-5 w-5" />} />
            <AdminStatCard label="Nakit ödenen" value={moneyText(Math.max(0, totals.paid - (labelCredit?.convertedTotal || 0)), "EUR")} tone="slate" icon={<CreditCard className="h-5 w-5" />} />
          </section>
          <AdminPanel
            title="Kazancınızı etiket bakiyesine çevirin"
            caption="Kazançlar 24 saat sonra otomatik onaylanır; müşteri isterse bekleme sırasında da yönetici onayı olmadan etiket kredisine çevirebilir."
          >
            <div className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_280px]">
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-3">
                  <InfoLine label="Kullanılabilir toplam" value={moneyText(customerAffiliateAvailable, "EUR")} />
                  <InfoLine label="Bekleyen" value={moneyText(labelCredit?.pendingAvailable || 0, "EUR")} />
                  <InfoLine label="Toplam çevrilen" value={moneyText(labelCredit?.convertedTotal || 0, "EUR")} />
                </div>
                <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_220px]">
                  <label className="grid gap-2 text-sm font-semibold text-slate-700">
                    Etiket ürünü
                    <select
                      value={labelProductId}
                      disabled={redeemingLabels}
                      onChange={(event) => setLabelProductId(event.target.value)}
                      className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 disabled:opacity-60"
                    >
                      {(labelCredit?.products || []).map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name} · {moneyText(product.grossUnitPrice, "EUR")} / adet · stok {product.availableStock}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm font-semibold text-slate-700">
                    Adet
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        max={selectedLabelProduct?.availableStock || 1000}
                        step="1"
                        value={labelQuantity}
                        disabled={redeemingLabels}
                        onChange={(event) => setLabelQuantity(event.target.value)}
                        className="h-11 min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 disabled:opacity-60"
                      />
                      <button type="button" disabled={redeemingLabels || maximumAffordableLabels < 1} onClick={() => setLabelQuantity(String(maximumAffordableLabels))} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 disabled:opacity-50">
                        Maksimum
                      </button>
                    </div>
                  </label>
                </div>
                <div className="flex flex-col gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Kullanılacak kazanç</p>
                    <p className="mt-1 text-xl font-bold text-emerald-950">{moneyText(labelRedemptionAmount, "EUR")}</p>
                    <p className="mt-1 text-xs text-emerald-800">KDV dahil etiket fiyatı üzerinden hesaplanır. Kalan kazanç hesabınızda kalır.</p>
                  </div>
                  <button
                    type="button"
                    disabled={redeemingLabels || !canRedeemLabels}
                    onClick={() => void redeemLabelCredits()}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 disabled:opacity-50"
                  >
                    {redeemingLabels ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tags className="h-4 w-4" />}
                    Bakiyeme ekle
                  </button>
                </div>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-sm font-semibold text-emerald-950">Doğrudan dönüşüm</p>
                <p className="mt-1 text-sm leading-6 text-emerald-900">Etiket kullanımından oluşan kazanç 24 saatlik kontrol süresinin sonunda otomatik onaylanır. Onay beklerken de etiket bakiyesine çevrilebilir; yönetici işlemi gerekmez.</p>
              </div>
            </div>
          </AdminPanel>
          {labelCredit?.redemptions.length ? (
            <AdminPanel title="Etikete dönüşüm geçmişi" caption="Kazançtan etiket bakiyesine yapılan son işlemler." count={labelCredit.redemptions.length}>
              <DataTable
                rows={labelCredit.redemptions as unknown as DataRow[]}
                empty="Henüz etikete dönüştürülen kazanç yok."
                columns={[
                  { label: "Tarih", value: (row) => dateText(row.completedAt || row.createdAt) },
                  { label: "Ürün", value: (row) => <NameStack title={text(row.productName) || text(row.labelProductId)} detail={`${numberText(row.quantity)} etiket`} /> },
                  { label: "Kullanılan kazanç", value: (row) => moneyText(row.amount, "EUR"), align: "right" },
                  { label: "Kaynak", value: (row) => badge(text(row.source) === "admin" ? "Yönetici" : "Müşteri") },
                  { label: "Durum", value: (row) => statusBadge(text(row.status)) },
                ]}
              />
            </AdminPanel>
          ) : null}
          <AdminPanel
            title="Referans linkiniz"
            caption="Bu linkle kayıt olan müşteriler otomatik olarak hesabınıza bağlanır."
            action={
              <button
                type="button"
                onClick={() => void copyReferralLink()}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                <Copy className="h-4 w-4" />
                Kopyala
              </button>
            }
          >
            <div className="grid gap-4 p-5 lg:grid-cols-[220px_minmax(0,1fr)]">
              <InfoLine label="Kod" value={profile.referralCode} />
              <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase text-slate-500">Kayıt linki</p>
                <p className="mt-2 break-all text-sm font-semibold text-slate-950">{profile.referralLink}</p>
              </div>
            </div>
          </AdminPanel>
          <AdminPanel title="Yönlendirilen müşteriler" caption={`${numberText(filteredReferrals.length)} kayıt gösteriliyor.`} count={filteredReferrals.length}>
            <DataTable
              rows={filteredReferrals}
              empty="Henüz bağlı referans yok. Yeni kayıtlar referans linkiyle otomatik bağlanır; mevcut hesap bağlantısını destek ekibi yapar."
              columns={[
                { label: "Müşteri", value: (row) => <NameStack title={text(row.referredUserCompanyName) || text(row.referredUserEmail)} detail={text(row.referredUserEmail)} /> },
                { label: "Kod", value: (row) => text(row.referralCodeUsed) },
                { label: "Kural", value: (row) => text(row.calculationType) === "fixed" ? `${moneyText(row.commissionAmount, "EUR")} / etiket` : `%${numberText(Number(row.commissionRate || 0) * 100)}` },
            { label: "Bu ay satın alma", value: (row) => <NameStack title={`${numberText(row.monthPurchasedQuantity)} kredi`} detail={`${moneyText(row.monthPurchasedTotal, "EUR")} · Tüm zamanlar: ${numberText(row.purchasedQuantity)}`} />, align: "right" },
            { label: "Bu ay kullanılan", value: (row) => <NameStack title={numberText(row.monthUsedLabels)} detail={`Tüm zamanlar: ${numberText(row.usedLabels)}`} />, align: "right" },
                { label: "Komisyona giren", value: (row) => numberText(row.commissionEventCount), align: "right" },
                { label: "Komisyon", value: (row) => <NameStack title={moneyText(row.totalCommission, "EUR")} detail={text(row.calculationType) === "fixed" ? `${numberText(row.commissionEventCount)} × ${moneyText(row.commissionAmount, "EUR")}` : "Dönem içindeki uygun hareketler"} />, align: "right" },
                { label: "Dönem", value: (row) => <NameStack title={dateText(row.effectiveFrom || row.referredAt)} detail={`Bitiş: ${dateText(row.effectiveUntil)}`} /> },
                { label: "Durum", value: (row) => statusBadge(text(row.status)) },
              ]}
            />
          </AdminPanel>
          <AdminPanel title="Komisyon hareketleri" caption={`${numberText(filteredEvents.length)} kayıt gösteriliyor.`} count={filteredEvents.length}>
            <DataTable
              rows={filteredEvents}
              empty="Komisyon hareketi yok."
              columns={[
                { label: "Kullanıcı", value: (row) => <NameStack title={text(row.referredUserEmail)} detail={text(row.externalOrderId) || text(row.eventKey)} /> },
                { label: "Kaynak", value: (row) => badge(text(row.source) || (text(row.eventKey).startsWith("usage:") ? "shipmentUsage" : "invoice")) },
                { label: "Tutar", value: (row) => moneyText(row.amount, row.currency), align: "right" },
                { label: "Oran", value: (row) => Number(row.rate || 0) > 0 ? percentText(Number(row.rate) * 100) : "Sabit", align: "right" },
                { label: "Durum", value: (row) => <NameStack title={statusBadge(text(row.status))} detail={text(row.statusReason) || text(row.paymentReference)} /> },
                { label: "Tarih", value: (row) => dateText(row.createdAt) },
              ]}
            />
          </AdminPanel>
        </>
      )}
    </div>
  );
}

type ManualImportDraft = {
  address1: string;
  address2: string;
  city: string;
  companyName: string;
  countryCode: string;
  email: string;
  labelProductId: string;
  orderNumber: string;
  packageCount: number;
  phone: string;
  postalCode: string;
  recipientName: string;
  shipmentType: string;
};

const manualImportHeaderAliases: Record<keyof ManualImportDraft | "addressText", string[]> = {
  address1: ["address1", "adres1", "adres", "strasse", "straße", "street", "streetaddress", "sokak"],
  address2: ["address2", "adres2", "ekadres", "hausnummer", "housenumber", "apartment"],
  addressText: ["addresstext", "addressblock", "tekadres", "fulladdress", "volladresse", "adreskutusu"],
  city: ["city", "sehir", "şehir", "ort", "stadt"],
  companyName: ["company", "companyname", "firma", "firmenname", "unternehmen", "organisation", "organization"],
  countryCode: ["country", "countrycode", "ulke", "ülke", "land"],
  email: ["email", "e-mail", "eposta", "e-posta"],
  labelProductId: ["labelproductid", "productid", "etiketurunu", "etiketürünü", "product"],
  orderNumber: ["ordernumber", "order", "siparisno", "siparişno", "referans", "ref"],
  packageCount: ["packagecount", "quantity", "qty", "adet", "paket", "parca", "parça"],
  phone: ["phone", "telefon", "tel", "mobile", "gsm"],
  postalCode: ["postalcode", "postcode", "zip", "plz", "postakodu"],
  recipientName: ["recipientname", "recipient", "alici", "alıcı", "name", "empfanger", "empfänger", "kunde"],
  shipmentType: ["shipmenttype", "type", "tip", "gonderitipi", "gönderitipi", "retoure"],
};

function normalizeImportHeader(value: unknown) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[ıİ]/g, "i")
    .replace(/[ğĞ]/g, "g")
    .replace(/[üÜ]/g, "u")
    .replace(/[şŞ]/g, "s")
    .replace(/[öÖ]/g, "o")
    .replace(/[çÇ]/g, "c")
    .replace(/[^a-z0-9]+/g, "");
}

function cleanImportValue(value: unknown, maxLength = 240) {
  return String(value ?? "").trim().slice(0, maxLength);
}

function readManualRecordValue(record: Record<string, string>, key: keyof ManualImportDraft | "addressText") {
  const aliases = manualImportHeaderAliases[key].map(normalizeImportHeader);
  for (const [header, value] of Object.entries(record)) {
    if (aliases.includes(normalizeImportHeader(header))) return cleanImportValue(value, key === "email" ? 320 : 240);
  }
  return "";
}

function parsePackageCount(value: unknown) {
  const parsed = Number(String(value || "1").replace(",", "."));
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  return Math.max(1, Math.min(50, Math.trunc(parsed)));
}

function normalizeShipmentTypeForForm(value: unknown) {
  const normalized = cleanImportValue(value, 40).toLowerCase();
  return normalized.includes("return") || normalized.includes("retoure") || normalized.includes("iade") ? "return" : "outbound";
}

function splitCsvRows(text: string, delimiter: string) {
  const rows: string[][] = [];
  let cell = "";
  let row: string[] = [];
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];
    if (char === '"') {
      if (quoted && next === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (!quoted && char === delimiter) {
      row.push(cell);
      cell = "";
      continue;
    }
    if (!quoted && (char === "\n" || char === "\r")) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(cell);
      if (row.some((value) => value.trim())) rows.push(row);
      row = [];
      cell = "";
      continue;
    }
    cell += char;
  }
  row.push(cell);
  if (row.some((value) => value.trim())) rows.push(row);
  return rows;
}

function recordsFromRows(rows: string[][]) {
  const headers = rows[0]?.map((header) => cleanImportValue(header, 120)) || [];
  return rows.slice(1).map((row) =>
    headers.reduce<Record<string, string>>((record, header, index) => {
      if (header) record[header] = cleanImportValue(row[index] || "", 500);
      return record;
    }, {})
  );
}

function parseDelimitedManualImport(text: string) {
  const candidates = [";", ",", "\t"];
  const firstLine = text.split(/\r?\n/u)[0] || "";
  const delimiter = candidates
    .map((candidate) => ({ candidate, count: firstLine.split(candidate).length }))
    .sort((a, b) => b.count - a.count)[0]?.candidate || ",";
  return recordsFromRows(splitCsvRows(text, delimiter));
}

function readUInt16(view: DataView, offset: number) {
  return view.getUint16(offset, true);
}

function readUInt32(view: DataView, offset: number) {
  return view.getUint32(offset, true);
}

function findEndOfCentralDirectory(view: DataView) {
  const minimum = Math.max(0, view.byteLength - 66000);
  for (let offset = view.byteLength - 22; offset >= minimum; offset -= 1) {
    if (readUInt32(view, offset) === 0x06054b50) return offset;
  }
  return -1;
}

async function inflateRawZipData(bytes: Uint8Array) {
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const stream = new Blob([buffer]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function readZipEntries(buffer: ArrayBuffer) {
  const view = new DataView(buffer);
  const decoder = new TextDecoder();
  const entries = new Map<string, Uint8Array>();
  const endOffset = findEndOfCentralDirectory(view);
  if (endOffset < 0) throw new Error("XLSX dosyası okunamadı.");
  const entryCount = readUInt16(view, endOffset + 10);
  let centralOffset = readUInt32(view, endOffset + 16);

  for (let entryIndex = 0; entryIndex < entryCount; entryIndex += 1) {
    if (readUInt32(view, centralOffset) !== 0x02014b50) throw new Error("XLSX central directory okunamadı.");
    const method = readUInt16(view, centralOffset + 10);
    const compressedSize = readUInt32(view, centralOffset + 20);
    const nameLength = readUInt16(view, centralOffset + 28);
    const extraLength = readUInt16(view, centralOffset + 30);
    const commentLength = readUInt16(view, centralOffset + 32);
    const localOffset = readUInt32(view, centralOffset + 42);
    const nameBytes = new Uint8Array(buffer, centralOffset + 46, nameLength);
    const name = decoder.decode(nameBytes);
    const localNameLength = readUInt16(view, localOffset + 26);
    const localExtraLength = readUInt16(view, localOffset + 28);
    const dataOffset = localOffset + 30 + localNameLength + localExtraLength;
    const compressed = new Uint8Array(buffer, dataOffset, compressedSize);
    if (method === 0) entries.set(name, compressed);
    else if (method === 8) entries.set(name, await inflateRawZipData(compressed));
    centralOffset += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

function columnIndexFromRef(ref: string) {
  const letters = (ref.match(/[A-Z]+/i)?.[0] || "").toUpperCase();
  let index = 0;
  for (const letter of letters) index = index * 26 + letter.charCodeAt(0) - 64;
  return Math.max(0, index - 1);
}

async function parseXlsxManualImport(file: File) {
  const entries = await readZipEntries(await file.arrayBuffer());
  const parser = new DOMParser();
  const sharedXml = entries.get("xl/sharedStrings.xml");
  const sharedStrings = sharedXml
    ? Array.from(parser.parseFromString(new TextDecoder().decode(sharedXml), "application/xml").getElementsByTagName("si")).map((item) =>
        Array.from(item.getElementsByTagName("t")).map((node) => node.textContent || "").join("")
      )
    : [];
  const sheetEntry = [...entries.entries()].find(([name]) => /^xl\/worksheets\/sheet\d+\.xml$/i.test(name));
  if (!sheetEntry) throw new Error("XLSX içinde çalışma sayfası bulunamadı.");
  const sheet = parser.parseFromString(new TextDecoder().decode(sheetEntry[1]), "application/xml");
  const rows = Array.from(sheet.getElementsByTagName("row")).map((row) => {
    const cells: string[] = [];
    for (const cell of Array.from(row.getElementsByTagName("c"))) {
      const index = columnIndexFromRef(cell.getAttribute("r") || "");
      const type = cell.getAttribute("t") || "";
      const valueNode = cell.getElementsByTagName("v")[0];
      const inlineNode = cell.getElementsByTagName("t")[0];
      const raw = type === "inlineStr" ? inlineNode?.textContent || "" : valueNode?.textContent || "";
      cells[index] = type === "s" ? sharedStrings[Number(raw)] || "" : raw;
    }
    return cells;
  });
  return recordsFromRows(rows);
}

async function parseManualImportFile(file: File) {
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith(".xlsx")) return parseXlsxManualImport(file);
  return parseDelimitedManualImport(await file.text());
}

function manualDraftFromRecord(record: Record<string, string>, fallback: { labelProductId: string; shipmentType: string }): ManualImportDraft {
  const parsed = parseManualAddressBlock(readManualRecordValue(record, "addressText"));
  return {
    address1: readManualRecordValue(record, "address1") || parsed.address1 || "",
    address2: readManualRecordValue(record, "address2") || parsed.address2 || "",
    city: readManualRecordValue(record, "city") || parsed.city || "",
    companyName: readManualRecordValue(record, "companyName"),
    countryCode: (readManualRecordValue(record, "countryCode") || parsed.countryCode || "DE").toUpperCase(),
    email: readManualRecordValue(record, "email") || parsed.email || "",
    labelProductId: readManualRecordValue(record, "labelProductId") || fallback.labelProductId,
    orderNumber: readManualRecordValue(record, "orderNumber"),
    packageCount: parsePackageCount(readManualRecordValue(record, "packageCount")),
    phone: readManualRecordValue(record, "phone") || parsed.phone || "",
    postalCode: readManualRecordValue(record, "postalCode") || parsed.postalCode || "",
    recipientName: readManualRecordValue(record, "recipientName") || parsed.recipientName || "",
    shipmentType: normalizeShipmentTypeForForm(readManualRecordValue(record, "shipmentType") || fallback.shipmentType),
  };
}

function isValidManualDraft(row: ManualImportDraft) {
  return Boolean(row.labelProductId && row.recipientName && row.address1 && row.postalCode && row.city);
}

type CreatedManualShipmentLink = {
  labelProductId?: string;
  orderNumber?: string;
  shipmentId: string;
  trackingNumber?: string;
};

function manualDestinationBlocked(...parts: Array<unknown>) {
  const haystack = parts
    .map((part) => String(part || "").toLowerCase())
    .join(" ");
  return haystack.includes("packstation") || haystack.includes("postfiliale");
}

function manualRecipientKey(row: DataRow) {
  return [
    text(row.recipientCompany).toLowerCase(),
    text(row.recipientName).toLowerCase(),
    text(row.address1).toLowerCase(),
    text(row.postalCode),
    text(row.city).toLowerCase(),
  ].join("|");
}

function recentManualRecipients(rows: DataRow[]) {
  const seen = new Set<string>();
  return rows.filter((row) => {
    if (text(row.sourcePlatform).toLowerCase() !== "manual") return false;
    if (!text(row.recipientName) || !text(row.address1) || !text(row.postalCode) || !text(row.city)) return false;
    const key = manualRecipientKey(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function createdManualLinksFromResult(result: CreateShipmentResponse | { results?: Array<Partial<CreateShipmentResponse> & { ok?: boolean; labelProductId?: string; orderNumber?: string }>; summary?: unknown }) {
  if ("results" in result) {
    return (result.results || [])
      .filter((row) => row.ok && text(row.shipmentId))
      .map((row) => ({
        labelProductId: text(row.labelProductId) || undefined,
        orderNumber: text(row.orderNumber) || undefined,
        shipmentId: text(row.shipmentId),
        trackingNumber: text(row.trackingNumber) || undefined,
      }));
  }
  const singleResult = result as Partial<CreateShipmentResponse>;
  return singleResult.ok && singleResult.shipmentId
    ? [{ shipmentId: singleResult.shipmentId, trackingNumber: singleResult.trackingNumber || undefined }]
    : [];
}

function cleanManualSingleLine(value: unknown) {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
}

function shipmentLabelHref(shipmentId: string, variant: "normal" | "adobe" | "zip" = "normal", download = false) {
  const params = new URLSearchParams({ shipmentId, variant });
  if (download) params.set("download", "1");
  return `/api/shipments/label-download?${params.toString()}`;
}

function CreateLabelPage() {
  const location = useLocation();
  const isOrderCreatePath = location.pathname.includes("/orders/new");
  const [mode, setMode] = useState<"order" | "manual">(() => (location.pathname.includes("/orders/new") ? "order" : "manual"));
  const [manualTab, setManualTab] = useState<"single" | "bulk">("single");
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [products, setProducts] = useState<LabelProductSummary[]>([]);
  const [balances, setBalances] = useState<LabelBalanceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [singlePackageCount, setSinglePackageCount] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [addressBlock, setAddressBlock] = useState("");
  const [importRows, setImportRows] = useState<ManualImportDraft[]>([]);
  const [importFileName, setImportFileName] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [addressSuggestionLoading, setAddressSuggestionLoading] = useState(false);
  const [addressSuggestionOpen, setAddressSuggestionOpen] = useState(false);
  const [postalLookup, setPostalLookup] = useState<{ loading: boolean; message: string | null }>({ loading: false, message: null });
  const [recentManualRows, setRecentManualRows] = useState<DataRow[]>([]);
  const [recentManualLoading, setRecentManualLoading] = useState(false);
  const [createdManualShipments, setCreatedManualShipments] = useState<CreatedManualShipmentLink[]>([]);
  const [manualRowActionId, setManualRowActionId] = useState("");
  const latestAddressSearchRequest = useRef(0);
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();
  const [form, setForm] = useState({
    orderId: "",
    labelProductId: "",
    shipmentType: "outbound",
    orderNumber: "",
    companyName: "",
    recipientName: "",
    address1: "",
    address2: "",
    postalCode: "",
    city: "",
    countryCode: "DE",
    phone: "",
    email: "",
    senderName: "",
  });

  const balanceByProductId = useMemo(() => {
    const map = new Map<string, LabelBalanceSummary>();
    for (const balance of balances) map.set(balance.labelProductId, balance);
    return map;
  }, [balances]);

  const recentRecipients = useMemo(() => recentManualRecipients(recentManualRows), [recentManualRows]);
  const sortedProducts = useMemo(() => sortLabelProductsForOrders(products), [products]);
  const selectedProduct = sortedProducts.find((product) => product.id === form.labelProductId) || null;
  const selectedBalance = form.labelProductId ? balanceByProductId.get(form.labelProductId) : null;
  const selectedAvailable = Number(selectedBalance?.quantityAvailable || 0);
  const formattedManualRecipientName = cleanManualSingleLine(form.recipientName);
  const manualMissing = mode === "manual" && (!form.recipientName.trim() || !form.address1.trim() || !form.postalCode.trim() || !form.city.trim());
  const manualBlocked = mode === "manual" && manualDestinationBlocked(form.recipientName, form.address1, form.address2);
  const manualFormatWarnings = mode === "manual" ? dhlAddressInputWarnings(form) : [];
  const manualFormatAdjustments = mode === "manual" ? dhlAddressInputAdjustments(form) : [];
  const productNoCredit = Boolean(form.labelProductId) && selectedAvailable <= 0;
  const singleInsufficientCredit = Boolean(form.labelProductId) && selectedAvailable < singlePackageCount;
  const submitDisabled =
    submitting ||
    !form.labelProductId ||
    productNoCredit ||
    (mode === "order" && !form.orderId) ||
    (mode === "manual" && (manualMissing || manualBlocked || manualFormatWarnings.length > 0 || singleInsufficientCredit));

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    Promise.all([apiClient.orders(), apiClient.labelProducts(), apiClient.balances(), apiClient.profile().catch(() => null)])
      .then(([ordersResult, productsResult, balancesResult, profileResult]) => {
        if (!active) return;
        const nextOrders = ordersResult.orders || [];
        const nextProducts = productsResult.products || [];
        const nextBalances = balancesResult.balances || [];
        const sortedNextProducts = sortLabelProductsForOrders(nextProducts);
        const defaultProductId = profileResult?.profile.defaultLabelProductId || "";
        const defaultProductHasBalance = nextBalances.some((balance) => balance.labelProductId === defaultProductId && Number(balance.quantityAvailable || 0) > 0);
        setOrders(nextOrders);
        setProducts(sortedNextProducts);
        setBalances(nextBalances);
        setForm((current) => ({
          ...current,
          orderId: current.orderId || nextOrders[0]?.id || "",
          labelProductId:
            current.labelProductId ||
            (defaultProductHasBalance ? defaultProductId : "") ||
            sortedNextProducts.find((product) =>
              nextBalances.some((balance) => balance.labelProductId === product.id && Number(balance.quantityAvailable || 0) > 0)
            )?.id ||
            sortedNextProducts[0]?.id ||
            "",
          shipmentType: profileResult?.profile.defaultShipmentType || current.shipmentType || "outbound",
        }));
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : "Etiket bilgileri alınamadı.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (mode !== "manual") return;
    const postalCode = form.postalCode.trim();
    if (!/^\d{5}$/.test(postalCode) || form.city.trim()) {
      setPostalLookup((current) => (current.loading ? { loading: false, message: null } : current));
      return;
    }

    let active = true;
    const timer = window.setTimeout(() => {
      setPostalLookup({ loading: true, message: null });
      apiClient.lookupGermanPostalCode(postalCode)
        .then((result) => {
          if (!active) return;
          const city = result.city || result.localities[0]?.name || "";
          if (!city) {
            setPostalLookup({ loading: false, message: null });
            return;
          }
          setForm((current) => {
            if (current.postalCode.trim() !== postalCode || current.city.trim()) return current;
            return { ...current, city };
          });
          setPostalLookup({ loading: false, message: `Şehir otomatik dolduruldu: ${city}` });
        })
        .catch(() => {
          if (active) setPostalLookup({ loading: false, message: "Posta kodu kontrol edilemedi." });
        });
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [mode, form.postalCode, form.city]);

  useEffect(() => {
    if (mode !== "manual") return;
    const query = form.address1.trim();
    if (query.length < 3) {
      setAddressSuggestions([]);
      setAddressSuggestionOpen(false);
      setAddressSuggestionLoading(false);
      return;
    }

    const requestId = ++latestAddressSearchRequest.current;
    const timer = window.setTimeout(() => {
      setAddressSuggestionLoading(true);
      apiClient
        .searchGermanAddress(query)
        .then((result) => {
          if (requestId !== latestAddressSearchRequest.current) return;
          const suggestions = Array.isArray(result.suggestions) ? result.suggestions.slice(0, 8) : [];
          setAddressSuggestions(suggestions);
          setAddressSuggestionOpen(suggestions.length > 0);
        })
        .catch(() => {
          if (requestId !== latestAddressSearchRequest.current) return;
          setAddressSuggestions([]);
          setAddressSuggestionOpen(false);
        })
        .finally(() => {
          if (requestId === latestAddressSearchRequest.current) setAddressSuggestionLoading(false);
        });
    }, 350);

    return () => {
      window.clearTimeout(timer);
    };
  }, [mode, form.address1]);

  async function loadRecentManualShipments() {
    setRecentManualLoading(true);
    try {
      const result = await apiClient.resource<{ shipments?: DataRow[] }>("/api/shipments?platform=manual&status=printed&limit=50");
      setRecentManualRows(Array.isArray(result.shipments) ? result.shipments : []);
    } catch {
      setRecentManualRows([]);
    } finally {
      setRecentManualLoading(false);
    }
  }

  useEffect(() => {
    void loadRecentManualShipments();
  }, []);

  function updateForm(key: keyof typeof form, value: string) {
    if (key === "postalCode" || key === "city") setPostalLookup({ loading: false, message: null });
    if (key === "address1") setAddressSuggestionOpen(value.trim().length >= 3);
    setForm((current) => ({ ...current, [key]: value }));
  }

  function keepTypedManualAddress() {
    setAddressSuggestionOpen(false);
    setAddressSuggestions([]);
    setAddressSuggestionLoading(false);
  }

  function applyParsedAddressBlock(value: string, announce = false) {
    const parsed = parseManualAddressBlock(value);
    setForm((current) => ({
      ...current,
      recipientName: parsed.recipientName || current.recipientName,
      address1: parsed.address1 || current.address1,
      address2: parsed.address2 ?? current.address2,
      postalCode: parsed.postalCode || current.postalCode,
      city: parsed.city || current.city,
      countryCode: parsed.countryCode || current.countryCode || "DE",
    }));
    setAddressSuggestionOpen(false);
    setAddressSuggestions([]);
    if (announce && manualAddressBlockIsComplete(parsed)) setNotice("Adres bilgileri otomatik ayrıştırıldı.");
  }

  function applyAddressBlock() {
    applyParsedAddressBlock(addressBlock, true);
  }

  function handleAddressBlockPaste(event: ReactClipboardEvent<HTMLTextAreaElement>) {
    const pastedText = event.clipboardData.getData("text");
    if (!pastedText.trim() || !/[\r\n]/.test(pastedText)) return;
    event.preventDefault();
    setAddressBlock(pastedText);
    applyParsedAddressBlock(pastedText, true);
  }

  function applyAddressSuggestion(suggestion: AddressSuggestion) {
    setForm((current) => ({
      ...current,
      address1: suggestion.address1,
      city: suggestion.city,
      countryCode: suggestion.countryCode || current.countryCode || "DE",
      postalCode: suggestion.postalCode,
    }));
    setAddressSuggestionOpen(false);
    setAddressSuggestions([]);
    setPostalLookup({ loading: false, message: "Adres önerisi uygulandı." });
  }

  function applyRecentManualRow(row: DataRow) {
    const nextAddressBlock = [
      text(row.recipientName),
      text(row.address1),
      text(row.address2),
      `${text(row.postalCode)} ${text(row.city)}`.trim(),
      text(row.countryCode),
    ]
      .filter(Boolean)
      .join("\n");
    setMode("manual");
    setAddressBlock(nextAddressBlock);
    setForm((current) => ({
      ...current,
      address1: text(row.address1),
      address2: text(row.address2),
      city: text(row.city),
      companyName: text(row.recipientCompany),
      countryCode: text(row.countryCode) || "DE",
      email: "",
      labelProductId: text(row.labelProductId) || current.labelProductId,
      orderNumber: "",
      phone: "",
      postalCode: text(row.postalCode),
      recipientName: text(row.recipientName),
      shipmentType: normalizeShipmentTypeForForm(row.shipmentType || current.shipmentType),
    }));
    setCreatedManualShipments([]);
    setNotice("Eski manuel alıcı forma aktarıldı.");
  }

  function resetManualRecipientFields(options: { clearCreatedShipments?: boolean; clearSenderName?: boolean } = {}) {
    latestAddressSearchRequest.current += 1;
    setAddressBlock("");
    setSinglePackageCount(1);
    setAddressSuggestions([]);
    setAddressSuggestionOpen(false);
    setAddressSuggestionLoading(false);
    if (options.clearCreatedShipments) setCreatedManualShipments([]);
    setPostalLookup({ loading: false, message: null });
    setForm((current) => ({
      ...current,
      address1: "",
      address2: "",
      city: "",
      companyName: "",
      countryCode: "DE",
      email: "",
      orderNumber: "",
      phone: "",
      postalCode: "",
      recipientName: "",
      senderName: options.clearSenderName ? "" : current.senderName,
    }));
  }

  function clearManualForm() {
    resetManualRecipientFields({ clearCreatedShipments: true, clearSenderName: true });
  }

  async function saveManualDefaults() {
    setError(null);
    setNotice(null);
    try {
      await apiClient.updateProfile({
        defaultLabelProductId: form.labelProductId,
        defaultShipmentType: form.shipmentType,
      });
      setNotice("Varsayılan etiket seçimi kaydedildi.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Varsayılan seçim kaydedilemedi.");
    }
  }

  async function onManualImportFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError(null);
    setNotice(null);
    setImportFileName(file.name);
    try {
      const records = await parseManualImportFile(file);
      const rows = records.map((record) => manualDraftFromRecord(record, { labelProductId: form.labelProductId, shipmentType: form.shipmentType }));
      setImportRows(rows);
      const validCount = rows.filter(isValidManualDraft).length;
      setNotice(`${validCount} geçerli satır hazırlandı.`);
    } catch (err) {
      setImportRows([]);
      setError(err instanceof Error ? err.message : "Import dosyası okunamadı.");
    }
  }

  async function submitManualImport() {
    const rows = importRows.filter(isValidManualDraft);
    if (rows.length === 0) {
      setError("Import için geçerli satır bulunamadı.");
      return;
    }
    setError(null);
    setNotice(null);
    setCreatedManualShipments([]);
    setImportSubmitting(true);
    try {
      const result = await apiClient.bulkCreateManualShipments({
        labelProductId: form.labelProductId,
        shipmentType: form.shipmentType,
        rows,
      });
      setNotice(`${result.summary.success} etiket oluşturuldu, ${result.summary.failed} satır başarısız.`);
      if (result.summary.failed > 0) {
        const firstError = result.results.find((row) => !row.ok)?.error;
        if (firstError) setError(firstError);
      }
      setCreatedManualShipments(createdManualLinksFromResult(result));
      await loadRecentManualShipments();
      setImportRows([]);
      setImportFileName("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Toplu manuel etiket oluşturulamadı.");
    } finally {
      setImportSubmitting(false);
    }
  }

  async function regenerateRecentManualShipment(row: DataRow) {
    const shipmentId = text(row.id);
    if (!shipmentId) return;
    if (!(await askConfirmation(regenerateShipmentConfirmation(row)))) return;
    setManualRowActionId(`regenerate:${shipmentId}`);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.regenerateShipment({ shipmentId, confirmed: true });
      if (!result.ok) throw new Error(result.error || "Etiket yeniden oluşturulamadı.");
      setCreatedManualShipments(createdManualLinksFromResult(result));
      setNotice("Manuel etiket yeniden oluşturuldu.");
      await loadRecentManualShipments();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Etiket yeniden oluşturulamadı.");
    } finally {
      setManualRowActionId("");
    }
  }

  async function cancelRecentManualShipment(row: DataRow) {
    const shipmentId = text(row.id);
    if (!shipmentId) return;
    const label = text(row.trackingNumber) || text(row.orderNumber) || shipmentId;
    const confirmed = await askConfirmation({
      title: "Manuel gönderi iptal edilsin mi?",
      description: `${label} gönderisi iptal edilecek.`,
      details: ["Etiket kredisi müşteri bakiyesine geri alınır ve gönderi iptal listesinde görünür."],
      confirmLabel: "Gönderiyi iptal et",
      tone: "danger",
    });
    if (!confirmed) return;
    setManualRowActionId(`cancel:${shipmentId}`);
    setError(null);
    setNotice(null);
    try {
      const result = await apiClient.cancelShipment(shipmentId);
      setNotice(result.message || "Gönderi iptal edildi ve etiket kredisi hesabınıza geri döndü.");
      await Promise.all([loadRecentManualShipments(), apiClient.balances().then((next) => setBalances(next.balances || [])).catch(() => undefined)]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Gönderi iptal edilemedi.");
    } finally {
      setManualRowActionId("");
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setCreatedManualShipments([]);
    setSubmitting(true);
    try {
      if (mode === "manual" && singlePackageCount > 1) {
        const result = await apiClient.bulkCreateManualShipments({
          labelProductId: form.labelProductId,
          shipmentType: form.shipmentType,
          rows: [{
            address1: form.address1,
            address2: form.address2,
            city: form.city,
            companyName: form.companyName,
            countryCode: form.countryCode || "DE",
            email: form.email,
            labelProductId: form.labelProductId,
            orderNumber: form.orderNumber,
            packageCount: singlePackageCount,
            phone: form.phone,
            postalCode: form.postalCode,
            recipientName: formattedManualRecipientName,
            senderName: form.senderName,
            shipmentType: form.shipmentType,
          }],
        });
        setCreatedManualShipments(createdManualLinksFromResult(result));
        setNotice(`${result.summary.success} etiket oluşturuldu, ${result.summary.failed} etiket başarısız.`);
        if (result.summary.failed > 0) {
          const firstError = result.results.find((row) => !row.ok)?.error;
          setError(firstError || result.error || "Bazı etiketler oluşturulamadı.");
        }
        await Promise.all([
          loadRecentManualShipments(),
          apiClient.balances().then((next) => setBalances(next.balances || [])).catch(() => undefined),
        ]);
        if (result.summary.success > 0 && result.summary.failed === 0) resetManualRecipientFields();
        return;
      }

      const result =
        mode === "order"
          ? await apiClient.createShipmentForOrder({
              confirmed: true,
              orderId: form.orderId,
              labelProductId: form.labelProductId,
              shipmentType: form.shipmentType,
            })
          : await apiClient.createManualShipment({
              address1: form.address1,
              address2: form.address2,
              city: form.city,
              companyName: form.companyName,
              countryCode: form.countryCode || "DE",
              email: form.email,
              labelProductId: form.labelProductId,
              orderNumber: form.orderNumber,
              phone: form.phone,
              postalCode: form.postalCode,
              recipientName: formattedManualRecipientName,
              senderName: form.senderName,
              shipmentType: form.shipmentType,
            });

      if (!result.ok) {
        setError(result.error || "Etiket oluşturulamadı.");
        return;
      }

      setNotice(
        result.labelDownloadUrl
          ? `Etiket oluşturuldu. Takip: ${result.trackingNumber}`
          : `Gönderi oluşturuldu. Takip: ${result.trackingNumber}`
      );
      setCreatedManualShipments(createdManualLinksFromResult(result));
      if (mode === "manual") {
        await Promise.all([
          loadRecentManualShipments(),
          apiClient.balances().then((next) => setBalances(next.balances || [])).catch(() => undefined),
        ]);
        resetManualRecipientFields();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etiket oluşturulamadı.");
    } finally {
      setSubmitting(false);
    }
  }

  const validManualImportRows = importRows.filter(isValidManualDraft);
  const invalidManualImportRows = importRows.filter((row) => !isValidManualDraft(row));
  const manualAddressPreview = compactAddress(form.address1, form.address2, form.postalCode, form.city, form.countryCode);
  const manualCanCreateCount = manualTab === "single" ? singlePackageCount : validManualImportRows.reduce((sum, row) => sum + parsePackageCount(row.packageCount), 0);

  if (!isOrderCreatePath) {
    return (
      <div className="space-y-5">
        {confirmationDialog}
        <CustomerPageHeader
          title="Manuel etiket oluştur"
          caption="Tekli ve toplu manuel etiketleri hızlı adres girişi, tekrar kullanım ve indirme aksiyonlarıyla yönetin."
          icon={<Send className="h-5 w-5" />}
        />

        {error ? <ErrorBanner message={error} /> : null}
        {notice ? <AdminNotice message={notice} /> : null}
        {createdManualShipments.length > 0 ? (
          <section className="rounded-2xl border border-emerald-200 bg-emerald-50/80 p-4 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-base font-semibold text-emerald-950">{numberText(createdManualShipments.length)} etiket hazır</h2>
                <p className="mt-1 text-sm text-emerald-800">PDF, ZIP ve gönderi detayına hızlıca ulaşabilirsiniz.</p>
              </div>
              {createdManualShipments.length > 1 ? (
                <a
                  href={shipmentBulkHref(createdManualShipments.map((item) => item.shipmentId), "manual")}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800"
                >
                  <Download className="h-4 w-4" />
                  Tümünü ZIP indir
                </a>
              ) : null}
            </div>
            <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {createdManualShipments.map((item, index) => (
                <div key={item.shipmentId} className="rounded-xl border border-emerald-200 bg-white px-3 py-3 shadow-sm">
                  <p className="truncate text-sm font-semibold text-slate-950">{item.trackingNumber || item.orderNumber || `Etiket ${index + 1}`}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Link
                      to={`/customer/shipments/${encodeURIComponent(item.shipmentId)}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      Detay
                    </Link>
                    <a
                      href={shipmentLabelHref(item.shipmentId, "normal", true)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      PDF
                    </a>
                    <a
                      href={shipmentLabelHref(item.shipmentId, "zip", true)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <Download className="h-3.5 w-3.5" />
                      ZIP
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <AdminPanel title="Ortak seçenekler" caption="Bu seçimler tekli ve toplu manuel etiket oluşturma için kullanılır.">
          {loading ? (
            <div className="p-5">
              <LoadingPanel label="Etiket bilgileri yükleniyor" />
            </div>
          ) : (
            <div className="grid gap-4 p-5 lg:grid-cols-[minmax(0,1fr)_220px_180px_170px]">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Etiket ürünü</span>
                <select
                  value={form.labelProductId}
                  onChange={(event) => updateForm("labelProductId", event.target.value)}
                  className="h-12 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-950 shadow-sm outline-none transition focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                >
                  <option value="">Ürün seçin</option>
                  {sortedProducts.map((product) => {
                    const balance = balanceByProductId.get(product.id);
                    return (
                      <option key={product.id} value={product.id}>
                        {translatedPanelText(`${product.name} · ${numberText(balance?.quantityAvailable || 0)} adet`)}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Gönderi tipi</span>
                <select
                  value={form.shipmentType}
                  onChange={(event) => updateForm("shipmentType", event.target.value)}
                  className="h-12 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-950 shadow-sm outline-none transition focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                >
                  <option value="outbound">Gönderi</option>
                  <option value="return">İade / Retoure</option>
                </select>
              </label>
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Kredi</p>
                <p className={cx("mt-2 text-lg font-semibold", productNoCredit ? "text-red-600" : "text-slate-950")}>{form.labelProductId ? numberText(selectedAvailable) : "-"}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Oluşacak</p>
                <p className="mt-2 text-lg font-semibold text-slate-950">{numberText(manualCanCreateCount)}</p>
              </div>
            </div>
          )}
        </AdminPanel>

        <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
          {([
            ["single", "Tekli manuel"],
            ["bulk", "Toplu import"],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setManualTab(key)}
              className={cx(
                "rounded-lg px-4 py-2 text-sm font-semibold transition",
                manualTab === key ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-900"
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {manualTab === "single" ? (
          <form className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]" onSubmit={onSubmit}>
            <AdminPanel title="Tekli manuel etiket" caption="Alıcı ve adres bilgilerini aşağıdaki alanlara girin.">
              <div className="space-y-5 p-5">
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-slate-700">Tek adres kutusu</span>
                    <textarea
                      value={addressBlock}
                      onChange={(event) => setAddressBlock(event.target.value)}
                      onPaste={handleAddressBlockPaste}
                      rows={4}
                      placeholder={"Acar Ugur\nGeibelstr 19\n68167 Mannheim"}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                    />
                  </label>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs font-medium text-slate-500">Alıcı adresini tek kutuya yapıştırın; alanlar otomatik doldurulur.</p>
                    <button
                      type="button"
                      onClick={applyAddressBlock}
                      disabled={!addressBlock.trim()}
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                    >
                      <WandSparkles className="h-4 w-4" />
                      Adresi ayrıştır
                    </button>
                  </div>
                </div>
                <label className="block max-w-xs">
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">Etiket adedi</span>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    step={1}
                    value={singlePackageCount}
                    onChange={(event) => setSinglePackageCount(parsePackageCount(event.target.value))}
                    className="h-12 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-950 shadow-sm outline-none transition focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                  />
                  <span className="mt-1.5 block text-xs font-medium text-slate-500">Aynı alıcı ve adrese 1 ile 50 arasında ayrı etiket oluşturabilirsiniz.</span>
                </label>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label={form.shipmentType === "return" ? "Müşteri adı" : "Alıcı"} value={form.recipientName} onChange={(value) => updateForm("recipientName", value)} required />
                  <Field label="Firma (opsiyonel)" value={form.companyName} onChange={(value) => updateForm("companyName", value)} />
                  <Field label="Sipariş no" value={form.orderNumber} onChange={(value) => updateForm("orderNumber", value)} />
                </div>

                <div className="grid gap-4 lg:grid-cols-6">
                  <div className="relative lg:col-span-3">
                    <span className="mb-1.5 block text-sm font-medium text-slate-700">Adres 1</span>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        value={form.address1}
                        onChange={(event) => updateForm("address1", event.target.value)}
                        onFocus={() => setAddressSuggestionOpen(addressSuggestions.length > 0)}
                        onBlur={() => window.setTimeout(() => setAddressSuggestionOpen(false), 120)}
                        onKeyDown={(event) => {
                          if (event.key === "Escape" || event.key === "Tab" || event.key === "Enter") {
                            setAddressSuggestionOpen(false);
                          }
                        }}
                        autoComplete="off"
                        name="manual-address-line1"
                        aria-autocomplete="list"
                        aria-expanded={addressSuggestionOpen && addressSuggestions.length > 0}
                        required
                        placeholder="Sokak ve kapı numarası"
                        className="h-12 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-24 text-sm text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                      />
                      {addressSuggestionLoading ? (
                        <span className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1.5 text-xs font-medium text-slate-500">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Aranıyor
                        </span>
                      ) : null}
                      {addressSuggestionOpen && addressSuggestions.length > 0 ? (
                        <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl" role="listbox">
                          <button
                            type="button"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={keepTypedManualAddress}
                            className="mb-1 flex w-full items-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-left text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100"
                          >
                            <CheckCircle2 className="h-4 w-4 shrink-0" />
                            Yazdığım adresle devam et
                          </button>
                          {addressSuggestions.map((suggestion, index) => (
                            <button
                              key={`${suggestion.display}-${index}`}
                              type="button"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => applyAddressSuggestion(suggestion)}
                              className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-100"
                              role="option"
                            >
                              {suggestion.display}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="lg:col-span-3">
                    <Field label="Adres 2" value={form.address2} onChange={(value) => updateForm("address2", value)} />
                  </div>
                  <div className="lg:col-span-2">
                    <Field label="Posta kodu" value={form.postalCode} onChange={(value) => updateForm("postalCode", value)} required />
                  </div>
                  <div className="lg:col-span-2">
                    <Field label="Şehir" value={form.city} onChange={(value) => updateForm("city", value)} required />
                    {postalLookup.loading ? (
                      <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-500">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Şehir kontrol ediliyor
                      </p>
                    ) : postalLookup.message ? (
                      <p className="mt-1.5 text-xs font-medium text-emerald-700">{postalLookup.message}</p>
                    ) : null}
                  </div>
                  <div className="lg:col-span-2">
                    <Field label="Ülke" value={form.countryCode} onChange={(value) => updateForm("countryCode", value)} />
                  </div>
                </div>

                {manualBlocked ? (
                  <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>Packstation veya Postfiliale adresleri manuel etikette bloklanır.</span>
                  </div>
                ) : null}
                {manualFormatWarnings.length > 0 ? (
                  <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>{manualFormatWarnings.map((warning) => <p key={warning}>{warning}</p>)}</div>
                  </div>
                ) : null}
                {manualFormatAdjustments.length > 0 ? (
                  <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>{manualFormatAdjustments.map((adjustment) => <p key={adjustment}>{adjustment}</p>)}</div>
                  </div>
                ) : null}
                {singleInsufficientCredit ? (
                  <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{singlePackageCount} etiket için yeterli kredi yok. Kullanılabilir kredi: {numberText(selectedAvailable)}.</span>
                  </div>
                ) : null}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    disabled={submitDisabled}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {singlePackageCount > 1 ? `${singlePackageCount} etiketi oluştur` : "Etiketi oluştur"}
                  </button>
                  <button
                    type="button"
                    onClick={clearManualForm}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Formu temizle
                  </button>
                </div>
              </div>
            </AdminPanel>

            <div className="space-y-5">
              <AdminPanel title="Etiket özeti">
                <div className="grid gap-3 p-4">
                  <InfoLine label="Ürün" value={selectedProduct?.name || "Ürün seçilmedi"} />
                  <InfoLine label="Kredi" value={form.labelProductId ? numberText(selectedAvailable) : "-"} />
                  <InfoLine label="Etiket adedi" value={numberText(singlePackageCount)} />
                  <InfoLine label="Tip" value={shipmentTypeLabel(form.shipmentType)} />
                  <InfoLine label="Alıcı" value={formattedManualRecipientName || "-"} />
                  <InfoLine label="Firma" value={cleanManualSingleLine(form.companyName) || "-"} />
                  <InfoLine label="Adres" value={manualAddressPreview} />
                </div>
                <div className="border-t border-slate-100 bg-slate-50 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => void saveManualDefaults()}
                    disabled={!form.labelProductId}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Varsayılan yap
                  </button>
                </div>
              </AdminPanel>

              <AdminPanel
                title="Eski alıcılar"
                count={recentRecipients.length}
                action={
                  <button
                    type="button"
                    onClick={() => void loadRecentManualShipments()}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    {recentManualLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <History className="h-3.5 w-3.5" />}
                    Yenile
                  </button>
                }
              >
                <div className="space-y-2 p-4">
                  {recentManualLoading && recentRecipients.length === 0 ? (
                    <LoadingPanel label="Son manuel alıcılar yükleniyor" />
                  ) : recentRecipients.length > 0 ? (
                    recentRecipients.slice(0, 6).map((row) => (
                      <button
                        key={text(row.id) || manualRecipientKey(row)}
                        type="button"
                        onClick={() => applyRecentManualRow(row)}
                        className="block w-full rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
                      >
                        <span className="block truncate text-sm font-semibold text-slate-950">{[text(row.recipientCompany), text(row.recipientName)].filter(Boolean).join(" · ")}</span>
                        <span className="mt-1 block line-clamp-2 text-xs leading-5 text-slate-500">{compactAddress(row.address1, row.address2, row.postalCode, row.city, row.countryCode)}</span>
                        <span className="mt-1 block text-xs font-medium text-slate-400">{text(row.labelProductName) || "Manuel etiket"} · {dateText(row.createdAt)}</span>
                      </button>
                    ))
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-sm font-medium text-slate-500">
                      Henüz manuel alıcı yok.
                    </div>
                  )}
                </div>
              </AdminPanel>
            </div>
          </form>
        ) : (
          <AdminPanel title="Toplu manuel import" caption="CSV, TSV veya XLSX dosyasındaki adresleri toplu manuel etikete çevirin.">
            <div className="space-y-4 p-5">
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px_180px_180px]">
                <label className="inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">
                  <Upload className="h-4 w-4" />
                  Dosya seç
                  <input className="hidden" type="file" accept=".csv,.tsv,.txt,.xlsx" onChange={(event) => void onManualImportFile(event)} />
                </label>
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Geçerli</p>
                  <p className="mt-1 text-lg font-semibold text-slate-950">{numberText(validManualImportRows.length)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Hatalı</p>
                  <p className="mt-1 text-lg font-semibold text-slate-950">{numberText(invalidManualImportRows.length)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => void submitManualImport()}
                  disabled={importSubmitting || validManualImportRows.length === 0 || productNoCredit || !form.labelProductId}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                >
                  {importSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusSquare className="h-4 w-4" />}
                  Importu oluştur
                </button>
              </div>
              {importFileName ? <p className="text-sm font-medium text-slate-600">{importFileName}</p> : null}
              <p className="text-xs font-medium text-slate-500">
                Sütunlar: companyName, recipientName, address1, address2, postalCode, city, email, phone, orderNumber, packageCount. Tek adres kutusu için addressText de kullanılabilir.
              </p>
              {importRows.length > 0 ? (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Firma / Alıcı</th>
                        <th className="px-3 py-2">Adres</th>
                        <th className="px-3 py-2 text-right">Paket</th>
                        <th className="px-3 py-2">Durum</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {importRows.slice(0, 12).map((row, index) => (
                        <tr key={`${row.orderNumber || row.recipientName}-${index}`}>
                          <td className="px-3 py-3 font-medium text-slate-900">{[row.companyName, row.recipientName].filter(Boolean).join(" · ") || "-"}</td>
                          <td className="px-3 py-3 text-slate-600">{compactAddress(row.address1, row.address2, row.postalCode, row.city, row.countryCode)}</td>
                          <td className="px-3 py-3 text-right text-slate-600">{numberText(row.packageCount)}</td>
                          <td className="px-3 py-3">{isValidManualDraft(row) ? statusBadge("ready") : statusBadge("failed")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importRows.length > 12 ? <div className="bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500">+{numberText(importRows.length - 12)} satır</div> : null}
                </div>
              ) : null}
            </div>
          </AdminPanel>
        )}

        <AdminPanel
          title="Son manuel etiketler"
          caption="Oluşturulan manuel etiketleri buradan tekrar indirebilir, yenileyebilir veya aynı alıcıyı yeniden kullanabilirsiniz."
          count={recentManualRows.length}
          action={
            <button
              type="button"
              onClick={() => void loadRecentManualShipments()}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              {recentManualLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}
              Yenile
            </button>
          }
        >
          {recentManualLoading && recentManualRows.length === 0 ? (
            <div className="p-5"><LoadingPanel label="Son manuel etiketler yükleniyor" /></div>
          ) : recentManualRows.length === 0 ? (
            <div className="flex min-h-48 flex-col items-center justify-center gap-3 bg-slate-50/60 p-8 text-center">
              <Package className="h-8 w-8 text-slate-400" />
              <p className="text-sm font-medium text-slate-600">Henüz manuel etiket yok.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1120px] divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                  <tr>
                    <th className="px-5 py-3.5">Tarih</th>
                    <th className="px-5 py-3.5">Takip / Sipariş</th>
                    <th className="px-5 py-3.5">Alıcı</th>
                    <th className="px-5 py-3.5">Etiket</th>
                    <th className="px-5 py-3.5">Durum</th>
                    <th className="px-5 py-3.5 text-right">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentManualRows.map((row) => {
                    const shipmentId = text(row.id);
                    const working = manualRowActionId.endsWith(`:${shipmentId}`);
                    const inactive = isCancelledShipmentRow(row) || isTechnicalVoidShipmentRow(row);
                    const ready = shipmentHasDownload(row);
                    return (
                      <tr key={shipmentId} className="align-top transition hover:bg-slate-50/70">
                        <td className="px-5 py-4 text-slate-600">{dateText(row.createdAt)}</td>
                        <td className="px-5 py-4">
                          <NameStack title={text(row.trackingNumber) || shipmentId} detail={text(row.orderNumber) || "manual"} />
                        </td>
                        <td className="px-5 py-4">
                          <NameStack title={[text(row.recipientName), text(row.recipientCompany)].filter(Boolean).join(" · ")} detail={compactAddress(row.address1, row.address2, row.postalCode, row.city, row.countryCode)} />
                        </td>
                        <td className="px-5 py-4">
                          <NameStack title={text(row.labelProductName) || "-"} detail={`${text(row.labelProductCarrier) || "-"} · ${shipmentTypeLabel(row.shipmentType)}`} />
                        </td>
                        <td className="px-5 py-4">{statusBadge(text(row.status) || text(row.labelGenerationStatus) || "created")}</td>
                        <td className="px-5 py-4 text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => applyRecentManualRow(row)}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                            >
                              <Copy className="h-3.5 w-3.5" />
                              Tekrar kullan
                            </button>
                            {ready ? (
                              <>
                                <a href={shipmentLabelHref(shipmentId, "normal", true)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50">
                                  <FileText className="h-3.5 w-3.5" />
                                  PDF
                                </a>
                                <a href={shipmentLabelHref(shipmentId, "zip", true)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50">
                                  <Download className="h-3.5 w-3.5" />
                                  ZIP
                                </a>
                              </>
                            ) : null}
                            <button
                              type="button"
                              disabled={working || inactive}
                              onClick={() => void regenerateRecentManualShipment(row)}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                            >
                              {manualRowActionId === `regenerate:${shipmentId}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <WandSparkles className="h-3.5 w-3.5" />}
                              Yenile
                            </button>
                            <button
                              type="button"
                              disabled={working || inactive}
                              onClick={() => void cancelRecentManualShipment(row)}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-50"
                            >
                              {manualRowActionId === `cancel:${shipmentId}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                              İptal
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </AdminPanel>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {confirmationDialog}
      <CustomerPageHeader title="Etiket oluştur" caption="Siparişten veya manuel adresten gönderi oluşturun." icon={<Send className="h-5 w-5" />} />

      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}
      {createdManualShipments.length > 0 ? (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50/80 p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-base font-semibold text-emerald-950">{numberText(createdManualShipments.length)} etiket hazır</h2>
              <p className="mt-1 text-sm text-emerald-800">PDF, ZIP veya gönderi detayına buradan hızlıca ulaşabilirsiniz.</p>
            </div>
            {createdManualShipments.length > 1 ? (
              <a
                href={shipmentBulkHref(createdManualShipments.map((item) => item.shipmentId), "manual")}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800"
              >
                <Download className="h-4 w-4" />
                Tümünü ZIP indir
              </a>
            ) : null}
          </div>
          <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {createdManualShipments.map((item, index) => (
              <div key={item.shipmentId} className="rounded-xl border border-emerald-200 bg-white px-3 py-3 shadow-sm">
                <p className="truncate text-sm font-semibold text-slate-950">{item.trackingNumber || item.orderNumber || `Etiket ${index + 1}`}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Link
                    to={`/customer/shipments/${encodeURIComponent(item.shipmentId)}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Detay
                  </Link>
                  <a
                    href={shipmentLabelHref(item.shipmentId, "normal", true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    PDF
                  </a>
                  <a
                    href={shipmentLabelHref(item.shipmentId, "zip", true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <Download className="h-3.5 w-3.5" />
                    ZIP
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <AdminPanel title="Gönderi bilgileri" caption="Sipariş seçerek veya manuel adres girerek etiket üretin.">
        {loading ? (
          <div className="p-5"><LoadingPanel label="Etiket bilgileri yükleniyor" /></div>
        ) : (
          <form className="space-y-5 p-5" onSubmit={onSubmit}>
            <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
              {(["order", "manual"] as const).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setMode(item)}
                  className={cx(
                    "rounded-lg px-4 py-2 text-sm font-semibold transition",
                    mode === item ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-900"
                  )}
                >
                  {item === "order" ? "Sipariş" : "Manuel"}
                </button>
              ))}
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Seçili ürün</p>
                <p className="mt-2 text-sm font-semibold text-slate-950">{selectedProduct?.name || "Ürün seçilmedi"}</p>
                <p className={cx("mt-1 text-xs font-medium", productNoCredit ? "text-red-600" : "text-slate-500")}>
                  {form.labelProductId ? `${numberText(selectedAvailable)} kullanılabilir kredi` : "Etiket ürünü zorunlu"}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Adres kontrolü</p>
                <p className="mt-2 text-sm font-semibold text-slate-950">
                  {mode === "order" ? "Sipariş adresi kullanılacak" : manualMissing ? "Eksik alan var" : manualBlocked ? "Adres tipi desteklenmiyor" : "Manuel adres hazır"}
                </p>
                <p className="mt-1 text-xs font-medium text-slate-500">
                  {mode === "order" ? "Mağazadan gelen müşteri adresiyle etiket üretir." : "Alıcı, adres, posta kodu ve şehir dolu olmalı."}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Tekrar kullanım</p>
                <p className="mt-2 text-sm font-semibold text-slate-950">{numberText(recentRecipients.length)} eski manuel alıcı</p>
                <p className="mt-1 text-xs font-medium text-slate-500">Aşağıdaki listeden seçip tekrar yazmadan forma aktarabilirsiniz.</p>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {mode === "order" ? (
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">Sipariş</span>
                  <select
                    value={form.orderId}
                    onChange={(event) => updateForm("orderId", event.target.value)}
                    className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-950 shadow-sm outline-none focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                    required
                  >
                    <option value="">Sipariş seçin</option>
                    {orders.map((order) => (
                      <option key={order.id} value={order.id}>
                        {order.orderNumber} · {order.recipientName} · {order.city}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <>
                  <div className="lg:col-span-2">
                    <label className="block">
                      <span className="mb-1.5 block text-sm font-medium text-slate-700">Tek adres kutusu</span>
                      <textarea
                        value={addressBlock}
                        onChange={(event) => setAddressBlock(event.target.value)}
                        rows={4}
                        placeholder={"Max Mustermann\nMusterstr. 12\n10115 Berlin"}
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={applyAddressBlock}
                      disabled={!addressBlock.trim()}
                      className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                    >
                      <WandSparkles className="h-4 w-4" />
                      Ayrıştır
                    </button>
                  </div>
                  <Field label="Alıcı" value={form.recipientName} onChange={(value) => updateForm("recipientName", value)} required />
                  <Field label="Firma (opsiyonel)" value={form.companyName} onChange={(value) => updateForm("companyName", value)} />
                  <Field label="Sipariş no" value={form.orderNumber} onChange={(value) => updateForm("orderNumber", value)} />
                  <Field label="Adres 1" value={form.address1} onChange={(value) => updateForm("address1", value)} required />
                  <Field label="Adres 2" value={form.address2} onChange={(value) => updateForm("address2", value)} />
                  <Field label="Posta kodu" value={form.postalCode} onChange={(value) => updateForm("postalCode", value)} required />
                  <div>
                    <Field label="Şehir" value={form.city} onChange={(value) => updateForm("city", value)} required />
                    {postalLookup.loading ? (
                      <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-500">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Şehir kontrol ediliyor
                      </p>
                    ) : postalLookup.message ? (
                      <p className="mt-1.5 text-xs font-medium text-emerald-700">{postalLookup.message}</p>
                    ) : null}
                  </div>
                  <Field label="Ülke" value={form.countryCode} onChange={(value) => updateForm("countryCode", value)} />
                  <Field label="Gönderen adı" value={form.senderName} onChange={(value) => updateForm("senderName", value)} />
                </>
              )}

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Etiket ürünü</span>
                <select
                  value={form.labelProductId}
                  onChange={(event) => updateForm("labelProductId", event.target.value)}
                  className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-950 shadow-sm outline-none focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                  required
                >
                  <option value="">Ürün seçin</option>
                  {sortedProducts.map((product) => {
                    const balance = balanceByProductId.get(product.id);
                    return (
                      <option key={product.id} value={product.id}>
                        {product.name} · {numberText(balance?.quantityAvailable || 0)} adet
                      </option>
                    );
                  })}
                </select>
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Tip</span>
                <select
                  value={form.shipmentType}
                  onChange={(event) => updateForm("shipmentType", event.target.value)}
                  className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-950 shadow-sm outline-none focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10"
                >
                  <option value="outbound">Gönderi</option>
                  <option value="return">İade</option>
                </select>
              </label>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => void saveManualDefaults()}
                  disabled={!form.labelProductId}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Varsayılan yap
                </button>
              </div>
            </div>

            {mode === "manual" ? (
              <div className="space-y-4">
                {manualBlocked ? (
                  <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>Packstation veya Postfiliale adresleri manuel etiket için bloklanır. Bu adresler mağaza siparişi üzerinden veya doğru alıcı adresiyle işlenmeli.</span>
                  </div>
                ) : null}
                {manualFormatWarnings.length > 0 ? (
                  <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>{manualFormatWarnings.map((warning) => <p key={warning}>{warning}</p>)}</div>
                  </div>
                ) : null}
                {manualFormatAdjustments.length > 0 ? (
                  <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>{manualFormatAdjustments.map((adjustment) => <p key={adjustment}>{adjustment}</p>)}</div>
                  </div>
                ) : null}
                {productNoCredit ? (
                  <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>Seçili üründe kullanılabilir kredi yok. Etiket oluşturmak için farklı ürün seçin veya kredi satın alın.</span>
                  </div>
                ) : null}
                <section className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-950">Son manuel alıcılar</h3>
                      <p className="mt-1 text-xs font-medium text-slate-500">Eski müşteriyi seçip adresi tekrar yazmadan yeni etiket oluşturabilirsiniz.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void loadRecentManualShipments()}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      {recentManualLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <History className="h-3.5 w-3.5" />}
                      Yenile
                    </button>
                  </div>
                  {recentManualLoading ? (
                    <div className="mt-3"><LoadingPanel label="Son manuel alıcılar yükleniyor" /></div>
                  ) : recentRecipients.length > 0 ? (
                    <div className="mt-3 grid gap-2 lg:grid-cols-2">
                      {recentRecipients.slice(0, 8).map((row) => (
                        <div key={text(row.id) || manualRecipientKey(row)} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-slate-950">{[text(row.recipientCompany), text(row.recipientName)].filter(Boolean).join(" · ")}</p>
                              <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">
                                {compactAddress(row.address1, row.address2, row.postalCode, row.city, row.countryCode)}
                              </p>
                              <p className="mt-1 text-xs font-medium text-slate-400">{text(row.labelProductName) || "Manuel etiket"} · {dateText(row.createdAt)}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => applyRecentManualRow(row)}
                              className="shrink-0 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
                            >
                              Tekrar kullan
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-4 text-sm font-medium text-slate-500">
                      Henüz manuel oluşturulmuş alıcı bulunmuyor. İlk manuel etiketten sonra burada tekrar kullanım listesi oluşacak.
                    </div>
                  )}
                </section>
              </div>
            ) : null}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="submit"
                disabled={submitDisabled}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Etiketi oluştur
              </button>
              {mode === "manual" ? (
                <button
                  type="button"
                  onClick={clearManualForm}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Formu temizle
                </button>
              ) : null}
            </div>
          </form>
        )}
      </AdminPanel>

      <AdminPanel title="Toplu manuel import" caption="CSV, TSV veya XLSX dosyasındaki adresleri manuel etikete çevirin.">
        <div className="space-y-4 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50">
              <Upload className="h-4 w-4" />
              Dosya seç
              <input className="hidden" type="file" accept=".csv,.tsv,.txt,.xlsx" onChange={(event) => void onManualImportFile(event)} />
            </label>
            <button
              type="button"
              onClick={() => void submitManualImport()}
              disabled={importSubmitting || importRows.filter(isValidManualDraft).length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              {importSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusSquare className="h-4 w-4" />}
              Importu oluştur
            </button>
          </div>
          {importFileName ? <p className="text-sm font-medium text-slate-600">{importFileName}</p> : null}
          {importRows.length > 0 ? (
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Firma / Alıcı</th>
                    <th className="px-3 py-2">Adres</th>
                    <th className="px-3 py-2">Paket</th>
                    <th className="px-3 py-2">Durum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {importRows.slice(0, 8).map((row, index) => (
                    <tr key={`${row.orderNumber || row.recipientName}-${index}`}>
                      <td className="px-3 py-2 font-medium text-slate-900">{[row.companyName, row.recipientName].filter(Boolean).join(" · ") || "-"}</td>
                      <td className="px-3 py-2 text-slate-600">{compactAddress(row.address1, row.address2, row.postalCode, row.city, row.countryCode)}</td>
                      <td className="px-3 py-2 text-slate-600">{numberText(row.packageCount)}</td>
                      <td className="px-3 py-2">{isValidManualDraft(row) ? statusBadge("ready") : statusBadge("failed")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {importRows.length > 8 ? <div className="bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500">+{numberText(importRows.length - 8)} satır</div> : null}
            </div>
          ) : null}
        </div>
      </AdminPanel>
    </div>
  );
}

type LabelCartItem = {
  available: number;
  grossLineTotal: number;
  grossUnitPrice: number;
  lineTotal: number;
  netLineTotal: number;
  product: LabelProductSummary;
  quantity: number;
  taxLineTotal: number;
  taxRate: number;
  unitNetPrice: number;
};

const labelPurchaseTaxRate = 0.19;

function roundLabelMoney(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function labelTaxBreakdown(netAmount: number, taxRate = labelPurchaseTaxRate) {
  const netTotal = roundLabelMoney(Math.max(0, netAmount));
  const taxTotal = roundLabelMoney(netTotal * Math.max(0, taxRate));
  const grossTotal = roundLabelMoney(netTotal + taxTotal);
  return { grossTotal, netTotal, taxRate, taxTotal };
}

type PendingLabelCheckout = {
  checkoutToken: string;
  checkoutUrl: string;
  draftOrderId: string;
  grossTotal?: number;
  items: Array<{
    grossTotal?: number;
    grossUnitPrice?: number;
    labelProductId: string;
    labelProductName: string;
    netTotal?: number;
    netUnitPrice?: number;
    quantity: number;
    taxRate?: number;
    taxTotal?: number;
    totalPrice: number;
    unitPrice: number;
  }>;
  labelProductName?: string;
  netTotal?: number;
  quantity: number;
  taxRate?: number;
  taxTotal?: number;
  totalPrice: number;
};

function normalizePendingCheckout(value: unknown): PendingLabelCheckout | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const checkoutToken = text(record.checkoutToken);
  const checkoutUrl = text(record.checkoutUrl);
  const draftOrderId = text(record.draftOrderId);
  if (!checkoutToken || !checkoutUrl || !draftOrderId) return null;
  const legacyName = text(record.labelProductName);
  const legacyQuantity = Number(record.quantity || 0);
  const items: PendingLabelCheckout["items"] = Array.isArray(record.items)
    ? record.items
        .map((item) => {
          const line = item as Record<string, unknown>;
          return {
            grossTotal: Number(line.grossTotal || 0),
            grossUnitPrice: Number(line.grossUnitPrice || 0),
            labelProductId: text(line.labelProductId),
            labelProductName: text(line.labelProductName) || "Etiket ürünü",
            netTotal: Number(line.netTotal || 0),
            netUnitPrice: Number(line.netUnitPrice || 0),
            quantity: Number(line.quantity || 0),
            taxRate: Number(line.taxRate ?? labelPurchaseTaxRate),
            taxTotal: Number(line.taxTotal || 0),
            totalPrice: Number(line.totalPrice || 0),
            unitPrice: Number(line.unitPrice || 0),
          };
        })
        .filter((item) => item.labelProductId && item.quantity > 0)
    : legacyName && legacyQuantity > 0
      ? [{ labelProductId: "", labelProductName: legacyName, quantity: legacyQuantity, totalPrice: Number(record.totalPrice || 0), unitPrice: 0 }]
      : [];
  const explicitNetTotal = Number(record.netTotal || 0);
  const explicitTaxTotal = Number(record.taxTotal || 0);
  const explicitGrossTotal = Number(record.grossTotal || 0);
  const legacyTotalPrice = Number(record.totalPrice || 0);
  const netTotal =
    explicitNetTotal ||
    items.reduce((sum, item) => sum + Number(item.netTotal || item.totalPrice || 0), 0) ||
    legacyTotalPrice;
  const taxTotal =
    explicitTaxTotal ||
    items.reduce((sum, item) => sum + Number(item.taxTotal || 0), 0) ||
    (netTotal > 0 && !explicitGrossTotal ? labelTaxBreakdown(netTotal).taxTotal : 0);
  const grossTotal =
    explicitGrossTotal ||
    items.reduce((sum, item) => sum + Number(item.grossTotal || 0), 0) ||
    (netTotal > 0 ? roundLabelMoney(netTotal + taxTotal) : legacyTotalPrice);
  return {
    checkoutToken,
    checkoutUrl,
    draftOrderId,
    grossTotal: grossTotal || undefined,
    items,
    labelProductName: legacyName || undefined,
    netTotal: netTotal || undefined,
    quantity: Number(record.quantity || items.reduce((sum, item) => sum + item.quantity, 0)),
    taxRate: Number(record.taxRate ?? labelPurchaseTaxRate),
    taxTotal: taxTotal || undefined,
    totalPrice: Number(record.totalPrice || items.reduce((sum, item) => sum + item.totalPrice, 0)),
  };
}

function reconciledLabelQuantity(result: { results?: Array<{ quantity?: number; status?: string }> } | null | undefined) {
  return (result?.results || [])
    .filter((row) => text(row.status) === "allocated")
    .reduce((sum, row) => sum + Number(row.quantity || 0), 0);
}

function LabelProductsPage({ title }: { title: string }) {
  const [products, setProducts] = useState<LabelProductSummary[]>([]);
  const [stock, setStock] = useState<Record<string, number>>({});
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [balances, setBalances] = useState<LabelBalanceSummary[]>([]);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [pendingCheckout, setPendingCheckout] = useState<PendingLabelCheckout | null>(() => {
    try {
      const raw = localStorage.getItem("kenpost.pendingCheckout");
      return raw ? normalizePendingCheckout(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const sortedProducts = useMemo(() => sortLabelProductsForOrders(products), [products]);

  async function loadPurchasingData() {
    setLoading(true);
    setError(null);
    try {
      const reconciliation = await apiClient.reconcileLabelPurchases({ sinceDays: 14 }).catch(() => null);
      const allocatedQuantity = reconciledLabelQuantity(reconciliation);
      if (allocatedQuantity > 0) {
        setNotice(`${numberText(allocatedQuantity)} etiketlik tamamlanmış Shopify ödemeniz bakiyeye işlendi.`);
      }
      const [productResult, balanceResult] = await Promise.all([apiClient.labelProductsForPurchase(), apiClient.balances()]);
      const nextStock = productResult.stock || {};
      setProducts(
        productResult.products.map((product) => {
          const basePrice = Number(product.basePrice ?? 0);
          const customerPrice = Number(product.customerPrice ?? 0);
          const effectivePrice = Number(product.effectivePrice ?? product.price ?? 0);
          return {
            ...product,
            effectivePrice: effectivePrice > 0 ? effectivePrice : customerPrice > 0 ? customerPrice : basePrice,
            stockAvailable: product.stockAvailable ?? nextStock[product.id] ?? 0,
            weightMaxGrams: Number(product.weightMaxGrams || 0),
            weightMinGrams: Number(product.weightMinGrams || 0),
          };
        })
      );
      setStock(nextStock);
      setBillingEnabled(Boolean(productResult.billingEnabled));
      setBalances(balanceResult.balances || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Etiket ürünleri alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPurchasingData();
  }, []);

  function balanceFor(productId: string) {
    return balances.find((balance) => balance.labelProductId === productId)?.quantityAvailable || 0;
  }

  function stockFor(product: LabelProductSummary) {
    return stock[product.id] ?? product.stockAvailable ?? 0;
  }

  const cartItems = useMemo<LabelCartItem[]>(() => {
    return sortedProducts
      .map((product) => {
        const quantity = Number(quantities[product.id] || 0);
        if (!Number.isInteger(quantity) || quantity <= 0) return null;
        const unitNetPrice = roundLabelMoney(Number(product.effectivePrice || 0));
        const netLineTotal = roundLabelMoney(quantity * unitNetPrice);
        const tax = labelTaxBreakdown(netLineTotal);
        return {
          available: stockFor(product),
          grossLineTotal: tax.grossTotal,
          grossUnitPrice: quantity > 0 ? roundLabelMoney(tax.grossTotal / quantity) : roundLabelMoney(unitNetPrice * (1 + labelPurchaseTaxRate)),
          lineTotal: tax.grossTotal,
          netLineTotal: tax.netTotal,
          product,
          quantity,
          taxLineTotal: tax.taxTotal,
          taxRate: tax.taxRate,
          unitNetPrice,
        };
      })
      .filter((item): item is LabelCartItem => Boolean(item));
  }, [quantities, sortedProducts, stock]);

  const cartTotal = cartItems.reduce((sum, item) => sum + item.lineTotal, 0);
  const cartNetTotal = roundLabelMoney(cartItems.reduce((sum, item) => sum + item.netLineTotal, 0));
  const cartTaxTotal = roundLabelMoney(cartItems.reduce((sum, item) => sum + item.taxLineTotal, 0));
  const cartQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartStockIssue = cartItems.find((item) => item.quantity > item.available);
  const cartBelowMinimum = cartItems.length > 0 && !meetsLabelCheckoutMinimum(cartTotal);
  const pendingCheckoutMeetsMinimum = pendingCheckout && meetsLabelCheckoutMinimum(pendingCheckout.grossTotal || pendingCheckout.totalPrice);
  const checkoutBlockReason = !billingEnabled
    ? "Ödeme bağlantısı şu anda kullanılamıyor. Lütfen destek ekibine bildirin."
    : cartItems.length === 0
      ? pendingCheckout
        ? null
        : "Ödeme için önce ürün miktarı girin veya Sepete ekle düğmesini kullanın."
      : cartStockIssue
        ? "Sepetteki miktarı mevcut stok sınırına düşürün."
        : cartBelowMinimum
          ? minimumLabelCheckoutMessage
          : null;

  function setCartQuantity(product: LabelProductSummary, value: string) {
    setQuantities((current) => ({ ...current, [product.id]: value.replace(/[^\d]/g, "") }));
  }

  function adjustCartQuantity(product: LabelProductSummary, delta: number) {
    const current = Number(quantities[product.id] || 0);
    const next = Math.max(0, current + delta);
    setQuantities((values) => ({ ...values, [product.id]: next > 0 ? String(next) : "" }));
  }

  function ensureCartProduct(product: LabelProductSummary) {
    setQuantities((current) => {
      const quantity = Number(current[product.id] || 0);
      const next = Number.isInteger(quantity) && quantity > 0 ? quantity : 1;
      return { ...current, [product.id]: String(next) };
    });
  }

  function removeCartItem(productId: string) {
    setQuantities((current) => ({ ...current, [productId]: "" }));
  }

  function clearCart() {
    setQuantities({});
  }

  async function createCartCheckout() {
    setError(null);
    setNotice(null);
    if (cartItems.length === 0) {
      setError("Ödeme için sepete en az bir etiket ürünü ekleyin.");
      return;
    }
    if (!billingEnabled) {
      setError("Ödeme bağlantısı şu anda kullanılamıyor. Lütfen destek ekibine bildirin.");
      return;
    }
    if (cartStockIssue) {
      setError(`${cartStockIssue.product.name} için yeterli etiket stoku yok.`);
      return;
    }
    if (cartBelowMinimum) {
      setError(minimumLabelCheckoutMessage);
      return;
    }

    // Open the tab while the click is still a direct user gesture. Browsers
    // commonly block window.open calls made after the checkout API await.
    let checkoutWindow: Window | null = null;
    try {
      checkoutWindow = window.open("about:blank", "_blank");
      if (checkoutWindow) {
        checkoutWindow.opener = null;
        checkoutWindow.document.title = "KenPost ödeme hazırlanıyor";
        checkoutWindow.document.body.textContent = "Ödeme sayfanız hazırlanıyor…";
        checkoutWindow.document.body.style.cssText =
          "margin:0;min-height:100vh;display:grid;place-items:center;font:600 16px system-ui;color:#0f172a;background:#f8fafc";
      }
    } catch {
      checkoutWindow = null;
    }

    setProcessingId("cart");
    try {
      const result = await apiClient.createLabelCheckout({
        items: cartItems.map((item) => ({ labelProductId: item.product.id, quantity: item.quantity })),
      });
      const resultItems = new Map((result.items || []).map((item) => [item.labelProductId, item]));
      const checkout: PendingLabelCheckout = {
        checkoutToken: result.checkoutToken,
        checkoutUrl: result.checkoutUrl,
        draftOrderId: result.draftOrderId,
        items: cartItems.map((item) => ({
          grossTotal: resultItems.get(item.product.id)?.grossTotal ?? item.grossLineTotal,
          grossUnitPrice: resultItems.get(item.product.id)?.grossUnitPrice ?? item.grossUnitPrice,
          labelProductId: item.product.id,
          labelProductName: item.product.name,
          netTotal: resultItems.get(item.product.id)?.netTotal ?? item.netLineTotal,
          netUnitPrice: resultItems.get(item.product.id)?.netUnitPrice ?? item.unitNetPrice,
          quantity: item.quantity,
          taxRate: resultItems.get(item.product.id)?.taxRate ?? item.taxRate,
          taxTotal: resultItems.get(item.product.id)?.taxTotal ?? item.taxLineTotal,
          totalPrice: resultItems.get(item.product.id)?.totalPrice ?? item.netLineTotal,
          unitPrice: resultItems.get(item.product.id)?.unitPrice ?? item.unitNetPrice,
        })),
        grossTotal: result.grossTotal ?? result.totalPrice ?? cartTotal,
        netTotal: result.netTotal ?? cartNetTotal,
        quantity: result.quantity,
        taxRate: result.taxRate ?? labelPurchaseTaxRate,
        taxTotal: result.taxTotal ?? cartTaxTotal,
        totalPrice: result.grossTotal ?? result.totalPrice ?? cartTotal,
      };
      setPendingCheckout(checkout);
      localStorage.setItem("kenpost.pendingCheckout", JSON.stringify(checkout));
      let openedInNewTab = false;
      if (checkoutWindow && !checkoutWindow.closed) {
        try {
          checkoutWindow.location.replace(result.checkoutUrl);
          openedInNewTab = true;
        } catch {
          checkoutWindow.close();
        }
      }

      setQuantities({});
      if (!openedInNewTab) {
        // Popup blocking must never strand a valid Shopify checkout. Keep the
        // pending checkout in localStorage and continue in the current tab.
        window.location.assign(result.checkoutUrl);
        return;
      }
      setNotice("Ödeme sayfası açıldı. Ödeme tamamlanınca bakiye otomatik güncellenecek.");
    } catch (err) {
      if (checkoutWindow && !checkoutWindow.closed) checkoutWindow.close();
      setError(err instanceof ApiError ? err.message : "Checkout oluşturulamadı.");
    } finally {
      setProcessingId(null);
    }
  }

  function clearPendingCheckout() {
    setPendingCheckout(null);
    localStorage.removeItem("kenpost.pendingCheckout");
  }

  async function applyCheckoutResult(checkout: PendingLabelCheckout, showPendingNotice = true) {
    try {
      const result = await apiClient.confirmLabelCheckout({
        checkoutToken: checkout.checkoutToken,
        draftOrderId: checkout.draftOrderId,
      });
      if (result.status === "pending_payment") {
        if (showPendingNotice) setNotice(result.message || "Ödeme henüz tamamlanmamış görünüyor.");
        return;
      }
      if (result.status === "paid_and_allocated" || result.status === "already_processed") {
        setNotice("Ödeme doğrulandı ve etiketler bakiyeye eklendi.");
        clearPendingCheckout();
        await loadPurchasingData();
        return;
      }
      setNotice(result.message || "Ödeme kontrolü tamamlandı.");
    } catch (err) {
      if (showPendingNotice) setError(err instanceof ApiError ? err.message : "Ödeme doğrulanamadı.");
      throw err;
    }
  }

  async function verifyPendingCheckout() {
    if (!pendingCheckout || processingId) return;
    setError(null);
    setProcessingId("pending-checkout");
    try {
      await applyCheckoutResult(pendingCheckout);
    } catch {
      // applyCheckoutResult already exposes the useful customer-facing error.
    } finally {
      setProcessingId(null);
    }
  }

  useEffect(() => {
    if (!pendingCheckout) return;
    let running = false;
    async function verifyOnReturn() {
      if (!pendingCheckout || running) return;
      running = true;
      setError(null);
      try {
        await applyCheckoutResult(pendingCheckout, false);
      } catch {
        // Silent auto-check; the user can retry by opening checkout again.
      } finally {
        running = false;
      }
    }
    const focusHandler = () => {
      void verifyOnReturn();
    };
    const visibilityHandler = () => {
      if (document.visibilityState === "visible") void verifyOnReturn();
    };
    const timer = window.setTimeout(() => void verifyOnReturn(), 1000);
    const interval = window.setInterval(() => void verifyOnReturn(), 60_000);
    window.addEventListener("focus", focusHandler);
    document.addEventListener("visibilitychange", visibilityHandler);
    return () => {
      window.clearTimeout(timer);
      window.clearInterval(interval);
      window.removeEventListener("focus", focusHandler);
      document.removeEventListener("visibilitychange", visibilityHandler);
    };
  }, [pendingCheckout]);

  if (loading) {
    return <LoadingPanel label="Etiket ürünleri yükleniyor" />;
  }

  return (
    <div className="space-y-5">
      <CustomerPageHeader
        title={title}
        caption="Etiket kredilerini checkout ile satın alın; ödeme tamamlanınca bakiyeniz otomatik güncellenir."
        icon={<CreditCard className="h-5 w-5" />}
        action={billingEnabled ? statusBadge("active") : statusBadge("inactive")}
      />

      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <AdminNotice message={notice} /> : null}

      {pendingCheckout ? (
        <section className="rounded-2xl border border-cyan-200 bg-cyan-50 p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-cyan-950">Tamamlanmayı bekleyen ödeme</p>
              <p className="mt-1 text-sm text-cyan-800">
                {numberText(pendingCheckout.quantity)} etiket · {moneyText(pendingCheckout.grossTotal || pendingCheckout.totalPrice, "EUR")}
              </p>
              <p className="mt-1 text-xs text-cyan-700">{pendingCheckoutMeetsMinimum ? "Ödeme sekmesi açılmadıysa bağlantıyı buradan yeniden açabilirsiniz." : minimumLabelCheckoutMessage}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {pendingCheckoutMeetsMinimum ? <a
                href={pendingCheckout.checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
              >
                <ExternalLink className="h-4 w-4" />
                Ödeme sayfasını aç
              </a> : null}
              <button
                type="button"
                onClick={() => void verifyPendingCheckout()}
                disabled={processingId !== null}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-cyan-300 bg-white px-4 text-sm font-semibold text-cyan-950 shadow-sm transition hover:bg-cyan-100 disabled:opacity-60"
              >
                {processingId === "pending-checkout" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Ödemeyi kontrol et
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {!billingEnabled ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Ödeme bağlantısı şu anda kullanılamıyor. Lütfen destek ekibine bildirin.
        </div>
      ) : null}

      {sortedProducts.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">Satın alınabilir ürün yok.</div>
      ) : (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
          <div className="grid gap-4 lg:grid-cols-2">
            {sortedProducts.map((product) => {
              const quantity = Number(quantities[product.id] || 0);
              const available = stockFor(product);
              const unitNetPrice = roundLabelMoney(Number(product.effectivePrice || 0));
              const unitTax = labelTaxBreakdown(unitNetPrice);
              const lineTax = labelTaxBreakdown(quantity > 0 ? quantity * unitNetPrice : 0);
              return (
                <section key={product.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <NameStack title={product.name} detail={`${product.carrier} · ${orderWeightRangeText(product.weightMinGrams, product.weightMaxGrams)}`} />
                    {statusBadge(available > 0 ? "available" : "inactive")}
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-xl bg-slate-50 p-3">
                      <p className="text-slate-500">Netto fiyat</p>
                      <p className="mt-1 font-semibold text-slate-950">{moneyText(unitNetPrice, "EUR")}</p>
                      <p className="mt-1 text-xs text-slate-500">+ MwSt {moneyText(unitTax.taxTotal, "EUR")}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-3">
                      <p className="text-slate-500">Stok</p>
                      <p className="mt-1 font-semibold text-slate-950">{numberText(available)}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-3">
                      <p className="text-slate-500">Mevcut</p>
                      <p className="mt-1 font-semibold text-slate-950">{numberText(balanceFor(product.id))}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-3">
                      <p className="text-slate-500">Satır brutto</p>
                      <p className="mt-1 font-semibold text-slate-950">{moneyText(lineTax.grossTotal, "EUR")}</p>
                      <p className="mt-1 text-xs text-slate-500">Netto {moneyText(lineTax.netTotal, "EUR")}</p>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto]">
                    <div className="grid h-11 grid-cols-[44px_minmax(0,1fr)_44px] overflow-hidden rounded-xl border border-slate-300 bg-white">
                      <button type="button" onClick={() => adjustCartQuantity(product, -1)} className="text-lg font-semibold text-slate-500 transition hover:bg-slate-50" aria-label={`${product.name} miktarını azalt`}>-</button>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={quantities[product.id] || ""}
                        onChange={(event) => setCartQuantity(product, event.target.value)}
                        className="min-w-0 border-x border-slate-200 px-3 text-center text-sm font-semibold text-slate-950 outline-none"
                        placeholder="0"
                      />
                      <button type="button" onClick={() => adjustCartQuantity(product, 1)} disabled={available <= 0} className="text-lg font-semibold text-slate-700 transition hover:bg-slate-50 disabled:text-slate-300" aria-label={`${product.name} miktarını artır`}>+</button>
                    </div>
                    <button
                      type="button"
                      onClick={() => ensureCartProduct(product)}
                      disabled={available <= 0}
                      className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
                    >
                      <PlusSquare className="h-4 w-4" />
                      Sepete ekle
                    </button>
                  </div>
                  {quantity > available && available >= 0 ? <p className="mt-2 text-xs font-semibold text-red-600">Stok en fazla {numberText(available)} adet.</p> : null}
                </section>
              );
            })}
          </div>

          <AdminPanel title="Etiket kredi sepeti" count={cartItems.length}>
            <div className="space-y-4 p-5">
              {cartItems.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">
                  Ürünlerden miktar girerek sepete satır ekleyin.
                </div>
              ) : (
                <div className="space-y-3">
                  {cartItems.map((item) => (
                    <div key={item.product.id} className="rounded-xl border border-slate-200 bg-white p-3">
                      <div className="flex items-start justify-between gap-3">
                        <NameStack title={item.product.name} detail={`${numberText(item.quantity)} adet · netto ${moneyText(item.unitNetPrice, "EUR")}`} />
                        <button
                          type="button"
                          onClick={() => removeCartItem(item.product.id)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
                          aria-label={`${item.product.name} satırını sepetten çıkar`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-3 flex items-center justify-between text-sm">
                        <span className="text-slate-500">Satır brutto</span>
                        <span className="font-semibold text-slate-950">{moneyText(item.lineTotal, "EUR")}</span>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                        <span>Netto {moneyText(item.netLineTotal, "EUR")}</span>
                        <span>MwSt {moneyText(item.taxLineTotal, "EUR")}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="rounded-xl bg-slate-950 p-4 text-white">
                <div className="flex items-center justify-between text-sm text-slate-300">
                  <span>Toplam adet</span>
                  <span>{numberText(cartQuantity)}</span>
                </div>
                <div className="mt-3 flex items-center justify-between text-sm text-slate-300">
                  <span>Ara toplam netto</span>
                  <span>{moneyText(cartNetTotal, "EUR")}</span>
                </div>
                <div className="mt-2 flex items-center justify-between text-sm text-slate-300">
                  <span>MwSt 19%</span>
                  <span>{moneyText(cartTaxTotal, "EUR")}</span>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm font-semibold">Ödenecek brutto</span>
                  <span className="text-xl font-semibold">{moneyText(cartTotal, "EUR")}</span>
                </div>
              </div>
              <p className="text-xs text-slate-500">Minimum alışveriş: KDV dahil 20 €.</p>
              {cartStockIssue ? <p className="text-xs font-semibold text-red-600">{cartStockIssue.product.name} stok sınırını aşıyor.</p> : null}
              <button
                type="button"
                onClick={() => void createCartCheckout()}
                disabled={processingId === "cart"}
                aria-describedby={checkoutBlockReason ? "label-checkout-guidance" : undefined}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
              >
                {processingId === "cart" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                Ödemeye geç
              </button>
              {checkoutBlockReason ? (
                <p id="label-checkout-guidance" className="text-xs font-medium text-amber-700">
                  {checkoutBlockReason}
                </p>
              ) : null}
              <button
                type="button"
                onClick={clearCart}
                disabled={cartItems.length === 0 || processingId === "cart"}
                className="inline-flex h-10 w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
              >
                Sepeti temizle
              </button>
            </div>
          </AdminPanel>
        </div>
      )}
    </div>
  );
}

function ProtectedRoute({
  auth,
  role,
  children,
}: {
  auth: AuthState;
  role: "admin" | "customer";
  children: ReactNode;
}) {
  const location = useLocation();
  if (auth.status === "loading") return <FullScreenLoader />;
  if (auth.status === "signed-out") return <Navigate to={isCustomerConnectionReturnPath(location.pathname) ?  `/sign-in?returnTo=${encodeURIComponent(location.pathname + location.search)}` : "/sign-in"} replace />;
  if (role === "admin" && auth.user.role !== "admin") return <Navigate to="/customer" replace />;
  if (role === "customer" && auth.user.role === "admin") return <Navigate to="/admin" replace />;
  return <>{children}</>;
}

function FullScreenLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
}

function AuthHeader({ title, caption }: { title: string; caption: string }) {
  return (
    <div>
      <p className="text-sm font-semibold uppercase text-slate-500">KenPost</p>
      <h1 className="mt-2 text-3xl font-semibold text-slate-950">{title}</h1>
      <p className="mt-2 text-sm text-slate-500">{caption}</p>
    </div>
  );
}

function Field({
  label,
  type = "text",
  value,
  onChange,
  autoComplete,
  required = false,
  showPasswordLabel = "Şifreyi göster",
  hidePasswordLabel = "Şifreyi gizle",
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  required?: boolean;
  showPasswordLabel?: string;
  hidePasswordLabel?: string;
}) {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword && passwordVisible ? "text" : type;

  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
      <span className="relative block">
        <input
          type={inputType}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          autoComplete={autoComplete}
          className={cx(
            "h-12 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-950 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10",
            isPassword ? "pr-12" : null,
          )}
          required={required}
        />
        {isPassword ? (
          <button
            type="button"
            onClick={() => setPasswordVisible((visible) => !visible)}
            className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            aria-label={passwordVisible ? hidePasswordLabel : showPasswordLabel}
            title={passwordVisible ? hidePasswordLabel : showPasswordLabel}
          >
            {passwordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        ) : null}
      </span>
    </label>
  );
}

function LoadingPanel({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-sm font-medium text-slate-500 shadow-sm">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
      </span>
      <span>{label}</span>
    </div>
  );
}

function MetricCard({ icon, label, value }: { icon: ReactNode; label: string; value: unknown }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md">
      <div className="flex items-center justify-between gap-3">
        <p className="truncate text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600 ring-1 ring-slate-200">{icon}</div>
      </div>
      <p className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">{numberText(value)}</p>
    </div>
  );
}

function DetailCard({ items, title }: { items: Array<[string, ReactNode]>; title: string }) {
  return (
    <section className="min-w-0 overflow-visible rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4">
        <h2 className="text-base font-semibold text-slate-950">{title}</h2>
      </div>
      <dl className="mt-4 grid gap-3 text-sm">
        {items.map(([label, value]) => (
          <div key={label} className="mx-5 grid gap-1 rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2 sm:grid-cols-[170px_1fr] sm:gap-3">
            <dt className="font-medium text-slate-500">{label}</dt>
            <dd className="min-w-0 break-words text-slate-900">{value || "-"}</dd>
          </div>
        ))}
      </dl>
      <div className="h-4" />
    </section>
  );
}

function DataSection({
  columns,
  empty,
  rows,
  title,
}: {
  columns: DataColumn[];
  empty: string;
  rows: unknown[];
  title: string;
}) {
  return (
    <AdminPanel title={title} count={rows.length}>
      <DataTable rows={rows as DataRow[]} columns={columns} empty={empty} />
    </AdminPanel>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function ErrorPanel({ title, message }: { title: string; message: string }) {
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800">
      <h1 className="text-lg font-semibold">{title}</h1>
      <p className="mt-2 text-sm">{message}</p>
    </div>
  );
}

function displayName(user: SessionUser) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || user.companyName || user.email;
}

function currentPageTitle(pathname: string, role: UserRole) {
  if (role === "admin") {
    const adminTitles = [
      ["/admin/amazon-v2", "Amazon V2 yönetimi"],
      ["/admin/settings/invoices", "Fatura ayarları"],
      ["/admin/settings", "Ayarlar"],
      ["/admin/customer-prices", "Müşteri fiyatları"],
      ["/admin/labels", "Etiket Ürünleri"],
      ["/admin/label-products", "Etiket Ürünleri"],
      ["/admin/label-sources", "Etiket Envanteri"],
      ["/admin/expired-labels", "Süresi Bitmiş Etiketler"],
      ["/admin/invoices", "Faturalama"],
      ["/admin/purchases", "Faturalama"],
      ["/admin/affiliates", "Affiliate hesapları"],
      ["/admin/support", "Destek merkezi"],
      ["/admin/training", "Eğitimler"],
      ["/admin/commissions", "Komisyonlar"],
      ["/admin/error-logs", "Hata kayıtları"],
      ["/admin/label-checks", "Etiket kontrolleri"],
      ["/admin/faulty-labels", "Hatalı Etiketler"],
      ["/admin/missing-labels", "Taşıyıcı Kontrol Hataları"],
      ["/admin/canceled-labels", "Müşteri İptalleri"],
      ["/admin/plans", "Planlar"],
      ["/admin/tracking-api", "Takip API"],
      ["/admin/reports/order-sync", "Mağaza kontrol raporu"],
      ["/admin/reports", "Raporlar"],
    ];
    const customTitle = adminTitles.find(([path]) => pathname === path || pathname.startsWith(`${path}/`));
    if (customTitle) return customTitle[1];
  } else {
    if (/^\/customer\/stores\/[^/]+\/logs$/.test(pathname)) return "Senkron logları";
    const customerTitles = [
      ["/customer/onboarding", "Başlangıç"],
      ["/customer/profile/security", "Güvenlik"],
      ["/customer/support", "Bize ulaşın"],
      ["/customer/training", "Eğitimler"],
      ["/customer/team", "Ekip yönetimi"],
      ["/customer/plan", "Plan"],
      ["/customer/buy-labels", "Etiket satın al"],
      ["/customer/balances", "Etiket bakiyeleri"],
      ["/customer/reports/shipping-health", "Kargo Sağlığı"],
      ["/customer/reports", "Raporlar"],
      ["/customer/label-inventory", "Etiket envanteri"],
      ["/customer/api", "API"],
      ["/customer/jtl-connect", "JTL Connect"],
      ["/customer/create-label", "Etiket oluştur"],
      ["/customer/shipments", "Gönderilerim"],
      ["/customer/stores", "Mağazalar"],
    ];
    const customTitle = customerTitles.find(([path]) => pathname === path || pathname.startsWith(`${path}/`));
    if (customTitle) return customTitle[1];
  }
  const items = role === "admin" ? adminNav : [...customerNav, ...customerAccountNav];
  const active = items.find((item) => navPathMatches(pathname, item, role === "admin" ? "/admin" : "/customer"));
  return active?.label ?? (role === "admin" ? "Kontrol paneli" : "Genel bakış");
}

function roleDashboardTitle(role: UserRole) {
  if (role === "admin") return "Operasyon kontrol paneli";
  if (role === "subcustomer") return "Alt müşteri çalışma alanı";
  return "Müşteri çalışma alanı";
}

function roleCapability(role: UserRole) {
  if (role === "admin") {
    return ["Müşteri ve alt müşteri yönetimi", "Etiket envanteri ve faturalama", "Platform bağlantıları ve raporlar"];
  }
  if (role === "subcustomer") {
    return ["Sipariş ve gönderi operasyonları", "Etiket oluşturma", "Sınırlı müşteri görünümü"];
  }
  return ["Mağaza bağlantıları", "Sipariş ve gönderi yönetimi", "Etiket bakiyesi ve faturalar"];
}

function NameStack({ title, detail }: { title: ReactNode; detail?: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="truncate font-medium text-slate-950">{title || "-"}</p>
      {detail ? <p className="mt-1 truncate text-xs text-slate-500">{detail}</p> : null}
    </div>
  );
}

function text(value: unknown) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

function compactAddress(...parts: Array<unknown>) {
  return parts.map(text).filter(Boolean).join(", ") || "-";
}

function orderAddressSourceLabel(value: unknown) {
  const source = text(value);
  if (source === "billing_fallback") return "Fatura adresi yedeği";
  if (source === "unknown") return "Adres kaynağı belirsiz";
  return "Kargo adresi";
}

function displayRowName(row: DataRow) {
  const fullName = [row.firstName, row.lastName].map(text).filter(Boolean).join(" ").trim();
  return text(row.companyName) || fullName || text(row.email) || "-";
}

function numberText(value: unknown) {
  const parsed = Number(value || 0);
  return new Intl.NumberFormat(currentLocaleTag()).format(Number.isFinite(parsed) ? parsed : 0);
}

function percentText(value: unknown) {
  const parsed = Number(value || 0);
  return `${new Intl.NumberFormat(currentLocaleTag(), { maximumFractionDigits: 0 }).format(Number.isFinite(parsed) ? parsed : 0)}%`;
}

function moneyText(value: unknown, currency: unknown) {
  const parsed = Number(value || 0);
  return new Intl.NumberFormat(currentLocaleTag(), {
    style: "currency",
    currency: text(currency) || "EUR",
  }).format(Number.isFinite(parsed) ? parsed : 0);
}

function badge(value: string) {
  return <span className="inline-flex whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">{value ? platformLabel(value) : "-"}</span>;
}

function statusBadgeLabel(value: string) {
  const normalized = value.toLowerCase().replace(/[\s-]+/g, "_");
  if (["label_created", "label_generated", "label_ready"].includes(normalized)) return "Etiket oluştu";
  if (["cancelled", "canceled"].includes(normalized)) return "İptal";
  if (normalized === "reconnect_required") return "Yeniden bağlantı gerekli";
  return value;
}

function statusBadge(value: string) {
  const normalized = value.toLowerCase().replace(/[\s-]+/g, "_");
  const positive = ["active", "success", "completed", "final", "paid", "open", "available", "label_created", "label_generated", "label_ready"].includes(normalized);
  const negative = ["inactive", "failed", "error", "cancelled", "canceled", "void", "expired", "disconnected"].includes(normalized);
  return (
    <span
      className={cx(
        "inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold",
        positive && "border-emerald-200 bg-emerald-50 text-emerald-700",
        negative && "border-red-200 bg-red-50 text-red-700",
        !positive && !negative && "border-amber-200 bg-amber-50 text-amber-700"
      )}
    >
      {value ? statusBadgeLabel(value) : "-"}
    </span>
  );
}

function dateText(value: unknown) {
  const normalized = text(value);
  if (!normalized) return "-";
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return normalized;
  return formatDate(normalized);
}

function dateTextWithYear(value: unknown) {
  const normalized = text(value);
  if (!normalized) return "-";
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return normalized;
  return new Intl.DateTimeFormat(currentLocaleTag(), {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function placeholderActivity() {
  return [
    { id: "empty-1", label: "Hesap hazır", detail: "Panel bilgileri yüklenmeye hazır", createdAt: new Date().toISOString() },
    { id: "empty-2", label: "Rol sistemi aktif", detail: "Admin, müşteri ve alt müşteri rolleri ayrıldı", createdAt: new Date().toISOString() },
  ];
}

function emptyDashboardActivity(role: UserRole) {
  const now = new Date().toISOString();
  return role === "admin"
    ? [
        { id: "empty-admin-1", label: "Hareket bekleniyor", detail: "Son sipariş, gönderi, etiket alımı ve fatura kayıtları burada görünecek", createdAt: now },
        { id: "empty-admin-2", label: "Raporlar hazır", detail: "Yeni veri geldikçe dashboard otomatik dolacak", createdAt: now },
      ]
    : [
        { id: "empty-customer-1", label: "Henüz hareket yok", detail: "İlk sipariş veya etiket oluşturulduğunda burada görünecek", createdAt: now },
        { id: "empty-customer-2", label: "Panel hazır", detail: "Mağaza, sipariş, etiket ve fatura hareketleri takip edilecek", createdAt: now },
      ];
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(currentLocaleTag(), {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

type ShopifyOpsData = {
  accessScopes?: string;
  apiVersion?: string;
  checkedAt: string;
  logs: DataRow[];
  ok?: boolean;
  orderAccessWarning?: string | null;
  purchases: DataRow[];
  requests: DataRow[];
  shop: string;
  shopInfo?: DataRow;
  source?: string;
  summaries: {
    inventory?: DataRow;
    labels?: DataRow;
    operations?: DataRow;
    orders?: DataRow;
    purchases?: DataRow;
    support?: DataRow;
  };
};

async function getShopifyIdToken() {
  for (let attempt = 0; attempt < 20; attempt += 1) {
    if (window.shopify?.idToken) return window.shopify.idToken();
    await new Promise((resolve) => window.setTimeout(resolve, 100));
  }
  const queryToken = new URLSearchParams(window.location.search).get("id_token");
  if (queryToken) return queryToken;
  throw new Error("Open KenPost Billing Bridge from Shopify Admin so App Bridge can provide a session token.");
}

async function loadShopifyOpsData() {
  const token = await getShopifyIdToken();
  const response = await fetch("/api/shopify/ops", {
    cache: "no-store",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const payload = (await response.json().catch(() => ({}))) as ShopifyOpsData & { error?: string };
  if (!response.ok) throw new Error(payload.error || "Shopify API data could not be loaded. Approve the requested Shopify scopes and reopen the app.");
  return payload;
}

function ShopifyEmbeddedOpsPage() {
  const [data, setData] = useState<ShopifyOpsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  async function refresh() {
    setRefreshing(true);
    setError(null);
    try {
      const result = await loadShopifyOpsData();
      setData(result);
      window.shopify?.toast?.show?.("Shopify API data refreshed.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Shopify API data could not be loaded.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    loadShopifyOpsData()
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : "Shopify API data could not be loaded.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const summaries = data?.summaries || {};
  const purchaseSummary = summaries.purchases || {};
  const supportSummary = summaries.support || {};
  const inventorySummary = summaries.inventory || {};
  const operationsSummary = summaries.operations || {};
  const orderSummary = summaries.orders || {};
  const labelSummary = summaries.labels || {};
  const shopCurrency = text(data?.shopInfo?.currencyCode) || "EUR";

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-4 text-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-4">
        <header className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-950 text-white">
                <ShoppingCart className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Shopify App Home</p>
                <h1 className="truncate text-xl font-semibold tracking-tight text-slate-950">KenPost Billing Bridge</h1>
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-500">Live orders, products, payment status, and API health loaded from this Shopify store through Shopify Admin GraphQL.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {data?.shop ? <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">{text(data.shopInfo?.myshopifyDomain) || data.shop}</span> : null}
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">Embedded</span>
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={refreshing}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
            >
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
          </div>
        </header>

        {error ? (
          <ErrorBanner message={error} />
        ) : null}
        {!error && data?.orderAccessWarning ? (
          <ErrorBanner message={`Shopify order API access needs attention: ${data.orderAccessWarning}`} />
        ) : null}

        {loading ? (
          <LoadingPanel label="Loading Shopify API data" />
        ) : (
          <>
            <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
              <AdminStatCard label="Recent orders" value={purchaseSummary.count || 0} tone="slate" icon={<ShoppingCart className="h-5 w-5" />} />
              <AdminStatCard label="Products" value={labelSummary.productCount || 0} tone="cyan" icon={<Tags className="h-5 w-5" />} />
              <AdminStatCard label="Order total" value={moneyText(purchaseSummary.totalPrice, shopCurrency)} tone="emerald" icon={<CreditCard className="h-5 w-5" />} />
              <AdminStatCard label="Unfulfilled" value={orderSummary.openOrderCount || 0} tone="violet" icon={<MessageCircle className="h-5 w-5" />} />
              <AdminStatCard label="Payment review" value={supportSummary.totalCount || 0} tone="amber" icon={<AlertTriangle className="h-5 w-5" />} />
              <AdminStatCard label="API records" value={operationsSummary.operationalLogCount || 0} tone="emerald" icon={<ShieldCheck className="h-5 w-5" />} />
            </section>

            <section className="grid gap-3 lg:grid-cols-4">
              <AdminPanel title="Shopify Admin API" caption="Store data returned by the installed app permissions.">
                <div className="grid gap-2 p-4 text-sm">
                  <InfoLine label="Shopify orders" value={numberText(orderSummary.shopifyOrderCount)} />
                  <InfoLine label="Open orders" value={numberText(orderSummary.openOrderCount)} />
                  <InfoLine label="API version" value={text(data?.apiVersion) || "-"} />
                  <InfoLine label="Source" value={text(data?.source) || "-"} />
                </div>
              </AdminPanel>
              <AdminPanel title="Products" caption="Product and inventory signals from Shopify.">
                <div className="grid gap-2 p-4 text-sm">
                  <InfoLine label="Products" value={numberText(labelSummary.productCount)} />
                  <InfoLine label="Active products" value={numberText(labelSummary.activeProductCount)} />
                  <InfoLine label="Inventory units" value={numberText(inventorySummary.availableCount)} />
                  <InfoLine label="Products returned" value={numberText(labelSummary.mappedProductCount)} />
                </div>
              </AdminPanel>
              <AdminPanel title="Payment statuses" caption="Orders that need payment review.">
                <div className="grid gap-2 p-4 text-sm">
                  <InfoLine label="Pending" value={numberText(supportSummary.totalCount)} />
                  <InfoLine label="Today" value={numberText(supportSummary.todayCount)} />
                  <InfoLine label="Latest shown" value={numberText(data?.requests?.length || 0)} />
                  <InfoLine label="Custom app token" value="Not required" />
                </div>
              </AdminPanel>
              <AdminPanel title="Connection health" caption="Embedded app authentication path.">
                <div className="grid gap-2 p-4 text-sm">
                  <InfoLine label="App Bridge" value={data?.shop ? "Active" : "Loaded"} />
                  <InfoLine label="Token exchange" value={data?.accessScopes ? "Granted" : "Checked"} />
                  <InfoLine label="GraphQL Admin API" value={data?.ok ? "Connected" : "Check"} />
                  <InfoLine label="Scopes" value={text(data?.accessScopes) || "read_orders,read_products"} />
                </div>
              </AdminPanel>
            </section>

            <AdminPanel title="Recent Shopify store orders" caption="Orders loaded from the current merchant store through Shopify Admin GraphQL." count={data?.purchases?.length || 0}>
              <DataTable
                rows={data?.purchases || []}
                empty="No recent Shopify orders were returned for this store."
                columns={[
                  { label: "Order", value: (row) => <NameStack title={text(row.invoiceNumber) || text(row.shopifyOrderName) || "-"} detail={text(row.shopifyOrderName) || "Shopify"} /> },
                  { label: "Store", value: (row) => text(row.customer) || "-" },
                  { label: "Line items", value: (row) => <NameStack title={text(row.productSummary) || "-"} detail={`${numberText(row.quantity)} items`} /> },
                  { label: "Total", value: (row) => <NameStack title={moneyText(row.totalPrice, shopCurrency)} detail={text(row.paymentMethodName) || "Shopify Admin GraphQL"} /> },
                  { label: "Financial status", value: (row) => statusBadge(text(row.status) || "unknown") },
                  { label: "Created", value: (row) => dateText(row.createdAt) },
                ]}
              />
            </AdminPanel>

            <section className="grid gap-4 xl:grid-cols-2">
              <AdminPanel title="Payment status review" caption="Pending, authorized, or partially paid Shopify orders from this store." count={data?.requests?.length || 0}>
                <DataTable
                  rows={data?.requests || []}
                  empty="No pending payment statuses were returned for this store."
                  columns={[
                    { label: "Order", value: (row) => <NameStack title={text(row.referenceId)} detail={dateText(row.createdAt)} /> },
                    { label: "Store", value: (row) => text(row.customer) || "-" },
                    { label: "Signal", value: (row) => <NameStack title={text(row.subject)} detail={text(row.message)} /> },
                    { label: "Priority", value: (row) => statusBadge(text(row.priority) || "normal") },
                    { label: "Page", value: (row) => text(row.pagePath) || "-" },
                  ]}
                />
              </AdminPanel>

              <AdminPanel title="Recent API records" caption={`Products from Shopify Admin API. Last checked ${dateText(data?.checkedAt)}`} count={data?.logs?.length || 0}>
                <DataTable
                  rows={data?.logs || []}
                  empty="No Shopify product records were returned."
                  columns={[
                    { label: "Reference", value: (row) => <NameStack title={text(row.referenceId)} detail={dateText(row.createdAt)} /> },
                    { label: "Area", value: (row) => badge(text(row.area) || "system") },
                    { label: "Message", value: (row) => <NameStack title={text(row.message)} detail={text(row.errorCode) || text(row.pagePath)} /> },
                    { label: "Scope", value: (row) => statusBadge(text(row.scope) || "system") },
                  ]}
                />
              </AdminPanel>
            </section>
          </>
        )}
      </div>
    </main>
  );
}

export default function App() {
  const location = useLocation();
  const [auth, setAuth] = useState<AuthState>(() => {
    const cachedUser = readCachedAuthUser();
    return cachedUser ? { status: "signed-in", user: cachedUser } : { status: "loading", user: null };
  });

  useDocumentMetadata(location.pathname, auth);

  function setSignedIn(user: SessionUser) {
    cacheAuthUser(user);
    setAuth({ status: "signed-in", user });
  }

  function setSignedOut() {
    clearCachedAuthUser();
    setAuth({ status: "signed-out", user: null });
  }

  useEffect(() => {
    if (!shouldVerifyAuthOnLoad()) {
      setSignedOut();
      return;
    }

    let active = true;
    apiClient
      .me()
      .then((result) => {
        if (active) setSignedIn(result.user);
      })
      .catch(() => {
        if (active) setSignedOut();
      });
    return () => {
      active = false;
    };
  }, []);

  async function logout() {
    await apiClient.logout().catch(() => undefined);
    setSignedOut();
    window.location.assign("/sign-in");
  }

  async function stopImpersonation() {
    const result = await apiClient.stopImpersonation().catch(() => ({ redirect: "/admin/customers" }));
    clearCachedAuthUser();
    window.location.assign(result.redirect || "/admin/customers");
  }

  const hideFloatingVersionLink = location.pathname === "/versions" || location.pathname === "/shopify" || isPublicInfoPath(location.pathname);

  return (
    <>
      <Routes>
      <Route path="/" element={<Navigate to={auth.status === "signed-in" ? (auth.user.role === "admin" ? "/admin" : "/customer") : "/sign-in"} replace />} />
      <Route path="/support" element={<PublicInfoRoute pageKey="support" />} />
      <Route path="/privacy" element={<PublicInfoRoute pageKey="privacy" />} />
      <Route path="/faq" element={<PublicInfoRoute pageKey="faq" />} />
      <Route path="/changelog" element={<PublicInfoRoute pageKey="changelog" />} />
      <Route path="/docs" element={<PublicInfoRoute pageKey="docs" />} />
      <Route path="/pricing" element={<PublicInfoRoute pageKey="pricing" />} />
      <Route path="/shopify" element={<ShopifyEmbeddedOpsPage />} />
      <Route
        path="/sign-in"
        element={auth.status === "signed-in" ? <Navigate to={safeAuthReturnPath(location.search, auth.user)} replace /> : <SignIn onSignedIn={setSignedIn} />}
      />
      <Route
        path="/sign-up"
        element={auth.status === "signed-in" ? <Navigate to={auth.user.role === "admin" ? "/admin" : "/customer"} replace /> : <SignUp onSignedIn={setSignedIn} />}
      />
      <Route
        path="/email-two-factor"
        element={auth.status === "signed-in" ? <Navigate to={safeAuthReturnPath(location.search, auth.user)} replace /> : <EmailTwoFactor onSignedIn={setSignedIn} />}
      />
      <Route
        path="/forgot-password"
        element={auth.status === "signed-in" ? <Navigate to={auth.user.role === "admin" ? "/admin" : "/customer"} replace /> : <ForgotPassword />}
      />
      <Route
        path="/reset-password"
        element={auth.status === "signed-in" ? <Navigate to={auth.user.role === "admin" ? "/admin" : "/customer"} replace /> : <ResetPassword />}
      />
      <Route
        path="/verify-email"
        element={auth.status === "signed-in" ? <Navigate to={auth.user.role === "admin" ? "/admin" : "/customer"} replace /> : <VerifyEmailPage />}
      />
      <Route
        path="/verify-email-sent"
        element={auth.status === "signed-in" ? <Navigate to={auth.user.role === "admin" ? "/admin" : "/customer"} replace /> : <VerifyEmailSentPage />}
      />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute auth={auth} role="admin">
            <AppShell user={auth.user!} onLogout={logout} onStopImpersonation={stopImpersonation}>
              <Routes>
                <Route path="/" element={<Dashboard user={auth.user!} />} />
                <Route path="customers" element={<AdminCustomersPage />} />
                <Route path="customers/new" element={<AdminCreateCustomerPage />} />
                <Route path="customers/:customerId" element={<AdminCustomerDetailPage />} />
                <Route path="customer-prices" element={<AdminCustomerPricesPage />} />
                <Route path="labels" element={<Navigate to="/admin/label-products" replace />} />
                <Route path="label-products" element={<AdminLabelsPage />} />
                <Route path="label-products/new" element={<AdminLabelProductFormPage mode="create" />} />
                <Route path="label-products/:labelProductId" element={<AdminLabelProductFormPage mode="edit" />} />
                <Route path="label-sources" element={<AdminLabelInventoryPage />} />
                <Route path="label-sources/upload" element={<AdminLabelUploadPage />} />
                <Route path="expired-labels" element={<AdminExpiredLabelsPage />} />
                <Route path="platforms" element={<AdminPlatformsPage />} />
                <Route path="amazon-v2" element={<AmazonV2AdminPage onManageCustomer={async (customerId, storeId) => {
                  const result = await apiClient.impersonateCustomer(customerId);
                  cacheAuthUser(result.user);
                  window.location.assign(`/customer/stores/amazon-v2/new${storeId ? `?storeId=${encodeURIComponent(storeId)}` : ""}`);
                }} />} />
                <Route path="jtl-connect-health" element={<JtlDeviceHealthPage admin />} />
                <Route path="platforms/:platform" element={<AdminPlatformDetailPage />} />
                <Route path="billing" element={<AdminBillingPage />} />
                <Route path="billing/invoices/:invoiceId" element={<InvoiceDetailPage admin />} />
                <Route path="invoices" element={<AdminBillingPage />} />
                <Route path="invoices/:invoiceId" element={<InvoiceDetailPage admin />} />
                <Route path="purchases" element={<AdminBillingPage />} />
                <Route path="orders" element={<AdminOrdersPage />} />
                <Route path="shipments" element={<AdminShipmentsPage />} />
                <Route path="reports" element={<AdminReportsPage />} />
                <Route path="reports/:slug" element={<AdminReportSlugPage />} />
                <Route path="support" element={<AdminSupportPage />} />
                <Route path="training" element={<TrainingVideosPage admin />} />
                <Route path="affiliates" element={<AdminAffiliatesPage />} />
                <Route path="commissions" element={<AdminCommissionsPage />} />
                <Route path="error-logs" element={<AdminAppErrorLogsPage />} />
                <Route path="activity-logs" element={<AdminActivityLogsPage />} />
                <Route path="label-checks" element={<AdminLabelChecksPage />} />
                <Route path="faulty-labels" element={<AdminFaultyLabelsPage />} />
                <Route path="missing-labels" element={<AdminMissingLabelsPage />} />
                <Route path="canceled-labels" element={<AdminCanceledLabelsPage />} />
                <Route path="plans" element={<AdminPlansPage />} />
                <Route path="profile" element={<ProfilePage currentUser={auth.user!} onUserUpdated={setSignedIn} />} />
                <Route path="settings" element={<AdminSettingsPage />} />
                <Route path="settings/error-logs" element={<AdminAppErrorLogsPage />} />
                <Route path="settings/activity-logs" element={<AdminActivityLogsPage />} />
                <Route path="settings/invoices" element={<AdminInvoiceSettingsPage />} />
                <Route path="settings/profile" element={<ProfilePage currentUser={auth.user!} onUserUpdated={setSignedIn} />} />
                <Route path="settings/tracking-api" element={<AdminTrackingApiPage />} />
                <Route path="settings/versions" element={<AdminVersionsPage />} />
                <Route path="tracking-api" element={<AdminTrackingApiPage />} />
              </Routes>
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer/*"
        element={
          <ProtectedRoute auth={auth} role="customer">
            <AppShell user={auth.user!} onLogout={logout} onStopImpersonation={stopImpersonation}>
              <Routes>
                <Route path="/" element={<Dashboard user={auth.user!} />} />
                <Route path="onboarding" element={<CustomerOnboardingPage />} />
                <Route path="reports" element={<CustomerReportsPage />} />
                <Route path="reports/shipping-health" element={<ShippingHealthPage />} />
                <Route path="reports/:slug" element={<CustomerReportsPage />} />
                <Route path="stores" element={<StoresPage />} />
                <Route path="stores/new" element={<StoreCreatePage />} />
                <Route path="stores/amazon-v2/new" element={<AmazonV2ConnectionPage />} />
                <Route path="stores/:platform/new" element={<StoreCreatePage />} />
                <Route path="stores/:storeId/logs" element={<StoreSyncLogsPage />} />
                <Route path="stores/:storeId" element={<StoreDetailPage />} />
                <Route path="stores/:storeId/edit" element={<StoreDetailPage />} />
                <Route path="orders" element={auth.user ? <OrdersPage key={auth.user.id} user={auth.user} /> : null} />
                <Route path="orders/new" element={<CreateLabelPage />} />
                <Route path="orders/:platform/:storeId/:orderId" element={<OrderDetailPage />} />
                <Route path="orders/:orderId" element={<OrderDetailPage />} />
                <Route path="create-label" element={<CreateLabelPage />} />
                <Route path="invoices" element={<CustomerInvoicesPage />} />
                <Route path="invoices/:invoiceId" element={<InvoiceDetailPage />} />
                <Route path="affiliate" element={<CustomerAffiliatePage />} />
                <Route path="plan" element={<CustomerPlanPage />} />
                <Route path="buy-labels" element={<LabelProductsPage title="Etiket satın al" />} />
                <Route path="balances" element={<CustomerBalancesPage />} />
                <Route path="team" element={<TeamPage />} />
                <Route path="profile" element={<ProfilePage currentUser={auth.user!} onUserUpdated={setSignedIn} />} />
                <Route path="profile/security" element={<ProfilePage currentUser={auth.user!} onUserUpdated={setSignedIn} />} />
                <Route path="support" element={<CustomerSupportPage />} />
                <Route path="training" element={<TrainingVideosPage />} />
                <Route path="shipments" element={<CustomerShipmentsPage />} />
                <Route path="shipments/:shipmentId" element={<ShipmentDetailPage />} />
                <Route path="shipments/:shipmentId/label" element={<ShipmentDetailPage />} />
                <Route path="label-inventory" element={<CustomerLabelInventoryPage />} />
                <Route path="api" element={<ApiAccessPage />} />
                <Route path="jtl-connect" element={<JtlConnectPage />} />
              </Routes>
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route path="/versions" element={<AdminVersionsPage />} />
      <Route path="/public/*" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <AppVersionRefreshBanner />
      {hideFloatingVersionLink ? null : <ProductVersionFloatingLink />}
    </>
  );
}
